/**
 * Phase 4H: Frontend Configuration Management Standardization
 *
 * Centralized configuration management for all Vue components and composables.
 * This eliminates hard-coded values scattered throughout the frontend codebase.
 */
import { isReceiptBearingDocType } from '@/composables/usePaymentDocumentTypes';
import type { PaymentStatus, RoleContext, SessionEnrollmentStatus } from '@/types/database';
import type { HubFamily, HubStatusKey } from './hubUi';
import type { TransferAction } from '@/types/transfer';
// Type only (erased at build) — SEAT_USAGE_CONFIG.HOLD_CHOICES names 其他處理 choices.
import type { OtherHandlingTopChoice } from '@/utils/otherHandlingChoice';

// 課務總覽 display rules (Hub UI redesign, 2026-09-28): page identity, status → colour,
// button kinds, panel variants. Kept in its own module; re-exported so components keep
// importing from '@/config/appConfig'. hubUi.ts imports nothing from this file.
export * from './hubUi';
// The page identity table, split out of hubUi.ts (2026-10-14, W8.3); it reads hubUi's button
// labels, so it is re-exported here rather than from hubUi.ts. Imports nothing from this file.
export * from './hubPageIdentity';
// The button labels a sentence here names (使用說明 › 怎麼聯絡) — no cycle, see above.
import { HUB_BUTTON_KIND, HUB_CHANGE_TARGET_LABEL, HUB_TRANSFER_HANDLE_LABEL } from './hubUi';
// The 近期未處理 page name LIST_STATE_NAME_BY_STATE reads (約課切斷確認 D80) — no cycle, see above.
import { HUB_PAGE_IDENTITY } from './hubPageIdentity';

// ==================================================================================
// CATEGORY: UI CONFIGURATION
// Description: Settings related to the visual appearance and user interface.
// ==================================================================================

export const UI_CONFIG = {
  /**
   * 2026-10-25 (約課切斷確認 D77, PO): 「keep all these modals at the same width, wide enough to
   * reduce horizontal scrolling」 — ONE width for every window that shows 名額使用情況 and for the
   * 約課切斷確認／續報／轉班 windows beside them (ParentMessageWindow, LsedCutoffFixModal,
   * SetReservationModal, TransferMarkCompleteForm, TransferCompleteForm, TransferProposalForm,
   * TransferRegisterModal, VerifyEnrollmentModal, OtherHandlingModal). D79 (PO, 23:0x): 「760px
   * is over kill, move it down to 580 px」 — 580 holds the chart's 名字 column + about 23 weeks
   * (today − 8 weeks … + 14 weeks) before it scrolls; phones keep a 16px gutter. Bound as an
   * inline style: a preset="card" modal is teleported, scoped widths never reach it. 2026-11-03
   * (tasks/todo-renewal-pending-chart.md D68 (1), PO 「A」): 660 — 580 cut a term's weeks after
   * the 最後約課日 flag; at 660 a whole term fits without scrolling.
   */
  WINDOW_WIDTH: 'min(660px, calc(100vw - 32px))',
  /**
   * @description Defines the application's color scheme and component theme overrides for Naive UI.
   */
  THEME: {
    PRIMARY: 'primary',
    SECONDARY: 'tertiary',
    SUCCESS: 'success',
    WARNING: 'warning',
    ERROR: 'error',
    INFO: 'info',
    BACKGROUND: 'var(--n-body-color)',
    SURFACE: 'var(--n-card-color)',
    BORDER: 'var(--n-border-color)',
  },

  THEME_OVERRIDES: {
    common: {
      primaryColor: '#3498db',
      primaryColorHover: '#2980b9',
      primaryColorPressed: '#3498db',
      successColor: '#4caf50',
      warningColor: '#ff9800',
      errorColor: '#f44336',
      infoColor: '#2196f3',
    },
    Card: { borderRadius: '8px' },
    Button: { borderRadius: '6px' },
    Input: { borderRadius: '6px' },
  },

  MOBILE_THEME_OVERRIDES: {
    common: {
      primaryColor: '#fb8c00',
      primaryColorHover: '#e67e22',
      primaryColorPressed: '#d35400',
    },
    Button: {
      textColorPrimary: '#FFFFFF',
      textColorHoverPrimary: '#FFFFFF',
      textColorPressedPrimary: '#FFFFFF',
    },
  },

  SIDEBAR_MENU_THEME_OVERRIDES: {
    BLUE: {
      common: {
        primaryColor: '#3498db',
        primaryColorHover: '#5dade2',
        primaryColorPressed: '#2980b9',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#3498db',
      itemIconColorHover: '#3498db',
      itemTextColorActive: '#3498db',
      itemIconColorActive: '#3498db',
      itemTextColorChildActive: '#3498db',
      itemIconColorChildActive: '#3498db',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
    ORANGE: {
      common: {
        primaryColor: '#ff9800',
        primaryColorHover: '#ffb74d',
        primaryColorPressed: '#f57c00',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#ff9800',
      itemIconColorHover: '#ff9800',
      itemTextColorActive: '#ff9800',
      itemIconColorActive: '#ff9800',
      itemTextColorChildActive: '#ff9800',
      itemIconColorChildActive: '#ff9800',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
    GREEN: {
      common: {
        primaryColor: '#2ecc71',
        primaryColorHover: '#27ae60',
        primaryColorPressed: '#2ecc71',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#2ecc71',
      itemIconColorHover: '#2ecc71',
      itemTextColorActive: '#2ecc71',
      itemIconColorActive: '#2ecc71',
      itemTextColorChildActive: '#2ecc71',
      itemIconColorChildActive: '#2ecc71',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
    PURPLE: {
      common: {
        primaryColor: '#bb86fc',
        primaryColorHover: '#a66df8',
        primaryColorPressed: '#bb86fc',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#bb86fc',
      itemIconColorHover: '#bb86fc',
      itemTextColorActive: '#bb86fc',
      itemIconColorActive: '#bb86fc',
      itemTextColorChildActive: '#bb86fc',
      itemIconColorChildActive: '#bb86fc',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
    TEAL: {
      common: {
        primaryColor: '#1abc9c',
        primaryColorHover: '#17a589',
        primaryColorPressed: '#1abc9c',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#1abc9c',
      itemIconColorHover: '#1abc9c',
      itemTextColorActive: '#1abc9c',
      itemIconColorActive: '#1abc9c',
      itemTextColorChildActive: '#1abc9c',
      itemIconColorChildActive: '#1abc9c',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
    INDIGO: {
      common: {
        primaryColor: '#5c6bc0',
        primaryColorHover: '#7986cb',
        primaryColorPressed: '#3f51b5',
      },
      itemTextColor: 'rgba(255, 255, 255, 0.85)',
      itemIconColor: 'rgba(255, 255, 255, 0.12)',
      itemTextColorHover: '#7986cb',
      itemIconColorHover: '#7986cb',
      itemTextColorActive: '#7986cb',
      itemIconColorActive: '#7986cb',
      itemTextColorChildActive: '#7986cb',
      itemIconColorChildActive: '#7986cb',
      itemColorActive: 'transparent',
      itemColorActiveCollapsed: 'transparent',
      itemColorHover: 'transparent',
      itemColorActiveHover: 'transparent',
      itemIconColorCollapsed: 'rgba(255, 255, 255, 0.12)',
    },
  },

  DATA_DISPLAY: {
    ITEMS_PER_PAGE: 20,
  },
} as const;

// ==================================================================================
// CATEGORY: DATABASE CONFIGURATION
// Description: Constants related to Firestore database structure.
// ==================================================================================

export const DATABASE_CONFIG = {
  COLLECTIONS: {
    USERS: 'users',
    CLASSES: 'classes',
    STUDENTS: 'students',
    ATTENDANCE: 'attendance',
    SCHEDULE_UPLOAD_JOBS: 'scheduleUploadJobs',
    FEEDBACK_DATA: 'feedbackData',
    DAILY_DUTY_ROSTER: 'dailyDutyRoster',
    FEEDBACK_PREPARATION_ITEMS: 'feedbackPreparationItems',
    PROGRAM_WAITLIST: 'programWaitlist',
    SESSION_ENROLLMENTS: 'sessionEnrollments',
    SYSTEM_SETTINGS: 'system_settings',
    // ─── 出勤管理 (Staff Attendance) — Phase 1 ───
    STAFF_PUNCHES: 'staffPunches',
    // ─── 加班 (Overtime) — Phase 2 ───
    OVERTIME_REQUESTS: 'overtimeRequests',
    // ─── 排班 (Staff Scheduling) — Phase 2 ───
    STAFF_SHIFT_SCHEDULES: 'staffShiftSchedules',
    NATIONAL_HOLIDAYS: 'nationalHolidays',
    // ─── BF (BookFastPOS) booking-email reconciliation ───
    BF_BOOKING_EVENTS: 'bfBookingEvents',
    // ─── 學生名冊 (student directory, 2026-09-29) — backend-written, read-only here ───
    STUDENT_DIRECTORY: 'studentDirectory',
  },
} as const;

/**
 * The 學生名冊 part doc ids' prefix (`v1-part-{k}`). Deep review C8: a string of its own so
 * a config-mirror row can pin it to the backend builder's prefix after the merge.
 */
const STUDENT_DIRECTORY_PART_PREFIX = 'v1-part-';

/**
 * 學生名冊 (PO 2026-09-29, 「候補 連結既有學生 ＋ 全站學生搜尋改用學生名冊」). The one list every
 * student search reads: `studentDirectory/v1` (+ `v1-part-{k}` when it outgrows one doc),
 * rebuilt whole by the backend (LINE branch `feature/course-ops-line-alert`). The website
 * reads it with ONE getDoc per page load (composables/useStudentDirectory.ts) and filters in
 * memory; `REBUILD_FUNCTION` is the 重新整理名冊 button.
 */
export const STUDENT_DIRECTORY_CONFIG = {
  DOC_ID: 'v1',
  PART_DOC_ID_PREFIX: STUDENT_DIRECTORY_PART_PREFIX,
  /** Doc id of part k (k ≥ 1). */
  PART_DOC_ID: (k: number) => `${STUDENT_DIRECTORY_PART_PREFIX}${k}`,
  /** Matches a search shows (decision 1: the top 20; the rest → 「請輸入更完整…」). */
  SEARCH_LIMIT: 20,
  REBUILD_FUNCTION: 'admin-rebuildStudentDirectory',
  /**
   * A result's 「目前：…」 line (decision 6): the enrollment statuses of a class the student
   * is in now — 轉班中 included (deep review C19: it read 「目前沒有上課」).
   */
  CURRENT_STATUSES: ['active', 'paused', 'transferring'],
  /**
   * D-10 (PO 2026-10-02, freshness change approved): a search field that opens or gets focus
   * re-reads `studentDirectory/v1` when the last read is older than this (ms) — a student
   * added by today's upload is found without 重新整理名冊 / F5. One doc read; the parts and
   * the search index only when its `builtAt` changed.
   */
  STALE_MS: 60_000,
} as const;

/**
 * 課務總覽 › 體驗回饋 search box (D25, corrected by D32-7／R2P-3). FIELDS names the STORED fields
 * by collection: 會員編號 = `students.memberAccount` (exact), 會員姓名 = `students.memberName`
 * (prefix), the names on the form = `feedbackData.childName`／`parentName` (prefix). A form has
 * no memberName, and nobody types a studentId: students resolve first, then their forms by
 * `studentId in` chunks (queryInChunks). The rows already loaded are matched locally on the
 * same facts and drawn at once; the server lookup runs on every search as well (D32-18 — nothing
 * can know what is not loaded without asking), its hits merged into the same stage order (a form
 * the store holds is drawn from the store).
 */
export const FEEDBACK_SEARCH = {
  /** Characters typed (after trim) before anything is searched. */
  MIN_CHARS: 2,
  /**
   * Most docs one server fallback query returns (each `students` query; the forms of the
   * students found). Not STUDENT_DIRECTORY_CONFIG.SEARCH_LIMIT (20 = matches a student search
   * shows) — this bounds the reads of one search.
   */
  SERVER_LIMIT: 50,
  /** Pause after the last keystroke before the box searches (FeedbackHubSearch; Enter at once). */
  DEBOUNCE_MS: 300,
  FIELDS: {
    [DATABASE_CONFIG.COLLECTIONS.STUDENTS]: [
      { field: 'memberAccount', match: 'exact' },
      { field: 'memberName', match: 'prefix' },
    ],
    [DATABASE_CONFIG.COLLECTIONS.FEEDBACK_DATA]: [
      { field: 'childName', match: 'prefix' },
      { field: 'parentName', match: 'prefix' },
    ],
  },
} as const;

// ─── 排班 (Staff Scheduling) — Phase 2 — lean FE mirror (display only) ───
// The backend BUSINESS_RULES.SCHEDULING is authoritative; this carries just the
// display essentials the SchedulePage needs (preset buttons + status/day-type labels).
export const SCHEDULING_CONFIG = {
  /** 早/午/全日 quick-author preset buttons (Taipei HH:MM). Mirrors backend SHIFT_PRESETS. // PO-TUNABLE */
  SHIFT_PRESETS: {
    morning: { key: 'morning', label: '早', start: '09:00', end: '13:00', breakMinutes: 0 },
    afternoon: { key: 'afternoon', label: '午', start: '13:00', end: '18:00', breakMinutes: 0 },
    full_day: { key: 'full_day', label: '全日', start: '09:00', end: '18:00', breakMinutes: 60 },
  },
  /** Roster status display labels (draft|pending|published|declined|deleted). */
  SHIFT_STATUS_LABELS: {
    draft: '草稿',
    pending: '待簽核',
    published: '已發佈',
    declined: '已退回',
    deleted: '已刪除',
  } as Record<string, string>,
  /** Max break minutes the shift editor accepts (mirrors backend MAX_BREAK_MINUTES = 24*60). */
  SHIFT_MAX_BREAK_MINUTES: 24 * 60,
  /**
   * 國定假日表的日別分類（M-C.3）。Mirrors backend
   * BUSINESS_RULES.SCHEDULING.HOLIDAY_DAY_TYPES — the holidays-admin page offers
   * these as the 日別 options, and the roster authoring grid warns when a shift
   * lands on a 國定假日 / 例假 day. // CPA-TUNABLE
   */
  HOLIDAY_DAY_TYPES: {
    NATIONAL_HOLIDAY: '國定假日',
    REST_DAY: '休息日',
    REGULAR_LEAVE: '例假',
    MAKEUP_WORKDAY: '補班日',
  } as Record<string, string>,
  /**
   * Day-types that warrant a warn-only authoring badge in the roster grid (M-C.3).
   * VALUES (國定假日/例假) — matched against the stored holiday.dayType, which is the
   * 繁中 value (NOT the enum key). Was keys, so the warning never fired; fixed 2026-07-02.
   */
  HOLIDAY_WARN_DAY_TYPES: ['國定假日', '例假'] as readonly string[],
  /**
   * 政府行事曆匯入 — human-facing dataset page (資料來源 / 手動下載 link on the
   * 國定假日 page). The automatic fetch happens backend-side (data.gov.tw API);
   * this is only the page a user can open to download/verify manually.
   */
  GOV_CALENDAR: {
    DATASET_PAGE_URL: 'https://data.gov.tw/dataset/14718',
  },
} as const;

// ─── 加班 (Overtime) — Phase 2 (OT 申請 + 簽核) — lean FE mirror (display + form preview) ───
// The backend BUSINESS_RULES.OVERTIME is authoritative (server re-validates everything);
// this carries just what the OT form/list needs for preview + status display.
export const OVERTIME_CONFIG = {
  /** Minimum OT unit in hours — the form rounds the live preview UP to this. // CPA-TUNABLE */
  OT_MIN_UNIT_HOURS: 0.5,
  /** Sanity cap on a single OT window length, hours (the form blocks longer windows). */
  MAX_OT_SPAN_HOURS: 12,
  /** 補申請 deadline (days) — a worked date older than this is rejected by the server. */
  OT_RETRO_MAX_AGE_DAYS: 7,
  /** Max length of an OT reason / approve-decline comment. */
  MAX_COMMENT_LENGTH: 500,
  /** Status display labels (pending|approved|declined|cancelled). */
  OT_STATUS_LABELS: {
    pending: '待簽核',
    approved: '已核准',
    declined: '未核准',
    cancelled: '已取消',
  } as Record<string, string>,
  /** Status → indicator tone for chips (Naive UI n-tag type). */
  OT_STATUS_TONES: {
    pending: 'warning',
    approved: 'success',
    declined: 'error',
    cancelled: 'default',
  } as Record<string, 'warning' | 'success' | 'error' | 'default'>,
} as const;

// ==================================================================================
// CATEGORY: BUSINESS RULES & CONSTANTS
// Description: Core business logic, system-wide constants, and enumerated values.
// ==================================================================================

// Canonical data value is '代課' (from Excel upload). '臨時老師' is an alternate display term. Both must be recognized.
// Extracted before SYSTEM_CONSTANTS so it can be referenced inside HELPERS without self-reference.
const SUBSTITUTE_KEYWORDS_LIST: readonly string[] = ['代課', '臨時老師'];
// Exact (trimmed) placeholder-name test — read through
// SYSTEM_CONSTANTS.COOP_CLASSES.HELPERS.isPlaceholderTeacherName / classHasPlaceholderTeacher.
const isPlaceholderName = (name: string): boolean =>
  SUBSTITUTE_KEYWORDS_LIST.includes(String(name ?? '').trim());
// A class's teacher names as ONE list, whatever shape arrived — read through
// SYSTEM_CONSTANTS.COOP_CLASSES.HELPERS.teacherNameList. A string is a one-name list;
// every name is trimmed; blanks and non-strings are dropped. Mirrors the backend.
const toTeacherNameList = (raw: unknown): string[] => {
  const items: readonly unknown[] = Array.isArray(raw) ? raw : [raw];
  return items
    .filter((n): n is string => typeof n === 'string')
    .map((n) => n.trim())
    .filter((n) => n !== '');
};

export const SYSTEM_CONSTANTS = {
  USER_ROLES: {
    SUPERVISOR: 'supervisor',
    ADMIN: 'admin',
    TEACHER: 'teacher',
    KIOSK: 'kiosk',
    TREASURY: 'treasury',
    GENERAL_AFFAIRS: 'general_affairs',
    STAFF: 'staff',
    // Phase 2 (排班): 救生員 + 工讀生 — same app access as 一般員工 (staff); split out so the
    // roster can be grouped/granted by role. NOTE: the `work_study` ROLE (工讀生, access axis)
    // is distinct from the `工讀` employmentType (pay axis) — spec §2.2.
    LIFEGUARD: 'lifeguard',
    WORK_STUDY: 'work_study',
    VALUES: [
      'supervisor',
      'admin',
      'teacher',
      'kiosk',
      'treasury',
      'general_affairs',
      'staff',
      'lifeguard',
      'work_study',
    ],
    DISPLAY_NAMES: {
      supervisor: '主管',
      admin: '課務',
      teacher: '老師',
      kiosk: 'iPad',
      treasury: '出納',
      general_affairs: '總務',
      staff: '一般員工',
      lifeguard: '救生員',
      work_study: '工讀生',
    },
  } as const,

  /**
   * 助教 (tasks/todo-substitute-pay.md) — a tick on the employee form, stored as
   * `employees.isAssistant`. NOT a login role: never in USER_ROLES.VALUES, never
   * in a user's `roles` (a unit test guards this). KEY is the form key only.
   */
  ASSISTANT_ROLE: {
    KEY: 'assistant',
    LABEL: '助教',
    TOOLTIP: '老師核對時可被選為代課，不需登入',
  } as const,

  /** Roles that represent devices, not people — used to filter DeviceAccountsSection. */
  DEVICE_ROLES: ['kiosk'] as readonly string[],

  /** Roles that grant elevated privileges — granting these should trigger a confirmation dialog. */
  SENSITIVE_ROLES: ['supervisor', 'treasury'] as readonly string[],
  SENSITIVE_ROLE_LABELS: {
    supervisor: '主管 — 可核准請款、管理員工、修改系統設定',
    treasury: '出納 — 可處理付款、查看所有銀行帳戶資料',
  } as Record<string, string>,

  /** Default login enablement per employment type — controls form toggle default in EmployeeFormModal. */
  EMPLOYMENT_TYPE_LOGIN_DEFAULTS: {
    temp: false,
    part_time: false,
    full_time: true,
  } as Record<string, boolean>,

  // Mirror of BUSINESS_RULES.EMPLOYMENT_TYPES in functions/src/config/appConfig.ts — keep in sync
  EMPLOYMENT_TYPES: {
    TEMP: 'temp',
    PART_TIME: 'part_time',
    FULL_TIME: 'full_time',
    VALUES: ['temp', 'part_time', 'full_time'],
    LABELS: {
      temp: '工讀',
      part_time: '兼職',
      full_time: '正職',
    },
  } as const,

  /** 員工表單 (tasks/todo-employee-form-redesign.md) — section titles and notes. */
  EMPLOYEE_FORM: {
    SECTIONS: {
      IDENTITY: '基本資料',
      EMPLOYMENT: '任職',
      ROLES: '角色',
      ATTENDANCE: '出勤與排班',
      ACCOUNT: '系統帳號與通知',
      BANK: '銀行帳戶',
    },
    DISPLAY_NAME_NOTE: '名單、打卡與排班顯示的名稱',
    LOGIN_REQUIRED: '需系統登入',
    ATTENDANCE_NEEDS_LOGIN: '需系統登入才能打卡',
    LOGIN_DEFAULT_NOTE: '依員工類型預設，可自行調整',
    /** Grey note under 啟用打卡. */
    ATTENDANCE_NOTE: '預設關閉；開啟後此員工才能使用打卡、請假、加班與補登。',
    /** 員工管理: the banner 出納 sees (they edit 電話 and 銀行帳戶 only). */
    TREASURY_BANNER: '您僅可修改電話與銀行帳戶，其他資料需由主管修改。',
    /** 員工管理 tab labels (the 臨時助教 tab: TEMP_ASSISTANT.TAB_LABEL). */
    TABS: {
      ACTIVE: '現職',
      ARCHIVED: '已停用',
    },
    /** Known Google domains for a login e-mail; any other domain only warns (Workspace). */
    GOOGLE_DOMAINS: ['gmail.com', 'googlemail.com'] as readonly string[],
    /** On-screen labels of the fields with a message — the 還缺 line names them. */
    FIELD_LABELS: {
      name: '姓名',
      displayName: '暱稱',
      email: '登入信箱',
      roles: '需登入的角色',
      notificationEmail: '通知信箱',
    },
    /** Beside 建立登入帳號 while the form holds changes not yet saved. */
    LINK_NEEDS_SAVE: '有未儲存的變更，請先確認',
    /** The LINE 連結 row inside 系統帳號與通知. */
    LINE: {
      LABEL: 'LINE 連結',
      COPY: '複製',
    },
    /** The confirm before a save grants or removes a SENSITIVE_ROLES role. */
    SENSITIVE_CONFIRM: {
      TITLE: '敏感權限提醒',
      GRANT: '您即將授予：',
      REMOVE: '您即將移除：',
      CHECK: '請確認是否正確。',
    },
    /** Field messages — read by useEmployeeForm and the 臨時助教 form. */
    VALIDATION: {
      NAME_REQUIRED: '請輸入姓名',
      DISPLAY_NAME_REQUIRED: '請輸入暱稱',
      EMAIL_REQUIRED: '請輸入電子郵件',
      EMAIL_INVALID: '請輸入有效的電子郵件格式',
      /** A login e-mail outside the known Google domains — a warning, never a block. */
      EMAIL_NOT_GOOGLE: '系統僅支援 Google 帳號登入。非 Gmail 請確認為 Google Workspace 帳號。',
      /** A login needs at least one role (checked only when the roles are being set). */
      ROLES_REQUIRED: '請至少選擇一個需登入的角色 (助教不需登入)',
      /** Left of the footer buttons while 確認 is grey: what is still missing. */
      MISSING_SUMMARY: (n: number, labels: readonly string[]) =>
        `還缺 ${n} 項：${labels.join('、')}`,
    },
    /**
     * The only employee fields 出納 may save — MIRROR of
     * functions/src/config/appConfig.ts BUSINESS_RULES.EMPLOYEE_FIELDS.TREASURY_WRITABLE
     * (backend keys; keep in sync). Every other field is read-only for 出納 on the form,
     * and useEmployeeFormSave never sends one for them.
     */
    TREASURY_WRITABLE: [
      'phone',
      'bankName',
      'bankBranch',
      'bankCode',
      'branchCode',
      'bankAccountNumber',
      'bankAccountName',
      'bankAccountNameUpload',
      'personalIdNumber',
      'bankDocuments',
    ] as readonly string[],
  } as const,

  /**
   * 員工管理 › 臨時助教 (tasks/todo-substitute-pay.md D22, D24, D33, D37, D41) —
   * 助教 who are not employees; own ids, never deleted, only 停用.
   */
  TEMP_ASSISTANT: {
    TAB_LABEL: '臨時助教',
    /** 類型 tag of a stopped 臨時助教 row on the 已停用 tab. */
    TYPE_TAG: '臨時助教',
    ADD: '新增臨時助教',
    TITLE_CREATE: '新增臨時助教',
    TITLE_EDIT: '編輯臨時助教',
    COLUMNS: { DISPLAY_NAME: '暱稱', NAME: '姓名', ACTIONS: '操作' },
    FIELDS: { NAME: '姓名', DISPLAY_NAME: '暱稱' },
    DISPLAY_NAME_NOTE: '代課名單與簽到作業顯示的名稱',
    EMPTY: '目前沒有臨時助教。',
    EDIT: '編輯',
    DEACTIVATE: '停用',
    CONVERT: '轉為員工',
    CONFIRM: '確認',
    CANCEL: '取消',
    DEACTIVATE_CONFIRM: (displayName: string) =>
      `確定停用 ${displayName}？停用後不再出現在代課名單。`,
    CONVERTED_TO: (employeeDisplayName: string) => `已轉為員工：${employeeDisplayName}`,
    /** 已停用: a converted entry whose employee is not in the loaded list — no raw id. */
    CONVERTED_TO_UNKNOWN: '已轉為員工',
    /** The employee form's title when it opens from 轉為員工. */
    CONVERT_TITLE: (displayName: string) => `轉為員工：${displayName}`,
  } as const,

  /**
   * Order of the role ticks on the employee form. Every key except
   * ASSISTANT_ROLE.KEY ('assistant', a form-only tick) is a USER_ROLES.VALUES
   * entry (unit test). 'kiosk' is a device role and never shown here.
   */
  EMPLOYEE_FORM_ROLE_ORDER: [
    'supervisor',
    'admin',
    'teacher',
    'assistant',
    'treasury',
    'general_affairs',
    'lifeguard',
    'staff',
    'work_study',
  ] as const,

  JOB_STATUS: {
    PENDING: 'pending',
    PROCESSING: 'processing',
    COMPLETED: 'completed',
    FAILED: 'failed',
  } as const,

  FEEDBACK_APPROVAL_STATUS: {
    PENDING: 'pending',
    REVIEW: 'review',
    APPROVED: 'approved',
    REJECTED: 'rejected',
  } as const,

  FEEDBACK_FINAL_STATUS: {
    OPEN: 'open',
    COMPLETED: 'completed',
  } as const,

  /**
   * feedbackData.pdfCopyState — the Hub's own copy of the approved 體驗回饋 PDF
   * (體驗回饋 → 課務總覽 D18／D20, plan P17). PENDING = a save is due or being retried
   * (set together with the approval, or by a failed save); SAVED = pdfCopy holds the
   * current copy; GONE = Jotform no longer has the submission — final, never retried,
   * never blocks 通知家長. One field, mirrored in functions/src/config/appConfig.ts.
   */
  FEEDBACK_PDF_COPY_STATE: {
    PENDING: 'pending',
    SAVED: 'saved',
    GONE: 'gone',
  } as const,

  /**
   * feedbackData.pdfCopy.source — where a saved copy came from (fix-choices B87): JOTFORM =
   * downloaded with the Hub's key; LOCAL_BACKUP = uploaded from the 11/01 backup folder.
   * Mirrored with functions/src/config/appConfig.ts (check-config-mirror.cjs).
   */
  FEEDBACK_PDF_COPY_SOURCE: {
    JOTFORM: 'jotform',
    LOCAL_BACKUP: 'local-backup',
  } as const,

  /**
   * feedback-retryFeedbackPdfCopy `status` — the values 重新儲存 answers with (B87;
   * functions/src/feedback/pdfCopyCallables.ts RetryPdfCopyStatus). Mirrored with the backend.
   */
  FEEDBACK_PDF_COPY_RETRY_RESULT: {
    SAVED: 'saved',
    UNCHANGED: 'unchanged',
    PENDING: 'pending',
    GONE: 'gone',
    SKIPPED: 'skipped',
    CLAIMED: 'claimed',
  } as const,

  /**
   * Failed saves after which the hourly job stops retrying a form's PDF copy on its own
   * (fix-choices Q8): from this many `pdfCopyAttempts` the row says 「PDF 儲存失敗」 instead of
   * 「PDF 儲存中」. Mirror of functions FEEDBACK_PDF_COPY.MAX_AUTO_ATTEMPTS
   * (config/feedbackPdfCopyMirror.test.ts).
   */
  FEEDBACK_PDF_COPY_MAX_AUTO_ATTEMPTS: 24,

  /**
   * feedback-manageFeedbackPdfLink's `mode` (Q23, plan R2.7／R3.3): STOP = 停止連結, REGENERATE =
   * 重新產生連結 (only after a stop). Mirror of functions BUSINESS_RULES.FEEDBACK_PDF_LINK_MODES
   * (check-config-mirror.cjs); the website's link-actions composable sends these values.
   */
  FEEDBACK_PDF_LINK_MODES: {
    STOP: 'stop',
    REGENERATE: 'regenerate',
  } as const,

  CLASS_STATUS: {
    NORMAL: '正常',
    CANCELLED: '停課',
    SUBSTITUTE: '代課',
  } as const,

  PHOTO_CONSENT_STATUS: {
    AGREED: '同意',
    DISAGREED: '不同意',
    UNSET: '未設定',
  } as const,

  /**
   * @description Defines the ticket types and keywords used to identify trial classes.
   */
  TICKET_TYPE: {
    MANUAL: '手動新增',
    TRIAL: '體驗',
    REGULAR: '正式生',
    ELECTIVE: '任選',
    /** Keywords used to automatically identify elective-ticket students. */
    ELECTIVE_KEYWORDS: ['任選'],
  } as const,

  ATTENDANCE_STATUS: {
    CHECKED_IN: 'Checked-In',
    ON_LEAVE: 'On Leave',
    /** Retired 2026-09-24 — display-only for old rows; not selectable (FRONTEND_BUSINESS_RULES.ATTENDANCE). */
    ABSENT: 'Absent',
    OTHER: 'Other',
    UNSELECTED: '',
  } as const,

  /** Display labels for attendance check-in status values (used in roster buttons + error toasts). */
  ATTENDANCE_STATUS_LABELS: {
    'Checked-In': '出席',
    'On Leave': '請假',
    Absent: '缺席',
    Other: '其他',
    '': '未選取',
  } as const,

  /** Display labels for the editable attendance fields (used in the failed-save toast). */
  ATTENDANCE_FIELD_LABELS: {
    checkInStatus: '簽到狀態',
    photoConsent: '拍照同意',
    isFirstClass: '首次上課',
  } as const,

  /**
   * @description Co-op class business rules and constants for dual-teacher functionality.
   */
  COOP_CLASSES: {
    STUDENT_COUNT: {
      COOP_SPLIT_RATIO: 0.5, // 50% split for true co-op classes (Lucy+Phyllis)
      SUBSTITUTE_RATIO: 1.0, // 100% for substitute scenarios (Lucy+代課)
    },
    DETECTION: {
      COOP_PATTERN: '*協同', // Pattern in class names to detect co-op classes
      TEACHER_SEPARATOR: '+', // Separator between teacher names (Lucy+Phyllis)
      SUBSTITUTE_KEYWORD: '代課', // Legacy: kept for backwards compatibility
      SUBSTITUTE_KEYWORDS: SUBSTITUTE_KEYWORDS_LIST,
    },
    APPROVAL: {
      DUAL_REQUIRED: true, // Both teachers must approve co-op classes
    },
    CLASS_TYPE: {
      REGULAR: 'regular',
      COOP: 'co-op',
      LEGACY_NULL: null, // Legacy classes with null/undefined classType = regular
    },
    /**
     * @description Helper functions for co-op class detection and handling
     */
    HELPERS: {
      /**
       * 助教紀錄: the ONE normaliser of a class's teacher names — a string → [trimmed]
       * when not blank; an array → its trimmed non-blank strings; anything else → [].
       * Every placeholder rule below reads names through it. Mirrors the backend.
       */
      teacherNameList: (raw: unknown): string[] => toTeacherNameList(raw),
      /**
       * Every teacher on the class is a placeholder (['代課'], ['臨時老師'], or the
       * single string '代課') — no teacher signs it; the server marks its teacher side
       * done by itself. Exact names after trim, mirroring the backend helper.
       */
      isSubstituteOnly: (teacherNames: unknown): boolean => {
        const names = toTeacherNameList(teacherNames);
        return names.length > 0 && names.every(isPlaceholderName);
      },
      /**
       * 助教紀錄 (tasks/todo-substitute-pay.md): one teacher name IS a placeholder —
       * EXACT match (after trim) against SUBSTITUTE_KEYWORDS. 「代課」 ✓, 「臨時老師」 ✓;
       * 「代課五更」, 「Lucy(代課)」 and '' ✗. Not a substring test — the substring
       * helpers below keep their own meaning. Mirrors the backend helper.
       */
      isPlaceholderTeacherName: (name: string): boolean => isPlaceholderName(name),
      /**
       * The class's teacher list holds at least one placeholder name (exact match,
       * see isPlaceholderTeacherName) — the teacher must pick who the 代課 was at
       * 老師核對. A single string counts as a one-name list (teacherNameList). Mirrors
       * the backend.
       */
      classHasPlaceholderTeacher: (teacherNames: unknown): boolean =>
        toTeacherNameList(teacherNames).some(isPlaceholderName),
      isCoopClass: (teacherName: string, classType?: 'regular' | 'co-op' | null): boolean => {
        const keywords = SUBSTITUTE_KEYWORDS_LIST;
        // Explicit co-op type AND contains + but NOT substitute scenario
        return (
          classType === 'co-op' &&
          teacherName.includes('+') &&
          !keywords.some((k) => teacherName.includes(k))
        );
      },
      isSubstituteClass: (teacherName: string): boolean => {
        const keywords = SUBSTITUTE_KEYWORDS_LIST;
        // Contains + and substitute keyword (treat as single teacher)
        return teacherName.includes('+') && keywords.some((k) => teacherName.includes(k));
      },
      isRegularClass: (classType?: 'regular' | 'co-op' | null): boolean => {
        // Explicit regular OR null/undefined (legacy backward compatibility)
        return classType === 'regular' || classType === null || classType === undefined;
      },
    },
  } as const,
};

// 薪資撥款的扣款帳號 — the company account payroll is debited FROM.
// NOT SCHOOL_BANK_INFO: that is the 富邦 account students pay INTO; this is the
// 兆豐 account salaries are paid OUT of. Printed on the 臨櫃薪轉清冊 letterhead
// and repeated on every row of the counter file (欄 A).
// Mirror of functions/src/config/appConfig.ts — keep in sync.
// Declared ABOVE FRONTEND_BUSINESS_RULES because MEGA_EXPORT.UPLOAD_GUIDE reads it
// (a const referenced during that object's initialisation must already exist).
export const PAYROLL_DEBIT_ACCOUNT = {
  bankName: '兆豐銀行',
  branch: '中山分行',
  /** 7-digit 通匯代碼 = bankCode '017' + branchCode '0158' */
  code: '0170158',
  bankCode: '017',
  branchCode: '0158',
  accountNumber: '01509051031',
  accountHolder: '康馨國際企業股份有限公司',
  currency: 'TWD',
} as const;

/**
 * 走薪資流程的請款類型 —— 決定企網銀要上傳到哪一個頁籤。
 * 薪資與一般款項在企網銀是兩個不同頁籤（台幣薪資轉帳／台幣轉帳），
 * 混在同一個檔會上錯頁籤，所以匯出時必須分流。
 *
 * 宣告在 FRONTEND_BUSINESS_RULES 之外，是因為 EXPORT_DESTINATIONS 的兩個目的地都要引用它；
 * 物件字面值內部無法互相參照。對外一律讀 FRONTEND_BUSINESS_RULES.PAYMENT.SALARY_TYPES。
 * Mirror of functions/src/config/appConfig.ts — keep in sync.
 */
const SALARY_TYPES = ['supervisor_payment'] as readonly string[];

/**
 * 費用明細 期末狀態／紙本狀態 的 key 集合（交付包 Wave 2）。Top-level so the label
 * maps below can be checked against them at compile time (see the guards after
 * FRONTEND_BUSINESS_RULES); check-config-mirror resolves the identifier to this
 * literal and compares it byte for byte with the backend copy.
 */
const CPA_PERIOD_END_STATES = ['settled', 'payable', 'non_payable'] as const;
export type PeriodEndState = (typeof CPA_PERIOD_END_STATES)[number];
const CPA_PAPER_STATES = [
  'handed_over',
  'in_hand',
  'awaiting_receipt',
  'chasing',
  'pending_document',
  'closed',
  'untracked',
  'none',
] as const;
export type PaperState = (typeof CPA_PAPER_STATES)[number];

/**
 * 學期 12 堂 → which plan of the CENTRAL price list (signupMessages.ts SIGNUP_PLANS
 * ids; supervisor-editable in 價目表設定, `appSettings/pricing`). Ids, never prices:
 * the price is read through useSignupPricing, so a price change lands in one place.
 *
 * Shared by 候補 推薦訊息 (PROGRAM_WAITLIST.OFFER_TERM_PLAN_IDS, pre-selection) and
 * the 續報通知／轉班繳費 messages (FrontendConfigUtils.termPlanIdForTimeslot) — 續報
 * M2 T18a (plan D-1, review H2). Swimming splits weekday/weekend (平日 is cheaper);
 * FMS splits 親子/獨立.
 */
export const TERM_PLAN_IDS = {
  SWIM_WEEKEND: 'sw-term-we',
  SWIM_WEEKDAY: 'sw-term-wd',
  FMS_PARENT: 'fm-term-pa',
  FMS_SOLO: 'fm-term-solo',
} as const;

export const FRONTEND_BUSINESS_RULES = {
  /**
   * 臨時新增學生 ticket values. Mirrors
   * `BUSINESS_RULES.TICKET_TYPES.ALLOWED_MANUAL_ADD` (enforced by
   * scripts/check-config-mirror.cjs).
   *
   * The server rejects anything outside this list, because the ticket string is
   * what keeps a walk-in off 學期 and therefore off 最後約課日, the seat lock,
   * 續報, auto-archive, 待確認 and 轉班 detection. The choice on screen is the
   * convenience; the server is the gate.
   *
   * 2026-10-11 (plan tasks/todo-manual-add-choices.md D2/D3): 單堂體驗 / 三堂體驗
   * are gone from the choice — nothing in the logic told them apart, only the
   * row's tag did — and new trial walk-ins store the generic 體驗 (tag 體驗生).
   * Rows written before keep their old strings; reads never validate.
   */
  TICKET_TYPES: {
    ALLOWED_MANUAL_ADD: ['手動新增', '體驗'],
    /**
     * The three kinds 課務 picks from (one radio each, none pre-selected):
     * 一般學生 · 首堂體驗 · 體驗（已體驗過）. One choice carries BOTH the stored
     * ticket and the 首堂課 verdict, so the 首堂 decision can never hide behind a
     * dropdown default (PO 2026-10-11). 首堂課 stays a human statement — the
     * system suggests nothing for a hand-added row (PO 2026-09-17). Labels:
     * UI_STRINGS.RESERVED_UNBOOKED.MANUAL_ADD_CHOICE_LABELS; mapping helper:
     * utils/manualAddChoice.ts.
     */
    MANUAL_ADD_CHOICES: [
      { key: 'regular', ticketType: '手動新增', isFirstClass: false },
      { key: 'firstTrial', ticketType: '體驗', isFirstClass: true },
      { key: 'repeatTrial', ticketType: '體驗', isFirstClass: false },
    ],
  },
  /**
   * The statuses 課務 can pick on 簽到表 (the segmented control renders exactly
   * these). Mirrors `BUSINESS_RULES.ATTENDANCE.SELECTABLE_STATUSES`, which the
   * attendance writers enforce (scripts/check-config-mirror.cjs).
   * 缺席 was dropped 2026-09-24 (PO): it had the same effect as 請假／其他, and a
   * no-show without leave consumes a class, so it is marked 出席. Old 缺席 rows
   * still display through ATTENDANCE_STATUS_LABELS.
   */
  ATTENDANCE: {
    SELECTABLE_STATUSES: ['Checked-In', 'On Leave', 'Other'],
  },
  /**
   * BF (BookFastPOS) booking-email reconciliation — frontend display rules for the
   * 未上傳約課 surface in the 待確認 panel. Read-only verification aid; Excel stays
   * the sole source of truth. Backend mirror: BUSINESS_RULES.BF_EVENTS.
   */
  BF_EVENTS: {
    URGENCY: {
      /** Class within this many days (Taipei) → 近期 (prominent); beyond → 較遠期. */
      NEAR_DAYS: 3,
    },
    /**
     * 未上傳約課 inbox near-term window (Taipei class-date): today + tomorrow +
     * day-after (今/明/後). Admin uploads every 1–2 days, so only these 3 days need
     * pre-upload reconciliation. The resident BF listener covers classes in
     * [today, today+WINDOW_DAYS) Taipei and feeds the inbox + chip. Never hardcode
     * the day count in templates — derive it from here.
     */
    WINDOW_DAYS: 3,
  },
  /** Payslip PIN protection — per-employee PIN for viewing salary data on shared devices. */
  PAYSLIP_PIN: {
    LENGTH: 4,
    MAX_ATTEMPTS: 5,
    LOCKOUT_MINUTES: 30,
    INACTIVITY_TIMEOUT_MS: 2 * 60 * 1000, // 2 minutes
    LOCKOUT_STORAGE_KEY: 'payslip_pin_lockout',
  },
  PAYMENT: {
    // MIRROR: functions BUSINESS_RULES.PAYMENT.REQUEST_CREATOR_ROLES — who may file
    // a 請款. Drives the payment routes (routes.ts) and canCreatePaymentRequest.
    // 救生員 added 2026-09-16 (PO: same 請款 rights as 一般員工, mobile + desktop).
    // Plain literals (not SYSTEM_CONSTANTS refs) so check-config-mirror.cjs can
    // compare this list against the backend's — the same convention as the other
    // mirrored role lists (TEACHER_MOBILE.*, TOOLS.TIMER_ROLES).
    REQUEST_CREATOR_ROLES: [
      'supervisor',
      'admin',
      'teacher',
      'treasury',
      'staff',
      'lifeguard',
    ] as readonly string[],
    /**
     * 請款單「編輯」按鈕的目的地，依請款類型分流。`{id}` 由
     * `FrontendConfigUtils.paymentEditPath()` 代換 —— 每個呈現編輯入口的畫面
     * （RequestDetailPage / RequestDetailViewSection）都讀這一份，避免各自寫死路徑
     * 而分歧（差旅費原本只在其中一處分流）。
     */
    EDIT_ROUTE_BY_TYPE: {
      travel_expense: '/payment/travel/{id}/edit',
      customer_refund: '/payment/refund/{id}/edit',
    } as Record<string, string>,
    /** 未列於 EDIT_ROUTE_BY_TYPE 的類型一律走通用請款表單。 */
    EDIT_ROUTE_DEFAULT: '/payment/requests/{id}/edit',

    /** mirrors functions BUSINESS_RULES.PAYMENT.REQUESTER_EDITABLE_STATUSES —
     * the statuses in which the original requester may still edit their request */
    REQUESTER_EDITABLE_STATUSES: ['draft', 'returned'] as readonly string[],
    /** Default lookback window for "My Requests" list — must match backend HISTORY_MONTHS */
    HISTORY_MONTHS: 3,
    /** NT$ — soft warning shown when total amount exceeds this threshold on submit */
    AMOUNT_WARNING_THRESHOLD: 50000,
    /** Taiwan business tax rate (5%) — mirrors functions BUSINESS_RULES.PAYMENT.TAX_RATE (utils/itemTax.ts). */
    TAX_RATE: 0.05,
    /** Milliseconds between auto-save attempts on the request form */
    AUTO_SAVE_INTERVAL_MS: 30000,
    /** Taiwan bank account number format: 8-16 digits (covers postal/credit union accounts) */
    BANK_ACCOUNT_NUMBER_PATTERN: /^\d{8,16}$/,
    /** Max bank proof documents per payment request */
    MAX_BANK_DOCUMENTS_PER_REQUEST: 3,
    /**
     * 廠商銀行帳號變更防護 —— 變更依據與相關門檻（PO 定案 2026-09-17）。
     *
     * **誠實定位（寫 UI 文案前必讀）**：變更依據**擋不住變更匯款帳號詐騙**。
     * 它記錄的是「已經被騙的那個人」自己的說法 ——「廠商來函」通常是真的，
     * 那封偽造的信確實寄到了。這個功能提供的是**證據**與**錢匯出去前的
     * 第二雙眼睛**，不是驗證。任何文案都不得暗示系統驗證過帳號的真偽。
     *
     * Mirror of functions/src/config/appConfig.ts — keep in sync.
     */
    BANK_CHANGE: {
      /** 變更依據下拉選項 —— 三選一，外加選填備註。 */
      REASONS: [
        { value: 'letter', label: '廠商來函／email' },
        { value: 'callback', label: '電話回撥確認' },
        { value: 'internal', label: '內部更正' },
        { value: 'bounce_correction', label: '退匯後更正' },
        { value: 'other', label: '其他（請於備註說明）' },
      ] as readonly { value: string; label: string }[],
      /**
       * 改到這些欄位就必須填變更依據。
       *
       * **通匯代碼在列**：兆豐依 7 碼通匯代碼路由，只改代碼一樣會把錢送到
       * 別的地方 —— 只看 bankAccountNumber 會整條漏掉。
       */
      REASON_REQUIRED_ON: ['bankAccountNumber', 'bankCode', 'branchCode'] as readonly string[],
      /** 廠商名冊列上「N 日內變更帳號」標記的天數門檻。 */
      RECENT_CHANGE_DAYS: 30,
      /**
       * 「N 日內新建」標記的天數門檻。
       *
       * 針對「停用舊廠商＋同名新建」這條繞過路徑（AUDIT_ACCEPTED_RISKS PB2）：
       * 系統擋不住它，但一個剛冒出來的同名廠商在名冊上看得見，就不再是無聲的。
       */
      RECENT_CREATED_DAYS: 30,
      /**
       * 銀行轉帳匯出把 `unverified` 廠商列排除時的原因字串（2026-09-19）。
       * 前端用它把這些列從「資料不完整」分出來，改成就地核對的區塊 —— 所以字串
       * 必須前後端同一份，不能各打一次。勾選＝某人說他把帳號和存摺對過了，不是
       * 系統驗證，文案不得暗示系統驗證過帳號。
       */
      NEEDS_CHECK_EXPORT_REASON: '銀行帳戶待核對：付款前請先核對銀行資料與證明文件',
    },
    /**
     * 兆豐銀行薪資轉帳匯出限制 — mirror of functions/src/config/appConfig.ts.
     * Stricter than BANK_ACCOUNT_NUMBER_PATTERN above: 兆豐 caps the transfer
     * account at 14 digits, so a 15-16 digit account is storable but not exportable.
     */
    MEGA_EXPORT: {
      /** E 欄 轉入帳號 — 最多 14 位數字 */
      ACCOUNT_MAX_LEN: 14,
      /**
       * 收款人姓名字數上限 —— **臨櫃清冊 ONLY**（GPA0230 Excel 範本 B 欄，39 個中文字
       * ／78 英數）。企網銀「國內匯款檔」不用這個值：那支檔案以 **Big5 位元組** 計長
       * （見 DOMESTIC_REMITTANCE_FILE.PAYEE_NAME_MAX_BYTES = 60），字數上限在那裡沒有意義。
       */
      PAYEE_NAME_MAX_CN: 39,
      /** F 欄 付款金額 — 最長 13 位整數 */
      AMOUNT_MAX_DIGITS: 13,
      /** 銀行代號 3 碼 + 分行代號 4 碼 = 7 碼通匯代碼 */
      BANK_CODE_LEN: 3,
      BRANCH_CODE_LEN: 4,
      /** I 欄 摘要 — 最多 5 個中文字或 10 個英數字（臨櫃清冊 ONLY，見 MEMO_BY_TYPE） */
      MEMO_MAX_CN: 5,
      /**
       * 摘要 by request type —— **臨櫃清冊 and the EXCEL薪資轉帳檔 I 欄（自訂摘要）**，
       * never the 國內匯款檔. Blank means blank — deliberately. In the EXCEL file the
       * memo prints on the passbook only when 【轉帳】 is chosen in the upload page's 摘要欄.
       *
       * ⚠ 企網銀「國內匯款檔」**禁止**帶附言：規格頁第 6 欄原文「GEB 此欄位僅供通知用，
       * 請勿輸入附言資料」，所以上傳檔一律留空白，不得讀這張表
       * （docs/MEGA_BANK_FILE_FORMATS.md §3 欄 6、§4）。改走企網銀之後，員工存摺上就不會
       * 再出現「康馨薪資」——這是銀行規格造成的，不是漏做。
       *
       * PO 2026-09-03: only 薪資 and 退費 get a label. 「貨款」 would be plain wrong
       * on rent, maintenance or a service fee, and a WRONG 摘要 is worse than an
       * empty one — it lands in the recipient's permanent bank record. Reconciliation
       * is done from the 核對表 (which carries 請款單號), never from a 4-character
       * passbook label.
       *
       * No month in the salary memo: bonuses also go through 薪資請款, so one person
       * can be paid twice in a month and a month label would eventually be false.
       *
       * ⚠ The 摘要 only reaches 兆豐 recipients (GPA0230 template, I 欄 comment:
       * 「收款人本行存摺的摘要欄才會顯示本欄資訊」). Cross-bank recipients likely
       * never see it. Both values are 4 中文字, inside the 5-char cap.
       * Mirror of functions/src/config/appConfig.ts.
       */
      MEMO_BY_TYPE: {
        supervisor_payment: '康馨薪資',
        customer_refund: '康馨退費',
      } as Record<string, string>,
      /**
       * 兆豐全球金融網「國內匯款檔」固定長度記錄格式。
       * SSOT: docs/MEGA_BANK_FILE_FORMATS.md §3（規格頁逐欄）＋ §4（轉檔程式 VBA 補充）。
       * 台幣轉帳／台幣薪資轉帳兩個頁籤的規格完全相同，所以只有這一份。
       *
       * `start` 是規格頁的 1-based 起始位元組；`len` 是位元組長度（**不是字數**）。
       * 數字欄右靠左補 "0"，其餘左靠右補 " "。無表頭、無表尾。
       * 檔案本身在後端組（Big5 需要 iconv-lite）；前端這份是給畫面顯示規格用的。
       * Mirror of functions/src/config/appConfig.ts — keep in sync.
       */
      DOMESTIC_REMITTANCE_FILE: {
        /** 每列固定 164 bytes（7+13+14+60+10+60）。 */
        RECORD_LENGTH: 164,
        /** 中文一律 Big5（轉檔程式以 CP950 ANSI 寫檔）。 */
        // cp950 (Windows ANSI Big5) — what the bank's converter writes; mirror of backend.
        ENCODING: 'cp950',
        LINE_ENDING: '\r\n',
        /** 預設檔名是 Big5.dat；.dat 與 .txt 皆可，我們用 .txt。 */
        FILE_EXTENSION: '.txt',
        HAS_HEADER: false,
        FIELDS: [
          /** 解款行代碼 = 收款人 bankCode(3) + branchCode(4)。 */
          { key: 'bankCode7', start: 1, len: 7, align: 'left', pad: ' ' },
          /** 解款金額 = 11 位整數 + 2 位小數，右靠左補 0。 */
          { key: 'amount', start: 8, len: 13, align: 'right', pad: '0' },
          /** 收款人帳號，右靠左補 0（017 例外見後端 SAME_BANK_ACCOUNT_MODE）。 */
          { key: 'account', start: 21, len: 14, align: 'right', pad: '0' },
          /** 收款人姓名，以 Big5 位元組計長，左靠右補空白。 */
          { key: 'payeeName', start: 35, len: 60, align: 'left', pad: ' ' },
          /** 收款人電話（可空白）——我們一律留空。 */
          { key: 'phone', start: 95, len: 10, align: 'left', pad: ' ' },
          /** 附言（可空白）——銀行規定勿填，一律留空（見 MEMO_BY_TYPE）。 */
          { key: 'memo', start: 105, len: 60, align: 'left', pad: ' ' },
        ],
        /** 金額整數位數（PADL(整數, 11, "0")）。 */
        AMOUNT_INTEGER_DIGITS: 11,
        /** 金額小數位數（一律 "00"，我們沒有角分）。 */
        AMOUNT_DECIMAL_DIGITS: 2,
        /** 收款人姓名以 Big5 位元組計長：3 個中文字 = 6 bytes + 54 空白。 */
        PAYEE_NAME_MAX_BYTES: 60,
        /** 金資跨行單筆上限；超過銀行端會拆單。我方金額遠低於此，超過就整筆排除並具名。 */
        FISC_MAX_SINGLE_TRANSFER: 50000000,
      },
      /**
       * 戶名（拆字）(PO 2026-09-11) — see the backend block for the full rule.
       * The form reads LABEL / REQUIRED; the export dialog reads EXPORT_NOTE.
       * Mirror of functions/src/config/appConfig.ts — keep in sync.
       */
      UPLOAD_NAME: {
        LABEL: '戶名（拆字）',
        REQUIRED: '戶名的「{chars}」無法寫入企網銀上傳檔，請填拆字寫法（例：甄培(文三撇)）',
        UNENCODABLE: '戶名（拆字）仍含無法寫入的字「{chars}」，請改用其他寫法',
        TOO_LONG: '戶名（拆字）過長（上限 60 bytes，約 30 個中文字），請縮短',
        EXPORT_NOTE: '以戶名（拆字）「{name}」寫入，兆豐將人工處理，不需另外處理',
        EXCLUDED_FILL: '戶名的「{chars}」無法寫入，請至 {where} 填寫 戶名（拆字）',
        EXCLUDED_NAME_CHANGED:
          '戶名的「{chars}」無法寫入，且本單戶名與 {where} 的戶名不同，無法套用 戶名（拆字）；請把本單戶名改成與 {where} 相同，或改用臨櫃清冊',
        EXCLUDED_ACCOUNT_CHANGED:
          '戶名的「{chars}」無法寫入，且本單帳號與 {where} 的帳號不同，無法套用 戶名（拆字）；請改用臨櫃清冊，或先核對帳號',
        EXCLUDED_NO_MASTER:
          '戶名的「{chars}」無法寫入企網銀上傳檔，此單未連結員工或支付對象資料，請改用臨櫃清冊',
        /**
         * 薪資批次（EXCEL薪資轉帳檔）only: the row carries NO 戶名（拆字） and is
         * therefore written with B 收款人姓名 BLANK, matched by the C 身分證號
         * alone (PO 2026-09-13 — a 拆字 is written to B whenever one exists).
         */
        SALARY_ID_NOTE: '戶名無法寫入且未填戶名（拆字），本筆以身分證號比對',
        CONTROL_CHARS: '戶名（拆字）不可含換行或控制字元',
        /** Input cap for 戶名 / 戶名（拆字） on master-record saves (characters). */
        MAX_INPUT_CHARS: 100,
        INPUT_TOO_LONG: '戶名或戶名（拆字）過長（上限 100 字）',
        WHERE_TO_FIX: {
          employees: '員工管理',
          payees: '廠商與定期款項',
        } as Record<string, string>,
      },
    },
    /**
     * 匯出時**不加警示**的請款單狀態。'approved' = 已核准尚未付款，正常的匯出時點。
     *
     * ⚠ 這份清單不再定義「哪些單會被撈進來」——候選集合由後端 treasuryCandidates.ts
     * 決定（已核准 ∪ 已付款但未放行）。已付款未放行的單本來就會被撈進來並帶警示，
     * 所以舊名 SALARY_EXPORT_STATUSES 會讓人以為它是候選條件（P19，2026-09-07 改名）。
     */
    EXPORT_NO_WARNING_STATUSES: ['approved'] as readonly string[],
    SALARY_TYPES,
    /**
     * 銀行轉帳匯出的三個目的地。臨櫃清冊是既有的 Excel；兩個 web_* 是企網銀上傳檔：
     * 一般款項＝「國內匯款檔」(.txt)，薪資批次＝「EXCEL薪資轉帳檔」(.xlsx，可帶
     * 身分證號，PO 2026-09-11)。
     *
     *  - `tab` / `tabEn`: 企網銀頁籤名稱（中／英介面）。counter 沒有頁籤。
     *  - `uploadSpec`: 頁籤裡「上傳檔案規格」要選的項目。
     *  - `fileFormat`: 後端要組哪一種檔（domestic_remittance | salary_excel）。
     *  - `defaultUntickedTypes`: 撈進來但**預設不勾**的類型（仍可手動勾選納入）。
     *  - `onlyTypes`: 只允許這些類型（其餘一律不得納入此檔）。
     *
     * 分組與勾選預設值由後端計算後回傳（groups[]），前端只負責畫，
     * 所以這張表前後端都要有。SSOT: docs/MEGA_BANK_FILE_FORMATS.md §1、§2。
     * Mirror of functions/src/config/appConfig.ts — keep in sync.
     */
    EXPORT_DESTINATIONS: {
      counter: { label: '臨櫃清冊', tab: null },
      web_general: {
        label: '企網銀上傳檔 — 一般款項',
        tab: '台幣轉帳',
        tabEn: 'TWD',
        uploadSpec: '國內匯款檔',
        fileFormat: 'domestic_remittance',
        defaultUntickedTypes: SALARY_TYPES,
      },
      web_salary: {
        label: '企網銀上傳檔 — 薪資批次',
        tab: '台幣薪資轉帳',
        tabEn: 'TWD Salary',
        uploadSpec: 'EXCEL薪資轉帳檔',
        fileFormat: 'salary_excel',
        onlyTypes: SALARY_TYPES,
      },
    },
    /**
     * 每個目的地在企網銀裡的完整路徑，直接顯示在匯出視窗的選項底下。
     * 出納一邊看畫面一邊點，路徑寫死在元件裡就會跟銀行改版脫節。
     * counter = 臨櫃送件，沒有企網銀路徑，所以是 null。
     * 前端專用（後端不畫畫面）。SSOT: docs/MEGA_BANK_FILE_FORMATS.md §2。
     */
    DESTINATION_ROUTE: {
      counter: null,
      web_general: '收付款 » 轉帳付款 » 付款檔案上傳 » 台幣轉帳 » 上傳檔案規格 國內匯款檔',
      web_salary: '收付款 » 轉帳付款 » 付款檔案上傳 » 台幣薪資轉帳 » 上傳檔案規格 EXCEL薪資轉帳檔',
    } as Record<string, string | null>,
    /**
     * 企網銀上傳步驟 —— 匯出檔案後直接顯示在畫面上的操作指引。
     * 出納拿到 .txt 之後還要在銀行網站走完這一串，寫在這裡才不會散落在元件裡。
     * 前端專用。SSOT: docs/MEGA_BANK_FILE_FORMATS.md §2。
     */
    UPLOAD_GUIDE: {
      /** 檔名格式：由後端產生後回傳，這裡只是給畫面說明用。 */
      FILE_NAME_PATTERN: '康馨_國內匯款檔_{destination}_{YYYYMMDD}.txt',
      /** 第一頁：選頁籤與檔案規格。{tab} 由 EXPORT_DESTINATIONS 的 tab／tabEn 代入。 */
      STEPS: [
        {
          zh: '登入兆豐全球金融網 » 收付款 » 轉帳付款 » 付款檔案上傳',
          en: 'Payment & Collection » Payment » Payment data upload',
        },
        {
          zh: '選頁籤：{tab}',
          en: 'Tab: {tabEn}',
        },
        {
          // {uploadSpec} = EXPORT_DESTINATIONS[destination].uploadSpec
          zh: '上傳檔案規格選「{uploadSpec}」» 確認',
          en: 'Upload file form » Confirm',
        },
        {
          zh: '第二頁：填帳戶資訊、選擇檔案後按「檔案上傳」（欄位見下）',
          en: 'Second page: account information, choose the file, press Upload file',
        },
        {
          zh: '檔案分兩階段處理：先回覆上傳成功，系統再事後檢查內容；到「查詢檔案檢查結果」核對筆數與總額，確認後才執行整批轉帳',
          en: 'Two stages: upload succeeds first, the bank checks the content later; review the checking result (rows, total) and confirm the batch transfer',
        },
        {
          // {date} = the 預計付款日期 chosen at export (the file's own date).
          zh: '核對無誤後回 出納作業 » 安排付款（預計付款日期填 {date}）» 放行',
          en: 'Then release the batch',
        },
      ],
      /** 上傳指引數字列下方：到企網銀要核對什麼。 */
      CHECK_RESULT_HINT: '上傳後在企網銀「查詢檔案檢查結果」比對筆數與總額。',
      /** 薪資批次（EXCEL薪資轉帳檔）：B 收款人姓名、C 身分證號 是兆豐的核對欄位。 */
      SALARY_BANK_CHECK:
        '收款人姓名／身分證號由兆豐用來核對收款帳戶戶名；「查詢檔案檢查結果」如有不符，請至 員工管理 核對該員工的戶名與身分證／居留證號。',
      /** 薪資批次、檔案內有身分證號時：個資提醒。 */
      SALARY_DELETE_FILE: '檔案含身分證號，上傳完成後請刪除本機檔案。',
      /**
       * 第二頁（按「確認」之後那一頁）的欄位，中文依 PO 2026-09-08 截圖逐字抄錄。
       * 只說明「上傳機制需要哪些欄位」，不指定要選哪個付款帳號 —— 帳戶是人的決定，
       * 而且 國內匯款檔 裡沒有付款方帳號，檔案不會要求對應（PO 2026-09-08）。
       * `onlyFor` 限定只在該目的地顯示；缺省＝兩個頁籤都有。
       */
      SECOND_PAGE: [
        { labelZh: '付款戶名', labelEn: 'Payer', note: '下拉選公司' },
        { labelZh: '付款銀行', labelEn: 'Bank', note: '下拉選付款分行' },
        {
          labelZh: '付款帳號',
          labelEn: 'A/C No.',
          note: '下拉選要扣款的帳戶（可先按「可用餘額查詢」）',
        },
        {
          labelZh: '付款日期',
          labelEn: 'Payment date',
          note: '預計付款日',
          // The guide replaces `note` with SCHEDULED_DATE.GUIDE_NOTE and the
          // date chosen at export — this flag, not the label text, finds the row.
          isPaymentDate: true,
        },
        { labelZh: '手續費負擔別', labelEn: 'Charge by', note: '付款人負擔／Remitter' },
        {
          labelZh: '摘要欄',
          labelEn: 'Memo',
          // {memo} = MEGA_EXPORT.MEMO_BY_TYPE 的薪資摘要
          note: '要讓存摺顯示「{memo}」請選【轉帳】（僅兆豐本行帳戶的存摺會顯示）；不選則顯示「薪資」',
          onlyFor: 'web_salary',
        },
        {
          labelZh: '上傳檔案規格',
          labelEn: 'Upload file form',
          note: '已顯示「{uploadSpec}」，不用再選',
        },
        { labelZh: '上傳檔案', labelEn: 'Upload file', note: '選擇本頁下載的檔案' },
        { labelZh: '備註、上傳通知檔', labelEn: 'Remarks, Upload notify file', note: '免填' },
        { labelZh: '交易流程選擇', labelEn: 'Select trade process', note: '一般流程（預設流程）' },
        {
          labelZh: '按「檔案上傳」',
          labelEn: 'Upload file button',
          note: '送出後等「檔案上傳成功」',
        },
      ] as ReadonlyArray<{
        labelZh: string;
        labelEn: string;
        note: string;
        onlyFor?: string;
        isPaymentDate?: boolean;
      }>,
      /**
       * 預計付款日期 —— 企網銀上傳檔 only (PO 2026-09-11). The 國內匯款檔 has no date
       * column: the bank asks for it on the upload's second page. Choosing it in
       * the export dialog puts one agreed date in front of the operator, and the
       * guide repeats it at the step where it is typed. 臨櫃清冊 keeps its
       * handwritten 撥款日期 line (a counter visit is same-day).
       * Label reuses 安排付款's field name — one name for one date.
       * Not sent to the server: the file and the 個資 log are unchanged.
       */
      SCHEDULED_DATE: {
        LABEL: '預計付款日期',
        HINT: '上傳檔不含日期；上傳時請於企網銀第二頁「付款日期」填入同一天。',
        REQUIRED: '請選擇預計付款日期。',
        OUT_OF_RANGE: '預計付款日期須在今天至 {days} 天內。',
        /** `{date}` is rendered in bold by BankExportUploadGuide. */
        GUIDE_NOTE: '請填 {date}（與匯出時選的預計付款日期相同）',
        // 期限 of the ticked rows vs the chosen date (PO 2026-09-11: treasury
        // decides the date here as it does at 安排付款). Informs, never blocks.
        // 「期限」 = the 待付款 card's word for paymentTermDueDate.
        DUE_RANGE: '勾選中有期限的 {count} 筆：期限 {from} ～ {to}',
        DUE_RANGE_SINGLE: '勾選中有期限的 {count} 筆：期限皆為 {from}',
        DUE_LATE:
          '勾選中有 {count} 筆的期限早於預計付款日期。可改日期，或取消勾選這幾筆後另行匯出。',
        /** Button — 「帶入」 as in 安排付款's 「直接帶入」. */
        APPLY_EARLIEST: '帶入最早期限 {date}',
        /** Same button when the earliest 期限 has already passed. */
        APPLY_TODAY: '帶入今天 {date}',
        ROW_DUE: '期限 {date}',
        ROW_LATE_TITLE: '期限早於預計付款日期',
        ROW_OVERDUE_TITLE: '已過期限',
      },
      /**
       * 上傳指引底部的「存摺摘要」面板，依目的地各一份 (PO 2026-09-11)。
       *  - web_general＝國內匯款檔：附言欄由銀行保留，帶不出自訂摘要。
       *  - web_salary＝EXCEL薪資轉帳檔：「空白或自訂摘要」欄已填 {memo}，但只有兆豐（本行）
       *    帳戶的存摺會顯示（範本 I 欄註解「收款人本行存摺的摘要欄才會顯示本欄資訊」），
       *    且第二頁「摘要欄」須選【轉帳】；不選則為「薪資」。
       * {memo} = MEGA_EXPORT.MEMO_BY_TYPE 的薪資摘要；{uploadSpec} = EXPORT_DESTINATIONS 的上傳檔案規格。
       */
      MEMO_PANEL: {
        web_general: {
          title: '存摺摘要（康馨退費等自訂文字）不會出現在這個檔案',
          body: '{uploadSpec}的附言欄由銀行保留、規定不得填寫，只有臨櫃清冊帶得出自訂摘要。核對請以核對表的請款單號為準。',
        },
        web_salary: {
          title: '存摺摘要：第二頁「摘要欄」選【轉帳】，兆豐（本行）帳戶的存摺才會顯示「{memo}」',
          body: '{uploadSpec}的自訂摘要欄已填「{memo}」。只有兆豐（本行）帳戶的存摺會顯示這個摘要，且第二頁「摘要欄」須選【轉帳】；不選則為「薪資」。核對請以核對表的請款單號為準。',
        },
      } as Record<string, { title: string; body: string }>,
      /** 上傳指引數字列 —— EXCEL薪資轉帳檔 中帶身分證號的筆數（只顯示筆數，從不顯示號碼）。 */
      PERSONAL_ID_COUNT: '含身分證號 {n} 筆',
    },
    /**
     * 匯出對話框候選清單的用字 —— 前端專用（不鏡射）。
     */
    EXPORT_LIST: {
      /**
       * 薪資批次：勾選中有／沒有身分證號的筆數。全部都有時改用
       * UPLOAD_GUIDE.PERSONAL_ID_COUNT。
       */
      SALARY_ID_SUMMARY: '含身分證號 {n} 筆；未填 {m} 筆以戶名比對（可至 員工管理 補填）',
      /** 「怎麼補？」：未納入原因是 戶名（拆字）／身分證號 時加這一行。 */
      HOWTO_UPLOAD_NAME: '戶名（拆字）、身分證號請依上方說明到該頁填寫，不要修改請款單的戶名。',
    },
    /**
     * 廠商帳戶核對（勾選即存檔、匯出對話框／待付款卡片就地核對）的畫面用字 ——
     * 前端專用（不鏡射）。標籤在 REPAYMENT.CHIP_LABELS.bankNeedsCheck、匯出原因在
     * BANK_CHANGE.NEEDS_CHECK_EXPORT_REASON（皆鏡射）。
     *
     * 紅線：勾選＝某人說他把帳號和存摺對過了，不是系統驗證。這裡任何字都不得暗示
     * 系統驗證過帳號（不出現「已驗證」「確認無誤」「安全」）；「驗證」一詞在本系統
     * 屬於憑證／發票，這裡一律用「核對」。
     */
    BANK_CHECK_UI: {
      /** 付款面板、批次付款、就地核對面板共用的同一句 —— 清除標籤的動作。 */
      CONFIRM_LABEL: '已核對銀行資料與證明文件',
      /** 勾選當下已寫入伺服器（不必付款）；沒有取消核對的動作。 */
      SAVED_HINT: '核對已記錄，離開視窗也不會失效',
      /** 勾選後寫入中 —— 送出鍵在寫入成功前不開。 */
      SAVING_HINT: '核對記錄儲存中，請稍候',
      /** 名冊廠商沒有上傳存摺影像 —— 仍允許核對（人手上可能有紙本），但要說清楚。 */
      NO_DOCUMENTS: '查無存摺影像',
      NO_DOCUMENTS_HINT: '系統內沒有這個廠商的存摺影像；如已用紙本或其他方式核對，仍可按下確認。',
      /**
       * 手動輸入的廠商（不在名冊）＋ 請款單沒有附證明文件 —— 伺服器會拒絕核對
       * （`verifyBankDetails` Path A 要求單上至少一份文件），所以這裡直接擋住並
       * 說明出路，不讓人按一顆必定失敗的按鈕。名冊廠商不受此限。
       */
      NO_DOCUMENTS_BLOCKED:
        '此請款單未附帳戶證明文件，系統不接受核對。請退回請款單補附存摺影像，或先將此廠商加入名冊。',
      /** 匯出對話框的區塊標題。{n}／{total} 由呼叫端填入。 */
      SECTION_TITLE: '銀行帳戶待核對 {n} 筆 · NT${total} —— 在這裡核對後即可納入本檔',
      /** 該列除了待核對還有別的原因 —— 核對完仍會留在「資料不完整」。 */
      OTHER_REASONS: '另有：{reasons}',
      /** 核對成功後的提示。{name} = 支付對象。 */
      CONFIRMED_TOAST: '已記錄核對：{name}',
    },
    /**
     * 員工／支付對象 銀行欄位的按鈕與提示字 —— 前端專用（不鏡射）。規則與錯誤訊息在
     * MEGA_EXPORT.UPLOAD_NAME 與 PERSONAL_ID（前後端鏡射），這裡只放畫面用字。
     * 取消鍵依「取消 + 被取消的動作」命名：更換 → 取消更換、清除 → 取消清除。
     */
    BANK_FORM: {
      UPLOAD_NAME_PLACEHOLDER: '例：甄培(文三撇)',
      /** 檢查戶名的呼叫失敗 → 戶名（拆字）改為選填並顯示這句（伺服器儲存時仍會檢查）。 */
      UPLOAD_NAME_CHECK_FAILED:
        '無法確認戶名能否寫入企網銀上傳檔；如有無法寫入的字，請填戶名（拆字），儲存時會再檢查',
      /** 戶名改了，原本的拆字是舊戶名的寫法 → 已清空。 */
      UPLOAD_NAME_STALE: '戶名已修改，請重新填寫戶名（拆字）',
      /** 既有資料缺拆字、本次未改戶名 → 只提醒，不擋儲存。{chars} = 無法寫入的字。 */
      UPLOAD_NAME_LEGACY:
        '戶名的「{chars}」無法寫入企網銀上傳檔，需填戶名（拆字）才能列入；不影響本次儲存',
      /** 帳號格式（BANK_ACCOUNT_NUMBER_PATTERN）— 與伺服器訊息相同。 */
      ACCOUNT_FORMAT_INVALID: '銀行帳號格式不正確，請輸入 8-16 位數字',
      /** 帳號可儲存但超過企網銀上傳檔上限（MEGA_EXPORT.ACCOUNT_MAX_LEN）→ 警告。 */
      ACCOUNT_OVER_WEB_LIMIT: '帳號超過 {max} 位，無法寫入企網銀上傳檔；付款時請改用臨櫃清冊',
      PERSONAL_ID_PLACEHOLDER: '選填，例：A123456789',
      /** 「更換」後的空白輸入框。 */
      PERSONAL_ID_REPLACE_PLACEHOLDER: '輸入新號碼；不更換請按「取消更換」',
      PERSONAL_ID_REPLACE: '更換',
      PERSONAL_ID_CANCEL_REPLACE: '取消更換',
      PERSONAL_ID_CLEAR: '清除',
      PERSONAL_ID_CANCEL_CLEAR: '取消清除',
      PERSONAL_ID_WILL_CLEAR: '將於儲存時清除',
    },
    /**
     * 帳號前綴 ⇄ 分行代號 交叉檢查規則（僅警告，不擋存檔）。
     *
     * Some banks embed the branch code in the account number. Where they do, the
     * account number is a MORE reliable branch signal than the stored branch name,
     * because an account number cannot go stale but a branch name can.
     *
     * ⚠ INFERRED from our own stored accounts, not documented by the banks. Hence:
     * warn only — never auto-fill, never block. Banks not listed here return
     * 'unknown' and produce no warning. A rule stays here only while EVERY real
     * record of that bank obeys it.
     *
     * 2026-09-12 re-check (read-only, all employees + payees + request snapshots):
     *  - 兆豐 017: 11 of 11 accounts start with the branch code's first 3 digits
     *    (the 4th digit is a check digit) → kept.
     *  - 富邦 012: only 1 of 7 accounts starts with its 4-digit branch code — the
     *    single 2026-09-03 case (`7521…` ↔ 0127521) this rule was generalised from;
     *    the other six do not (14-digit accounts start `00…`). REMOVED (PO 2026-09-12:
     *    "I checked Fubon accounts, they are not like that").
     *
     * `takeAccount` / `takeBranch` = how many leading digits to compare.
     */
    BANK_ACCOUNT_PREFIX_RULES: {
      '017': { takeAccount: 3, takeBranch: 3, label: '兆豐' },
    } as Record<string, { takeAccount: number; takeBranch: number; label: string }>,
    /** Fallback company tax ID used when payment config is not yet loaded */
    DEFAULT_COMPANY_TAX_ID: '42839964',
    /** NT$ — default petty cash replenishment target (must match backend) */
    DEFAULT_PETTY_CASH_TARGET: 10000,
    /** Default value for isBfTransaction on the refund request form */
    REFUND_DEFAULT_IS_BF_TRANSACTION: true,
    /** Default value for hasCreditNote on the refund request form */
    REFUND_DEFAULT_HAS_CREDIT_NOTE: true,
    /** Billing period range limits (months from current month) — must match backend */
    BILLING_PERIOD_FUTURE_MONTHS: 6,
    BILLING_PERIOD_PAST_MONTHS: 12,
    /** Contract date range for recurring bills — wider than billing period for long contracts */
    CONTRACT_RANGE_FUTURE_MONTHS: 24,
    /** Maximum rows returned by query — must match backend QUERY_LIMITS.PAYMENT_REQUESTS */
    QUERY_LIMITS: {
      PAYMENT_REQUESTS: 1000,
    },
    /** Maximum date range span for the GA invoice-verification date picker (~3 months in ms) */
    MAX_DATE_RANGE_SPAN_MS: 92 * 24 * 60 * 60 * 1000,
    /**
     * Maximum date range span for the treasury 付款紀錄 picker (~12 months in ms).
     * Wider than the invoice picker because bookkeeping and CPA reconciliation
     * work in whole years. Cost is negligible at current volume (~400 requests
     * a year ≈ 400 reads per full-year fetch); the row cap above stays as the
     * safety net and the table warns when it is reached.
     */
    HISTORY_MAX_DATE_RANGE_SPAN_MS: 366 * 24 * 60 * 60 * 1000,
    /** Default lookback for the treasury 付款紀錄 date picker, in months. */
    HISTORY_DEFAULT_RANGE_MONTHS: 6,
    /**
     * Days of FORWARD reach in the treasury 付款紀錄 default range.
     *
     * Treasury may complete a payment with a payment date up to
     * PAYMENT_DATE_MAX_FUTURE_DAYS (30) ahead — a scheduled bank transfer. Such
     * a request is already `paid`, so it has left the 待付款 queue; if the
     * history range also ended at "today" it was visible on NO treasury surface
     * until its payment date arrived (real case 2026-08-20: C26AU04/05/06,
     * paymentDate 2026-08-24, NT$16,184 total, reported as "missing").
     * Matches the backend PAYMENT_DATE_QUERY_BUFFER_FORWARD_DAYS window, so the
     * default view can never ask for rows the padded query would not return.
     */
    HISTORY_DEFAULT_RANGE_FORWARD_DAYS: 45,
    /**
     * How far ahead a 預計付款日期 may be picked — the typo guard 安排付款 enforces
     * on the server (±30 days). The 匯出銀行轉帳清冊 date picker offers today …
     * today + this, so a date chosen there is always one 安排付款 would accept.
     * Mirror of functions BUSINESS_RULES.PAYMENT.PAYMENT_DATE_MAX_FUTURE_DAYS
     * (check-config-mirror.cjs).
     */
    PAYMENT_DATE_MAX_FUTURE_DAYS: 30,
    /**
     * 公司付款日 —— days of the month the company pays on. 「下一個付款日」 is the
     * first of these strictly AFTER today (Taipei) — on the 5th itself it is the
     * 19th; from the 19th on it is next month's first pay day. Read through
     * `FrontendConfigUtils.nextCompanyPayDate` by 安排付款 / 批次安排付款 (即期 帶入)
     * and the 匯出銀行轉帳清冊 default date; the server derives 即期 期限 from the
     * same days (computePaymentTermDueDate). Ascending order required.
     * Mirror of functions BUSINESS_RULES.PAYMENT.COMPANY_PAY_DAYS (check-config-mirror.cjs).
     */
    COMPANY_PAY_DAYS: [5, 19] as number[],
    /** Default pagination sizes for payment list views */
    PAGINATION_DEFAULT_SIZE: 20,
    PAGINATION_SIZE_OPTIONS: [20, 50, 100] as number[],

    /**
     * CPA 對帳工作表 — mirrors backend BUSINESS_RULES.PAYMENT.CPA_EXPORT.
     * Spec: docs/CPA_RECONCILIATION_SPEC.md.
     *
     * The backend derives 帳別 / 認列日期 / as-of flags and returns them on each
     * row (it holds the raw timestamps and is the source of truth). The frontend
     * uses these only for LABELS and sheet names, never to re-derive the rules —
     * two copies of the classification would drift.
     */
    CPA_EXPORT: {
      /**
       * 性質 column values. internal = 主管專案: personal spend booked under the
       * company, kept out of the company expense figure.
       *
       * Wording (PO 2026-09-08): the earlier 外帳／內帳 labels are the everyday
       * phrase for keeping two sets of books and would read as exactly that to a
       * tax auditor; the export now says what the rows ARE — 公司費用 / 專案 —
       * matching the category name 主管專案. No rule changed, only the words.
       */
      LEDGER_SCOPE_LABELS: {
        external: '公司費用',
        internal: '專案',
        mixed: '混合',
        /**
         * 退費 (customer_refund): cash out, but a reduction of revenue
         * (銷貨退回及折讓), never an expense — kept out of 公司費用合計 and off
         * 費用明細 (PO 2026-09-08).
         */
        refund: '退費',
      } as Record<string, string>,
      /**
       * Sheet names. Order is SHEET_ORDER (交付包 Wave 2, PO 2026-09-20): the
       * accountant's own axis first, the cash book last.
       */
      SHEET_NAMES: {
        cover: '封面說明',
        paymentDetail: '付款明細',
        monthlySummary: '月結摘要',
        accrual: '費用明細',
        changes: '本期異動',
        unpaid: '期末未付明細',
        missingReceipt: '已付未取憑證',
      } as Record<string, string>,
      /** Workbook order, by SHEET_NAMES key (N4). */
      SHEET_ORDER: [
        'cover',
        'accrual',
        'changes',
        'unpaid',
        'missingReceipt',
        'paymentDetail',
        'monthlySummary',
      ] as readonly string[],
      /**
       * 檔名（N4）：公司＋內容＋所屬期間＋產出日（西元以利排序）。
       * {company} ＝ COMPANY_INFO.shortName；{period} 由 formatExportPeriod 決定：
       * 整月 2026-08、整年 2026、其他 起~迄。
       * 產出日前加「產出」二字，才不會被讀成第二個期間（審查 2026-09-20）。
       *
       * 舊的「匯出次數版本」（{versionSegment}）已於 2026-09-21 **刪除**（seal spec §9）：
       * 交付（定版）有自己的版本序（v1／v2…，見 COVER.ROWS.deliveryVersion），兩者同名
       * 不同義，留著必被混用。已交付月份的檔名由 CF 回傳（下載時沿用原檔名）。
       */
      FILE_NAME_TEMPLATE: '{company}_費用與付款明細_{period}_產出{date}.xlsx',
      /**
       * 交付（定版）角色（seal spec §8）。守門在 CF（authMiddleware）；這兩份清單只
       * 決定前端要不要畫出按鈕——藏起來的按鈕不是權限，CF 才是。
       */
      DELIVERY_ROLES: ['treasury', 'supervisor'] as readonly string[],
      REOPEN_ROLES: ['supervisor'] as readonly string[],
      /**
       * 年＋月／全年選擇器（Wave 3；PO 2026-09-11）：期末未付／已付未取 是區間結束日的快照，
       * 任意區間的結束日不是月底就沒有會計意義；全年是唯一合理的合併。
       */
      PERIOD_PICKER: {
        YEARS_BACK: 5,
        /** 「全年」在月份下拉裡的值（1–12 是月份）。 */
        WHOLE_YEAR_VALUE: 0,
        LABELS: { year: '年度', month: '月份', wholeYear: '全年' } as Record<string, string>,
      },
      /**
       * 期末未付明細 狀態 欄的基準日語意（Wave 3 #2／D3，PO 2026-09-11）。Key 由 CF 決定
       * （BE CPA_EXPORT.UNPAID_STATUS，鏡像），這裡只有標籤；其餘 key 走 STATUS_LABELS。
       */
      /** Mirror of BE CPA_EXPORT.UNPAID_STATUS (check-config-mirror). */
      UNPAID_STATUS: {
        approvedPaidLater: 'approved_paid_later',
        arrangedUnreleased: 'arranged_unreleased',
      },
      /** Mirror of BE CPA_EXPORT.CHANGE_KINDS (check-config-mirror). */
      CHANGE_KINDS: {
        voided: 'voided',
        corrected: 'corrected',
        returned: 'returned',
        reapproved: 'reapproved',
        // 交付（定版）v1.5 補三種（seal spec §4.1）。
        declined: 'declined',
        deferred_filled: 'deferred_filled',
        arrangement_cancelled: 'arrangement_cancelled',
      },
      /** 本期異動 分頁的事件文字（交付模式）。 */
      CHANGE_KIND_LABELS: {
        voided: '作廢',
        corrected: '更正',
        returned: '退回修改',
        reapproved: '重新核准',
        declined: '拒絕',
        deferred_filled: '憑證補齊',
        arrangement_cancelled: '取消付款安排',
      } as Record<string, string>,
      /**
       * 本期異動 更正欄位 欄的中文（審查 H6：原本印 `items[0].amountWithTax` 這種英文路徑）。
       * Key ＝ BE CPA_EXPORT.DELIVERED_FACT_FIELDS 的欄名（`items.` 前綴＝明細行欄位）；
       * 行增刪兩個 key 由 CF 在 changes[] 的 field 直接給。
       */
      CHANGE_FIELD_LABELS: {
        'items.amountWithTax': '含稅金額',
        'items.amountPreTax': '未稅金額',
        'items.taxAmount': '稅額',
        'items.taxType': '稅別',
        'items.category': '類別',
        'items.invoiceNumber': '發票號碼',
        'items.invoiceDate': '憑證日期',
        'items.expenseDate': '消費日期',
        'items.documentType': '憑證類型',
        'items.nonPayable': '不付款',
        'items.description': '說明',
        'items.added': '新增行',
        'items.removed': '刪除行',
        // 行增刪 的實際欄名（CF 2026-09-21 定案）：`__` 前綴才不會與真實欄位同名。
        // 上面兩個無前綴的 key 保留，舊資料與舊 CF 版本都還會送。
        'items.__added': '新增行',
        'items.__removed': '刪除行',
        payeeName: '收款人',
        type: '請款類型',
        billingPeriod: '所屬月份',
        totalAmountWithTax: '請款金額',
        /** 取消付款安排 事件帶的欄位：原本安排的付款日（to 為空＝安排已撤銷）。 */
        paymentDate: '付款日期',
      } as Record<string, string>,
      CHANGES_NOTES: {
        basis:
          '本期（{start}～{end}）發生在已交付請款單上的事件：作廢、拒絕、更正、退回修改、重新核准、憑證補齊、取消付款安排。每筆一列；更正與重新核准的金額印 舊→新（舊值取自原交付月份的封存檔）。本表是清單，不是可加總的調整表。',
        none: '本期無異動。',
        moneyReturned: '款項已於 {date} 退回',
        moneyNotReturned: '款項未匯出',
        /** 更正／重新核准 列的 金額 欄：舊→新。 */
        oldToNew: '{from}→{to}',
        /** 原交付月份 欄：該月從未交付（早於首個可交付月份、或尚未交付）。 */
        notDelivered: '{month}（未交付）',
        /** 原交付月份 欄：無 approved 條目的舊資料（seal spec §1.1）。 */
        noApprovalRecord: '（無核准紀錄）',
        /** 舊值來源：原交付月份沒有封存 JSON（從未交付、FIRST 月之前）。 */
        noSealedJson: '原交付月份未封存，無法比對',
        /** 舊值來源版本（該月曾重新交付）。 */
        comparedVersion: '對 v{version}',
        /** 更正欄位 欄的每一項：欄名 舊→新。 */
        fieldChange: '{field} {from}→{to}',
        /**
         * 年度本專屬（seal spec §5.1）：該月曾重新交付，最新版的值可能已含這筆異動，
         * 於是列上的 舊→新 會是同值——標記出來，才不會被讀成「還要再調一次」。
         */
        absorbedInVersion: '已納入 {month} v{version}',
      } as Record<string, string>,
      /** Mirror of BE CPA_EXPORT.DELIVERY_STATES (check-config-mirror). */
      DELIVERY_STATES: {
        notDelivered: 'not_delivered',
        delivered: 'delivered',
        reopened: 'reopened',
      },
      /** Mirror of BE CPA_EXPORT.FIRST_DELIVERY_MONTH (check-config-mirror). 更早月份只能預覽。 */
      FIRST_DELIVERY_MONTH: '2026-07',
      /**
       * 消費日期框的已交付鎖（tasks/todo-invoice-date-check.md D23）只讀這些狀態的單：從未核准的
       * 請款單沒有交付月份（payment-getPaymentRequestDetail 回 delivery: null），不必去讀。
       */
      DELIVERY_LOOKUP_STATUSES: ['approved', 'paid'] as readonly PaymentStatus[],
      /** Mirror of BE CPA_EXPORT.SEAL_ERROR_CODES (check-config-mirror). 契約 docs/CPA_DELIVERY_CF_CONTRACT.md §0。 */
      SEAL_ERROR_CODES: {
        role: 'role',
        monthNotEnded: 'monthNotEnded',
        beforeFirstMonth: 'beforeFirstMonth',
        prevMonthNotDelivered: 'prevMonthNotDelivered',
        versionMismatch: 'versionMismatch',
        generationUsed: 'generationUsed',
        alreadyDelivered: 'alreadyDelivered',
        storageFailed: 'storageFailed',
        notDelivered: 'notDelivered',
        reasonRequired: 'reasonRequired',
        workbookTooLarge: 'workbookTooLarge',
        notFullMonth: 'notFullMonth',
        yearMonthJsonMissing: 'yearMonthJsonMissing',
      },
      /**
       * 已交付事實守門的兩支更正 CF（updateInvoiceDetails／correctBillingPeriod）
       * 在「主管但沒填原因」時丟 invalid-argument，訊息含這一段。它們**不帶**
       * details.reason（與 SEAL_ERROR_CODES 不同支），所以前端只能認這段字——
       * 改 CF 訊息時這裡要一起改（兩邊都在 appConfig，鏡像檢查看得到）。
       */
      REASON_REQUIRED_MESSAGE_MARK: '必須填寫原因',
      /** 交付／重開／下載 每一種拒絕的繁中訊息，key ＝ SEAL_ERROR_CODES；找不到 key 才用 CF 的 message。 */
      SEAL_MESSAGES: {
        role: '只有出納或主管可以交付；重新開啟只有主管可以操作。',
        monthNotEnded: '該月份尚未結束，月底之後才能交付。',
        beforeFirstMonth: '早於第一個可交付月份（{first}）的月份只能預覽，不能交付。',
        prevMonthNotDelivered: '前一個月（{prev}）尚未交付，請先交付前一個月。',
        versionMismatch: '此月份的交付狀態已被其他人更新，請重新開啟對話框後再試。',
        generationUsed: '這份預覽已交付過或已失效，請重新產生後再交付。',
        alreadyDelivered: '此月份已交付；要更正請先由主管重新開啟。',
        storageFailed: '封存檔案寫入失敗，本次交付未完成，請再試一次。',
        notDelivered: '此月份尚無交付紀錄。',
        reasonRequired: '請填寫重新開啟的原因。',
        workbookTooLarge: '檔案超過交付上限，請聯絡系統管理員。',
        notFullMonth: '只能交付完整的一個月份。',
        yearMonthJsonMissing: '{month} 的封存資料讀不到，無法產出年度本；請先處理該月份。',
      } as Record<string, string>,
      /**
       * 交付（定版）UI 的每一句（出納作業頁的匯出對話框與紅色橫幅）。元件內零字面
       * 中文；交付摘要的每個數字都由 CF 的 sealSummary 給，前端不重算（seal spec §3）。
       */
      DELIVERY_UI: {
        monthStatusLabel: '交付狀態',
        stateNotDelivered: '未交付',
        stateDelivered: '已交付 v{version}（{date}，{by}）',
        /**
         * 短版（交付前摘要的「上月」那一行）。不可用 `stateDelivered` 傳空字串
         * 代替 —— 那會留下「已交付 v1（，）」這種空括號（e2e 2026-09-20，E2）。
         */
        stateDeliveredShort: '已交付 v{version}',
        stateReopened: '重新開啟中（原因：{reason}）',
        /** 同理：短版不帶原因。 */
        stateReopenedShort: '重新開啟中',
        stateUnknown: '（查詢中）',
        previewButton: '預覽匯出',
        downloadButton: '下載已交付檔',
        sealButton: '交付',
        reopenButton: '重新開啟',
        /** 出納作業頁最上方的常駐紅色橫幅，直到該月再次交付為止。 */
        banner: '{month} 已重新開啟，尚未重新交付',
        bannerTitle: '有月份重新開啟中',
        summaryHeading: '交付前摘要（由系統計算）',
        summaryAccrual: '費用明細 {count} 列 / 含稅 {amount} 元',
        summaryCash: '本月付款 {count} 筆（銀行進出）/ {amount} 元',
        summaryPostSealCash: '期後補登（前期）{count} 筆（銀行進出）/ {amount} 元',
        summaryChanges: '本期異動 {count} 列',
        summaryUnpaid: '期末未付 {count} 筆 / {amount} 元',
        summaryReturned: '本月核准、目前退回修改中：{count} 張',
        summaryUnreleased: '付款日在本月、尚未放行：{count} 筆',
        summaryMissingReceipt: '憑證未到：{count} 行',
        summaryUnverifiedPaper: '紙本未點收：{count} 行',
        summaryScanFrom: '掃描起點：{date}（更早建立的請款單不在本檔）',
        summaryPrevMonth: '上月（{month}）：{state}',
        /** 渲染出來的檔案自己的合計——與上面 CF 的數字並列，操作者對照後才按確認。 */
        renderedHeading: '本次產出的檔案',
        /**
         * 兩欄怎麼比：比金額與筆數。「筆」＝銀行進出筆數（一筆付款可能有多張憑證、
         * 在 付款明細 渲染成好幾列），所以列數本來就會不同，不是對照的依據。
         */
        renderedCompareHint:
          '請逐項比對兩邊的金額與筆數。付款明細的「筆」是銀行進出筆數；一筆付款若有多張憑證，表上會是多列，列數不等於筆數。',
        renderedAccrual: '費用明細 {count} 列 / 含稅 {amount} 元',
        renderedCash: '付款明細 {count} 筆（銀行進出）/ 實付 {amount} 元',
        sealConfirmTitle: '交付 {month}',
        sealConfirmIntro: '交付後這個月的檔案不再改變；之後發生的事會列在發生當月的 本期異動。',
        sealConfirmPositive: '確認交付',
        sealConfirmNegative: '取消',
        sealSecondTitle: '再次確認：交付 {month} v{version}',
        sealSecondBody:
          '即將把 {month} 定版為 v{version}：費用明細 {rows} 列 / 含稅 {amount} 元、付款明細 {cashRows} 筆（銀行進出）/ 實付 {cash} 元。定版後只有主管能重新開啟。確定交付？',
        sealedSuccess: '已交付 v{version}',
        reopenTitle: '重新開啟 {month}',
        reopenBody:
          '重新開啟後這個月可以再次交付（v{next}）；舊版檔案保留不刪。重新交付之後請記得重寄事務所。',
        reopenReasonPlaceholder: '請填寫原因（必填）',
        reopenPositive: '確認重新開啟',
        reopenedSuccess: '{month} 已重新開啟，請重新交付',
        /** 匯出對話框的說明：七張分頁，順序同 SHEET_ORDER。 */
        modalExplainer: '匯出指定期間的 CPA 對帳活頁簿（{sheets}），供記帳士核對。',
        notFullMonthHint: '「全年」為年度彙總本，只能下載、不能交付。',
        // ── 請款單頁 交付紀錄 區塊（seal spec §6）。即時資料，非封存檔。
        requestHeading: '交付紀錄',
        requestDelivered: '交付於 {month} 檔（v{version}，{date}）',
        requestNotDelivered: '所屬 {month}，尚未交付',
        requestReopened: '{month} 重新開啟中',
        requestNoRecord: '尚未核准，無交付歸屬',
        requestEventsHeading: '交付後事件（列於 {month} 的 本期異動）',
        requestEventsNone: '交付後尚無事件。',
        requestEvent: '{date} {kind}',
        requestEventBy: '（{name}）',
        requestEventReason: '原因：{reason}',
        /** 已交付月份的守門規則（與 CF 的拒絕訊息同字）。 */
        requestSealedHint:
          '此請款單所屬月份（{month}）已交付事務所，更正已交付的欄位需由主管操作並填寫原因。',
        requestSealedHintReason: '主管更正已交付的欄位必須填寫原因。',
        // ── 已交付欄位的更正原因（updateInvoiceDetails／correctBillingPeriod）。
        correctionReasonTitle: '更正已交付的欄位',
        correctionReasonHint: '此單所屬月份已交付事務所，請填寫原因',
        correctionReasonPlaceholder: '請填寫更正原因（必填）',
        correctionReasonPositive: '填好了，繼續更正',
        beforeFirstMonthHint: '{first} 之前的月份只能預覽，不能交付。',
        monthNotEndedHint: '月份尚未結束，月底之後才能交付。',
      } as Record<string, string>,
      UNPAID_STATUS_LABELS: {
        approved_paid_later: '已核准（期後 {date} 付款）',
        arranged_unreleased: '已安排付款（尚未放行）',
      } as Record<string, string>,
      /** 已付未取憑證 憑證狀態：基準日當時未收、之後才核收（Wave 3 #3）。 */
      MISSING_RECEIPT_RECEIVED_LATER: '基準日未收（{date} 核收）',
      /**
       * 零列的分頁在表頭下印的一句（sheetFromRows 的預設）。本期異動 有自己的一句
       * （CHANGES_NOTES.none），兩句都印會變成同一件事講兩遍（審查 M10）。
       */
      SHEET_EMPTY_TEXT: '本期無資料',
      /**
       * 每張分頁表上方共用的行。「版本：第 N 版」（匯出次數）已於 2026-09-21 刪除
       * （seal spec §9）——交付版本是另一件事，只印在封面。
       */
      NOTES: {
        generatedAt: '產出時間：{time}',
      } as Record<string, string>,
      /** Mirror of BE CPA_EXPORT.PERIOD_END_STATES (check-config-mirror). */
      PERIOD_END_STATES: CPA_PERIOD_END_STATES,
      /** Mirror of BE CPA_EXPORT.PAPER_STATES (check-config-mirror). */
      PAPER_STATES: CPA_PAPER_STATES,
      /**
       * 費用明細 期末狀態 欄（N1，單表方案 D-W2-1）。Key 由 CF 決定
       * （BE CPA_EXPORT.PERIOD_END_STATES，鏡像檢查鍵集合）；這裡只有標籤。
       * 判定基準＝基準日當時的付款紀錄（與 期末未付明細 同一個 as-of 視角）。
       */
      PERIOD_END_STATE_LABELS: {
        settled: '期內已付',
        payable: '期末未付',
        non_payable: '不付款',
      },
      /**
       * 費用明細 紙本狀態 欄（N2）。只陳述我們的紀錄，不陳述紙在哪裡：
       * 「無交件紀錄」而不是「尚未交件」——上線切點（2026-09）前付款的憑證早就交給
       * 事務所了，系統只是沒有紀錄（審查 2026-09-20）。handed_over 由 builder 補上
       * 交件批次識別（與紙本憑證頁同一個樣板）。
       */
      PAPER_STATE_LABELS: {
        handed_over: '已交件（{label}）',
        in_hand: '尚未交件（已點收）',
        awaiting_receipt: '尚未交件（待點收）',
        chasing: '尚未交件（催交中）',
        pending_document: '尚未交件（憑證未到）',
        closed: '已結案（未取得憑證）',
        untracked: '無交件紀錄',
        none: '',
      },
      /** 費用明細 憑證跨期 欄：憑證日期早於本期時印的字（空白＝同期）。 */
      VOUCHER_EARLIER_MARK: '憑證日期早於本期',
      /** handed_over 但歸不到批次（後開補交、v3.3 前的批次）時只印這個，不猜批次。 */
      PAPER_STATE_HANDED_OVER_NO_BATCH: '已交件',
      /**
       * 費用明細 表尾 期末小計 區塊（N1／N8／D-W2-2）。用與 期末狀態 欄同一套詞
       * （期內已付／期末未付／期末未付・審核中／不付款），每一列都分 公司費用／專案，
       * 事務所才能拿欄位對小計（審查 2026-09-20）。{end} ＝ 基準日。
       */
      PERIOD_END_TOTAL_LABELS: {
        heading: '期末小計（基準日 {end}，以含稅金額計）',
        settled: '期內已付 小計（公司費用）',
        settledInternal: '期內已付 小計（專案）',
        payable: '期末未付 小計（公司費用）',
        payableInternal: '期末未付 小計（專案）',
        nonPayable: '不付款 小計（不列應付）',
        /**
         * 應付薪資 ＝ 期末未付明細 中薪資列的合計（seal spec §4.4，PO 2026-09-21）：
         * 同一個母體、同一個查詢，所以標籤不再寫「所屬月份 …」，改寫判定基準。
         */
        salary: '應付薪資（基準日已核准未付，見 期末未付明細）{count} 筆 / {amount} 元',
        salaryNumbers: '單號：{numbers}',
        refund: '退費：見 期末未付明細 退費金額 欄（{end} 已核准尚未退款者）',
      } as Record<string, string>,
      /** 費用明細 表頭註記（改版後）。零筆的句子不印。 */
      ACCRUAL_NOTES: {
        scope:
          '本表範圍：主管核准日在 {start}～{end} 的請款單（每張只在核准當月的檔案出現一次）。憑證日期 為記帳依據，早於本期者於 憑證跨期 欄標示。',
        basis: '本檔未區分二聯式／三聯式，統一發票的進項稅額可否扣抵請人工判定。',
        cutoff:
          '付款狀態、付款日期、紙本狀態、期末狀態 與 表尾期末小計 皆為 {end} 當時的狀態；之後的付款見該月的 付款明細，之後的作廢或更正見該月的 本期異動。',
        /**
         * 年度本（seal spec §5.1）：這四欄**沒有**單一基準日 —— 每一列都是它
         * 自己 原交付月份 月底的狀態，十二個月就是十二個不同的基準日。沿用單月
         * 的句子會印成「皆為 2026-12-31 當時的狀態」，與封面第 7 列自相矛盾，
         * 而且會把已交付月份那些「已核准／期末未付」的列讀成年底仍未付 ——
         * 直接讀錯年底應付（e2e 2026-09-21，D4）。表尾期末小計 在年度本本來就
         * 不印（`accrualFooterRows` 的 yearMode 分支），所以這裡也不提它。
         */
        cutoffYear:
          '付款狀態、付款日期、紙本狀態、期末狀態 為各列 原交付月份 當月月底的狀態（十二個月＝十二個不同基準日），不是 12/31 的狀態；本表不印期末小計，年底應付請看 期末未付明細。之後的付款見該月的 付款明細，之後的作廢或更正見該月的 本期異動。',
        excludedSalary: '薪資（依所屬月份）{count} 筆 / {amount} 元',
        excludedRefund: '退費 {count} 筆 / {amount} 元（收入減項，非費用）',
        /** 「期末應付」→「期末未付 小計」：表尾的標籤就叫 期末未付（審查 M9）。 */
        excluded: '明細未列：{clauses}；明細見 付款明細，期末未付 小計列於表尾。',
        /** 年度本（seal spec §5.1）：未納入 是流量不是餘額，十二個月相加。 */
        excludedYearMark: '（全年）',
        notAdditive:
          '本表表尾的期末小計 依憑證日期逐行；期末未付明細 依核准日、每單總額。兩表不可相加。',
        needsReview: '其中：需人工判定的稅額',
        needsReviewCount: '{count} 列',
      } as Record<string, string>,
      /** 付款明細／月結摘要 表頭註記（N3：兩張表原本沒有產出時間）。 */
      CASH_NOTES: {
        paymentDetail:
          '認列基準：實際付款日（現金收付制），供銀行對帳；含薪資、退費、退匯、金額調整。',
        paymentDetailUnreleased: '本表僅含已放行付款；排除尚未放行 {count} 筆 / 淨額 {amount} 元。',
        /** 月結摘要 的同一句後面多這一段（那張表旁邊就是費用明細的數字）。 */
        unreleasedAccrualUnaffected: '費用明細不受此影響。',
        monthlySummary: '付款明細之彙總（認列基準：實際付款日），非權責發生制。',
        /**
         * 期後補登（seal spec §4.2）：付款日期屬**已交付**的月份、但登錄時間晚於那
         * 個月的交付時刻，所以只能列在登錄月。付款日期不改（那是銀行對帳單上的日期）。
         */
        postSealColumnMark: '是（屬 {month}）',
        postSealBasis:
          '期後補登（前期）：付款日期屬先前已交付的月份、本期才登錄者；合計另列一行，請歸回所屬月份對帳。',
        totalCurrent: '本月付款 合計（{count} 筆）',
        totalPostSeal: '期後補登（前期）合計（{count} 筆）',
        /** 月結摘要 的類別／請款類型區塊只算本月付款（seal spec §4.2）。 */
        summaryCurrentOnly: '【費用類別】與【請款類型】只含本月付款，不含期後補登（前期）。',
      } as Record<string, string>,
      /**
       * 封面說明（N3／N10／N11）。每一句都在這裡，builder 內零字面文字。
       * 措辭規則：類別名不加解釋、不出現兩本帳的說法、不出現術語。
       */
      COVER: {
        TITLE: '{company} 費用與付款明細',
        ROWS: {
          period: '所屬期間',
          periodValue: '{start} ～ {end}（民國 {rocYear} 年）',
          periodValueNoRoc: '{start} ～ {end}',
          /**
           * 交付（定版）版本（seal spec §2）。與舊的「匯出次數版本」不同：那個已刪除
           * （§9），因為同名不同義必被混用。預覽檔沒有版本，印「預覽（未交付）」。
           */
          deliveryVersion: '交付版本',
          deliveryVersionValue: 'v{version}（交付於 {date}）',
          deliveryVersionPreview: '預覽（未交付）',
          generatedAt: '產出時間',
          status: '狀態基準',
          /**
           * E1（2026-09-21）：原句「同一期間日後重新匯出，內容相同」對預覽與再交付
           * 都不成立——不變的是**已交付的檔案**，不是「重新匯出」。
           */
          statusValue:
            '每張分頁的每一個狀態都是 {end} 當時的狀態。已交付的檔案不再改變；之後發生的事列在發生當月的 本期異動。',
          statusValuePreview: '本檔為預覽，尚未交付；交付前內容可能變動。',
          batches: '本期相關的送件單',
          batchesNone: '本期尚無交件',
          batchesNote: '（只含各請款單第一次交件的送件單；之後補交的行在 費用明細 只印「已交件」）',
          basisHeading: '各分頁認列基準',
          disclosureHeading: '閱讀前請注意',
          // ── 再次交付（seal spec §1.1／§1.3）：逐條揭露本版納入了什麼。
          resealHeading: '重新交付說明',
          resealReplaces:
            '本檔取代 {date} 交付之版本；原因：{reason}；本版以產出當時之資料重新產生，已納入前版交付後之下列異動：',
          /** 逐條一列：日期 事件 單號（欄位 舊→新）。 */
          resealChange: '{on} {kind} {requestNumber}{detail}',
          resealChangeDetail: '（{detail}）',
          resealNoEventHeading: '本版與前版可能不同、但系統沒有事件紀錄的欄位：',
          resealUnverifiedHint:
            '以上四類無法逐條列出，請以本檔為準；金額類的異動仍逐條列於上方與發生月份之 本期異動。',
          resealDuplicateWarning: '該等異動同時列於發生月份之 本期異動，請勿重複調整。',
          // ── 年度本（seal spec §5.1）。
          yearMonthsHeading: '各月份來源',
          yearMonthDelivered: '已交付（v{version}）',
          yearMonthResealed: '已交付（v{version}，曾重新交付）',
          yearMonthLive: '即時（尚未交付）',
          yearBasis:
            '費用明細 為各月交付當時之數。交付後的異動列於 本期異動，僅供查閱；各期實際應入帳的調整，請以當月交付檔為準。本表僅含至 {date} 止已發生之異動（之後發生的會出現在下一年度的檔案）。',
          yearNoPeriodEndTotals:
            '年度本不印 期末小計：付款狀態、付款日期、期末狀態、紙本狀態 四欄是逐列各自月份的月底狀態（見 原交付月份 欄），十二個不同基準日的小計不是 12/31 的應付。年底應付請看 期末未付明細（十二月那份）。',
          crossYearHeading: '跨年揭露',
          crossYearCashOut:
            '付款日在本年度 12 月、次年才登錄者 {count} 筆 / {amount} 元：不在本表（會出現在次年度的 期後補登）。',
          crossYearCashIn:
            '付款日在前一年度 12 月、本年度才登錄者 {count} 筆 / {amount} 元：已列入本表（期後補登）。',
          crossYearAccrualOut:
            '憑證日期在本年度 12 月、次年才核准者 {count} 筆 / {amount} 元：不在本表。',
          crossYearAccrualIn:
            '憑證日期在前一年度、本年度才核准者 {count} 筆 / {amount} 元：已列入本表（憑證跨期）。',
        } as Record<string, string>,
        /**
         * 每月封面三句（seal spec §6）。交付模式的三個承諾，與下方 DISCLOSURES 接續編號。
         */
        DELIVERY_SENTENCES: [
          '本檔交付之請款單日後如有異動，列於發生當月之 本期異動；查詢請以請款單號。',
          '紙本狀態 為基準日當時；其後之交件見送件單。',
          '本月期末未付的增減不等於本月付款合計；期後補登列請歸回所屬月份對帳。',
        ] as readonly string[],
        /**
         * 再交付封面必揭露的四條「無事件紀錄」變動路徑（seal spec §1.3(b)）。
         * 這四條的舊值在系統裡已經被覆寫或清掉，重建不出來，所以只能揭露、不能逐條列。
         */
        RESEAL_NO_EVENT_PATHS: [
          '收款人銀行資料（廠商帳號變更會一併改動歷史列）',
          '紙本狀態（退回修改會清掉逐行點收章與交件戳記；取消點收會清掉尚未交件的逐行點收章）',
          '費用備註與退費說明（刻意不守門，申請人可於退回後修改）',
          '憑證驗收狀態（更正發票資料會重設驗收）',
        ] as readonly string[],
        /**
         * 2026-07／08 v1 封面加印：這兩個月先前寄出過人工更正版（seal spec §6）。
         */
        REPLACES_MANUAL_MONTHS: ['2026-07', '2026-08'] as readonly string[],
        REPLACES_MANUAL_SENTENCE: '本檔取代先前以人工更正後寄出之版本。',
        /**
         * 上線前唯讀盤點（seal spec §7）發現、且只影響特定月份的一次性歷史揭露。
         * 2026-07：五行發票資料曾被編輯，系統只留編輯時間、沒留原值（W0 盤點）。
         * 2026-08：九項盤點全為零，故無揭露。
         */
        ONE_TIME_DISCLOSURES: {
          '2026-07': [
            '本檔另行揭露：C26JL03、C26JL07、P26JL08、C26JL10、C26JL11 各有一行之發票資料曾被編輯，系統僅記錄編輯時間、未保留原值，故無法列出更正前後對照。',
          ],
          '2026-08': [],
        } as Record<string, readonly string[]>,
        SHEET_BASIS: [
          {
            sheet: 'accrual',
            text: '主管核准日在本期的請款單，每行一張憑證，含 憑證日期（記帳依據）、{end} 當時的付款與紙本狀態；不含薪資與退費。',
            // 年度本：狀態欄沒有單一基準日，見 ACCRUAL_NOTES.cutoffYear（D4）。
            textYear:
              '主管核准日在本年度的請款單，每行一張憑證，含 憑證日期（記帳依據）、各列 原交付月份 當月月底的付款與紙本狀態（不是 12/31）；不含薪資與退費。',
          },
          {
            sheet: 'changes',
            text: '本期發生在已交付請款單上的作廢、拒絕、更正、退回修改、重新核准、憑證補齊、取消付款安排，每筆一列。',
          },
          {
            sheet: 'unpaid',
            text: '基準日當時已核准、尚未付款之請款單（每單一列，含退費）。次期付款時沖銷。',
          },
          { sheet: 'missingReceipt', text: '已付款但截至基準日尚未取得憑證者。' },
          {
            sheet: 'paymentDetail',
            text: '認列基準：實際付款日（現金收付制），供銀行對帳；含薪資、退費及退匯、金額調整等現金往來。',
          },
          {
            sheet: 'monthlySummary',
            text: '付款明細之彙總（認列基準：實際付款日），非權責發生制。',
          },
        ] as ReadonlyArray<{ sheet: string; text: string; textYear?: string }>,
        DISCLOSURES: [
          '金額為本公司核實請款金額，可能低於憑證面額。',
          '每張請款單只在主管核准當月的檔案出現一次；憑證日期早於當月者，於 費用明細 的 憑證跨期 欄標示，入帳月份由貴所判斷。',
          '已交付的請款單日後作廢或更正，不會回頭改動先前的檔案，而是列在發生當月的 本期異動。',
          '費用明細 表尾的期末小計 與 期末未付明細 基準不同，兩表不可相加。',
          '每張分頁上方有數列產出資訊，表頭不在第 1 列；以固定儲存格位置讀取本檔者請調整。',
        ] as readonly string[],
      },
      /** Totals rows. Split three ways so 專案 never blends into company expense. */
      TOTAL_LABELS: {
        external: '公司費用合計',
        internal: '專案合計',
        refund: '退費合計',
        combined: '總支出合計',
      } as Record<string, string>,
      /**
       * 放行確認 column values. 「待確認」 was one character away from 「已確認」
       * and mis-read at a glance (PO 2026-09-08); the pair now names the state.
       */
      RELEASE_LABELS: { released: '已放行', pending: '尚未放行' } as Record<string, string>,
      /**
       * 狀態 printed on a 付款明細 row whose amounts are negative: the bank sent
       * a transfer back (退匯). One bounce = one payment row + one return row,
       * like the bank statement (PO 2026-09-08).
       */
      RETURN_ROW_LABEL: '退匯退回',
      /** Same shape for a voided payment whose money came back (voidInfo.moneyReturned). */
      VOID_RETURN_ROW_LABEL: '作廢退回',
      /**
       * 狀態 printed on a 付款明細 row that is a 金額調整 — a separate payment
       * recorded after release. Positive = 補付差額 (we paid more), negative =
       * 退回溢付 (the payee returned the excess). Same words the app uses.
       */
      ADJUSTMENT_ROW_LABELS: { positive: '補付差額', negative: '退回溢付' } as Record<
        string,
        string
      >,
      /** 類別 shown for an adjustment row and its 月結摘要 pivot line — it has no category of its own. */
      ADJUSTMENT_CATEGORY_LABEL: '金額調整',
      /**
       * 期末未付明細 狀態 for a payment the bank sent short (實際匯出金額 <
       * 請款金額) whose make-up has not gone out by the cut-off. Mirrors backend
       * CPA_EXPORT.SHORT_PAID_STATUS ('short_paid').
       */
      SHORT_PAID_STATUS: 'short_paid',
      SHORT_PAID_STATUS_LABEL: '實匯短少待補付',
      /**
       * 月結摘要 備註 when the period's 實付金額 differs from its 憑證 含稅金額
       * (a short / over wire or a 金額調整). `{gap}` = signed difference.
       */
      CASH_GAP_NOTE:
        '本期實付金額與憑證含稅金額相差 {gap} 元（實匯短少／溢匯或金額調整，見付款明細 實付金額 欄）',
      /** 進項稅額可扣抵 column values (backend decides which applies). */
      /**
       * 進項稅額 verdicts. There is deliberately no 「可扣抵」 value: whether a
       * 統一發票 line is claimable turns on 二聯式 vs 三聯式, which the request
       * form does not capture, so the sheet asks for a human call instead of
       * asserting one. See resolveTaxDeductible in functions/src/payment/cpaExport.ts.
       */
      TAX_DEDUCTIBLE_LABELS: {
        manual_review: '需人工判定',
        not_deductible: '不可扣抵',
        pending_document: '憑證未到',
        not_assessed: '',
      } as Record<string, string>,
      /** 月結摘要 section headings. */
      SUMMARY_SECTION_LABELS: {
        categories: '【費用類別】',
        internalLedger: '【專案】',
        refunds: '【退費（收入減項）】',
        types: '【請款類型】',
      } as Record<string, string>,
    },

    // ─── Display Label Maps (single source of truth for all payment UI) ─────

    STATUS_LABELS: {
      draft: '草稿',
      pending_approval: '待審核',
      pending_approval_l2: '待二級審核',
      approved: '已核准',
      declined: '已拒絕',
      returned: '退回修改',
      paid: '已付款',
      voided: '已作廢',
    } as Record<string, string>,

    /** Valid payment request status transitions — mirrors backend PAYMENT_STATUS_TRANSITIONS. */
    STATUS_TRANSITIONS: {
      draft: ['pending_approval', 'approved', 'pending_approval_l2'],
      pending_approval: ['pending_approval_l2', 'approved', 'declined', 'returned', 'draft'],
      pending_approval_l2: ['approved', 'declined', 'returned', 'draft'],
      // 'voided' = 作廢 a request that has ALREADY been paid once and bounced
      // (退匯重付 left it at `approved` with repaymentHistory / hasEverBeenPaid).
      // Money genuinely moved, so 退回／拒絕／刪除 are all refused for such a
      // request — 作廢 is the ONLY terminal action that keeps the record. A
      // never-paid `approved` request still cannot be voided (voidPayment gates
      // on the payment history, not on this table).
      approved: ['paid', 'returned', 'declined', 'voided'],
      declined: [] as string[],
      returned: ['pending_approval', 'approved', 'pending_approval_l2'],
      // 'approved' = 退匯／重新付款 (repayPayment): the bank bounced the transfer, so
      // the money came back and the request returns to 待付款 with its approval intact.
      // NOT a general un-pay — see REPAYMENT for who may do it and when.
      paid: ['voided', 'approved'],
      voided: [] as string[],
    } as Record<string, string[]>,

    /**
     * 現金類付款（零用金／現金提款）— PARK-9 (PO 2026-09-10).
     *
     * No bank holds an instruction for cash, so a future 付款日期 means nothing:
     * 安排付款 for cash is only a reminder that the cash still has to be
     * withdrawn. The date is therefore OPTIONAL at 安排付款 and REQUIRED at
     * 已提領 (the release step), when the cash has actually been withdrawn.
     * A cash row with no date is correctly carried as still unpaid by the CPA
     * export (cpaExport.ts guards `!!paymentDate`).
     *
     * Exception: a request linked to a 定期款項 schedule WITHOUT a billingPeriod
     * derives its period from the date, so it still needs one at 安排付款.
     * Mirrored in the frontend appConfig.
     */
    CASH_ARRANGEMENT: {
      METHODS: ['cash', 'cash_withdrawal'] as readonly string[],
      LABELS: {
        pending: '待提領',
        released: '已提領',
        dateField: '實際領現日期',
        confirmAction: '確認已提領',
      },
      /**
       * 待提領 tag colour (PO 2026-10-15): blue, not the orange of 待放行 —
       * orange reads as a warning, and cash waiting to be withdrawn is a normal
       * step. 待放行 stays orange (PO: only 待提領 changes). Frontend only.
       */
      PENDING_TAG_TYPE: 'info' as const,
      MESSAGES: {
        dateRequiredForNonCash: '請選擇預計付款日期。',
        dateRequiredForSchedule:
          '此單連結定期款項且未指定所屬期別，需要日期才能判定期別，請填寫日期。',
        releaseDateRequired:
          '此筆尚未記錄日期，請填寫實際付款日期（現金請填實際領現日期）後再確認。',
      },
    },

    /**
     * 取消付款安排 — the inverse of 安排付款, for a payment that was arranged but
     * NOT yet 放行. Named from the button it undoes (安排付款 → 取消付款安排), per
     * the wording rule: an undo takes the original's noun phrase and negates it.
     * Mirrored in the frontend appConfig.
     */
    CANCEL_ARRANGEMENT: {
      LABEL: '取消付款安排',
      MESSAGES: {
        notPaid: '只有已安排付款、尚未放行的請款單可以取消付款安排。',
        alreadyReleased:
          '這筆款項已放行，不能取消付款安排。若銀行退匯請用「退匯／重新付款」；' +
          '若款項實際未匯出，請先「標記付款異常」再作廢。',
        /** Export refusal for a 待放行 row — names the way back, not just the block. */
        pendingReleaseNotExportable:
          '已安排付款、尚未放行，不可再匯出（以免付兩次）。' +
          '如需重新匯出，請先在 付款紀錄 對此單按「取消付款安排」。',
      },
      /** Shown on the confirm dialog — says exactly what returns and what does not. */
      CONFIRM_BODY:
        '將取消這筆付款安排，請款單回到「待付款」，付款日期與付款方式一併清除。' +
        '憑證驗證狀態與定期款項期別連結不受影響。',
    },
    /**
     * 退匯／重新付款 —— 銀行把款項退回來，請款單回到「待付款」重新付一次。
     *
     * 這**不是**「取消付款」：錢真的動過（出去又回來），所以退匯過的請款單永遠不能
     * 被退回／拒絕／刪除（見 MESSAGES.hasPaymentHistory），要終止只能作廢。
     * Mirror of functions/src/config/appConfig.ts — keep in sync.
     */
    REPAYMENT: {
      LABEL: '退匯／重新付款',
      /** 退匯原因最少字數 —— 「錯誤」兩個字對三個月後查帳的人沒有任何資訊。 */
      REASON_MIN_LENGTH: 4,
      /** 佇列卡片上的短標籤（空間有限，不用完整 LABEL）。 */
      CHIP_LABELS: {
        repayment: '退匯重付',
        missingCode: '缺通匯代碼',
        /**
         * `bankDetailsChangedSinceLastApproval` 的佇列短標籤。完整句子在付款
         * 與審核面板的兩個紅色指示器裡（「銀行資料在本單送出後曾被變更，請重新
         * 核對」）—— 同一個布林值，四個地方不會各說各話。
         *
         * 2026-09-17 改字（審查 L7）：原本寫「與前次核准時不同」，但 cascade
         * 也會對**從未核准過**的待審核單設這個旗標，那種單沒有「前次核准」可
         * 比。匯出列的文案仍寫「核准後變更」—— 帶 warnings 的列一定核准過
         * （沒核准過的列在更前面就被 excluded 擋掉，只會有 reasons），對它來說
         * 是準確的。
         */
        bankChanged: '銀行資料已變更',
        /**
         * 廠商帳戶 `unverified` 的佇列短標籤（2026-09-19，原「需驗帳」）。
         * 照清除它的動作命名 —— 付款面板的勾選框說「已核對」，標籤就說「待核對」。
         * 不用「驗證」：那個動詞在本系統屬於憑證／發票。這個標籤是卡片上唯一真
         * 正會擋住付款與匯出的旗標，所以與 bankChanged 同為紅色（紅＝錢會匯去哪
         * 有問題；橘＝付款前該讀的事）。
         */
        bankNeedsCheck: '銀行帳戶待核對',
      },
      /**
       * 已放行（已確認錢出去）的付款，只有在**銀行資料真的改過**時才能退匯重付。
       * 否則「退匯」就變成一鍵反轉放行，任何理由都能用——那是作廢該做的事。
       */
      RELEASED_REQUIRES_BANK_CHANGE: true,
      /**
       * 匯出畫面的分組標籤。
       *
       * `pendingRelease` / `priorReleased` 是兩個**退匯重付**分組（預設勾選規則
       * 不同，見 P14）。`paidPendingRelease` 是**待放行**分組——已在系統標記付款、
       * 尚未確認放行的列；退匯重付的單若又被標記付款，會落在這一組而不是退匯組
       * （分組依「現在的狀態」而不是「曾經退匯過」），所以標籤要講明含退匯重付。
       */
      GROUP_LABELS: {
        pendingRelease: '退匯重付（原未放行）',
        priorReleased: '退匯重付（原已放行）',
        paidPendingRelease: '待放行（含退匯重付）',
      },
      MESSAGES: {
        notPaid: '只有已付款的請款單可以退匯重付。',
        releasedNeedsBankChange:
          '已放行的付款只能因銀行資料錯誤退匯重付；如為其他原因，請先用「標記付款異常」再作廢。',
        hasPaymentHistory: '此請款單曾有實際付款紀錄，不可退回／拒絕／刪除，如需終止請作廢。',
      },
    },

    /**
     * 7 碼通匯代碼檢查碼規則 —— **僅供輸入端提示，永遠不擋存檔**。
     *
     * 來源：兆豐 EXCEL 資料轉檔程式 V6.94 的 `chkBankID`，摘錄於
     * `docs/reference/megabank-excel-convert-frmRmt.vba.txt:1468-1503`；
     * 銀行消滅清單為同檔 `chkBankExist`（:1506-1519）。
     * 為什麼只提示不擋：這是別人家系統的規則，我們沒有官方對照表可以覆核；
     * 誤擋一個合法代碼會讓出納無法送出真正正確的資料。
     * Mirror of functions/src/config/appConfig.ts — keep in sync.
     */
    BANK_CODE_CHECKDIGIT: {
      /**
       * 一般銀行：第 1–6 碼分別乘這組權數（VBA `tmpAry = Array(0,3,7,9,3,7,9)`，
       * 迴圈 cnt = 1..6 取 tmpAry(cnt)，所以 index 0 的 0 是佔位、實際用到的是後 6 個）。
       * 檢查碼 = (10 − 總和 mod 10) mod 10，須等於第 7 碼。VBA :1470、:1492-1498。
       */
      GENERAL_WEIGHTS: [3, 7, 9, 3, 7, 9],
      /**
       * 彰化銀行不適用一般算法：只取第 4、5、6 碼（4 碼分行代號的前 3 碼）
       * 乘 (1,4,7)，取總和的**個位數**當檢查碼。VBA :1488-1490。
       */
      CHANGHWA_BANK_CODE: '009',
      CHANGHWA_BRANCH_WEIGHTS: [1, 4, 7],
      /** 彰銀權數對應的 1-based 位置（第 7 碼仍是檢查碼本身）。VBA :1489。 */
      CHANGHWA_BRANCH_POSITIONS: [4, 5, 6],
      /**
       * 信用合作社（首碼 1 或 2）：權數與一般銀行相同，但取總和的**個位數**，
       * 不取 10 的補數。VBA :1495-1496。
       */
      CREDIT_UNION_FIRST_DIGITS: ['1', '2'],
      /**
       * 中華郵政完全不套算法：只有這兩個代碼有效（VBA 判斷後 4 碼是 0010 或 0021）。
       * VBA :1479-1485。
       */
      POST_OFFICE_BANK_CODE: '700',
      POST_OFFICE_VALID_CODES: ['7000010', '7000021'],
      /** 一律無效的代碼（VBA :1471 明確排除）。 */
      INVALID_CODES: ['0000000'],
      /**
       * 已消滅、被併走的銀行代號 —— 提示用（VBA `chkBankExist` :1507-1516）：
       * 002 農銀→006 合庫、003 交銀→017 兆豐、051 北商銀→807 永豐、151 第七銀→013 國泰世華。
       */
      DEFUNCT_BANK_CODES: ['002', '003', '051', '151'],
    },

    /**
     * 中華郵政（PO 2026-10-05）：匯款到任何一間郵局，通匯代碼一律 7000021 ——
     * 郵局由帳號前 7 碼（局號）辨認。選中華郵政＝分行代號自動 0021、不可改；
     * 帳號必須 14 碼。0010 是代碼表另一個有效值，既有資料照收。
     * Mirror of functions/src/config/appConfig.ts — keep in sync (check-config-mirror).
     */
    POST_OFFICE: {
      BANK_CODE: '700',
      DEFAULT_BRANCH_CODE: '0021',
      VALID_BRANCH_CODES: ['0021', '0010'],
      ACCOUNT_DIGITS: 14,
      BRANCH_MESSAGE: '中華郵政的分行代號固定為 0021（所有郵局共用），請在分行選「0021 中華郵政」',
      ACCOUNT_MESSAGE: '郵局帳號須為 14 位數字（局號 7 碼＋帳號 7 碼）',
      EXPORT_REASON: '中華郵政 通匯代碼須為 7000021',
    },

    /** 退費 存摺封面 必傳（PO 2026-10-05）。Mirror of backend — keep in sync. */
    REFUND_BANK_DOCUMENT_REQUIRED_MESSAGE: '請上傳收款人存摺封面',

    /** Valid receipt status transitions — mirrors backend RECEIPT_STATUS_TRANSITIONS exactly. */
    RECEIPT_STATUS_TRANSITIONS: {
      pending: ['received', 'issue', 'deferred'],
      deferred: ['received', 'issue'],
      issue: ['received', 'written_off', 'pending'],
      received: [] as string[], // terminal
      written_off: [] as string[], // terminal
    } as Record<string, string[]>,

    /** Statuses that indicate a request is in-flight (blocks bank changes, deactivation). */
    IN_FLIGHT_STATUSES: [
      'pending_approval',
      'pending_approval_l2',
      'approved',
    ] as readonly string[],

    /** Statuses in which the requester (or supervisor) may permanently delete a
     * payment request. `draft` = never submitted; `returned` (退回修改) = bounced
     * back to the requester's court, so the author may discard it instead of being
     * forced to edit+resubmit. Mirrors backend BUSINESS_RULES.PAYMENT.DELETABLE_STATUSES. */
    DELETABLE_STATUSES: ['draft', 'returned'] as readonly string[],

    /** Statuses where staff (treasury/supervisor) may directly edit bank info on a locked request.
     * Draft/returned belong to the requester and are editable via the normal form — excluded here.
     * Mirrors backend BUSINESS_RULES.PAYMENT.BANK_EDITABLE_STATUSES. */
    BANK_EDITABLE_STATUSES: [
      'pending_approval',
      'pending_approval_l2',
      'approved',
    ] as readonly string[],

    /** Statuses where GA may mutate per-line receipt state (verify, write-off, etc.).
     * Pre-approval verification: GA can verify the physical receipt as soon as she
     * has it in hand without waiting for supervisor approval.
     * Mirrors backend BUSINESS_RULES.PAYMENT.RECEIPT_MUTATION_ALLOWED_STATUSES. */
    RECEIPT_MUTATION_ALLOWED_STATUSES: [
      'pending_approval',
      'pending_approval_l2',
      'approved',
      'paid',
    ] as readonly string[],

    /** Item fields whose value-change invalidates GA's prior receipt verification.
     * Mirrors backend BUSINESS_RULES.PAYMENT.VERIFICATION_INVALIDATING_ITEM_FIELDS
     * (sameDocumentAs: setting / removing 「同 #N」 sends that line back to 待驗證, D50). */
    VERIFICATION_INVALIDATING_ITEM_FIELDS: [
      'amountWithTax',
      'invoiceNumber',
      'invoiceDate',
      'documentType',
      'invoiceDeferred',
      'sameDocumentAs',
    ] as readonly string[],

    RECEIPT_STATUS_LABELS: {
      pending: '發票待收',
      received: '發票已收',
      deferred: '後開發票',
      partial: '部分已收',
      issue: '有問題',
      written_off: '已核銷',
    } as Record<string, string>,

    /** Short receipt labels for space-constrained contexts (queue cards, batch confirm). */
    RECEIPT_STATUS_SHORT_LABELS: {
      pending: '票待驗',
      received: '票已驗',
      deferred: '票延後',
      issue: '票有問題',
      written_off: '已核銷',
    } as Record<string, string>,

    /**
     * 「已驗證」這一勾到底代表什麼 —— 前端專用（不鏡射）。
     *
     * 出納在付款畫面勾的每一列「已驗證」就是影像驗證本身（CF 會把該列的
     * receiptStatus 寫成 received，treasury-payments.ts）。既然這一勾就是驗證，
     * 就必須寫明它宣告了哪些事實，否則每個人心裡的標準都不一樣。
     * PO 決定（2026-09-13）：四項全部核對過才可以勾。
     *
     * `{taxId}` 由呼叫端代換成實際的公司統編（usePaymentConfig().companyTaxId()，
     * 未載入時回退 DEFAULT_COMPANY_TAX_ID）。PO 2026-10-08 D61：四項是**一個提醒**，每一個
     * 「已驗證」旁都是同一個 ⓘ（components/payment/VerifyChecklistHint.vue），沒有逐項勾選。顯示於：
     *   1. 付款情境 發票驗證 欄位標題的說明圖示（usePaymentItemColumns）
     *   2. 批次付款 每列「已驗證」勾選框旁（BatchPayPage）
     *   3. 付款紀錄 › 發票卡 每列「已驗證」旁（PerLineReceiptActions；含填寫發票並驗證、一張發票區塊）
     *   4. 批次處理發票 每列「已驗證」旁（BatchInvoiceLineRow）
     *   5. 紙本憑證 › 補發票 對話框（FillDeferredInvoiceDialog —— 取代原本的四個勾）
     */
    LINE_VERIFY_HINT: {
      TITLE: '已驗證 = 已核對憑證',
      CHECKLIST: [
        '圖片清晰可辨',
        '買方統編為公司統編 {taxId}',
        '發票號碼與日期與圖片相符',
        '金額與圖片相符',
      ] as readonly string[],
    },

    /**
     * 出納「驗證」影像那一條線的畫面字 —— 前端專用（不鏡射）。
     * 規格 v3.3 §2（PO 2026-09-15）：影像一律寫「驗證」，「收」只用在紙本；紙本的
     * 「點收」字句在 PAPER_RECEIPT／PAPER_RECEIPT_UI。
     *
     * 刻意不動 RECEIPT_STATUS_LABELS：它同時是會計對帳 Excel「憑證狀態」「發票狀態」
     * 欄的儲存格文字（utils/paymentExportSheets.ts、utils/paymentCashSheets.ts）——
     * 那份檔案交給事務所，字不跟著畫面改。畫面上的字全部從這裡讀。
     */
    RECEIPT_VERIFY_UI: {
      /** 請款單層級的發票狀態標籤（useReceiptHelpers 的 RECEIPT_STATUS_MAP）。 */
      STATUS_TAGS: {
        received: '發票已驗證',
        written_off: '已核銷',
        pending: '發票待驗證',
        deferred: '後開發票',
        partial: '部分已驗證',
        issue: '發票待驗證',
      } as Record<string, string>,
      /**
       * 發票卡片抬頭與出納佇列的字（usePaymentConfig receiptWordingFor 的一般版）。
       * 差旅版是 TRAVEL.RECEIPT_WORDING —— 兩份的鍵必須一樣，元件整份替換。
       */
      WORDING: {
        received: '發票已驗證',
        receivedShort: '發票已驗證',
        pending: '發票待驗證',
        pendingN: '發票待驗證 {n} 張',
        partial: '部分發票已驗證',
        deferred: '後開發票',
        issue: '發票有異常',
      } as Record<string, string>,
      /** 逐行驗證（付款視窗、批次付款、批次處理發票、請款單明細）。 */
      LINE: {
        /** 出納勾的那一格。 */
        CHECKBOX: '已驗證',
        /** 這一行已驗證（或已核銷）的唯讀標籤。 */
        VERIFIED: '已驗證',
        /** 逐行驗證之前，整張單在請款單層級驗證過的舊資料。 */
        VERIFIED_LEGACY: '已驗證（舊資料）',
        /** 已驗證的那一行：「已驗證 MM/DD 姓名」（缺日期或姓名時省略那一段）。 */
        VERIFIED_BY: '已驗證 {date} {name}',
        /** 一般行的送出鍵。 */
        MARK: '標記已驗證',
        /** 後開發票那一行：填入＋驗證一次完成。{noun} ＝ 發票／憑證（documentNounFor）。 */
        FILL_AND_MARK: '填寫{noun}並驗證',
        /** 補後開發票影像的拖放區提示（出納與申請人補發票共用 DeferredInvoiceFillPanel）。 */
        UPLOAD_HINT: '或將檔案拖曳至此 — 影像必填',
      },
      // 請款單詳情歷程裡的那一步（ApprovalTimeline）讀 WORDING.received —— 差旅單換成
      // TRAVEL.RECEIPT_WORDING.received（「憑證已驗證」），與發票卡片同一個字。
      /** 出納作業頁：付款紀錄的兩個篩選鍵、待驗證筆數，以及待付款佇列的排序選項。 */
      HISTORY_FILTER: {
        pending: '發票待驗證',
        received: '發票已驗證',
      },
      HISTORY_PENDING_COUNT: '{n} 筆發票待驗證',
      SORT_RECEIPT_READY: '發票已驗證優先',
      // （3.17.x 的 DEFERRED_SAVED_WARNING 已移除：v3.3 起「填寫發票資料」只做更正、
      // 不列後開待補的行（規格 §6.11），存檔再也不會留下一行只填一半的後開發票。）
      /** 批次處理發票面板（BatchInvoicePanel）。確認句的數字由畫面加粗，所以拆兩段。 */
      BATCH_PANEL: {
        TITLE: '批次發票驗證',
        INCOMPLETE: '仍有 {n} 筆尚未填妥，請依紅框補齊發票資料並勾選「已驗證」',
        CONFIRM_PREFIX: '即將處理',
        CONFIRM_SUFFIX: '筆發票驗證',
      },
      /** 批次付款頁（BatchPayPage）。 */
      BATCH_PAY: {
        SECTION_LABEL: '發票驗證',
        INCLUDED_TAG: '含發票驗證',
        /**
         * 伺服器說「還有行憑證未驗證」的那一筆（畫面上的驗證清單已過時）：頁面已重新載入
         * 它的明細，結果視窗那一列掛這個標籤；重試鍵改成回頁面重新核對（不重送舊的勾選）。
         */
        RELOADED_TAG: '已重新載入，請重新核對',
        RECHECK_FAILED: '重新核對失敗項目（{n} 筆）',
      },
      /**
       * 單筆付款視窗：伺服器以「還有行憑證未驗證」退回（畫面上的驗證清單已過時），頁面正在
       * 重新載入這張請款單 —— 確認送出鍵旁邊的那一句（載入後逐行驗證重建，照常核對再送）。
       */
      PAY_RELOADING: '請款單內容已變動，正在重新載入最新明細',
    },

    /**
     * 「重新上傳」—— 送出之後更換一行憑證（統一發票／收據）的影像 —— 前端專用（整組不鏡射）。
     * 誰能按、什麼時候鎖住：utils/receiptReplaceGate.ts（伺服器 payment-replaceLineReceipt
     * 的鏡射）。伺服器拒絕的字句在後端 BUSINESS_RULES.PAYMENT.RECEIPT_REPLACE；
     * LOCKED_HANDED_OVER／REQUESTER_AFTER_VERIFIED 兩句與後端同名鍵必須一字不差（按鈕旁的
     * 說明與伺服器的拒絕講同一件事）。只換影像：已驗證／已點收的行維持原狀、不回到待驗證，
     * 所以出納／保管人在已驗證的行上要勾 CHECK_TICK。
     */
    RECEIPT_REPLACE: {
      BUTTON: '重新上傳',
      CONFIRM_TITLE: '重新上傳',
      CONFIRM_BODY: '確定要替換目前的影像？',
      /** 確認框裡那一行：{line} ＝ 這一行的名稱，{fileName} ＝ 剛選的檔名（呼叫端代入）。 */
      FILE_LINE: '{line} · 新檔案 {fileName}',
      /**
       * 已驗證／已點收的行才出現的勾選框（沒勾不能確認）—— 一句話說完「核對了什麼、之後怎樣」
       * （PO 2026-10-08 D55）：影像已驗證的行 → CHECK_TICK；只有紙本已點收的行 → CHECK_TICK_PAPER_RECEIVED。
       */
      CHECK_TICK: '已核對新影像，維持「已驗證」',
      CHECK_TICK_PAPER_RECEIVED: '已核對新影像，維持「已點收」',
      CONFIRM: '確認',
      CANCEL: '取消',
      SUCCESS: '已更換影像',
      /** 按鈕的 aria-label（一張表有好幾顆「重新上傳」，讀出是哪一行）：{line} ＝ 「單號 #N 用途」。 */
      ARIA_LABEL: '重新上傳 {line}',
      /** 停用時的 aria-label：{reason} ＝ 下面兩句之一。 */
      ARIA_LOCKED: '重新上傳 {line}：{reason}',
      /** 按鈕停用時的 tooltip：這一行的紙本已交件。 */
      LOCKED_HANDED_OVER: '已交件：紙本已在事務所，不能更換',
      /** 按鈕停用時的 tooltip：申請人看自己已驗證的那一行。 */
      REQUESTER_AFTER_VERIFIED: '已驗證的憑證請洽出納更換',
      /**
       * 同上，但影像還沒驗證、只有紙本已點收在保管人手上（前端專用：伺服器拒絕時仍回
       * REQUESTER_AFTER_VERIFIED）。
       */
      REQUESTER_AFTER_PAPER_RECEIVED: '紙本已點收的憑證請洽出納更換',
      /** 紙本憑證頁（保管人）打開那張單的影像視窗的按鈕與標題。 */
      PAPER_IMAGES_BUTTON: '憑證影像',
      PAPER_IMAGES_TITLE: '憑證影像：{requestNumber} {requester}',
      /** 影像視窗的一行：「#1 用途 · NT$3,591」，有號碼時再接 PAPER_IMAGES_JOIN ＋ 號碼。 */
      PAPER_IMAGES_LINE: '#{n} {description} · NT${amount}',
      PAPER_IMAGES_JOIN: ' · ',
      /** 影像視窗裡沒有任何一行有紙本（例：打開之前那幾行剛被核銷）—— 說為什麼（UI-L4）。 */
      PAPER_IMAGES_EMPTY: '有紙本的行都已核銷，這張單沒有可看的憑證影像',
      PAPER_IMAGES_CLOSE: '關閉',
    },

    /**
     * 「同 #N」—— 一張統一發票／收據涵蓋同一張請款單的幾行（計畫 D7–D16、D28、D29、D31）——
     * 前端專用（整組不鏡射）。連結存在被涵蓋的那一行（items[].sameDocumentAs ＝ 目標行的
     * itemId）；那一行沒有自己的影像，號碼／日期／後開由伺服器從目標行抄。分組、張數、稅額
     * 拆分、重號檢查：utils/documentLinks.ts。伺服器拒絕的字句在後端
     * BUSINESS_RULES.PAYMENT.DOCUMENT_LINK。佔位：{n} ＝ 目標行在畫面上的序號（陣列索引 ＋ 1）；
     * {description} ＝ 用途；{lines} ＝ 那幾行的序號；{total} ＝ 合計含稅金額；{tax} ＝ 合計稅額；
     * {count} ＝ 行數；{kind} ＝ 發票／收據。
     */
    DOCUMENT_LINK: {
      TAG: '同 #{n}',
      TOOLTIP: '與第 {n} 行是同一張{kind}',
      PICKER_PLACEHOLDER: '同一張',
      /** 「同一張」旁的 ⓘ：什麼時候用 (PO 2026-10-08 D56)。 */
      PICKER_HINT: '多行共用一張發票／收據時用，號碼、日期、影像自動帶入',
      OPTION: '同 #{n} {description}',
      /** 收據欄「上傳」與「同 #N」選單之間的那個字。 */
      UPLOAD_OR: '或',
      UNLINK: '取消同一張',
      /** 請款單填寫：連結的行「同 #N」旁邊的取消（文字連結，不用 ✕ —— 與刪除整行的 ✕ 分開）。 */
      UNLINK_SHORT: '取消',
      /** 那個取消的 aria-label：{line} ＝ 這一行的序號。 */
      UNLINK_ARIA: '取消 #{line} 的同一張',
      /**
       * 補發票「一張發票」區塊（PO 2026-10-08 D57，取代「也補 #N」）：後面那一行選「同 #N」→
       * 搬進第 N 行的區塊，一組欄位、一顆按鈕補整張。{kind} ＝ 發票／收據；{total} ＝ 合計含稅；
       * {line} ＝ 「#2 用途」（紙本憑證頁沒有序號：「用途 · NT$金額」）。
       */
      BLOCK_TITLE: '一張{kind}',
      BLOCK_TOTAL: '合計 NT${total}',
      BLOCK_TOTAL_HINT: '對照{kind}上的總金額',
      /** 區塊裡跟著的行旁的 ✕（tooltip）＋ aria-label。 */
      SPLIT: '分開',
      SPLIT_ARIA: '分開 {line}',
      /** 請款單上就設了「同一張」的行：在區塊裡、沒有 ✕（伺服器一定一起補），鎖的 tooltip。 */
      FORM_LINKED_TOOLTIP: '請款單上已設為同一張',
      /**
       * 補發票：後面那一行欄位上方的勾（PO 2026-10-08 D62，取代「同 #N ▾」與它的 ⓘ —— 字自己說明）。
       * 上方只有一行可以是同一張 → SAME_AS_ONE；兩行以上 → SAME_AS_ABOVE，勾了再選 SAME_AS_PICK
       * 哪一行（選項「#1 用途」，選了才搬進那一行的區塊）。{kind} ＝ 發票／收據。
       */
      SAME_AS_ONE: '與 #{n} 同一張{kind}',
      SAME_AS_ABOVE: '與上方同一張{kind}',
      SAME_AS_PICK: '哪一行？',
      /**
       * 總務補發票對話框：其餘後開待補的行，各一個勾把它併進這一張（D62；紙本行沒有 #N，寫主行的
       * 用途）。{main} ＝ 主行用途、{description}／{amount} ＝ 那一行的用途與含稅金額。
       */
      PAPER_OTHERS_TITLE: '其他後開待補的行',
      PAPER_SAME_AS_CHECK: '與{main}同一張{kind}：{description} · NT${amount}',
      /** 「同 #N」連到一行、而那一行不在畫面上的區塊裡（舊資料）：這一行沒有自己的欄位。 */
      FOLLOWS_TARGET: '跟著 #{n} 一起補',
      /** 出納驗證目標行時，同一組的其他行一起驗證（計畫 D15）。 */
      VERIFIED_WITH_TARGET: '跟著 #{n} 驗證',
      /** 影像視窗表頭／核對清單的合計（計畫 D16）。 */
      GROUP_TOTAL: '同一張{kind}：{lines} ＝ 合計 NT${total}',
      /** GROUP_TOTAL 的 {lines}：每一行「#1 獅子 M NT$3,591」，以 GROUP_LINES_JOIN 串起（mockup B5）。 */
      GROUP_LINE: '#{n} {description} NT${amount}',
      GROUP_LINES_JOIN: ' ＋ ',
      /** 會計對帳 Excel 費用明細的費用備註（計畫 D28 (b)；{kind} 依憑證類型，D49）。 */
      EXPORT_NOTE: '同一張{kind} {lines} · 合計 NT${total} · 稅額 {tax}',
      /** EXPORT_NOTE 的 {lines}：每一行寫成 LINE_REF，以 LINES_JOIN 串起（「#1＋#2」）。 */
      LINE_REF: '#{n}',
      LINES_JOIN: '＋',
      /** 費用備註裡，這一行自己的備註與 EXPORT_NOTE 之間。 */
      EXPORT_NOTE_JOIN: '；',
      /** 同一張單另一行已有這個號碼、兩行沒有連結（計畫 D29：附「設為同 #N」，絕不自動連結）。 */
      DUPLICATE_IN_REQUEST: '此發票號碼已在 #{n}，是同一張嗎？',
      SET_LINK_BUTTON: '設為同 #{n}',
      /** 實體張數（例：「發票 2 張」）—— 同一組只算一張（countSheets）。 */
      SHEET_COUNT: '{kind} {n} 張',
      /** 不能設「同一張」的請款類型（計畫 D20：出差旅費有四張表，「#N」說不清楚；伺服器同樣拒絕）。 */
      NO_LINK_REQUEST_TYPES: ['travel_expense'] as readonly string[],
      /**
       * SHEET_COUNT 的 {kind}，也是每一句「同一張{kind}」的 {kind}（D49：收據的一組寫「同一張收據」，
       * 其餘 —— 含不知道類型的 —— 寫「發票」）；兩種都有時以 SHEET_COUNT_JOIN 串起（「發票 2 張 · 收據 1 張」）。
       */
      SHEET_KIND_INVOICE: '發票',
      SHEET_KIND_RECEIPT: '收據',
      SHEET_COUNT_JOIN: ' · ',
      /** 請款單填寫：檔案拖到「同 #N」的那一行（它沒有自己的影像，D12）。 */
      DROP_ON_LINKED: '此項目與 #{n} 是同一張，影像請在 #{n} 上傳',
      /** 審核／付款／批次畫面、填寫發票資料：連結的行停用的號碼／日期欄的 tooltip。 */
      LINKED_FIELD_TOOLTIP: '與 #{n} 是同一張，號碼與日期跟著 #{n}，請在 #{n} 修改',
      /** 填寫發票資料：停用的「同一張」選單的 tooltip（已交件 → D14；其餘 → 不能參加同一張的行）。 */
      PICKER_LOCKED_HANDED_OVER: '已交件，不能更改「同一張」',
      PICKER_LOCKED: '這一行不能更改「同一張」(已核銷、後開待補或出差旅費)',
      /**
       * 填寫發票資料：已驗證的行設了或取消了「同一張」—— 存檔之後那一行回到待驗證（D50），
       * 存檔前在儲存鍵旁邊說一句。
       */
      RELINK_RESETS_VERIFIED: '改了「同一張」的行會回到待驗證',
      /** 編輯更正：一組裡的行不能改憑證類型（D38；伺服器 LINK_BLOCKS_TYPE_CHANGE 同一句）。 */
      LINK_BLOCKS_TYPE_CHANGE: '此品項有「同一張」的關聯，請先取消「同一張」，再更改憑證類型',
      /** 出納逐行列 異常核銷 的表單：一組會整組一起核銷（D46）。{lines} ＝ 「#1 用途、#2 用途」。 */
      WRITE_OFF_GROUP: '同一張{kind}的 {lines} 會一起核銷',
      WRITE_OFF_GROUP_JOIN: '、',
    },

    /**
     * 出納／主管在付款或核准時代請款人更正了該列的發票號碼／日期時，請款人在
     * 請款單明細上看到的署名 —— 前端專用（不鏡射）。資料來自 items[] 的
     * invoiceEditedByName / invoiceEditedAt（CF 於套用修改時寫入，兩者同時寫）。
     * `{name}` = 更正者、`{date}` = 更正日期。
     * NAME_ONLY 用於舊資料只留下姓名、沒有時間的情況 —— 少了日期也要讓請款人
     * 知道這一列被改過，不能整句不顯示。
     */
    INVOICE_EDITED_NOTICE: {
      WITH_DATE: '由 {name} 於 {date} 更正',
      NAME_ONLY: '由 {name} 更正',
    },

    /**
     * Request types that never carry a receipt, whatever their line items say.
     * 退費 has no receipt to collect — its 折讓發票單號 is captured at submit
     * (refundInfo.creditNoteNumber) — and has no line items, so the per-line
     * 免憑證 rule cannot catch it. Salary needs no entry: its lines are all
     * no_document. Read via `expectsReceipt()` (useReceiptHelpers), never inline.
     */
    NO_RECEIPT_TYPES: ['customer_refund'] as readonly string[],

    /** Tag color type for each payment request type (Naive UI NTag type prop). */
    TYPE_TAG_COLORS: {
      reimbursement: 'default',
      supplier_payment: 'info',
      petty_cash: 'warning',
      supervisor_payment: 'default',
      customer_refund: 'info',
      travel_expense: 'success',
    } as Record<string, string>,

    TYPE_LABELS: {
      reimbursement: '報銷',
      supplier_payment: '供應商付款',
      petty_cash: '零用金',
      supervisor_payment: '薪資',
      customer_refund: '退費',
      travel_expense: '差旅費',
    } as Record<string, string>,

    /**
     * 請款類型 → 指示面板色系（`.pay-indicator--*` / `.pay-item-card--family-*`）。
     *
     * 一個類型在每一個畫面都穿同一個顏色；元件一律查這張表，不得自己選色
     * （UI_DESIGN_SPEC.md §6：每個面板家族顏色唯一）。薪資取 lime —— 該色系已在
     * §6b 登記過（BF 商品現金），這裡是它在請款模組的第二個用途，三個值同一組 token。
     * 顏色是輔助訊號：每張卡片仍必須同時顯示類型文字，色盲與黑白列印都要讀得懂。
     */
    TYPE_PANEL_FAMILY: {
      reimbursement: 'slate',
      supplier_payment: 'blue',
      petty_cash: 'teal',
      customer_refund: 'indigo',
      travel_expense: 'cyan',
      supervisor_payment: 'lime',
    } as Record<string, string>,

    /** 出差旅費 — display labels for TravelRegion / LodgingMode / TravelLineKind / TravelEvidenceKind. */
    TRAVEL_REGION_LABELS: { domestic: '國內', intl: '國外', prc: '大陸港澳' } as Record<
      string,
      string
    >,
    LODGING_MODE_LABELS: {
      per_diem_full: '全額日支（不另檢據住宿）',
      actual_lodging_50: '宿費核實＋膳雜費 50%',
      /** 國內（查核準則 §74）：膳雜費依職稱全額計給，住宿費另行檢據列報。 */
      domestic_receipted: '膳雜費全額＋住宿費檢據',
    } as Record<string, string>,
    LINE_KIND_LABELS: {
      per_diem: '生活費',
      transport: '交通費',
      lodging: '住宿費',
      /** 實報實銷限定 —— 日支數額法的膳雜費已含於日支數額內，另列膳食費即重複列報。 */
      meal: '膳食費',
      other: '其他',
    } as Record<string, string>,
    EVIDENCE_KIND_LABELS: {
      e_ticket: '電子機票',
      boarding_pass: '登機證',
      passport: '護照影本',
      airline_cert: '航空公司搭機證明',
      entry_exit_cert: '入出國日期證明書',
      other: '其他',
    } as Record<string, string>,

    TAX_TYPE_LABELS: {
      taxable_5: '應稅 5%',
      tax_exempt: '免稅',
      zero_rated: '零稅率',
    } as Record<string, string>,

    /**
     * Reverse lookup for Excel-paste tax-type parsing.
     * Maps human-readable Chinese labels (from spreadsheet cells) to the canonical TaxType code.
     * Centralised here so the paste logic in LineItemTable is not the source of truth.
     */
    TAX_TYPE_PASTE_LABELS: {
      應稅5: 'taxable_5', // stripped of '%' for loose matching
      '應稅5%': 'taxable_5',
      '應稅 5%': 'taxable_5',
      '統一發票（應稅5%）': 'taxable_5',
      '統一發票（免稅）': 'tax_exempt',
      '收據（免用統一發票）': 'tax_exempt',
      免稅: 'tax_exempt',
      零稅率: 'zero_rated',
      免憑證: 'tax_exempt',
    } as Record<string, string>,

    DOCUMENT_TYPE_LABELS: {
      uniform_invoice: '統一發票',
      receipt_only: '收據',
      no_document: '免憑證',
      document_elsewhere: '憑證另附（發票在他單）',
      document_lost: '憑證遺失',
    } as Record<string, string>,

    PAYMENT_METHOD_OPTIONS: [
      { value: 'bank_transfer', label: '銀行轉帳' },
      { value: 'cash', label: '現金' },
      { value: 'cash_withdrawal', label: '現金提款' },
      { value: 'check', label: '支票' },
      { value: 'other', label: '其他' },
    ] as Array<{ value: string; label: string }>,

    PAYMENT_TERM_OPTIONS: [
      { value: 'immediate', label: '即期（下一個付款日）' },
      { value: 'custom_date', label: '指定到期日' },
    ] as Array<{ value: string; label: string }>,

    /** Legacy terms removed from new requests but still displayed for existing data. */
    LEGACY_PAYMENT_TERM_LABELS: {
      end_of_month: '月結',
      net_30: '月結30天',
      net_60: '月結60天',
    } as Record<string, string>,

    /** Recurring payment frequency options shown when 此為定期付款 is checked. */
    SCHEDULE_FREQUENCY_OPTIONS: [
      { value: 'monthly', label: '月繳' },
      { value: 'yearly', label: '年繳' },
    ] as Array<{ value: string; label: string }>,

    HELPERS: {
      /** Check if a date string (YYYY-MM-DD) falls on a weekend (Sat/Sun).
       *  Note: Does NOT account for 補上班日 — caller should warn user, not auto-adjust. */
      isWeekend: (dateStr: string): boolean => {
        const d = new Date(dateStr + 'T00:00:00+08:00');
        const day = d.getDay();
        return day === 0 || day === 6;
      },
    },

    COMBINED_TAX_DOC_OPTIONS: [
      {
        value: 'taxable_5__uniform_invoice',
        label: '統一發票（應稅5%）',
        taxType: 'taxable_5',
        documentType: 'uniform_invoice',
      },
      {
        value: 'tax_exempt__uniform_invoice',
        label: '統一發票（免稅）',
        taxType: 'tax_exempt',
        documentType: 'uniform_invoice',
      },
      {
        value: 'tax_exempt__receipt_only',
        label: '收據（免用統一發票）',
        taxType: 'tax_exempt',
        documentType: 'receipt_only',
      },
      {
        value: 'tax_exempt__no_document',
        label: '免憑證',
        taxType: 'tax_exempt',
        documentType: 'no_document',
      },
      // 憑證另附 carries BOTH tax variants. 免憑證 exists only as tax_exempt,
      // which is why using it for a split invoice silently dropped the 5% input
      // tax on the other half.
      {
        value: 'taxable_5__document_elsewhere',
        label: '憑證另附・發票在他單（應稅5%）',
        taxType: 'taxable_5',
        documentType: 'document_elsewhere',
      },
      {
        value: 'tax_exempt__document_elsewhere',
        label: '憑證另附・發票在他單（免稅）',
        taxType: 'tax_exempt',
        documentType: 'document_elsewhere',
      },
      {
        value: 'tax_exempt__document_lost',
        label: '憑證遺失',
        taxType: 'tax_exempt',
        documentType: 'document_lost',
      },
    ] as Array<{ value: string; label: string; taxType: string; documentType: string }>,
    /**
     * 紙本憑證（驗收・催交・交件給事務所）—— MIRROR of
     * functions/src/config/appConfig.ts BUSINESS_RULES.PAYMENT.PAPER_RECEIPT
     * （functions/scripts/check-config-mirror.cjs 逐字比對，鍵的順序與字句都不得飄移）。
     * 純前端字串（頁面標題、篩選、欄位名）放在 PAPER_RECEIPT_UI，不比對。
     */
    PAPER_RECEIPT: {
      /** 送出請款後，申請人應在幾天內把紙本交給總務（送出後畫面的提示）。 */
      HAND_IN_DAYS: 7,
      /** 期限 ＝ 催交當日 ＋ N 天（Taipei）。 */
      TRACKING_DEADLINE_DAYS: 30,
      /** 主管按一次「延長」加幾天。 */
      EXTENSION_DAYS: 30,
      /** 同一張單的「LINE 提醒」最短間隔。 */
      REMINDER_COOLDOWN_HOURS: 24,
      /**
       * 上線切點（付款月份 YYYY-MM）。付款日在此之前、且從未標記過紙本狀態的已付款單
       * 不進紙本憑證清單、不計入月結 —— 那些紙本已依舊流程交給會計師。未付款的舊單
       * 仍在範圍內（它們會在切點之後才付款）；已標記過的單一律保留（已進入新流程）。
       */
      START_MONTH: '2026-09',
      /** 備註／原因欄位長度上限（會印在月結清單的備註欄，太長排不下）。 */
      NOTE_MAX_LENGTH: 200,
      /**
       * 紙本憑證清單只列建立在這幾個月內的請款單（催交中、結案而未入交件的不限）。
       * 搜尋的「建立超過 12 個月的單不在此頁」讀同一個數字。
       */
      LOOKBACK_MONTHS: 12,
      /**
       * 發票號碼搜尋（D1／D2）：此頁沒有、且打滿一個完整號碼時，伺服器查全部請款單，
       * 回答「為什麼不在此頁」（只讀）。
       */
      LOOKUP: {
        /** 正規化後的完整統一發票號碼：2 個英文字母 ＋ 8 位數字。 */
        COMPLETE_NUMBER_PATTERN: '^[A-Z]{2}[0-9]{8}$',
        /** 一個號碼最多回幾張請款單。 */
        MAX_RESULTS: 10,
        /**
         * 一張請款單只回一種原因，依這個順序判斷（P3）；回覆也依這個順序排。
         * notReadable＝呼叫者看不到的那一包（主管請款）；refresh＝應該在此頁、
         * 但此頁是在它進來之前打開的。
         */
        KINDS: [
          'notReadable',
          'refresh',
          'voided',
          'draft',
          'declined',
          'returned',
          'refund',
          'writtenOff',
          'beforeStart',
          'outsideWindow',
        ],
      },
      /**
       * 申請人可從「我的請款」補發票（後開發票）的狀態。前端決定按鈕出不出現，
       * 伺服器（payment-fillDeferredInvoice）用同一份清單把關 —— 兩邊同一個來源。
       * 審核中的單刻意不開：它還可能被退回修改，發票資料應跟著表單一起改。
       */
      DEFERRED_FILL_STATUSES: ['approved', 'paid'] as readonly string[],
      /**
       * 紙本狀態。'pending' ＝ 文件上沒有 paperReceipt 欄位（尚未標記）。
       * 「逾期」不是狀態，是 tracking ＋ 期限已過算出來的，所以不在這裡。
       */
      STATUSES: {
        PENDING: 'pending',
        RECEIVED: 'received',
        TRACKING: 'tracking',
        CLOSED: 'closed',
      },
      /**
       * 含 overdue（算出來的）— 標籤表要能標示它。
       * v3.3 用字（PO 2026-09-15）：紙本在手上核對＝「驗收」，D30（PO 2026-11-03）起改稱「點收」；紙本沒交來＝「催交」。
       * 鍵是資料值（paperReceipt.status），不跟著改名。
       */
      STATUS_LABELS: {
        pending: '待點收',
        received: '已點收',
        tracking: '催交中',
        overdue: '逾期',
        closed: '結案',
      } as Record<string, string>,
      /**
       * 分包的**對外名稱** —— 送件單、紙本憑證頁的分包切換、交件對話框標題都用它。
       * 「機密」是對內讓主管知道這一包誰看得到的說法；送到事務所的那張紙上寫
       * 「機密」沒有意義，對方要知道的是「這是主管那一包」（PO 2026-09-14）。
       * 系統其他地方（權限訊息、送出後提示等）仍用「機密請款」，那是對內的字。
       */
      BUNDLES: {
        general: '一般請款',
        confidential: '主管請款',
      } as Record<string, string>,
      /** 各分包的收件人角色名 —— MESSAGES.NOT_CUSTODIAN 的 {who}。 */
      CUSTODIAN_LABELS: {
        general: '總務',
        confidential: '主管',
      } as Record<string, string>,
      /**
       * 可以交件的請款單狀態（PO 2026-09-14）：系統狀態「已付款」＝出納已安排付款，
       * 含待放行／待提領／已放行。v3.3 起驗收也要在付款之後（規格 §3），之前的列
       * 顯示「待付款」、沒有按鈕。
       */
      ENCLOSABLE_STATUSES: ['paid'] as readonly string[],
      /**
       * 「待處理」（紙本憑證頁第一個、預設的分頁，規格 v3.3 §6.10）＝ 保管人現在該動手的列：
       * 紙本欄的主要狀態（佇列列的 paperState）是待點收、催交中、後開已補 · 待點收三者之一。
       * 分頁的篩選與筆數（utils/paperQueueDisplay）和首頁的「筆紙本憑證待點收」／
       * 「筆機密紙本憑證待點收」卡片（後端 admin/dashboard.ts）讀同一份，卡片點進去才對得起來。
       */
      PENDING_TAB_STATES: ['toVerify', 'chasing', 'deferredFilledToVerify'] as readonly string[],
      /**
       * 誰能做什麼 —— 與後端 authMiddleware 同一套角色字串。
       * 畫面的 capabilities（useRoleContext）與 CF 的守門都讀這一份。
       */
      ROLES: {
        /** 開得了紙本憑證頁的人（出納對此頁唯讀）。 */
        QUEUE: ['general_affairs', 'supervisor', 'treasury'] as readonly string[],
        /**
         * 驗收／催交／補發票（後開）：一般包＝總務或主管（主管是總務的後備），
         * 機密包＝主管（規格 v3.3 §4）。
         */
        CUSTODY: {
          general: ['general_affairs', 'supervisor'] as readonly string[],
          confidential: ['supervisor'] as readonly string[],
        },
        /** 預覽並列印／交件／填其他附件 —— 與收件同一批人。 */
        HANDOVER: {
          general: ['general_affairs', 'supervisor'] as readonly string[],
          confidential: ['supervisor'] as readonly string[],
        },
        /** 延長／結案（要填原因）—— 主管，兩包皆同。 */
        RESOLVE: ['supervisor'] as readonly string[],
        /**
         * 整批備註（D19／D22，計畫 P13）—— 新增：一般包＝總務、出納、主管（看得到送件單的人）；
         * 主管請款＝主管（只有主管看得到那一包，D22）。
         */
        HANDOVER_NOTES: {
          general: ['general_affairs', 'supervisor', 'treasury'] as readonly string[],
          confidential: ['supervisor'] as readonly string[],
        },
        /** 作廢任何一則整批備註（D21）。寫的人自己那一則一定可以作廢，不靠這張清單。 */
        HANDOVER_NOTES_VOID_ANY: ['supervisor'] as readonly string[],
      },
      /**
       * 交件（送件單）。一次交件 ＝ 一張獨立的流水帳，不回頭更正前一張
       * （docs/PAPER_RECEIPT_BUSINESS_RULES.md v3.2）。
       */
      HANDOVER: {
        /** 單批最多隨附幾筆（依憑證月份由早到晚）；實際上限 min(450, 499 − 結案筆數)；結案一批也最多這麼多筆。 */
        MAX_ROWS: 450,
        /** 交件紀錄文件的大小保護上限（bytes）。 */
        MAX_DOC_BYTES: 900000,
        /** 「其他附件」自由文字上限 —— 對話框的 maxlength 讀這個值。 */
        ATTACHMENT_NOTE_MAX_LENGTH: 1000,
        /**
         * 一張憑證的交件備註（D10／D31）長度上限 —— 點收與交件預覽的備註框 maxlength 讀這個值；
         * 與催交備註同一個數，兩者印在同一欄。只顯示、不比對。
         */
        NOTE_MAX_LENGTH: 200,
        /** 一次交件最多帶幾則交件備註（伺服器驗證層的保護上限）。 */
        NOTE_MAX_COUNT: 2000,
        /** 交件的識別＝交件日期＋分包（不編流水號）。 */
        LABEL: '{bundle} {date}',
        /** 識別字串一律帶時間（同日兩批才不會撞名；封面本來就印交件時刻）。 */
        LABEL_WITH_TIME: '{bundle} {date} {time}',
      },
      /**
       * 整批備註（D19–D22，計畫 P13）：交件之後寫在那一次交件旁邊的一則一則備註，只加不改；
       * 寫錯的作廢（劃線留著，D20）。只在畫面上，不印在送件單上。只顯示、不比對。
       */
      HANDOVER_NOTES: {
        /** 一則的長度上限 —— 輸入框的 maxlength 讀這個值（伺服器同一個數）。 */
        MAX_LENGTH: 500,
      },
      /**
       * 建議節奏（提示，不是規則）：營業稅每兩個月申報一次，單月 15 日前。
       * 頁面在申報月份的 1–{SHOW_UNTIL_DAY} 日顯示；句子由後端算好帶回來。
       */
      HANDOVER_HINT: {
        FILING_MONTHS: [1, 3, 5, 7, 9, 11] as readonly number[],
        SHOW_UNTIL_DAY: 5,
        /**
         * 最遲寄出日（N5，PO 2026-09-19）：15 日申報，13 日寄出只剩 1–2 天給事務所
         * 處理退件或補件；目標 5 日、硬性底線 13 日，兩個日期都印在畫面上。
         */
        HARD_DEADLINE_DAY: 13,
        /** 事務所的營業稅申報期限（每逢單月）。 */
        FILING_DAY: 15,
        TEXT: '{month}/{filingDay} 申報 {year} 年 {p1}–{p2} 月營業稅：請在 {month}/{day} 前交齊那兩個月的憑證 (最遲 {month}/{hardDay})。',
        OLDEST_WAITING_TEXT: '目前待交件最久 {n} 天。',
        /** 紙本頁交件區塊旁的常駐說明（N5）—— 與上面的時效提示分開，永遠顯示。 */
        STANDING_TEXT:
          '每逢單數月將前兩個月的紙本憑證連同送件單寄給事務所 (例：9 月交 7–8 月)：{softDay} 日前寄出，最遲 {hardDay} 日，事務所 {filingDay} 日要申報營業稅。',
      },
      /**
       * 送件單上每一個日期都要標明它是哪一種日期。後開發票與「收據日期晚於付款日」
       * 印的是消費日期，憑證本身的日期改印在第二行（SHEET.DATE_SECOND_LINE）。
       */
      DATE_SOURCE_LABELS: {
        invoice: '發票日',
        receipt: '收據日',
        invoice_late: '消費日',
        receipt_late: '消費日',
        expense: '消費日',
      } as Record<string, string>,
      /** 明細「憑證」欄的憑證類型字樣（例：發票 2、收據 1）。 */
      VOUCHER_LABELS: {
        uniform_invoice: '發票',
        receipt_only: '收據',
      } as Record<string, string>,
      DIALOG: {
        /**
         * 驗收核對清單（規格 v3.3 §5）：寫出這一次驗收的實際內容，不是泛稱。
         * 全部勾選前「確認點收」不可按；對不上就改用催交（RECEIVED_MISMATCH_HINT）。
         *
         * text 的佔位符由畫面代入：
         *  - {counts} ＝ 依 RECEIVED_CHECKLIST_COUNTS 組出來的張數（只列有的那一種）；
         *  - {taxId} ＝ 公司統編（getPaperReceiptQueue 回傳的 companyTaxId）；
         *  - {companyName} ＝ COMPANY_INFO.name（公司抬頭；前後端各有這一個常數，
         *    送件單封面印的也是它，不另存第二份）。
         * appliesTo：'always' 一律出現；'uniform_invoice'／'receipt_only' 只在這一次
         * 驗收的紙本含那一種憑證時出現（③ 買方統編只問統一發票、④ 抬頭只問收據）。
         */
        RECEIVED_CHECKLIST_ITEMS: [
          { key: 'count', text: '張數：{counts}', appliesTo: 'always' },
          { key: 'amount', text: '每張金額與上表相符', appliesTo: 'always' },
          {
            key: 'taxId',
            text: '每張統一發票的買方統編是 {taxId}',
            appliesTo: 'uniform_invoice',
          },
          {
            key: 'companyName',
            text: '每張收據的抬頭是 {companyName}',
            appliesTo: 'receipt_only',
          },
        ],
        /**
         * 核對清單第 ① 項 {counts} 的兩個部分，依憑證類型；這一次沒有的那一種不列，
         * 兩者之間以 RECEIVED_CHECKLIST_SEPARATOR 相連。
         * 例：「統一發票 2 張（號碼 AB12345678、CD87654321）、收據 1 張」。
         * 張數是實體張數（規格 §2：統一發票依號碼去重，收據一行一張）。
         */
        RECEIVED_CHECKLIST_COUNTS: {
          uniform_invoice: '統一發票 {invoiceCount} 張 (號碼 {invoiceNumbers})',
          receipt_only: '收據 {receiptCount} 張',
        } as Record<string, string>,
        /** {counts} 的兩部分之間、{invoiceNumbers} 的號碼之間，都用這個分隔字。 */
        RECEIVED_CHECKLIST_SEPARATOR: '、',
        RECEIVED_MISMATCH_HINT: '有一項對不上：按「取消」，改用「催交」，備註寫明缺什麼。',
        /**
         * 催交對話框（v3.3）。送件單不再印催交中的列（規格 §6.7），所以這裡不再說
         * 「會列在送件單」；催交中只顯示在紙本憑證頁與首頁卡片。
         * LINE 只發給一般請款的申請人（機密包的保管人就是主管本人）。
         */
        TRACKING_LINE1:
          '這幾筆會顯示「催交中」，直到點收或結案；一般請款會以 LINE 通知申請人補交紙本。',
        TRACKING_LINE2:
          '請最晚於 {days} 天內 ({deadline} 前) 取得紙本，點收後隨下一次交件一併送出。',
        EXTEND_TITLE: '延長 {days} 天',
        EXTEND_HINT: '新期限：{newDeadline} (原 {deadline})。這一筆會繼續催交，直到點收或結案。',
        EXTEND_REASON_REQUIRED: '請填延長原因',
        CLOSE_TITLE: '結案',
        CLOSE_HINT:
          '視為未取得憑證，會在下一次送件單的「未取得憑證 (結案)」列出一次。結案後無法再變更這筆的紙本狀態。',
        CLOSE_REASON_REQUIRED: '請填結案原因',
        /**
         * 主管逐張結案（遺失，D24／D45，W8c.4）：從已點收的列打開結案時，視窗問遺失的是哪一張
         * （只列在手上、還沒交件、不是「不在信封內」的憑證；同一張的幾行一起勾）。CLOSE_HINT 在
         * 這裡不成立（其餘的憑證照常交件），所以另一句。
         */
        CLOSE_LOST_QUESTION: '遺失的是哪一張？',
        CLOSE_LOST_HINT:
          '勾的那幾張會在下一次送件單的「未取得憑證 (結案)」列出一次；其他張照常交件。',
        /** 一張都沒勾（K15(b)；畫面先擋，伺服器也擋）。 */
        CLOSE_LOST_MISSING: '還缺：遺失的是哪一張',
        /** 取消點收沒寫原因（D33；畫面先擋，伺服器也擋）。 */
        CANCEL_RECEIVED_REASON_REQUIRED: '請填原因',
      },
      /**
       * 送件單（A4，只有黑／灰）印出來的每一句話 —— 封面、明細、尚未取得憑證三部分。
       * 畫面只排版、代入 {} 佔位符，不自己拼句子（規格 §6.7／§6.8）。
       */
      SHEET: {
        TITLE: '紙本憑證送件單',
        /** 每一頁的頁首（規格 §6.8）。 */
        RUNNING_HEAD: '{company} · {title} · {bundle}',
        COMPANY_LINE: '{company}　統一編號 {taxId}',
        LABEL_CAPTION: '交件',
        /** 預覽不記錄、不封存 —— 每一頁都要看得出來它還不是交件憑據。 */
        PREVIEW_BANNER: '預覽 — 尚未交件',
        META: {
          PREPARER: '整理人',
          LAST: '上次交件',
          DATE: '交件日期',
          LAST_VALUE: '{date} · {requests} 筆 · {pages} 張',
          LAST_NONE: '無 (這是第一次交件)',
        },
        SCOPE:
          '本次交件：截至 {time}，已安排付款或已放行、紙本已點收且尚未交件的憑證 (不分憑證月份)。',
        /**
         * 總務保留了一部分沒交（規格 §6.2：勾＝已放進信封，沒勾的留在待交件）。
         * 這時候上面那句「全部」就是**不實**的 —— 事務所拿著簽收聯，看不出來
         * 有東西被留下。所以少交一張就換一句話，把張數講明白。
         * v3.3 以**張**計（事務所點的是紙，不是請款單）：{pages} ＝ 這一批封存的張數、
         * {heldPages} ＝ 符合條件卻沒有勾（或超過單批上限）的張數 —— 交件紀錄的
         * scope.{pages, heldPages}。
         */
        SCOPE_PARTIAL:
          '本次交件：截至 {time}，交出紙本已點收的憑證 {pages} 張；另有 {heldPages} 張符合條件，留待下次交件。',
        /** 超過單批上限時的範圍句（規格 §6.6；只有預覽會用到）。 */
        SCOPE_CAPPED:
          '本次交件：截至 {time}，已安排付款或已放行、紙本已點收且尚未交件的憑證中，依憑證月份最早的 {pages} 張；其餘 {heldPages} 張請再按一次交件。',
        ATTACHED: {
          TITLE: '隨附紙本',
          ASIDE: '請依張數核對',
          PAGES: '憑證 (實體張數)',
          PAGES_UNIT: '張',
          PAGES_SPLIT: '統一發票 {invoice} · 收據 {receipt}',
          TOTAL: '憑證合計',
          TOTAL_UNIT: '元',
          NON_PAYABLE: '含不付款憑證 {n} 張 {amount} 元',
          /** 有憑證不在信封內時（D10／P9，只有 sheetLayout 2 的紀錄）：實體張數只算信封裡的。 */
          OUTSIDE_ENVELOPE: '另 {n} 張不在信封內 (見交件明細備註)',
        },
        BY_MONTH: {
          TITLE: '依憑證月份',
          ASIDE: '月份依發票／收據上的日期；後開發票及晚於付款的收據依消費日期',
          /** 標籤本身由畫面畫成方框（與明細的日期標籤同一種樣子），這裡只給張數。 */
          BASIS_ITEM: '{n} 張',
          COLUMN_MONTH: '憑證月份',
          COLUMN_PAGES: '張數',
          COLUMN_AMOUNT: '憑證金額',
          TOTAL: '合計',
          FOOTNOTE: '分月規則與會計對帳 Excel「費用明細」相同。',
          FOOTNOTE_SPAN:
            '跨月請款單 {n} 筆 ({list})：張數與金額依明細分入各月，筆數計在最早的月份。',
        },
        /** 封面「憑證合計」底下：這一批有多少是主管專案。 */
        PROJECT_LINE: '含主管專案 {n} 張 {amount} 元',
        /**
         * v3.3 起封面不再有「尚未取得憑證」那一格（第 3 頁移除，規格 §6.7）；只剩結案
         * 超過單批上限的那一句，封面與明細頁都印。
         */
        AWARE: {
          /** 結案超過單批上限（HANDOVER.MAX_ROWS）：其餘下一批自動列出，這裡先講明少了幾筆。 */
          CLOSED_HELD: '另有 {n} 筆結案超過單批上限，未列入本批，將列在下一次送件單。',
        },
        /** 事務所收件時唯一要做的動作：數張數、勾一個、簽名。 */
        COUNT_CHECK: {
          LABEL: '張數核對 (事務所填)',
          MATCH: '相符',
          MISMATCH_PREFIX: '不符，實收',
          MISMATCH_SUFFIX: '張',
        },
        SIGN: {
          HANDOVER: '交件人 簽章',
          FIRM: '事務所 簽收',
          DATE: '日期',
        },
        DETAIL: {
          TITLE: '交件明細',
          CAPTION: '隨附紙本明細',
          COLUMN_REQUEST_NUMBER: '單號',
          COLUMN_REQUESTER: '申請人',
          COLUMN_SUMMARY: '收款人／款項',
          COLUMN_DATE: '憑證日期',
          COLUMN_VOUCHER: '憑證',
          COLUMN_AMOUNT: '憑證金額',
          COLUMN_PAYMENT: '付款',
          COLUMN_NOTE: '備註',
          GROUP_ASIDE: '{n} 筆',
          /** 這個月還含了某張跨月請款單的明細（筆數已計在較早的月份）。 */
          GROUP_ASIDE_SPAN: '{n} 筆 (另含 {list} 的本月明細)',
          /** 封面月份表裡，主管專案那幾列的月份後綴（Σ 各列必須等於合計）。 */
          PROJECT_ROW_SUFFIX: ' (主管專案)',
          SUBTOTAL: '{month}小計 (依明細)',
          /**
           * 明細分兩段的段名。**只寫類別名，不加任何解釋**（PO 2026-09-14）：
           * 「與一般費用分開入帳」那一類的句子會被讀成公司在記兩本帳，而事務所
           * 看到類別就知道怎麼處理 —— 與 CPA_EXPORT 當年把 外帳／內帳 改成
           * 公司費用／專案 是同一條理由。只有這一批真的有專案憑證時才印。
           */
          PROJECT_TITLE: '主管專案',
          PROJECT_SUBTOTAL: '專案合計',
          COMPANY_TITLE: '一般費用',
          SUBTOTAL_SPAN: '{month}小計 (依明細，含 {list})',
          TOTAL: '本次交件合計　{n} 筆',
          PAGES_CELL: '{n} 張',
          VOUCHER_CELL: '{label} {n}',
          /**
           * 交件備註印在那一行的備註欄：粗體標籤（D35／D37）＋備註原文；同一行另有「憑證有問題」
           * 的備註時，交件備註在前（N14）。點收與交件預覽的備註框收起後也用這兩個標籤。
           */
          LINE_NOTE_LABEL: {
            OUTSIDE_ENVELOPE: '【不在信封內】',
            IN_ENVELOPE: '【備註】',
          },
          /**
           * sheetLayout 2 的交件明細（D3／D4，畫面代入；後端不印）：請款單那一列的憑證欄
           * （每一部分自己的張數）、混合的單另一部分的張數與金額（{title} ＝ COMPANY_TITLE／
           * PROJECT_TITLE）、一張紙涵蓋兩段的行時沒有那張紙的那一段（D40，{line} ＝ 拿到
           * 那張紙的那一行在請款單上的序號）、每一張憑證那一行的不付款標記（W0.0 F18）、一張
           * 單長過一頁時下一頁第一行（F22）。
           */
          VOUCHER_CELL_PAGES: '{label} {n} 張',
          OTHER_PART: '同單另有{title} {n} 張 {amount}',
          PRINTED_IN: '同 #{line} (印在{title})',
          LINE_NON_PAYABLE: '不付款',
          CONTINUED: '{requestNumber} (續)',
        },
        /** 付款欄：事務所三種都要入帳，這一欄只說哪些錢已經出去了。 */
        PAYMENT: {
          PAID: '已付 {date}',
          PENDING_RELEASE: '待放行 {date}',
          PENDING_RELEASE_NO_DATE: '待放行',
          PENDING_CASH: '待提領',
        },
        /** 消費日期那一列的第二行 —— 憑證本身的日期還是要看得到。 */
        DATE_SECOND_LINE: {
          invoice_late: '發票日 {date} (後開)',
          receipt_late: '收據日 {date} (晚於付款)',
        } as Record<string, string>,
        NOTES: {
          NON_PAYABLE: '含不付款憑證 {n} 張 (NT${amount})',
          MONTH_SPAN: '跨月：{parts}',
          MONTH_SPAN_PART: '{month} {pages} 張 {amount}',
          ISSUE: '憑證有問題',
          TRACKED_THEN_RECEIVED: '催交後收到 ({date})',
          DEFERRED_INVOICE: '後開發票：依消費日期列入 {month}',
          RECEIPT_LATE: '收據日期晚於付款日：依消費日期列入 {month}',
          /**
           * 補交的列（規格 v3.3 §5 (B)）：同一張單的其他紙本已在更早的一批交給事務所，
           * 這一列是之後才交來的那幾張。鍵 ＝ 封存列上的 lateHandover.kind（交件當下由
           * 伺服器決定）：deferred ＝ 這一列放進信封的每一張都是後開發票（items[].
           * deferredFilledAt）；other ＝ 其餘（例：催交後才交來的收據）。{date} ＝ 第一次
           * 真的交出紙本的那一天（MM/DD）—— 只報結案、一張紙都沒交出去的那一批不算。
           * 印在該列備註的第一句，後面接「：」與那幾張（PAPER_RECEIPT_UI.
           * SHEET_LATE_HANDOVER_LINE）；交件勾選清單那一列的第二行是同一句。
           */
          LATE_HANDOVER: {
            deferred: '後開補交 (原隨 {date} 交件)',
            other: '補交 (同單其他憑證已於 {date} 交件)',
          },
        },
        /**
         * 明細頁最後兩小段（規格 v3.3 §6.7：第 3 頁移除，催交中與「已付款、紙本尚未
         * 交到保管人」不再印）：未取得憑證（結案）與其他附件。鍵名沿用第 3 頁時代。
         */
        NOT_ENCLOSED: {
          CLOSED_TITLE: '未取得憑證 (結案)',
          CLOSED_ASIDE: '憑證未取得，只列這一次',
          CLOSED_COLUMN: '原因',
          CLOSED_NOTE: '主管結案 {date}：{reason}',
          OTHER_TITLE: '其他附件',
          OTHER_ASIDE: '{custodian}填寫，信封內不在明細上的憑證',
        },
        /** 沒有內容的段落印「無」—— 空白會讓人以為漏印（規格 §6.7）。 */
        EMPTY: '無',
        MONTH_LABEL: '{year} 年 {month} 月',
      },
      /** CF 回傳給畫面的結果訊息。 */
      MESSAGES: {
        RECEIVED_OK: '已點收 {n} 筆紙本',
        TRACKING_OK: '已催交 {n} 筆，期限 {deadline}',
        /** 驗收、催交、補發票都走這一道守門，所以不指名是哪一個動作。 */
        NOT_CUSTODIAN: '此筆紙本由{who}保管，請交由{who}處理',
        REMINDER_SENT: '已傳 LINE 提醒 {name}',
        REMINDER_COOLDOWN: '今天已提醒過，明天再試',
        /** 以下是 CF 拒絕時的訊息（{requestNumber}／{month} 依樣板代入）。 */
        DEFERRED_PENDING: '{requestNumber} 有後開發票尚未補，請先提醒申請人補發票',
        ALREADY_TRACKING: '{requestNumber} 已在催交中',
        REQUEST_NOT_FOUND: '找不到請款單',
        NO_PAPER: '{requestNumber} 沒有需要收取的紙本憑證',
        PAPER_CLOSED: '{requestNumber} 已結案，無法再變更紙本狀態',
        NOT_TRACKING: '此筆紙本不在催交中',
        CHECKLIST_REQUIRED: '請先完成核對清單',
        NO_DEFERRED_PENDING: '此請款單沒有待補的後開發票',
        REMINDER_FAILED: 'LINE 提醒發送失敗：{name} 尚未綁定 LINE，請直接聯絡申請人',
        FILL_NOT_OWNER: '只有申請人本人可以補發票',
        FILL_WRONG_STATUS: '此請款單目前無法補發票',
        FILL_ITEM_NOT_FOUND: '找不到此品項',
        FILL_NOT_DEFERRED: '此品項不是後開發票',
        FILL_ALREADY_FILLED: '此品項已補過發票',
        FILL_INVOICE_REQUIRED: '請填寫發票號碼',
        FILL_INVOICE_DATE_REQUIRED: '發票日期格式錯誤 (需 YYYY-MM-DD)',
        FILL_RECEIPT_REQUIRED: '請上傳發票影像',
        /**
         * 後開發票一條規則（規格 v3.3 §6.11）：號碼＋日期＋影像三樣都有才算補完，由伺服器
         * 把關。申請人（補發票）、出納（填寫發票並驗證）、總務（補發票）三個入口共用。
         */
        DEFERRED_NEEDS_IMAGE:
          '後開發票須有發票號碼、發票日期與發票影像三樣才算補完，請上傳發票影像。',
        /** 「填寫發票資料」只做更正；後開待補的行要走補發票那一條路（規格 §6.11）。 */
        DEFERRED_USE_FILL:
          '{requestNumber} 的後開發票請在該行按「填寫發票並驗證」補上號碼、日期與影像；「修改發票資料」只用來更正已有的號碼與日期。',
        /** 總務／主管在紙本憑證頁補發票：填入＋這一行驗收一次完成（規格 §5）。 */
        FILL_CUSTODIAN_OK: '已補發票並點收：{requestNumber} {description}',
        NO_SELECTION: '請選擇至少一筆請款單',
        NO_CONFIDENTIAL_ACCESS: '您沒有存取機密請款的權限。',
        NO_PERMISSION: '您沒有執行此操作的權限。',
        ALREADY_RECEIVED: '{requestNumber} 已點收過',
        NON_PAYABLE_AMEND_ERROR: '本單所有明細皆為不付款憑證，無付款金額，無法更正',
        NON_PAYABLE_FLIP_AFTER_RELEASE:
          '已放行付款的請款單不可改變應付金額 (不付款憑證的類別變更)，請改用金額調整',
        TRACK_AFTER_RECEIVED: '{requestNumber} 已點收，不能再催交',
        /**
         * 一條路（規格 v3.3 §3）：出納確認付款（請款單狀態「已付款」）之後才能驗收、
         * 催交、補發票。之前的列畫面上是「待付款」，沒有按鈕；這句是伺服器的守門。
         */
        NOT_PAID_YET: '{requestNumber} 出納尚未確認付款：請等付款後再點收、催交或補發票。',
        /**
         * 付款時的伺服器守門（規格 §6.11）：每一行該驗證的憑證都要已驗證（原本只有畫面擋）。
         * {n} ＝ 還沒驗證的行數。
         */
        PAY_UNVERIFIED_LINES:
          '{requestNumber} 還有 {n} 行憑證未驗證：請逐行核對影像並勾「已驗證」；畫面若已全部勾選，表示內容已變動，請重新整理後再送出',
        /** 後開待補的行在付款時不能被標為已驗證 —— 那張發票還沒開出來。 */
        DEFERRED_NOT_AT_PAYMENT:
          '{requestNumber} 有後開發票尚未補上，付款時不能標為已驗證：請取消勾選那一行，發票補上後再驗證。',
        SCAN_TRUNCATED: '待查的請款單超過查詢上限，清單可能不完整，請先聯絡系統管理員再交件',
        /** 交件（送件單）。 */
        HANDOVER_OK: '已交件 {requests} 筆 · {pages} 張',
        /** 這一包既沒有待交件的紙本，也沒有新的結案要報 —— 沒有東西可印。 */
        EMPTY_BATCH: '目前沒有待交件的紙本，也沒有新的結案要報',
        NOTHING_TICKED: '請至少勾選一筆要放進信封的紙本',
        /** 交易內重讀發現有人搶先一步（已被交件、已不是已付款＋已收、分包改了）。 */
        HANDOVER_RACE: '剛才有人交件或修改，請重新整理',
        /** 同上，但講得出是哪一張單（40 筆裡有 1 筆漂移時，她才知道該看哪一列）。 */
        HANDOVER_RACE_ROW: '{requestNumber} 剛才被交件或修改過，請重新整理後再試',
        BATCH_NOT_FOUND: '找不到這份送件單',
        NOT_ENCLOSABLE: '{requestNumber} 目前不能交件 (需已安排付款、紙本已點收且尚未交件)',
        ALREADY_HANDED_OVER: '{requestNumber} 已交件，不能再變更紙本狀態',
        /**
         * 驗收對話框送出的是它**列出的那幾行**（payment-markPaperReceived 的
         * requests[].itemIds）。伺服器那一刻看到的可驗收的行與它不一樣（別人剛驗收、
         * 申請人剛補完後開發票、舊分頁送來舊的參數）→ 整筆拒絕，請她重新整理。
         */
        PAGE_STALE: '畫面內容已變動，請重新整理後再點收',
        /** 同上，交件（勾選清單列出的行 ≠ 伺服器那一刻可以放進信封的行）。 */
        PAGE_STALE_HANDOVER: '畫面內容已變動，請重新整理後再交件',
        /**
         * 一行憑證沒有品項編號（items[].itemId）：驗收的章與交件紀錄都以它指名那一行，
         * 沒有它就無從記錄（正式環境 2026-09-15 查證 0 行，這是守門）。
         */
        LINE_WITHOUT_ITEM_ID:
          '{requestNumber} 有一行憑證缺少品項編號，無法點收或交件，請聯絡系統管理員',
        /** 勾的比單批上限多 —— 不截斷（勾＝已放進信封），整批拒絕請她分兩次交。 */
        TICKED_OVER_CAP: '一次最多交件 {n} 筆，請取消部分勾選後分批交件',
        /** 封存內容超過文件大小保護上限（正常筆數永遠碰不到）。 */
        HANDOVER_TOO_LARGE: '這一批的內容太大，請減少勾選筆數後分批交件 (一次最多 {n} 筆)',
        /** 舊分頁按到已被取代的「月結並列印清單」時的回覆（stub，本次不刪）。 */
        MONTH_CLOSE_REMOVED: '月結已由交件取代，請重新整理頁面',
        /** 發票號碼搜尋（payment-explainPaperInvoiceNumber）只收完整號碼。 */
        LOOKUP_NOT_COMPLETE: '請輸入完整的發票號碼 (2 個英文字母 ＋ 8 位數字)',
        /** 交件備註選了「不在信封內」卻沒寫原因（畫面先擋；伺服器守門，D10）。 */
        NOTE_REQUIRED: '{requestNumber} 還缺：不在信封內的原因',
        /** 取消點收（payment-cancelPaperReceived，D33）。 */
        CANCEL_RECEIVED_OK: '已取消點收：{requestNumber}',
        /** 這張單沒有在手上、還沒交件的憑證（已取消過、或從來沒點收）。 */
        NOTHING_TO_CANCEL: '{requestNumber} 沒有已點收、還沒交件的憑證，不能取消點收',
        /** 主管逐張結案（payment-closePaperTracking 帶 itemIds，D45）。{n} ＝ 結案的張數。 */
        LOST_OK: '已結案 {n} 張憑證：{requestNumber}',
        LOST_HANDED_OVER: '{requestNumber} 勾的憑證已交件，不能結案',
        LOST_ALREADY: '{requestNumber} 勾的憑證已結案過',
        /** 還沒點收、也不在催交中的憑證：先催交，拿不到才結案（D24）。 */
        LOST_NOT_ELIGIBLE: '{requestNumber} 勾的憑證還沒點收：請先催交，拿不到再結案',
        /** 不在信封內 ＝ 事務所已經有了，不是遺失（D29 DERIVED，K10）。 */
        LOST_OUTSIDE_ENVELOPE: '{requestNumber} 勾的憑證已直接交給事務所 (不在信封內)，不能結案',
        /** 結案視窗列出的憑證與伺服器那一刻不同（行被刪改、舊分頁）。 */
        PAGE_STALE_CLOSE: '畫面內容已變動，請重新整理後再結案',
        /** 整批備註（payment-addPaperHandoverNote／voidPaperHandoverNote，D19–D21）。 */
        HANDOVER_NOTE_ADDED: '已新增整批備註',
        HANDOVER_NOTE_VOIDED: '已作廢整批備註',
        /** 畫面在空白時先擋（「確認」停用）；這是伺服器的守門。 */
        HANDOVER_NOTE_EMPTY: '還缺：整批備註的內容',
        /** {n} ＝ HANDOVER_NOTES.MAX_LENGTH。 */
        HANDOVER_NOTE_TOO_LONG: '整批備註最多 {n} 個字',
        HANDOVER_NOTE_NOT_FOUND: '找不到這則整批備註',
        /** 作廢只有寫的人自己，或主管（D21）。 */
        HANDOVER_NOTE_VOID_NOT_ALLOWED: '只有寫的人或主管可以作廢這則整批備註',
        HANDOVER_NOTE_ALREADY_VOIDED: '這則整批備註已作廢',
      },
    },
    /**
     * 費用類別 → 稅別／憑證類型／是否付款 的預設值。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.PAYMENT.CATEGORY_RULES
     * （逐字比對）。
     *
     * 這些只是「選了類別之後自動帶入」的預設值，申請人仍可自行改成別的組合
     * （可用組合見上面的 COMBINED_TAX_DOC_OPTIONS）。
     * `payable: false` ＝ 僅登錄憑證（不付款）：金額不列入應付合計、不進企網銀
     * 轉帳檔，但仍要上傳影像、仍要交紙本、仍會被驗證。
     *
     * 每一個 defaultExpenseCategories 的 code 都要在此列出；
     * expenseCategories 可由 Firestore 覆寫，查無對應 code 時不帶入預設值。
     */
    CATEGORY_RULES: {
      ops_supplies: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      ops_maintenance: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      ops_equipment: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      ops_insurance: { taxType: 'tax_exempt', documentType: 'receipt_only', payable: true },
      ops_rent: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      ops_deposit_interest: {
        taxType: 'taxable_5',
        documentType: 'uniform_invoice',
        payable: false,
      },
      ops_other: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_office: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_welfare: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_training: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_travel: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_entertainment: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_professional: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_fees: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      admin_other: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      mktg_advertising: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      mktg_software: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      mktg_other: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      hr_payroll: { taxType: 'tax_exempt', documentType: 'no_document', payable: true },
      hr_instructor: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      hr_executive: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
      hr_other: { taxType: 'taxable_5', documentType: 'uniform_invoice', payable: true },
    } as Record<
      string,
      {
        taxType: 'taxable_5' | 'tax_exempt' | 'zero_rated';
        documentType: 'uniform_invoice' | 'receipt_only' | 'no_document';
        payable: boolean;
      }
    >,
    /**
     * 催交時真的送到申請人 LINE 的那一段字 —— 與後端
     * `BUSINESS_RULES.LINE_MESSAGES.PAPER_TRACKING_NOTICE` / `..._NOTE`
     * **逐字相同**，由 check-config-mirror 盯著（兩個 PAIRS）。
     *
     * 為什麼要在前端留一份：催交對話框要在送出前把訊息原文顯示給總務看（PO
     * 2026-09-16）。若前端自己另寫一句「差不多的話」，預覽就會與實際送出的內容
     * 分岔 —— 那比不顯示更糟。佔位符沿用後端的雙大括號（renderTemplate 的格式），
     * 所以這裡不能用 fillTemplate（那支吃單大括號）。
     */
    PAPER_TRACKING_LINE_PREVIEW: {
      TEXT: '【紙本憑證催交】請款單 {{requestNumber}}（{{summary}}）的紙本憑證總務尚未收到，請最晚於 {{deadline}} 前交給總務。{{note}}',
      NOTE: '備註：{{note}}',
    },
    /**
     * 紙本憑證頁的純前端字串（標題、篩選、欄位名、提示）。FE-ONLY — 後端沒有
     * 對應項，也不參與 check-config-mirror。會被後端引用的字句（狀態標籤、對話框
     * 內容、月結清單段落、擋月結原因、CF 回傳訊息）一律放在 PAPER_RECEIPT。
     */
    PAPER_RECEIPT_UI: {
      PAGE_TITLE: '紙本憑證',
      /** 這一頁做的兩件事，用規格 v3.3 §2 的兩個動詞（驗收＝紙本、交件＝給事務所）。 */
      PAGE_SUBTITLE: '點收紙本 · 交件給事務所',
      MENU_LABEL: '紙本憑證',
      /**
       * 分頁（規格 v3.3 §6.10）—— 與出納作業頁同一種 n-tabs 樣式。鍵序＝畫面順序。
       * （取代 3.17.x 的狀態篩選 CHIPS，已移除。）
       * 「待交件」「待安排付款」兩個分頁移除：待交件由交件列與交件對話框負責；
       * 待付款的列只出現在「全部」。
       */
      TABS: {
        pending: '待處理',
        deferred: '後開發票待補',
        verified: '已點收',
        handedOver: '已交件',
        all: '全部',
      },
      /** 預設落在「待處理」（規格 §6.10：第一個、預設）。 */
      DEFAULT_TAB: 'pending',
      /**
       * 發票號碼搜尋（D1／D2；mockup docs/tools/paper-receipt-search-mockup.html）。
       * 搜尋框與「目前只顯示符合條件的…」那一行沿用出納作業 › 待付款；此頁找不到時，
       * 下面的一行說號碼在哪一張、為什麼不在此頁（伺服器回的原因，P3）。
       */
      SEARCH: {
        PLACEHOLDER: '搜尋 發票號碼 / 單號...',
        /** 打字停下多久才篩清單（出納作業 › 待付款同一個值）。 */
        DEBOUNCE_MS: 200,
        /** 清單篩完、此頁沒有、號碼完整之後，再等多久才問伺服器（打錯一碼馬上改，不必問兩次）。 */
        LOOKUP_DEBOUNCE_MS: 300,
        /** 找到的那一張是這張單的第幾張（請款單上的順序，不是格子裡的排列）。 */
        SHEET_ORDINAL: '(第 {n} 張)',
        /** 主管在一般請款打到主管請款的號碼，而那張單在主管請款的清單上（D2）。 */
        IN_CONFIDENTIAL: '{number} 在主管請款：{requestNumber} · {requester} · {status}',
        SWITCH_TO_CONFIDENTIAL: '切換到主管請款',
        SWITCH_TO_GENERAL: '切換到一般請款',
        /** 總務／出納打到主管請款的號碼：不說是哪一張、誰的、金額（D41）。 */
        BELONGS_CONFIDENTIAL: '{number} 屬於主管請款，請通知主管。',
        /** 主管在主管請款打到一般請款的號碼（D42）。 */
        BELONGS_GENERAL: '{number} 屬於一般請款，請通知總務。',
        NO_REQUEST: '沒有任何請款單有 {number}。',
        /** 「後開發票待補」分頁有單時，才接在 NO_REQUEST 下面（D43）。 */
        DEFERRED_HINT: '若是後開發票，請到「後開發票待補」分頁按「補發票」。',
        /**
         * 「在哪一張」：同一包只寫單號；主管在一般請款查到一張主管請款的單時，
         * 寫明在主管請款（F4）。
         */
        PLACE: {
          same: '在 {requestNumber}',
          confidential: '在主管請款：{requestNumber}',
        },
        /** 伺服器回的原因，一種一句（P3；mockup § 4、§ 5，D36）。 */
        REASONS: {
          refresh: '{number} {place} · {requester} —— 請重新整理此頁。',
          voided: '{number} {place} · {requester} · 已作廢 —— 作廢的單不在此頁。',
          draft: '{number} {place} · {requester} · 草稿 —— 送出後會到此頁。',
          declined: '{number} {place} · {requester} · 已拒絕 —— 拒絕的單不在此頁。',
          returned: '{number} {place} · {requester} · 退回修改中 —— 重新送出後會回到此頁。',
          refund: '{number} {place} · {requester} · 退費 —— 退費的單不在此頁。',
          writtenOff: '{number} {place} · {requester} · 這一行已核銷 (憑證遺失)。',
          beforeStart:
            '{number} {place} · {requester} · 付款日 {paymentDate} —— {month} 月之前付款的單不在此頁。',
          outsideWindow: '{number} {place} · {requester} —— 建立超過 {months} 個月的單不在此頁。',
        },
        /**
         * 這一張已交件時的第二行（D28；W0.2 K12：時間是這一張自己那一批的交件時間）。
         * 有交件備註才接「· 交件備註：…」；不在信封內的備註照清單上的寫法（D12）。
         */
        HANDED_OVER_AT: '{time} 交件',
        HANDED_OVER_WITH_NOTE: '{time} 交件 · 交件備註：{note}',
        NOTE_OUTSIDE_ENVELOPE: '不在信封內 · {text}',
        /** 問伺服器「為什麼不在此頁」沒拿到答案（逾時、剛啟動）：這一句 ＋「重新載入」再問一次（review U4）。 */
        LOOKUP_FAILED: '沒查到 {number} 不在此頁的原因。',
      },
      /**
       * 右側那個小的「分組」切換的標籤（外觀與分頁明顯不同 —— 分頁是篩選，分組只是
       * 排列方式）。兩個選項沿用 VIEWS。
       */
      GROUP_LABEL: '分組',
      /**
       * 交件列（頁面最上方那一塊）。數字全部由 CF 算好帶回來 —— 畫面不自己加總，
       * 「待交件 N 筆」與勾選清單、送件單封面必須是同一個數字（UI_DESIGN_SPEC §7b）。
       */
      HANDOVER_BAR: {
        /**
         * v3.3 以行為單位：張數與金額只算放得進信封的行（後開補交的單只算那一行）。
         * （3.17.x 另有「已驗收 · 待核准／待安排付款」兩行 —— v3.3 驗收在付款之後，那種列
         * 只剩舊資料與驗收後退匯，紙本欄「待付款」、只在「全部」；那兩行已移除。）
         */
        PENDING: '待交件 {requests} 筆 · {pages} 張 · NT${amount}',
        TRACKING_LINE: '催交中 {tracking} · 逾期 {overdue}',
        /**
         * 交件列只剩這一顆（D7／D8，2026-11-03）：交件視窗裡先勾選、再預覽、最後「交件並列印」。
         * 原本旁邊的「預覽並列印」（一筆都沒勾時印全部待交件當揀貨清單）已拿掉。
         */
        HAND_OVER: '交件',
        LAST: '上次交件：{summary}',
        /** 一次交件的一行摘要（上次交件與歷次交件選單共用同一個句型）。 */
        HISTORY_ITEM: '{label} · {requests} 筆 · {pages} 張',
        /**
         * 有整批備註（還沒作廢的至少一則）的那一次，接在 HISTORY_ITEM 後面、中間一個空格
         * （上次交件那一行與歷次交件選單都一樣；mockup § 5a）。送件單封面的「上次交件」不接。
         */
        HAS_NOTES: '· 有整批備註',
        REPRINT: '重印',
        HISTORY: '歷次交件',
        /**
         * 歷次交件依年份（D17）：交件跨兩年以上才出現「年份」選單，預設最新的一年；
         * 「歷次交件」只列選到的那一年，新的在上。
         */
        YEAR: '年份',
        YEAR_OPTION: '{year} 年',
        // 交件鍵停用的理由由後端算好帶回（PaperHandoverStatus.emptyReason），這裡不另存一份。
        READ_ONLY: '此頁對你為唯讀，可以重印已交件的送件單。',
        NOT_LOADED: '讀不到交件狀態，請按「重新載入」。',
      },
      /**
       * 交件勾選清單（按「交件」跳出）。勾 ＝ 這張已經放進信封 ——
       * 與「已收」核對同一個原則：手上真的有，才勾得下去（規格 §6.2）。
       */
      TICKS: {
        /** 對話框標題與送出鍵跟著按鈕改名：這裡做的事是「交件」，印紙是下一步。 */
        TITLE: '交件 — {bundle}',
        /** 分組切換與工作清單同一個元件（PaperGroupToggle）、同一個「分組」標籤（GROUP_LABEL）。 */
        GROUP_BY_MONTH: '依憑證月份',
        GROUP_BY_REQUESTER: '依申請人',
        /** 勾選畫面上方的提示，兩行（D34，PO 的原句）：一勾＝一筆請款。 */
        HINT: '憑證放入信封就勾選，沒勾的下次再交。',
        HINT_OUTSIDE_ENVELOPE: '若選擇「不在信封內」，一樣要勾選。',
        /**
         * 點收時就有憑證選了「不在信封內」的請款，列尾加這一段（D52）：總務才知道那幾張不在
         * 抽屜裡。接在 ROW／ROW_BY_REQUESTER 之後，中間一個空格。
         */
        OUTSIDE_ENVELOPE_TAG: '· {n} 張不在信封內',
        SELECT_ALL_COUNT: '全選 ({n} 筆)',
        /** 分組標題上的筆數 —— 不寫「全選」：那顆勾選框本身就是全選（重複又像按鈕）。 */
        GROUP_COUNT: '({n} 筆)',
        /** 交件不可撤銷（規格 §6.6）—— 陳述句，不是問句（問句在確認框才有意義）。 */
        IRREVERSIBLE: '交件後不能撤銷；印了還沒交出去，下次帶同一張送件單交即可。',
        /** 分組標題列的勾選框名稱（報讀軟體只念得到 aria-label）。 */
        SELECT_GROUP: '選取{group}的 {n} 筆',
        SELECT_ROW: '選取 {requestNumber}',
        ROW: '{requester} · {summary} · {voucher}',
        ROW_BY_REQUESTER: '{summary} · {voucher} · {month}',
        // 列的第二行（放進信封的是哪幾張）：補交的列用 SHEET_LATE_HANDOVER_LINE，
        // 與送件單那一列的備註是同一句。
        SUMMARY: '已勾 {requests} 筆 · {pages} 張 (待交件 {total} 筆)',
        /** 送件單一律依憑證月份印，分組只影響這個勾選畫面。 */
        SHEET_ALWAYS_BY_MONTH: '送件單一律依憑證月份印。',
        NOTHING_TICKED: '至少勾選一筆',
        /**
         * 勾選這一步不交件、也不印（D7）：只往下一步看預覽。超過單批上限時這一顆停用，
         * 旁邊印 PAPER_RECEIPT.MESSAGES.TICKED_OVER_CAP（伺服器同一句）。
         */
        NEXT: '下一步：預覽',
        /** 明細上一個日期都沒有的列（極少）—— 分不出憑證月份，獨立一組。 */
        NO_MONTH: '無憑證日期',
        /** 沒有待交件的紙、只有新的結案要報：這一批就是一張「拿不到了」的交代。 */
        CLOSED_ONLY: '這一批沒有待交件的紙本，只回報新的「未取得憑證 (結案)」。',
      },
      /**
       * 交件視窗 › 預覽（D7，mockup docs/tools/paper-handover-one-print-mockup.html § 3）：
       * 同一個視窗變寬，封面＋交件明細在裡面看，頂端寫「預覽 — 尚未交件」，沒有列印鍵。
       * 「交件並列印」＝一次全有全無、不可撤銷的交件；成功後新分頁打開正式的送件單並跳出
       * 列印視窗。不可撤銷那一句沿用 TICKS.IRREVERSIBLE。
       */
      PREVIEW_STEP: {
        /** 標題接在 TICKS.TITLE 後面。 */
        TITLE_SUFFIX: ' · 預覽',
        /** 標題旁的數字讀這一次預覽回來的封存數學（不是畫面自己加）。 */
        SUMMARY: '已勾 {requests} 筆 · {pages} 張 · NT${amount}',
        BACK: '上一步',
        SUBMIT: '交件並列印',
      },
      /**
       * 交件呼叫的前端逾時（毫秒）。SDK 預設 70 秒，但伺服器的預算是 300 秒
       * （functions/src/index.ts setGlobalOptions）：一批幾百筆＋冷啟動可能超過
       * 70 秒，那時瀏覽器會先放棄、伺服器卻照樣交件成功，她看到的是「失敗」。
       * 鏡射伺服器的 timeoutSeconds × 1000（useFirebaseFunctions 的約定）。
       */
      HANDOVER_CALL_TIMEOUT_MS: 300000,
      /** 「其他附件」—— 信封裡不在明細上的憑證，總務自己寫一行交代（規格 §6.9）。 */
      OTHER_ATTACHMENTS: {
        LABEL: '其他附件 (選填) — 信封裡不在上表的憑證，寫一行交代',
        PLACEHOLDER: '例：P26OC01 換開發票 1 張 (原發票統編錯誤，事務所 10/28 通知)',
        COUNTER: '{n}/{max}',
      },
      /**
       * 一張憑證的交件備註編輯框（D10／D31：點收時與交件預覽時用同一個）。一張憑證一則；
       * 選「不在信封內」就一定要寫原因。備註只顯示、不比對；長度上限讀
       * PAPER_RECEIPT.HANDOVER.NOTE_MAX_LENGTH。收起後的標籤用 SHEET.DETAIL.LINE_NOTE_LABEL。
       */
      HANDOVER_NOTE_EDITOR: {
        ADD: '＋備註',
        EDIT: '修改',
        IN_ENVELOPE: '在信封內',
        OUTSIDE_ENVELOPE: '不在信封內',
        /**
         * 「不在信封內」旁 ⓘ 的兩行（D32a，PO 的原句）：什麼時候才選它。點收視窗與交件預覽
         * 同一個編輯框，同一段字。
         */
        OUTSIDE_ENVELOPE_TIP: [
          '其他交件發票方式才選，如：傳電子發票、紙本先前已先交、賣方直接寄給事務所。',
          '發票還沒收到、或不見了，請不要點收、或取消點收。',
        ] as readonly string[],
        /** ⓘ 的名稱（報讀軟體只念得到 aria-label）。 */
        OUTSIDE_ENVELOPE_TIP_ARIA: '什麼時候選「不在信封內」',
        LABEL: '備註',
        PLACEHOLDER: '例：10/1 已交紙本、電子檔已寄事務所',
        /** 選了「不在信封內」、原因還空著（框下方，名出缺的是什麼）。 */
        MISSING_REASON: '還缺：不在信封內的原因',
        /**
         * 還有打開、沒按「確認」的備註框時，送出鍵旁的那一句（W0.1 N3）：{numbers} 是那幾張的
         * 名字（有號碼的發票寫號碼，收據寫「收據 #N」—— HANDOVER_NOTE_LINE 的短寫法；review U8）。
         */
        UNCONFIRMED: '還有 {n} 則備註沒按確認：{numbers}',
        UNCONFIRMED_JOIN: '、',
        CONFIRM: '確認',
      },
      /** 送件單列印頁（螢幕上的狀態；紙上的字句全在 PAPER_RECEIPT.SHEET）。 */
      SHEET_PAGE: {
        /**
         * 只剩交件後那一種分頁（D7：預覽改在交件視窗裡看，不能印）。從「交件並列印」打開時
         * 列印視窗自動跳出一次；「重印」與「歷次交件」打開時不自動跳。
         */
        PRINT_HANDED: '列印送件單',
        /** 列印鍵旁邊那一句（D9：印一份，事務所在封面簽收後拍照回傳；Hub 不存照片）。 */
        PRINT_HANDED_HINT: '請印一份交事務所，並請事務所在封面簽收後拍照回傳。',
        BACK: '返回紙本憑證',
        LOAD_FAILED: '送件單載入失敗。',
        NOT_FOUND: '找不到這份送件單。',
        NO_PERMISSION: '你沒有檢視這份送件單的權限。',
        INVALID: '這個網址不是一份送件單，請從紙本憑證頁的「交件」或「重印」進入。',
        /**
         * 舊的預覽網址（?preview=，D7 之前的預覽分頁、書籤）：預覽改在交件視窗裡（W0.0 F13，
         * mockup「怎麼建」第 3 點）。
         */
        PREVIEW_GONE: '請從紙本憑證頁按「交件」。',
      },
      /**
       * 整批備註（D19–D22；mockup docs/tools/paper-handover-one-print-mockup.html § 5a）：送件單
       * 分頁上面那一格，一則一行、舊的在上。只在畫面上 —— 列印時藏起來，印出來的送件單跟交件
       * 當天一模一樣。只加不改：寫錯的作廢，劃線留著，寫上誰、什麼時候作廢。
       */
      HANDOVER_NOTES: {
        TITLE: '整批備註',
        ADD: '＋新增',
        VOID: '作廢',
        /** 每一則後面：誰、什麼時候寫的。{time} ＝ 台北 YYYY/MM/DD HH:mm。 */
        BYLINE: '— {name} · {time}',
        /** 作廢的那一則接在 BYLINE 後面（半形括號）。 */
        VOIDED: '已作廢 ({name} · {time})',
        /** 新增框下方：存了就不能改。「取消」沿用 PAPER_RECEIPT_UI.CANCEL。 */
        HINT: '新增後不能修改；寫錯可以作廢 (劃線留著)。',
        CONFIRM: '確認',
        /** 新增框的名稱（報讀軟體只念得到 aria-label）。 */
        INPUT_ARIA: '新增一則整批備註',
        /** 按「作廢」先問一次（D20）：標題、那一則的原文、不能恢復。「取消」同上。 */
        VOID_TITLE: '作廢這則整批備註？',
        VOID_QUOTE: '「{text}」',
        VOID_WARNING: '作廢後會劃線留著，不能恢復。',
      },
      /**
       * 驗收對話框的逐張明細 —— 核對清單第 ②③④ 項是按「每張」問的，只給整單合計
       * 她核對不了（PO 2026-09-14）。v3.3：只列手上的那幾張（號碼 · 日期 · 金額 · 用途），
       * 後開待補的行不在這一次驗收裡，另起一行講明（DEFERRED_EXCLUDED）。
       */
      RECEIVED_LINES: {
        /** 收據沒有號碼可核，只能核日期與金額（號碼欄寫「收據」）。 */
        RECEIPT: '收據',
        NO_NUMBER: '(無號碼)',
        NON_PAYABLE: '不付款',
        ISSUE: '有問題',
        EMPTY: '(這一單沒有要核對的憑證)',
        /** 逐張明細的欄頭（規格 v3.3 §5：號碼 · 日期 · 金額 · 用途）。 */
        COLUMN_NUMBER: '號碼',
        COLUMN_DATE: '日期',
        COLUMN_AMOUNT: '金額',
        COLUMN_DESCRIPTION: '用途',
        /** 後開待補的行：這一次不驗收，發票來了再補發票、再驗收（規格 §5 分次）。 */
        DEFERRED_EXCLUDED: '後開待補，不在這次點收：{description} · NT${amount}',
        /**
         * 「對不上怎麼辦」那一句後面接的這一句（D29／D31，D49 的字）：不論能不能催交都成立。
         */
        OUTSIDE_ENVELOPE_HINT: '憑證已直接交給事務所的，按那一張的「＋備註」選「不在信封內」。',
        /**
         * 一張後開發票選了「不在信封內」、影像還沒有人驗證過（D48，PO 的字）：確認點收會記下
         * 「已驗證」，所以先看影像。只在那一張底下。
         */
        LOOK_AT_IMAGE: '這張憑證不在手上，點收前請先按「憑證影像」看影像。',
        /**
         * 核對清單第 ① 項：每一張都選了「不在信封內」時（手上 0 張，K11 —— 可以點收）。
         * 接在 RECEIVED_CHECKLIST_ITEMS 的「張數：」後面。
         */
        NONE_IN_HAND: '0 張 (都不在信封內)',
        /** 每一張的「＋備註」／「修改」給報讀軟體的名稱：{action} ＋ 那一張的號碼。 */
        NOTE_LINK_ARIA: '{action} {paper}',
      },
      VIEWS: { byRequest: '依請款單', byRequester: '依申請人' },
      /** 列上的動作（規格 v3.3 §6.10）：驗收、催交（待點收的列）；補發票、LINE 提醒（後開待補的行）。 */
      ROW_ACTIONS: {
        received: '點收',
        /** 機密包：催交不發 LINE（保管人就是主管本人）→ 只寫「催交」。 */
        tracking: '催交',
        /** 一般包：催交會以 LINE 通知申請人 → 按鈕直接說出來，圖示與「LINE 提醒」相同（PO 2026-10-08 D54）。 */
        trackingLine: 'LINE 催交',
        /** 總務／主管在紙本憑證頁補後開發票（填入＋那一行驗收一次完成，規格 §5）。 */
        fillDeferred: '補發票',
        remind: 'LINE 提醒',
        /** {days} ＝ PAPER_RECEIPT.EXTENSION_DAYS —— 天數只有設定檔一個來源。 */
        extend: '延長 {days} 天',
        close: '結案',
        /**
         * 「⋯」選單（D33／D24，mockup § 1a ④、§ 7 ②）：列上有在手上、還沒交件的憑證時才有。
         * 裡面是「取消點收」（保管人）與「結案」（主管，只結案勾的那幾張）。
         */
        more: '⋯',
        moreAria: '更多動作：{requestNumber}',
        cancelReceived: '取消點收',
      },
      BATCH_ACTIONS: {
        received: '批次點收',
        tracking: '批次催交',
        /** 勾選的列裡有一般包（會發 LINE）時的字 (D54)。 */
        trackingLine: '批次 LINE 催交',
      },
      /** 催交按鈕的滑鼠提示 (D54)：{deadline} ＝ 今天 ＋ TRACKING_DEADLINE_DAYS（trackingDeadlinePreview）。 */
      TRACKING_TIPS: {
        line: '標記催交中 (期限 {deadline})，並以 LINE 通知申請人交紙本',
        noLine: '標記催交中 (期限 {deadline})；機密請款不發 LINE',
      },
      /**
       * 勾選中混了催交中的列時，「批次催交」按不下去的理由。催交與驗收看的是同一組行
       * （還沒交來的紙，utils/paperQueueDisplay canChaseRow，與伺服器同一條逐行規則），
       * 勾得起來、卻不能催交的只剩已經在催交中的列 —— 那種列只能驗收。
       */
      BATCH_TRACK_BLOCKED: '勾選中有催交中的列 (只能點收，不能再催交)：先取消勾選那幾筆',
      /**
       * 一次 CF 呼叫最多帶幾筆；超過就分批依序送（usePaperReceipts）。
       * 這個數字必須等於後端 BUSINESS_RULES.PAYMENT.BATCH_SIZE_LIMIT（markPaperReceived／
       * markPaperTracking 的 maxLength）—— 它不在 PAPER_RECEIPT 裡，mirror 檢查涵蓋不到，
       * 後端調整時要一起改。
       */
      BATCH_CHUNK: 50,
      /** 置頂的催交總覽面板（逾期時預設展開）。 */
      TRACK_PANEL: {
        title: '催交中 {n} 筆',
        overdue: '逾期 {n} 筆',
        /** 主管的面板同時列兩包 —— 用分包的對外名稱（與 bundleShort 同一組字）。 */
        both: '一般＋主管',
        /** 短名 —— 交件識別（「一般 2026/11/05」）與一句話裡都用它，「一般請款」會太長。 */
        /**
         * 送件單的識別字串（「主管 2026/09/14 21:54」）、列上的「（原主管包）」與
         * 追蹤面板的「一般＋主管」都用它。跟著分包的對外名稱走（PO 2026-09-14）——
         * 標題寫【主管請款】、識別卻寫「機密」會像是兩份不同的文件。
         */
        bundleShort: {
          general: '一般',
          confidential: '主管',
        } as Record<string, string>,
        staffOnly: '延長／結案請找主管',
        inDeadline: '期限內 · 待點收',
        /**
         * 這一列只剩後開那一行：發票到了在下方清單按「補發票」（填入＋驗收一次完成），
         * 還沒到就用這一列的「LINE 提醒」請申請人補。
         */
        deferredPending: '只剩後開發票：發票到了在下方清單按「補發票」，或按「LINE 提醒」',
        /** 已經延長過一次 —— 期限底下印出原因，主管才知道上次為什麼放行。 */
        extended: '已延長 ({reason})',
        /** 逾期、但這個人既不收件也不能延長／結案時的狀態說明（出納）。 */
        supervisorHandling: '主管處理中',
      },
      /**
       * 紙本欄標籤的補充字（主要標籤是下面的 STATE_LABELS，utils/paperQueueDisplay
       * paperStateTag）。3.17.x 被 STATE_LABELS 取代的那幾個（未收／追蹤中／已收／
       * 待安排付款／待核准…）已移除；留下的是 STATE_LABELS 之外仍要講的事。
       */
      TAGS: {
        /** 催交中且期限已過（逾期不是一個狀態，是 isOverdue）；{deadline} ＝ 'MM/DD'。 */
        overdue: '催交逾期 · 期限 {deadline}',
        overdueTooltip: '催交期限 {deadline} 已過，紙本還沒交來',
        /** 交件之後這張單又被更正過：事務所手上那張送件單已經不是最新的金額。 */
        receivedAmended: '已交件 {date} · 印後更正',
        /** 已列在送件單上的結案列又被更正過（規格 §8：結案列也蓋章、也會被更正）。 */
        closedAmended: '已結案 · 印後更正',
        /** 兩個「印後更正」標籤的滑鼠提示。 */
        amendedTooltip: '交件後這張單又被更正過，事務所手上送件單的內容已不是最新',
        /** 交件後機密旗標改了（規格 §8 row 6）：接在日期後面，例「已交件 11/05（原一般包）」。 */
        handedOverOtherBundle: ' (原{bundle}包)',
        /** 同一列已按過 LINE 提醒（24 小時內不能再按）—— 讓總務不必按第二次才知道。 */
        deferredReminded: '後開發票待補 · 已提醒 {date}',
        /** 催交面板裡主管那一包的列（分包的對外名稱，同 bundleShort）。 */
        confidential: '主管',
      },
      /**
       * 紙本欄的狀態標籤（規格 v3.3 §6.10），鍵 ＝ 佇列列上的 `paperState`
       * （types/database.ts PaperStateKey）。每一個都配一句 STATE_TOOLTIPS。
       *  {days} ＝ 送出請款後的天數（超過 UNRECEIVED_RED_DAYS 轉紅）；
       *  {deadline}／{date} ＝ 'MM/DD'。
       * 逾期不是一個狀態：催交中且期限已過（paper.isOverdue）時沿用 TAGS.overdue。
       */
      STATE_LABELS: {
        awaitingPayment: '待付款',
        toVerify: '待點收 · {days} 天',
        chasing: '催交中 · 期限 {deadline}',
        deferred: '後開發票待補',
        deferredFilledToVerify: '後開已補 · 待點收',
        verified: '已點收',
        handedOver: '已交件 {date}',
        closed: '結案',
      },
      /**
       * 每個狀態滑鼠移上去的那一句（規格 v3.3 §6.10；UI_DESIGN_SPEC §9：一句話講這是什麼，
       * 規則與門檻不進提示）。「後開已補 · 待點收」與「結案」規格沒有逐字寫出，依 §5／§2
       * 的定義寫成一句。
       */
      STATE_TOOLTIPS: {
        awaitingPayment: '出納確認付款後才能點收',
        toVerify: '出納已確認付款、紙本還沒點收；天數從送出請款算起',
        chasing: '已 LINE 通知申請人補交紙本',
        deferred: '這一行的發票還沒開；收到後按「補發票」',
        deferredFilledToVerify: '發票資料已補上，紙本還沒點收；收到紙本後按「點收」',
        verified: '總務／主管已點收，下次交件時列入送件單',
        handedOver: '已隨 {date} 送件單交給事務所',
        closed: '主管已確認拿不到紙本，視為未取得憑證',
      },
      /**
       * 機密請款催交時不發 LINE（actions.ts notifyTracking 只通知一般請款 —— 那一包的
       * 保管人就是主管本人），所以機密列的催交中不能說「已 LINE 通知」。
       */
      STATE_TOOLTIP_CHASING_CONFIDENTIAL: '紙本還沒交來，由主管直接向申請人催交',
      /**
       * 「待點收 · N 天」**超過**這個天數（N > 14，規格 §6.10「超過 14 天轉紅」）變紅字。
       * 純顯示強調，後端沒有對應規則；門檻只有這一個來源。
       */
      UNRECEIVED_RED_DAYS: 14,
      HINT_TRACKING_PANEL:
        '逾期在最上面；一般列由總務或主管點收，機密列只有主管點收；點收前都要過核對清單。',
      /**
       * 這一頁**唯一**的說明（PO 2026-09-14：原本交件列、清單上方、清單下方
       * 三段各講一次）。v3.3（PO 2026-09-15）移除前半「「待交件」＝…」：待交件由
       * 交件列與交件對話框交代，尚未付款的單只在「全部」。
       */
      LIST_SCOPE_HINT: '清單含所有月份；退費、薪資、免憑證不在此頁。',
      /**
       * 不能收件的人看到的一行說明 —— 只剩出納（此頁只讀）。
       * 主管自 2026-09-13 起是一般包的收件人之一（已收／追蹤都做得到），所以原本
       * 那句「一般請款的紙本由總務收件；你可標記追蹤」已無人會看到，一併移除。
       */
      READ_ONLY_HINT: {
        readOnly: '此頁對你為唯讀。',
      },
      EMPTY: '目前沒有符合條件的紙本憑證。',
      /** 空清單要講清楚「空的是哪一格」，並給一條回到全部的路。 */
      EMPTY_CHIP: '「{chip}」目前沒有紙本憑證。',
      /** 「待處理」（預設分頁）空了 ＝ 現在沒有要動手的事，直接講是哪兩件事。 */
      EMPTY_PENDING: '目前沒有要點收或催交的紙本。',
      EMPTY_SHOW_ALL: '看全部',
      /** 只有螢幕報讀軟體會用到的名稱（勾選欄表頭、全選、月份與篩選鍵）。 */
      SELECT_ALL: '全選',
      SELECT_COLUMN: '選取',
      /** 每一列／每一組的勾選框都要講清楚勾的是誰（只有單號會讀成一串代碼）。 */
      SELECT_ROW: '選取 {requestNumber}',
      SELECT_GROUP: '選取 {name} 的 {n} 筆',
      /** 標記追蹤對話框：每一列的備註輸入格各自的名稱。 */
      NOTE_ARIA: '備註 {requestNumber}',
      ARIA_VIEW_GROUP: '清單檢視方式',
      ARIA_BUNDLE_GROUP: '請款分包',
      /** 讀取失敗（CF 掛了／權限不足）—— 清單不留舊資料，改成可重試的錯誤狀態。 */
      LOAD_FAILED: '紙本憑證載入失敗。',
      RETRY: '重新載入',
      COLUMNS: {
        requestNumber: '單號',
        requester: '申請人',
        /** 催交面板的欄（工作清單 v3.3 起不再有「款項」「建立日」兩欄）。 */
        summary: '款項',
        paidAt: '付款日',
        pageCount: '應收',
        voucherTotal: '憑證合計',
        paper: '紙本',
        actions: '操作',
        /**
         * v3.3 清單欄位（規格 §6.10）：勾選 · 單號 · 申請人 · 憑證 · 付款日 · 張數／金額 ·
         * 紙本 · 操作。「款項」「建立日」「應收」「憑證合計」不再是清單的欄（併入下面兩欄）。
         */
        vouchers: '憑證',
        pagesAmount: '張數／金額',
        trackedAt: '催交日',
        deadline: '期限',
        status: '狀態',
        note: '備註 (選填)',
      },
      /**
       * 「憑證」欄（規格 v3.3 §6.10）：這張單的每一張「號碼 · 用途」，收據寫「收據 · 用途」，
       * 後開那一行寫「後開待補：用途 · NT$金額」，已交給事務所的那幾張後面掛 HANDED_OVER；
       * 超過 MAX_SHOWN 張只列前面那幾張，再接 MORE，滑鼠移上去看全部。一列維持一行高。
       */
      VOUCHER_CELL: {
        INVOICE: '{number} · {description}',
        RECEIPT: '收據 · {description}',
        DEFERRED: '後開待補：{description} · NT${amount}',
        HANDED_OVER: '已交',
        /** 有些已交、有些還沒交的單上，還沒交的那幾張（D13）。 */
        NOT_HANDED_OVER: '未交',
        /** 同一張單另有已交或在手上的憑證時，結案的那一張（D26，送件單上的字）。 */
        NOT_OBTAINED: '未取得',
        MORE: '…共 {n} 張',
        MAX_SHOWN: 2,
        /**
         * 兩張之間的分隔字。不用「、」：一張發票拆成兩行時，那一張的用途本身就以「、」
         * 串起來（「租金、押金設算利息」），同一個字會分不出哪裡是下一張。
         */
        SEPARATOR: '；',
        /** 一張發票拆成好幾行時，那一張的幾個用途之間。 */
        DESCRIPTION_JOIN: '、',
      },
      /**
       * 清單列上一張憑證的交件備註（D27）：{paper} 是有號碼的發票寫號碼；收據寫
       * RECEIPT_PAPER，沒有號碼的發票寫 INVOICE_PAPER（#{line} 是這張單自己的行號）。
       * 單號不寫 —— 同一列的單號欄已經有。
       */
      HANDOVER_NOTE_LINE: {
        IN_ENVELOPE: '交件備註：{paper} · {text}',
        OUTSIDE_ENVELOPE: '交件備註：{paper} 不在信封內 · {text}',
        RECEIPT_PAPER: '收據 #{line} ({description})',
        INVOICE_PAPER: '發票 #{line} ({description})',
        /** 沒有用途時括號整個不寫（D54(6)），不印一對空的「()」。 */
        RECEIPT_PAPER_PLAIN: '收據 #{line}',
        INVOICE_PAPER_PLAIN: '發票 #{line}',
      },
      /** 「張數／金額」欄（規格 v3.3 §6.10：原「應收」與「憑證合計」併成一欄）。 */
      PAGES_AMOUNT: '{pages} 張 · NT${amount}',
      /**
       * 已點收／已交件的列上，後開那一行還在等發票時的第二個小標籤（規格 v3.3 §5 分次：
       * 手上的先交，後開的之後補）。滑鼠提示一句話講這是什麼。
       */
      LATE_LINE_CHIP: '後開待補 {n}',
      LATE_LINE_CHIP_TOOLTIP: '這張單還有 {n} 行後開發票沒補；收到發票後按「補發票」',
      /** 依申請人分組的標題列：這個人幾筆、整張單的張數與金額（與「張數／金額」欄同一個範圍）。 */
      SELECTION_SUMMARY: '{n} 筆 · {pages} 張 · NT${amount}',
      /** 批次列：勾了幾筆（這一次會驗收或催交哪幾張，打開對話框才逐張列出）。 */
      BATCH_SELECTED: '已勾選 {n} 筆',
      /**
       * 驗收對話框的標題：{pages}／{amount} ＝ **這一次**要驗收的那幾張（後開待補、已點收、
       * 已交件的行不在裡面），所以寫「本次」，不寫整張單的數字。
       */
      DIALOG_RECEIVED_TITLE: '點收 — {n} 筆 · 本次 {pages} 張 · NT${amount}',
      /** 驗收對話框每一張單的抬頭（規格 v3.3：單號 · 申請人 · 付款日）。 */
      DIALOG_RECEIVED_HEADER: '{requestNumber} · {requester} · 付款日 {paymentDate}',
      /**
       * 驗收對話框底下「對不上怎麼辦」：能催交的列用 PAPER_RECEIPT.DIALOG.RECEIVED_MISMATCH_HINT
       * （改用催交）；已經在催交中的列不能再催一次，只剩把紙本退給申請人補正這一條路。
       */
      RECEIVED_MISMATCH_RETURN: '有一項對不上：按「取消」，把紙本退給申請人補正。',
      /**
       * 批次驗收時核對清單第 ① 項的張數：逐張的號碼已經列在上面的表裡，不再全部塞進
       * 這一句（收據那一半沿用 PAPER_RECEIPT.DIALOG.RECEIVED_CHECKLIST_COUNTS.receipt_only）。
       */
      RECEIVED_BATCH_COUNTS: {
        uniform_invoice: '統一發票 {invoiceCount} 張',
        SEE_TABLE: ' (號碼見上表)',
      },
      DIALOG_TRACKING_TITLE: '催交 — {n} 筆',
      DIALOG_ROW_TITLE: '{title} — {requestNumber} {requester} · {summary}',
      CONFIRM: {
        received: '確認點收',
        tracking: '確認催交',
        extend: '確認延長',
        close: '確認結案',
        cancelReceived: '確認',
      },
      /**
       * 取消點收的視窗（D33，mockup § 1a ④；字依 D49 與 W0.2 K15(a)）：{n} ＝ 會回到「待處理」
       * 的張數（在手上、還沒交件的那幾張；同一張的幾行算一張）。標題用 DIALOG_ROW_TITLE；
       * 原因空著時旁邊那一句是 PAPER_RECEIPT.DIALOG.CANCEL_RECEIVED_REASON_REQUIRED。
       */
      CANCEL_RECEIVED_DIALOG: {
        TITLE: '取消點收',
        HINT: '這 {n} 張會回到「待處理」，憑證到了再重新點收。已寫的備註留著。',
        PLACEHOLDER: '原因 (必填，例：批次點收時多勾了這一筆)',
      },
      /**
       * 結案視窗（整張單結案與逐張結案，D24，mockup § 7 ③）「確認結案」旁邊那一句：還缺的幾件
       * 用「、」接成一句（review U12）——「還缺：結案原因」「還缺：遺失的是哪一張」「還缺：遺失的是
       * 哪一張、結案原因」。伺服器拒絕時自己的那一句（PAPER_RECEIPT.DIALOG.CLOSE_REASON_REQUIRED／
       * CLOSE_LOST_MISSING）不在這裡。
       */
      CLOSE_MISSING: '還缺：{items}',
      CLOSE_MISSING_ITEMS: { LOST: '遺失的是哪一張', REASON: '結案原因' },
      CLOSE_MISSING_JOIN: '、',
      /** 「遺失的是哪一張？」兩張的字一樣（例：兩張「收據 · 油資」）時，那幾張後面接金額才分得出來。 */
      CLOSE_LOST_SAME_LABEL: '{label} · NT${amount}',
      /**
       * 憑證遺失怎麼辦（D23／D24／D49，mockup § 7）—— 一段字，兩個地方讀：結案視窗最上面的紅框
       * （CloseTrackingDialog）與頁面標題旁的「ⓘ 憑證遺失怎麼辦」（每個人都看得到）。改一次，兩邊
       * 一起改。電子發票那一行等事務所回覆後改成事務所說的做法（PO 已去問；在那之前寫
       * 「請先問事務所」）。
       */
      LOST_PAPER_GUIDE: {
        /** 頁面標題旁那一個連結的字（前面畫 ⓘ 圖示）。 */
        TITLE: '憑證遺失怎麼辦',
        /** 結案視窗紅框的標題。 */
        ALERT_TITLE: '結案是最後一步，請先補拿憑證',
        /** 頁面說明第一段的標題（紅框裡由 ALERT_TITLE 說同一件事）。 */
        REPLACE_TITLE: '先補拿憑證：',
        REPLACEMENTS: [
          '紙本統一發票：請賣方影印他留存的存根聯，並蓋發票章。這份影本可以代替遺失的發票，照常交件，不必結案。',
          '電子發票：證明聯只能印一次，賣方不能因為遺失重印。請先問事務所要附什麼。',
          '收據：請開收據的人重開一張，並簽名或蓋章。',
        ] as readonly string[],
        /** 第二段的標題：結案視窗裡是主管自己結案；頁面說明是請主管結案。 */
        LAST_RESORT: {
          close: '真的拿不到，才結案，並開一張支出證明單：',
          page: '真的拿不到，才請主管結案，並開一張支出證明單：',
        },
        STEPS: [
          '經手人寫上日期、店家、品名、金額，以及拿不到憑證的原因，並簽名。',
          '主管簽名。',
          '總務把它放進下一次交件的信封，在「其他附件」寫一行。',
        ] as readonly string[],
        /** 只在頁面說明最後（D29 DERIVED）：遺失不是「不在信封內」。 */
        NOT_OUTSIDE_ENVELOPE: '遺失的憑證不要選「不在信封內」—— 那是說事務所已經有了。',
      },
      /**
       * 請款單詳情的歷程：一次取消點收一筆（D50）—— 誰 · 何時 · 原因 · 「原 {date} {name} 點收」。
       * {date} ＝ 被取消的那一次點收的日期（查不出時用 PRIOR_NO_DATE）。
       */
      RECEIVED_CANCELLED_TIMELINE: {
        TITLE: '取消點收',
        PRIOR: '原 {date} {name} 點收',
        PRIOR_NO_DATE: '原 {name} 點收',
      },
      /**
       * 總務補發票（規格 v3.3 §5／§6.11）：後開那一行的發票交到總務（機密：主管）手上時，
       * 在紙本憑證頁填號碼、日期、上傳影像（三樣必填，伺服器把關），同一步完成那一行的
       * 發票驗證與紙本點收。
       */
      FILL_DIALOG: {
        TITLE: '補發票 — {requestNumber} {requester} · {description}',
        HINT: '確認後這一行同時完成發票驗證與紙本點收。',
        /**
         * 日期欄的名字。號碼、影像、費用備註三欄的字在共用的 DeferredInvoiceFillPanel 裡
         * （出納「填寫發票並驗證」與申請人「補發票」同一個面板），這裡不另存一份。
         * 影像的四項核對不再是勾選清單（PO 2026-10-08 D61）：與每一個「已驗證」同一個 ⓘ 提醒
         * （LINE_VERIFY_HINT，VerifyChecklistHint）；按「補發票並點收」就是確認。
         */
        INVOICE_DATE: '發票日期',
        /** 一張單有好幾行後開待補時，先選這一次補的是哪一行（選項字沿用 VOUCHER_CELL.DEFERRED）。 */
        LINE_LABEL: '補哪一行',
        /** 送出鍵講明它做的兩件事（PO 2026-09-14：按鈕名照它真正做的事取）。 */
        CONFIRM: '補發票並點收',
        /** 按鈕還按不下去時，講清楚還差什麼（號碼、日期、影像三樣必填，伺服器也把關）。 */
        MISSING: '填發票號碼、發票日期、上傳影像後即可按「補發票並點收」。',
      },
      CANCEL: '取消',
      /** 追蹤面板裡，款項底下那一行的備註。 */
      NOTE_PREFIX: '備註：{note}',
      /** 「應收」欄的單位。 */
      PAGE_COUNT: '{n} 張',
      /** 跨月的列在勾選清單上的月份標示（例：9–10 月）。 */
      MONTH_RANGE: '{from}–{to} 月',
      NOTE_PLACEHOLDER: '例：少一張 8/20 收據',
      /**
       * 催交對話框的 LINE 預覽（PO 2026-09-16）—— 按下「確認催交」之前，先讓總務看到
       * 申請人手機上會出現的**那一段字**，包含她剛打的備註（備註是逐字送出去的）。
       * 原本對話框只說「會以 LINE 通知申請人」，沒說內容 —— 有人就會把內部備註寫進去。
       */
      TRACKING_LINE_PREVIEW: {
        TITLE: '申請人會收到的 LINE',
        /** 機密（主管請款）那一包不發（後端 notifyTracking 也一樣擋掉）。 */
        NONE_CONFIDENTIAL: '主管請款不發 LINE (紙本由主管自己保管)',
        /** 每一則前面標出收訊人，批次催交時才看得出哪一則是誰的。 */
        RECIPIENT: '{requestNumber} → {requester}',
      },
      REASON_PLACEHOLDER: {
        extend: '原因 (必填，例：申請人出差中，10 月中回來)',
        close: '原因 (必填，例：申請人已離職、憑證確定遺失)',
      },
      /** 主管動作完成的提示（PAPER_RECEIPT.MESSAGES 只涵蓋總務那幾個動作）。 */
      TOASTS: {
        extended: '已延長至 {deadline}',
        closed: '已結案，會列在下一次送件單',
        /** 交件失敗後重新載入，發現其實已經交件成功 —— 直接把那份送件單打開。 */
        alreadyHandedOver: '剛才已交件，正在開啟送件單',
        /** 結案超過單批上限：少列的那幾筆會自動列在下一批，但畫面要當下講，不能只寫在紙上。 */
        closedHeld: '未取得憑證 (結案) 超過單批上限，其餘 {n} 筆會自動列在下一次送件單。',
      },
      /**
       * 出納看得到的紙本狀態（規格 v3.3 §6.12）：請款單詳情裡一行唯讀字 ——
       * 不進出納的清單、不做篩選（付款從不等紙本）。用在兩個時候：回答事務所的詢問、
       * 作廢或更正之前。{date} ＝ 'MM/DD'。
       * 主要狀態的鍵 ＝ paperState（utils/paperLineState，與紙本憑證頁同一個優先順序）；
       * closed 規格沒有列出，補上是為了主管結案的單這一行不會空白。
       */
      TREASURY_LINE: {
        TEMPLATE: '紙本：{state}',
        awaitingPayment: '待付款',
        toVerify: '待點收',
        chasing: '催交中',
        deferred: '後開發票待補',
        deferredFilledToVerify: '後開已補 · 待點收',
        verified: '已點收 {date} {name}',
        handedOver: '已交件 {date}',
        closed: '結案',
        /**
         * 一張單可以分次交件（規格 v3.3 §5 (B)），主要狀態後面再接逐行的補充（以 SEPARATOR
         * 相連）：已交件、後開那幾行還沒交 → LATE_NOT_HANDED_OVER；還沒交件、後開那一行還在
         * 等發票 → LATE_PENDING；主要狀態不是已交件、但已有一部分紙本交給事務所 →
         * PARTLY_HANDED_OVER（全部都交了則用上面的 handedOver）。
         */
        LATE_NOT_HANDED_OVER: '後開 {n} 張未交',
        LATE_PENDING: '後開待補',
        PARTLY_HANDED_OVER: '部分已交件 {date}',
        SEPARATOR: ' · ',
      },
      /** 作廢確認框多一句（紙本已交件時，規格 §6.12）：那張憑證已經在事務所手上（D49 說「憑證」）。 */
      VOID_HANDED_OVER_WARNING: '憑證已於 {date} 交事務所，作廢後請告知事務所',
      /**
       * 同上，憑證標著「不在信封內」、還沒交件（D51，W6.9）：事務所已經有了那幾張。{n} ＝ 張數
       * （paymentRequest.paperOutsideEnvelopeCount —— 只有張數，不帶備註文字）。
       */
      VOID_OUTSIDE_ENVELOPE_WARNING: '{n} 張憑證已直接交給事務所 (不在信封內)，作廢後請告知事務所',
      /** 上面兩件事都有時併成的一句（review U11）：「作廢後請告知事務所」只說一次。 */
      VOID_HANDED_OVER_AND_OUTSIDE_WARNING:
        '憑證已於 {date} 交事務所，另 {n} 張已直接交給事務所 (不在信封內)，作廢後請告知事務所',
      /**
       * 補交那一列的整句：{note} ＝ PAPER_RECEIPT.SHEET.NOTES.LATE_HANDOVER 的那一句（後開補交
       * ／補交，前後端鏡射），{sheets} ＝ 放進信封的那幾張（與「憑證」欄同一套字，
       * VOUCHER_CELL）。送件單的備註與交件勾選清單的第二行是同一句。
       */
      SHEET_LATE_HANDOVER_LINE: '{note}：{sheets}',
      /** 送件單「未取得憑證（結案）」那一段的憑證欄：那張紙不在信封裡，不印張數。 */
      SHEET_CLOSED_VOUCHER: '未取得',
      /**
       * 同一欄、本版的送件單（sheetLayout 2，D62）：那一列有號碼時，每一張憑證一行，號碼後面寫
       * 這一句 —— 事務所對得回支出證明單與自己的紀錄。{paper} ＝ 號碼，收據寫「收據」；沒有號碼
       * 的發票那一行、或整列都沒有號碼時，照舊只寫 SHEET_CLOSED_VOUCHER。
       */
      SHEET_CLOSED_VOUCHER_NUMBER: '{paper} (未取得)',
      /**
       * 交件視窗預覽裡，封面「交件」那一格寫這個字，不寫時刻（mockup § 3：預覽還沒交件，
       * 沒有交件時刻）。正式的送件單照舊印交件時刻。
       */
      SHEET_PREVIEW_ID: '預覽',
      /**
       * 交件明細（sheetLayout 2）一張請款單最多連著印幾行憑證，再多就從下一頁接著印、第一行寫
       * 「{單號} (續)」（W0.0 F22）。A4 扣掉頁首約放得下 30 行；留幾行給換行的用途與第二行日期。
       * 不超過這個數的單整張印在同一頁，不會被切開。
       */
      SHEET_PAPER_LINES_PER_PAGE: 25,
    },
    /**
     * 請款送出後的確認頁（不是 toast）。沒有任何紙本要交時整頁略過。
     * {days} 來自 PAPER_RECEIPT.HAND_IN_DAYS，{name} 是總務的姓名。FE-ONLY。
     */
    REQUEST_SUBMITTED: {
      TITLE: '請款單已送出',
      HAND_IN:
        '請在 {days} 天內把下列紙本憑證交給總務（{name}），附便條註明單號 {requestNumber} 與姓名。',
      /**
       * 同一句，但不指名總務是誰。名字由 payment-submitPaymentRequest 回傳
       * （custodian.names，依角色查 users），經 history.state 帶到送出後畫面；
       * 重新整理後 state 消失、或系統裡還沒有人具總務角色時，就用這一句。
       */
      HAND_IN_NO_NAME:
        '請在 {days} 天內把下列紙本憑證交給總務，附便條註明單號 {requestNumber} 與姓名。',
      /**
       * 機密請款、且看畫面的人就是主管本人：紙本不經總務，自行保管到交件。送件單的名字
       * 用分包的對外名稱（PAPER_RECEIPT.BUNDLES：主管請款），與印出來的那一張一致。
       */
      HAND_IN_CONFIDENTIAL:
        '機密請款：下列紙本憑證請由主管自行保管，交件時連同主管請款送件單交事務所。',
      /**
       * 機密請款、但送出的人不是主管（例：出納代建）：紙本一樣不經總務，要交給
       * 主管。名字由 payment-submitPaymentRequest 的 custodian.names 帶過來。
       */
      HAND_IN_TO_SUPERVISOR:
        '請在 {days} 天內把下列紙本憑證交給主管（{name}），附便條註明單號 {requestNumber} 與姓名。',
      /** 同一句，但系統裡查不到主管姓名（或重新整理後 state 已消失）。 */
      HAND_IN_TO_SUPERVISOR_NO_NAME:
        '請在 {days} 天內把下列紙本憑證交給主管，附便條註明單號 {requestNumber} 與姓名。',
      EINVOICE: '電子發票請列印證明聯，一併交出。',
      /**
       * 送出後這張單目前卡在誰身上（狀態 → 一句話）。查無對應狀態就不顯示這一行 ——
       * 寧可少一行，也不要講一句不確定的話。
       */
      STAGE: {
        pending_approval: '待主管審核',
        pending_approval_l2: '待二級審核',
        approved: '已自動核准',
      } as Record<string, string>,
      /** 後開發票那一列在送出後畫面上的說明（沒有號碼可印，只能先記著）。 */
      LINE_DEFERRED: '後開發票 — 收到後請至我的請款補發票',
      /** 合計列：張數與金額講的是同一疊紙（金額印在同一列的右邊）。 */
      TOTAL: '應交 {n} 張 · 憑證合計',
      BUTTON_NEW: '再建一筆',
      BUTTON_LIST: '查看我的請款',
      /** 讀不到那張單（重新整理後網址失效、權限不足）時的退路。 */
      NOT_FOUND: '找不到這張請款單，可到「我的請款」查看。',
      /**
       * {custodian} ＝ PAPER_RECEIPT.CUSTODIAN_LABELS[分包]：機密單的紙本不經總務。
       * 號碼、發票日期、影像三樣都要（規格 v3.3 §6.11，伺服器把關），所以三樣都講到。
       */
      DEFERRED_BANNER:
        '此單有 {n} 張後開發票尚未補：收到發票後請填號碼、發票日期並上傳影像，再把紙本交由{custodian}保管。',
      FILL_BUTTON: '補發票',
      /** 「補發票」按鈕還按不下去時，講清楚還差什麼（按鈕本身不會說話）。 */
      FILL_HINT: '填發票號碼、發票日期、上傳影像後即可送出。',
      /** 補發票成功後的提示 —— 影像與號碼都進系統了，剩下紙本。 */
      FILL_DONE: '已補上發票，請把紙本交由{custodian}保管',
      /** 這張發票是申請人自己補上來的（總務／出納看明細時要看得出來源）。 */
      FILLED_BY: '{name} 於 {date} 補入',
      TRACKING_BANNER: '紙本憑證催交中（期限 {deadline}）：{note}',
      /** 收件人沒寫備註時的同一句話 —— 不用破折號佔位，直接講申請人要做什麼、在哪一天之前。 */
      TRACKING_BANNER_NO_NOTE: '紙本憑證催交中：請在 {deadline} 前把紙本交給{custodian}。',
    },
    /**
     * 不付款憑證（CATEGORY_RULES[...].payable === false）的畫面字串。FE-ONLY。
     * 例：押金設算利息 —— 房東開立的統一發票，要登錄、要影像、要紙本、可扣抵，
     * 但公司不付這筆錢，所以不列入應付合計，也不進企網銀轉帳檔。
     */
    LINE_ITEM_NON_PAYABLE: {
      TAG: '不付款',
      VOUCHER_TOTAL: '憑證合計',
      PAYABLE_TOTAL: '應付合計',
      AUTO_FILLED: '（自動帶入）',
      NOTE_COUNT: '不付款憑證 {n} 張 NT${amount}',
      /**
       * 整張單都是不付款憑證 —— 應付合計會是 0，沒有錢要匯出去。送出前就擋下來，
       * 字句與伺服器（BUSINESS_RULES.PAYMENT.NON_PAYABLE_SUBMIT_ERROR）一致。
       */
      ALL_LINES_ERROR: '本單所有明細皆為不付款憑證，無付款金額，無法送出',
    },
    /**
     * 發票日期・消費日期 需確認 — FE-ONLY, soft (tasks/todo-invoice-date-check.md D1, D6).
     * A line's dates are 「需確認」 when (a) 發票日期／收據日期 or 消費日期 is more than
     * MAX_AGE_MONTHS calendar months before the base day (送出日; today while filling),
     * (b) either date is after today, or (c) the two are more than MAX_APART_MONTHS apart.
     * Only alerts — never refuses a save, a 送出, an approval or a payment, and nothing
     * is stored (the flag is computed from the dates on screen). 出差旅費 uses the trip
     * rule instead (憑證日期 vs TRAVEL.OTHER_LINE_PRE_TRIP_DAYS before the trip start).
     * The rule itself is `utils/lineDateCheck.ts`.
     */
    LINE_DATE_CHECK: {
      /** (a) 「比送出日早超過 2 個月」 — calendar months, same day allowed (11/02 → 09/02 ok). */
      MAX_AGE_MONTHS: 2,
      /** (c) 發票日期 and 消費日期 more than this many calendar months apart. */
      MAX_APART_MONTHS: 2,
      /** Shown number = days ÷ this, rounded (389 d → 13). */
      DAYS_PER_MONTH: 30.44,
    },
  },

  // 員工欄位 — MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.EMPLOYEE_FIELDS
  EMPLOYEE: {
    /** Max length of 職稱 (jobTitle) — mirrors EMPLOYEE_FIELDS.MAX_JOB_TITLE_LENGTH. */
    MAX_JOB_TITLE_LENGTH: 60,
  },

  /**
   * 身分證／居留證號 — see the backend block for the rule. The form uses it for an
   * instant format hint (FrontendConfigUtils.isValidPersonalId); the server is the
   * gate. The full number never reaches the browser after it is saved — the list
   * shows `personalIdMasked`.
   * Mirror of functions/src/config/appConfig.ts BUSINESS_RULES.PERSONAL_ID — keep in sync.
   */
  PERSONAL_ID: {
    LABEL: '身分證／居留證號',
    SINGLE_LETTER_PATTERN: /^[A-Z][1289]\d{8}$/,
    OLD_RESIDENT_PATTERN: /^[A-Z][A-D]\d{8}$/,
    LETTER_CODES: {
      A: 10,
      B: 11,
      C: 12,
      D: 13,
      E: 14,
      F: 15,
      G: 16,
      H: 17,
      I: 34,
      J: 18,
      K: 19,
      L: 20,
      M: 21,
      N: 22,
      O: 35,
      P: 23,
      Q: 24,
      R: 25,
      S: 26,
      T: 27,
      U: 28,
      V: 29,
      W: 32,
      X: 30,
      Y: 31,
      Z: 33,
    } as Record<string, number>,
    MASK_KEEP_HEAD: 3,
    MASK_KEEP_TAIL: 3,
    MASK_STARS: '****',
    INVALID_MESSAGE: '身分證／居留證號格式不正確，請核對（例：A123456789）',
    LENGTH: 10,
    DUPLICATE_MESSAGE: '此身分證／居留證號已登錄於其他員工，請核對',
    /** The EXCEL薪資轉帳檔 checks C 欄 against the ACCOUNT HOLDER (存款人). */
    HOLDER_HINT: '須為薪資帳戶戶名本人的證號',
  },

  // 出差旅費 — MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL (keep in lockstep)
  TRAVEL: {
    REGIONS: ['domestic', 'intl', 'prc'] as const,
    RATE_TABLE_KINDS: ['intl', 'prc'] as const,
    /**
     * 日支數額表類別的顯示名稱（日支數額表管理頁的類別選擇器、狀態列與更新通知）。
     * 與 TRAVEL_REGION_LABELS 的 intl／prc 用字一致。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.RATE_TABLE_KIND_LABELS.
     */
    RATE_TABLE_KIND_LABELS: { intl: '國外', prc: '大陸港澳' } as Record<'intl' | 'prc', string>,
    /**
     * 日支數額表來源的顯示名稱。seed = 程式內建的版本（資料庫還沒有任何版本時採用），
     * firestore = 已寫入資料庫的版本。一旦資料庫有任何一版，就完全不再參考內建版本。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.RATE_TABLE_SOURCE_LABELS.
     */
    RATE_TABLE_SOURCE_LABELS: { seed: '內建', firestore: '資料庫' } as Record<string, string>,
    /**
     * 「這次讀不到主計總處法規頁的版本」的原因說明（日支數額表管理頁的第三種狀態）。
     * key 對應 functions/src/payment/travel/rateTableSource.ts 的 LawPageUnreadableReason。
     * 「讀不到」不等於「已是最新」，所以畫面必須說得出是哪一種讀不到。
     */
    RATE_TABLE_UNREADABLE_REASON_LABELS: {
      fetch_failed: '連不上主計總處法規網站',
      no_revision_date: '法規頁上找不到修正日期，頁面版面可能已變更',
      no_pdf_link: '法規頁上找不到數額表附件連結，頁面版面可能已變更',
    } as Record<string, string>,
    /**
     * PDF 解析器對「這一行看不懂、已略過」所用的字樣。
     * MIRROR of functions/src/config/appConfig.ts
     * BUSINESS_RULES.TRAVEL.RATE_SAVE_GUARD.UNPARSED_WARNING_MARKER —— 伺服器一律拒絕
     * 帶著這種警示的匯入結果，前端據此先停用「儲存」並說明原因（改字要兩邊一起改）。
     */
    RATE_UNPARSED_WARNING_MARKER: '無法解析',
    REGION_TABLE_KIND: { domestic: null, intl: 'intl', prc: 'prc' } as Record<
      string,
      'intl' | 'prc' | null
    >,
    LINE_KINDS: ['per_diem', 'transport', 'lodging', 'meal', 'other'] as const,
    EVIDENCE_KINDS: [
      'e_ticket',
      'boarding_pass',
      'passport',
      'airline_cert',
      'entry_exit_cert',
      'other',
    ] as const,
    /**
     * 手動費用行的**固定**費用類別 —— 四張表都是 admin_travel（PO 決策 P7）。
     *
     * 費用類別在差旅單裡沒有選擇的餘地：內帳沒有「住宿」科目，總帳本來就把整趟出差
     * 當成一個 bucket，明細要分交通／住宿／膳食是靠 travelLineKind（報告單依此分類），
     * 不是靠費用類別。讓使用者選一個永遠只有一個正確答案的欄位，只會產生錯誤資料。
     * 前端專用（決定表單預設與唯讀顯示）；後端以 travelLineKind 為準。
     */
    MANUAL_LINE_CATEGORY: {
      transport: 'admin_travel',
      lodging: 'admin_travel',
      meal: 'admin_travel',
      other: 'admin_travel',
    } as Record<string, string>,
    /**
     * 出差地區 → 手動費用行的稅別／憑證預設組合（`TAX_DOC_OPTIONS` 的 value）。
     *
     * 國外／大陸港澳的收據不可能是統一發票，預設成應稅 5% 會讓進項稅額憑空多一筆。
     * 下拉選單仍然全開（國內開立的機票統一發票是合法的，只是要自己改），
     * 真正的把關在後端 TRAVEL.TAX_DOC_RULE —— 這裡只是把預設值放在對的一邊。
     * 前端專用。
     */
    DEFAULT_TAX_DOC_BY_REGION: {
      domestic: 'taxable_5__uniform_invoice',
      intl: 'tax_exempt__receipt_only',
      prc: 'tax_exempt__receipt_only',
    } as Record<string, string>,
    /** 稅別／憑證硬性規則 — MIRROR of backend TRAVEL.TAX_DOC_RULE（derive.ts / amendments.ts 以同一句拒收）。 */
    TAX_DOC_RULE: {
      MESSAGE: '應稅 5% 只能搭配統一發票；國外憑證請選「收據（免稅）」。',
    },
    /**
     * 「憑證」在各種狀態下的固定說法。
     *
     * 差旅單收的是收據、行程單、登機證，不全是發票；同一個畫面一下寫「發票」一下寫
     * 「憑證」會讓人以為是兩件事。所有標題與狀態字樣一律從這裡取，元件不自己拼字。
     * `pendingN` 的 {n} 由呼叫端代入件數。前端專用（純文案）。
     * 這是出納驗證影像的字（規格 v3.3 §2：影像一律寫「驗證」）；一般請款的同一組字在
     * PAYMENT.RECEIPT_VERIFY_UI.WORDING，兩份的鍵必須一樣。
     */
    RECEIPT_WORDING: {
      received: '憑證已驗證',
      deferred: '後開憑證',
      partial: '部分憑證已驗證',
      issue: '憑證有異常',
      pendingN: '憑證待驗證 {n} 件',
      pending: '憑證待驗證',
      receivedShort: '憑證已驗證',
    } as Record<string, string>,
    /**
     * 外幣憑證原始金額 —— 手動費用行（交通／住宿／膳食／其他）的「選填」憑證出處。
     *
     * 新台幣金額（`amountWithTax`）永遠是請領依據；這三個欄位只是讓會計師能把
     * NT$ 對回收據上印的原幣數字。因為是出處而不是請領金額，沒填或填錯都不擋單：
     * 伺服器會丟掉看不懂的值並改記一筆警示。
     *
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.ORIGINAL_AMOUNT.
     */
    ORIGINAL_AMOUNT: {
      /** 幣別代碼長度（ISO 4217 為 3 碼英文大寫；放寬到 4 碼容納少數非標準寫法） */
      CURRENCY_MIN_LENGTH: 3,
      CURRENCY_MAX_LENGTH: 4,
      /** 原幣金額上限 —— 超過視為輸入錯誤 */
      MAX_AMOUNT: 100_000_000,
      /** 匯率上限：1 單位原幣可換得的新台幣 */
      MAX_FX_RATE: 10_000,
      /**
       * 原幣 × 匯率 相對列報新台幣金額的容許倍數。抓的是打錯一位數（10 倍）這類
       * 明顯錯誤，不是手續費／進位差；超出只警示，不擋單。
       */
      RATE_TOLERANCE_FACTOR: 1.5,
      /**
       * 匯率來源字串長度上限（`originalFxSource`）。留空時伺服器補上
       * FX.DEFAULT_SOURCE，所以這個上限只約束「自己打字改寫來源」的情況。
       */
      MAX_FX_SOURCE_LENGTH: 40,
      /**
       * 列印換算匯率時最多保留幾位小數。日圓等小面額幣別的匯率是 0.2134 這種
       * 四位小數，用金額的兩位小數去印會變成 0.21 —— 會計師照著印出來的數字乘
       * 回去會對不上收據。多餘的 0 會自動去掉（0.22 仍印 0.22）。
       */
      FX_RATE_MAX_DECIMALS: 6,
    },
    /**
     * 費用報支方式 —— 一趟出差只能擇一，兩者互斥。
     *
     *  - per_diem 日支數額法（預設，現行行為）：生活費由主計總處日支數額表推導；
     *    膳食費已含於日支數額內，不得另行檢據列報（`mealLines: 'forbidden'`）；
     *    住宿費依 LODGING_MODES 決定；國外／大陸港澳須填匯率。
     *  - actual 實報實銷：完全不推導日支。膳食費／住宿費／交通費／其他一律以
     *    新台幣檢據核實列報，因此不需匯率（`usesFx: false`）、不適用住宿費報支
     *    方式（`appliesLodgingMode: false`，住宿費明細一律可列報）。公司內規
     *    上限見 ACTUAL_EXPENSE_CAPS。
     *
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.EXPENSE_METHODS.
     */
    EXPENSE_METHODS: {
      per_diem: {
        label: '日支數額法',
        derivesPerDiem: true,
        usesFx: true,
        appliesLodgingMode: true,
        mealLines: 'forbidden',
      },
      actual: {
        label: '實報實銷',
        derivesPerDiem: false,
        usesFx: false,
        appliesLodgingMode: false,
        mealLines: 'allowed',
      },
    } as Record<
      string,
      {
        label: string;
        derivesPerDiem: boolean;
        usesFx: boolean;
        appliesLodgingMode: boolean;
        mealLines: 'forbidden' | 'allowed';
      }
    >,
    /** 未指定時（含尚未帶此欄位的舊文件）一律視為日支數額法 —— 現行行為。 */
    DEFAULT_EXPENSE_METHOD: 'per_diem',
    /**
     * 實報實銷的「公司內規」上限（新台幣整數）。這是 COMPANY POLICY，不是稅法規定：
     * 查核準則沒有這些數字，是公司自行決定每人每日膳食／住宿與每趟交通／其他的
     * 報支天花板。
     *
     *  - `null` = 不設限（預設值）。四項全為 null 時，實報實銷的行為與未導入本
     *    功能前完全相同；PO 之後填入數字即可啟用，不需改程式。
     *  - 膳食費／住宿費按「每人每日」計：同一消費日期的該類明細合計，與
     *    `上限 × 出差人數` 比較。
     *  - 交通費／其他按「每趟」計：整張出差單的該類明細合計。
     *  - 只影響「之後的驗證」（存檔與送出），不會改寫既有的請款單金額。
     *  - 日支數額法完全不看這組數字（該法的天花板是日支數額表本身）。
     *
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.ACTUAL_EXPENSE_CAPS.
     */
    ACTUAL_EXPENSE_CAPS: {
      meal: { perDayPerPerson: null },
      lodging: { perDayPerPerson: null },
      transport: { perTrip: null },
      other: { perTrip: null },
      // Explicitly typed (not `as const`) so the PO can fill in an integer
      // without a type error, exactly like LODGING_MODES above.
    } as {
      meal: { perDayPerPerson: number | null };
      lodging: { perDayPerPerson: number | null };
      transport: { perTrip: number | null };
      other: { perTrip: number | null };
    },
    DOMESTIC_PER_DIEM: { TIER_700: 700, TIER_600: 600 },
    DOMESTIC_TIER_700_TITLES: ['董事長', '總經理', '經理', '廠長', '負責人'] as readonly string[],
    /**
     * 職稱選單裡代表「非 700 級距」的預設職稱。
     *
     * 查核準則 §74 只列出 NT$700 那幾個職稱，其餘一律 NT$600 —— 但員工卡的職稱欄
     * 若留空，選單就只剩 700 級距的選項，看起來像是「不填就沒有日支」。列一個具名
     * 的 600 級距選項，才能一眼看出兩級都存在。職稱本身仍是開放欄位（可自行輸入），
     * 這只是選單上的預設值。前端專用（選單內容），後端沒有對應設定。
     */
    DOMESTIC_TIER_600_DEFAULT_TITLE: '一般職員',
    /**
     * 國內膳雜費按「日」計，與是否留宿無關（查核準則 §74）。
     * 某日不列計＝勾選該日的「不列計」（由勾選框寫入 overrideMinor: 0 與自動理由），
     * 不是叫使用者自己在覆寫欄填 0 —— 手打的 0 與「還沒填」在畫面上分不出來。
     * 清空留宿地是**國外**的排除方式，國內不適用。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.DOMESTIC_DAY_RULE.
     */
    DOMESTIC_DAY_RULE:
      '國內出差膳雜費依查核準則 §74 按日計給：行程範圍內每一日均計，與是否留宿無關；某日不列計請勾選該日的「不列計」。',
    /** 國外／大陸港澳：逐日以留宿地日支數額計給（日支數額表 附註 3）。 */
    FOREIGN_DAY_RULE: '每一日預設以目的地日支數額計給；該日不列計時請清空「留宿地」。',
    /**
     * 逐日行程的預設值（日支數額表 附註 3）。
     *
     * - 首日／末日是去程與回程，地點先帶「去程」／「回程」，由申請人改寫為實際行程。
     * - `LAST_DAY_HAS_NO_OVERNIGHT`: PO decision D10 (2026-09-02) — 行程內每一日
     *   （含回程日）都預設以目的地日支計給（對應紙本範例 2/10–2/15 = 6 日全計）。
     *   附註 3「以留宿地計價」是申請人的手動例外：清空該日留宿地即不計。
     *   保留成具名旗標，日後 CPA 若有不同認定可在此一處翻轉。
     */
    DAY_DEFAULTS: {
      FIRST_DAY_LOCATION: '去程',
      LAST_DAY_LOCATION: '回程',
      LAST_DAY_HAS_NO_OVERNIGHT: false,
    },
    /**
     * 返國當日（行程最後一日）的生活費倍率 —— 國外／大陸港澳限定。
     *
     * 國外出差旅費報支要點：「住宿免費宿舍、過境旅館或在交通工具歇夜**及返國當日**，
     * 生活費按該地區生活費日支數額 30％ 報支」，而查核準則 §74 對營利事業的規定是
     * 「國外出差膳宿雜費應**比照國外出差旅費報支要點所定**，依…日支數額表之日支數額
     * 認定」—— 30% 是該要點所定的一部分。回國當日人在飛機上、境外不發生住宿，
     * 這個比例正是為此而設。
     *
     * 只套在**行程最後一日**。要點另有三種同為 30% 的情形（免費宿舍、過境旅館、
     * 在交通工具歇夜），但那需要逐日蒐集系統目前沒有的資訊，且不是結構性可推導的；
     * 需要時另案處理。
     *
     * 國內不適用：§74 的 700／600 是按日計給，要點是國外規則。
     *
     * 與 LODGING_MODES 的 factor **不相乘**：要點寫的是「按日支數額 30%」，
     * 基準是表定全額，不是減半後的金額。最後一日一律取全額的 30%。
     *
     * PO 2026-09-17 裁示採用。數字放在這裡，是因為「比照」的射程（是否連同
     * 要點的操作規則一併適用於營利事業）尚待會計師確認 —— 屆時只要改這一個值。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.LAST_DAY_FACTOR.
     */
    LAST_DAY_FACTOR: 0.3,
    LODGING_MODES: {
      per_diem_full: { factor: 1, lodgingLines: 'forbidden' },
      actual_lodging_50: { factor: 0.5, lodgingLines: 'required' },
      /**
       * 國內（查核準則 §74）：膳雜費依職稱每日 700／600 全額計給（不折半），
       * 住宿費另行核實檢據列報 —— 折半是國外「膳宿雜費」的規則，不適用國內。
       */
      domestic_receipted: { factor: 1, lodgingLines: 'allowed' },
    } as Record<string, { factor: number; lodgingLines: 'forbidden' | 'required' | 'allowed' }>,
    /** 各地區可用的住宿費報支方式（陣列首項＝該地區預設）。 */
    LODGING_MODES_BY_REGION: {
      domestic: ['domestic_receipted'],
      intl: ['per_diem_full', 'actual_lodging_50'],
      prc: ['per_diem_full', 'actual_lodging_50'],
    } as Record<string, readonly string[]>,
    ROUNDING: { usd: 'cents', fxToTwd: 'ceil_per_line' } as const,
    FX: {
      DEFAULT_CURRENCY: 'USD',
      DEFAULT_SOURCE: '台灣銀行現金賣出價',
      SANITY_RANGE: { USD: [25, 40] } as Record<string, [number, number]>,
      RATE_DATE_OFFSET_DAYS: -1,
      /** 匯率日期最多可早於出差起日幾天（超過即視為引用過期匯率）。 */
      RATE_DATE_MAX_LOOKBACK_DAYS: 30,
      BOT_HISTORY_URL: 'https://rate.bot.com.tw/xrt/history/USD',
    },
    /**
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.OTHER_LINE_PRE_TRIP_DAYS.
     *
     * 「其他」明細的消費日期可以早於 出差起日 幾天（PO 2026-09-17，會計師要求
     * 簽證費列入出差旅費）。交通費／住宿費／膳食費不適用——它們的消費日期就是
     * 搭乘／住宿／用餐當日。180 天是因為簽證常常提前數月辦；上界仍是 出差迄日。
     */
    OTHER_LINE_PRE_TRIP_DAYS: 180,
    MAX_TRIP_DAYS: 60,
    /**
     * 常用出差地點 —— 出差地點選單最上方「常用地點」群組要列出的城市。
     *
     * 政府日支數額表有 551 個國外城市，常去的那幾個每次都要重打，因此置頂成一組。
     * 值是日支數額表的 `cityId`（`intl/…` 屬國外表、`prc/…` 屬大陸港澳表），
     * 可於 functions/src/payment/travel/seed/perDiem-2026.ts 查得；陣列順序即畫面
     * 顯示順序，PO 增減城市只需要改這裡。
     *
     * 兩張表的 id 可以混在同一份清單：畫面一律拿目前載入的那張數額表去解析，
     * 解析不到的（不屬於目前地區、或政府改版後已不存在）整筆不顯示，
     * 不會出現一個點了沒有數額的壞選項。
     *
     * 前端專用（選單排序），後端沒有對應設定。
     */
    FREQUENT_CITY_IDS: [
      'intl/indonesia/jakarta', // 雅加達
      'intl/u-s-a/los-angeles-california', // 洛杉磯
      'intl/united-kingdom/london', // 倫敦
      'prc/beijing', // 北京
      'prc/shanghai', // 上海
      'prc/shenzhen', // 深圳
    ] as readonly string[],
    /** 常用地點群組在出差地點選單上的標題。 */
    FREQUENT_CITY_GROUP_LABEL: '常用地點',
    MAX_TRAVELLERS: 10,
    MAX_CITY_NAME_LENGTH: 60,
    MAX_DAY_TEXT_LENGTH: 200,
    MAX_PURPOSE_LENGTH: 500,
    /** 出差單備註上限（比一般請款單的 500 字寬，行程說明常較長）。 */
    MAX_NOTES_LENGTH: 2000,
    MAX_TRAVEL_DETAILS_BYTES: 200_000,
    /** 查無日支標準（標準為 0）時的覆寫上限，單位同 rateMinor（美分／新台幣元）。 */
    MAX_OVERRIDE_MINOR: 100_000,
    MAX_EVIDENCE_FILES: 12,
    /**
     * Per-evidence-file size cap, enforced SERVER-side against the object's real
     * Storage metadata (`getMetadata().size`) — the client-declared `fileSize` is
     * never trusted. Mirrored here so the panel can reject an oversized file
     * BEFORE the upload instead of after the round trip.
     */
    MAX_EVIDENCE_FILE_BYTES: 20 * 1024 * 1024,
    /** 手動輸入的交通/住宿/其他明細上限（生活費為系統推導，不計入） */
    MAX_MANUAL_ITEMS: 50,
    /**
     * 出差單費用明細「不得使用」的費用類別代碼。
     *
     * 交際費（`admin_entertainment`）自成一個會計科目，且有自己的可扣抵上限
     * （查核準則 §80）；會計師 2026-09-03 明確要求「不列入出差單，另外以交際費
     * 報支」。因此出差表單的四張明細表都把它從選單（含 Excel 貼上）拿掉，伺服器
     * 送出時再擋一次（BUSINESS_RULES.TRAVEL.REPORT.ENTERTAINMENT_CATEGORY）。
     * 一般請款單不受影響 —— 那才是交際費該走的單。
     *
     * MIRROR of functions/src/config/appConfig.ts
     * BUSINESS_RULES.TRAVEL.REPORT.ENTERTAINMENT_CATEGORY（後端只有單一代碼，
     * 因為它在後端的角色是「被拒絕的類別」而不是清單）。
     */
    FORBIDDEN_CATEGORIES: ['admin_entertainment'] as readonly string[],
    /** 手動明細可用的憑證類別 — 不含 no_document（交通/住宿/其他須有憑證，查核準則 §74） */
    MANUAL_LINE_DOCUMENT_TYPES: [
      'uniform_invoice',
      'receipt_only',
      'document_elsewhere',
      'document_lost',
    ] as readonly string[],
    /**
     * 交通費明細含這些字樣即視為有機票（大小寫不拘），用來提醒補附登機證／護照影本。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.REPORT
     * .TRANSPORT_KEYWORDS.AIR（後端保留其 REPORT 下的那一份）。
     */
    FLIGHT_KEYWORDS: ['機票', '航空', 'flight'] as readonly string[],
    /** 日支全額模式下，明細描述含這些字樣會產生重複請領提醒（大小寫不拘） */
    LODGING_KEYWORDS: [
      '住宿',
      '飯店',
      '酒店',
      '旅館',
      '民宿',
      'hotel',
      'lodging',
    ] as readonly string[],
    /**
     * 全額日支模式下，手動明細描述含這些關鍵字 → 伺服器持久化警示（膳雜費已含餐費，
     * 疑似重複列報）。Mirror of BUSINESS_RULES.TRAVEL.MEAL_KEYWORDS.
     */
    MEAL_KEYWORDS: ['餐', '膳', '飲', 'meal', 'breakfast', 'lunch', 'dinner'] as readonly string[],
    PER_DIEM_LINE: {
      descriptionTemplate: '生活費 {city} {start}~{end} {days}日 {traveller}',
      expenseDateRule: 'endDate',
      category: 'admin_travel',
      documentType: 'no_document',
      taxType: 'tax_exempt',
    } as const,
    OVERLAP: {
      hardBlockExactDuplicate: true,
      warnOnOverlap: true,
      lookbackMonths: 6,
      /** 已作廢／已拒絕不再佔用該區間，不擋也不提醒 */
      IGNORED_STATUSES: ['voided', 'declined'] as readonly string[],
      MAX_SCAN: 500,
    },
    /**
     * 可申報項目 checklist（住宿方式比較卡片）。日支數額表是硬上限，膳食與雜費一律
     * 含在日支數額內，因此「可以附什麼」只有交通、旅行證明與住宿費三類。
     * `when` 決定該項目在目前住宿方式下是否列出：
     *  - `always`  一律列出
     *  - `flight`  交通費明細有機票時才列出（登機證／護照影本，查核準則 §74）
     *  - `lodging` 目前住宿方式可列報住宿費時才列出（`lodgingLines !== 'forbidden'`）
     */
    CLAIMABLE_DOCUMENTS: [
      { id: 'transport', label: '機票／交通費憑證', domesticLabel: '交通費憑證', when: 'always' },
      { id: 'boarding', label: '登機證或護照影本', when: 'flight' },
      { id: 'lodging', label: '住宿費收據', when: 'lodging' },
    ] as ReadonlyArray<{
      id: string;
      label: string;
      domesticLabel?: string;
      when: 'always' | 'flight' | 'lodging';
    }>,
    /** 兩種住宿方式都不得另附的憑證 —— 膳雜費已含在日支數額內。 */
    NOT_CLAIMABLE_HINT: '膳食與雜費已含於日支數額，無須另附收據。',
    /**
     * 可作為機票行程證明的旅行證明文件種類（查核準則 §74）。
     * 電子機票刻意不算：它證明的是票價與訂位，不是有沒有登機。
     * 自動通關（e-Gate）之後護照上不會有出入境章，所以另外收兩種同等效力的證明：
     * 航空公司搭機證明（`airline_cert`）與移民署入出國日期證明書（`entry_exit_cert`）。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.BOARDING_PROOF_KINDS.
     */
    BOARDING_PROOF_KINDS: [
      'boarding_pass',
      'passport',
      'airline_cert',
      'entry_exit_cert',
    ] as readonly string[],
    /**
     * 送出前必須至少附一件 BOARDING_PROOF_KINDS 文件的出差地區。
     *
     * 日支數額法免附膳宿收據，但「免附收據」不等於「免證明行程存在」：國外／
     * 大陸港澳出差即使是純日支、連一筆交通費明細都沒有，仍必須證明人真的出去了。
     * 國內出差不適用。草稿不受限 —— 與其他 §74 檢核一樣只擋送出。
     *
     * ⚠️ 這是「整趟一件」的規則，DELIBERATELY NOT 逐筆明細比對件數：一份 PDF 可能
     * 同時含來回兩段、多位出差人的登機證，要求「每筆明細各一件」會擋掉正確的單，
     * 並逼使用者為了湊件數把檔案拆開。件數不足只由 TravelEvidencePanel 以警示提醒。
     *
     * MIRROR of functions/src/config/appConfig.ts
     * BUSINESS_RULES.TRAVEL.EVIDENCE_REQUIRED_REGIONS.
     */
    EVIDENCE_REQUIRED_REGIONS: ['intl', 'prc'] as readonly string[],
    /** 缺件時的訊息 —— 前後端逐字相同（改字要兩邊一起改）。
     *  MIRROR of BUSINESS_RULES.TRAVEL.EVIDENCE_REQUIRED_MESSAGE. */
    EVIDENCE_REQUIRED_MESSAGE:
      '國外／大陸港澳出差須附登機證（含手機電子登機證截圖）、電子機票行程單＋航空公司搭機證明、護照影本，或移民署入出國日期證明書（查核準則 §74）',
    /**
     * 同一句話的分段版本，只給畫面用（EVIDENCE_REQUIRED_MESSAGE 是驗證訊息，
     * 前後端逐字相同，不能塞連結）。把「入出國日期證明書」做成連到 MyData 的連結，
     * 使用者才不用自己去找是哪個網站申請。分段內容串起來必須與上面那句一致。
     * 前端專用。
     */
    EVIDENCE_HINT_SEGMENTS: [
      { text: '登機證（含手機電子登機證截圖）' },
      { text: '、電子機票行程單＋航空公司搭機證明、護照影本，或移民署' },
      { text: '入出國日期證明書', href: 'https://mydata.nat.gov.tw/inquiry/docs' },
      { text: '（查核準則 §74）' },
    ] as ReadonlyArray<{ text: string; href?: string }>,
    /**
     * 自動通關的人沒有出入境章，最常見的疑問就是「那我要附什麼」。
     * 這一句直接給答案，避免對方以為自己不符合規定。前端專用。
     */
    EVIDENCE_EGATE_HINT: '自動通關者請上傳登機證截圖。',
    EVIDENCE_STORAGE_SEGMENT: 'travel-evidence',
    /**
     * 誰可以檢視「旅行證明文件」（護照影本／登機證）。申請人本人一律可看；
     * 其餘僅限主管與出納 —— 護照影本屬個資，總務只需要逐筆收據即可核帳。
     * MIRROR of the privileged set in functions/src/payment/bankDocuments.ts
     * (getReceiptUrl, itemId === EVIDENCE_STORAGE_SEGMENT).
     */
    EVIDENCE_PRIVILEGED_ROLES: [
      SYSTEM_CONSTANTS.USER_ROLES.SUPERVISOR,
      SYSTEM_CONSTANTS.USER_ROLES.TREASURY,
    ] as readonly string[],
    /**
     * 差旅報告單 bundle 的 callable timeout（毫秒）。
     * MIRROR of functions/src/config/appConfig.ts BUSINESS_RULES.TRAVEL.REPORT
     * .TIMEOUT_SECONDS (540 s) — the SDK default is 70 s, which would abort the
     * browser call long before a photo-heavy month finishes streaming, leaving the
     * operator with an error for an export that actually succeeded. Only the two
     * exportTravelReportBundle calls opt in; every other callable keeps 70 s.
     */
    REPORT_EXPORT_TIMEOUT_MS: 540_000,
    /**
     * 匯入政府日支數額表的 callable timeout（毫秒）。
     * MIRROR of functions/src/payment/travel/rateTableAdmin.ts 的
     * `importTravelRateTable` `timeoutSeconds` (120 s) × 1000 — SDK 預設 70 s 會在
     * 伺服器仍在下載／解析政府 PDF 時就中止呼叫，讓維護者看到一個其實還在跑的錯誤。
     */
    RATE_TABLE_IMPORT_TIMEOUT_MS: 120_000,
    /**
     * 出差人選單的快取有效時間（毫秒）。名單極少變動，短 TTL 讓同一分頁反覆開表單
     * 不必每次都打一次 `payment-getTravelTravellerOptions`，又不會拿到過期名單。
     */
    TRAVELLER_OPTIONS_TTL_MS: 5 * 60_000,
    SEED_VERSIONS: { intl: '2026-01-01', prc: '2026-01-01' } as Record<'intl' | 'prc', string>,
  },

  FEEDBACK_APPROVAL_STATUS: {
    DISPLAY_NAMES: {
      pending: '待填寫',
      review: '待審核',
      approved: '已審核',
      rejected: '已拒絕',
    },
    COLORS: {
      pending: 'default',
      review: 'warning',
      approved: 'success',
      rejected: 'error',
    },
    ICONS: {
      pending: 'edit_note',
      review: 'hourglass_top',
      approved: 'check_circle',
      rejected: 'cancel',
    },
  },

  /**
   * 首堂課判定 — MIRROR of backend BUSINESS_RULES.FIRST_TRIAL (byte-for-byte,
   * functions/scripts/check-config-mirror.cjs). Spec: docs/FIRST_TRIAL_FEEDBACK_BUSINESS_RULES.md.
   * Logic: src/utils/firstTrialShared.ts (parity-tested against the backend copy).
   */
  FIRST_TRIAL: {
    LOOKBACK_DAYS: 300,
    TRIAL_TICKET_KEYWORD: '體驗',
    PARENT_ANSWER_FIELD: 'firstTrialParentAnswer',
    VERDICTS: {
      FIRST: 'first',
      NOT_FIRST: 'not-first',
    },
    SOURCES: {
      SYSTEM: 'system',
      KIOSK_PARENT: 'kiosk-parent',
      ADMIN: 'admin',
      TEACHER: 'teacher',
      LEGACY: 'legacy',
    },
    DISCREPANCY_KINDS: {
      PARENT_NO_SYSTEM_YES: 'parent-no-system-yes',
      PARENT_YES_SYSTEM_NO: 'parent-yes-system-no',
    },
    PARENT_ANSWERS: {
      YES: 'yes',
      NO: 'no',
    },
    VERDICT_ACTIONS: {
      SET_FIRST: 'set-first',
      SET_NOT_FIRST: 'set-not-first',
      RESOLVE_FIRST: 'resolve-first',
      RESOLVE_NOT_FIRST: 'resolve-not-first',
    },
    FEEDBACK_STAGES: {
      NONE: 'none',
      QUEUED: 'queued',
      CREATED: 'created',
      APPROVED: 'approved',
      COMPLETED: 'completed',
      LINE_FAILED: 'line-failed',
    },
    FEEDBACK_REASONS: {
      REISSUE: 'reissue',
      SECOND: 'second',
      OTHER: 'other',
      FIRST_CLASS: 'first-class',
    },
    FAMILY_REVIEW: {
      NAME_SEPARATOR: ' - ',
      NAME_SEPARATOR_FALLBACK: '-',
    },
    LIKELIHOOD: {
      LIKELY_FIRST: 'likely-first',
      NEEDS_CHECK: 'needs-check',
      LIKELY_NOT: 'likely-not',
    },
    LIKELIHOOD_REASONS: {
      CLEAR: 'clear',
      OWN_PRIOR: 'own-prior',
      ONLY_CHILD_ATTENDED: 'only-child-attended',
      PARENT_NO_FAMILY_PRIOR: 'parent-no-family-prior',
      MANY_CHILDREN: 'many-children',
      PARENT_YES_FAMILY_PRIOR: 'parent-yes-family-prior',
      OTHER_ADULT: 'other-adult',
      PARENT_NO_ALONE: 'parent-no-alone',
    },
    PARENT_REPLY_FIELD: 'firstTrialParentReply',
    PARENT_REPLY_NONE: 'none',
  } as const,

  /** 回饋課程類別 — MIRROR of backend BUSINESS_RULES.FEEDBACK_PROGRAMMES (byte-for-byte). */
  FEEDBACK_PROGRAMMES: [
    {
      key: 'SWIM',
      label: '游泳',
      classPrefixes: ['親子游泳', '獨立游泳'],
      ticketKeywords: ['游泳'],
      formSet: 'SWIM',
      enabled: true,
    },
    {
      key: 'FMS',
      label: 'FMS',
      classPrefixes: ['FMS體能課'],
      ticketKeywords: ['FMS'],
      formSet: null,
      enabled: false,
    },
  ] as const,

  /**
   * 首堂課與體驗回饋 — frontend-only display (NOT mirrored). Glyph changes per
   * stage, colour is secondary (spec §3); colours are CSS custom properties
   * from src/css/app.scss, never hex.
   */
  FIRST_TRIAL_DISPLAY: {
    STAGE_ICONS: {
      none: 'post_add',
      queued: 'pending_actions',
      created: 'assignment',
      approved: 'verified',
      completed: 'task_alt',
      'line-failed': 'sms_failed',
    },
    /**
     * The day list's two class-chip warnings (PO 2026-09-18). They used to share
     * one ⚠, so a 首堂 N ⚠ could not be read without hovering — and the row it
     * pointed at looked perfectly normal, because 尚未建立回饋 is not a pill state.
     * Now the glyph says which: `post_add` is the SAME glyph the student row
     * shows for 未建立 (STAGE_ICONS.none) — follow it to the row and the icon
     * matches; the triangle stays reserved for the one that needs a judgement.
     */
    CLASS_MARKER_ICONS: {
      noFeedback: 'post_add',
      discrepancy: 'warning_amber',
      /** 首堂待確認 rows (D-I): the pill's ? badge (D-B); the triangle stays discrepancy-only (P15). */
      review: 'help',
    },
    /**
     * 首堂課 檢查 card headline colour per likelihood (spec §4 可能性三段): CSS custom
     * properties from src/css/app.scss, never hex. Display only.
     */
    LIKELIHOOD_TONES: {
      'likely-first': {
        text: '--pay-panel-teal-text',
        bg: '--pay-panel-teal-bg',
        border: '--pay-panel-teal-border',
      },
      'needs-check': {
        text: '--pay-panel-orange-text',
        bg: '--pay-panel-orange-bg',
        border: '--pay-panel-orange-border',
      },
      'likely-not': {
        text: '--pay-panel-red-text',
        bg: '--pay-panel-red-bg',
        border: '--pay-panel-red-border',
      },
    },
    /** The 可能性 meter's lit bar (1–3, left to right) per likelihood (mockup §2). Display only. */
    LIKELIHOOD_BARS: { 'likely-first': 1, 'needs-check': 2, 'likely-not': 3 },
    /** 首堂課 檢查 check icons (Material): ✓ ✕ ! – of mockup §2. */
    CHECK_ICONS: {
      ok: 'check',
      no: 'close',
      warn: 'priority_high',
      none: 'remove',
    },
    /**
     * z-index of the 家庭帳號 panel (FamilyNotesSheet, both hosts): above the 簽到作業
     * class window (.custom-modal-overlay 999) and its sticky table header (D-J).
     */
    FAMILY_PANEL_Z_INDEX: 2600,
    /**
     * 簽到作業 (router/routes.ts) — a 待建立 row's class time line opens this class's day on it
     * in a new tab (B1): `${path}?date=YYYY/MM/DD&classId=<id>`, the 時段頁's format
     * (TimeSlotDetailView.vue linkTo). Built by pendingFirstTrialRowView.pendingSheetLink.
     */
    SIGN_IN_SHEET_PATH: '/admin/schedule',
    /**
     * The confirm box's 建議 tag (mockup §3): only on green or red, never on orange.
     * Maps a likelihood to the verdict action the tag sits on.
     */
    LIKELIHOOD_SUGGESTED_ACTION: {
      'likely-first': 'set-first',
      'likely-not': 'set-not-first',
    } as Partial<
      Record<'likely-first' | 'needs-check' | 'likely-not', 'set-first' | 'set-not-first'>
    >,
    /**
     * 學生紀錄 ticket pill colour on a 約課時段 card (a seat from today on — PO 2026-10-16
     * 「pill in color, same as the old design」, the D-D / mockup §5 colours): the first
     * keyword the label contains wins; anything else (補課, 任選 …) is grey. A 已完成課程
     * card's pills are all light grey instead (past — PO 2026-10-16 「已完成 all grey」).
     */
    TICKET_PILL_TONES: [
      { keyword: '體驗', tone: 'trial' },
      { keyword: '學期', tone: 'term' },
    ] as ReadonlyArray<{ keyword: string; tone: 'trial' | 'term' }>,
    /**
     * D-T (PO 2026-10-13): the mark on 首堂已上 — the first lesson's 回饋表 in icon AND
     * colour (readable without colour). Words = UI_STRINGS.FIRST_TRIAL.CHECK.FAMILY
     * FEEDBACK_DONE / _PENDING / _NONE (D-N).
     */
    FORM_MARKS: {
      done: { icon: 'check_circle', colorVar: '--pay-color-success' },
      pending: { icon: 'pending', colorVar: '--pay-panel-orange-text' },
      none: { icon: 'error', colorVar: '--pay-color-danger' },
    },
    /**
     * D-V (PO 2026-10-13): on a confirmed 首堂課 row with no form yet, the 回饋表 icon
     * sits white on this solid circle (white on #d46b08 ≈ 3.6:1, above the 3:1 for icons).
     */
    ICON_EMPHASIS_BG_VAR: '--pay-color-warning-text',
    STAGE_COLOR_VARS: {
      none: '--pay-panel-blue-text',
      queued: '--pay-panel-orange-text',
      created: '--pay-panel-blue-text',
      approved: '--pay-panel-indigo-text',
      completed: '--pay-panel-teal-text',
      'line-failed': '--pay-color-danger',
    },
    STAGE_LABELS: {
      none: '建立體驗回饋表',
      queued: '準備清單中',
      created: '已建表',
      approved: '已審核',
      completed: '課務已標記完成',
      'line-failed': 'LINE 傳送失敗',
    },
    SOURCE_LABELS: {
      system: '系統判定',
      'kiosk-parent': '家長 iPad 確認',
      admin: '課務',
      teacher: '老師',
      legacy: '舊資料',
    },
    REASON_LABELS: {
      reissue: '補發',
      second: '第二次回饋',
      other: '其他',
      'first-class': '首次上課',
    },
    PARENT_ANSWER_LABELS: {
      yes: '是，第一次上課',
      no: '不是第一次',
    },
    /**
     * 體驗生 column ticket tags (spec §5 D2): trial tickets say 三堂體驗／單堂體驗／體驗生;
     * non-trial 單堂／三堂 tickets (N23–N25, 首體驗過) say only 單堂／三堂.
     */
    TICKET_TAGS: {
      THREE_KEYWORD: '三堂',
      SINGLE_KEYWORD: '單堂',
      TRIAL_THREE: '三堂體驗',
      TRIAL_SINGLE: '單堂體驗',
      TRIAL_OTHER: '體驗生',
      PLAIN_THREE: '三堂',
      PLAIN_SINGLE: '單堂',
      ELECTIVE: '任選',
    },
    /** feedbackData.lineStatus values the page reads (mirror of backend FEEDBACK_LINE_STATUS). */
    /** Mirror of backend FIRST_TRIAL_LIMITS.LINE_PENDING_STALE_MS: a Pending send older than this shows as failed. */
    LINE_PENDING_STALE_MS: 2 * 60 * 1000,
    LINE_STATUS: {
      PENDING: 'Pending',
      SENT: 'Sent',
      FAILED: 'Failed',
      NOT_SENT: 'NotSent',
      /** 課務 copied the link and told the teacher themselves (PO 2026-09-16). */
      MANUAL_NOTIFIED: 'ManualNotified',
    },
    /**
     * D8 (PO 2026-09-14 round 9): teacher pages show only these pill kinds, read-only.
     * 'review' = 首堂待確認 with no › and no ? badge, not clickable (D-G c, 2026-10-12).
     */
    TEACHER_VISIBLE_PILLS: ['pre-mark', 'confirmed', 'trialled', 'review'] as const,
    /** Mirror of backend FIRST_TRIAL_LIMITS.MARKERS_MAX_CLASSES: classes per feedback-getFirstTrialClassMarkers call. */
    MARKERS_MAX_CLASSES: 120,
    /** classes.adminApprovalHistory action of a pill verdict (backend FIRST_TRIAL_HISTORY.VERDICT_ACTION). */
    HISTORY_ACTION: 'first-trial-verdict',
  },

  APPROVAL_STATUS: {
    DISPLAY_NAMES: {
      APPROVED: '已核對',
      UNAPPROVED: '未核對',
      FULLY_APPROVED: '完全核對',
      PARTIALLY_APPROVED: '部分核對',
    },
  },

  SYNC_STATUS: {
    MANUAL_UPDATE: '手動更新',
  },

  /**
   * @description Controls the time window and behavior for real-time attendance updates on the sign-in page.
   */
  REAL_TIME_SYNC: {
    /** Time in minutes before a class starts to enable real-time updates. */
    BEFORE_CLASS_MINUTES: 20,
    /** Time in minutes after a class starts to disable real-time updates. */
    AFTER_CLASS_MINUTES: 5,
    /** Frequency in milliseconds to check for updates within the active time window. */
    REFRESH_INTERVAL_MS: 30000,
    MAX_RETRY_ATTEMPTS: 3,
    RETRY_DELAY_MS: 1000,
  },

  /**
   * @description Configures the display logic and behavior of the iPad Kiosk page.
   */
  KIOSK_PAGE: {
    /** How long a class stays on the Kiosk after it starts. (e.g., A 10:00 class disappears at 10:20 if set to 20). */
    CLASS_VISIBLE_AFTER_START_MINUTES: 80,
    /** Switches focus to the next class if the current one has been running for this long. (e.g., At 10:25, switches from 10:00 class to 11:00 class if set to 25). */
    PREFER_NEXT_CLASS_AFTER_ONGOING_MINUTES: 6,
    /** Auto-focuses on a class if it starts within this window. (e.g., At 9:45, focuses on a 10:00 class if set to 15). */
    UPCOMING_CLASS_FOCUS_WINDOW_MINUTES: 20,
    /** Shows 'Starting Soon' countdown on the footer button when a class is this many minutes away. */
    SMART_BUTTON_UPCOMING_WINDOW_MINUTES: 30,
    /** How long the Kiosk can be idle before the screensaver appears. (e.g., 60000 ms = 1 minute). */
    SCREENSAVER_INACTIVITY_MS: 2 * 60 * 1000,
    /** Crossfade duration between rotating screensaver slides (ms). */
    SCREENSAVER_TRANSITION_MS: 500,
    /**
     * Default screensaver rotation list. Each slide has its own duration; supervisor
     * overrides (Phase 2) layer on top via Firestore — this array is the always-on fallback.
     */
    SCREENSAVER_SLIDES: [
      {
        id: 'misconceptions',
        enabledInScreensaver: true,
        enabledInGallery: true,
        order: 1,
        durationMs: 7000,
      },
      {
        id: 'rules',
        enabledInScreensaver: true,
        enabledInGallery: true,
        order: 2,
        durationMs: 10000,
      },
      {
        id: 'signin-zh',
        enabledInScreensaver: true,
        enabledInGallery: false,
        order: 3,
        durationMs: 5000,
      },
      {
        id: 'signin-en',
        enabledInScreensaver: true,
        enabledInGallery: false,
        order: 4,
        durationMs: 5000,
      },
    ] as const,
    /** Master toggle for the top-right gallery button on the kiosk page.
     *  Supervisors can disable the button entirely (kiosk reverts to the
     *  classic class-list + idle-triggered screensaver behaviour). */
    GALLERY_BUTTON_ENABLED: true,
    /** Screensaver polling interval during class hours (ms). Polls loadDailyData to keep class list fresh. */
    SCREENSAVER_POLL_INTERVAL_MS: 2 * 60 * 1000,
    /** How many minutes before the first class to start screensaver polling. */
    SCREENSAVER_POLL_BUFFER_BEFORE_MINUTES: 60,
    /** How many minutes after the last class ends to stop screensaver polling. */
    SCREENSAVER_POLL_BUFFER_AFTER_MINUTES: 60,
    /** Configuration for student photo uploads from the Kiosk. */
    PHOTO_UPLOAD: {
      /** Maximum file size in bytes for uploaded photos after compression. */
      MAX_SIZE_BYTES: 500 * 1024,
      /** The maximum dimension (width or height) of the image in pixels after resizing. */
      MAX_DIMENSION_PX: 800,
    },
  },

  /**
   * Kiosk wall-iPad 打卡 station (/kiosk/clock).
   * Timings for the grid → PIN → preview → success state machine.
   */
  KIOSK_CLOCK: {
    /** PIN length — mirrors the payslip PIN (the same PIN is reused). */
    PIN_LENGTH: 4,
    /** Any sub-screen (PIN / preview / success) returns to the grid after this idle window. */
    IDLE_RESET_MS: 30 * 1000,
    /** Success screen auto-returns to the grid after this delay (a 完成 button also returns). */
    SUCCESS_AUTO_RETURN_MS: 5 * 1000,
    /** Consecutive wrong-PIN attempts before bouncing back to the grid (log-only; no lockout). */
    MAX_PIN_ATTEMPTS: 3,
  },

  /**
   * @description Sets the delay after a class starts before it can be approved.
   * @details The current value of 1,205,000 ms is approximately 20.1 minutes.
   */
  APPROVAL: {
    DELAY_MS: 1205000,
    NOTE_MAX_LENGTH: 200,
    /**
     * 主管代簽 (PO 2026-10-14, tasks/todo-teacher-signoff.md D2/D3/D6): signing
     * a class's teacher sign-off for the teacher named on it — any teacher, with
     * or without an account. ONLY these roles; 課務 (admin) may not. A short
     * reason is required (a batch takes one reason for all its classes). LABEL
     * follows the signer's name wherever the signer is shown. Mirrored from the
     * backend (check-config-mirror.cjs).
     */
    PROXY_SIGN: {
      ROLES: ['supervisor'],
      REASON_MAX_LENGTH: 100,
      LABEL: '主管代簽',
    },
    /**
     * batchSaveClassStatus: most class ids the CF accepts in ONE call — mirrors
     * functions/src/config/appConfig.ts APPROVAL.BATCH_MAX_CLASS_IDS (guarded by
     * check-config-mirror.cjs). The 待審核 list spans three months, so 全選 can
     * exceed this; the page SPLITS the selection into chunks of this size and
     * sends them one after another (N+1 review R2, 2026-09-20). A drift means the
     * CF rejects a chunk the page just sent → zero classes approved.
     */
    BATCH_MAX_CLASS_IDS: 50,
    /**
     * Display labels for batchSaveClassStatus per-class results (skip-and-report).
     * Keys mirror functions/src/config/appConfig.ts APPROVAL.BATCH_RESULT values.
     * Terminology per PO 2026-07-13: 新增說明 (not 補註).
     */
    BATCH_RESULT_LABELS: {
      STATUSES: {
        approved: '已核對',
        'note-added': '已新增說明',
        skipped: '略過',
      },
      SKIP_REASONS: {
        'supervisor-sign-off': '主管已簽核',
        'already-approved': '已由他人核對',
        'not-found': '課程不存在',
        'not-yet-allowed': '時間未到',
        ghost: '代課保護',
        error: '錯誤',
      },
    },
  },

  /**
   * 助教紀錄 (tasks/todo-substitute-pay.md): at 老師核對 the teacher picks who the
   * 「代課」 was when the class's teacher list holds a placeholder name
   * (SYSTEM_CONSTANTS.COOP_CLASSES.HELPERS.classHasPlaceholderTeacher); 簽到作業
   * shows `${LINE_PREFIX}<暱稱>` or `${LINE_PREFIX}${UNRECORDED}`. KINDS / VIA
   * mirror BUSINESS_RULES.SUBSTITUTE_RECORD in functions/src/config/appConfig.ts
   * (check-config-mirror.cjs). Pay and counts never read these.
   */
  SUBSTITUTE_RECORD: {
    LABEL: '代課',
    LINE_PREFIX: '代課：',
    PICK_PLACEHOLDER: '請選擇',
    UNRECORDED: '未記錄',
    NOT_ON_LIST_HINT: '找不到人？請主管新增臨時助教',
    /** The grey last line instead of NOT_ON_LIST_HINT when the list could not be read. */
    LOAD_FAILED_HINT: '代課名單載入失敗，請重新開啟',
    /** 簽到作業 › 編輯課程老師: what a save resets (P4-F1, 9b). */
    TEACHER_EDIT_NOTE:
      '儲存後，老師核對會重設，需要重新核對 (老師有代課時要重新選擇代課；只有代課時由主管簽核選擇，已簽核的保留)。課務核對保留不變。',
    CHANGE: '更改',
    CHANGE_TITLE: '更改代課',
    /** 更改代課 window footer (ChangeSubstituteModal). */
    CANCEL: '取消',
    CONFIRM: '確認',
    /** The pick list's two group headers, by kind — 員工 first, then 臨時助教 (mockup § 1). */
    GROUP_LABELS: {
      employee: '員工',
      tempAssistant: '臨時助教',
    },
    REQUIRED_LABEL: '代課 (必填):',
    /** Batch sign-off: how many selected classes need their own 代課 pick. */
    BATCH_EXCLUDED: (n: number) => `${n} 堂有代課，請逐堂核對`,
    KINDS: {
      EMPLOYEE: 'employee',
      TEMP_ASSISTANT: 'tempAssistant',
    },
    VIA: {
      TEACHER_SIGN: 'teacher-sign',
      COOP_SIGN: 'coop-sign',
      PROXY_SIGN: 'proxy-sign',
      SUPERVISOR_SIGN: 'supervisor-sign',
      SUPERVISOR_CHANGE: 'supervisor-change',
      TEACHER_CHANGE: 'teacher-change',
    },
  },

  DATE_TIME: {
    TIMEZONE: 'Asia/Taipei',
    DATE_FORMAT: 'YYYY/MM/DD',
    TIME_FORMAT: 'HH:mm',
    DATETIME_FORMAT: 'YYYY/MM/DD HH:mm',
    WEEKDAY_NAMES: ['週日', '週一', '週二', '週三', '週四', '週五', '週六'],
  },

  /**
   * @description Timeslot page student filter configuration
   */
  TIMESLOT_STUDENT_FILTER: {
    /** Number of months without booking to consider a student "archived" for a timeslot */
    ARCHIVED_THRESHOLD_MONTHS: 2,
  },

  /**
   * @description Timeslot page pagination configuration
   */
  TIMESLOT_PAGINATION: {
    /** Maximum number of "Load Earlier" clicks allowed (each click loads ~4 weeks) */
    MAX_LOAD_PREV_CLICKS: 2,
    /** Label shown when max previous loads reached (M = months, since 2 clicks ≈ 2 months) */
    MAX_LOAD_PREV_LABEL: '最多2M舊資料',
  },

  /**
   * @description Configuration for the timeslot peek drawer (read-only preview).
   */
  TIMESLOT_PEEK: {
    /** Maximum number of simultaneously pinned timeslot tabs in the peek drawer. */
    MAX_TABS: 5,
    /**
     * Pill labels must stay compact so 5 tabs fit across the bottom strip.
     * A class shortname (FrontendConfigUtils.classShortName) is used when it's
     * this short or shorter (known shortforms are ≤6, e.g. `FMS親子班`); when a
     * class has no recognizable shortform classShortName returns the full long
     * name, so we fall back to its first N characters + ellipsis.
     */
    LABEL_SHORTNAME_MAXLEN: 7,
    LABEL_FALLBACK_CHARS: 5,
  },

  /**
   * @description Configuration for the "Wipe Risk Lock" logic.
   * Prevents data loss by locking features when there is a risk of Excel upload overwriting data.
   */
  WIPE_RISK_LOCK: {
    // Admin Policy: Lock only future days. Unlock today.
    ADMIN_LOCK_IF_BEFORE_CLASS_DAY: true,

    // Teacher Policy: Lock if > 2 hours before class start.
    TEACHER_LOCK_WINDOW_MINUTES: 120,
  },

  /**
   * @description Configuration for the Program Waitlist (候補名單) and GSheets (其他待辦) features.
   */
  /**
   * @description Configuration for the topbar 「快速連結」 launcher (Google Sheets bookmarks).
   */
  QUICK_LINKS: {
    /** Doc ID under `system_settings` collection that holds the launcher config */
    SETTINGS_DOC_ID: 'gsheets',
    /** Default category name used when an existing sheet has no category assigned */
    DEFAULT_CATEGORY: '未分類',
    /** Required URL prefix for sheet links */
    URL_PREFIX: 'https://docs.google.com/spreadsheets/d/',
  },

  /**
   * Jotform links of a 體驗回饋表 (the 體驗回饋 page and the 課務總覽 › 體驗回饋 row): 重新編輯 opens
   * `EDIT_BASE + submissionId`; 主管審核 opens `INBOX_BASE + formId/submissionId` (formId from
   * the prefill URL, utils/feedbackActionsView jotformFormIdOf).
   */
  JOTFORM_LINKS: {
    EDIT_BASE: 'https://www.jotform.com/edit/',
    INBOX_BASE: 'https://www.jotform.com/inbox/',
  },

  PROGRAM_WAITLIST: {
    /** Seconds before a completion action becomes permanent (undo window) */
    COMPLETION_COUNTDOWN_SECONDS: 5,
    /** Class name prefixes that are eligible for the waitlist feature */
    SUPPORTED_PREFIXES: ['親子游泳', '獨立游泳', 'FMS體能課'] as const,
    /** Custom display order for class groups per prefix. Unknown groups appear at end, sorted alphabetically. */
    GROUP_ORDER: {
      親子游泳: ['小水狸班', '小鴨班', '小青蛙班', '小企鵝班'],
      獨立游泳: [
        '小海象班',
        '小海象班*協同',
        '小海象一班',
        '小海象一班*協同',
        '小海象二班',
        '小海象二班*協同',
        '小熊班',
        '小熊班*協同',
      ],
      FMS體能課: ['FMS親子班', 'FMS獨立一班', 'FMS獨立二班', 'FMS獨立三班'],
    } as Record<string, string[]>,
    /**
     * Single representative emoji per class shortform — rendered as a small
     * prefix in the browse panel for at-a-glance identification.
     * Emojis are plain Unicode (no library/font required) — rendered by the
     * platform's native emoji font (Apple Color Emoji on macOS/iOS, Segoe UI
     * Emoji on Windows, Noto Color Emoji on Android/Linux). Zero load cost.
     * FMS體能課 classes don't have animal mascots — they use a generic 💪
     * fitness icon to keep the visual treatment consistent.
     */
    SHORTFORM_EMOJI: {
      小水狸班: '🦫',
      小鴨班: '🐤', // yellow baby-chick (no yellow-duck emoji exists in standard Unicode; chick reads as "small yellow waterfowl")
      小青蛙班: '🐸',
      小企鵝班: '🐧',
      小海象班: '🦭', // closest available — no walrus emoji in standard Unicode
      '小海象班*協同': '🦭', // 2026-07-10 deep-review: base co-op variants were missing (fell back to no emoji)
      小海象一班: '🦭',
      '小海象一班*協同': '🦭',
      小海象二班: '🦭',
      '小海象二班*協同': '🦭',
      小熊班: '🐻‍❄️', // polar bear (ZWJ sequence — Bear + Snowflake)
      '小熊班*協同': '🐻‍❄️',
      FMS親子班: '👶', // baby face — communicates the parent-child program at a glance
      FMS獨立一班: '💪',
      FMS獨立二班: '💪',
      FMS獨立三班: '💪',
    } as Record<string, string>,
    /**
     * Origin of a waitlist entry.
     * `admin` — created by admin/supervisor for an inquiry from a potential customer (default for legacy entries).
     * `teacher` — created by a teacher mid-class to suggest a current student should move to a different timeslot.
     */
    SOURCE: { ADMIN: 'admin', TEACHER: 'teacher', ADMIN_TRANSFER: 'admin-transfer' } as const,
    /** Forced `type` value when a teacher creates a reservation. */
    TEACHER_DEFAULT_TYPE: '正式課程',
    /**
     * Fields a teacher is allowed to modify on their own un-completed reservation.
     * Intentionally restricted to `notes` only — target timeslot and type are
     * locked at creation. To change either, ask admin to delete and recreate.
     */
    TEACHER_EDITABLE_FIELDS: ['notes'] as const,
    /**
     * 重排 reasons (PO 2026-09-29) — the optional reason when staff put a row at the end of
     * its line (M3's 重排 radios; OTHER needs a written 說明). MIRROR of the backend
     * BUSINESS_RULES.PROGRAM_WAITLIST.REQUEUE_REASONS (requeueProgramWaitlistEntry accepts
     * only these) — pinned by functions/scripts/check-config-mirror.cjs.
     */
    REQUEUE_REASONS: {
      NO_REPLY: '未回覆',
      DECLINED_OFFERS: '多次婉拒推薦的日期',
      OTHER: '其他',
    } as const,
    /**
     * 重排's 其他 說明: at most this many characters (the field's maxlength + counter). MIRROR
     * of the backend BUSINESS_RULES.PROGRAM_WAITLIST.REQUEUE_NOTE_MAX_LENGTH (it refuses a
     * longer one) — pinned by functions/scripts/check-config-mirror.cjs.
     */
    REQUEUE_NOTE_MAX_LENGTH: 500,
    /**
     * 轉為候補 reasons (M7, PO 2026-09-30, design § 9B-1) — the optional reason the 轉為候補
     * window records on the closed 轉班 record and its new 候補 (the 候補's 備註 first line,
     * its 轉班轉入 hover, 學生紀錄's 轉為候補 card); OTHER needs a written 說明. The window adds
     * 「(付款、時間等)」 after NOT_READY as display text only. MIRROR of the backend
     * BUSINESS_RULES.PROGRAM_WAITLIST.CONVERT_REASONS — pinned by
     * functions/scripts/check-config-mirror.cjs.
     */
    CONVERT_REASONS: {
      FULL: '新班額滿',
      NOT_READY: '家長暫時無法開始',
      OTHER_SLOT: '想等其他時段',
      OTHER: '其他',
    } as const,
    /**
     * 轉為候補's 其他 說明: at most this many characters (the field's maxlength + counter).
     * MIRROR of the backend BUSINESS_RULES.PROGRAM_WAITLIST.CONVERT_REASON_NOTE_MAX_LENGTH (it
     * refuses a longer one) — pinned by functions/scripts/check-config-mirror.cjs.
     */
    CONVERT_REASON_NOTE_MAX_LENGTH: 200,
    /**
     * PO 2026-10-02 (Part 3, Q1 = A): 轉為候補 while the family still holds a seat asks
     * 「目前時段」 — keep (default) or 不續報. MIRROR of the backend
     * BUSINESS_RULES.PROGRAM_WAITLIST.CURRENT_SEAT — pinned by
     * functions/scripts/check-config-mirror.cjs.
     */
    CURRENT_SEAT: {
      KEEP: 'keep',
      NOT_RENEWING: 'notRenewing',
    } as const,
    /**
     * Display labels for waitlist entry type values.
     * Keys = stored enum values; values = UI display strings shown to admin.
     * Wave 4 builds the 類型 select labels from this map.
     * Stored values in Firestore are never changed by this config.
     */
    TYPE_DISPLAY_LABELS: {
      // Naming, settled by the PO 2026-09-21: these five are DISTINCT products,
      // not synonyms. 體驗一堂 is a trial; 單堂 is a single paid class — the two
      // are unrelated and must never be collapsed into one label.
      // (An earlier note here claimed 體驗一堂 collided with the SIGNIN
      //  TRIAL_SINGLE / TRIAL_THREE tags 單堂體驗 / 三堂體驗. It does not: those
      //  are kiosk sign-in tags on a different surface. No alignment is wanted.)
      體驗: '體驗一堂',
      體驗三堂: '體驗三堂',
      // 單堂/任選 (PO 2026-09-21). ONE stored type, two ways to pay for it —
      // 任選 6 堂 is the 單堂 ticket bundle, not a separate kind of class
      // (命名鐵律, see OFFER_SINGLE_BUNDLE_PLAN_IDS). Naming the option this way
      // is what tells the 課務 that a family who wants the bundle belongs HERE
      // and not under 正課. Display only — the stored value is still `單堂`.
      單堂: '單堂/任選',
      單堂補課: '補課',
      正式課程: '正課',
    } as const,
    /**
     * 候補 offer rule (PO 2026-09-20) — what the 課務 may offer a waiting family
     * RIGHT NOW, per stored `type`. `正式課程` has no entry: it keeps the
     * reservation-aggregate line (offer only while 保留名額 is not full).
     *
     *   windowWeeks — how far ahead we are willing to offer. Beyond it the seat
     *                 is too far away to be a useful offer.
     *   offerCount  — how many dates go out at once. PO: give the earliest few,
     *                 not everything, so the list moves forward one week at a
     *                 time as the 課務 works it.
     *   chained     — 體驗三堂 (小水狸班／小鴨班, 0–18m) books THREE classes as one
     *                 course, so the dates must form a run: consecutive weeks
     *                 preferred, at most ONE skipped week between them
     *                 (maxGapWeeks = 2 ⇒ A, C, E is legal; A, D is not).
     *                 Unchained types offer independent single classes.
     *
     * MIRROR: functions appConfig BUSINESS_RULES.PROGRAM_WAITLIST.VALID_TYPES
     * must list the same stored values (the CF validates against that set).
     */
    OFFER_RULES: {
      體驗: { windowWeeks: 8, offerCount: 2, chained: false, maxGapWeeks: 1 },
      體驗三堂: { windowWeeks: 10, offerCount: 3, chained: true, maxGapWeeks: 2 },
      單堂: { windowWeeks: 8, offerCount: 2, chained: false, maxGapWeeks: 1 },
      單堂補課: { windowWeeks: 8, offerCount: 2, chained: false, maxGapWeeks: 1 },
    } as const,
    /**
     * The 0–18m classes, whose trial is a THREE-class course
     * (報名流程「為什麼 0–18m 要上三堂」: 「0-18m寶貝的體驗班都是規劃三堂課」).
     *
     * PO 2026-09-21, exactly these two:
     *   - 體驗三堂 may ONLY be used with these classes — no other class offers it.
     *   - These classes DEFAULT to 體驗三堂 for a new trial entry.
     *   - 體驗一堂 stays allowed for them as a deliberate edge case, so the rule
     *     is NOT derived at offer time: the stored type stays the truth.
     *
     * Matched against a stored programGroup the same way WaitlistEditModal's
     * resolveCanonicalClass does (equal, or contains the bare name), because the
     * stored value may be 「小鴨班」, 「小鴨班 (平日)」 or the full class name
     * 「親子游泳【8-18m/爬走寶寶】小鴨班」. Read through
     * FrontendConfigUtils.isThreeTrialGroup(), never inline.
     */
    THREE_TRIAL_GROUPS: ['小水狸班', '小鴨班'] as const,
    /**
     * Deadline the 課務 gives a family to finish paying once they accept an
     * offer (PO 2026-09-20: 「complete before 5pm ... first come first serve」).
     * ONE source — both the parent message and the 使用說明 interpolate it.
     */
    OFFER_PAYMENT_DEADLINE: '17:00',
    /**
     * 推薦訊息 → which 付款連結 to PRE-SELECT, by (programPrefix, stored type).
     *
     * The values are `shortLinks` slugs — the same codes 付款連結管理 manages and
     * TICKET_LINK_OPTIONS lists for the 報名訊息產生器. One vocabulary, one place
     * to change a BookFast URL.
     *
     * ⚠ This is a PRE-SELECTION, never a silent decision: the 課務 sees the
     * chosen link's name in the modal and can change it before copying. A type
     * with no entry here simply starts unselected — we would rather ask than
     * send a family the wrong product's payment page.
     *
     * ⚠ THE 類型 ALONE DECIDES THE TICKET (PO 2026-09-29). 體驗一堂 means 首次體驗課
     * and 體驗三堂 means 首次三堂體驗 — the 課務 picks the type when the family is
     * added, and that already fixes the price: a trial type always gets the
     * 首次優惠 ticket; 單堂/任選 is the regular single-class ticket (or the 任選
     * bundle, picked in the modal). The former 未體驗/已體驗 pair (review
     * 2026-09-21, H2 — a returning family on a trial type got the 原價 ticket) is
     * retired with the 已體驗 toggle: a family who has trialed before is entered
     * as 單堂/任選, not as a trial. `bs3-single` (三堂 原價) is no type's default;
     * it stays in the picker under OFFER_LINK_EXTRA_TERMS.
     *
     * Deliberately absent:
     *   - 正式課程 — semester fees are paid by BANK TRANSFER (BANK_BLOCK), not a
     *     ticket link; the 報名訊息產生器 has no link for them either.
     *   - 單堂補課 — a makeup is drawn from a ticket the family already bought,
     *     so there is nothing new to pay for.
     * FMS splits 親子/獨立 by the group name, not the prefix, so it is resolved
     * by FrontendConfigUtils.waitlistOfferLinkSlug() rather than listed here.
     */
    OFFER_PAYMENT_LINK_SLUGS: {
      親子游泳: {
        體驗: 'bs-trial-promo',
        體驗三堂: 'bs3-trial-promo',
        單堂: 'bs-single',
      },
      獨立游泳: {
        體驗: 'ks-trial-promo',
        單堂: 'ks-single',
      },
    } as Record<string, Record<string, string>>,
    /**
     * Tickets in TICKET_LINK_OPTIONS that no 類型 pre-selects, named in this tab's
     * wording for the 付款連結 picker (it must never show a raw slug — see
     * FrontendConfigUtils.waitlistOfferLinkTerms). `bs3-single` is the 三堂 ticket
     * at the regular price ($4,950; 體驗三堂 is the 首次 one, $4,725).
     */
    OFFER_LINK_EXTRA_TERMS: { 'bs3-single': '親子游泳 三堂 (原價)' } as Record<string, string>,
    /**
     * FMS has one prefix but two products; the group name carries the split.
     * 體能 has ONE price — 體驗單堂 is a flat $850 (PO 2026-09-21).
     */
    OFFER_PAYMENT_LINK_FMS: { 親子: 'fms-tot-trial', 獨立: 'fms-kid-trial' } as Record<
      string,
      string
    >,
    /**
     * 正課 → which 學期方案 to PRE-SELECT, so the message can state the amount.
     *
     * Values are SIGNUP_PLANS ids (the 報名流程 price list, overridable by a
     * supervisor in 價目表設定) — NOT prices. Keeping the id here and reading the
     * price through useSignupPricing means a price change lands in one place.
     *
     * Swimming splits on weekday vs weekend (the 平日 term is cheaper); FMS
     * splits 親子/獨立 on the group name. As with the ticket link this is only a
     * pre-selection — the 課務 sees the plan and its price and can change it
     * (任選 6 堂 and the co-op cases are not inferable from the slot alone).
     * The map itself is the shared TERM_PLAN_IDS (續報 M2 T18a).
     */
    OFFER_TERM_PLAN_IDS: TERM_PLAN_IDS,
    /**
     * 任選 6 堂 — the 單堂 ticket BUNDLE. Belongs to 單堂, never to 正課.
     *
     * ⚠ 命名鐵律 (signupSteps.ts 任01 `SINGLE_CLASS_RULES`, re-confirmed by the
     * PO 2026-09-21): 「任選 6 堂」是單堂課的**票券包（付款方式）**，不是第三種
     * 上課方式。口徑是「單堂課，可以買 6 堂票券包比較方便」，不說「任選方案」.
     *
     * So it is a discount bundle of 單堂 ($9,750 for 6 = $1,625/堂 against the
     * $1,650 單堂 原價), and it must NOT appear in the 正課 學期方案 picker —
     * an earlier revision of this file listed it there, which read as a third
     * kind of enrolment and contradicted the rule above.
     *
     * Not yet wired into the 單堂 payment picker: unlike every 單堂 ticket, the
     * bundle has **no BookFast link** (PO 2026-08-12 — 學期課與任選 6 堂 走匯款),
     * so choosing it has to switch the message from a payment link to 金額 ＋
     * 匯款帳號. Pending the PO's ruling on how that message should read.
     */
    OFFER_SINGLE_BUNDLE_PLAN_IDS: {
      SWIM_ANY6: 'sw-any6',
      FMS_ANY6: 'fm-any6',
    } as const,
    /** Weekday prefixes that count as 週末 for term pricing. */
    OFFER_TERM_WEEKEND_PREFIXES: ['週六', '週日'] as const,
    /**
     * Where the 付款連結 picker must NOT borrow `TYPE_DISPLAY_LABELS`.
     *
     * The two maps answer different questions. TYPE_DISPLAY_LABELS answers
     * 「這位候補是哪一種」 — and for 單堂 that is 單堂/任選, because one stored type
     * covers both ways of paying. This map answers 「這張票券賣的是什麼」, and
     * `bs-single`/`ks-single` sell exactly ONE class ($1,650) — the 任選 6 堂
     * bundle is a different product with no ticket at all (走匯款). Labelling the
     * single-class ticket 單堂/任選 would suggest buying it covers the bundle.
     */
    OFFER_LINK_TERM_LABEL_OVERRIDES: { 單堂: '單堂' } as Record<string, string>,
    // ─── Type-aware per-week availability (候補 tab) ─────────────────────────
    /**
     * Stored `type` enum values (NOT display labels) that use the per-week
     * availability treatment — a class line shows upcoming available-week chips
     * instead of the reservation-aggregate line. `正式課程` (正課) is intentionally
     * excluded: it keeps the reservation-aggregate line. Single source of truth
     * for the branch — see FrontendConfigUtils.usesPerWeekAvailability().
     */
    PERWEEK_AVAILABILITY_TYPES: ['體驗', '體驗三堂', '單堂', '單堂補課'] as const,
    /** Max available-week date chips shown on the chip FACE per class line —
     *  remaining open dates are enumerated inside the +N hover.
     *  PO 2026-07-07: earliest date only + +N; the group must stay on ONE line
     *  with the class tag, never wrap below it.
     *
     *  ⚠ 2026-09-20: the 候補 tab no longer uses this — it shows exactly the
     *  dates OFFER_RULES says it may offer (2 or 3), with no +N and no hover.
     *  This value now serves the 報名訊息產生器 (SIGNUP_COMPOSER) only, which
     *  kept the original chip-face behaviour. */
    MAX_AVAILABLE_WEEKS_SHOWN: 1,
    /**
     * DEFAULT near-horizon window (weeks from today) to scan for available weeks.
     *
     * ⚠ 2026-09-20: the 候補 tab passes its own window per type instead
     * (OFFER_RULES.windowWeeks — 8 weeks for single classes, 10 for 體驗三堂),
     * so this default now governs the 報名訊息產生器 path and anything else that
     * does not pass a window. Deliberate divergence: the offer horizon is a
     * 候補 business rule, while the composer is a separate 招生 surface whose
     * behaviour the PO did not ask to change.
     */
    PERWEEK_AVAILABILITY_WINDOW_WEEKS: 6,
    /**
     * Fetch lookahead (weeks from today) for the per-week batch CF — wider than
     * the near window so a row whose near window is full can still show
     * 「{N}週內無空位・最早可約 MM/DD」 (PO 2026-07-13: the bare 近期無空位 hid a
     * real October seat). DISPLAY-ONLY beyond the near window: chips, +N and
     * placeability stay bounded by PERWEEK_AVAILABILITY_WINDOW_WEEKS. ~6 months
     * ≈ the BF/schedule upload horizon (addMonths(today, 6)).
     */
    PERWEEK_LOOKAHEAD_WEEKS: 26,
    // ─── 候補 保留名額 (PO 2026-09-30, main repo tasks/todo-waitlist-placement-hold.md) ───
    /**
     * Which kind of hold a 候補 row's stored `type` makes (PO C-3): 正式課程 → 'term'
     * (the class's header 保留名額 N); every per-week type → 'dates' (only the held
     * weeks' 總計). A type outside this table cannot be held (the server refuses it).
     * MIRROR of functions BUSINESS_RULES.PROGRAM_WAITLIST.PLACEMENT_KIND_BY_TYPE.
     */
    PLACEMENT_KIND_BY_TYPE: {
      正式課程: 'term',
      體驗: 'dates',
      體驗三堂: 'dates',
      單堂: 'dates',
      單堂補課: 'dates',
    } as const,
    /** `placement.status` values. MIRROR of functions PLACEMENT_STATUS. */
    PLACEMENT_STATUS: { HOLDING: 'holding', ENDED: 'ended' } as const,
    /**
     * `placement.endedReason` values (arranged = 已安排, cancelled = 取消保留, given-up = 放棄,
     * expired = 保留已到期 後 03:30 自動取消 — audit item 6).
     * MIRROR of functions PLACEMENT_ENDED_REASONS (same source shape — the mirror check compares
     * the text; deep review W15).
     */
    PLACEMENT_ENDED_REASONS: {
      ARRANGED: 'arranged',
      CANCELLED: 'cancelled',
      GIVEN_UP: 'given-up',
      EXPIRED: 'expired',
    } as const,
    /**
     * Audit item 6 (PO 2026-10-06): 保留已到期 → the 03:30 run cancels the hold on this Taipei
     * calendar day after its 保留至 and the row is a plain 候補 again (the 保留已到期 hint names
     * that day). MIRROR of functions PLACEMENT_EXPIRED_RELEASE_DAYS.
     */
    PLACEMENT_EXPIRED_RELEASE_DAYS: 14,
    /**
     * `outcome` values admin-endProgramWaitlistPlacement accepts. MIRROR of functions
     * PLACEMENT_END_OUTCOMES (useWaitlistPlacements/actions END_OUTCOME names them).
     */
    PLACEMENT_END_OUTCOMES: ['arranged', 'cancelled'] as const,
    /**
     * The 保留名額 window lists a class's weeks up to this many weeks ahead
     * (PO 2026-09-30: 「change to 12 weeks for both」 — the 8 / 10-week OFFER_RULES
     * windows stay the pre-shown part, 顯示更多週次 reveals the rest). MIRROR of
     * functions PLACEMENT_DATES_MAX_WEEKS_AHEAD — the server refuses a held day
     * later than this.
     */
    PLACEMENT_DATES_MAX_WEEKS_AHEAD: 12,
    /**
     * 候補 保留中 (deep review Q4, PO 2026-09-30): a hold whose 保留至 is this many Taipei
     * calendar days away or fewer shows amber 「保留至 M/D · 剩 N 天」 — parity with the 轉班
     * hold's TRANSFER_BF_WARNING_DAYS. Website display only (no server rule reads it).
     */
    PLACEMENT_EXPIRY_WARNING_DAYS: 7,
  },

  /**
   * 排課時段表 / 候補 timeslot directory (2026-09-29, PO "do both"): ONE stored
   * doc (appSettings/timeslotDirectory) written by the backend after every
   * schedule upload, nightly at 03:10 and on admin-syncTimeslotDirectory.
   * useTimeSlotData listens to it and feeds the SAME discovery cache every
   * consumer already reads, so admin-getAvailableTimeSlots is only the
   * bootstrap / fallback. MIRROR: functions appConfig BUSINESS_RULES.TIMESLOT_DIRECTORY.
   */
  TIMESLOT_DIRECTORY: {
    COLLECTION: 'appSettings',
    DOC_ID: 'timeslotDirectory',
    /** How long discovery waits for the listener's first emission before falling back to the CF. */
    FIRST_EMISSION_TIMEOUT_MS: 3000,
  },

  /**
   * Per-account dismissible banners: `users/{uid}.dismissedFlags[KEY]` = Timestamp, written
   * by sessionEnrollments-dismissUserBanner. BF_CHANGE_REMINDER = the monthly 「BF 常態班：
   * 要改班名、換老師？」 banner (常態班更名 DT9 — 主管 + 課務 via `seesBfChangeReminder`;
   * hidden until the next Taipei calendar month after 已了解，關閉). Words:
   * UI_STRINGS.BF_CHANGE_REMINDER. MIRROR: functions appConfig BUSINESS_RULES.USER_BANNERS
   * (check-config-mirror pair).
   */
  USER_BANNERS: {
    BF_CHANGE_REMINDER: 'bfChangeReminder',
  },

  /**
   * Route contexts (route `meta.roleContext`, router/routes.ts) on which a USER_BANNERS banner
   * is NOT drawn. FE-only — where the banner shows; the server never reads it, so it is not
   * part of the USER_BANNERS mirror. BF_CHANGE_REMINDER: not on the 'tools' pages (計時器 /
   * 抽籤順序 / BF 計算機) — fixed-height full-screen layouts the banner would push off screen
   * (常態班更名 Q5, PO 2026-10-05). Hidden there only: nothing is dismissed, it shows again on
   * the next page outside these contexts.
   */
  USER_BANNER_HIDDEN_ROLE_CONTEXTS: {
    BF_CHANGE_REMINDER: ['tools'] as const satisfies readonly RoleContext[],
  },

  /**
   * W10.5 X11 / X12 (續報・轉班 完成規則 sweep, 2026-10-15): the long callables — the browser
   * waits their server limit + CLIENT_DEADLINE_MARGIN_SECONDS (utils/longCallDeadline.ts)
   * instead of the Firebase SDK's 70 s, so a run the server finishes is never reported as
   * failed. TIMEOUT_SECONDS MIRROR: functions appConfig BUSINESS_RULES.LONG_CALLS.
   * TIMEOUT_SECONDS (= HIGH_MEMORY_CONFIG 540 s; check-config-mirror pair). Users: 課表上傳
   * (預覽 / 確認 / 手動救援), 系統維護 (立即執行每日掃描, timeslotId 補齊) and the 轉班 actions
   * that release the new class's hold — the server re-checks that student + class before it
   * replies (TRANSFER_HOLD_RELEASE_ACTIONS / _PATHS: mutateTransferRecord `action`, and the
   * `path` of action 'complete'); every other 轉班 action keeps the SDK default.
   */
  LONG_CALLS: {
    TIMEOUT_SECONDS: 540,
    CLIENT_DEADLINE_MARGIN_SECONDS: 15,
    TRANSFER_HOLD_RELEASE_ACTIONS: ['convertToWaitlist', 'parentKeptSource'] as readonly string[],
    TRANSFER_HOLD_RELEASE_PATHS: [
      'cancel',
      'cancel-from-bf-pending',
      'pivot-not-renewing-from-bf-pending',
      'change-target',
    ] as readonly string[],
  },

  /**
   * 常態班更名 (teacher-change mapping) limits. MAX_TEACHERS / MAX_TEACHER_NAME_LENGTH = the
   * save CF's bounds on the 新老師 list (number of names / characters per name) — not the
   * form's warning, which fires on "more teachers than the old class had" (GT14).
   * APPLY_MARKER_FRESH_MINUTES = how long a 套用中 marker counts as a run still in progress
   * (the server refuses 重新套用 and detection defers 時段下架 inside it) — the card's
   * 套用仍在進行 / 套用已中斷 hints read it. MIRROR: functions appConfig
   * BUSINESS_RULES.CLASS_IDENTITY (check-config-mirror pairs).
   */
  CLASS_IDENTITY_LIMITS: {
    /** W8 (item 7) — the ONLY capped list left in the 常態班更名 windows: the other class's
     *  會員姓名 in the own-cohort alert (the backend sends at most this many, then a count).
     *  The merge / skip / restore / 重新報名 lists are never capped — every student the apply
     *  touches is listed before 確認 (DT6 / A19); they scroll instead. */
    OWN_COHORT_NAMES_MAX: 10,
    /** W8 (item 38) — overlapping class days shown in the own-cohort alert (Taipei M/D; the
     *  backend's sampleDates hold at most this many). */
    OWN_COHORT_SAMPLE_DATES_MAX: 5,
    /** Every `code` the 套用 engine writes on an apply-report line (functions
     *  utils/classIdentityChange.ts). The 套用 window shows some of them in a dedicated spot and
     *  drops their list line (never twice). MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.APPLY_WARNING_CODES (check-config-mirror pair). */
    APPLY_WARNING_CODES: {
      MERGED_SYSTEM_RECORD: 'MERGED_SYSTEM_RECORD',
      MERGED_HUMAN_ACTION: 'MERGED_HUMAN_ACTION',
      SUCCESSOR_ENDED_ARCHIVED: 'SUCCESSOR_ENDED_ARCHIVED',
      COLLISION_TRANSFER: 'COLLISION_TRANSFER',
      SUCCESSOR_TRANSFERRING: 'SUCCESSOR_TRANSFERRING',
      SELF_TRANSFER: 'SELF_TRANSFER',
      COHORT_DROPPED: 'COHORT_DROPPED',
      RESTORED: 'RESTORED',
      RESTORED_DEADLINE_LOST: 'RESTORED_DEADLINE_LOST',
      RESURRECTED: 'RESURRECTED',
      RESURRECTED_AFTER_HUMAN_END: 'RESURRECTED_AFTER_HUMAN_END',
      RESTORE_TRANSFER_SKIPPED: 'RESTORE_TRANSFER_SKIPPED',
      RESTORE_HOLD_SKIPPED: 'RESTORE_HOLD_SKIPPED',
      RESTORE_SUCCESSOR_TRANSFERRING: 'RESTORE_SUCCESSOR_TRANSFERRING',
      RESTORE_SELF_TRANSFER: 'RESTORE_SELF_TRANSFER',
      TRANSFER_HOLD_EXPIRED: 'TRANSFER_HOLD_EXPIRED',
      RESTORE_BOUND_MISSING: 'RESTORE_BOUND_MISSING',
      RESTORE_NOT_PREVIEWED: 'RESTORE_NOT_PREVIEWED',
      UNDER_THRESHOLD_RESTORE: 'UNDER_THRESHOLD_RESTORE',
      TRANSFER_RECORD_SELF_TARGET: 'TRANSFER_RECORD_SELF_TARGET',
      INVARIANT_PREEXISTING: 'INVARIANT_PREEXISTING',
      INVARIANT_ENGINE: 'INVARIANT_ENGINE',
      DANGLING_REF: 'DANGLING_REF',
      NOTES_SWEEP: 'NOTES_SWEEP',
      NOTES_SWEEP_MERGED: 'NOTES_SWEEP_MERGED',
      UNSEEN_TEACHER: 'UNSEEN_TEACHER',
      COOP_FEEDBACK_TEACHERS: 'COOP_FEEDBACK_TEACHERS',
      PREVIEWED_RESTORE_MISSING: 'PREVIEWED_RESTORE_MISSING',
      DIRECTORY_REBUILD_FAILED: 'DIRECTORY_REBUILD_FAILED',
      REPORT_TRUNCATED: 'REPORT_TRUNCATED',
      INTERRUPTED_RUN_RECOVERED: 'INTERRUPTED_RUN_RECOVERED',
    },
    MAX_TEACHERS: 4,
    MAX_TEACHER_NAME_LENGTH: 40,
    APPLY_MARKER_FRESH_MINUTES: 30,
    /** A12 — the `type` of the 課表上傳 preview warning rows built from 套用中 markers
     *  (functions sessionEnrollments/applyingMarkers.ts). MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.APPLYING_MARKER_WARNING_TYPE (check-config-mirror). */
    APPLYING_MARKER_WARNING_TYPE: 'classIdentityApplying',
    /** W8 (digest A21) — the `type` of the 課表上傳 preview rows that carry 常態班更名 notices
     *  (new-teacher slot seen / pending entry's slot in this upload / 轉班中 students left);
     *  shown in the same section as the 套用中 rows. MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.CLASS_IDENTITY_NOTICE_WARNING_TYPE (check-config-mirror). */
    CLASS_IDENTITY_NOTICE_WARNING_TYPE: 'classIdentityNotice',
    /** A13 / W8 item 28 — the server-side timeout (seconds) of applyClassIdentityMapping.
     *  useClassIdentityApply waits (this + CLIENT_DEADLINE_MARGIN_SECONDS) × 1000 ms, so the
     *  client never gives up before the server does. MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.APPLY_TIMEOUT_SECONDS (check-config-mirror pair). */
    APPLY_TIMEOUT_SECONDS: 300,
    /** A13 / W8 item 28 — the server-side timeout (seconds) of rerunDetectionOneOff
     *  (重新偵測); the client waits it + CLIENT_DEADLINE_MARGIN_SECONDS. MIRROR: functions
     *  appConfig BUSINESS_RULES.CLASS_IDENTITY.RERUN_TIMEOUT_SECONDS (check-config-mirror pair). */
    RERUN_TIMEOUT_SECONDS: 540,
    /** Q10 (PO 2026-10-05) — the dead-run rule of a 課表上傳 job: a 處理中 job whose last
     *  status change is older than the upload CFs' timeout (HIGH_MEMORY_CONFIG's 540 s — the
     *  same value as RERUN_TIMEOUT_SECONDS) + this margin is a dead run and no longer locks the
     *  upload page (composables/useUploadJobBusy.ts; the server refuses on the same rule).
     *  MIRROR: functions appConfig BUSINESS_RULES.CLASS_IDENTITY.
     *  ACTIVE_UPLOAD_JOB_STALE_MARGIN_SECONDS (check-config-mirror pair). */
    ACTIVE_UPLOAD_JOB_STALE_MARGIN_SECONDS: 60,
    /** How much longer (seconds) the client waits than the server's own timeout — a reply
     *  that is late by network time is still received instead of reported as lost. */
    CLIENT_DEADLINE_MARGIN_SECONDS: 15,
    /** A2 — `details.code` of the save / apply refusal when the successor is another class
     *  and the overlap is not acknowledged. MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.OWN_COHORT_REFUSAL_CODE (check-config-mirror pair). */
    OWN_COHORT_REFUSAL_CODE: 'OWN_COHORT_NOT_ACKNOWLEDGED',
    /** W8 — `details.code` of the save refusal when the edited entry changed after this tab
     *  loaded it (the request carried `expectedUpdatedAtMs`; the backend's text is
     *  VALIDATION_ERRORS.ENTRY_CHANGED_SINCE_LOAD). The form then keeps 儲存變更 disabled until
     *  the page is reloaded. */
    ENTRY_CHANGED_REFUSAL_CODE: 'ENTRY_CHANGED_SINCE_LOAD',
    /** Pass 11 (M-2) — `details.code` of the apply refusal when a record appeared under the new
     *  identity after the apply's fresh read (the backend's MOVE_TARGET_APPEARED_NOTICE: stopped,
     *  重新套用 later). A refusal, not a lost reply: the window shows the server's sentence and
     *  the list reloads. MIRROR: functions appConfig
     *  BUSINESS_RULES.CLASS_IDENTITY.MOVE_TARGET_APPEARED_REFUSAL_CODE (check-config-mirror pair). */
    MOVE_TARGET_APPEARED_REFUSAL_CODE: 'MOVE_TARGET_APPEARED',
    /** W8 (Pass 6 D2) — `details.code` of the apply refusals that invalidate the preview the
     *  window shows (the backend's sentence ends 「請關閉此視窗，再按一次 套用。」): 確認 stays
     *  disabled and the sentence shows above the buttons. Lock / upload refusals are not
     *  among them (waiting is enough there). */
    APPLY_STALE_PREVIEW_CODES: [
      'EXPECTED_SUCCESSOR_MISMATCH',
      'ENTRY_CHANGED_SINCE_PREVIEW',
      'CONFIRM_TOKEN_MISMATCH',
      'SUCCESSOR_CHAINED',
    ] as readonly string[],
    /** 顯示方式 (PO 2026-10-05 第 11 段) — when slot labels start using an entry's new name /
     *  teachers: 'immediate' = from 套用 on, 'follow-excel' = from the week the next lesson in
     *  the uploaded schedule carries it. The form / card window offer one radio row per changed
     *  part, the default pre-selected with UI_STRINGS.CLASS_IDENTITY.DISPLAY_MODE_DEFAULT_SUFFIX;
     *  an entry without a stored choice uses the defaults. The backend decides every label
     *  (functions admin/slotDisplay.ts). MIRROR: functions appConfig BUSINESS_RULES.
     *  CLASS_IDENTITY.DISPLAY_MODE_VALUES / DISPLAY_MODE_DEFAULTS (check-config-mirror pairs). */
    DISPLAY_MODE_VALUES: ['immediate', 'follow-excel'] as const,
    DISPLAY_MODE_DEFAULTS: { className: 'immediate', teachers: 'follow-excel' } as const,
  },

  /**
   * 報名訊息產生器 (admin/message-composer) — P1.9 snapshot architecture
   * (PO-decided 2026-07-17): the page reads ONE pre-computed Firestore doc via
   * onSnapshot (no CF in the read path — instant in a fresh tab). The doc is
   * regenerated by admin-syncComposerSnapshot (manual 🔄 button) and the
   * schedule-upload completion hook.
   * MIRROR: functions appConfig BUSINESS_RULES.COMPOSER_SNAPSHOT.
   */
  SIGNUP_COMPOSER: {
    /** Snapshot doc location (rules: read auth != null / write false — CF only). */
    SNAPSHOT_COLLECTION: 'appSettings',
    SNAPSHOT_DOC_ID: 'composerSnapshot',
    /** 價目/方案 doc（appSettings/pricing，admin-saveSignupPricing 專寫；
     *  MIRROR: functions appConfig BUSINESS_RULES.SIGNUP_PRICING）。 */
    PRICING_DOC_ID: 'pricing',
    /**
     * Max available-week chips shown per slot row before collapsing to +N.
     * PO 2026-07-17 (round 2): PARITY with the 候補 tab — nearest date only,
     * remaining dates enumerate in the +N hover (其他可報名日).
     */
    CHIP_DISPLAY_CAP: 1,
  },

  /**
   * 家長訊息視窗 統一 (PO 2026-09-25) — FE-only (no backend mirror; nothing here is
   * sent to a Cloud Function). Words: UI_STRINGS.PARENT_MESSAGE.
   */
  PARENT_MESSAGE: {
    /** How long 複製訊息 reads 「✓ 已複製」 (replaces two separate 1600 ms constants). */
    COPIED_FEEDBACK_MS: 2000,
    /**
     * 簽到表 shows a mark's answer (「已標記：…」 or a refusal) in the page's own corner
     * card, as all its messages (PO 2026-09-26 Q3) — shown this long.
     */
    SIGN_IN_CARD_MS: 3000,
    /**
     * 「保留期限剛改為 M/D…」 as a toast (the window's line can be off-screen on a phone —
     * review A6): ~50 characters of instructions, so it stays longer than an answer
     * (re-review R3).
     */
    DEADLINE_CHANGED_TOAST_MS: 8000,
    /**
     * The 招生客服說明 route NAME (router/routes.ts). The default-text link targets
     * it and hides itself while that page is open (you are already there).
     */
    COMPOSER_ROUTE_NAME: 'MessageComposerPage',
  },

  /**
   * 招生訊息範本後台編輯 (PO 2026-09-18) — supervisor edits the DEFAULT wording of
   * any 招生客服說明 step in place, on /admin/message-composer.
   *
   * Storage = ONE doc `appSettings/signupTemplates` holding a SPARSE override map
   * (only edited steps appear). Not a collection: 70 docs × onSnapshot remount
   * ≈ 98k reads/tab/night (tasks/todo-signup-messages.md A-quater #2). The
   * `messageTemplates` collection in todo-liff-messaging.md is a DIFFERENT set
   * (9 automated LINE dispatch keys, read server-side at send time) — both coexist.
   *
   * An unedited step keeps reading config/signupSteps.ts, so code improvements
   * still reach the PO; an edited step is theirs until 還原成系統預設.
   *
   * MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.SIGNUP_TEMPLATES
   * (check-config-mirror.cjs compares them).
   */
  SIGNUP_TEMPLATES: {
    /** Document id inside the appSettings collection. */
    DOC_ID: 'signupTemplates',
    /**
     * Max archived versions kept for ONE template (PO 2026-09-20: 8).
     *
     * 實測：81 則全文加起來才 36 KB（平均 456 bytes，最長 1,124），而 Firestore
     * 單一文件上限是 1,024 KB —— 純文字幾乎不佔空間，所以上限可以給得大方。
     */
    HISTORY_MAX_PER_KEY: 8,
    /**
     * 整份文件的歷史總筆數上限。它不是用來限制每一則的（那是上面那個），而是
     * 擋住「文件無限長大」——要吃到它，得有 25 則以上各自改過 8 次。
     * 沒有每則上限的話，這個全域數字就是在對主管說謊：還原說「你的版本會留在
     * 歷史版本」，但改別的步驟幾十次就會把這一則唯一的存檔擠掉。
     */
    HISTORY_MAX: 200,
    /** LINE's own ceiling for a single text message. */
    MAX_BODY_CHARS: 5000,
    /** Hard cap on override entries (70 steps + ~20 variants exist today). */
    MAX_OVERRIDES: 120,
    /** Max length of a variant name inside an override key. */
    MAX_VARIANT_NAME_CHARS: 60,
    /**
     * Refuse a write that would take the document past this.
     *
     * ⚠️ MAX_OVERRIDES × MAX_BODY_CHARS is ~1.8 MB in Traditional Chinese
     * (3 bytes/char) — i.e. the two limits above, multiplied, EXCEED Firestore's
     * 1 MiB document ceiling on their own. Without this guard the supervisor can
     * walk the doc into a state where every further save throws
     * INVALID_ARGUMENT, and 還原 does not reliably shrink it (it moves the removed
     * body INTO history), so the editor locks itself out with no UI way back.
     */
    MAX_DOC_BYTES: 900000,
    /**
     * Tokens INJECTED at send time by the step's kind. They have no entry in the
     * step's own `fields`, so a step's vocabulary = fields ∪ INJECTED_TOKENS[kind].
     */
    INJECTED_TOKENS: {
      link: ['link', 'validity'],
      feedback: ['pdfLink'],
      t03: ['plan', 'price', 'slotNote', 'slots', 'emoji'],
    },
    /**
     * Dropping one of these ships a message with no payment link, no PDF, no
     * schedule or no price — the save is REJECTED. Every other token only warns,
     * because removing 孩子名 may be a deliberate choice.
     */
    /**
     * Tokens sourced from SHARED CONSTANTS in signupMessages.ts, interpolated
     * directly into step bodies (BANK_BLOCK, BANK_BLOCK_EN, BOOKING_LINKS,
     * MAKEUP_RULES, BF_REGISTER_URL, GOOGLE_REVIEW_URL — no counts here: they
     * drift every time a step is added). They MUST survive an edit as tokens:
     * an override stores text, so an expanded copy freezes them — the day the bank account changes,
     * an edited step would keep sending the OLD account number to a paying parent.
     * That is why `bankBlock` is in REQUIRED_TOKENS.
     */
    SHARED_TOKENS: [
      'bankBlock',
      'bankBlockEn',
      'bookingLinks',
      'makeupRules',
      'bfRegisterUrl',
      'googleReviewUrl',
    ],
    REQUIRED_TOKENS: [
      'link',
      'validity',
      'pdfLink',
      'plan',
      'price',
      'slots',
      // 可提供日期 is the 候補 sibling of `slots`: 候01 is literally named
      // 「推薦上課日期」, and 報05 是「回傳約好的日期」. Both are the one message
      // in their flow that carries dates, so dropping the token ships an offer
      // with nothing to accept (review 2026-09-21, finding M4 — verified against
      // the live CF that such a body saved happily before this).
      'dates',
      'bankBlock',
      'bankBlockEn',
      // 「家長主動稱讚時」存在的目的就是這個連結；體06 的 Google 邀請句少了它，
      // 就是一句沒有入口的邀請（PO 2026-09-24）。
      'googleReviewUrl',
      // 續報通知 告訴家長「幾號前要繳費」——少了它，之後「已再提醒」記下的
      // 「家長被告知的日期」就對應到一則沒有日期的訊息。**不**沿用 `deadline`：
      // 那是 候01／候02 的付款截止時間，這張表是全域的，會連帶改掉候補的地板
      // （計畫 D-3，審查 2026-09-24）。
      'renewalDeadline',
      // 學期結束日：續報通知 與 約課通知 都是「告訴家長這一期何時結束」的那一則；
      // 刪掉它，存得起來的就是一則沒有日期的通知（與 {{續報期限}} 同一個理由，
      // 計畫 D-3 的目的推得；只有這兩則宣告了 lsed 欄位）。
      'lsed',
      // 建議轉班 存在的目的就是建議時段；刪掉它就是一則只說「建議轉班」卻沒有
      // 時段可選的訊息（PO 2026-09-24 Q5，T16d）。
      'suggestedSlots',
    ],
    /**
     * 繁中 label per injected token — the editor shows {{付款連結}}, never {{link}}.
     * Field-declared tokens take their label from the step's own `fields[].label`.
     * Stored text always holds the MACHINE key: storing the 繁中 word would send a
     * literal {{付款連結}} to a parent the day a label is reworded.
     */
    TOKEN_LABELS: {
      link: '付款連結',
      validity: '票券期限',
      pdfLink: '回饋PDF連結',
      plan: '方案',
      price: '價格',
      slotNote: '堂數說明',
      slots: '可選時段',
      // Generic on purpose: 候01 calls its field 可提供日期 and 報05 calls it 日期.
      // Field-declared tokens take their chip label from `fields[].label`, so
      // this is only the wording of the 「這一則必須保留」 refusal — which showed
      // the raw `{{dates}}` until it was added.
      dates: '日期',
      emoji: '課別圖示',
      bankBlock: '銀行資訊',
      bankBlockEn: '銀行資訊 (英文)',
      bookingLinks: '約課連結',
      makeupRules: '補課規則',
      bfRegisterUrl: '註冊網址',
      googleReviewUrl: 'Google評論連結',
      // 續報／轉班／約課通知（M2）。欄位宣告的 token 在編輯器用步驟自己的
      // fields[].label；這裡是「這一則必須保留」拒絕訊息用的同一套說法。
      lsed: '學期結束日',
      renewalDeadline: '續報期限',
      currentSlot: '目前時段',
      observation: '老師觀察',
      suggestedSlots: '建議時段',
      // ⑤ 續報提醒（M3）：剩下的天數 (「最後 N 天」／「今天」) 與剩 3 天內句首的 ⚠️。
      daysLeft: '剩餘天數',
      urgentMark: '急件符號',
    },
    /**
     * 全域 token 字彙＝所有步驟宣告的欄位 ∪ 各 kind 的注入 token ∪ 共用常數。
     * 後端用它擋錯字（`{{chlid}}`）。它**不是**逐步驟白名單，只是「本系統認得的字」
     * ——主管本來就可以把某步驟宣告過、但預設文案沒用到的 token 加進去
     * （例如 體06 宣告了 child/teacher，預設文案卻一個都沒用）。
     */
    KNOWN_TOKENS: [
      'alt',
      'bankBlock',
      'bankBlockEn',
      'bfRegisterUrl',
      'bookingLinks',
      'child',
      'cls',
      'count',
      'currentSlot',
      'dates',
      'datesWord',
      'daysLeft',
      'deadline',
      'due',
      'emoji',
      'googleReviewUrl',
      'last',
      'link',
      'lsed',
      'm',
      'makeupRules',
      'n',
      'next',
      'observation',
      'off',
      'on',
      'pdfLink',
      'plan',
      'price',
      'ratio',
      'renewalDeadline',
      'slot',
      'slotNote',
      'slots',
      'suggestedSlots',
      'typeWord',
      'urgentMark',
      'validity',
      'who',
    ],
    /**
     * 「掉了就會寄出壞訊息」的步驟 → 該步驟必須保留的 token。只有下列步驟有，其餘步驟
     * 沒有 payload token。這是**伺服器端的地板**，主要防線仍是編輯器。
     *
     * ⚠ 手維護的清單會腐爛（CLAUDE.md #11）。所以 `signupMessages.test.ts` 會從
     * 真實步驟定義重新推導這張表並逐字比對——新增一個帶付款連結／銀行資訊的步驟
     * 卻忘了補這裡，測試就會紅，而不是靜默失去保護。
     */
    STEP_REQUIRED_TOKENS: {
      INQUIRY_RECOMMEND: ['plan', 'price', 'slots'],
      INQUIRY_PAYMENT_LINK: ['link', 'validity'],
      INQUIRY_RETURNING: ['link', 'validity'],
      TRIAL_FEEDBACK: ['googleReviewUrl', 'pdfLink'],
      'TERM_PAYMENT::游泳學期課': ['bankBlock'],
      'TERM_PAYMENT::體能學期課': ['bankBlock'],
      // 報05 回傳的就是「約好的日期」，刪掉 {{日期}} 等於寄出一則沒有課表的報名完成。
      INQUIRY_BOOKED: ['dates'],
      // 候補：票券三版都必須留著 {{付款連結}} 與 {{可提供日期}}——少了連結家長無從
      // 付款，少了日期則根本沒有東西可以答應。正課 是分開的一則（走匯款），它不推薦
      // 日期，地板是金額與帳號。
      'WAITLIST_OFFER::單堂類・多個日期': ['dates', 'link'],
      'WAITLIST_OFFER::單堂類・只有一個日期': ['dates', 'link'],
      'WAITLIST_OFFER::體驗三堂': ['dates', 'link'],
      WAITLIST_OFFER_TERM: ['bankBlock', 'plan', 'price'],
      // 候03 單堂買「任選 6 堂」票券包：走匯款，所以地板是日期＋金額＋帳號，沒有連結。
      WAITLIST_OFFER_BUNDLE: ['bankBlock', 'dates', 'plan', 'price'],
      // 候04 正課・使用剩餘票券 (M7, WAITLIST_OFFER_TERM_PREPAID) 只帶 時段與班級——沒有金額、
      // 帳號、日期或連結，推導不出必留欄位，所以不列 (signupMessages.test 的推導；後端照此)。
      BANK_INFO_TW: ['bankBlock'],
      // 英文匯款範本改走共用常數之後（PO 2026-09-20），它也受同一道保護：
      // 刪掉 {{銀行資訊（英文）}} 就等於寄出一則沒有帳號的匯款訊息。
      BANK_INFO_EN: ['bankBlockEn'],
      // 這兩則把 slots 宣告成**自己的欄位**（不是 kind 注入的），第一版的推導只看
      // 注入 token 與共用常數，所以整個漏掉。體07 是 mustSay ＋ soloOnly，它的
      // mustWhy 自己寫著「這是全流程唯一給時段清單的時點」——少了它就是把唯一
      // 一則帶時段的訊息寄成空的。
      DISCLOSURE_SUITABLE_SLOTS: ['slots'],
      SLOTS_UPDATE: ['slots'],
      // 「家長主動稱讚時」三版：這則訊息的目的就是 Google 評論連結（PO 2026-09-24）。
      'REVIEW_INVITE::一般・較客氣': ['googleReviewUrl'],
      'REVIEW_INVITE::熟客・輕鬆': ['googleReviewUrl'],
      'REVIEW_INVITE::寫了一段心得': ['googleReviewUrl'],
      // ⑤ 續報通知：期限、金額、帳號，三者少一個就是一則無法照著繳費的通知
      // （計畫 D-3；帳號走 {{bankBlock}}，改了銀行資料連改過的文案也跟著換）。
      RENEWAL_NOTICE: ['bankBlock', 'lsed', 'price', 'renewalDeadline'],
      // ⑤ 續報提醒（M3）：標記已再提醒 會記下「家長被告知的日期」＝訊息裡的續報期限，
      // 刪掉它就對應到一則沒有日期的提醒（D-3）。剩餘天數／急件符號 不是必留。
      RENEWAL_REMINDER: ['renewalDeadline'],
      // ⑪ 轉班與約課通知（PO 2026-09-24 Q5：家長訊息的預設一律住在 招生客服說明）。
      TRANSFER_PAYMENT_REMINDER: ['bankBlock', 'price'],
      TRANSFER_PROPOSAL: ['suggestedSlots'],
      // 約課通知 就是「班級＋最後約課日」這一句話。
      BOOKING_NOTICE: ['lsed'],
    },
  } as const,

  TEACHER_MOBILE: {
    /**
     * Rolling future window (days) shown on teacher mobile class views.
     * Teacher view is reference-only ("who is teaching next 2 weeks") —
     * NOT a slot-allocation surface. Empty future cells beyond this window
     * could be misinterpreted as available slots, which they aren't.
     *
     * MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.TEACHER_MOBILE.FUTURE_DAYS_CAP
     * Both must stay in sync.
     */
    FUTURE_DAYS_CAP: 14,
    /**
     * 救生員 手機版: a VIEW-ONLY roster user holds a role in ROSTER_VIEW_ONLY_ROLES
     * and NONE of ROSTER_FULL_ACCESS_ROLES (teacher+lifeguard → teacher view;
     * admin/supervisor+lifeguard → observe view). permissions.ts isRosterViewOnly
     * reads these; the backend decides the same way from server-read roles.
     *
     * MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.TEACHER_MOBILE
     * (functions rosterRoleParity.test.ts + check-config-mirror.cjs compare them).
     */
    ROSTER_VIEW_ONLY_ROLES: ['lifeguard'],
    ROSTER_FULL_ACCESS_ROLES: ['teacher', 'admin', 'supervisor'],
    /**
     * 救生員 view (incl. a teacher's 救生員模式): today − DAYS_BACK … today +
     * DAYS_AHEAD only — PO 2026-09-16: today and the next 6 days, no past. The
     * backend enforces the same window. MIRROR: functions BUSINESS_RULES.TEACHER_MOBILE
     */
    ROSTER_VIEW_ONLY_DAYS_BACK: 0,
    ROSTER_VIEW_ONLY_DAYS_AHEAD: 6,
    /** Height of the pull-up 學生紀錄 drawer on the phone roster (家庭帳號 plan D4). FE-only. */
    RECORD_DRAWER_HEIGHT: '86%',
  } as const,

  /**
   * 家庭帳號 on the teacher phone roster + admin 簽到表 (plan tasks/todo-family-accounts.md
   * D1 D3 D5 D7 D8). UI strings only — every on-screen word of the family-notes feature
   * lives here, components never author them.
   */
  FAMILY_NOTES_UI: {
    /** Hint under a typed name's note box (D3, PO wording). No 「臨」 chip after the name (D26). */
    TEMP_HINT: '臨時名稱僅單日記錄',
    /** Link under the note box; {n} = other names on the same 會員帳號 (D5). */
    LINK: (n: number) => `家庭帳號 ${n} 名 ›`,
    /** The panel's small heading; the class itself is the big block under it (PO 2026-10-16). */
    SHEET_KICKER: '家庭帳號',
    /** Label before the drawer's family chips (D8). */
    CHIPS_LABEL: '家庭帳號',
    /** Pill on the row's own name inside the sheet (listed first, D25; wording D29). */
    SELF: '約課帳戶',
    /** A name with no note in this timeslot — nothing appended (D24). */
    NONE_IN_SLOT: '無紀錄',
    /** Per-name action: opens that name's drawer at this timeslot, 新增記錄 open (D7). */
    ADD_NOTE: '新增記錄 ›',
    /** Same action for a viewer who cannot write 學習歷程記錄 (課務 admin-only, R1-M3). */
    VIEW_NOTES: '看記錄 ›',
    CHEVRON: '›',
    /**
     * 首堂課 家庭帳號 確認 M2 (D-C, D-F, D-H; spec §5b PO round 7). The 課務 panel's
     * read-only link is 老師紀錄 › (replaces VIEW_NOTES there). D-AA: on the teacher
     * phone a note writer types in the drawer's 學習歷程記錄 box, and the link is
     * 老師紀錄 › there too; 新增記錄 › stays only where there is no box.
     */
    TEACHER_NOTES: '老師紀錄 ›',
    STUDENT_RECORD: '學生紀錄 ›',
    ADMIN_NOTE_LABEL: '課務備註',
    ADMIN_NOTE_PLACEHOLDER: '輸入課務備註…',
    ADMIN_NOTE_STAMP: (by: string, mmdd: string) => `${by} · ${mmdd}`,
    /** D-AA: the teacher drawer's box — the class list box's word (NotesCell single mode). */
    TEACHER_NOTE_LABEL: '學習歷程記錄',
    TEACHER_NOTE_PLACEHOLDER: '輸入學習歷程記錄…',
    /**
     * Line 2 (上課): any programme. firstMmdd '' leaves 「· 自 …」 out — the panel does
     * that when the first class is the latest one's day (mockup §4A 「已上 1 堂 · 最近 10/04 小鴨班」).
     */
    ATTENDED: (n: number, latestMmdd: string, latestClass: string, firstMmdd: string) =>
      [
        `已上 ${n} 堂`,
        latestMmdd || latestClass ? `最近 ${latestMmdd} ${latestClass}`.trim() : '',
        firstMmdd ? `自 ${firstMmdd}` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    ONLY_THIS_CLASS: '只有這一堂，無其他出席',
    NO_ATTENDANCE: '無出席',
    /**
     * PO 2026-10-16 (mockup docs/tools/family-drawer-attendance-block.html): per name
     * three label pills — 體驗 · 此班 · 其他班; 此班／其他班 say 「已上 N 堂 · 期間」 with
     * the 學生紀錄 card's own writer (UI_STRINGS.FIRST_TRIAL.SLOT_LISTS.ATTENDED — PO
     * 2026-10-16, docs/tools/student-record-one-card-per-class.html). ATTENDED /
     * ONLY_THIS_CLASS above = an older server's one line only.
     */
    TRIAL_LABEL: '體驗',
    TRIAL_NONE: '無紀錄',
    THIS_CLASS: '此班',
    OTHER_CLASSES: '其他班',
    /** Line 3 (學習歷程) when this time slot has no record. */
    NO_PROGRESS_NOTE: '學習歷程 無紀錄',
    /** Merged pill on the roster name's own 首堂待確認 row: 首堂待確認｜日期 班名 · 票種. */
    REVIEW_TICKET: (ticket: string) => `· ${ticket}`,
    /** feedback-getFamilyFacts failed: one line above the names; names, links and notes stay usable. */
    FACTS_FAILED: '上課與回饋紀錄讀取失敗，請關閉後再打開',
  } as const,

  /**
   * Tools pages (/tools/countdown, /tools/draw-sequence). 救生員 = same as
   * teachers, including timer takeover (PO P1, 2026-09-16). routes.ts spreads it.
   *
   * MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.TOOLS
   */
  TOOLS: {
    TIMER_ROLES: ['admin', 'supervisor', 'teacher', 'kiosk', 'lifeguard'],
  } as const,

  /**
   * Student photo capture on the teacher mobile roster (PO P4, 2026-09-16):
   * 20 min before class start … 20 min after class end (Taipei); student on the
   * roster; never when 拍照 = 不同意; teachers only on their own class. The
   * server re-checks everything. Window check: utils/photoCaptureShared.ts
   * isWithinPhotoWindow (identical copy in functions).
   *
   * MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.PHOTO_CAPTURE
   */
  PHOTO_CAPTURE: {
    WINDOW_MINUTES: 20,
    CLASS_BOUND_ROLES: ['teacher', 'lifeguard'],
    /** Photo paths without the class window (iPad kiosk, 簽到作業) — unchanged by P4. */
    UNBOUNDED_ROLES: ['admin', 'supervisor', 'kiosk'],
    BLOCKED_CONSENT: '不同意',
  } as const,
} as const;

// ==================================================================================
// CATEGORY: TIMER (countdown / countup) BUSINESS RULES
// Description: Constants for the shared /tools/countdown session timer.
// ==================================================================================

export const TIMER_STATUS = {
  PENDING: 'pending',
  RUNNING: 'running',
  PAUSED: 'paused',
} as const;

export type TimerStatus = (typeof TIMER_STATUS)[keyof typeof TIMER_STATUS];

export const TIMER_STATUS_LABELS: Record<TimerStatus, string> = {
  pending: '等待開始',
  running: '進行中',
  paused: '已暫停',
};

export const TIMER_VISIBILITY = {
  PRIVATE: 'private',
  SHARED: 'shared',
} as const;

export type TimerVisibility = (typeof TIMER_VISIBILITY)[keyof typeof TIMER_VISIBILITY];

export const TIMER_VISIBILITY_LABELS: Record<TimerVisibility, string> = {
  private: '私人',
  shared: '共用',
};

export const TIMER_MODE = {
  COUNTDOWN: 'countdown',
  COUNTUP: 'countup',
} as const;

export type TimerMode = (typeof TIMER_MODE)[keyof typeof TIMER_MODE];

// Contrast validated against the dark timer background (.countdown-container in
// SyncedCountdownPage.vue uses a near-black surface). If the component theme
// changes to a light background, re-verify via WCAG.
// Yellow and green are intentionally bright — projector / classroom readability.
export const TIMER_CLOCK_COLOR_HEX = {
  white: '#ffffff',
  yellow: '#ffe24a',
  green: '#2ef27e',
  red: '#ff4d6d',
  blue: '#4aa8ff',
  orange: '#ff944a',
} as const;

export type TimerClockColor = keyof typeof TIMER_CLOCK_COLOR_HEX;

export const TIMER_CLOCK_COLOR_LABELS: Record<TimerClockColor, string> = {
  white: '白色',
  yellow: '黃色',
  green: '綠色',
  red: '紅色',
  blue: '藍色',
  orange: '橘色',
};

export const TIMER_CONFIG = {
  /** Default mode → color mapping when the creator does not pick one. */
  DEFAULT_COLORS: { countdown: 'yellow', countup: 'green' } as const,
  /** Running / paused session TTL (ms). Refreshed on every state transition. */
  SESSION_TTL_MS: 12 * 60 * 60 * 1000, // 12 hours
  /** Pending session TTL (ms). Lets creators pre-schedule up to 30 days ahead. */
  PENDING_TTL_MS: 30 * 24 * 60 * 60 * 1000, // 30 days
  /** Post-stop grace period (ms) — undo window before permanent deletion. */
  STOP_GRACE_MS: 30 * 1000, // 30 seconds
  /** Max length of user-entered session name / lap participant label. */
  MAX_SESSION_NAME_LENGTH: 60,
  MAX_LAP_LABEL_LENGTH: 40,
  /** Participant roster limits. */
  MAX_ROSTER_ENTRIES: 200,
  MAX_ROSTER_LABEL_LENGTH: 40,
  /** Takeover PIN length (digits). Fixed at 4 for timer-takeover use. */
  PIN_LENGTH: 4,
  /** PIN validation pattern — exactly 4 numeric digits. */
  PIN_PATTERN: /^\d{4}$/,
  /** Server-side pause clamp tolerance — widens the accepted clientElapsedMs window. */
  PAUSE_CLAMP_TOLERANCE_MS: 2000,
  /** Reconciliation threshold — skew smaller than this is ignored (monotonic authoritative). */
  RECONCILE_THRESHOLD_MS: 1000,
  /** Max laps displayed per session (onSnapshot limit). */
  MAX_LAPS_PER_SESSION: 500,
  /** Max minutes for a countdown (24h). Cap on the create form. */
  MAX_COUNTDOWN_MINUTES: 1440,
  /** Countup has no real duration; a sentinel used for `durationMinutes` so the doc remains valid. */
  COUNTUP_SENTINEL_DURATION_MINUTES: 240,
  /** Render tick interval (ms). Display driver only — never source of truth. */
  RENDER_TICK_MS: 100,
  /**
   * Pre-start "3 → 2 → 1 → START" ceremony duration (ms). Determines how long
   * the visual overlay + beep sequence runs before the clock is revealed.
   */
  START_CEREMONY_MS: 4000,
  /**
   * One-frame paint bias (ms). Added to the clock anchor so clock-time-zero
   * lands on the NEXT paint frame (where the user actually sees 00:00), not
   * the rAF-callback frame where we schedule the state change. Tuned for 60Hz.
   */
  START_PAINT_BIAS_MS: 16,
  /** localStorage key for the once-per-device viewer-hint banner dismissal. */
  VIEWER_HINT_STORAGE_KEY: 'oovi.countdown.viewerHintSeen',
} as const;

// ==================================================================================
// CATEGORY: ROUTING & NAVIGATION
// Description: Configuration for Vue Router, including permissions and menu structures.
// ==================================================================================

export const ROUTE_CONFIG = {
  PERMISSIONS: {
    ADMIN_ROUTES: ['/admin'],
    TEACHER_ROUTES: ['/teacher'],
    KIOSK_ROUTES: ['/kiosk'],
    PUBLIC_ROUTES: ['/login', '/404'],
  },

  NAVIGATION: {
    ADMIN_MENU: [
      { title: '簽到管理', route: '/admin/signin', icon: 'how_to_reg' },
      { title: '學員管理', route: '/admin/students', icon: 'people' },
      { title: '課程管理', route: '/admin/classes', icon: 'school' },
      { title: '報表統計', route: '/admin/reports', icon: 'analytics' },
    ],
    TEACHER_MENU: [
      { title: '我的課程', route: '/teacher/classes', icon: 'schedule' },
      { title: '學員名單', route: '/teacher/students', icon: 'list_alt' },
      { title: '簽到紀錄', route: '/teacher/attendance', icon: 'fact_check' },
    ],
  },
} as const;

// ==================================================================================
// CATEGORY: API CONFIGURATION
// Description: Settings for frontend communication with backend services (Cloud Functions).
// ==================================================================================

export const FRONTEND_API_CONFIG = {
  REQUESTS: {
    TIMEOUT_MS: 30000,
    MAX_RETRIES: 3,
    RETRY_DELAY_MS: 1000,
    DEFAULT_HEADERS: {
      'Content-Type': 'application/json',
    },
    FIRESTORE_IN_QUERY_CHUNK_SIZE: 30,
    FIRESTORE_BATCH_WRITE_SIZE: 500,
  },

  RESPONSES: {
    SUCCESS_STATUS: 'success',
    ERROR_STATUS: 'error',
    LOADING_MIN_DURATION: 300,
  },

  /**
   * Warm-on-use (utils/firebaseFunctions.ts warmCallable): after a page loads, a
   * fire-and-forget `{ __warmup: true }` ping keeps the NEXT callable warm (e.g.
   * the class list pre-warms getAggregatedClassDetails). At most one ping per
   * callable per MIN_INTERVAL_MS, only between START_HOUR and END_HOUR Taipei
   * (END_HOUR exclusive). MIRROR (hours): functions/src/utils/warmupScheduler.ts
   * cron covers 06:00–21:56 (PO 2026-09-29, was 19:56); warm-on-use runs one
   * hour past it, to 23:00, so the phone student list and the rarely used
   * targets stay covered after the scheduler stops.
   */
  WARM_ON_USE: {
    MIN_INTERVAL_MS: 240000,
    START_HOUR: 6,
    END_HOUR: 23,
  },

  /**
   * PreloadProgressBanner gate (utils/preloadGate.ts). The 「預先載入中：學期續報」
   * pill waits on the renewal listeners' first SERVER-confirmed snapshot
   * (2026-09-29 — it used to wait on two CF calls whose results were thrown
   * away). If no snapshot arrives (offline, permission lost) the gate settles
   * by itself after this many ms so the pill never sticks.
   */
  PRELOAD_GATE_TIMEOUT_MS: 20_000,
} as const;

// ==================================================================================
// CATEGORY: ENVIRONMENT CONFIGURATION
// Description: Manages environment-specific variables and settings.
// ==================================================================================

/**
 * Addresses Firebase Hosting serves the Hub on (site `oovi-hub`). Each one also serves
 * Firebase Auth's sign-in handler at `/__/auth/handler`, so Google sign-in runs on the
 * SAME address the person is using (`utils/authDomain.ts`). Same-address sign-in is
 * required: Safari / iPhone (and newer Chrome) block a redirect sign-in that bounces
 * through a different site. `hub.oovi.tw` added 2026-09-30 (tasks/todo-hub-domain.md).
 *
 * Adding an address here also needs, outside this file: Firebase Auth authorized domain,
 * Google OAuth redirect URI `https://<address>/__/auth/handler`, the reCAPTCHA (App Check)
 * key's domain list, both functions CORS lists (backend `HUB_SITE_CONFIG`) and `cors.json`.
 */
export const HUB_HOSTED_DOMAINS: readonly string[] = [
  'hub.oovi.tw',
  'oovi-hub.web.app',
  'oovi-hub.firebaseapp.com',
];

export class FrontendEnvironmentConfig {
  static isDevelopment(): boolean {
    return import.meta.env.MODE === 'development';
  }

  static isProduction(): boolean {
    return import.meta.env.MODE === 'production';
  }

  static getFirebaseConfig() {
    return {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
    };
  }

  static getEmulatorConfig() {
    return {
      auth: { host: 'localhost', port: 9099 },
      functions: { host: 'localhost', port: 5001 },
      firestore: { host: 'localhost', port: 8080 },
      storage: { host: 'localhost', port: 9199 },
    };
  }

  static shouldUseEmulators(): boolean {
    return this.isDevelopment() && import.meta.env.VITE_USE_EMULATORS === 'true';
  }

  static getApiBaseUrl(): string {
    if (this.shouldUseEmulators()) {
      return 'http://localhost:5001/oovi-hub-dev/asia-east1';
    }
    return `https://asia-east1-${this.getProjectId()}.cloudfunctions.net`;
  }

  static getProjectId(): string {
    return this.getFirebaseConfig().projectId || 'oovi-hub-dev';
  }
}

// ==================================================================================
// CATEGORY: VALIDATION CONFIGURATION
// Description: Rules and limits for form and data validation.
// ==================================================================================

export const VALIDATION_CONFIG = {
  RULES: {
    REQUIRED: '此欄位為必填',
    EMAIL: '請輸入有效的電子郵件地址',
    MIN_LENGTH: (min: number) => `最少需要 ${min} 個字元`,
    MAX_LENGTH: (max: number) => `最多允許 ${max} 個字元`,
    NUMERIC: '請輸入有效的數字',
    POSITIVE_NUMBER: '請輸入正數',
  },

  LIMITS: {
    STUDENT_NAME_MIN: 2,
    STUDENT_NAME_MAX: 50,
    NOTES_MAX: 1000,
    CLASS_NAME_MAX: 100,
    TEACHER_NAME_MAX: 50,
  },

  PATTERNS: {
    EMAIL: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    PHONE: /^[\d\s\-\+\(\)]+$/,
    NUMERIC: /^\d+$/,
    TIME: /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/,
  },
} as const;

// ==================================================================================
// CATEGORY: NOTIFICATION CONFIGURATION
// Description: Settings for user-facing notifications and messages.
// ==================================================================================

// 新增員工: the outcome of the test mail sent after a create with a 通知信箱
// (undefined = no mail was sent) — appended to EMPLOYEE_CREATED／TEMP_ASSISTANT_CONVERTED.
const testMailSuffix = (testEmailSent: boolean | undefined): string => {
  if (testEmailSent === undefined) return '';
  return testEmailSent ? '，測試信已發送' : ' (測試信發送失敗，請稍後重試)';
};

export const NOTIFICATION_CONFIG = {
  DEFAULTS: {
    TIMEOUT: 5000,
    POSITION: 'top-right' as const,
    ACTIONS: true,
    COLOR: 'primary',
  },

  TYPES: {
    SUCCESS: { color: 'positive', icon: 'check_circle', timeout: 3000 },
    ERROR: { color: 'negative', icon: 'error', timeout: 8000 },
    WARNING: { color: 'warning', icon: 'warning', timeout: 5000 },
    INFO: { color: 'info', icon: 'info', timeout: 4000 },
  },

  MESSAGES: {
    CLOSE_ALL_NOTIFICATIONS: '關閉所有通知',
    FETCH_ON_DUTY_ADMIN_ERROR: {
      title: '讀取課務失敗',
      content: '無法從伺服器讀取課務資料，請稍後重試。',
    },
    UPDATE_ON_DUTY_ADMIN_SUCCESS: {
      title: '課務已更新',
      content: '{date} 的課務已設定為 {admin}。',
    },
    UPDATE_ON_DUTY_ADMIN_ERROR: { title: '儲存課務失敗', content: '無法將課務資訊儲存至伺服器。' },
    SAVE_SUCCESS: { title: '保存成功', content: '資料已成功儲存。' },
    SAVE_ERROR: { title: '保存失敗', content: '無法儲存變更，請稍後重試。' },
    LOAD_ERROR: { title: '載入失敗', content: '無法載入資料，請稍後重試。' },
    DELETE_SUCCESS: { title: '刪除成功', content: '資料已成功刪除。' },
    DELETE_ERROR: { title: '刪除失敗', content: '無法刪除資料，請稍後重試。' },
    /** 員工管理 › 臨時助教 (tasks/todo-substitute-pay.md W4). */
    TEMP_ASSISTANT_CREATED: (displayName: string) => ({
      title: '已新增',
      content: `已新增臨時助教：${displayName}`,
    }),
    TEMP_ASSISTANT_UPDATED: (displayName: string) => ({
      title: '已更新',
      content: `已更新臨時助教：${displayName}`,
    }),
    TEMP_ASSISTANT_DEACTIVATED: (displayName: string) => ({
      title: '已停用',
      content: `已停用臨時助教：${displayName}`,
    }),
    /** 轉為員工 saved: the employee was created and the 臨時助教 stopped in one save. */
    TEMP_ASSISTANT_CONVERTED: (displayName: string, testEmailSent?: boolean) => ({
      title: '已建立',
      content: `已建立員工：${displayName} (臨時助教已轉為員工)${testMailSuffix(testEmailSent)}`,
    }),
    /** 員工表單 (EmployeeFormModal). */
    EMPLOYEE_UPDATED: { title: '已更新', content: '員工資料已更新' },
    EMPLOYEE_CREATED: (testEmailSent?: boolean) => ({
      title: '已建立',
      content: `員工資料已建立${testMailSuffix(testEmailSent)}`,
    }),
    DELETE_USER_BLOCKED: {
      title: '無法刪除成員',
      content: '尚有 {count} 筆未完成的付款申請：{requestNumbers}。請先完成、拒絕或撤回這些申請。',
    },
    NETWORK_ERROR: { title: '網路錯誤', content: '網路連接異常，請檢查網路設定。' },
    /** Google 登入被 blockUnprovisionedAccounts 擋下（尚未由後端建立帳號）。
     *  後端：functions/src/identity/blockUnprovisionedAccounts.ts。
     *  偵測方式：錯誤訊息含 UNPROVISIONED_ACCOUNT_MARKER（見 stores/auth.ts）。 */
    LOGIN_UNPROVISIONED: {
      title: '此帳號尚未開通',
      content: '這個 Google 帳號還沒有 Oovi Hub 的存取權，請聯絡管理員建立員工帳號後再登入。',
    },
    /** 帳號存在但被停用（員工已停用，或主管關閉了登入權限）。
     *  必須與「尚未開通」分開講：這是「有帳號但被關掉」，處理方式完全不同
     *  —— 主管到 員工管理 » 編輯 » 登入帳號 按「啟用登入」即可。 */
    LOGIN_DISABLED: {
      title: '此帳號已停用',
      content: '這個帳號目前被停用，無法登入。若需要恢復，請聯絡主管開啟登入權限。',
    },
    /** 其他 Google 登入失敗（重導回來才會知道，故不顯示原始技術訊息）。 */
    LOGIN_FAILED: {
      title: 'Google 登入失敗',
      content: '登入未完成，請再試一次；若持續失敗請聯絡管理員。',
    },
    UNAUTHORIZED: { title: '權限不足', content: '您的權限不足，請重新登入。' },
    SERVER_ERROR: { title: '伺服器錯誤', content: '伺服器發生錯誤，請聯繫系統管理員。' },
    OPERATION_SUCCESS: { title: '操作成功', content: '操作已成功完成。' },
    OPERATION_FAILED: { title: '操作失敗', content: '操作失敗，請稍後重試。' },
    VALIDATION_ERROR: { title: '資料錯誤', content: '資料格式不正確，請檢查輸入內容。' },
    UNKNOWN_ERROR: { title: '未知錯誤', content: '系統發生未知錯誤，請聯繫技術支援。' },
    DATA_INCOMPLETE: { title: '資料不完整', content: '請確保所有必填欄位都已填寫。' },
    REQUIRED_FIELDS: { title: '欄位未填', content: '請填寫所有必填欄位。' },
    LOAD_STUDENT_DETAILS_ERROR: { title: '載入失敗', content: '無法載入學生詳細資料。' },
    NO_CHANGES_DETECTED: { title: '沒有變更', content: '您沒有做出任何修改。' },
    CLASS_REAPPROVED_SUCCESS: { title: '核可成功', content: '課程已成功重新核可。' },
    SAVE_CHANGES_ERROR: { title: '儲存失敗', content: '無法儲存變更，請重試。' },
    DATA_UPDATED_SUCCESS: { title: '更新成功', content: '資料已成功更新。' },
    AUTO_SAVE_SUCCESS: { title: '自動儲存', content: '已自動儲存老師備註。' },
    AUTO_SAVE_ERROR: { title: '自動儲存失敗', content: '部分老師備註可能未儲存成功。' },
    /**
     * Detailed failed-save toast for the attendance modals: names each student, the
     * field, and the attempted value so the admin knows exactly what did NOT save.
     */
    ATTENDANCE_SAVE_ERROR: (
      updates: Array<{ attendanceId: string; field: string; value: unknown }>,
      students: Array<{ attendanceId: string; memberName?: string }>,
    ) => {
      const nameByAtt: Record<string, string> = {};
      for (const s of students) nameByAtt[s.attendanceId] = s.memberName || '學生';
      const fieldLabels = SYSTEM_CONSTANTS.ATTENDANCE_FIELD_LABELS as Record<string, string>;
      const statusLabels = SYSTEM_CONSTANTS.ATTENDANCE_STATUS_LABELS as Record<string, string>;
      const valueLabel = (field: string, value: unknown): string => {
        if (field === 'checkInStatus') return statusLabels[String(value ?? '')] ?? String(value);
        if (field === 'isFirstClass') return value ? '是' : '否';
        return String(value); // photoConsent value is already a Chinese label (同意/不同意/未設定)
      };
      const MAX = 6;
      const lines = updates
        .slice(0, MAX)
        .map(
          (u) =>
            `• ${nameByAtt[u.attendanceId] || '學生'} — ${fieldLabels[u.field] ?? u.field}改為「${valueLabel(u.field, u.value)}」`,
        );
      const extra = updates.length > MAX ? `\n…等共 ${updates.length} 項` : '';
      return {
        title: '儲存失敗',
        content: `未儲存 ${updates.length} 項變更：\n${lines.join('\n')}${extra}`,
      };
    },
    SAVE_ADMIN_NOTES_ERROR: { title: '儲存失敗', content: '無法儲存課務備註。' },
    SAVE_TEACHER_NOTES_ERROR: { title: '儲存失敗', content: '無法儲存老師備註。' },
    UPLOAD_PHOTO_PROMPT: {
      title: '請先儲存學生',
      content: '您必須先儲存新增的學生，然後才能上傳照片。',
    },
    PHOTO_PROCESSING: { title: '正在處理照片', content: '壓縮和上傳中...' },
    PHOTO_UPLOAD_SUCCESS: { title: '照片上傳成功', content: '照片已壓縮並上傳。' },
    PHOTO_UPLOAD_ERROR: { title: '照片上傳失敗', content: '上傳過程中發生錯誤。' },
    UPDATE_CONSENT_ERROR: { title: '更新失敗', content: '無法儲存拍照同意狀態。' },
    FEEDBACK_PROCESSING_STARTED: {
      title: '處理中',
      content: '已開始處理準備清單，請稍後查看結果。',
    },
    FEEDBACK_PROCESSING_SUCCESS: { title: '處理成功', content: '已成功處理準備清單。' },
    FEEDBACK_PROCESSING_PARTIAL_FAILURE: {
      title: '處理完成',
      content: '處理完成，但有 {failureCount} 項失敗: {failedNames}',
    },
    FEEDBACK_PROCESSING_ERROR: {
      title: '處理失敗',
      content: '處理準備清單時發生錯誤: {errorMessage}',
    },
    FEEDBACK_VALIDATION_ERROR: {
      title: '資料驗證失敗',
      content: '請檢查以下項目的資料: {errorDetails}',
    },
    FEEDBACK_PROCESS_SUCCESS_TITLE: '回饋表建立成功',
    FEEDBACK_PROCESS_FAILURE_TITLE: '回饋表建立失敗',
    FEEDBACK_PROCESS_SUCCESS_ITEM: '{simpleId} - {parentName} / {childName}',
    FUNC_INVALID_ARGUMENT: { title: '參數錯誤', content: '傳送的資料格式不正確。' },
    FUNC_NOT_FOUND: { title: '找不到資源', content: '找不到指定的資源。' },
    FUNC_ALREADY_EXISTS: { title: '資料已存在', content: '您試圖建立的資料已存在。' },
    FUNC_PERMISSION_DENIED: { title: '權限不足', content: '權限不足，無法執行此操作。' },
    FUNC_UNAUTHENTICATED: { title: '需要驗證', content: '請重新登入後再試。' },
    FUNC_RESOURCE_EXHAUSTED: { title: '系統繁忙', content: '系統目前繁忙，請稍後再試。' },
    FUNC_FAILED_PRECONDITION: { title: '條件不符', content: '操作條件不符，無法執行。' },
    FUNC_ABORTED: { title: '操作中止', content: '操作已被中止。' },
    FUNC_OUT_OF_RANGE: { title: '範圍錯誤', content: '數值超出允許範圍。' },
    FUNC_UNIMPLEMENTED: { title: '功能未實作', content: '此功能尚未實作。' },
    FUNC_INTERNAL: { title: '內部錯誤', content: '伺服器發生內部錯誤。' },
    // Shown when the request never reached the server at all (network dropped, or the
    // Cloud Run instance was still cold-starting and the CORS preflight was refused).
    // Deliberately does NOT claim the operation was cancelled — see the note in
    // useStandardizedErrors.mapFirebaseFunctionError for why that is unknowable.
    FUNC_CONNECTION_LOST: {
      title: '連線中斷',
      content: '無法連線至伺服器，操作可能未完成。請重新整理頁面確認後再試一次。',
    },
    FUNC_UNAVAILABLE: { title: '服務不可用', content: '服務暫時無法使用，請稍後再試。' },
    FUNC_DATA_LOSS: { title: '資料遺失', content: '操作過程中發生資料遺失。' },
    FUNC_DEADLINE_EXCEEDED: { title: '操作逾時', content: '操作時間過長，請稍後重試。' },
    AUTH_INVALID_EMAIL: { title: 'Email 無效', content: '電子郵件格式不正確。' },
    AUTH_USER_DISABLED: { title: '帳號已停用', content: '此帳號已被停用。' },
    AUTH_USER_NOT_FOUND: { title: '找不到帳號', content: '找不到此使用者帳號。' },
    AUTH_WRONG_PASSWORD: { title: '密碼錯誤', content: '您輸入的密碼不正確。' },
    AUTH_TOO_MANY_REQUESTS: { title: '嘗試次數過多', content: '偵測到異常活動，請稍後再試。' },
    AUTH_NETWORK_FAILED: { title: '網路錯誤', content: '網路連線失敗，請檢查您的網路設定。' },
    AUTH_GENERIC_FAILURE: { title: '登入失敗', content: '登入驗證失敗，請重試。' },

    // Timer (/tools/countdown) messages — see TIMER_CONFIG above.
    TIMER_START_FAILED: { title: '無法開始', content: '無法開始計時，請重試。' },
    TIMER_PAUSE_FAILED: { title: '無法暫停', content: '無法暫停計時，請重試。' },
    TIMER_RESUME_FAILED: { title: '無法繼續', content: '無法繼續計時，請重試。' },
    TIMER_STOP_FAILED: { title: '結束失敗', content: '結束計時失敗，請檢查網路。' },
    TIMER_TAKEOVER_FAILED: { title: '接管失敗', content: '無法接管控制權，請重試。' },
    TIMER_TAKEOVER_DISPLACED: {
      title: '控制權已被接管',
      content: '控制權已被 {name} 接管。',
    },
    TIMER_REMOTE_PAUSED: { title: '', content: '由 {name} 暫停' },
    TIMER_REMOTE_RESUMED: { title: '', content: '由 {name} 繼續' },
    TIMER_REMOTE_STARTED: { title: '', content: '由 {name} 開始計時' },
    TIMER_LAP_ADD_FAILED: { title: '紀錄失敗', content: '無法新增紀錄，請重試。' },
    TIMER_LAP_DELETE_FAILED: { title: '刪除失敗', content: '無法刪除紀錄，請重試。' },
    TIMER_LAPS_CLEAR_FAILED: { title: '清除失敗', content: '無法清除紀錄，請重試。' },
    TIMER_COLOR_SET_FAILED: { title: '設定失敗', content: '無法更新顯示顏色，請重試。' },
    TIMER_VISIBILITY_SET_FAILED: { title: '設定失敗', content: '無法更新分享設定，請重試。' },
    TIMER_LAP_DELETE_DENIED: {
      title: '權限不足',
      content: '您已不是控制者，無法刪除紀錄。',
    },
    TIMER_STOP_CONFIRM: {
      title: '結束計時',
      content: '這將永久結束此計時，無法復原。確定停止？',
    },
    TIMER_STOPPED_GRACE: {
      title: '',
      content: '已停止 — {seconds}秒內可撤銷',
    },
    TIMER_STOP_UNDONE: { title: '', content: '已還原計時' },
    TIMER_LAPS_COPIED: { title: '已複製', content: '紀錄已複製到剪貼簿。' },
    TIMER_URL_COPIED: { title: '已複製', content: '網址已複製到剪貼簿。' },
    TIMER_COPY_FAILED: {
      title: '複製失敗',
      content: '複製失敗，請手動選取文字。',
    },
    TIMER_PIN_REQUIRED: { title: '需要 PIN', content: '此計時為共用，請輸入 PIN 接管控制。' },
    TIMER_PIN_INCORRECT: { title: 'PIN 錯誤', content: 'PIN 錯誤，請重試。' },
    TIMER_PIN_INVALID: { title: 'PIN 格式錯誤', content: 'PIN 必須為 4 位數字。' },
    TIMER_ROSTER_SAVED: { title: '', content: '名單已儲存' },
    TIMER_ROSTER_FULL: {
      title: '名單已滿',
      content: '最多 200 位參與者，請先刪除再新增。',
    },
    TIMER_ROSTER_SAVE_FAILED: { title: '儲存失敗', content: '無法儲存名單，請重試。' },
    /**
     * Roster-edit validation errors. Content uses `{name}` / `{pos}` / `{max}`
     * placeholders so we can show the specific offending entry.
     */
    TIMER_ROSTER_DUPLICATE: {
      title: '',
      content: '名單中有重複的名字：{name}，請確認',
    },
    TIMER_ROSTER_EMPTY_LINE: {
      title: '',
      content: '名單第 {pos} 項為空白，請刪除再試',
    },
    TIMER_ROSTER_OVER_LIMIT: {
      title: '',
      content: '名單超過上限 {max} 位，請刪除多餘項目',
    },
    /**
     * Silent drawer-close rejection — fires when the user taps backdrop/Esc/X
     * while the roster draft has a validation error, so the user gets feedback
     * instead of a confusingly-ignored tap.
     */
    TIMER_ROSTER_CLOSE_BLOCKED: {
      title: '',
      content: '請先修正名單錯誤，或點「放棄編輯」',
    },
    TIMER_LAPS_LIMIT_REACHED: {
      title: '已達上限',
      content: '單一計時最多 500 筆紀錄，請清除後再新增。',
    },
    /** Lap-copy clipboard section headers + unlabeled-bucket label. */
    TIMER_COPY_HEADER_ORDERED: { title: '', content: '【完成順序】' },
    TIMER_COPY_HEADER_GROUPED: { title: '', content: '【依參與者】' },
    TIMER_COPY_UNLABELED: { title: '', content: '（未指定）' },

    // Timeslot peek drawer — not-found branch
    PEEK_TIMESLOT_NOT_FOUND: {
      title: '找不到此時段',
      content: '此時段已下架或 ID 已變更，無法開啟預覽。',
    },
    // Timeslot peek drawer — oldest tab auto-closed when the pin cap is reached.
    // {max} / {label} are interpolated at the call site.
    PEEK_TAB_LIMIT_REACHED: {
      title: '已達預覽上限',
      content: '最多同時釘選 {max} 個預覽，已自動關閉最舊的「{label}」。',
    },
  },
} as const;

// ==================================================================================
// CATEGORY: HELPER FUNCTIONS
// Description: Utility functions derived from configuration values.
// ==================================================================================

/**
 * Verdict of the 7 碼通匯代碼 check-digit rule (`FrontendConfigUtils.checkCode7`).
 *
 * - `ok`       —— 通過規則，或沒有代碼可核（空值）
 * - `mismatch` —— 格式正確但檢查碼與規則算出來的不同
 * - `invalid`  —— 規則明確判定不可能存在（非 7 位數字、0000000、郵局非法代碼）
 * - `unknown`  —— 該銀行已消滅／被併走，規則無從覆核（見 DEFUNCT_BANK_CODES）
 *
 * 全部僅供提示，任何一種都不擋存檔 —— 見 PAYMENT.BANK_CODE_CHECKDIGIT 的理由。
 */
export type BankCode7Check = 'ok' | 'mismatch' | 'invalid' | 'unknown';

/**
 * One 候補 type's offer rule — the shape of
 * FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.OFFER_RULES entries.
 * Read it through FrontendConfigUtils.waitlistOfferRule(), never inline.
 */
export interface WaitlistOfferRule {
  /** How far ahead we are willing to offer, in weeks from today. */
  readonly windowWeeks: number;
  /** How many dates go out at once. */
  readonly offerCount: number;
  /** true when the dates must form one course (體驗三堂). */
  readonly chained: boolean;
  /**
   * Max distance between two consecutive offered dates, in weeks.
   * 2 = one week may be skipped. Only meaningful when `chained`.
   */
  readonly maxGapWeeks: number;
}

/**
 * 2026-10-13 (續報・轉班 完成規則 W0.2, E-00 / D-01b) — the row states the shared
 * re-anchor rule (FrontendConfigUtils.renewalReanchorOutcome) reads: 一般 / 待通知 /
 * 已過期未通知 / 已通知·待確認 / 已通知·已逾期 / 約課通知待發 / 不續報 / 已轉出 /
 * 課務未通知 / 期限內未結案 / 暫時保留名額 / 請長假 / 轉出中 / 沒有紀錄·已結束; and since
 * 2026-10-13 (W1.4, E-00c) 其他 (結束狀態) = released-other (endingReason auto-ended /
 * timeslot-removed / bf-grace-expired / no-active-bookings).
 * The website maps its lifecycle state with FrontendConfigUtils.renewalRowStateOf. BE mirror
 * in functions appConfig.ts.
 */
export type RenewalRowState =
  | 'general'
  | 'pending-notify'
  | 'expired-unnotified'
  | 'notified-awaiting'
  | 'notified-overdue'
  | 'booking-notice-pending'
  | 'not-renewing'
  | 'transferred-out'
  | 'released-admin-unnotified'
  | 'released-expired'
  | 'released-other'
  | 'special-hold'
  | 'long-leave'
  | 'transferring'
  | 'none';

/**
 * 2026-10-13 (續報・轉班 完成規則 W1.4, E-00 / E-00c) — the website's lifecycle states
 * (useEnrollmentActions.ts classifyEnrollmentState), spelled out here because appConfig.ts must
 * not import a composable (useEnrollmentActions.ts imports FROM here). useEnrollmentActions.ts
 * EnrollmentState mirrors this list: its reservationActionKindOf call fails vue-tsc on a state
 * missing here, and config/reservationActionKind.test.ts walks every value.
 */
export type ReservationEnrollmentState =
  | 'no-enrollment'
  | 'active-clean'
  | 'renewal-notified'
  | 'auto-renewed'
  | 'renewal-success-pending'
  | 'soft-end-not-renewing'
  | 'soft-end-scan-a'
  | 'soft-end-scan-b'
  | 'soft-end-transferred'
  | 'soft-end-other'
  | 'special-hold'
  | 'transfer-awaiting'
  | 'transferring'
  | 'paused'
  | 'ended';

/** The dates FrontendConfigUtils.renewalRowStateOf reads (W1.4). Instants are epoch ms. */
export interface RenewalRowStateContext {
  /** 最後約課日 (lastSessionEndDate); null when the row has none. */
  lsedMs: number | null;
  /** 保留至 (reservationLockedUntil); null when there is no lock. */
  lockMs: number | null;
  nowMs: number;
}

/** What the 修改最後約課日 window does (P4: 結算 by fact; the entry selects the settle set, D60). */
export type RenewalReanchorKind = 'settle' | 'reanchor' | 'restore' | 'create';

/**
 * 2026-10-13 (W0.2, E-00 / D3 #16) — the button each state opens: seat held → 修改最後約課日
 * (reanchor); seat released or unconfirmed (incl. 已通知·已逾期, D3 #16) → 恢復保留名額
 * (restore); no record → 確認保留名額 (create). A Record, so a new state cannot be left out.
 */
export const RENEWAL_REANCHOR_KIND_BY_STATE: Readonly<
  Record<RenewalRowState, Exclude<RenewalReanchorKind, 'settle'>>
> = {
  general: 'reanchor',
  'pending-notify': 'reanchor',
  'expired-unnotified': 'reanchor',
  'notified-awaiting': 'reanchor',
  'booking-notice-pending': 'reanchor',
  transferring: 'reanchor',
  'notified-overdue': 'restore',
  'not-renewing': 'restore',
  'transferred-out': 'restore',
  'released-admin-unnotified': 'restore',
  'released-expired': 'restore',
  'released-other': 'restore',
  'special-hold': 'restore',
  'long-leave': 'restore',
  none: 'create',
};

/**
 * 2026-10-13 (W0.2, P4 + D9 (b)) — D-02's rows, the only ones where 結算 by fact applies.
 * 2026-10-24 (約課切斷確認 D60): the set 結算續報 (entry 'settle') settles by fact on —
 * settleStatesFor picks it. MIRROR of functions RENEWAL_SETTLE_STATES (check-config-mirror).
 */
export const RENEWAL_SETTLE_STATES: readonly RenewalRowState[] = [
  'general',
  'pending-notify',
  'expired-unnotified',
  'notified-awaiting',
  'notified-overdue',
  'released-admin-unnotified',
  'released-expired',
];

/**
 * 2026-10-15 (續報・轉班 完成規則 W10.8, D33 / D34) — PO: 「when a row already exist in 續報 or
 * transfer, it's better to keep it in one place」; 「待確認 shows those with new bookings under 8 …
 * it should be right to handle those in renewal or transfer page, instead of in 待確認」.
 * The states whose 續報 row already offers 結算續報 — 待通知 (pending-notify) ／ 已通知·待確認
 * (notified-awaiting) ／ 已過期未通知 (expired-unnotified) ／ 近期未處理 (notified-overdue,
 * released-admin-unnotified, released-expired): 待確認 does not list that student's flagged
 * 既有報名 row and no count includes it. Every RENEWAL_SETTLE_STATES entry but 一般.
 * 暫時保留名額 and 約課通知待發 rows stay listed (那些清單沒有完成的動作／不是在等約課); 請長假 and
 * 一般 (not yet in 續報 time) stay listed. MIRROR of functions LSED_REVIEW_DEFER_STATES
 * (check-config-mirror); both tiers run the shared fixture's `isLsedReviewDeferred` group
 * (test-excel/fixtures/renewal-rule-cases.json). The website reads it through utils/lsedReviewLink
 * (the 待確認 list, its counts and chips, the 續報 rows' 期間已約 N 堂).
 */
export const LSED_REVIEW_DEFER_STATES: readonly RenewalRowState[] = RENEWAL_SETTLE_STATES.filter(
  (s) => s !== 'general',
);

/** W10.8 (D33 / D34) — is a flagged 既有報名 row in `state` handled on its 續報 row instead of 待確認? */
export function isLsedReviewDeferred(state: RenewalRowState): boolean {
  return LSED_REVIEW_DEFER_STATES.includes(state);
}

/**
 * 2026-10-24 (約課切斷確認 D60 — PO 「7.A」: 「照事實記 結算續報 的狀態由按的鈕決定」) — the states
 * on which FrontendConfigUtils.renewalReanchorOutcome records 結算續報 by fact, chosen by the
 * button that opened the window (`entry`): none (修改最後約課日) → LSED_REVIEW_DEFER_STATES
 * (一般 not in it — D44); 'settle' (結算續報 / 家長留原班) → RENEWAL_SETTLE_STATES (一般
 * included); 'cutoffFix' (補正最後約課日) → never (D34). Supersedes P4's 「the entry never
 * changes the outcome」. A switch over ManualMarkEntry, so a new entry cannot be left out.
 * MIRROR of functions settleStatesFor (check-config-mirror); pinned by the shared fixture
 * (test-excel/fixtures/renewal-rule-cases.json, group renewalReanchorOutcome).
 */
export const settleStatesFor = (entry?: ManualMarkEntry): readonly RenewalRowState[] => {
  switch (entry) {
    case undefined:
      return LSED_REVIEW_DEFER_STATES;
    case 'settle':
      return RENEWAL_SETTLE_STATES;
    case 'cutoffFix':
      return [];
  }
};

/**
 * 2026-10-15 (續報・轉班 完成規則 B5, D43 — PO 「ok accept」; D46 R7) — after a click that handled a
 * 'pending' 3–7 row (近期未處理 › 已處理, 已過期未通知 › 已確認釋出名額, 不續報, 設為暫時保留名額) the
 * server keeps lsedReviewHandledDates = the row's lsedReviewBookingDates at that moment. The row
 * comes back to 待確認 only when a lesson was booked AFTER the click: true (left out) when every
 * booking date is a Taipei day the copy holds; false when one is not, or when there is no copy
 * (`handledDatesMs` null — handled before this rule, or never handled; R7: never infer which dates
 * were seen, D35 decides). Taipei calendar days, as detection's 忽略 evidence (hasUnseenEvidence).
 * Pure. MIRROR of functions isLsedReviewLeftOutAsHandled; both tiers run the shared fixture's
 * `lsedReviewRelistAfterHandled` group. The website reads it through utils/lsedReviewLink.
 */
export function isLsedReviewLeftOutAsHandled(
  bookingDatesMs: readonly number[],
  handledDatesMs: readonly number[] | null,
): boolean {
  if (handledDatesMs === null) return false;
  const dayMs = 24 * 60 * 60 * 1000;
  // Taipei is +08:00 all year (no DST): the Taipei calendar day number of an instant.
  const taipeiDay = (ms: number) => Math.floor((ms + 8 * 60 * 60 * 1000) / dayMs);
  const seen = new Set(handledDatesMs.map(taipeiDay));
  return bookingDatesMs.every((ms) => seen.has(taipeiDay(ms)));
}

/**
 * 2026-10-15 (續報・轉班 完成規則 W10.9, D38 — PO: an 已轉出 old class never appears in 待確認;
 * basis D36 scenario 1, 「they can opt to not click at all」): the 續報 states whose flagged
 * 既有報名 row is never a 待確認 row — not listed, not counted (the tab number, the bands, the
 * 排課時段表 「待確認 N」 chip) and no 「期間已約 N 堂」 points to 待確認. 已轉出 (確定轉入's soft-end,
 * status active + endingReason 轉班): its 3–7 is the temporary class the admin chose to ignore; 8+
 * re-enrolls it by itself (C-03). MIRROR of functions LSED_REVIEW_EXCLUDED_STATES
 * (check-config-mirror). The website reads it through utils/lsedReviewLink lsedReviewPlacementOf.
 */
export const LSED_REVIEW_EXCLUDED_STATES: readonly RenewalRowState[] = ['transferred-out'];

/**
 * 2026-10-24 (約課切斷確認 Spec 2, D33 + D45 — PO 「when customer 不續報, meaningless to fix 補正
 * anymore, so is 請長假 … yes on trasnfer out」, 「8.1 all ok agree」, 「10.4 OK」) — the row states
 * whose 約課切斷確認 row is not listed and not counted (the 待確認 tab number, 「顯示全部 (N)」, the
 * 排課時段表 「待確認 N」 chip), and on which the server refuses entry 'cutoffFix': 不續報, 請長假,
 * 已轉出, 課務未通知, 期限內未結案, 已通知·已逾期 and 名額已釋出 (系統結束). The flag stays stored;
 * 恢復保留名額 re-evaluates it. A 轉班保留 is refused by the hold predicate, not by this list.
 * MIRROR of functions LSED_CUTOFF_HIDDEN_STATES (check-config-mirror).
 */
export const LSED_CUTOFF_HIDDEN_STATES: readonly RenewalRowState[] = [
  'not-renewing',
  'long-leave',
  'transferred-out',
  'released-admin-unnotified',
  'released-expired',
  'notified-overdue',
  'released-other',
];

/**
 * 2026-10-24 (約課切斷確認 D39 / D50, N34) — 「 (約課切斷確認 的列除外)」: the exception clause the
 * 使用說明 ★ and ※ lines (UI_STRINGS.HUB.REVIEW.CUTOFF.EXPLAINER_STAR_PARENTHESIS /
 * EXPLAINER_IGNORED_PARENTHESIS) and the 已忽略 caption (RENEWAL_WORKFLOW_CONFIG.PAIR_REVIEW
 * .DISMISSED_SECTION_NOTE) carry — one home for the words (W5.4 FE-L7). Website-only.
 */
const LSED_CUTOFF_EXCEPT_PARENTHESIS = ' (約課切斷確認 的列除外)';

/**
 * 2026-10-24 (約課切斷確認 N16 / N19 (ii); here since N35 (iv)) — the row states whose
 * 補正最後約課日 step-2 list gets line 2 「續報 的日期以 M/D 算 (待通知 從 M/D 起)」 (the 續報
 * cycle restarts from the pick): 一般, 待通知, 已過期未通知. 暫時保留名額 has its own line
 * (§ 8.2 (a)); 轉出中 gets none. D69 / D70 (4) (annex § 一 / § 二.4 之後 — the facts the
 * 「● 確認後」 line no longer carries): 已通知·待確認 gets this line when the cycle restarts
 * (E-03 (a)) and 「名額保留：… 保留至 M/D」 when the notice is kept; 約課通知待發 gets the
 * 名額保留 line. Website-only (a display rule; no backend twin). Read by utils/cutoffFixLines.
 */
export const LSED_CUTOFF_FIX_CYCLE_LINE_STATES: readonly RenewalRowState[] = [
  'general',
  'pending-notify',
  'expired-unnotified',
];

/**
 * 2026-10-24 (約課切斷確認 D50 — mockup § 11.2 建議, PO 「11.2 ok」; W5.4 UX-H1 / N40) — the
 * listed row states whose 待確認 › 約課切斷確認 row carries a grey tag in 注意 naming the state
 * in the OTHER list (UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE words): 待通知, 已過期未通知,
 * 已通知·待確認, 約課通知待發, 暫時保留名額. 一般 gets none; 轉出中 gets none — a 轉班 row keeps
 * its stage words (「轉班 安排中」／「轉班 安排中 · 新班」, § 16 Q4 A), never a second tag. The
 * other states are never listed (LSED_CUTOFF_HIDDEN_STATES). Website-only (a display rule; no
 * backend twin). Read by utils/cutoffReviewView cutoffRowView.
 */
export const LSED_CUTOFF_STATE_TAG_STATES: readonly RenewalRowState[] = [
  'pending-notify',
  'expired-unnotified',
  'notified-awaiting',
  'booking-notice-pending',
  'special-hold',
];

/**
 * 2026-10-24 (約課切斷確認 W1.3 (a), Spec 5 / 6) — the context
 * FrontendConfigUtils.isCutoffFixDateAllowed reads. Instants are epoch ms. Mirror of functions
 * CutoffFixDateContext.
 */
export interface CutoffFixDateContext {
  /** The timeslot's weekday — 0 = 週日 … 6 = 週六, as utils/timeslotDisplay.ts parseTimeslotId. */
  weekday: number;
  /** The stored 最後約課日 (12:00 Taipei). */
  currentLsedMs: number;
  nowMs: number;
}

/**
 * 2026-10-31 (候補 預計開始上課日, D4 / D14) — the context
 * FrontendConfigUtils.isWaitlistStartDateAllowed reads. Weekdays are 0 = 週日 … 6 = 週六.
 * Mirror of functions programWaitlist/startDateRules.ts normalizeStartDateKey's day rules.
 */
export interface WaitlistStartDateContext {
  /**
   * The held class's weekday — the class chosen in the 保留名額 window, or the row's 正課 hold in
   * 編輯. null = no class yet, or its day is unreadable (no day passes — only 還沒確定 or the
   * stored date).
   */
  heldWeekday: number | null;
  /** Taipei 'YYYY-MM-DD' of today. */
  todayKey: string;
  /** 編輯: the hold's stored date — sent back unchanged, it is not checked again. */
  storedKey?: string | null;
}

/**
 * 2026-10-13 (續報・轉班 完成規則 W0.3, E-00 / D-01) — the button name and its one-line hover
 * per re-anchor kind. Keyed by RenewalReanchorKind (the plan's `…_BY_STATE` became `…_BY_KIND`
 * once W0.2's RENEWAL_REANCHOR_KIND_BY_STATE existed: state → kind there, kind → words here —
 * one table each). A row reads `RESERVATION_ACTION_LABEL_BY_KIND[RENEWAL_REANCHOR_KIND_BY_STATE
 * [state]]`; 結算續報 is its own button (hubUi HUB_MUTATING_ACTIONS.settleRenewal — the word's
 * home). A Record, so a new kind cannot be left without words. Read by the 排課時段表 button,
 * the 待確認 row and the 保留名額 window (W2.1 / W2.2 / W2.4); hubUi's confirmEnrollment / setLsed
 * carry no label — the rows pass these words (hubUi cannot import this file).
 */
export const RESERVATION_ACTION_LABEL_BY_KIND: Readonly<
  Record<RenewalReanchorKind, { label: string; title: string }>
> = {
  reanchor: {
    label: '修改最後約課日',
    title: '改日期，保留名額不變，續報週期照新日期重算',
  },
  restore: {
    label: '恢復保留名額',
    // Also shown on 已通知·已逾期 / 暫時保留名額 / 請長假 rows, which have no ending state — so the
    // words name the effect (E-00), not the row (W2.1 report).
    title: '用選的日期重新保留名額，續報週期重新開始',
  },
  create: {
    label: '確認保留名額',
    title: '建立這位學生在這一班的報名紀錄',
  },
  settle: {
    label: HUB_BUTTON_KIND.settleRenewal.label,
    title: '把 最後約課日 之後已約的課算成新的一期',
  },
};

/**
 * 2026-10-13 (W0.4, P4 / D-02) — the `entry` a manualMarkAsSessionEnrollment call may carry:
 * which button opened the window ('settle' = 結算續報, the last booking preselected). MIRROR of
 * MANUAL_MARK_ENTRIES in functions appConfig.ts (check-config-mirror).
 * 2026-10-24 (約課切斷確認 W1.1, D60): + 'cutoffFix' = 補正最後約課日 (待確認 › 約課切斷確認).
 * The entry selects the settle set (settleStatesFor, D60) — it no longer only rides along.
 */
export const MANUAL_MARK_ENTRIES = ['settle', 'cutoffFix'] as const;
export type ManualMarkEntry = (typeof MANUAL_MARK_ENTRIES)[number];

/**
 * 2026-10-24 (約課切斷確認 Spec 13, D47–D49 / D52 / D54–D56 / D62) — the page that created a
 * 約課通知待發 notice (通知 › 約課通知待發 › 來源), stored as bookingNoticeSource.page in the same
 * write that sets pendingRenewalSuccessNotifyAt: renewal = 續報 · transfer = 轉班 · new = 新學期 ·
 * review = 待確認 · timeslot = 排課時段表 — the 來源 filter's order; the labels are
 * UI_STRINGS.HUB.NOTIFY.SOURCE_LABEL. MIRROR of functions BOOKING_NOTICE_SOURCE_PAGES
 * (check-config-mirror).
 */
export const BOOKING_NOTICE_SOURCE_PAGES = [
  'renewal',
  'transfer',
  'new',
  'review',
  'timeslot',
] as const;
export type BookingNoticeSourcePage = (typeof BOOKING_NOTICE_SOURCE_PAGES)[number];

/**
 * 2026-10-13 (W0.3, E-00) — what UI_STRINGS.SET_RESERVATION.STATE_LINE is given; each state's
 * line reads only what its words name. contactMd = 首次通知 M/D; lockMd = 保留至 M/D; daysLeft =
 * 待通知's 剩 N 天.
 */
export interface RenewalStateLineArgs {
  contactMd: string;
  lockMd: string;
  daysLeft: number;
  /**
   * 2026-10-14 (W9.5 TP-01, spec G-10 D-04c): 已再提醒's M/D in this 續報 cycle (the row's
   * renewalRemindedAt at/after its 首次通知); null = not reminded — the line has no 已再提醒 part.
   */
  remindedMd: string | null;
}

/**
 * 2026-10-13 (W5.3, D6) — the 「續報：」 every 續報 state line of UI_STRINGS.SET_RESERVATION.STATE_LINE
 * starts with; the 登記轉班 warning (utils/registerRenewalWarning) puts its own title in its place.
 */
export const RENEWAL_STATE_LINE_PREFIX = '續報：';

/** W9.5 TP-01 (G-10 D-04c): a told row's 「，M/D 已再提醒」 part; '' = not reminded this cycle. */
const stateLineRemindedPart = (a: RenewalStateLineArgs): string =>
  a.remindedMd ? `，${a.remindedMd} 已再提醒` : '';

/** Input of FrontendConfigUtils.renewalReanchorOutcome (W0.2). Instants are epoch ms. */
export interface RenewalReanchorInput {
  state: RenewalRowState;
  /** The stored 最後約課日 (null when there is no record). */
  currentLsedMs: number | null;
  /** The date picked in the window. */
  pickedMs: number;
  nowMs: number;
  /** 首次通知 (renewalContactedAt). Carried for the callers; no rule reads it. */
  contactedMs: number | null;
  /** 告知日期 (renewalNoticeDeadline). */
  toldMs: number | null;
  /** 保留至 (reservationLockedUntil) before the write. */
  currentLockMs: number | null;
  /** lastBookingSuccessNotifiedLsed — the date the parent was last told by a 約課通知. */
  lastNotifiedLsedMs: number | null;
  /** Distinct future 學期 booking days after the stored 最後約課日 up to the picked date. */
  postLsedBookingDays: number;
  /** Which button opened the window — the entry selects the settle set (settleStatesFor, D60). */
  entry?: ManualMarkEntry;
}

/** Output of FrontendConfigUtils.renewalReanchorOutcome (W0.2) — K-01 ①–⑤. */
export interface RenewalReanchorOutcome {
  kind: RenewalReanchorKind;
  /**
   * 2026-10-24 (約課切斷確認 R2-A-05 / R2-B-05, D35 / D50): true only for 補正最後約課日 (entry
   * 'cutoffFix') on 暫時保留名額 (special-hold) or 轉出中 (transferring) — the write changes
   * 最後約課日 alone: the hold / the 轉班 stays, the lock is untouched, no 約課通知待發. false on
   * every other outcome.
   */
  dateOnly: boolean;
  /** E-03 (b): the row stays 已通知·待確認 (續報週期維持). */
  keepsNotice: boolean;
  /** 保留至 after the write (null = no lock). */
  lockMs: number | null;
  /** E-03 (c): the new lock is on an earlier Taipei day than the told date (red line). */
  lockEarlierThanTold: boolean;
  /** K-01 ③: the instant the row enters 待通知 (null when the cycle is kept / 轉出中). */
  pendingListDayMs: number | null;
  /** K-01 ④ / P5: listed on 約課通知待發. */
  flagsBookingNotice: boolean;
  /** K-01 ⑤: 結算 with fewer than AUTO_TAG_THRESHOLD booking days (「只有 N 堂」). */
  subThreshold: boolean;
}

/** Input of FrontendConfigUtils.renewalReminderDue (W0.2, P6). Instants are epoch ms. */
export interface RenewalReminderDueInput {
  /** 首次通知 (renewalContactedAt). */
  contactMs: number | null;
  /** 告知日期 (renewalNoticeDeadline). */
  toldMs: number | null;
  /** 保留至 (reservationLockedUntil). */
  lockMs: number | null;
  /** renewalLockMovedAt — the lock was recomputed without a new notice (P6). */
  movedAtMs: number | null;
  /** 已再提醒 (renewalRemindedAt). */
  remindedAtMs: number | null;
  nowMs: number;
}

/** Output of FrontendConfigUtils.renewalReminderDue (W0.2, P6 / F-03). */
export interface RenewalReminderDue {
  /** R's Taipei day-start, or null (no cycle / no lock / span under the minimum). */
  reminderDayMs: number | null;
  /** Taipei calendar days from now to R (0 on R, negative after). */
  daysUntilReminder: number | null;
  /** Counted in 「今天要再次提醒 N 位」 and the 10:00 LINE. */
  counted: boolean;
  reason: 'lock-earlier-than-told' | 'reminder-day' | null;
  /** A 已再提醒 stamp exists at/after this cycle's 首次通知. */
  remindedThisCycle: boolean;
}

export class FrontendConfigUtils {
  /**
   * Strip the trailing " (週末)/(平日)" day marker + trim — mirrors the backend
   * SSOT `normalizeClassName` (functions/src/utils/timeSlotUtils.ts) so FE-side
   * comparisons (e.g. the mapping form's self-map guard) agree with what the
   * server will actually store (AR2-14, 2026-07-11).
   */
  static normalizeClassName(raw: string | null | undefined): string {
    return (raw ?? '').replace(/\s*[(（](?:週末|平日)[)）]\s*$/u, '').trim();
  }

  /**
   * Convert a full class name into its established shortform.
   *
   *   親子游泳【1.5-2.5y/基礎探索】小青蛙班 → 小青蛙班
   *   獨立游泳【5-8y/泳姿進階】小熊班      → 小熊班
   *   FMS體能課【1.8-3y/技能探索】親子班   → FMS親子班
   *
   * Fallback: if the class doesn't match a known prefix or has no `】`
   * separator, return the full name unchanged. The `GROUP_ORDER` whitelist
   * (line ~738) is the canonical list of expected shortforms but the
   * runtime split works for any class following the `主題【描述】短名`
   * pattern, so new classes get sensible shortforms automatically.
   */
  static classShortName(fullName: string | null | undefined): string {
    if (!fullName) return '';
    const trimmed = fullName.trim();
    const FMS_PREFIX = 'FMS體能課';
    if (trimmed.startsWith(FMS_PREFIX)) {
      const after = trimmed.split('】').pop()?.trim();
      return after ? `FMS${after}` : trimmed;
    }
    if (trimmed.startsWith('親子游泳') || trimmed.startsWith('獨立游泳')) {
      return trimmed.split('】').pop()?.trim() || trimmed;
    }
    return trimmed;
  }

  /**
   * Resolve a class's PRICING TIER from its full name (or the className
   * component of a timeslotId). Tier — not shortform — is the pricing key, so
   * renames within a family (e.g. 小海象班 → 小海象一班/二班) never break the price.
   *  - 親子游泳 / 獨立游泳 (all shortforms incl. *協同) → 'SWIM'
   *  - FMS體能課 親子班 → 'FMS_PARENT'
   *  - FMS體能課 獨立班 → 'FMS_INDEPENDENT'
   *  - anything else / unparseable → null (caller falls back to 【金額】)
   */
  static classPricingTier(
    fullName: string | null | undefined,
  ): 'SWIM' | 'FMS_PARENT' | 'FMS_INDEPENDENT' | null {
    if (!fullName) return null;
    const trimmed = fullName.trim();
    if (trimmed.startsWith('親子游泳') || trimmed.startsWith('獨立游泳')) return 'SWIM';
    if (trimmed.startsWith('FMS體能課')) {
      // FMS親子班 → parent tier; FMS獨立一/二/三班 → independent tier.
      return this.classShortName(trimmed).includes('親子') ? 'FMS_PARENT' : 'FMS_INDEPENDENT';
    }
    return null;
  }

  /**
   * 續報通知／轉班繳費: which central-list 學期 plan a timeslot's message quotes
   * (續報 M2 T18a, plan D-1, review H2) = classPricingTier(className) × weekend
   * (weekday 0 = Sun / 6 = Sat in the id `weekday_HH:MM_className_teachers`).
   * Swim 親子/獨立 share one price, so the 親子/獨立 split is FMS-only. A class with
   * no tier, or an unparseable id → null (the message shows 【金額】). Never the
   * binary planCategoryForClassName — that would quote every non-FMS class the
   * swimming price.
   */
  /**
   * 2026-09-25 (續報 review F-12 / L-7) — the four 學期 plans (TERM_PLAN_IDS) a saved price
   * list must keep: every 續報通知 / 延長 / 轉班繳費 / 候補 推薦訊息 of that tier reads its
   * price by id. A re-added plan gets a new id, so only these ids count. The price editor
   * disables their 刪; the backend (admin-saveSignupPricing) refuses a list without them.
   */
  static isTermPlanId(id: string): boolean {
    return (Object.values(TERM_PLAN_IDS) as string[]).includes(id);
  }

  /** The TERM_PLAN_IDS missing from a price list (empty = the list may be saved). */
  static missingTermPlanIds(planIds: readonly string[]): string[] {
    return Object.values(TERM_PLAN_IDS).filter((id) => !planIds.includes(id));
  }

  static termPlanIdForTimeslot(timeslotId: string | null | undefined): string | null {
    const parts = (timeslotId ?? '').split('_');
    if (parts.length < 4) return null;
    const weekday = parseInt(parts[0], 10);
    if (isNaN(weekday) || weekday < 0 || weekday > 6) return null;
    const weekend = weekday === 0 || weekday === 6;
    switch (this.classPricingTier(parts[2])) {
      case 'SWIM':
        return weekend ? TERM_PLAN_IDS.SWIM_WEEKEND : TERM_PLAN_IDS.SWIM_WEEKDAY;
      case 'FMS_PARENT':
        return TERM_PLAN_IDS.FMS_PARENT;
      case 'FMS_INDEPENDENT':
        return TERM_PLAN_IDS.FMS_SOLO;
      default:
        return null;
    }
  }

  /**
   * Look up the representative emoji for a class shortform (single source of
   * truth: BUSINESS_RULES.PROGRAM_WAITLIST.SHORTFORM_EMOJI).
   * Returns '' for shortforms with no mapping (custom classes, future
   * additions before the map is updated, etc.) so callers can render
   * unconditionally without a v-if.
   */
  static classShortNameEmoji(shortform: string | null | undefined): string {
    if (!shortform) return '';
    return FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.SHORTFORM_EMOJI[shortform] ?? '';
  }

  /**
   * Convenience helper: full class name → "🦫 小水狸班" (emoji + space + shortform).
   * Falls back to bare shortform when no emoji is mapped. Use this anywhere a
   * class shortform is rendered to admin so the visual treatment is consistent
   * across all surfaces (browse panel, renewal table, transfer table, etc.).
   */
  static classShortNameWithEmoji(fullName: string | null | undefined): string {
    const sf = this.classShortName(fullName);
    if (!sf) return '';
    const emoji = this.classShortNameEmoji(sf);
    return emoji ? `${emoji} ${sf}` : sf;
  }

  /**
   * 2026-06-05: Renewal-window countdown for the 轉班 tab 新建議 rows. Given a
   * student's LSED (ms), returns where "today" sits in the renewal lifecycle so
   * a teacher-suggestion card can show how long until 續報 begins:
   *
   *   pre  — now < LSED − RENEWAL_REMINDER_DAYS  (window not yet open; parked)
   *   open — LSED − REMINDER_DAYS ≤ now < LSED − SYSTEM_CAP_DAYS  (續報 active now)
   *   past — now ≥ LSED − SYSTEM_CAP_DAYS  (past the auto-archive cap)
   *
   * Single source for the window math so the 轉班 card agrees with the 學期續報
   * tab. Pass `nowMs` explicitly (from useStandardizedTimestamps.getNow) — never
   * read the clock here. Returns null when LSED is missing.
   */
  static renewalWindowCountdown(
    lsedMs: number | null | undefined,
    nowMs: number,
  ): {
    phase: 'pre' | 'open' | 'past';
    daysToOpen: number;
    daysToCap: number;
    windowOpenMs: number;
    capMs: number;
  } | null {
    if (lsedMs == null || !Number.isFinite(lsedMs)) return null;
    const dayMs = 24 * 60 * 60 * 1000;
    // 2026-10-13 (W0.2): the window opening is the shared 「在不在續報時間」 (E-03), not a copy.
    const windowOpenMs = FrontendConfigUtils.renewalWindowOpenMs(lsedMs);
    const capMs = lsedMs - SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS * dayMs;
    const daysToOpen = Math.ceil((windowOpenMs - nowMs) / dayMs);
    const daysToCap = Math.ceil((capMs - nowMs) / dayMs);
    const phase: 'pre' | 'open' | 'past' = !FrontendConfigUtils.isInRenewalTime(lsedMs, nowMs)
      ? 'pre'
      : nowMs < capMs
        ? 'open'
        : 'past';
    return { phase, daysToOpen, daysToCap, windowOpenMs, capMs };
  }

  /**
   * 2026-06-24 (runway redesign §2a / Messaging W1) — FE mirror of the backend
   * `computeRenewalLockMs` (functions/src/config/appConfig.ts). The SINGLE renewal
   * seat-trust-lock formula:
   *
   *   reservationLockedUntil = min(LSED, max(LSED − RENEWAL_SYSTEM_CAP_DAYS, today + RENEWAL_MIN_RUNWAY_DAYS))
   *
   * Three regimes (CAP = 14, RUNWAY = 10 since 2026-09-24):
   *   • EARLY  (today ≤ LSED−24d) → LSED−14d   (14-day re-marketing/waitlist window intact)
   *   • LATE   (LSED−24d < today, today+10 < LSED) → today+10   (the parent gets 10 days)
   *   • V.LATE (today+10 ≥ LSED)  → LSED   (LSED is the hard ceiling)
   *
   * Pure function — pass `nowMs` explicitly (from useStandardizedTimestamps.getNow),
   * never read the clock here. FE result MUST equal the BE helper for the same inputs.
   */
  static computeRenewalLockMs(lsedMs: number, nowMs: number): number {
    const dayMs = 24 * 60 * 60 * 1000;
    const capMs = SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS * dayMs;
    const runwayMs = SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS * dayMs;
    return Math.min(lsedMs, Math.max(lsedMs - capMs, nowMs + runwayMs));
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.2, E-03 / D9 (c)) — the instant a row enters
   * 待通知: 最後約課日 − RENEWAL_REMINDER_DAYS (ms arithmetic; Taiwan has no DST).
   * BE mirror renewalWindowOpenMs; pinned by test-excel/fixtures/renewal-rule-cases.json.
   */
  static renewalWindowOpenMs(lsedMs: number): number {
    return lsedMs - SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_DAYS * 24 * 60 * 60 * 1000;
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.2, E-03 / D9 (c)) — 「在不在續報時間」: true once
   * now ≥ 最後約課日 − RENEWAL_REMINDER_DAYS (and after 最後約課日 too). The definition for
   * the 續報 window helpers (D9 (c)): isInRenewalNotifyWindow and renewalWindowCountdown read
   * it. A non-finite 最後約課日 → false. Pure (nowMs is an input). BE mirror isInRenewalTime
   * (parity fixture).
   */
  static isInRenewalTime(lsedMs: number, nowMs: number): boolean {
    if (!Number.isFinite(lsedMs)) return false;
    return nowMs >= FrontendConfigUtils.renewalWindowOpenMs(lsedMs);
  }

  /**
   * 2026-09-02 (通知期限對齊 — PO decision): the renewal deadline QUOTED TO THE
   * PARENT in the 通知續報 / 延長並通知續報 message IS the seat-lock date. One date,
   * one clock: what the parent is told = what the system enforces = what the
   * 名額保留期限 column counts down to. Replaces the old public formula
   * clamp(today+7, LSED−21d, LSED−14d), which told parents a date up to a week
   * EARLIER than the seat actually released (three different dates on one row).
   * The backend persists the value as `renewalNoticeDeadline` at notify time.
   */
  static renewalNoticeDeadlineMs(lsedMs: number, nowMs: number): number {
    return FrontendConfigUtils.computeRenewalLockMs(lsedMs, nowMs);
  }

  /**
   * 2026-09-02 — resolve the deadline the parent was told, for DISPLAY.
   *   1. `renewalNoticeDeadline` when the row has it (notified after 2026-09-02).
   *   2. Legacy rows (notified before the field existed): re-derive the OLD public
   *      formula from the notify date — clamp(contactedAt + RENEWAL_MIN_RESPONSE_DAYS,
   *      LSED − RENEWAL_PUBLIC_DEADLINE_DAYS, LSED − RENEWAL_SYSTEM_CAP_DAYS). Exact
   *      unless the admin edited the date before copying the message.
   *   3. null when neither is derivable (never notified / no LSED).
   */
  static resolveRenewalNoticeDeadlineMs(
    storedMs: number | null,
    contactedAtMs: number | null,
    lsedMs: number | null,
  ): number | null {
    if (storedMs !== null && Number.isFinite(storedMs)) return storedMs;
    if (contactedAtMs === null || lsedMs === null) return null;
    const dayMs = 24 * 60 * 60 * 1000;
    const floorMs = contactedAtMs + SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RESPONSE_DAYS * dayMs;
    const publicMs = lsedMs - SESSION_ENROLLMENT_CONFIG.RENEWAL_PUBLIC_DEADLINE_DAYS * dayMs;
    const capMs = lsedMs - SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS * dayMs;
    return Math.min(Math.max(floorMs, publicMs), capMs);
  }

  /**
   * 2026-09-02 — how many days a late notice pushed the seat release PAST the
   * standard LSED − RENEWAL_SYSTEM_CAP_DAYS point (0 when on time). Drives the ⏱
   * 較晚通知 icon in the 名額保留期限 cell. Pure arithmetic on stored fields.
   */
  static lateNoticeExtensionDays(lsedMs: number, lockMs: number): number {
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.max(
      0,
      Math.round((lockMs - FrontendConfigUtils.renewalStandardReleaseMs(lsedMs)) / dayMs),
    );
  }

  /**
   * 2026-09-02 — the STANDARD (on-time) seat-release instant, LSED − RENEWAL_SYSTEM_CAP_DAYS.
   * The single anchor the ⏱ 較晚通知 hover compares the actual lock against.
   */
  static renewalStandardReleaseMs(lsedMs: number): number {
    return lsedMs - SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS * 24 * 60 * 60 * 1000;
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.2, G-10 / G-12) — the date a 轉出中 source's seat is
   * held to: with both a 保留至 and a 最後約課日, the LATER of the kept 保留至 and the on-time
   * release point 最後約課日 − RENEWAL_SYSTEM_CAP_DAYS (W9.6 P5-02 (a), D24 — a date-only
   * 修改最後約課日 that moved 最後約課日 later keeps the told 保留至, and every 保留名額 count
   * holds the seat to the later day, so the label says that day); only one → that one; neither
   * → null. Pure. BE mirror transferSourceHoldUntilMs.
   */
  static transferSourceHoldUntilMs(lockMs: number | null, lsedMs: number | null): number | null {
    const standardMs =
      lsedMs !== null ? FrontendConfigUtils.renewalStandardReleaseMs(lsedMs) : null;
    if (lockMs !== null && standardMs !== null) return Math.max(lockMs, standardMs);
    return lockMs ?? standardMs;
  }

  /**
   * 2026-09-02 — whole Taipei calendar days from `nowMs` to `targetMs` (negative
   * when the target day has passed, 0 on the day itself). Both instants are
   * floored to their Taipei day-start first, so a 11:33 deadline still reads
   * 「剩 0 天」 for the whole of that day — never "1 day" in the morning and
   * "0" in the afternoon. CLAUDE.md §6 timezone-safe (no bare local getters).
   */
  static taipeiCalendarDaysUntil(targetMs: number, nowMs: number): number {
    const dayMs = 24 * 60 * 60 * 1000;
    return Math.round(
      (this.floorToTaipeiDayStartMs(targetMs) - this.floorToTaipeiDayStartMs(nowMs)) / dayMs,
    );
  }

  /**
   * 2026-09-24 (續報 期限與提醒 M1, D-4) — Taipei calendar days from `nowMs` until an
   * un-notified row's on-time release point LSED − RENEWAL_SYSTEM_CAP_DAYS (negative
   * once that day has passed). The ONE count behind the 待通知 剩 N 天, its zone
   * colour, the topbar dot and (BE mirror) the digest S2.
   */
  static renewalReleaseDaysRemaining(lsedMs: number, nowMs: number): number {
    return FrontendConfigUtils.taipeiCalendarDaysUntil(
      FrontendConfigUtils.renewalStandardReleaseMs(lsedMs),
      nowMs,
    );
  }

  /**
   * 2026-09-24 (續報 期限與提醒 M1, D-4) — the 待通知 heat-map zone:
   *   released  the release instant has passed (matches the 通知續報 gate)
   *   red       fewer than RENEWAL_LASTCHANCE_DAYS calendar days left → LSED−23 onward,
   *             the same day isLateRenewalNotice turns on (one boundary for the colour
   *             and the ⏱ line)
   *   amber     fewer than RENEWAL_IDEAL_WINDOW_DAYS left (LSED−27 → LSED−24)
   *   green     otherwise
   */
  static renewalPendingZone(lsedMs: number, nowMs: number): 'green' | 'amber' | 'red' | 'released' {
    if (nowMs >= FrontendConfigUtils.renewalStandardReleaseMs(lsedMs)) return 'released';
    const days = FrontendConfigUtils.renewalReleaseDaysRemaining(lsedMs, nowMs);
    if (days < SESSION_ENROLLMENT_CONFIG.RENEWAL_LASTCHANCE_DAYS) return 'red';
    if (days < SESSION_ENROLLMENT_CONFIG.RENEWAL_IDEAL_WINDOW_DAYS) return 'amber';
    return 'green';
  }

  /**
   * 2026-09-24 (續報 期限與提醒 M1, PO Q4) — true once an un-notified row has reached the
   * red-zone start (LSED−23 00:00; stays true after the release point). Topbar dot;
   * BE mirror isPastRenewalRedZoneStart feeds the digest S2.
   */
  static isPastRenewalRedZoneStart(lsedMs: number, nowMs: number): boolean {
    return (
      FrontendConfigUtils.renewalReleaseDaysRemaining(lsedMs, nowMs) <
      SESSION_ENROLLMENT_CONFIG.RENEWAL_LASTCHANCE_DAYS
    );
  }

  /**
   * 2026-09-25 (review F-15 / L-14) — 延長並通知續報 is offered only while the last class
   * day is still ahead (Taipei calendar days > 0). On that day its message would ask the
   * parent to finish 「在 {today} 前」. BE mirror isRenewalExtendOpen (core.ts refuses).
   */
  static isRenewalExtendOpen(lsedMs: number, nowMs: number): boolean {
    return FrontendConfigUtils.taipeiCalendarDaysUntil(lsedMs, nowMs) > 0;
  }

  /**
   * 2026-09-29 (期壬, PO option A) — an 已過期未通知 row whose seat is STILL held: a live
   * reservation lock covers now (e.g. 設定最後約課日 locked the seat past the notice
   * deadline; the nightly release skips a locked seat). Such a row reads 「名額保留至 M/D」
   * and is NOT offered 已確認釋出名額 until the lock passes — 課務總覽 (renewalPendingRowView)
   * and 排課時段表 (StudentLifecycleStatusCell) alike. No lock, or a passed one = released.
   * The same boundary as the row's seat line (held through the lock instant itself).
   */
  static isLapsedSeatStillHeld(lockMs: number | null, nowMs: number): boolean {
    return lockMs !== null && nowMs <= lockMs;
  }

  /**
   * 2026-09-25 (續報 期限與提醒 M3 T24, A10 / PO Q3) — the 再次提醒家長 day:
   *
   *   R = dayKey(firstContact) + floor(c × RENEWAL_REMINDER_AT_FRACTION) days,
   *   c = taipeiCalendarDaysUntil(told, firstContact)
   *
   * Calendar days, not a halving of milliseconds — the two differ when the told
   * date's time of day is earlier than the contact's. Returns R's Taipei day-start
   * (00:00 +08:00), or null when c < RENEWAL_REMINDER_MIN_SPAN_DAYS (the parent had
   * at most 5 days at notice → no reminder). `toldMs` = renewalNoticeDeadline ??
   * reservationLockedUntil (A9) — never resolveRenewalNoticeDeadlineMs, which re-derives
   * the pre-2026-09-02 formula from the CURRENT LSED. Pure: no clock is read here.
   */
  static renewalReminderDayMs(firstContactMs: number, toldMs: number): number | null {
    if (!Number.isFinite(firstContactMs) || !Number.isFinite(toldMs)) return null;
    const span = FrontendConfigUtils.taipeiCalendarDaysUntil(toldMs, firstContactMs);
    if (span < SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_MIN_SPAN_DAYS) return null;
    const offsetDays = Math.floor(span * SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_AT_FRACTION);
    // Taiwan observes no DST, so a calendar day is exactly 24 h (CLAUDE.md §6).
    return (
      FrontendConfigUtils.floorToTaipeiDayStartMs(firstContactMs) + offsetDays * 24 * 60 * 60 * 1000
    );
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.2, P6 / F-01 / F-03 / E-03 (c) / D9 (a)) — the
   * 再次提醒家長 day and whether the row is counted in 「今天要再次提醒 N 位」 / the LINE.
   *   R = renewalReminderDayMs(首次通知, 保留至) — basis is the LOCK (= the told date on an
   *       unmoved row); null under RENEWAL_REMINDER_MIN_SPAN_DAYS.
   *   Counted only while the lock is live, when (i) the lock is on an earlier Taipei day
   *   than the told date (E-03 (c), at once, no stamp check — the mark rewrites the told
   *   date to the lock), else (ii) R has come, no 已再提醒 at/after the basis (the move day
   *   on a moved row, else 首次通知 — D9 (a)), and on a moved row R is on/after the move
   *   day (F-03 「新中點已過 → 不列入」). Pure. BE mirror renewalReminderDue (parity fixture).
   */
  static renewalReminderDue(input: RenewalReminderDueInput): RenewalReminderDue {
    const { contactMs, toldMs, lockMs, movedAtMs, remindedAtMs, nowMs } = input;
    if (contactMs === null) {
      return {
        reminderDayMs: null,
        daysUntilReminder: null,
        counted: false,
        reason: null,
        remindedThisCycle: false,
      };
    }
    const dayStart = (ms: number) => FrontendConfigUtils.floorToTaipeiDayStartMs(ms);
    const reminderDayMs =
      lockMs === null ? null : FrontendConfigUtils.renewalReminderDayMs(contactMs, lockMs);
    const base = {
      reminderDayMs,
      daysUntilReminder:
        reminderDayMs === null
          ? null
          : FrontendConfigUtils.taipeiCalendarDaysUntil(reminderDayMs, nowMs),
      remindedThisCycle: remindedAtMs !== null && remindedAtMs >= contactMs,
    };
    if (lockMs === null || lockMs <= nowMs) return { ...base, counted: false, reason: null };
    if (toldMs !== null && dayStart(lockMs) < dayStart(toldMs)) {
      return { ...base, counted: true, reason: 'lock-earlier-than-told' };
    }
    if (reminderDayMs === null) return { ...base, counted: false, reason: null };
    const basisMs = Math.max(contactMs, movedAtMs ?? contactMs);
    const counted =
      FrontendConfigUtils.taipeiCalendarDaysUntil(reminderDayMs, nowMs) <= 0 &&
      !(remindedAtMs !== null && remindedAtMs >= basisMs) &&
      (movedAtMs === null || reminderDayMs >= dayStart(movedAtMs));
    return { ...base, counted, reason: counted ? 'reminder-day' : null };
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.2, P4 / P5 / D-07 / E-03…E-06 / K-01 / D9 (b)(d)) —
   * what 修改最後約課日 / 恢復保留名額 / 確認保留名額 / 結算續報 does to a row; the server
   * writes from it, the window previews it (K-01 ①–⑤).
   *   kind: by state (RENEWAL_REANCHOR_KIND_BY_STATE), then 結算 by fact on D-02's rows —
   *     picked > the stored 最後約課日 with ≥ LSED_REVIEW_MIN_COUNT booking days beyond.
   *   keepsNotice (E-03 (b), D-07): 已通知·待確認 and the picked date is in renewal time.
   *   lockMs: recomputed when the notice is kept and on 約課通知待發 (manual.ts writes
   *     computeRenewalLockMs there); 轉出中 and a date-only 修改 on a never-notified held
   *     row keep the lock as it is (E-04, D9 (e)); else none.
   *   flagsBookingNotice (P5): never when the notice is kept; 約課通知待發 stays listed;
   *     轉出中 never; a never-told 修改 (no 約課通知 at all) not (E-04); otherwise listed
   *     unless the parent was told this exact date (Q4, exact ms — D9 (d)).
   * 2026-10-24 (約課切斷確認 D60): the entry selects the settle set (settleStatesFor) — none →
   * LSED_REVIEW_DEFER_STATES, 'settle' → RENEWAL_SETTLE_STATES, 'cutoffFix' → never (D34).
   * R2-A-05 / R2-B-05 (D35 / D50): 'cutoffFix' on special-hold / transferring is a date-only 修改
   * — kind reanchor, dateOnly, lock untouched, not listed, no 待通知 day. Every other outcome
   * carries dateOnly false. Pure. BE mirror renewalReanchorOutcome.
   */
  static renewalReanchorOutcome(input: RenewalReanchorInput): RenewalReanchorOutcome {
    const { state, currentLsedMs, pickedMs, nowMs, toldMs, currentLockMs } = input;
    const { lastNotifiedLsedMs, postLsedBookingDays, entry } = input;
    const cfg = SESSION_ENROLLMENT_CONFIG;
    const dayStart = (ms: number) => FrontendConfigUtils.floorToTaipeiDayStartMs(ms);
    if (entry === 'cutoffFix' && (state === 'special-hold' || state === 'transferring')) {
      // D35: the 暫時保留名額 stays; D50: the 轉班 stays (its flow owns its 約課通知).
      return {
        kind: 'reanchor',
        dateOnly: true,
        keepsNotice: false,
        lockMs: currentLockMs,
        lockEarlierThanTold: false,
        pendingListDayMs: null,
        flagsBookingNotice: false,
        subThreshold: false,
      };
    }
    const settles =
      settleStatesFor(entry).includes(state) &&
      currentLsedMs !== null &&
      pickedMs > currentLsedMs &&
      postLsedBookingDays >= cfg.LSED_REVIEW_MIN_COUNT;
    const kind: RenewalReanchorKind = settles ? 'settle' : RENEWAL_REANCHOR_KIND_BY_STATE[state];
    const keepsNotice =
      state === 'notified-awaiting' && FrontendConfigUtils.isInRenewalTime(pickedMs, nowMs);
    // D9 (e): a date-only 修改 on a never-notified held row (E-04 「不清除任何東西」) and
    // 轉出中 leave the lock as it is; 結算 / 恢復 / 確認 and a dropped notice clear it.
    const keepsLock =
      state === 'transferring' ||
      (kind === 'reanchor' &&
        (state === 'general' || state === 'pending-notify' || state === 'expired-unnotified'));
    const lockMs =
      keepsNotice || state === 'booking-notice-pending'
        ? FrontendConfigUtils.computeRenewalLockMs(pickedMs, nowMs)
        : keepsLock
          ? currentLockMs
          : null;
    // P5 (ii): listed unless the parent already holds this exact date (Q4, D9 (d)).
    const toldThisDate = lastNotifiedLsedMs !== null && lastNotifiedLsedMs === pickedMs;
    let flagsBookingNotice = !toldThisDate;
    if (keepsNotice || state === 'transferring') {
      // P5 (i) the notice is kept; 轉出中: the 轉班 flow owns its 約課通知 (manual.ts).
      flagsBookingNotice = false;
    } else if (state === 'booking-notice-pending') {
      // E-06: the row stays listed; its 30-day count restarts.
      flagsBookingNotice = true;
    } else if (kind === 'reanchor' && state !== 'notified-awaiting') {
      // E-04 / P5 (iii): a never-notified 修改 lists only when a 約課通知 was sent before.
      flagsBookingNotice = lastNotifiedLsedMs !== null && !toldThisDate;
    }
    return {
      kind,
      dateOnly: false,
      keepsNotice,
      lockMs,
      lockEarlierThanTold:
        keepsNotice && toldMs !== null && lockMs !== null && dayStart(lockMs) < dayStart(toldMs),
      pendingListDayMs:
        keepsNotice || state === 'transferring'
          ? null
          : FrontendConfigUtils.renewalWindowOpenMs(pickedMs),
      flagsBookingNotice,
      subThreshold: kind === 'settle' && postLsedBookingDays < cfg.AUTO_TAG_THRESHOLD,
    };
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W1.4, E-00 / E-00c / D3 #16 / P12) — the website's lifecycle
   * state → the row state the re-anchor rule reads; the SAME table the server builds (W1.1).
   * 已通知 splits on the lock: live → 已通知·待確認, passed → 已通知·已逾期 (seat released, D3 #16).
   * 進行中 / 自動續報 split on the 最後約課日: before renewal time → 一般; up to the on-time
   * release (最後約課日 − RENEWAL_SYSTEM_CAP_DAYS, inclusive) → 待通知; later → 已過期未通知.
   * 請長假 (paused) → long-leave (恢復保留名額, P12 (b)). Pure (nowMs is an input).
   */
  static renewalRowStateOf(
    state: ReservationEnrollmentState,
    ctx: RenewalRowStateContext,
  ): RenewalRowState {
    switch (state) {
      case 'no-enrollment':
      case 'ended':
        return 'none';
      case 'special-hold':
        return 'special-hold';
      case 'paused':
        return 'long-leave';
      case 'transferring':
      case 'transfer-awaiting':
        return 'transferring';
      case 'renewal-notified':
        return ctx.lockMs !== null && ctx.lockMs > ctx.nowMs
          ? 'notified-awaiting'
          : 'notified-overdue';
      case 'renewal-success-pending':
        return 'booking-notice-pending';
      case 'soft-end-not-renewing':
        return 'not-renewing';
      case 'soft-end-transferred':
        return 'transferred-out';
      case 'soft-end-scan-a':
        return 'released-admin-unnotified';
      case 'soft-end-scan-b':
        return 'released-expired';
      case 'soft-end-other':
        return 'released-other';
      case 'active-clean':
      case 'auto-renewed':
        if (ctx.lsedMs === null) return 'general';
        if (ctx.nowMs < FrontendConfigUtils.renewalWindowOpenMs(ctx.lsedMs)) return 'general';
        return ctx.nowMs <= FrontendConfigUtils.renewalStandardReleaseMs(ctx.lsedMs)
          ? 'pending-notify'
          : 'expired-unnotified';
    }
  }

  /**
   * 2026-10-13 (續報・轉班 完成規則 W1.4, E-00 / P4) — the 修改最後約課日 / 恢復保留名額 /
   * 確認保留名額 button a row shows: RENEWAL_REANCHOR_KIND_BY_STATE of its renewalRowStateOf.
   * Never 'settle' — 結算續報 is its own button (P4: 結算 by fact, server-side). Label + hover:
   * RESERVATION_ACTION_LABEL_BY_KIND[kind]. Pure.
   */
  static reservationActionKindOf(
    state: ReservationEnrollmentState,
    ctx: RenewalRowStateContext,
  ): Exclude<RenewalReanchorKind, 'settle'> {
    return RENEWAL_REANCHOR_KIND_BY_STATE[FrontendConfigUtils.renewalRowStateOf(state, ctx)];
  }

  /**
   * 2026-10-24 (約課切斷確認 W1.3 (b), D50 / D57 — PO 「11.2 ok」, 「4. A」; R2-A-07 / R2-B-04) —
   * the one exception to the 轉班 lock: 補正最後約課日 (entry 'cutoffFix') passes when the class
   * is the 轉出中 OLD class of a 轉班 in 安排中 (lock role oldClass, the enrollment's own status
   * `transferring`, D50), or the real seat an upload created on a proposed NEW class while
   * 安排中 (role newClass, status `active`, not a 轉班 hold shape — an option-S special-held hold
   * is never exempt, D57). A 轉班保留 (role hold), 待約課 / 未完成, every other entry and every
   * other action stay locked; no lock → nothing to exempt. Keyed on the enrollment's own status,
   * never RenewalRowState. Plain data: `lock` = the class's TransferLock when locked
   * (utils/transferLock transferLockOf), else null; `isHold` = the caller's hold-shape verdict
   * against the locking record (utils/transferHold). Pure. Mirror of functions
   * isCutoffFixLockExempt (appConfig.ts); pinned by CUTOFF_FIX_LOCK_EXEMPT_CASES
   * (utils/transferLock.cases.ts, replayed on both sides).
   */
  static isCutoffFixLockExempt(
    entry: ManualMarkEntry | null | undefined,
    lock: { readonly stage: string; readonly role: string } | null,
    enrollment: { readonly status: unknown; readonly isHold: boolean },
  ): boolean {
    if (entry !== 'cutoffFix' || lock === null || lock.stage !== 'arranging') return false;
    if (lock.role === 'oldClass') return enrollment.status === 'transferring';
    return lock.role === 'newClass' && enrollment.status === 'active' && !enrollment.isHold;
  }

  /**
   * 2026-06-24 (Messaging subsystem / W1, §2f) — the late-contact threshold,
   * DERIVED so it tracks both tunables: RENEWAL_SYSTEM_CAP_DAYS (14, the LSED−14d
   * release point) + RENEWAL_MIN_RUNWAY_DAYS (10, the guaranteed runway) = 24.
   * A renewal notice issued after LSED−24d is "late": computeRenewalLockMs can no
   * longer reach LSED−14d, so the lock shifts to today+10 (the §2f banner fires).
   *
   * A getter (not a static field) so it reads SESSION_ENROLLMENT_CONFIG LAZILY —
   * the config object is declared later in this module than the class, so an eager
   * static-field initializer would hit the temporal-dead-zone.
   */
  static get RENEWAL_LATE_CONTACT_THRESHOLD_DAYS(): number {
    return (
      SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS +
      SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS
    );
  }

  /**
   * 2026-06-24 (Messaging subsystem / W1, §2f) — floors an arbitrary epoch-ms to
   * the START of its Taipei (+08:00) calendar day. Never uses bare
   * `new Date(y, m, d)` (Firebase CFs + tests can run UTC → 8-hour shift); builds
   * the Taipei day-start via an explicit +08:00 ISO string per CLAUDE.md §Timezone.
   */
  private static floorToTaipeiDayStartMs(ms: number): number {
    // Shift into the Taipei wall-clock frame, read the Y-M-D there, then rebuild
    // that calendar day's 00:00 at +08:00.
    const taipeiWall = new Date(ms + 8 * 60 * 60 * 1000);
    const y = taipeiWall.getUTCFullYear();
    const mo = String(taipeiWall.getUTCMonth() + 1).padStart(2, '0');
    const d = String(taipeiWall.getUTCDate()).padStart(2, '0');
    return new Date(`${y}-${mo}-${d}T00:00:00+08:00`).getTime();
  }

  /**
   * 2026-06-24 (Messaging subsystem / W1, §2f) — true when a renewal notice issued
   * "today" is LATE, i.e. today is strictly after LSED − RENEWAL_LATE_CONTACT_THRESHOLD_DAYS
   * (LSED−24d). Both `today` and the LSED−24d anchor are floored to Taipei day-start
   * before the comparison, and the boundary equality is OFF (a notice exactly on
   * LSED−24d is still on-time — `>` not `>=`).
   */
  static isLateRenewalNotice(lsedMs: number, nowMs: number): boolean {
    if (!Number.isFinite(lsedMs) || !Number.isFinite(nowMs)) return false;
    const dayMs = 24 * 60 * 60 * 1000;
    const todayStartMs = this.floorToTaipeiDayStartMs(nowMs);
    const anchorStartMs = this.floorToTaipeiDayStartMs(
      lsedMs - FrontendConfigUtils.RENEWAL_LATE_CONTACT_THRESHOLD_DAYS * dayMs,
    );
    return todayStartMs > anchorStartMs;
  }

  /**
   * 2026-06-26 (Option C / transfer-completion threshold) — FE mirror of the
   * backend `pickedSessionCount` (functions/src/sessionEnrollments/utils.ts).
   * Counts the bookings in the PICKED transfer session: the unique Taipei-calendar
   * dates in `datesIso` that fall ON OR BEFORE the picked LSED's Taipei day.
   *
   * Used by 確定轉入 (Path B) + 結算轉班 to decide whether a MANUAL transfer
   * completion is sub-threshold (< SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD)
   * and surface the caution. `datesIso` MUST be the list returned by
   * `admin-getTargetExistingBookings` — already 學期-only, future, and baseline-
   * filtered to > waitlist.lastModifiedAt. NEVER pass `existingDates.length` or the
   * live 約課數; they measure DIFFERENT sets. Result MUST equal the BE helper for
   * identical inputs (same en-CA / Asia-Taipei day-key + ≤ pick + Set dedupe).
   */
  static pickedSessionCount(datesIso: string[], pickedLsedMs: number): number {
    if (!Number.isFinite(pickedLsedMs) || datesIso.length === 0) return 0;
    const pickedKey = FrontendConfigUtils.taipeiDayKey(pickedLsedMs);
    const seen = new Set<string>();
    for (const iso of datesIso) {
      const ms = new Date(iso).getTime();
      if (!Number.isFinite(ms)) continue;
      const key = FrontendConfigUtils.taipeiDayKey(ms);
      if (key <= pickedKey) seen.add(key);
    }
    return seen.size;
  }

  /**
   * Taipei-calendar day key (YYYY-MM-DD) for an epoch-ms instant. Mirror of the
   * backend `taipeiDayKey` (sessionEnrollments/utils.ts) — identical
   * en-CA / Asia-Taipei config so FE/BE day-bucketing (and the picked-session
   * counts built on it) match exactly.
   */
  // 2026-09-20 (browser-freeze M1, HIGH-1): one formatter for the class — this
  // runs once per booking date inside pickedSessionCount's loop, and constructing
  // Intl.DateTimeFormat is ~80× the cost of calling it.
  private static readonly TAIPEI_DAY_KEY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  private static taipeiDayKey(ms: number): string {
    return FrontendConfigUtils.TAIPEI_DAY_KEY_FORMATTER.format(new Date(ms));
  }

  /**
   * The Taipei weekday (0 = 週日 … 6 = 週六) of an epoch-ms instant, read from its Taipei day key
   * (that day's 12:00 +08:00 instant — no offset arithmetic, no local getDay()). W5.4 BE-F3.
   * MIRROR of functions taipeiWeekday (beside taipeiDayKey there).
   */
  static taipeiWeekday(ms: number): number {
    return new Date(`${FrontendConfigUtils.taipeiDayKey(ms)}T12:00:00+08:00`).getUTCDay();
  }

  /**
   * 2026-10-24 (約課切斷確認 W1.3 (a), Spec 1, D20 / D21; R2-B-02) — the flag rule: a 最後約課日
   * on or within SESSION_ENROLLMENT_CONFIG.LSED_CUTOFF_WINDOW_DAYS Taipei days before the last
   * class day of the latest completed upload (`rangeEndMs`) → 約課切斷確認. Inclusive on both
   * ends (end − 14 yes, end − 15 no, the end day itself yes); a date after the range end is not a
   * cut; no completed upload (null) → false. Compares Taipei DAY keys, never instants: the range
   * end is stored at 00:00 Taipei while every 最後約課日 is 12:00. Pure. MIRROR of functions
   * isLsedCutoffCandidate — pinned by the shared fixture test-excel/fixtures/lsed-cutoff-cases.json.
   */
  static isLsedCutoffCandidate(lsedMs: number, rangeEndMs: number | null | undefined): boolean {
    if (typeof rangeEndMs !== 'number' || !Number.isFinite(rangeEndMs)) return false;
    if (!Number.isFinite(lsedMs)) return false;
    // Taiwan observes no DST, so a calendar day is exactly 24 h.
    const dayMs = 24 * 60 * 60 * 1000;
    const lastDay = FrontendConfigUtils.taipeiDayKey(rangeEndMs);
    const firstDay = FrontendConfigUtils.taipeiDayKey(
      FrontendConfigUtils.floorToTaipeiDayStartMs(rangeEndMs) -
        SESSION_ENROLLMENT_CONFIG.LSED_CUTOFF_WINDOW_DAYS * dayMs,
    );
    const lsedDay = FrontendConfigUtils.taipeiDayKey(lsedMs);
    return lsedDay >= firstDay && lsedDay <= lastDay;
  }

  /**
   * 2026-10-24 (約課切斷確認 W1.3 (a), Spec 5 / Spec 6, D29 / D32) — the clickable-date rule of
   * 補正最後約課日 (the window's calendar `is-date-disabled`; the server applies its twin): the
   * Taipei weekday of `dayMs` is the timeslot's, its Taipei day is after the current
   * 最後約課日's and after today + SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS (counted in
   * Taipei days — today + 14 no, today + 15 yes); no upper limit. Non-finite input → false.
   * Pure. MIRROR of functions isCutoffFixDateAllowed — pinned by the shared fixture.
   */
  static isCutoffFixDateAllowed(dayMs: number, ctx: CutoffFixDateContext): boolean {
    const { weekday, currentLsedMs, nowMs } = ctx;
    if (!Number.isFinite(dayMs) || !Number.isFinite(currentLsedMs) || !Number.isFinite(nowMs)) {
      return false;
    }
    if (FrontendConfigUtils.taipeiWeekday(dayMs) !== weekday) return false;
    const day = FrontendConfigUtils.taipeiDayKey(dayMs);
    if (day <= FrontendConfigUtils.taipeiDayKey(currentLsedMs)) return false;
    const dayLength = 24 * 60 * 60 * 1000;
    const floorDay = FrontendConfigUtils.taipeiDayKey(
      FrontendConfigUtils.floorToTaipeiDayStartMs(nowMs) +
        SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS * dayLength,
    );
    return day > floorDay;
  }

  /**
   * 2026-10-31 (候補 預計開始上課日, D4 / D14) — may the Taipei day `dateKey` ('YYYY-MM-DD') be
   * the 預計開始上課日 of a 正課 hold? In this order: the hold's stored date sent back unchanged →
   * yes, it is not checked again (編輯); not a real calendar day, or before today → no; else
   * only the held class's weekday. The weekday is read at 12:00 +08:00. Pure.
   * MIRROR of functions programWaitlist/startDateRules.ts — the same fixtures (ANALYST-16).
   */
  static isWaitlistStartDateAllowed(dateKey: string, ctx: WaitlistStartDateContext): boolean {
    if (ctx.storedKey && dateKey === ctx.storedKey) return true;
    const noonMs = Date.parse(`${dateKey}T12:00:00+08:00`);
    // A real day only: Date.parse rolls 「2026-02-30」 over to 3/2.
    if (!Number.isFinite(noonMs) || FrontendConfigUtils.taipeiDayKey(noonMs) !== dateKey) {
      return false;
    }
    if (dateKey < ctx.todayKey) return false;
    return new Date(noonMs).getUTCDay() === ctx.heldWeekday;
  }

  /**
   * 2026-05-29: emoji → self-hosted Twemoji SVG path map.
   * Single source of truth — both `classShortNameWithEmojiHtml` (v-html
   * call sites) and `classShortNameEmojiSvgPath` (h() / render-label call
   * sites) read from this.
   */
  private static readonly EMOJI_SVG_PATH: Record<string, string> = {
    '🦫': '/twemoji/1f9ab.svg',
    '🐤': '/twemoji/1f424.svg',
    '🐸': '/twemoji/1f438.svg',
    '🐧': '/twemoji/1f427.svg',
    '🦭': '/twemoji/1f9ad.svg',
    '🐻‍❄️': '/twemoji/1f43b-200d-2744-fe0f.svg',
    '👶': '/twemoji/1f476.svg',
    '💪': '/twemoji/1f4aa.svg',
  };

  /**
   * 2026-05-29: HTML variant of classShortNameWithEmoji — emits an inline
   * `<img class="class-emoji">` tag pointing at the self-hosted Twemoji SVG
   * (see public/twemoji/) instead of a raw emoji character. Template call
   * sites use this with `v-html` to avoid the flash-of-native-emoji that
   * the post-render parser at src/plugins/twemoji.ts produces.
   *
   * Input is config-controlled (full class name from BUSINESS_RULES), so
   * v-html is safe — no user input flows through here.
   */
  static classShortNameWithEmojiHtml(fullName: string | null | undefined): string {
    const sf = this.classShortName(fullName);
    if (!sf) return '';
    const emoji = this.classShortNameEmoji(sf);
    if (!emoji) return sf;
    const svg = this.EMOJI_SVG_PATH[emoji];
    if (!svg) return `${emoji} ${sf}`;
    return `<img class="class-emoji" src="${svg}" alt="${emoji}" draggable="false"> ${sf}`;
  }

  /**
   * 2026-05-29: returns the SVG asset path for a class shortform's emoji,
   * or null when the shortform has no emoji mapping (custom classes, etc.).
   * Use in h() render functions / n-select render-label callbacks where
   * v-html isn't available:
   *   const svg = FrontendConfigUtils.classShortNameEmojiSvgPath(sf);
   *   return h('span', null, [
   *     svg ? h('img', { class: 'class-emoji', src: svg }) : null,
   *     sf,
   *   ]);
   */
  static classShortNameEmojiSvgPath(shortform: string | null | undefined): string | null {
    if (!shortform) return null;
    const emoji = this.classShortNameEmoji(shortform);
    if (!emoji) return null;
    return this.EMOJI_SVG_PATH[emoji] ?? null;
  }

  /**
   * Reference capacity for a class by full or short name.
   * Returns CLASS_MAX_SLOTS[shortform] if known, else SESSION_ENROLLMENT_CONFIG.DEFAULT_TIMESLOT_CAPACITY.
   * Co-op shortforms (e.g. 小熊班*協同) resolve to their own capacity entry.
   * The M of 「保留名額 N/M」 (useSessionEnrollments/slotCapacity.ts). Since 2026-09-29
   * (額滿提醒) the backend resolves the same M with its mirror
   * DisplayConfigUtils.classMaxSlots for the full-slot backstop; the two resolvers are
   * pinned together by functions/src/__tests__/slotCapacityConfigParity.test.ts, which
   * lifts THIS method's source — keep it free of new dependencies.
   */
  static classMaxSlots(name: string | null | undefined): number {
    const sf = FrontendConfigUtils.classShortName(name);
    return CLASS_MAX_SLOTS[sf] ?? SESSION_ENROLLMENT_CONFIG.DEFAULT_TIMESLOT_CAPACITY;
  }

  /**
   * Single branch authority for type-aware per-week availability (候補 tab).
   * Returns true when the stored `type` uses upcoming available-week chips
   * (體驗 / 單堂 / 單堂補課). Any unknown or invalid `type` returns false, which
   * falls back to the safe reservation-aggregate (正課) path.
   */
  static usesPerWeekAvailability(type: string): boolean {
    return (
      FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.PERWEEK_AVAILABILITY_TYPES as readonly string[]
    ).includes(type);
  }

  /**
   * Single authority for the 候補 offer rule of a stored `type` (PO 2026-09-20).
   * Returns null for 正式課程 and for any unknown value — both fall back to the
   * reservation-aggregate (正課) path, which is the safe direction: it offers
   * only while 保留名額 is not full and never invents a date.
   *
   * Consumers must NOT re-derive the window or the count — read it from here.
   */
  /**
   * Is this stored programGroup one of the 0–18m classes whose trial is a
   * three-class course (THREE_TRIAL_GROUPS)? Same matching as
   * WaitlistEditModal.resolveCanonicalClass: equal to, or containing, the bare
   * class name — so 「小鴨班」, 「小鴨班 (平日)」 and 「親子游泳【8-18m/…】小鴨班」
   * all count.
   */
  static isThreeTrialGroup(programGroup: string | null | undefined): boolean {
    if (!programGroup) return false;
    return FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.THREE_TRIAL_GROUPS.some(
      (g) => programGroup === g || programGroup.includes(g),
    );
  }

  /**
   * May an entry in `programGroup` carry this stored `type`? The one rule
   * (PO 2026-09-21): 體驗三堂 only in the 0–18m classes. Every other type is
   * allowed everywhere — including 體驗 (一堂) in a 0–18m class, kept on
   * purpose as an edge case.
   */
  static isWaitlistTypeAllowedForGroup(
    type: string,
    programGroup: string | null | undefined,
  ): boolean {
    if (type !== '體驗三堂') return true;
    return FrontendConfigUtils.isThreeTrialGroup(programGroup);
  }

  /** The trial type a NEW entry starts with: 體驗三堂 for 0–18m, else 體驗一堂. */
  static defaultWaitlistTrialType(programGroup: string | null | undefined): '體驗' | '體驗三堂' {
    return FrontendConfigUtils.isThreeTrialGroup(programGroup) ? '體驗三堂' : '體驗';
  }

  static waitlistOfferRule(type: string): WaitlistOfferRule | null {
    const rules = FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.OFFER_RULES as Record<
      string,
      WaitlistOfferRule | undefined
    >;
    return rules[type] ?? null;
  }

  /**
   * How far ahead the 候補 tab may look for THIS type, in weeks. Falls back to
   * the shared default (the 報名訊息產生器 horizon) when the type has no offer
   * rule, so 正課 keeps scanning exactly the window it scanned before.
   */
  static waitlistOfferWindowWeeks(type: string): number {
    return (
      FrontendConfigUtils.waitlistOfferRule(type)?.windowWeeks ??
      FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.PERWEEK_AVAILABILITY_WINDOW_WEEKS
    );
  }

  /**
   * Which 付款連結 slug 推薦訊息 should PRE-SELECT for this offer.
   *
   * Keyed on (prefix, type, group) — the 類型 alone decides the ticket (PO
   * 2026-09-29: 體驗一堂／體驗三堂 are the 首次 products; see the config block).
   *
   * Returns null when there is no sensible default — 正課 (bank transfer),
   * 單堂補課 (already paid for), or any combination not registered in
   * OFFER_PAYMENT_LINK_SLUGS. The caller then leaves the picker empty rather
   * than guessing; see the config block for why that is deliberate.
   */
  static waitlistOfferLinkSlug(
    programPrefix: string,
    type: string,
    programGroup: string,
  ): string | null {
    const RULES = FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST;
    // FMS carries the 親子/獨立 split in the group name, not the prefix, and only
    // trials have a ticket link.
    if (programPrefix === 'FMS體能課') {
      if (type !== '體驗') return null;
      for (const [needle, slug] of Object.entries(RULES.OFFER_PAYMENT_LINK_FMS)) {
        if (programGroup.includes(needle)) return slug;
      }
      return null;
    }
    return RULES.OFFER_PAYMENT_LINK_SLUGS[programPrefix]?.[type] ?? null;
  }

  /**
   * Which 學期方案 id 推薦訊息 should PRE-SELECT for a 正課 offer, so the message
   * can quote the right amount. Returns null when it cannot be inferred — the
   * picker then starts empty rather than naming a price we are not sure of.
   */
  static waitlistOfferTermPlanId(
    programPrefix: string,
    programGroup: string,
    slotLabel: string,
  ): string | null {
    const IDS = FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.OFFER_TERM_PLAN_IDS;
    if (programPrefix === 'FMS體能課') {
      if (programGroup.includes('親子')) return IDS.FMS_PARENT;
      if (programGroup.includes('獨立')) return IDS.FMS_SOLO;
      return null;
    }
    if (programPrefix === '親子游泳' || programPrefix === '獨立游泳') {
      const weekend = FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.OFFER_TERM_WEEKEND_PREFIXES.some(
        (d) => slotLabel.startsWith(d),
      );
      return weekend ? IDS.SWIM_WEEKEND : IDS.SWIM_WEEKDAY;
    }
    return null;
  }

  /**
   * Must 推薦訊息 carry a payment link for this type before it may be copied?
   *
   * TRUE for the types a family pays for up front (體驗一堂／體驗三堂／單堂):
   * the whole offer is "pay and we book you", so a message without a link is
   * incomplete and the modal blocks the copy.
   * FALSE for 正式課程 (semester fee, paid by BANK TRANSFER — no ticket link
   * exists) and 單堂補課 (drawn from a ticket already bought).
   */
  static waitlistOfferRequiresPaymentLink(type: string): boolean {
    return type !== '正式課程' && type !== '單堂補課';
  }

  /**
   * slug → the 候補 tab's OWN wording for what that link sells, e.g.
   * `bs-trial-promo` → 「親子游泳 體驗一堂」.
   *
   * Why: the labels in 付款連結管理 are BookFast ticket wording (「親子游泳單堂
   * 體驗」), and 單堂 is a DIFFERENT product from 體驗一堂 in this column
   * (PO 2026-09-21). Showing the ticket label alone would invite exactly that
   * mix-up, so the picker leads with the 類型 the 課務 is looking at and keeps
   * the supervisor's label after it as confirmation of the actual product.
   *
   * ⚠ EVERY slug in TICKET_LINK_OPTIONS must come out of here. A slug this map
   * misses fell through to `opt.slug` in the picker and showed the 課務 raw
   * machine code — which is what this function exists to prevent (review
   * finding M3); `waitlistOfferMessage.test.ts` pins the coverage so a new
   * ticket cannot reintroduce it. A ticket no 類型 pre-selects (`bs3-single`)
   * is named by OFFER_LINK_EXTRA_TERMS.
   */
  static waitlistOfferLinkTerms(): Record<string, string> {
    const RULES = FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST;
    const labels = {
      ...(RULES.TYPE_DISPLAY_LABELS as Record<string, string>),
      ...RULES.OFFER_LINK_TERM_LABEL_OVERRIDES,
    } as Record<string, string>;
    const out: Record<string, string> = { ...RULES.OFFER_LINK_EXTRA_TERMS };
    for (const [prefix, byType] of Object.entries(RULES.OFFER_PAYMENT_LINK_SLUGS)) {
      for (const [type, slug] of Object.entries(byType)) {
        out[slug] = `${prefix} ${labels[type] ?? type}`;
      }
    }
    for (const [needle, slug] of Object.entries(RULES.OFFER_PAYMENT_LINK_FMS)) {
      out[slug] = `FMS體能課 ${needle} ${labels['體驗'] ?? '體驗'}`;
    }
    return out;
  }

  // ─── Co-op display helpers (Wave 2.1) ───────────────────────────────────────
  // Display-only. NEVER feed option value, group key, query param, or CF arg.

  /**
   * Returns true if the class name (full or short) contains the co-op pattern
   * (`*協同`). Single canonical test — always reads from SYSTEM_CONSTANTS so
   * the pattern string is never duplicated across call sites.
   */
  static classNameIsCoop(name: string | null | undefined): boolean {
    return !!name && name.includes(SYSTEM_CONSTANTS.COOP_CLASSES.DETECTION.COOP_PATTERN);
  }

  /**
   * Returns the raw shortform for admin table / cell display — includes the
   * literal `*協同` suffix for co-op classes (e.g. `小熊班*協同`).
   * Non-co-op names return `classShortName` unchanged (e.g. `小熊班`).
   *
   * Use this for admin TABLE cells where the asterisk convention is the
   * agreed admin-facing display standard.
   * For parent-facing text (LINE/notifications) use `classDisplayNameParen`.
   * **Display-only** — never assign to stored fields, option values, or CF args.
   */
  static classDisplayName(name: string | null | undefined): string {
    return this.classShortName(name);
  }

  /**
   * Returns a clean parent-facing display name for a class.
   * Co-op shortforms (e.g. `小熊班*協同`) strip the raw `*協同` asterisk and
   * re-append a clean full-width `（協同）` marker instead.
   * Non-co-op names return `classShortName` unchanged.
   *
   * Use this for parent-facing surfaces: LINE notifications, template
   * formatters, any text the parent sees. For admin table cells use
   * `classDisplayName` (raw `*協同` form) or `classDisplayNameHtml` (with icon).
   * **Display-only** — never assign to stored fields, option values, or CF args.
   */
  static classDisplayNameParen(name: string | null | undefined): string {
    const sf = this.classShortName(name);
    if (!sf) return '';
    const COOP = SYSTEM_CONSTANTS.COOP_CLASSES.DETECTION.COOP_PATTERN; // '*協同'
    if (sf.endsWith(COOP)) {
      return `${sf.replace(/\*協同$/, '')}（協同）`;
    }
    return sf;
  }

  /**
   * HTML variant of classDisplayName for v-html call sites (admin TABLE cells).
   * Renders the base animal Twemoji SVG + the raw `classDisplayName` text
   * (which is now `classShortName`, including `*協同` for co-op classes).
   * Result example: `<img class="class-emoji"> 小熊班*協同`.
   *
   * The Twemoji SVG is always resolved from the BASE shortform (strips `*協同`
   * before the emoji lookup) so the correct base-animal icon always appears.
   * Non-co-op classes are identical to classShortNameWithEmojiHtml.
   * **Display-only** — never assign to stored fields, option values, or query params.
   */
  static classDisplayNameHtml(name: string | null | undefined): string {
    const sf = this.classShortName(name);
    if (!sf) return '';
    const COOP = SYSTEM_CONSTANTS.COOP_CLASSES.DETECTION.COOP_PATTERN;
    // Resolve the base shortform for emoji/SVG lookup (strip co-op suffix).
    const baseSf = sf.endsWith(COOP) ? sf.replace(/\*協同$/, '') : sf;
    // displayText is now the raw shortform (小熊班*協同 or 小熊班).
    const displayText = this.classDisplayName(name);
    const emoji = this.classShortNameEmoji(baseSf);
    if (!emoji) return displayText;
    const svg = this.EMOJI_SVG_PATH[emoji];
    if (!svg) return `${emoji} ${displayText}`;
    return `<img class="class-emoji" src="${svg}" alt="${emoji}" draggable="false"> ${displayText}`;
  }

  /**
   * 2026-06-03: returns the Twemoji SVG asset path for a class shortform,
   * resolving co-op shortforms via their base (strips `*協同`).
   * Use in h() / render-label contexts alongside classDisplayName(name) text.
   * Returns null for classes without a mapped emoji.
   */
  static classDisplayNameEmojiSvgPath(shortform: string | null | undefined): string | null {
    if (!shortform) return null;
    const COOP = SYSTEM_CONSTANTS.COOP_CLASSES.DETECTION.COOP_PATTERN;
    const baseSf = shortform.endsWith(COOP) ? shortform.replace(/\*協同$/, '') : shortform;
    const emoji = this.classShortNameEmoji(baseSf);
    if (!emoji) return null;
    return this.EMOJI_SVG_PATH[emoji] ?? null;
  }

  /**
   * 請款單「編輯」的目的地路徑，依請款類型分流（差旅費 → 出差表單、退費 → 退費表單、
   * 其餘 → 通用請款表單）。規則讀自 `PAYMENT.EDIT_ROUTE_BY_TYPE`，元件只是消費者。
   */
  static paymentEditPath(type: string | null | undefined, id: string): string {
    const P = FRONTEND_BUSINESS_RULES.PAYMENT;
    const template = P.EDIT_ROUTE_BY_TYPE[type ?? ''] ?? P.EDIT_ROUTE_DEFAULT;
    return template.replace('{id}', id);
  }

  /**
   * 下一個公司付款日 (YYYY-MM-DD) — the first PAYMENT.COMPANY_PAY_DAYS day strictly
   * after today's Taipei calendar day; from the last pay day on, next month's
   * first. The one formula behind 安排付款's 即期 帶入, 批次安排付款, and the
   * 匯出銀行轉帳清冊 default 預計付款日期 — screens that must propose the same day.
   */
  static nextCompanyPayDate(nowMs: number): string {
    const [y, m, d] = this.taipeiDayKey(nowMs).split('-').map(Number) as [number, number, number];
    const days = FRONTEND_BUSINESS_RULES.PAYMENT.COMPANY_PAY_DAYS;
    const pad = (n: number) => String(n).padStart(2, '0');
    const laterThisMonth = days.find((day) => day > d);
    if (laterThisMonth !== undefined) return `${y}-${pad(m)}-${pad(laterThisMonth)}`;
    const nextY = m === 12 ? y + 1 : y;
    const nextM = m === 12 ? 1 : m + 1;
    return `${nextY}-${pad(nextM)}-${pad(days[0] as number)}`;
  }

  /**
   * 身分證／居留證號 canonical form — whitespace and invisible characters removed,
   * upper case. Mirrors functions/src/utils/personalId.ts normalizePersonalId.
   */
  static normalizePersonalId(raw: string): string {
    return raw
      .replace(/[\u200B-\u200F\u2028-\u202F\u2060-\u2064\uFEFF\u00A0]/g, '')
      .replace(/\s+/g, '')
      .toUpperCase();
  }

  /**
   * Instant form hint only — the server (functions/src/utils/personalId.ts,
   * same algorithm, same PERSONAL_ID table) is the gate. Expects a normalized
   * value. 國民身分證／新式居留證: letter + [1289] + 8 digits; 舊式居留證: letter +
   * [A-D] + 8 digits (2nd letter = units digit of its own area code). Check digit:
   * area tens ×1 + units ×9, next eight × 8…1, last ×1, sum % 10 === 0.
   */
  static isValidPersonalId(id: string): boolean {
    const R = FRONTEND_BUSINESS_RULES.PERSONAL_ID;
    const area = (letter: string | undefined): [number, number] | null => {
      const code = letter === undefined ? undefined : R.LETTER_CODES[letter];
      return code === undefined ? null : [Math.floor(code / 10), code % 10];
    };
    let body: number[];
    if (R.SINGLE_LETTER_PATTERN.test(id)) {
      body = Array.from(id.slice(1), Number);
    } else if (R.OLD_RESIDENT_PATTERN.test(id)) {
      const second = area(id[1]);
      if (!second) return false;
      body = [second[1], ...Array.from(id.slice(2), Number)];
    } else {
      return false;
    }
    const first = area(id[0]);
    if (!first || body.length !== 9) return false;
    let sum = first[0] + first[1] * 9;
    for (let i = 0; i < 8; i++) sum += (body[i] as number) * (8 - i);
    sum += body[8] as number;
    return sum % 10 === 0;
  }

  /**
   * 7 碼通匯代碼的檢查碼覆核 —— **提示用，永遠不擋存檔**。
   *
   * 演算法鏡射兆豐 EXCEL 轉檔程式的 `chkBankID`
   * (`docs/reference/megabank-excel-convert-frmRmt.vba.txt:1468-1503`)，權數與
   * 例外清單全部讀自 PAYMENT.BANK_CODE_CHECKDIGIT（規則在 config，這裡只是算式）：
   *
   *   1. 空值 ⇒ 'ok'（沒有東西可核，呼叫端不顯示任何提示）
   *   2. 非 7 位數字或 INVALID_CODES（0000000）⇒ 'invalid'
   *   3. 已消滅銀行代號 ⇒ 'unknown'。**先於檢查碼判斷**：這種代碼算得過也沒用，
   *      「這家銀行已併入別家」才是使用者要處理的事
   *   4. 郵局 700 不套算法，只有 POST_OFFICE_VALID_CODES 兩個有效
   *   5. 彰銀 009：第 4、5、6 碼 ×(1,4,7)，取總和個位數
   *   6. 其餘：第 1–6 碼 × GENERAL_WEIGHTS；首碼 1|2（信合社）取個位數，
   *      否則取 10 的補數 (10 − sum mod 10) mod 10
   *
   * 消費端目前是 `useBankBranchLookup().checkCode7`（BankCodePicker 由該 composable
   * 一併取得帳號前綴與檢查碼兩個提示）。演算法留在這裡是因為它是純粹讀 config 常數的
   * 公式（CLAUDE.md §4b：formulas → config utility class），不需要分行資料集。
   */
  static checkCode7(code7: string | null | undefined): BankCode7Check {
    const RULE = FRONTEND_BUSINESS_RULES.PAYMENT.BANK_CODE_CHECKDIGIT;
    const code = (code7 ?? '').trim();
    if (!code) return 'ok';
    // `.some(...)` rather than `.includes(...)`: the config is `as const`, so the
    // arrays are literal tuples and `includes` would only accept those literals.
    if (!/^\d{7}$/.test(code) || RULE.INVALID_CODES.some((c) => c === code)) return 'invalid';

    const bankCode = code.slice(0, 3);
    if (RULE.DEFUNCT_BANK_CODES.some((c) => c === bankCode)) return 'unknown';
    if (bankCode === RULE.POST_OFFICE_BANK_CODE) {
      return RULE.POST_OFFICE_VALID_CODES.some((c) => c === code) ? 'ok' : 'invalid';
    }

    /** 1-based digit, matching the VBA `Mid(bankid, n, 1)` positions. */
    const digitAt = (position: number): number => Number(code.charAt(position - 1));

    let expected: number;
    if (bankCode === RULE.CHANGHWA_BANK_CODE) {
      const sum = RULE.CHANGHWA_BRANCH_POSITIONS.reduce(
        (acc, position, index) =>
          acc + digitAt(position) * (RULE.CHANGHWA_BRANCH_WEIGHTS[index] ?? 0),
        0,
      );
      expected = sum % 10;
    } else {
      const sum = RULE.GENERAL_WEIGHTS.reduce(
        (acc, weight, index) => acc + digitAt(index + 1) * weight,
        0,
      );
      expected = RULE.CREDIT_UNION_FIRST_DIGITS.some((d) => d === code.charAt(0))
        ? sum % 10
        : (10 - (sum % 10)) % 10;
    }

    return expected === digitAt(7) ? 'ok' : 'mismatch';
  }

  /**
   * Convert numeric weekday (0=Sunday..6=Saturday) to display string.
   * Returns '' for out-of-range values rather than throwing — defensive.
   */
  static formatWeekday(weekday: number | null | undefined): string {
    if (typeof weekday !== 'number' || weekday < 0 || weekday > 6) return '';
    return FRONTEND_BUSINESS_RULES.DATE_TIME.WEEKDAY_NAMES[weekday] ?? '';
  }

  static hasAdminLevelAccess(roles: any[] | undefined | null): boolean {
    if (!roles) return false;
    return roles.some(
      (role) =>
        role === SYSTEM_CONSTANTS.USER_ROLES.ADMIN ||
        role === SYSTEM_CONSTANTS.USER_ROLES.SUPERVISOR,
    );
  }

  static getRealTimeSyncInfo(classStartTimeISO: string, now: Date) {
    const classStartTime = new Date(classStartTimeISO).getTime();
    const nowTime = now.getTime();
    const { BEFORE_CLASS_MINUTES, AFTER_CLASS_MINUTES } = FRONTEND_BUSINESS_RULES.REAL_TIME_SYNC;
    const windowStart = classStartTime - BEFORE_CLASS_MINUTES * 60 * 1000;
    const windowEnd = classStartTime + AFTER_CLASS_MINUTES * 60 * 1000;
    const inWindow = nowTime >= windowStart && nowTime <= windowEnd;
    const beforeWindow = nowTime < windowStart;
    const afterWindow = nowTime > windowEnd;
    let status = '手動更新';
    let minutesInfo = '';
    if (inWindow) {
      status = '自動更新中';
      const minutesUntilEnd = Math.ceil((windowEnd - nowTime) / (1000 * 60));
      minutesInfo = `還有 ${minutesUntilEnd} 分鐘`;
    } else if (beforeWindow) {
      const minutesUntilStart = Math.ceil((windowStart - nowTime) / (1000 * 60));
      minutesInfo = `${minutesUntilStart} 分鐘後開始自動更新`;
    } else if (afterWindow) {
      minutesInfo = '自動更新已結束';
    }
    return {
      windowStart: new Date(windowStart),
      windowEnd: new Date(windowEnd),
      inWindow,
      beforeWindow,
      afterWindow,
      status,
      minutesInfo,
    };
  }

  static shouldEnableRealTimeSync(classDate: string, startTime: string): boolean {
    try {
      const now = new Date();
      const classDateTime = new Date(`${classDate}T${startTime}:00+08:00`);
      const beforeWindow = new Date(
        classDateTime.getTime() -
          FRONTEND_BUSINESS_RULES.REAL_TIME_SYNC.BEFORE_CLASS_MINUTES * 60000,
      );
      const afterWindow = new Date(
        classDateTime.getTime() +
          FRONTEND_BUSINESS_RULES.REAL_TIME_SYNC.AFTER_CLASS_MINUTES * 60000,
      );
      return now >= beforeWindow && now <= afterWindow;
    } catch {
      return false;
    }
  }

  static formatDisplayDate(dateStr: string): string {
    try {
      const date = new Date(dateStr + 'T00:00:00+08:00');
      const formatted = date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
      });
      const dayOfWeek = FRONTEND_BUSINESS_RULES.DATE_TIME.WEEKDAY_NAMES[date.getDay()];
      return `${formatted} (${dayOfWeek})`;
    } catch {
      return dateStr;
    }
  }

  /**
   * Determines if editing features should be locked to prevent data loss from Excel uploads.
   * @param classDateStr - The date of the class (YYYY-MM-DD or YYYY/MM/DD)
   * @param now - Current time (usually from debugTimeStore)
   * @param classStartTimeStr - Optional start time (HH:mm) to check for early access warning
   * @param role - Role context for lock policy ('admin' or 'teacher'). Defaults to 'admin'.
   * @returns Object containing lock status and warning status
   */
  /** Lazily built once (reads DATE_TIME.TIMEZONE, so not a field initializer). */
  private static wipeRiskFormatterInstance: Intl.DateTimeFormat | null = null;
  private static wipeRiskDateFormatter(): Intl.DateTimeFormat {
    if (!FrontendConfigUtils.wipeRiskFormatterInstance) {
      FrontendConfigUtils.wipeRiskFormatterInstance = new Intl.DateTimeFormat('zh-TW', {
        timeZone: FRONTEND_BUSINESS_RULES.DATE_TIME.TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    }
    return FrontendConfigUtils.wipeRiskFormatterInstance;
  }

  static getWipeRiskLockStatus(
    classDateStr: string,
    now: Date,
    classStartTimeStr?: string,
    role: 'admin' | 'teacher' = 'admin',
  ) {
    // 1. Get 'now' in Taipei YYYY-MM-DD
    // 2026-09-20 (browser-freeze M1, HIGH-1): shared formatter — this runs per
    // class row on the daily tables; construction was ~80× the cost of a call.
    const nowTaipeiStr = FrontendConfigUtils.wipeRiskDateFormatter()
      .format(now)
      .replace(/\//g, '-'); // Ensure YYYY-MM-DD format

    // Normalize classDateStr to YYYY-MM-DD
    const normalizedClassDate = classDateStr.replace(/\//g, '-');

    let isWipeRiskLocked = false;
    let showEarlyAccessWarning = false;

    if (role === 'teacher') {
      // Teacher Policy: 2-hour lock window
      if (classStartTimeStr) {
        const classDateTimeStr = `${normalizedClassDate}T${classStartTimeStr}:00+08:00`;
        const classStart = new Date(classDateTimeStr);
        const minutesUntilStart = (classStart.getTime() - now.getTime()) / (1000 * 60);

        // Lock if more than window minutes before start
        const windowMinutes = FRONTEND_BUSINESS_RULES.WIPE_RISK_LOCK.TEACHER_LOCK_WINDOW_MINUTES;
        isWipeRiskLocked = minutesUntilStart > windowMinutes;
      } else {
        // Fallback if no time provided: Lock if future day
        const isFutureDay = normalizedClassDate > nowTaipeiStr;
        isWipeRiskLocked = isFutureDay;
      }
    } else {
      // Admin Policy: Lock future days only (Whole day unlocked for today)
      // Compare strings (lexicographically works for YYYY-MM-DD)
      // Locked if current date is strictly BEFORE class date (i.e. future classes)
      const isFutureDay = normalizedClassDate > nowTaipeiStr;
      const isSameDay = normalizedClassDate === nowTaipeiStr;

      isWipeRiskLocked = isFutureDay;

      // Warning logic: Show warning if same day AND (optional: before class start)
      // "提醒：開課當天開放編輯功能... 若在實際開課前更新上傳 Excel..."
      if (isSameDay && !isWipeRiskLocked && classStartTimeStr) {
        // Check if before start time
        // Construct class start time on class date in Taipei timezone
        const classDateTimeStr = `${normalizedClassDate}T${classStartTimeStr}:00+08:00`;
        const classStart = new Date(classDateTimeStr);

        // If now is before class start, show warning
        if (now < classStart) {
          showEarlyAccessWarning = true;
        }
      }
    }

    return { isWipeRiskLocked, showEarlyAccessWarning };
  }

  static getValidationRule(type: keyof typeof VALIDATION_CONFIG.RULES, ...args: number[]): string {
    const rule = VALIDATION_CONFIG.RULES[type];
    return typeof rule === 'function'
      ? (rule as (...args: number[]) => string)(...args)
      : (rule as string);
  }

  static getNotificationConfig(type: keyof typeof NOTIFICATION_CONFIG.TYPES) {
    return { ...NOTIFICATION_CONFIG.DEFAULTS, ...NOTIFICATION_CONFIG.TYPES[type] };
  }

  /**
   * Returns the upper-bound date (end-of-day Taipei) for the teacher mobile
   * future window: today + FUTURE_DAYS_CAP days.
   *
   * `today` should be sourced from debugTimeStore.getCurrentTime() at call
   * sites so QA time-shift testing remains accurate.
   */
  static getTeacherMobileMaxFutureDate(today: Date): Date {
    const cap = FRONTEND_BUSINESS_RULES.TEACHER_MOBILE.FUTURE_DAYS_CAP;
    const max = new Date(today);
    max.setDate(max.getDate() + cap);
    max.setHours(23, 59, 59, 999);
    return max;
  }

  /**
   * True if `date` falls within the teacher mobile window: today (00:00)
   * through today + FUTURE_DAYS_CAP days (23:59:59.999), inclusive on both ends.
   * Past dates also return true (the cap targets future only).
   */
  static isWithinTeacherMobileFutureWindow(date: Date, today: Date): boolean {
    const max = FrontendConfigUtils.getTeacherMobileMaxFutureDate(today);
    return date.getTime() <= max.getTime();
  }

  /**
   * True if `date` is a day the 救生員 view may show (incl. a teacher's 救生員模式):
   * today − ROSTER_VIEW_ONLY_DAYS_BACK … today + ROSTER_VIEW_ONLY_DAYS_AHEAD,
   * whole days. The backend enforces the same window (classRosterAccess).
   */
  static isWithinRosterViewOnlyWindow(date: Date, today: Date): boolean {
    const TM = FRONTEND_BUSINESS_RULES.TEACHER_MOBILE;
    const first = new Date(today);
    first.setHours(0, 0, 0, 0);
    first.setDate(first.getDate() - TM.ROSTER_VIEW_ONLY_DAYS_BACK);
    const last = new Date(today);
    last.setHours(23, 59, 59, 999);
    last.setDate(last.getDate() + TM.ROSTER_VIEW_ONLY_DAYS_AHEAD);
    return date.getTime() >= first.getTime() && date.getTime() <= last.getTime();
  }

  /** The teacher-mobile date rule for the current view: 救生員 window, else the teacher cap. */
  static isWithinTeacherMobileWindow(date: Date, today: Date, viewOnly: boolean): boolean {
    return viewOnly
      ? FrontendConfigUtils.isWithinRosterViewOnlyWindow(date, today)
      : FrontendConfigUtils.isWithinTeacherMobileFutureWindow(date, today);
  }
}

// ==================================================================================
// CATEGORY: RENEWAL WORKFLOW CONFIGURATION
// Description: All UI strings + thresholds for the session-enrollment renewal workflow.
// Centralized here per CLAUDE.md §7 (no inline strings in components).
// ==================================================================================

/**
 * Frontend mirror of the backend `BUSINESS_RULES.SESSION_ENROLLMENT` block.
 * Single source of truth for session-reservation business numbers — must stay
 * in sync with `functions/src/config/appConfig.ts`. See
 * `docs/SESSION_ENROLLMENT_BUSINESS_RULES.md` for the rules each value enforces.
 *
 * Phase 8-13 frontend (composable / TimeSlotPage cell rule / dashboard
 * 續報提醒 tab / topbar badge) reads directly from this object.
 */
/**
 * FB2/S4 (2026-06-21): DISPLAY-ONLY shortening of long `endingReason` labels for
 * width-constrained surfaces (the TimeSlotPage 狀態 chip + the 近期未處理 tab's
 * 「自動歸檔 — …」 row). The `endingReason` value itself is a classification KEY
 * matched in ~18 BE/FE sites + stored on the doc, so it is NOT renamed — only its
 * on-screen label is shortened here. Full text is preserved in the chip tooltip and
 * in the 學生紀錄 timeline (an audit/detail view, which keeps the full wording).
 * Centralized so the two surfaces never drift.
 */
export const ENDING_REASON_SHORT_LABEL: Record<string, string> = {
  完成續報但未上傳Excel: '完成續報未上傳',
};
export function shortenEndingReason(reason: string): string {
  return ENDING_REASON_SHORT_LABEL[reason] ?? reason;
}

/**
 * The endingReason tags of the three nightly archive scans (Scan A / B / C,
 * functions/src/sessionEnrollments/maintenance.ts) — ONE list (review H-7) read by the
 * 近期未處理 query (useSessionEnrollments/queries.ts, part of its `in` list), its
 * 「自動歸檔 —」 label (utils/renewalMissedRowView.ts) and PROCESS_GAP_ENDING_REASONS below.
 */
export const AUTO_ARCHIVE_SCAN_ENDING_REASONS = [
  '課務未通知',
  '期限內未結案',
  '完成續報但未上傳Excel',
] as const;

/**
 * 2026-09-28 (Hub UI; PO 「red on both」) — endings that are PROCESS GAPS (a step of the
 * renewal flow was missed), as opposed to a decision (不續報, 轉班) or a routine end
 * (時段下架, 無未來預約): the three archive scans + 學期到期未續報 (auto-ended) + 保留到期仍未收到約課資料
 * (bf-grace-expired). Every surface draws them red-2 with the warning icon — the 排課時段表
 * chip (useEnrollmentStatusChips/renewalChips.ts) and 近期未處理
 * (utils/renewalMissedRowView.ts) — 「a process gap must not look like a routine end」
 * (style sheet V6).
 */
export const PROCESS_GAP_ENDING_REASONS: ReadonlySet<string> = new Set([
  ...AUTO_ARCHIVE_SCAN_ENDING_REASONS,
  'auto-ended',
  'bf-grace-expired',
]);
export function isProcessGapEndingReason(reason: string | null | undefined): boolean {
  return !!reason && PROCESS_GAP_ENDING_REASONS.has(reason);
}

/**
 * Stored `endingReasonDetail` values that code compares or writes (2026-09-29, audit N7).
 * ELSEWHERE = 其他處理 › 不續報 › 已改報其他時段 (endingReasonNote holds the new slot). The
 * legacy 長假 / 其他 stay literals. MIRROR of ENDING_REASON_DETAILS in
 * functions/src/config/appConfig.ts — pinned by functions/scripts/check-config-mirror.cjs.
 */
export const ENDING_REASON_DETAILS = {
  ELSEWHERE: '改報其他時段',
} as const;

export const SESSION_ENROLLMENT_CONFIG = {
  /**
   * 2026-08-23 (課務追蹤備註) — textarea maxlength for the per-row 續報 tracking
   * note. Mirror of functions/src/config/appConfig.ts
   * BUSINESS_RULES.SESSION_ENROLLMENT.TRACKING_NOTE_MAX_LENGTH (the CF rejects
   * anything longer; this stops the admin from typing past it in the first place).
   */
  TRACKING_NOTE_MAX_LENGTH: 1000,
  /**
   * 2026-08-23 (課務追蹤備註) — a note untouched for this many days renders its
   * `作者 · N 天前` stamp in amber. Amber, not red — quiet isn't automatically wrong.
   * 2026-09-28 (Hub UI, PO Q14): 7 → 10, and ONE threshold for every notes cell in
   * 課務總覽 (續報 追蹤備註 and 候補 備註). Counted in Taipei calendar days, stale at
   * ≥ this many (utils/taipeiDateLabels.ts noteStamp). FE-only: no backend reader.
   */
  TRACKING_NOTE_STALE_DAYS: 10,
  /**
   * F1 (2026-10-08) — 學生紀錄 › 學期紀錄: how far a 報名 / 重新報名 entry's sentAt may sit from
   * the record's createdAt and still be the entry written WITH the creation
   * (utils/enrollmentManualLog), compared to the sub-second (the callable's _nanoseconds read).
   * Both stamps come from one `now`; the window only absorbs records older than the repo's history
   * whose two stamps might differ by a few ms. The hold-born and release-pair matches stay exact.
   * Mirror: functions BUSINESS_RULES.SESSION_ENROLLMENT.TIMELINE_CREATION_MATCH_MS — the 常態班更名
   * 重新報名 conversion (classIdentityRestore birthEntryOf) picks the same birth; kept equal by
   * functions/scripts/check-config-mirror.cjs.
   */
  TIMELINE_CREATION_MATCH_MS: 1000,
  // Mirror of functions/src/config/appConfig.ts BUSINESS_RULES.SESSION_ENROLLMENT.AUTO_TAG_THRESHOLD.
  // 2026-05-22 lowered 10 → 8. See backend mirror for full rationale, and
  // docs/EXCEL_UPLOAD_AND_DETECTION.md for the upload-range operating
  // assumption that makes this safe.
  AUTO_TAG_THRESHOLD: 8,
  /**
   * Mirror of functions/src/config/appConfig.ts
   * BUSINESS_RULES.SESSION_ENROLLMENT.LSED_REVIEW_MIN_COUNT.
   * Lower bound for flagging 待確認 (LSED grey-area review): N≥3 future 學期-ticket
   * bookings strictly past the current LSED, below the AUTO_TAG_THRESHOLD=8 that
   * auto-extends. 3 ≤ N ≤ 7 → flag lsedReviewStatus='pending' (human review);
   * N ≤ 2 → not flagged (parent self-book / deliberate 1–2 partial); N ≥ 8 →
   * existing auto-extend. AUTO_TAG_THRESHOLD is untouched.
   */
  LSED_REVIEW_MIN_COUNT: 3,
  /**
   * 2026-10-24 (約課切斷確認, tasks/todo-lsed-same-sitting.md Spec 1 / Spec 9, D20 / D21) — a
   * 最後約課日 written on or within this many Taipei days before the last class day of the latest
   * completed upload is flagged 約課切斷確認; the 約課通知 window warning reads the same flag
   * (D21 「14 天＝同一條規則」). MIRROR of functions BUSINESS_RULES.SESSION_ENROLLMENT
   * .LSED_CUTOFF_WINDOW_DAYS (check-config-mirror).
   */
  LSED_CUTOFF_WINDOW_DAYS: 14,
  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.3, C-05 / P10) — the 「期間已約 N 堂」 label on a 續報 row
   * (and its 排課時段表 chip) shows only from this many bookings: the 結算 threshold
   * (LSED_REVIEW_MIN_COUNT, D-02) — 1–2 堂 are most likely 補課 and are not labelled. A getter
   * so the number has ONE home. Mirror: functions BUSINESS_RULES.SESSION_ENROLLMENT
   * .PARTIAL_BOOKINGS_LABEL_MIN_RENEWAL (pinned by renewalConfigParity.test.ts). Read by W2.5's four callers.
   */
  get PARTIAL_BOOKINGS_LABEL_MIN_RENEWAL(): number {
    return SESSION_ENROLLMENT_CONFIG.LSED_REVIEW_MIN_COUNT;
  },
  /**
   * 2026-10-13 (W0.3, D-01c) — a 轉班 new-class hold (待約課) keeps 「期間已約 N 堂」 from 1:
   * it is what 結算轉班 and the 03:30 expiry rest on (unchanged). Mirror: functions
   * BUSINESS_RULES.SESSION_ENROLLMENT.PARTIAL_BOOKINGS_LABEL_MIN_TRANSFER (check-config-mirror).
   */
  PARTIAL_BOOKINGS_LABEL_MIN_TRANSFER: 1,
  /**
   * Mirror of functions/src/config/appConfig.ts
   * BUSINESS_RULES.SESSION_ENROLLMENT.PAIR_REVIEW_ENDED_CONTEXT_DAYS.
   * Pair-review (待確認統一涵蓋) context window: a sub-threshold NEW pair is
   * tagged 疑似換時段 when the same student has an enrollment ended within
   * this many days at another slot. Display hint only — never a row filter.
   */
  PAIR_REVIEW_ENDED_CONTEXT_DAYS: 30,
  // 2026-10-04 (轉班鎖定 Phase 5): PAIR_REVIEW_TRANSFER_CONFLICT_STATES (C2) and
  // TRANSFER_SEAT_BLOCK_STATES (Round 4 C-1) retired with their server mirrors — which 轉班
  // decides a class is the ONE lock rule now (utils/transferLock.ts ⇄ functions
  // sessionEnrollments/transferLock.ts).
  // Mirror of functions/src/config/appConfig.ts BACKFILL_DEFAULT_THRESHOLD.
  // 0–2 future 學期 bookings → not reserved; ≥3 → reserved by the admin
  // backfill button. Auto-trigger at upload still uses AUTO_TAG_THRESHOLD (8).
  BACKFILL_DEFAULT_THRESHOLD: 1,
  // 2026-05-15 Wave 5 cleanup: RENEWAL_REMINDER_WEEKS + RENEWAL_URGENT_WEEKS
  // removed. Both superseded by the *_DAYS constants below (RENEWAL_REMINDER_DAYS=33,
  // RENEWAL_URGENT_THRESHOLD_DAYS=21). Live composables (queries, derived, topbar)
  // read the *_DAYS values directly; the *_WEEKS aliases had zero readers.
  MISSED_RENEWAL_WINDOW_DAYS: 60,
  DEFAULT_TIMESLOT_CAPACITY: 12,
  /** Typical session length in classes — used as fallback in notification templates
   *  when the real class count isn't yet derivable. Spec: sessions are 12+ classes. */
  SESSION_TYPICAL_CLASS_COUNT: 12,
  // Wave 5 (2026-05-14): removed MANUAL_RESERVATION_BF_WINDOW_DAYS,
  // RENEWAL_BF_GRACE_DAYS, RENEWAL_BF_WARNING_DAYS — superseded per spec §14.
  /** Frontend cell-lock threshold for the ❓ verification icon. Set to 1 so
   * every unverified row's beyond-LSED cells show ❓. Verification is fully
   * decoupled from reservation logic — see spec doc §II. */
  VERIFICATION_SHOW_LOCK_MIN_COUNT: 1,
  /** total grace before 轉班 hold expires (信任鎖 — admin commits, 14 days for BF/payment) */
  TRANSFER_BF_GRACE_DAYS: 14,
  /** days remaining when warning highlight fires for 轉班 grace lock */
  TRANSFER_BF_WARNING_DAYS: 7,
  /**
   * PO 2026-10-05 Q13b: a 待約課 whose new class is on 暫時保留名額 never runs out — its
   * 「暫時保留名額 M/D 起 · 名字」 line (轉班 › 待約課, 名額保留) turns amber once the hold is
   * this many Taipei calendar days old (utils/transferHoldCount).
   */
  TRANSFER_SPECIAL_HOLD_AGE_DAYS: 30,
  /**
   * PO 2026-10-05 Q13d: the statuses the 續報 › 暫時保留名額 list (its listener,
   * useSessionEnrollments/queries.ts Subscription B-special) and the home 「筆暫時保留」 count
   * take — a 轉出中 row on 暫時保留名額 (the fail-safe keeps 轉出中, B2) is a held seat too.
   */
  SPECIAL_HOLD_LIST_STATUSES: ['active', 'transferring'] as readonly SessionEnrollmentStatus[],
  /**
   * When the 轉班 grace expiry (expireTransferGraceWindows) runs, Taipei wall clock: a
   * 待約課 record turns 未完成 at the first run at or after concludedAt +
   * TRANSFER_BF_GRACE_DAYS — utils/transferGrace, read by every "has the hold run out"
   * inference (deep review L7). MIRROR of the backend
   * BUSINESS_RULES.SESSION_ENROLLMENT.TRANSFER_GRACE_EXPIRY_RUN (check-config-mirror).
   */
  TRANSFER_GRACE_EXPIRY_RUN: { hour: 3, minute: 30 },
  /**
   * PO 2026-10-03 (未完成 自動歸檔): an open 未完成 nobody handled is closed by the 03:30
   * run on this Taipei calendar day after its hold ran out (a late payer the first night
   * after — functions programWaitlist/transferAutoClose). The screens quote it (未完成's
   * sentence, 使用說明, the ⋮ note). MIRROR of the backend
   * BUSINESS_RULES.SESSION_ENROLLMENT.TRANSFER_EXPIRED_AUTO_CLOSE_DAYS (check-config-mirror).
   */
  TRANSFER_EXPIRED_AUTO_CLOSE_DAYS: 30,
  /**
   * 其他處理 › 不續報's note (endingReasonNote — 已改報其他時段's 新時段, and the note of the
   * other sub-reasons): at most this many characters; the backend refuses longer (deep
   * review L13). MIRROR of the backend
   * BUSINESS_RULES.SESSION_ENROLLMENT.ENDING_REASON_NOTE_MAX_LENGTH (check-config-mirror).
   */
  ENDING_REASON_NOTE_MAX_LENGTH: 200,
  /**
   * 已改報其他時段's `endingReasonTimeslotId` (the other enrollment's timeslotId, sent with
   * the 已另報 fact — deep review D2): at most this many characters — room for a long class
   * name and several teachers (backend round 2, L-7); a longer id is not sent (the backend
   * would refuse the whole 不續報; the note still names the slot). MIRROR of the backend
   * BUSINESS_RULES.SESSION_ENROLLMENT.ENDING_REASON_TIMESLOT_ID_MAX_LENGTH.
   */
  ENDING_REASON_TIMESLOT_ID_MAX_LENGTH: 200,
  /**
   * The optional 原因 sent with manualMarkAsSessionEnrollment (`note` — appended to the 學期紀錄
   * line as 「最後約課日：…｜原因：…」): the input's maxlength in 修改最後約課日 / 確認保留名額
   * (SetReservationModal) and 補正最後約課日 (LsedCutoffFixModal via utils/cutoffFixLines).
   * MIRROR of BUSINESS_RULES.SESSION_ENROLLMENT.MANUAL_MARK_NOTE_MAX_LENGTH (check-config-mirror;
   * the backend refuses a longer 原因 — reanchorOutcome.ts assertManualMarkNoteLength, W3.10).
   */
  MANUAL_MARK_NOTE_MAX_LENGTH: 200,
  /**
   * 2026-10-24 (約課切斷確認 Spec 13, W5.4 BE-F2 / P-1) — the page a 修改最後約課日 came from
   * (manualMarkAsSessionEnrollment `origin`); it decides the 約課通知 來源
   * (bookingNoticeSource.page). PAIR_REVIEW = 確認保留名額 from 待確認, RENEWAL = the 續報 lists
   * (incl. ⋮ 其他處理) and the 通知 tab (N37 (iv), N39), REVIEW = 待確認, TRANSFER = the 轉班-host
   * peek drawer; no origin = 排課時段表. The SessionEnrollmentMutationPayload `origin` union
   * derives from it. MIRROR of functions BUSINESS_RULES.SESSION_ENROLLMENT.MANUAL_MARK_ORIGINS
   * (check-config-mirror — the server's validateRequest takes only these values).
   */
  MANUAL_MARK_ORIGINS: {
    PAIR_REVIEW: 'pairReview',
    RENEWAL: 'renewal',
    REVIEW: 'review',
    TRANSFER: 'transfer',
  } as const,
  /**
   * The deploy day of the 轉為候補 release (Taipei calendar day) — records concluded before it
   * may use the 14-day inference; records concluded on/after it read `targetConfirmedAt`
   * only (deep review R2-F3: the nightly expiry skips holds that are unverified or carry
   * 學期 bookings, so "past the run and still 待約課" proves nothing for a record the
   * stamping backend handled). Used by utils/transferGrace legacyInferenceApplies. Update
   * it if the deploy moves. MIRROR of the backend
   * BUSINESS_RULES.SESSION_ENROLLMENT.TARGET_CONFIRMED_AT_SINCE (check-config-mirror).
   */
  TARGET_CONFIRMED_AT_SINCE: '2026-10-01',
  /** How long 'completed' transfer rows stay visible in the 已轉班 sub-tab after `concludedAt`.
   * After this window the row drops out of the active 學期轉班 queue but is preserved
   * forever in the student's 學生紀錄 timeline + `previousSessions[]` audit history. */
  TRANSFER_COMPLETED_RETENTION_DAYS: 30,
  /** /home dashboard: transfer record older than this → red dot on 待轉班處理 card */
  DASHBOARD_TRANSFER_RED_DAYS: 7,
  /** /home dashboard: unverified enrollment older than this → red dot on 待驗證 card */
  DASHBOARD_VERIFICATION_RED_DAYS: 7,
  // 2026-05-14 renewal redesign — new constants (spec §14). Mirror of
  // functions/src/config/appConfig.ts. Replaces the WEEKS-based constants
  // above (cleanup in Wave 5).
  /** Days before LSED that surfaces row in 學期續報 tab Stage 1. Replaces RENEWAL_REMINDER_WEEKS=4. */
  RENEWAL_REMINDER_DAYS: 33,
  /** LEGACY (2026-09-02 通知期限對齊): the pre-2026-09-02 public renewal-deadline anchor (LSED-21d). Parents are now quoted the seat-lock date itself (computeRenewalLockMs — PO 2026-09-02 通知期限對齊); kept ONLY so the FE can re-derive what LEGACY rows (no `renewalNoticeDeadline`) were told. */
  RENEWAL_PUBLIC_DEADLINE_DAYS: 21,
  /** Days before LSED when row visual escalates to 緊急 red tint (Stage 1). Replaces RENEWAL_URGENT_WEEKS=2. */
  RENEWAL_URGENT_THRESHOLD_DAYS: 21,
  /** Days before LSED when row banner shows 最後警示 (LSED-17d → LSED-14d, 3-day window). */
  RENEWAL_FINAL_WARNING_THRESHOLD_DAYS: 17,
  /** Days before LSED = the ON-TIME seat-release point (an on-time notice locks to LSED − 14d; a late one to today + RENEWAL_MIN_RUNWAY_DAYS, capped at LSED — see computeRenewalLockMs). Un-notified rows auto-archive (Scan A) at LSED − 14d. */
  RENEWAL_SYSTEM_CAP_DAYS: 14,
  /**
   * 2026-06-24 (runway redesign §2a / Messaging W1) — minimum days the renewal
   * seat-trust lock guarantees forward regardless of how late the admin notifies.
   * Mirror of functions/src/config/appConfig.ts
   * BUSINESS_RULES.SESSION_ENROLLMENT.RENEWAL_MIN_RUNWAY_DAYS (parity test
   * functions/src/__tests__/renewalConfigParity.test.ts). Consumed by
   * FrontendConfigUtils.computeRenewalLockMs (mirror of the BE helper):
   *   reservationLockedUntil = min(LSED, max(LSED−RENEWAL_SYSTEM_CAP_DAYS, today + RENEWAL_MIN_RUNWAY_DAYS))
   * 2026-09-24 (PO, 續報 期限與提醒 M1): 14 → 10 — a late notice gives the parent
   * 10 days; spec docs/SESSION_ENROLLMENT_BUSINESS_RULES.md §續報日期規則 A4.
   */
  RENEWAL_MIN_RUNWAY_DAYS: 10,
  /**
   * 2026-06-24 (Messaging subsystem / W1, §2e zone thresholds — PO decision 1).
   * NAMED zone-threshold consts for the 保留期限 heat-map so the zones carry NO
   * inline numbers. Anchor = Taipei calendar days remaining until the LSED−14d
   * release point (FrontendConfigUtils.renewalPendingZone — D-4, 2026-09-24):
   *   🟢 green  剩 ≥ RENEWAL_IDEAL_WINDOW_DAYS (≥14天)            → LSED−33→−28
   *   🟡 amber  RENEWAL_LASTCHANCE_DAYS ≤ 剩 < ideal (10–13天)     → LSED−27→−24
   *   🔴 red    剩 < RENEWAL_LASTCHANCE_DAYS (<10天)              → LSED−23→−14
   *   ⚫ grey   已釋出 — the release instant has passed
   * Red starts exactly where a notice can no longer give the on-time deadline
   * (LSED−23 = the day isLateRenewalNotice turns on). Also read by the topbar dot
   * and, via the BE mirror, the digest S2 (PO Q4 2026-09-24). RENEWAL_IDEAL_WINDOW_DAYS
   * has its own BE mirror since 2026-09-29 (parity-tested): the 課務每日 LINE 提醒 counts
   * 該通知續報 on the last green day, LSED−28.
   */
  RENEWAL_IDEAL_WINDOW_DAYS: 14,
  /** 2026-06-24 (Messaging / W1, §2e) — last-chance step: 🔴 red when 剩 < 10天 (was 7; = RENEWAL_MIN_RUNWAY_DAYS since 2026-09-24). BE mirror BUSINESS_RULES.SESSION_ENROLLMENT.RENEWAL_LASTCHANCE_DAYS (parity-tested). See RENEWAL_IDEAL_WINDOW_DAYS. */
  RENEWAL_LASTCHANCE_DAYS: 10,
  /**
   * 2026-09-25 (續報 期限與提醒 M3, A10 / PO Q3) — the 再次提醒家長 day R = the 首次通知
   * day + floor(c × RENEWAL_REMINDER_AT_FRACTION) Taipei calendar days, where c = the
   * calendar days from 首次通知 to the date the parent was told
   * (FrontendConfigUtils.renewalReminderDayMs). Halfway, earlier day on a half. Since
   * 2026-09-29 the backend has a mirror (functions renewalReminderDayMs + these two
   * constants, parity-tested) for the 課務每日 LINE 提醒's 要再次提醒家長 count.
   */
  RENEWAL_REMINDER_AT_FRACTION: 0.5,
  /** No reminder (no button, not counted) when the parent had fewer than this many calendar days at notice — c ≤ 5, which only 延長並通知續報 can produce (PO Q3 2026-09-24). */
  RENEWAL_REMINDER_MIN_SPAN_DAYS: 6,
  /** The 續報提醒 message leads with ⚠️ when this many calendar days or fewer are left to 保留至 (mockup §②, D-4 count). */
  RENEWAL_REMINDER_URGENT_DAYS: 3,
  /**
   * 2026-09-25 (續報 期限與提醒 M3, D-4) — 已通知·待確認 row escalation by days left to the
   * seat lock (保留至): 緊急 red row tint at ≤ RENEWAL_LOCK_URGENT_DAYS, 最後警示 banner
   * 「倒數最後 N 天」 at ≤ RENEWAL_LOCK_FINAL_WARNING_DAYS. Taipei calendar days in the
   * 續報 tables (src/utils/renewalRowEscalation.ts); isInGraceWindow (排課時段表 cells)
   * reads the same two numbers. BE digest S3 mirrors both (RENEWAL_LOCK_URGENT_DAYS = 7 list
   * boundary, RENEWAL_STAGE2_LOCK_URGENT_DAYS = 3 緊急 tag) — parity-tested since review F-16
   * (functions/src/__tests__/renewalConfigParity.test.ts).
   */
  RENEWAL_LOCK_URGENT_DAYS: 7,
  RENEWAL_LOCK_FINAL_WARNING_DAYS: 3,
  /** Days post-[完成續報流程] to hold slot while waiting for Excel upload (cyan sync lock). */
  RENEWAL_POST_COMPLETION_GRACE_DAYS: 7,
  /** LEGACY (2026-09-02 通知期限對齊): the pre-2026-09-02 Net-7 response floor of the old
   *  public formula clamp(today + N, LSED-21d, LSED-14d) (2026-05-16 "庚 case"). Superseded
   *  by the seat-lock date (renewalNoticeDeadlineMs); kept ONLY for the legacy-row
   *  re-derivation in resolveRenewalNoticeDeadlineMs. */
  RENEWAL_MIN_RESPONSE_DAYS: 7,
  /** 2026-06-22 (Option B / W6): days a 通知約課成功 to-do lives before the nightly
   *  auto-clear sweep drops it; also the W8-migration recency cutoff. Tunable. */
  RENEWAL_SUCCESS_NOTIFY_WINDOW_DAYS: 30,
  /** 2026-07-17 (lsed-reanchor-notify Pass-7 C1): a 約課通知待發 row this old (days
   *  since the flag was set) triggers the nightly supervisor digest nudge. The
   *  effective window is NUDGE..WINDOW (20-29d) — rows past WINDOW are swept the
   *  same night. Backend-only nightly job consumes this; mirrored for parity. */
  RENEWAL_SUCCESS_NOTIFY_NUDGE_DAYS: 20,
  /** 2026-06-22 (Option B / W5): a 約課通知待發 row turns red once MORE than this many
   *  Taipei calendar days have passed since the day it was flagged → red from 第 4 天, all
   *  day (PO 2026-09-25: calendar days like the other 續報 tables; `successNotifyAge`). */
  RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS: 2,
  /**
   * 2026-06-24 (Messaging subsystem / W1, §2c state-gate / §7(2)) — the
   * EnrollmentState values on which a parent notice may be offered from a row. The name
   * is historical: the 通知家長？ picker it first gated (after a 設定保留名額 LSED edit) was
   * removed in W10.4 F3 (D26 (3)); its users now are the 排課時段表 通知家長 (補送/重送)
   * entry (StudentLifecycleStatusCell.vue) and the typed re-export in
   * useEnrollmentActions.ts. A renewal-positive or reclaim row may legitimately get a
   * 約課成功 / 學期日期更新 notice; a transfer-in-flight / special-hold row NEVER should
   * (transferring / transfer-awaiting / special-hold are excluded).
   *
   * String literals (not the imported EnrollmentState type — appConfig.ts must not
   * depend on a composable, and useEnrollmentActions.ts already imports FROM here →
   * a back-import would be circular). useEnrollmentActions.ts adds the compile-time
   * `satisfies readonly EnrollmentState[]` guarantee so these stay in lock-step with
   * the union (no silent typo, no parallel list).
   */
  NOTIFY_PICKER_ALLOWED_STATES: [
    'active-clean',
    'auto-renewed',
    'renewal-notified',
    'renewal-success-pending',
    'soft-end-not-renewing',
    'soft-end-scan-a',
    'soft-end-scan-b',
    'soft-end-transferred',
    'soft-end-other',
  ] as const,
} as const;

/**
 * 已另報 (PO 2026-09-29, 轉班 › 轉為候補 M2; docs/tools/transfer-to-waitlist.html § 2) — the
 * rule: the student has ANOTHER 學期 enrollment in a different slot that is active, carries
 * no endingReason, has a 最後約課日 more than LSED_LEAD_DAYS ahead (real Excel data, not a
 * class winding down), and whose enrolledAt (its first booked class) falls AFTER the row's
 * anchor — 轉班 = the record's createdAt; 續報 = LSED − RENEWAL_WINDOW_DAYS (the day the row
 * entered the 待通知 window); 暫時保留名額 = the hold's start; a linked 候補 row = its first
 * registration, originalCreatedAt ?? createdAt (9B-5, utils/waitlistElsewhere). A second
 * class the family already had before the anchor is never flagged (EXTEND keeps
 * enrolledAt). The one reader of both numbers is utils/enrolledElsewhere.ts.
 */
export const ENROLLED_ELSEWHERE = {
  /** The other slot counts only while its 最後約課日 is more than this many days away. */
  LSED_LEAD_DAYS: 7,
  /** 續報 anchor: the day the row entered the 待通知 window (LSED − this). */
  RENEWAL_WINDOW_DAYS: SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_DAYS,
} as const;

/**
 * 排課時段表 ⇄ 待確認 on a student row (待確認 Round 4, PO 2026-10-03 「go with your
 * recommendations」 — decision C6 #6; docs/tools/review-queue-review-decisions.html). Website
 * only. Readers: utils/lsedReviewLink.ts, useSessionEnrollments core.ts / pairReview.ts (the
 * live statuses). The 轉班 checks that used to live here (C3 / R4-3) are the ONE 轉班鎖定 rule
 * now (utils/transferLock.ts, PO 2026-10-04).
 */
export const TIMESLOT_ROW_GUARDS = {
  /**
   * The statuses of a live enrollment — the ONE list (Round 4 review): the enrollments
   * feed's Firestore `in` query (useSessionEnrollments/core.ts ACTIVE_STATUSES) and the
   * 新時段報名 live-pair join (pairReview.ts) read it, so these are the statuses 待確認 lists.
   * A student with none of them at a slot holds no seat there: 設定最後約課日 / 暫時保留名額
   * would CREATE one (R4-2). Equal to the server's sessionEnrollments/utils.ts
   * LIVE_REVIEW_STATUSES — pinned by functions reviewRowActionableParity.test.ts.
   */
  LIVE_ENROLLMENT_STATUSES: [
    'active',
    'paused',
    'transferring',
  ] as readonly SessionEnrollmentStatus[],
  /**
   * C6 #6a — the hover line added to 「期間已約 N 堂」 (排課時段表 chip, 續報 row flag) while
   * its enrollment's row is listed in 待確認; a click opens 課務總覽 › 待確認 filtered to
   * that slot.
   */
  BOOKED_DURING_REVIEW_LINK_TITLE: '點一下到 待確認 處理這幾堂',
} as const;

/**
 * Multi-file Excel schedule upload rules.
 *
 * BF (BookFastPOS) exports at most 3 months of schedule per file, but session enrollment
 * detection (AUTO_TAG_THRESHOLD = 8) requires full forward visibility for LSED correctness.
 * Admin uploads N files in one atomic session; backend validates coverage + gaps + begin
 * date before running the merged sync. Mirror of functions/src/config/appConfig.ts
 * BUSINESS_RULES.SCHEDULE_UPLOAD — must stay in sync. Spec: docs/EXCEL_UPLOAD_AND_DETECTION.md.
 */
export const SCHEDULE_UPLOAD_CONFIG = {
  /**
   * Required forward coverage from today (CALENDAR MONTHS). Hard-blocked at this threshold.
   * Months (not fixed days) because BF exports operate on calendar-month boundaries —
   * two contiguous BF files cover ~181-186 days depending on which months are spanned.
   */
  MIN_COVERAGE_MONTHS: 6,
  /**
   * Max calendar months BF allows in a single export file. Drives the expected-slot
   * scaffold count. If BF raises this to 6, change to 6 → admin uploads 1 file instead
   * of 2. No other code changes needed for forward compatibility.
   */
  BF_MAX_EXPORT_MONTHS: 3,
  /** Cap on files admin can include in one upload session. 6mo / 3mo-per-file = 2 files; cap allows up-to-12mo headroom. */
  MAX_FILES_PER_SESSION: 4,
  /**
   * Begin-date rule: earliest class date across all uploaded files must equal today's calendar
   * date in Asia/Taipei. Catches "wrong export range" mistakes. Tweakable to 'today-or-tomorrow'
   * if school's schedule timing changes.
   */
  BEGIN_DATE_RULE: 'today' as 'today' | 'today-or-tomorrow',
} as const;

/**
 * The `status` values the backend writes on a `scheduleUploadJobs` doc that the website
 * QUERIES by. MIRROR: functions/src/config/appConfig.ts BUSINESS_RULES.JOB_STATUS.VALUES
 * (add a member here only when a website query needs it). Not SYSTEM_CONSTANTS.JOB_STATUS —
 * those English values ('completed' …) are not what the upload writes.
 *
 * COMPLETED (約課切斷確認 W1.4, Spec 1 / P7): composables/useLatestUploadRangeEnd.ts reads
 * the latest 已完成 job (completedAt desc, limit 1) — the same query as the backend's
 * latestUploadRangeEndMs (functions/src/utils/uploadRangeEnd.ts) — and utils/uploadRangeEnd.ts
 * takes its range end.
 */
export const SCHEDULE_UPLOAD_JOB_STATUS = {
  COMPLETED: '已完成',
} as const;

// ──────────────────────────────────────────────────────────────────────────
// Multi-file upload validation utilities. Mirror of functions/src/config/appConfig.ts
// `MultiFileUploadRules`. Used by ScheduleUploadComponent + useUploader to disable
// the submit button until coverage/gaps/begin-date all pass. Backend runs the same
// rules independently (frontend disabled-state is UX-only; backend is authoritative).
// ──────────────────────────────────────────────────────────────────────────

export interface DateRange {
  from: Date;
  to: Date;
}

export interface CoverageResult {
  /** Days covered contiguously from today forward (exclusive day count). */
  covered: number;
  /** Authoritative required-end date = addMonthsTaipei(today, MIN_COVERAGE_MONTHS). */
  requiredEndDate: Date;
  /** Days from today to requiredEndDate — derived; varies 181-186 by start month. */
  requiredDays: number;
  isComplete: boolean;
  coverageEndDate: Date | null;
}

export interface ValidationResult {
  valid: boolean;
  reason?: string;
}

export class MultiFileUploadRules {
  /**
   * Validate forward coverage. Returns covered days from today through the contiguous range
   * starting at-or-before today. Files starting in the future are excluded from this metric
   * (those gaps are surfaced via detectGaps).
   */
  static validateCoverage(fileRanges: DateRange[], now: Date): CoverageResult {
    const today = MultiFileUploadRules.startOfTaipeiDay(now);
    const requiredEndDate = MultiFileUploadRules.getRequiredEndDate(today);
    const requiredDays = MultiFileUploadRules.diffDays(today, requiredEndDate);
    if (fileRanges.length === 0) {
      return {
        covered: 0,
        requiredEndDate,
        requiredDays,
        isComplete: false,
        coverageEndDate: null,
      };
    }
    const merged = MultiFileUploadRules.mergeRanges(fileRanges);
    const startingRange = merged.find((r) => r.from.getTime() <= today.getTime());
    if (!startingRange) {
      return {
        covered: 0,
        requiredEndDate,
        requiredDays,
        isComplete: false,
        coverageEndDate: null,
      };
    }
    const coverageEnd = startingRange.to;
    const covered = Math.max(0, MultiFileUploadRules.diffDays(today, coverageEnd));
    return {
      covered,
      requiredEndDate,
      requiredDays,
      isComplete: coverageEnd.getTime() >= requiredEndDate.getTime(),
      coverageEndDate: coverageEnd,
    };
  }

  /**
   * Return interior gaps between consecutive file ranges within [today, addMonthsTaipei(today, 6)].
   * Trailing shortfall (covered ends before requiredEnd) is reported by validateCoverage instead.
   */
  static detectGaps(fileRanges: DateRange[], now: Date): DateRange[] {
    if (fileRanges.length < 2) return [];
    const today = MultiFileUploadRules.startOfTaipeiDay(now);
    const requiredEnd = MultiFileUploadRules.getRequiredEndDate(today);
    const sorted = fileRanges
      .map((r) => ({
        from: MultiFileUploadRules.startOfTaipeiDay(r.from),
        to: MultiFileUploadRules.startOfTaipeiDay(r.to),
      }))
      .sort((a, b) => a.from.getTime() - b.from.getTime());
    const gaps: DateRange[] = [];
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      const gapStart = MultiFileUploadRules.addDays(prev.to, 1);
      const gapEnd = MultiFileUploadRules.addDays(curr.from, -1);
      if (gapStart.getTime() <= gapEnd.getTime() && gapStart.getTime() < requiredEnd.getTime()) {
        gaps.push({
          from: gapStart,
          to: new Date(Math.min(gapEnd.getTime(), requiredEnd.getTime())),
        });
      }
    }
    return gaps;
  }

  /**
   * Earliest actual class date across all files must match today's Taipei calendar date
   * (or today/tomorrow if BEGIN_DATE_RULE is 'today-or-tomorrow'). Catches the mistake
   * "admin picked the wrong export start date in BF."
   */
  static validateBeginDate(earliestClassDate: Date, now: Date): ValidationResult {
    const today = MultiFileUploadRules.startOfTaipeiDay(now);
    const earliest = MultiFileUploadRules.startOfTaipeiDay(earliestClassDate);
    const rule = SCHEDULE_UPLOAD_CONFIG.BEGIN_DATE_RULE;
    const validTimes =
      rule === 'today'
        ? [today.getTime()]
        : [today.getTime(), MultiFileUploadRules.addDays(today, 1).getTime()];
    if (validTimes.includes(earliest.getTime())) {
      return { valid: true };
    }
    const todayStr = MultiFileUploadRules.formatTaipeiYMD(today);
    const earliestStr = MultiFileUploadRules.formatTaipeiYMD(earliest);
    const expected = rule === 'today' ? '今天' : '今天或明天';
    return {
      valid: false,
      reason: `檔案中最早的預約日期為 ${earliestStr}，需為${expected} (${todayStr})。請至 BF 重新匯出，起始日期需設為${expected}。`,
    };
  }

  /** Cap on files per upload session. */
  static validateFileCount(n: number): ValidationResult {
    const max = SCHEDULE_UPLOAD_CONFIG.MAX_FILES_PER_SESSION;
    if (n < 1) {
      return { valid: false, reason: '請至少加入 1 個 BF 匯出檔案。' };
    }
    if (n > max) {
      return {
        valid: false,
        reason: `一次最多上傳 ${max} 個檔案。請先移除多餘檔案，或合併匯出範圍。`,
      };
    }
    return { valid: true };
  }

  /**
   * Required-end date = where BF's standard contiguous N-file export ends. Mirror of
   * backend.
   *
   * Each BF file covers up to BF_MAX_EXPORT_MONTHS (3) months and the next starts the
   * day after the previous ends, so this is EXACTLY what BF delivers: (a) never demands
   * a date BF can't produce (no false block); (b) the target line sits EXACTLY at file
   * 2's end on every date (no variable offset). Same N-file chain the suggested slots
   * use (UploadCoverageTimeline), so target == file 2 end always.
   *
   * History (2026-05-30): was addMonths(6)+1 (blocked on month-clamp dates like 05-30 →
   * 12-01 unreachable), briefly addMonths(6) (satisfiable but left a 0-4 day gap to file
   * 2's end). Now the chain end: always satisfiable AND zero offset. Still ≥
   * MIN_COVERAGE_MONTHS. See feedback_bf_upload_target_and_date_source memory.
   */
  static getRequiredEndDate(now: Date): Date {
    const today = MultiFileUploadRules.startOfTaipeiDay(now);
    const perFile = SCHEDULE_UPLOAD_CONFIG.BF_MAX_EXPORT_MONTHS;
    const numFiles = Math.ceil(SCHEDULE_UPLOAD_CONFIG.MIN_COVERAGE_MONTHS / perFile);
    let fileStart = today;
    let fileEnd = today;
    for (let i = 0; i < numFiles; i++) {
      fileEnd = MultiFileUploadRules.addMonthsTaipei(fileStart, perFile);
      fileStart = MultiFileUploadRules.addDays(fileEnd, 1);
    }
    return fileEnd;
  }

  // 2026-09-20 (browser-freeze M1, HIGH-1): built once for the class.
  private static readonly TAIPEI_YMD_FORMATTER = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  /** Format a Date as YYYY-MM-DD in Asia/Taipei. */
  static formatTaipeiYMD(d: Date): string {
    return MultiFileUploadRules.TAIPEI_YMD_FORMATTER.format(d);
  }

  /** Anchor a Date to 00:00 Asia/Taipei. */
  private static startOfTaipeiDay(d: Date): Date {
    const ymd = MultiFileUploadRules.formatTaipeiYMD(d);
    return new Date(`${ymd}T00:00:00+08:00`);
  }

  private static addDays(d: Date, n: number): Date {
    return new Date(d.getTime() + n * 24 * 60 * 60 * 1000);
  }

  /**
   * Add N calendar months to a date in Asia/Taipei. Clamps day-of-month to the
   * target month's last day (e.g., Aug 31 + 6mo = Feb 28/29). Mirror of backend.
   */
  static addMonthsTaipei(d: Date, months: number): Date {
    const ymd = MultiFileUploadRules.formatTaipeiYMD(d);
    const [y, m, day] = ymd.split('-').map(Number);
    const totalMonthsIdx = m - 1 + months;
    const newYear = y + Math.floor(totalMonthsIdx / 12);
    const newMonthIdx = ((totalMonthsIdx % 12) + 12) % 12;
    const lastDayOfTargetMonth = new Date(newYear, newMonthIdx + 1, 0).getDate();
    const targetDay = Math.min(day, lastDayOfTargetMonth);
    const mm = String(newMonthIdx + 1).padStart(2, '0');
    const dd = String(targetDay).padStart(2, '0');
    return new Date(`${newYear}-${mm}-${dd}T00:00:00+08:00`);
  }

  private static diffDays(a: Date, b: Date): number {
    return Math.floor((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
  }

  /** Merge overlapping or touching ranges (touching = next.from <= prev.to + 1 day). */
  private static mergeRanges(ranges: DateRange[]): DateRange[] {
    if (ranges.length === 0) return [];
    const sorted = ranges
      .map((r) => ({
        from: MultiFileUploadRules.startOfTaipeiDay(r.from),
        to: MultiFileUploadRules.startOfTaipeiDay(r.to),
      }))
      .sort((a, b) => a.from.getTime() - b.from.getTime());
    const merged: DateRange[] = [{ ...sorted[0] }];
    for (let i = 1; i < sorted.length; i++) {
      const last = merged[merged.length - 1];
      const next = sorted[i];
      const touching = next.from.getTime() <= MultiFileUploadRules.addDays(last.to, 1).getTime();
      if (touching) {
        last.to = new Date(Math.max(last.to.getTime(), next.to.getTime()));
      } else {
        merged.push({ ...next });
      }
    }
    return merged;
  }
}

// School bank account — substituted into customer-facing templates at render time.
// Push-only collection account already publicly visible in every LINE message; bundle
// inclusion does not increase exposure surface. Mirror of functions/src/config/appConfig.ts.
export const SCHOOL_BANK_INFO = {
  bankName: '富邦銀行',
  branch: '東湖分行',
  code: '012',
  accountNumber: '82120000211731',
  accountHolder: '康馨國際企業股份有限公司',
  /**
   * 英文版名稱（給非中文家長的匯款資訊用）。放在這裡而不是寫死在英文範本裡，
   * 是因為換銀行時要改的是**同一筆事實**——分成兩處遲早只改一邊，
   * 然後英文範本會繼續把舊帳號寄給家長（PO 2026-09-20）。
   */
  bankNameEn: 'Taipei Fubon Bank',
  branchEn: 'Donghu Branch',
  accountHolderEn: 'Kangsing International Co., Ltd',
} as const;

/**
 * 對家長的匯款資訊段落 — 全系統唯一的一份寫法（PO 2026-09-24 定稿）。
 *
 * 招生客服說明 的 `{{bankBlock}}`（signupMessages.ts BANK_BLOCK）就是它，所以
 * 正02、匯款帳號（中文）、候02、候03、續報通知、轉班繳費 全部同一段。號碼與戶名
 * 一律取自 SCHOOL_BANK_INFO，這裡只放文字。
 *
 * **靠左、不縮排**：它以 token 代入，縮排過的副本不會被認成 token，編輯器的預設
 * 文案就會露出展開後的帳號（見 useSignupTemplates.seedBodyFor）。
 */
export const SCHOOL_BANK_TRANSFER_BLOCK = `${SCHOOL_BANK_INFO.bankName}-${SCHOOL_BANK_INFO.branch} (代號 ${SCHOOL_BANK_INFO.code})
帳號：${SCHOOL_BANK_INFO.accountNumber}
戶名：${SCHOOL_BANK_INFO.accountHolder}
✏️ 轉帳後請告知後 5 碼
⚠️‼️ 若需要填寫轉帳備註欄，請務必包含約課家長姓名`;

// 公司抬頭 — printed on documents we issue ourselves. Mirror of functions/src/config/appConfig.ts.
export const COMPANY_INFO = {
  name: '康馨國際企業股份有限公司',
  /** 檔名用的短名（CPA 匯出 FILE_NAME_TEMPLATE {company}）；全名留給封面與送件單。 */
  shortName: '康馨國際企業',
  taxId: '42839964',
} as const;

// ──────────────────────────────────────────────────────────────────────────
// Per-class 學期 12-堂 pricing — drives the 「費用」 line in
// RENEWAL_INITIAL_CONTACT + TRANSFER_CONFIRMED_PAYMENT_REMINDER templates.
//
// Two pricing shapes:
//  - { weekday, weekend }  — swimming (親子游泳 + 獨立游泳): weekday class
//    earns 優惠 pricing; weekend class is full price.
//  - { flat }              — FMS體能課: same price every day of week.
//
// Keys are class shortforms (FrontendConfigUtils.classShortName output) so
// callers can look up directly after parsing the timeslotId. Any class not
// in this map falls back to the 【金額】 manual-entry placeholder.
//
// Mirror byte-for-byte in functions/src/config/appConfig.ts.
// ──────────────────────────────────────────────────────────────────────────
export const CLASS_PRICING = {
  // Keyed by class PRICING TIER (derived from the class-name prefix via
  // FrontendConfigUtils.classPricingTier), NOT per-shortform. This makes pricing
  // rename-proof: a class rename (e.g. 小海象班 → 小海象一班/二班) keeps the same
  // tier, so the 「費用」 line can never silently break on a rename. All swim
  // shortforms (親子游泳 + 獨立游泳, incl. *協同 variants) share the SWIM tier.
  SESSION_12_TIERS: {
    // Swimming — weekday earns the early-bird discount.
    SWIM: { weekday: 17900, weekend: 18900 },
    // FMS 體能課 — same price every day.
    FMS_PARENT: { flat: 9300 },
    FMS_INDEPENDENT: { flat: 9900 },
  } as Record<string, { weekday: number; weekend: number } | { flat: number }>,
} as const;

/**
 * Per-class 保留名額 capacity — the M in 「保留名額 N/M」.
 *
 * Keys are class shortforms (FrontendConfigUtils.classShortName output); a class not
 * listed uses SESSION_ENROLLMENT_CONFIG.DEFAULT_TIMESLOT_CAPACITY. Read by the
 * 排課時段表 header, the drawer / 候補 availability and the 額滿提醒 warning in every
 * action window (useSessionEnrollments/slotCapacity.ts).
 *
 * 2026-09-29 (額滿提醒, PO answers page § 14 → A): MIRRORED to
 * functions/src/config/appConfig.ts CLASS_MAX_SLOTS — the server's full-slot backstop
 * refuses at this same M (it replaced the old flat-12 恢復 guard). The two literals are
 * compared by functions/scripts/check-config-mirror.cjs: change both together.
 *
 * Co-op classes (e.g. 小熊班*協同) have their own capacity distinct from base.
 */
export const CLASS_MAX_SLOTS: Record<string, number> = {
  小水狸班: 6,
  小鴨班: 8,
  小青蛙班: 8,
  小企鵝班: 8,
  小海象班: 5,
  小海象一班: 5,
  小海象二班: 5,
  小熊班: 5,
  FMS親子班: 5,
  FMS獨立一班: 7,
  FMS獨立二班: 8,
  FMS獨立三班: 8,
  '小海象班*協同': 10,
  '小海象一班*協同': 10,
  '小海象二班*協同': 10,
  '小熊班*協同': 10,
};

/**
 * /home admin dashboard — color-stage thresholds and refresh cadence.
 *
 * NOTE: Pending-class window reuses backend `BUSINESS_RULES.NOTIFICATIONS.PENDING_CLASSES_LOOKBACK_DAYS`
 * (= 7 days, single source of truth). The pending/discrepancy color rule is fixed:
 * any 今天 item → red; any other item in window → amber. No window-derived constant needed.
 *
 * Discrepancy uses its own narrower window (14 days, vs the existing 180-day query
 * used by notifications). Mirrored in `functions/src/config/appConfig.ts`.
 */
export const HOME_DASHBOARD_CONFIG = {
  /** 差異數 card counts only discrepancies from the last N days (action window for /home). */
  DISCREPANCY_WINDOW_DAYS: 14,
  /** 回饋待審 oldest-item age thresholds. */
  FEEDBACK_AMBER_DAYS: 3,
  FEEDBACK_RED_DAYS: 7,
  /** 付款待審 oldest-item age thresholds. */
  PAYMENT_AMBER_DAYS: 1,
  PAYMENT_RED_DAYS: 3,
  /** Polling interval while /home is the active tab. */
  REFRESH_INTERVAL_MS: 60_000,
} as const;

// ==================================================================================
// CATEGORY: LISTENER LIFECYCLE (idle-pause + curfew transport control)
// Description: Thresholds governing when always-on Firestore listeners pause/resume
// to cut idle/overnight read churn. Consumed by useListenerLifecycle.ts.
// ==================================================================================

export const LISTENER_LIFECYCLE = {
  /** Hidden AND untouched for this long → detach (transport pause); resume on visibility/activity. */
  IDLE_PAUSE_MS: 10 * 60 * 1000,
  /**
   * Curfew: outside these hours, hidden-or-idle tabs pause; a visible tab touched
   * within IDLE_PAUSE_MS stays live at any hour. Taipei wall-clock.
   * PARITY NOTE: mirrors the backend BF-poll operating-hours precedent; there is NO
   * backend reader of this block (frontend-only transport control).
   */
  ACTIVE_HOURS: {
    START: '06:30',
    END: '20:00',
  },
  /** Controller re-evaluation cadence (curfew boundary / idle-elapsed check). */
  TICK_MS: 60_000,
  /** Minimum gap between processed activity signals (pointer/key/nav) — debounce. */
  ACTIVITY_THROTTLE_MS: 5_000,
  /**
   * Backstop timeout (ms) for the freshness gates (waitForFreshSnapshot /
   * waitForTransferFreshSnapshot). A gate normally resolves in ~50-300ms once
   * the live listener confirms a server snapshot. But if the transport is
   * PAUSED (idle-pause / curfew) or the tab is offline while a mutation awaits
   * the gate, the fresh-snapshot flag was reset false and the surviving watcher
   * may never fire — the mutation would hang until the next foreground/resume.
   * After this timeout the gate REJECTS (never resolves stale) so the caller can
   * abort with a retry toast instead of a silent unbounded hang.
   */
  FRESH_SNAPSHOT_TIMEOUT_MS: 12_000,
  /**
   * A write whose reply was LOST (useFirebaseFunctions isLostReply) is judged by what the
   * live list shows; a listener without a freshness gate (the open 候補 — 續報 › 轉為候補,
   * fix round 1 L2) gets at most this long to deliver the new row before the caller says
   * 「請先到 候補 查看…」. Short: it only waits when the write did not land.
   */
  LOST_REPLY_WAIT_MS: 5_000,
} as const;

// 2026-05-15 Wave 5 cleanup: removed dead `@deprecated` numeric fields
// (WEEKLY_BATCH_THRESHOLD, LOW_CONFIDENCE_BATCH_MIN/MAX, PREVIOUSLY_ENDED_LOOKBACK_DAYS,
// RENEWAL_DUE_WEEKS, URGENT_BADGE_WEEKS, AMBER_LOCK_WEEKS, MISSED_RENEWAL_LOOKBACK_DAYS).
// None had remaining readers and their @deprecated pointers referenced
// constants that were themselves deleted (RENEWAL_REMINDER_WEEKS /
// RENEWAL_URGENT_WEEKS). Canonical values now live in SESSION_ENROLLMENT_CONFIG
// (AUTO_TAG_THRESHOLD, RENEWAL_REMINDER_DAYS, RENEWAL_URGENT_THRESHOLD_DAYS,
// RENEWAL_FINAL_WARNING_THRESHOLD_DAYS, MISSED_RENEWAL_WINDOW_DAYS).
//
// RENEWAL_WORKFLOW_CONFIG is retained for TOOLTIPS / STATUS_LABELS /
// STATUS_DESCRIPTIONS / ACTIONS / RENEWAL_TAB / MISSED_RENEWALS_HEADER /
// CONFIRMATION_BANNER — these are still consumed by TimeSlotDetailView +
// SignInPage.
/**
 * The words for a student's two classes while a 轉班 is open — ONE name per side and stage
 * (PO 2026-10-06, 「go with A」 on docs/tools/transfer-terms-booking-notice.html § 1: 「should
 * match same terms as 轉入」). 安排中: the old class 待轉出 / the new class 待轉入 (both wait
 * for 確定轉入). 待約課: the old class 已轉出 / the new class 轉入待 Excel (its seat is held
 * until the parent's bookings reach Excel). The 轉班 box's first line keeps the stage
 * (UI_STRINGS.HUB.TRANSFER_LOCK.STAGE_LABEL) — the 轉班's own state, the same box on both classes.
 *
 * TRANSFER_OUT_SEAT_LABEL — the old class while 安排中 (status `transferring`): the 排課時段表
 * header chip, the student's chip, the 保留名額 hover / card group, the status legend, the 簽到表
 * status, 簽到作業 › 保留名額未約課, 待確認's status label, the menus' sentences. (T-J1 had made
 * it 轉出中 earlier the same day; Q11 on 10/5 the stage's 轉班安排中.)
 */
export const TRANSFER_OUT_SEAT_LABEL = '待轉出';
/** The new class while 安排中 (a proposed class; the header 「待轉入 +N」, the row chip). */
export const TRANSFER_IN_PENDING_LABEL = '待轉入';
/** The new class while 待約課 (the 轉班 hold — manualReservationReason 轉班). */
export const TRANSFER_IN_HOLD_LABEL = '轉入待 Excel';

export const RENEWAL_WORKFLOW_CONFIG = {
  // UI strings
  STATUS_LABELS: {
    轉班: '轉班',
    不續報: '不續報',
    停課: '停課',
  },
  STATUS_DESCRIPTIONS: {
    轉班: '此學生即將轉到其他課程。設定後此格將自動釋出，並可於目標課程加入候補名單。',
    不續報: '此學生確認不再續報。最後一週課程結束時系統會自動釋出位置。',
    停課: '此學生暫時停課。位置可暫時釋出。學生回來後請手動點選「解除停課」。',
  },
  TOOLTIPS: {
    LOCK_NORMAL: '此格代表此學生此週為保留位 (尚未開始下一期預約) — 仍佔用名額。',
    LOCK_AMBER: (date: string) =>
      `此保留將於 ${date} 自動釋出 (學生最後一堂課之後)。若已確認續報，請等待 BF 上傳；若不續報，請設定「不續報」狀態。`,
    LOCK_PAUSED: '此學生目前停課中，位置仍保留。',
    // PO 2026-10-06 (T-J1): the seat's one name (TRANSFER_OUT_SEAT_LABEL) — was 「轉班程序處理中，
    // 位置暫時保留名額至確認結果」 (暫時保留名額 is the special hold's name).
    LOCK_TRANSFERRING: `此學生${TRANSFER_OUT_SEAT_LABEL}，位置保留至轉班確認結果。`,
    /**
     * A cell's hover on a seat held for a while (the trust lock — 續報's runway, or a 轉班 hold:
     * `reason` 轉班 → the hold's one name, TRANSFER_IN_HOLD_LABEL, PO 2026-10-06 「go with A」).
     */
    LOCK_GRACE: (daysLeft: number, reason?: string | null): string => {
      const transferHold = reason === MANUAL_RESERVATION_REASONS.TRANSFER;
      const head = transferHold
        ? `${TRANSFER_IN_HOLD_LABEL} — 等付款、約課`
        : '保留中 (信任鎖) — 等待 BF/付款';
      if (daysLeft > 0) return `${head}，${daysLeft} 天後自動釋出`;
      return `${transferHold ? TRANSFER_IN_HOLD_LABEL : '保留中 (信任鎖)'} — 今日到期`;
    },
    SUMMARY_RESERVED: '已保留位（尚未開始下一期預約）— 仍佔用名額',
    // 2026-06-12 PO: column 總計 +N must be reconcilable against the visible
    // 🔒 cells — tooltip names every counted student. 2026-06-18 (PO refine):
    // the 「（含 Z 位此區間無約課）」 no-row clause was dropped — the per-student
    // list already makes the split clear, so the headline is just 已約 X ＋ 保留 Y.
    SUMMARY_RESERVED_HEADER: (booked: number, reserved: number) =>
      `已約 ${booked} ＋ 保留 ${reserved}`,
    // 2026-06-18 (PO final): only 停課中 still needs a rowLabel marker — 總計 now
    // types transferring / special-hold rows from reservedBreakdown's basis
    // (轉班處理中 / 暫時保留名額) via classifyReservedBasis, so SUMMARY_ROW_TRANSFERRING
    // and SUMMARY_ROW_SPECIAL_HOLD were removed.
    SUMMARY_ROW_PAUSED: '（停課中）',
    AUTO_TAGGED_HINT: '系統自動偵測為保留名額，可能不準確 — 點選「保留名額 / 取消保留名額」修正',
    MANUALLY_CONFIRMED: '已人工確認',
    UNIFIED_MODAL_INTRO: '請選擇此學生的狀態變更類型：',
    BADGE_PENDING: (weeks: number) => `點擊標記為已通知 — 還剩 ${weeks} 週`,
    BADGE_CONTACTED: (by: string, weeks: number) => `已通知 (${by}) — 還剩 ${weeks} 週`,
    // 2026-06-18: 排課時段表 表頭 pill tooltip → reading-card redesign.
    // Light grouped-by-status card shared by the 保留名額 / 候補 / 暫時保留 / 總計
    // header tooltips. Group pill labels reuse the 狀態欄 vocabulary; the per-row
    // hold-basis VALUES still come from `reservedBreakdown` (component) unchanged.
    HEADER_CARD: {
      TITLE_RESERVED: '保留名額',
      TITLE_WAITLIST: '候補',
      TITLE_SUMMARY: '本週總計',
      COUNT: (n: number) => `共 ${n} 位`,
      // 2026-06-18 (PO final): reserved holds group into FOUR type groups — a
      // single 一般保留名額 (merges the former renew + grace), a 待轉入保留
      // (transfer-in awaiting BF, carries a per-row 暫留至 {date} footnote), a
      // 轉班處理中 (status='transferring' / 轉出中 rows — a subset of 保留名額),
      // and 暫時保留名額 (VVIP, rendered as rich per-person detail blocks). No
      // per-row no-row footnote; the only per-row suffix is the 待轉入保留 lock
      // date. Pill labels keyed by classified hold-basis; the hold-basis VALUES
      // still come from `reservedBreakdown`. Same four type names drive the 總計
      // card too (booked + reserved classified together — no separate 已約 group).
      GROUP_PILL_GENERAL: '一般保留名額',
      // 轉班 畫面整理 (PO 2026-10-06): was 「待轉入保留」 — the same words as the row chip
      // 「轉入待 Excel (剩 N 天)」; the header's 「N 筆轉入待 Excel」 chip was removed (same people).
      GROUP_PILL_TRANSFER_IN: TRANSFER_IN_HOLD_LABEL,
      /** That group's row in its last TRANSFER_BF_WARNING_DAYS days (was the removed chip's 「N 即將到期」). */
      TRANSFER_IN_EXPIRING: '即將到期',
      // The seat's one name (PO 2026-10-06, T-J1 — the header chip beside it says 轉出中 -N;
      // Q11 on 10/5 had made it the stage's 「轉班安排中」).
      GROUP_PILL_TRANSFERRING: TRANSFER_OUT_SEAT_LABEL,
      GROUP_PILL_SPECIAL: '暫時保留名額',
      /**
       * 轉班 畫面整理 (PO 2026-10-06): the header's 「暫時保留 N」 chip was removed; its click
       * moved to this group's title in the 保留名額 card (not in the read-only peek).
       */
      SPECIAL_GROUP_LINK_TITLE: '點擊前往 課務總覽 → 續報 → 暫時保留名額',
      // 總計 card can additionally surface paused (停課中) rows that the per-week
      // summary projects but the 保留名額 chip does not.
      GROUP_PILL_PAUSED: '停課中',
      // 暫時保留 detail block field prefixes.
      SPECIAL_SET_BY: (setter: string, date: string) => `${setter} 於 ${date}`,
      SPECIAL_SET_BY_NO_SETTER: (date: string) => `於 ${date} 設定`,
      SPECIAL_SET_BY_NO_DATE: (setter: string) => `${setter} 設定`,
      SPECIAL_LABEL_SET: '設定：',
      SPECIAL_LABEL_REASON: '原因：',
      SPECIAL_LABEL_LSED: '最後約課日：',
      SPECIAL_LSED_PASSED: (days: number) => ` (已過 ${days} 天)`,
      SPECIAL_PARTIAL_BOOKINGS: (n: number) => `⚠ 期間已約 ${n} 堂`,
      WAITLIST_EMPTY_NOTE: '此時段目前無候補名單明細',
      // 2026-09-11: 候補 card rows are listed oldest-first; each row carries the
      // 加入候補 date (M/D, Taipei) and the 建立者. Either part may be absent on
      // legacy docs — the separator only appears between two real values.
      WAITLIST_ROW_META: (date: string, createdBy: string) =>
        [date, createdBy].filter(Boolean).join(' · '),
      // 2026-06-18: remaining header pills converted to the reading-card tooltip.
      // Each carries a card title + a one-line explanation; per-student lists are
      // built in the component from existing computeds (no new data sources).
      // 待驗證 — migration-period unverified rows.
      TITLE_VERIFY: '待驗證',
      VERIFY_NOTE: '此時段現有學生待驗證 — 點擊前往待驗證分頁',
      // 待轉入 — Stage-1 incoming transfers (names + source slot).
      /**
       * 轉班 畫面整理 item 6 (PO 2026-10-06 = C, mockup § 6): the header total, out of the class
       * cap — 「→ 預計 N/M 人」 (was 「＝ N 人」; the arrow is drawn by the view). Red over the cap.
       * TOTAL_TITLE = its hover, the sum it is made of. The 產生器 (T03) hover uses the same label.
       */
      TOTAL_LABEL: (total: number, max: number) => `預計 ${total}/${max} 人`,
      TOTAL_TITLE: (reserved: number, transferIn: number, waitlist: number, transferOut: number) =>
        `保留名額 ${reserved} ＋ ${TRANSFER_IN_PENDING_LABEL} ${transferIn} ＋ 候補 ${waitlist} − ${TRANSFER_OUT_SEAT_LABEL} ${transferOut}`,
      TITLE_TRANSFER_IN: TRANSFER_IN_PENDING_LABEL,
      TRANSFER_IN_NOTE: '待轉入此時段 — 點擊前往 課務總覽 → 轉班 確認/取消',
      TRANSFER_IN_SOURCE: (slot: string) => `來源：${slot}`,
      TRANSFER_IN_SOURCE_UNKNOWN: '來源：未知',
      // 2026-06-18 (PO final): the 轉出中 / 轉入待 Excel / 暫時保留 chips became
      // plain count chips (no hover card), so their card titles / notes / target
      // / remaining-days strings were removed. The held-seat detail lives in the 保留名額 card.
      // 2026-10-06 (轉班 畫面整理, PO): the 轉入待 Excel and 暫時保留 chips are gone too — the
      // card names the same people; 即將到期 → TRANSFER_IN_EXPIRING, the 暫時保留 click →
      // the card's group title (SPECIAL_GROUP_LINK_TITLE). 轉出中 stays (the ＝ total subtracts it).
      // 待確認 — LSED grey-area review queue.
      TITLE_LSED_REVIEW: '待確認',
      LSED_REVIEW_NOTE: '此時段有待確認最後約課日 — 點擊前往待確認分頁',
    },
  },
  ACTIONS: {
    SET_STATUS: '設定狀態',
    RETRACT: '撤回此狀態',
    MARK_CONTACTED: '已通知續報',
    UNMARK_CONTACTED: '取消續報通知',
    UNPAUSE: '解除停課',
    MARK_RESERVED: '保留名額',
    UNMARK_RESERVED: '取消保留名額',
    CANCEL_LINKED_WAITLIST_PROMPT:
      '是否同時取消目標課程的候補名單預約？（若已轉為正式預約，將會保留）',
  },
  RENEWAL_TAB: {
    HEADER: '以下學生課程即將結束，可能需要詢問是否續報。請對照 BF 與學生紀錄確認後再聯絡。',
    URGENCY_LABELS: {
      RED: '剩餘 ≤ 1 週｜緊急',
      ORANGE: '剩餘 2-3 週',
      YELLOW: '剩餘 4-5 週',
    },
    PENDING_CONTACT: '待聯絡',
    CONTACTED: '已通知',
  },
  MISSED_RENEWALS_HEADER: '以下學生續報期已過但未做任何處理。可能漏掉聯絡，請確認。',
  /**
   * 2026-07-13 — 待確認統一涵蓋 (pair review): sub-threshold NEW-pair rows +
   * evidence-based dismissal + 已忽略 section. Labels centralized per
   * CLAUDE.md §4b (enum → display-label map). Context labels are HINTS
   * (phrased 疑似) — appearance of a row is never filtered by context.
   */
  PAIR_REVIEW: {
    CONTEXT_LABELS: {
      moved: '疑似換時段',
      returned: '同時段回流',
      new: '全新時段',
    } as Record<string, string>,
    CONTEXT_MOVED_DETAIL: (slot: string, endedDate: string) => `原 ${slot} · ${endedDate} 結束`,
    /** 2026-07-18 (seat-hold guard, 5.5): 'moved' variant when the origin slot is
     *  still HELD but bookingless (context.originHeldTimeslotId set) — the student
     *  cancelled all classes there but the seat is kept. Distinct from the plain
     *  疑似換時段 (which references an ENDED origin). */
    CONTEXT_MOVED_HELD_LABEL: '疑似換時段 (原時段保留中)',
    /** Sub-caption for the held-origin 'moved' variant: 「原 <slot> · 名額保留中」
     *  (parity with CONTEXT_MOVED_DETAIL's 「原 <slot> · <M/D> 結束」). */
    CONTEXT_MOVED_HELD_DETAIL: (slot: string) => `原 ${slot} · 名額保留中`,
    /** Returned-context (同時段回流) sub-caption — the ended date of the pair's
     *  own prior enrollment. Parity with CONTEXT_MOVED_DETAIL's 「M/D 結束」. Round 2 H4
     *  (PO 2026-10-02): its ending reason (the entry's endedReason) joins the line. */
    CONTEXT_RETURNED_DETAIL: (endedDate: string, reason: string) =>
      reason ? `${endedDate} 結束 (${reason})` : `${endedDate} 結束`,
    /** Same line when the prior enrollment has no ended date — keeps its reason visible. */
    CONTEXT_RETURNED_NO_DATE: (reason: string) => `此時段已結束 (${reason})`,
    /** The ending reason is shown without the archive prefix (approved mockup:
     *  「9/14 結束 (期限內未結案)」, not 「(自動歸檔 — 期限內未結案)」). */
    ENDED_REASON_DROP_PREFIX: '自動歸檔 — ',
    REOPENED_CHIP: '忽略後有新預約',
    /**
     * 已忽略 › 恢復 greyed (PO 2026-10-03 「yes blocked」): the row can no longer lead to an
     * action (contract C1), so a restored row would sit in neither list. {existing} = a
     * 既有報名 row (adds the 最後約課日 case). The SAME sentence the server refuses with —
     * mirror of UI_STRINGS.PAIR_REVIEW.ERROR_RESTORE_NO_ACTION (check-config-mirror).
     */
    RESTORE_BLOCKED_NO_ACTION: (existing: boolean, cap: number, min: number) =>
      existing
        ? `這一列已經沒有可以改的日期 (日期都在 ${cap} 天內、剩不到 ${min} 堂，或最後約課日已在這些日期之後)，所以不能恢復；之後家長約了更晚的課，這一列會自動回到待確認`
        : `這一列已經沒有可以改的日期 (日期都在 ${cap} 天內或剩不到 ${min} 堂)，所以不能恢復；之後家長約了更晚的課，這一列會自動回到待確認`,
    DISMISSED_SECTION_TITLE: '已忽略',
    /**
     * 2026-10-24 (約課切斷確認 D39, N34): the come-back clause does not hold for 約課切斷確認 rows —
     * they return only when a new 最後約課日 is set within the window (Spec 4) — so the caption
     * carries the same exception the 使用說明 ※ line carries (LSED_CUTOFF_EXCEPT_PARENTHESIS).
     */
    DISMISSED_SECTION_NOTE: `忽略過的項目；之後有新約課會自動回來${LSED_CUTOFF_EXCEPT_PARENTHESIS}，也可手動恢復`,
    /** Toast after a 新時段 row's 忽略 saved (R2.4: the one ReviewDismissDialog, hosted by LsedReviewTab). */
    DISMISSED_TOAST: '已忽略此項目',
    /**
     * R4-4 (PO 2026-10-03, C6 「忽略可選填原因」): the optional reason in BOTH 忽略
     * confirmations (components/ReviewDismissReasonField). Trimmed, omitted when empty,
     * display-only (no stripInvisible). The cap mirrors the server's
     * BUSINESS_RULES.SESSION_ENROLLMENT.REVIEW_DISMISS_REASON_MAX_LENGTH (it refuses a longer
     * one).
     */
    DISMISS_REASON_LABEL: '原因 (選填)',
    DISMISS_REASON_PLACEHOLDER: '例：補課',
    DISMISS_REASON_MAX_LENGTH: 200,
    EMPTY_DISMISSED: '目前沒有已忽略的項目',
    LOAD_ERROR: '待確認清單載入失敗',
    /** Muted hint beside the 已忽略 count when the pair half failed to load. */
    DISMISSED_LOAD_HINT: '部分項目可能未載入 (載入失敗)',
    DISMISSED_META: (by: string, at: string) => `由 ${by} 於 ${at} 忽略`,
    /** R4-4: appended to DISMISSED_META when the 忽略 carried a reason — 「由 X 於 Y 忽略 · 原因：…」. */
    DISMISSED_REASON: (reason: string) => ` · 原因：${reason}`,
    /**
     * R4-8 (PO 2026-10-03, C6 #2): 確認報名's offer to release the student's original slot
     * (composables/usePairReleaseOffer). Exactly one candidate → the question (title
     * SEAT_HOLD.D2_TITLE, buttons RELEASE_OFFER_ACTION / SEAT_HOLD.D2_ACTION_KEEP); {untilMd}
     * = that slot's last future 學期 booking, '' = none left. More than one → no question,
     * RELEASE_OFFER_MANY after the 確認報名 instead — {n} = every live enrollment of the student
     * at another slot (the set 有其他時段 is built from), not the candidates (Round 4 review).
     */
    RELEASE_OFFER_BODY: (slot: string, untilMd: string) =>
      `原時段 ${slot} ${untilMd ? `還有約課到 ${untilMd}` : '已沒有約課'}；學生換到這裡後，要一併釋出原時段名額嗎？`,
    RELEASE_OFFER_ACTION: '一併釋出',
    RELEASE_OFFER_MANY: (n: number) => `學生還有 ${n} 個時段，原時段請到 排課時段表 處理`,
    /** After 一併釋出: the original slot recorded 不續報 · 已改報 {this slot} (L12). */
    RELEASED_TOAST: (student: string) => `已釋出 ${student} 的原時段名額`,
    /** The release of that same original slot is still being sent (its per-slot guard) — the
     *  second one is not sent (usePairReleaseOffer). */
    RELEASE_IN_FLIGHT: '原時段名額正在釋出中，未重複送出',
    /** How long the notes shown after a 確認報名 stay up (RELEASE_OFFER_MANY, the 候補 advice) —
     *  each is an instruction to act on, longer than a plain toast; closable. */
    AFTER_CONFIRM_NOTE_MS: 10000,
    /** 證據 line of a pending pair row (text-fix A5.2 — was 「N 堂：」; 學期票券 — PO
     *  2026-10-02 review-queue proposal § 1: only 學期 tickets count). */
    EVIDENCE: (n: number) => `Excel 上的學期票券約課：${n} 堂`,
    /**
     * 2026-10-02 (待確認 R-d): 確認報名 from 新時段報名待確認 tells the server where it came from
     * (validated and logged). Since the 轉班鎖定 (PO 2026-10-04) it picks nothing: a class an
     * open 轉班 still decides is refused for every caller by the one lock rule (functions
     * sessionEnrollments/transferLock.ts). 排課時段表 設定保留名額 does not send it.
     */
    // Literal type from the object's own `as const`; the value is pinned to the server's
    // BUSINESS_RULES.SESSION_ENROLLMENT.MANUAL_MARK_ORIGINS.PAIR_REVIEW (check-config-mirror).
    CONFIRM_ORIGIN: 'pairReview',
  },
  /**
   * 2026-07-14 — EXISTING-row 待確認 (LSED grey-area) section. Merged the former
   * 單一時段 + 跨時段 split into ONE section (PO: the split's naming wasn't
   * meaningful, 跨時段 too rare). The cross-timeslot rows keep a small inline
   * badge so the "compare multiple pending timeslots before reserving" signal
   * survives the merge.
   */
  LSED_REVIEW: {
    SECTION_TITLE: '既有報名待確認',
    // Hub UI A1.20 — the thresholds come from config (they were literal prose). 學期票券 —
    // PO 2026-10-02 review-queue proposal § 1 (P1: only 學期 tickets count). 2026-10-14 (W9.5
    // UX-03, C-04): the row's main button 結算續報 first, then 修改最後約課日, then 忽略.
    // 2026-10-15 (D45, PO 「remove it from 待確認」): no 結算續報 on the row any more.
    SECTION_NOTE: `在籍學生的最後約課日之後有 ${SESSION_ENROLLMENT_CONFIG.LSED_REVIEW_MIN_COUNT} 到 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD - 1} 堂學期票券約課：按 處理 修改最後約課日，或按 忽略`,
    /**
     * Hub UI W3.2: a multi-slot student's rows carry ONE flag that is both the badge and
     * the progress counter (the group card is gone); its title keeps the 「避免漏看」 reason
     * the old group hint gave.
     */
    CROSS_PROGRESS: (handled: number, total: number) =>
      handled > 0 ? `多時段·需比對 · 已處理 ${handled} / ${total}` : '多時段·需比對',
    CROSS_TITLE: (remaining: number, total: number) =>
      `此學生有 ${total} 個時段待確認，還有 ${remaining} 個待處理；全部處理完後，這幾列才會一起消失 (避免漏看)`,
    /** 證據 line (text-fix A5.1 — was 「期末後 N 堂：」; 學期票券 — PO 2026-10-02 review-queue
     *  proposal § 1). */
    EVIDENCE: (n: number) => `Excel 上最後約課日後的學期票券約課：${n} 堂`,
    /**
     * The amber flag in 注意 (A5.6; D2, PO 2026-10-03: 「最後約課日 m/d; 保留至 m/d」). {holdMd} =
     * the 續報 保留至 rule (the seat lock, else 最後約課日 − RENEWAL_SYSTEM_CAP_DAYS — the same
     * date 續報 shows); '' (暫時保留名額, 請長假中 — no release date) leaves it out.
     */
    SYSTEM_LSED: (md: string, holdMd: string) =>
      holdMd ? `最後約課日 ${md}；保留至 ${holdMd}` : `最後約課日 ${md}`,
    /** That flag's hover. */
    SYSTEM_LSED_TITLE: '最後約課日＝這一期最後一堂；保留至＝名額保留到這一天，之後開放給候補',
    /** One line of the 「有其他時段」 label's tooltip (HUB.REVIEW.HINTS.OTHER_SLOTS — PO
     *  2026-10-03: the label replaces the 學生 cell's 「其他時段：…」 line on both 待確認 row
     *  types): 「{slot} · {status} · 上到 M/D」 (PO 2026-10-02 mockup form). {status} is '' for
     *  an ordinary running class (進行中) and only shows when it changes how the row reads
     *  (已標記不續報, 請長假中, …); {untilMd} = that enrollment's 最後約課日. An '' part is
     *  left out. */
    OTHER_SLOT: (slot: string, status: string, untilMd: string) =>
      [slot, status, untilMd ? `上到 ${untilMd}` : ''].filter(Boolean).join(' · '),
    /**
     * The {slot} of a 待確認 line that names another class (B5 / L11, PO 2026-10-03): the slot
     * label with its teacher — 「週日 11:30 小鴨班 (Amy)」 — so two classes that differ only by
     * teacher read differently; no brackets when the id carries no teacher. Used by the
     * 有其他時段 tooltip and the 整班換時段 line.
     */
    SLOT_WITH_TEACHER: (slot: string, teacher: string) => (teacher ? `${slot} (${teacher})` : slot),
    /** A handled row's outcome flag (A4.14: the check icon, not a ✓ glyph). */
    DONE_SET: (md: string) => `最後約課日已設至 ${md}`,
    DONE_SET_GENERIC: '已更新最後約課日',
    DONE_DISMISSED: '已忽略',
    /** The toast after a 忽略 on a row (W5.4 FE-L7: was inline in LsedReviewTab; the one home of
     *  the words — the 約課切斷確認 row reads them as UI_STRINGS.HUB.REVIEW.CUTOFF.DISMISSED_TOAST). */
    DISMISSED_TOAST: '已忽略此筆待確認',
  },
  /**
   * 2026-07-18 (seat-hold guard, Wave 1) — UI strings for the "held but
   * bookingless" surface: a kept enrollment whose seat-hold is still live but
   * which currently has zero future 學期 bookings (zeroFutureBookingsAt stamped).
   * Terminology: 最後上課日 ONLY — NEVER 學期結束日 / 提醒窗 (those conflict with the
   * established admin-surface renewal vocabulary). Consumed by later waves'
   * chip / marker / dialog components; NO component reads these yet.
   */
  SEAT_HOLD: {
    // F5 cell chip (狀態 column, 80px) — \n split mirrors the 已過期未通知！\n名額已釋出
    // precedent. Must NOT lead with a release phrase.
    CHIP_LABEL: '名額保留中\n目前無約課',
    // Tooltip references 最後上課日 + 名額保留至 (the −14d / lock hold-until date).
    CHIP_TOOLTIP: (lsedDate: string, holdUntilDate: string) =>
      `此學生目前無未來約課，名額保留中。最後約課日 ${lsedDate}；名額保留至 ${holdUntilDate}。`,
    // F4 grid marker legend entries. 2026-07-18 (PO flag-always): both are flags,
    // differing only by fill — solid = 已約課, hollow (outlined) = 無約課. Wording
    // kept parallel: 「最後約課日（<實心/空心旗標>：<約課狀態>）」. (PO 2026-07-18: 約課 not 上課)
    MARKER_LEGEND_LSED: '最後約課日 (實心旗標：該日有約課)',
    MARKER_LEGEND_LSED_UNBOOKED: '最後約課日 (空心旗標：當日無約課，最後日不變)',
    // F4 grid marker cell tooltip (solid + hollow flag). Terminology 最後上課日 only.
    // holdUntilDate = later of LSED−14d / reservation lock; daysUntilRelease = days
    // until that auto-release point.
    MARKER_TOOLTIP: (lsedDate: string, holdUntilDate: string, daysUntilRelease: number) =>
      `最後約課日 ${lsedDate}；名額保留至 ${holdUntilDate} (自動釋出前 ${daysUntilRelease} 天)`,
    // Special-hold variant — no auto-release date (暫時保留名額 indefinite hold).
    MARKER_TOOLTIP_SPECIAL: (lsedDate: string) =>
      `最後約課日 ${lsedDate}；名額保留中 (暫時保留名額・無到期日)`,
    // Released / past variant — the last session bar on a slot whose hold already
    // lapsed (no forward countdown to show).
    MARKER_TOOLTIP_RELEASED: (lsedDate: string) => `最後約課日 ${lsedDate} (名額已釋出)`,
    // Fallback chip prefix when the LSED week has no loaded column (beyond either
    // edge OR not on the slot's weekday) — renders 「學期至 M/D」-style.
    FALLBACK_CHIP_PREFIX: '學期至',
    // Compact single-line row tag for the renewal tabs (RenewalActiveTable /
    // RenewalRemindersTab 狀態 column). Single-line variant of CHIP_LABEL for
    // list rows (no width-constrained 80px column there). Leading Material `flag`.
    ROW_TAG: '名額保留中·目前無約課',
    // Wave-7 ui-ux (2026-07-18): row-tag badge tooltip with the CONCRETE hold-until
    // date (max(LSED−14d, reservationLock)) instead of the static "前 14 天" phrase —
    // a late-contact lock can extend the hold past LSED−14d, so the fixed wording
    // was wrong for those rows.
    ROW_TAG_TOOLTIP: (holdUntilDate: string) =>
      `此學生目前無未來約課，名額仍保留中 (名額保留至 ${holdUntilDate})。`,
    // Wave-7 ui-ux (2026-07-18): 週期標記 legend rows for the two seat-hold chips.
    // LEGEND_HELD_CHIP takes ROW_TAG so the quoted label's · interpunct stays
    // single-sourced (never drifts to the katakana ・). LEGEND_FALLBACK_CHIP
    // describes the off-window fallback chip (its Material `flag` icon = the
    // legend sample, so no ⚑ char is repeated in the text).
    LEGEND_HELD_CHIP: (tag: string) => `「${tag}」標籤：仍保留名額但目前無未來約課的學生`,
    LEGEND_FALLBACK_CHIP: '學期至 M/D：最後約課日不在目前載入的週次時，以標籤顯示',
    // D2 pair-confirm reminder — the student's ORIGINAL slot still holds a seat.
    D2_TITLE: '原時段名額仍保留中',
    D2_ACTION_KEEP: '保留原時段',
    // F-11 SetReservationModal empty-state pointer (no selectable future dates).
    RESERVE_MODAL_EMPTY_POINTER: '如需持續保留此學生名額，請改用「暫時保留名額」。',
    // PO 2026-10-03 (Q4 = B): a 不續報 / 已轉出 undo on the date the parent was already told —
    // the server raises no 約課通知待發 (reclaimRules.ts); since W10.4 F3 this is a line of the
    // 保留名額 window's preview (reanchorPreviewLines.ts), in place of 「不列入 約課通知待發」.
    RECLAIM_TOLD_DATE_NOTE: '家長已收過這個日期的約課通知，不列入 約課通知待發',
    // F-12 不續報-on-bookingless irreversibility warning.
    NOT_RENEWING_BOOKINGLESS_WARNING:
      '此學生目前無未來約課：設定不續報後將無法從畫面復原 (復原需有可選的約課日期)。',
  },
  CONFIRMATION_BANNER:
    '本系統會自動標記長期報名的學生位置為「保留」。新功能上線後，學期續報、轉班、停課等操作將整合到課表頁面與 dashboard 提醒。如有任何疑問，請聯絡系統管理員。',
} as const;

// ==================================================================================
// CATEGORY: 名額使用情況 (通知 送出前 名額使用情況 — tasks/todo-seat-usage-facts.md)
// Description: the staff-only, facts-only week grid of how one 保留名額 has been used,
// shown in the parent-message windows and in 學生紀錄 (SeatUsageFacts.vue, SlotUsageRow.vue,
// utils/seatUsageView.ts read these). Words: UI_STRINGS.SEAT_USAGE. Who: useRoleContext
// capabilities.canViewSeatUsage (D34). Backend: BUSINESS_RULES.SEAT_USAGE.
// ==================================================================================

/**
 * Every week-cell state the grid writes — the mockup `ST` table (renewal-notice-seat-usage-
 * mockup.html:932-943) ＋ `out` (annex § 三之二 / annex script ST): at 出席 · lv 請假 · ot 其他 ·
 * ab 缺席 · ns 未簽到 · ap 未簽到 (簽到表還沒核可) · sub 已約・代課 · cx 取消 · bk 已約 ·
 * no 沒有約 · nr 沒有紀錄 · nc 沒有課 · sc 停課 · bs 已約・停課 · out 超出 Excel 上傳範圍.
 * MIRROR of functions BUSINESS_RULES.SEAT_USAGE.CELL_STATES (check-config-mirror.cjs).
 */
const SEAT_USAGE_CELL_STATES = [
  'at',
  'lv',
  'ot',
  'ab',
  'ns',
  'ap',
  'sub',
  'cx',
  'bk',
  'no',
  'nr',
  'nc',
  'sc',
  'bs',
  'out',
] as const;
export type SeatUsageCellState = (typeof SEAT_USAGE_CELL_STATES)[number];

/**
 * The drawer's nine note kinds, in the D25 filter order (mockup KIND_ORDER, line 1178):
 * track 追蹤備註 · bf 約課備註 · adm 課務備註 · slot 排課備註 · tr 轉班備註 · wl 候補備註 ·
 * tea 老師備註 · prog 學習歷程記錄 · fb 體驗備註.
 * MIRROR of functions BUSINESS_RULES.SEAT_USAGE.NOTE_KINDS (check-config-mirror.cjs).
 */
const SEAT_USAGE_NOTE_KINDS = [
  'track',
  'bf',
  'adm',
  'slot',
  'tr',
  'wl',
  'tea',
  'prog',
  'fb',
] as const;
export type SeatUsageNoteKind = (typeof SEAT_USAGE_NOTE_KINDS)[number];

export const SEAT_USAGE_CONFIG = {
  /**
   * D7 「今天往前 8 週 (設定檔)」: columns before today; column 0 starts at today − WEEKS_BACK × 7
   * days, today starts column WEEKS_BACK. Frontend reader: the placeholder row's width before
   * the data lands (P10). MIRROR of functions BUSINESS_RULES.SEAT_USAGE.WEEKS_BACK.
   */
  WEEKS_BACK: 8,
  /**
   * 約課切斷確認 D70 (2) (PO 2026-10-25 「mockup is correct」 — the D68 mockup 「從 8 週前畫到
   * 紅線之後 4 週」): in the 補正最後約課日 window only (the callable's `toRangeEnd`), the columns
   * reach the latest completed upload's last day + this many weeks. The server draws the columns;
   * the website never reads it. MIRROR of functions BUSINESS_RULES.SEAT_USAGE.CUTOFF_WEEKS_AFTER_RANGE.
   */
  CUTOFF_WEEKS_AFTER_RANGE: 4,
  /**
   * D21: a column = 7 days, cut back from today (column 0 starts today − WEEKS_BACK × 7 days).
   * Frontend only — the view, the steps and the step placement read it (review VR-10).
   */
  DAYS_PER_COLUMN: 7,
  /**
   * D2 / D32 / D37: the host's "do it" button waits for the facts at most this long; then the
   * block says UNAVAILABLE and the button frees (a maximum — it frees when the data lands).
   */
  FETCH_TIMEOUT_MS: 5000,
  CELL_STATES: SEAT_USAGE_CELL_STATES,
  /**
   * The states the backend counts per row, in this order (mockup COUNT_ORDER, line 944); `ap`
   * counts as `ns` (mockup line 1010). Kept as the backend's MIRROR (check-config-mirror.cjs):
   * the frontend prints no count since D76 (the hovers that named them are gone).
   */
  COUNT_ORDER: [
    'at',
    'lv',
    'ot',
    'ab',
    'ns',
    'cx',
    'bk',
    'bs',
  ] as const satisfies readonly SeatUsageCellState[],
  /**
   * The cell states under which the 最後約課日 flag is drawn HOLLOW (那一天沒約 — the 排課時段表
   * rule, mockup HOLLOW_FLAG line 946); every other state draws it solid.
   */
  HOLLOW_FLAG_STATES: [
    'cx',
    'no',
    'nr',
    'nc',
    'sc',
  ] as const satisfies readonly SeatUsageCellState[],
  NOTE_KINDS: SEAT_USAGE_NOTE_KINDS,
  /**
   * D83: the 備註 drawer's 「學生紀錄 ›」 opens 學生紀錄總覽 on 約課時段 — the tab 簽到表's panel
   * link opens on too (frontend only).
   */
  RECORD_LINK_VIEW: 'bookedSlots',
  /**
   * 其他處理: the choices whose 確認 waits for the facts (P3 / D32, A1 F-17). 設為暫時保留名額 is
   * the fail-safe that never waits.
   */
  HOLD_CHOICES: ['不續報'] as readonly OtherHandlingTopChoice[],
  /**
   * The months row (mockup:985): a month's first-week column gets its label only when it is at
   * least this many columns after the last label (frontend only — utils/seatUsageView.ts).
   */
  MONTH_LABEL_MIN_GAP: 2,
  /**
   * The longest `enrollmentId` (and the longest 原班／新班 timeslot id) the callable takes; the
   * server refuses the whole call for a longer one (BE-4). MIRROR of functions
   * BUSINESS_RULES.SEAT_USAGE.ID_MAX_LENGTH (check-config-mirror.cjs).
   */
  ID_MAX_LENGTH: 300,
  /**
   * The callable's id limits (tasks/todo-renewal-pending-chart.md D1／D10). The list strips left
   * the rows (D26) and their website code went (D38); the server's batch mode (`enrollmentIds`)
   * stays, so both limits stay mirrored.
   */
  STRIP: {
    /**
     * P1: the most ids one call takes; the server refuses more. MIRROR of functions
     * BUSINESS_RULES.SEAT_USAGE.STRIP.BATCH_MAX_IDS (check-config-mirror.cjs).
     */
    BATCH_MAX_IDS: 40,
    /**
     * P1／D10: the most 原班／新班 timeslot ids one row may name (`keepTimeslotIds`); the server
     * refuses more. MIRROR of functions BUSINESS_RULES.SEAT_USAGE.STRIP.KEEP_MAX_IDS.
     */
    KEEP_MAX_IDS: 6,
  },
} as const;

/**
 * 名額使用情況 in a decision window — where each label goes and in which colour
 * (tasks/todo-renewal-pending-chart.md D68 (3) / D69 (1); PO 2026-11-03 「all ok … remember to use
 * same purple as other pages」; docs/tools/hub-chart-windows-labels-2026-11-03.html § 1). The name
 * line keeps the name and the stage pill; every other label is drawn on the class line it is
 * about, from that line's 2nd row, in the list's colour (HUB_STATUS_FAMILY — 暫時保留名額 purple 🔒).
 * Website-only (a display rule; no backend twin). Read by utils/seatUsageLabels.
 */
export const SEAT_USAGE_LABELS = {
  /** A 轉班 row's flags (HubFlagView.key) about its 原班 — on the 原班 line when the chart has one. */
  SOURCE_KEYS: [
    'sourceSpecialHold',
    'releaseSoon',
    'sourceRefill',
    'sourceBooked',
    'released',
    'removed',
    'refill',
    'renewalKept',
    'lsedCutoff',
  ] as readonly string[],
  /** … about its 新班 — on that 新班 line (`aboutTimeslotId`), else the first 新班 line. */
  TARGET_KEYS: [
    'targetSpecialHold',
    'targetBooked',
    'targetAlive',
    'targetRemoved',
    'lsedCutoffNewClass',
    'partial',
    'manual',
  ] as readonly string[],
  /**
   * A seat's state word (utils/seatUsageView seatTagStateOf — UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE)
   * in the colour its list draws it; 'pending' = 待通知 in its zone colour (the 續報 table's
   * pendingGreen／Amber／Red); null = no word.
   */
  STATE_STATUS: {
    general: null,
    'pending-notify': 'pending',
    'expired-unnotified': 'pastUnnotified',
    'notified-awaiting': 'contacted',
    'notified-overdue': 'contactedLapsed',
    'booking-notice-pending': 'notifyPending',
    'not-renewing': 'notRenewing',
    'transferred-out': 'transferredOut',
    'released-admin-unnotified': 'archivedBySystem',
    'released-expired': 'archivedBySystem',
    'released-other': 'notRenewing',
    'special-hold': 'specialHold',
    'long-leave': 'notRenewing',
    transferring: 'transferring',
    'transfer-awaiting': 'transferAwaitingExcel',
    none: null,
  } as const satisfies Record<
    RenewalRowState | 'transfer-awaiting',
    HubStatusKey | 'pending' | null
  >,
  /**
   * 待確認's status label (utils/reviewRowHints statusFactLabel — 已標記不續報, 請長假中 …), by the
   * row's EnrollmentState: the seat state it names in other words. On a line that shows that state
   * the line's word stays and the label goes (one word per state, rule 3).
   */
  ENROLLMENT_STATE_STATUS: {
    'soft-end-not-renewing': 'notRenewing',
    'special-hold': 'specialHold',
    transferring: 'transferring',
    paused: 'notRenewing',
    'soft-end-transferred': 'transferredOut',
    'soft-end-scan-a': 'archivedBySystem',
    'soft-end-scan-b': 'archivedBySystem',
    'soft-end-other': 'notRenewing',
    'transfer-awaiting': 'transferAwaitingExcel',
  } as Readonly<Record<string, HubStatusKey>>,
  /**
   * Rule 3: the labels a window's stage pill already says, per stage — dropped from the chart
   * (轉班 › 安排中 → no 轉出中; 轉班 › 待約課 → no 轉入待 Excel; 續報 › 已通知·待確認 → no
   * 已通知·待確認 …). Keys: utils/transferStages TransferStage; components/renewal RenewalFilterKey.
   */
  STAGE_SAYS: {
    TRANSFER: {
      inbox: [],
      'in-progress': ['transferring'],
      awaiting: ['transferAwaitingExcel', 'arrangedAwaitingExcel'],
      confirmed: ['completed'],
      expired: ['expired'],
    },
    RENEWAL: {
      needContact: ['pendingGreen', 'pendingAmber', 'pendingRed'],
      pastDeadline: ['pastUnnotified'],
      confirmedAwaiting: ['contacted'],
      specialHold: ['specialHold'],
    },
  } as const satisfies Record<string, Record<string, readonly HubStatusKey[]>>,
} as const;

/**
 * 約課切斷確認 D63 item 5 / D71 (iv) (PO 2026-10-25 「the rest all agree with recommeneded」): the
 * 名額使用情況 cell states that mean the student HAS a class that week — 已約, 已約・停課, 已約・代課,
 * and every 簽到 fact (出席, 請假, 其他, 缺席, 未簽到). In the 補正最後約課日 window, a picked day
 * (on or before the upload end) whose 此名額 square is none of these gets the grey line
 * UI_STRINGS.HUB.REVIEW.CUTOFF.WINDOW.NO_BOOKING_ON_PICK (information only, never a block).
 * Website-only (a display rule; no backend twin). Read by utils/seatUsageCutLine.
 */
export const LSED_CUTOFF_FIX_BOOKED_CELL_STATES = [
  'bk',
  'bs',
  'sub',
  'at',
  'lv',
  'ot',
  'ab',
  'ns',
  'ap',
] as const satisfies readonly SeatUsageCellState[];

/** UI_STRINGS.SEAT_USAGE's shared pieces (one source inside the block). */
const SEAT_USAGE_SEP = ' · ';
const SEAT_USAGE_HOLD_WORD = '暫時保留名額';
const SEAT_USAGE_LSED_WORD = '最後約課日';
/** A 暫時保留名額's start on a line — 「自 M/D 保留」 (D49 b; the 續報, 轉班 and 待確認 lines alike). */
const SEAT_USAGE_HELD_SINCE = (sinceMd: string): string => `自 ${sinceMd} 保留`;
const SEAT_USAGE_NOTES_WORD = '備註';
const SEAT_USAGE_NOTES_TAIL = '›';
/** UNAVAILABLE, and the list strip's FAILED line built from it (W1.5). */
const SEAT_USAGE_UNAVAILABLE = '查不到名額使用情況';

// ==================================================================================
// CATEGORY: NOTIFICATION TEMPLATES (manual copy-paste + future LIFF auto-push)
// Description: Centralized LINE message templates for renewal/transfer/new-student
// lifecycle. Same strings used by today's manual 「複製通知模板」 buttons and future
// LIFF auto-push (see docs/LINE_OA_PARENT_BINDING_AND_NOTIFICATIONS_PLAN.md).
// All templates accept a parameters object and return a filled string.
// Mirror: functions/src/config/appConfig.ts — keep in sync.
// ==================================================================================

/** BookFastPOS parent-facing booking link (parents log in here). */
export const BF_PARENT_LINK = 'https://parent.bookfastpos.com';

export const NOTIFICATION_TEMPLATES = {
  // ⚠ 2026-09-25 (續報 M2 T19/T19b): SUPERSEDED — every screen now takes these texts from
  // 招生客服說明 (signupSteps.ts ⑤ 續報通知, ⑪ 建議轉班／轉班繳費／約課通知) through
  // useRenewalNoticeText; no live caller is left. Kept ONLY as the byte-comparison
  // reference of the move (renewalComposerSteps.test.ts, useRenewalNoticeText.test.ts)
  // until the PO OKs the removal (dead-code rule, plan A7/T21). Do not edit.
  //
  // 2026-05-16 consistency pass: standardised placeholder brackets to 【...】,
  // unified BF link phrasing to "請至 BF 確認您的XX紀錄 👉 {LINK}", trimmed
  // redundant filler. Mirror in functions/src/config/appConfig.ts.

  // [通知續報] — Stage 1 contact bundle (deadline + bank info + booking rules).
  // 2026-05-21: priceLine wired through CLASS_PRICING — caller passes the
  // pre-formatted line via formatSessionPriceLine(timeslotId). Falls back to
  // 【金額】 manual-entry placeholder when class isn't in the pricing table.
  // slotInfo is the formatted slot label from formatTimeslotLabel — bracket
  // wrapping happens in the template, not at the callsite.
  RENEWAL_INITIAL_CONTACT: (params: {
    slotInfo: string;
    lsedDate: string;
    /**
     * 2026-09-02 (通知期限對齊 — PO decision): the SEAT-LOCK date itself —
     * FrontendConfigUtils.renewalNoticeDeadlineMs(LSED, now), the same runway
     * value the backend writes to reservationLockedUntil + renewalNoticeDeadline.
     * One date for the parent, the 名額保留期限 column and the nightly release.
     * (Replaces the old clamp(today+7, LSED-21d, LSED-14d) public formula and its
     * 「（最後期限）」 suffix.) No app caller since 續報 M2: the 續報通知 text comes from
     * 招生客服說明 (useRenewalNoticeText); only useRenewalNoticeText.test.ts still calls this
     * template, as the old wording to compare against.
     */
    renewalDeadline: string;
    /**
     * Price line, e.g. "$18,900" (weekend swimming) / "優惠 $17,900 (原價 $18,900)"
     * (weekday swimming) / "$9,300" (FMS親子) / "【金額】" (unknown class).
     * Build with formatSessionPriceLine(timeslotId).
     */
    priceLine: string;
  }) =>
    `親愛的家長您好，

您 「${params.slotInfo}」 的學期課程預計於 ${params.lsedDate} 結束。
建議您在 ${params.renewalDeadline} 前完成續報，為寶貝保留 「${params.slotInfo}」 名額。

📌 續報內容：學期課程 12 堂
   費用：${params.priceLine}
   使用期限：第一堂課開始 5 個月

💳 繳費資訊
   ${SCHOOL_BANK_INFO.bankName}-${SCHOOL_BANK_INFO.branch} (代號 ${SCHOOL_BANK_INFO.code})
   帳號：${SCHOOL_BANK_INFO.accountNumber}
   戶名：${SCHOOL_BANK_INFO.accountHolder}
   ✏️ 轉帳後請告知後 5 碼
   📌 若需要填寫轉帳備註欄，請務必包含家長姓名

📌 其他需求請於繳費後告知我們
   📄 報帳資訊（統編 / 抬頭 / 載具）
      未提供時將開立雲端發票寄送至您註冊的電子信箱（需數個工作天）
   📅 預計請假的日期
      未告知將依目前預約自動排課，優先使用舊票券
      後續請假 / 補課請於約課平台自行操作

📋 約課/請假/補課說明 👉 https://oovi.fun/booking101

如有任何問題隨時告訴我們，謝謝您 💛`,

  // 2026-09-25 (續報 M2 T20a, PO OK 2026-09-24 Q1): RENEWAL_LATE_CONTACT (補通知續報範本)
  // removed — no screen could open it since 2026-06-21. The BE copy is dormant.

  // MERGED PARENT NOTICE — ① 約課成功通知 + ② 學期日期更新通知 unified 2026-07-18 (PO
  // decision). RENEWAL_FINAL_CONFIRMATION, TRANSFER_FINAL_CONFIRMATION and
  // LSED_UPDATE_NOTICE now all render the IDENTICAL body: a single generic message
  // that states 班級 + 最後約課日. The ①/② choice is retired from ALL UI; every send
  // surface uses this one message. Byte-for-byte body is PO-locked (do not edit
  // without PO sign-off). `startDate` + `classCount` are RETAINED in the signature
  // for caller compatibility (mirrors the earlier unrendered-param precedent) but are
  // no longer rendered — the 本次共約 N 堂 line is gone. The authoritative 最後約課日
  // (endDate) is the picked LSED (fresh 設定保留名額) → the record's stored
  // lastSessionEndDate; callers no longer derive it from real booked dates.
  RENEWAL_FINAL_CONFIRMATION: (params: {
    slotInfo: string;
    startDate: string;
    endDate: string;
    classCount: number;
  }) =>
    `親愛的家長您好，

您的學期課程已排定/更新如下：

班級：${params.slotInfo}
最後約課日：${params.endDate}

⚠️ 下學期續報時程將依此日期 (前 30 天) 安排。
⚠️ 請自行於平台確認每堂約課日，避免上課撲空！
📌 請假（取消約課）所歸還的票券，只能作「補課」使用。
✏️ 「補課」請於效期內自行平台操作約課。
✅ 若想約不同時段補課，請先與我們確認（避免進度不同）！
📋 約課平台 👉 https://oovi.fun/book`,

  /**
   * 2026-07-18 (PO merge): MERGED into the single generic parent notice above.
   * Retained as an alias-body for any legacy caller (the logParentNotice
   * 'lsed-update' CF path stays valid). Renders BYTE-IDENTICAL to
   * RENEWAL_FINAL_CONFIRMATION / TRANSFER_FINAL_CONFIRMATION. The ①/② distinction
   * is retired from the UI; `classCount` is retained-unrendered for caller
   * compatibility. endDate = the newly SET 最後約課日 (picked / stored LSED).
   */
  LSED_UPDATE_NOTICE: (params: { slotInfo: string; endDate: string; classCount: number }) =>
    `親愛的家長您好，

您的學期課程已排定/更新如下：

班級：${params.slotInfo}
最後約課日：${params.endDate}

⚠️ 下學期續報時程將依此日期 (前 30 天) 安排。
⚠️ 請自行於平台確認每堂約課日，避免上課撲空！
📌 請假（取消約課）所歸還的票券，只能作「補課」使用。
✏️ 「補課」請於效期內自行平台操作約課。
✅ 若想約不同時段補課，請先與我們確認（避免進度不同）！
📋 約課平台 👉 https://oovi.fun/book`,

  /**
   * Transfer Stage 1 — single template for both teacher-initiated (with frozen
   * evaluationReason as observation) and admin-initiated (observation defaults
   * to 【請填入觀察說明】 placeholder for admin to edit pre-send).
   * suggestedSlot is the multi-line 「」-wrapped list produced by
   * formatTimeslotLabelListBracketed (one slot per line).
   */
  TRANSFER_PROPOSAL: (params: {
    currentSlot: string;
    observation: string;
    suggestedSlot: string;
  }) =>
    `親愛的家長您好，
老師觀察到孩子在 「${params.currentSlot}」 的學習表現：${params.observation}

建議可以在下學期開始考慮轉班至其中一個時段：
${params.suggestedSlot}

請問您接受讓孩子轉班嗎？請告知您偏好的時段，我們會提早確認是否有名額可保留。如果有任何疑問也歡迎提出來喔！

⚠️ 請留意，若因時間暫時無法配合轉班，暫時留在原班級並不會比較不好喔！老師會視情況做指導上的微調。

⚠️ 同樣班級名稱可能有不同進度，目前只有上述的時段比較適合您孩子喔！`,

  /**
   * Transfer Stage 2 — admin clicked [確定轉入] BEFORE Excel arrives. Bundles
   * payment info because transfer reuses the 學期 12-堂 ticket (same price
   * structure as renewal). priceLine reflects the NEW slot's day-of-week
   * pricing — caller passes formatSessionPriceLine(concludedTargetTimeslotId).
   */
  TRANSFER_CONFIRMED_PAYMENT_REMINDER: (params: { newSlot: string; priceLine: string }) =>
    `親愛的家長您好，
您寶貝的轉班已確認，新時段為 「${params.newSlot}」！

請於 7 天內完成付款；我們會在收到學費後盡快替您預排好新時段的約課。

📌 續報內容：學期課程 12 堂
   費用：${params.priceLine}
   使用期限：第一堂課開始 5 個月

💳 繳費資訊
   ${SCHOOL_BANK_INFO.bankName}-${SCHOOL_BANK_INFO.branch} (代號 ${SCHOOL_BANK_INFO.code})
   帳號：${SCHOOL_BANK_INFO.accountNumber}
   戶名：${SCHOOL_BANK_INFO.accountHolder}
   ✏️ 轉帳後請告知後 5 碼
   📌 若需要填寫轉帳備註欄，請務必包含家長姓名

📋 約課/請假/補課說明 👉 https://oovi.fun/booking101

如有任何問題隨時告訴我們，謝謝您 💛`,

  /**
   * 2026-07-18 (PO merge): MERGED into the single generic parent notice
   * (RENEWAL_FINAL_CONFIRMATION). Renders BYTE-IDENTICAL to it — TRANSFER's `newSlot`
   * param renders as the 班級 line. `startDate` + `classCount` retained-unrendered
   * for caller compatibility. endDate = the target's stored LSED.
   */
  TRANSFER_FINAL_CONFIRMATION: (params: {
    newSlot: string;
    startDate: string;
    endDate: string;
    classCount: number;
  }) =>
    `親愛的家長您好，

您的學期課程已排定/更新如下：

班級：${params.newSlot}
最後約課日：${params.endDate}

⚠️ 下學期續報時程將依此日期 (前 30 天) 安排。
⚠️ 請自行於平台確認每堂約課日，避免上課撲空！
📌 請假（取消約課）所歸還的票券，只能作「補課」使用。
✏️ 「補課」請於效期內自行平台操作約課。
✅ 若想約不同時段補課，請先與我們確認（避免進度不同）！
📋 約課平台 👉 https://oovi.fun/book`,
} as const;

// ==================================================================================
// CATEGORY: 轉班 WORKFLOW CONFIGURATION
// Description: Constants for the 2-stage 轉班 (class transfer) flow. Mirrors
// backend functions/src/config/appConfig.ts — keep in sync.
// ==================================================================================

/**
 * Validation limits for 轉班 / status-change free-text fields.
 * Centralises max-length constants that were previously hardcoded in each form component.
 * Mirrors TRANSFER_VALIDATION in functions/src/config/appConfig.ts (backend not
 * currently enforcing these lengths via validateRequest, but kept in sync for future parity).
 */
export const TRANSFER_VALIDATION = {
  PROPOSAL_TEXT_MAX_LENGTH: 1000,
  NOTE_MAX_LENGTH: 500,
  REJECT_REASON_MAX_LENGTH: 500,
} as const;

/**
 * Human-readable labels for student status-change actions in the TimeSlotPage
 * ⋯ row menu (StudentRowActionMenu).  Centralises strings that were previously
 * inline in the component.
 */
export const STATUS_CHANGE_BUTTON_LABELS = {
  END_NOT_RENEWING: '不續報',
  START_PAUSE: '停課',
  RESUME_FROM_PAUSE: '解除停課',
  LONG_LEAVE: '請長假',
} as const;

/**
 * UI string constants for the 通知方式 (NotifyMethodSection) radio options.
 * Pre-LIFF: two options rendered; second is disabled/informational.
 * Post-LIFF (Wave 7): a third LINE auto-send option will be added.
 */
export const NOTIFY_METHOD_LABELS = {
  CHANNEL_NONE: '不發送 (僅在系統內留紀錄，我會自行 LINE 通知家長)',
  CHANNEL_MANUAL: '在系統內留待後續通知 (LINE 推送功能尚未啟用)',
} as const;

/**
 * Button labels for every admin-facing 轉班 action.
 * Keys describe the surface / actor, not the verb — so the same verb can appear
 * on multiple surfaces with different underlying mechanics.
 *
 * NOTE: PROPOSE_AND_CONTACT was removed (R6-D — proposal is embedded in the
 * 安排轉班 / 登記轉班 modals, not a separate action).
 *
 * Mirrors TRANSFER_BUTTON_LABELS in functions/src/config/appConfig.ts.
 */
export const TRANSFER_BUTTON_LABELS = {
  /** Admin's 登記轉班 button on TimeSlotPage row ⋯ menu (admin-initiated Stage 1). */
  STAGE_1_FROM_TIMESLOTPAGE: '登記轉班',
  /**
   * The title of the form 老師建議 › 處理 › 登記轉班 opens (teacher-initiated Stage 1).
   * 2026-10-14 (續報・轉班 完成規則 W7.4, J-03): was 加入安排名單 (Hub UI W4.4 / A3.1; before that
   * 安排轉班) — the 處理 choice's own word. The stored audit value '安排轉班'
   * (types/database.ts) is unchanged.
   */
  STAGE_1_FROM_TEACHER_REQUEST: '登記轉班',
  /** Stage 2 Path B — target locked, real bookings already on target: source ended, target finalized.
   *  Renamed 2026-05-10: 完成轉班 → 確定轉入. The verb makes the contrast with
   *  Stage 1 (安排轉班) clearer — admins were confusing the two when
   *  scanning the queue. 確定 (confirm/lock) signals finality. */
  STAGE_2_PATH_B: '確定轉入',
  /** Stage 2 Path A — target known but BF/booking not yet uploaded: releases source slot,
   *  stays in 待約課設定 awaiting Excel surface. */
  STAGE_2_PATH_A: '儲存(待約課設定)',
  /** Cancel transfer — returns source enrollment to active. */
  CANCEL: '取消',
  /** Pivot to non-renewal — family decided to leave; source ends with reason='不續報'. */
  PIVOT_NOT_RENEWING: '轉為不續報',
  // REJECT ('拒絕') removed 2026-10-14 (W7.4): 不採用 is a 老師建議 › 處理 choice now
  // (TRANSFER_HANDLE_CHOICES), the 拒絕 window (TransferRejectForm) is retired.
  /** 待約課設定 row finalize — semantically identical to STAGE_2_PATH_B but on the BF-pending row. */
  BF_DONE: '已安排',
} as const;

/** State labels displayed on 轉班申請 rows and teacher mobile 我的建議 list. */
export const TRANSFER_STATE_LABELS = {
  EVALUATING: '評估中',
  IN_PROGRESS: '進行中',
  // Renamed 2026-05-10: BF 待設定 → 待約課設定. The new label communicates
  // the user-visible meaning ("waiting for class booking to be set up") instead
  // of internal jargon ("BF" = Bookfast, the Excel upload source).
  BF_PENDING: '待約課設定',
  COMPLETED: '已完成',
  REJECTED: '已拒絕',
  CANCELLED: '已取消',
} as const;

/** Urgency labels used by teacher mobile submission form and admin column display. */
export const TRANSFER_URGENCY_LABELS = {
  STANDARD: '一般',
  URGENT: '緊急',
} as const;

/**
 * Aging thresholds (in days) that drive amber / red visual on 轉班申請 rows.
 * Urgent rows age faster than standard rows because teacher marked it time-sensitive.
 *
 * Mirrors TRANSFER_AGING_THRESHOLDS_DAYS in functions/src/config/appConfig.ts.
 */
export const TRANSFER_AGING_THRESHOLDS_DAYS = {
  URGENT: { AMBER: 3, RED: 7 },
  STANDARD: { AMBER: 7, RED: 14 },
} as const;

/**
 * 老師建議's sentence (PO 2026-10-31): when 課務 usually starts talking to the family about a
 * suggested move — while the old class has about this many booked classes left. Advice the
 * sentence quotes (hubIdentity HubTextConfig); no rule reads it.
 */
export const TRANSFER_SUGGESTION_TALK_REMAINING_CLASSES = { FROM: 5, TO: 6 } as const;

/**
 * 轉為候補 (PO 2026-09-29, plan M1): what the 候補 created from a 轉班 record carries that
 * its form does not ask — 類型 正式課程 (the family moves between term classes). 已體驗 is
 * no longer sent (M7, PO: 「體驗一堂＝首次體驗課、體驗三堂＝首次三堂體驗」 — the 類型 alone
 * decides the price; the field is read nowhere).
 */
export const TRANSFER_TO_WAITLIST_DEFAULTS = { TYPE: '正式課程' } as const;

/**
 * 續報 › 其他處理 › 轉為候補 (PO 2026-10-02, Part 3): the 續報 sub-tabs whose rows offer it —
 * 已過期未通知, 待通知, 已通知·待確認 (RenewalFilterKey values). The ONE list RenewalRemindersTab
 * and useRenewalToWaitlist read (fix round 1 L8).
 */
export const RENEWAL_TO_WAITLIST_LISTS = [
  'pastDeadline',
  'needContact',
  'confirmedAwaiting',
] as const;
export type RenewalToWaitlistList = (typeof RENEWAL_TO_WAITLIST_LISTS)[number];

/**
 * Why a cancelled 轉班 record closed (`ProgramWaitlistDoc.cancellationReason`) — the
 * 學生紀錄 timeline tells 轉為候補 apart by it; the teacher's views read the rest (M5,
 * utils/teacherTransferView). Every value mirrors the backend's
 * TRANSFER_CANCELLATION_REASONS (functions/src/config/appConfig.ts), which
 * programWaitlist/transferActions.ts writes: 轉為候補, 取消轉班, 轉為不續報, 撤回 by the teacher
 * or by 課務, 已處理 on 已完成 / 未完成 — each pinned by functions/scripts/check-config-mirror.cjs.
 * Every close writes one since the deep review 2026-09-30 (Pass 7 H1); older 取消轉班 /
 * 轉為不續報 records carry none (utils/transferCancellation reads their log instead).
 */
export const TRANSFER_CANCELLATION_REASONS = {
  CONVERTED_TO_WAITLIST: 'converted-to-waitlist',
  /** 取消轉班 (安排中 / 待約課). */
  ADMIN_CANCELLED: 'admin-cancelled',
  /** 轉為不續報 (安排中 / 待約課 / 未完成). */
  PIVOT_NOT_RENEWING: 'pivot-not-renewing',
  TEACHER_RETRACTED: 'teacher-retracted',
  ADMIN_RETRACTED: 'admin-retracted',
  /** 取消建議 on a 新建議 whose old class's seat ended (PO 2026-10-09, D5) — the teacher's 已取消. */
  ADMIN_CANCELLED_SUGGESTION: 'admin-cancelled-suggestion',
  DISMISSED_AFTER_SUCCESS: 'admin-dismissed-after-success',
  DISMISSED_AFTER_GRACE: 'admin-dismissed-after-grace',
  /**
   * PO 2026-10-03 (未完成 自動歸檔): the 03:30 run closed a 未完成 by itself — the late
   * payer's success / one nobody handled for TRANSFER_EXPIRED_AUTO_CLOSE_DAYS. 學生紀錄
   * reads 系統自動歸檔 (utils/transferCancellation), never 已處理.
   */
  SYSTEM_CLOSED_AFTER_SUCCESS: 'system-closed-after-success',
  SYSTEM_CLOSED_AFTER_GRACE: 'system-closed-after-grace',
  /**
   * Audit items 4 / 8 (PO 2026-10-06): the same run closed a 新建議 whose student no longer
   * holds a seat in the old class, and a 原時段已下架 record nobody handled for
   * TRANSFER_EXPIRED_AUTO_CLOSE_DAYS. 學生紀錄 reads 系統自動歸檔; the teacher 已取消.
   */
  SYSTEM_CLOSED_NO_SOURCE_SEAT: 'system-closed-no-source-seat',
  SYSTEM_CLOSED_SOURCE_REMOVED: 'system-closed-source-removed',
  /**
   * 2026-10-13 (續報・轉班 完成規則 W0.4, H-03 / D-05) — 家長留原班: the old class filled a term
   * again and 課務 closed the 轉班. Record line 「取消轉班 · 家長續報原班」. Not read yet (W6).
   */
  PARENT_RENEWED_SOURCE: 'parent-renewed-source',
  /**
   * 2026-10-13 (W0.4, §J / J-03) — 老師建議 › 處理 › 已另行安排; the teacher reads 課務已處理 (P9).
   * Not read yet (W7).
   */
  ADMIN_HANDLED_ELSEWHERE: 'admin-handled-elsewhere',
} as const;

/**
 * 2026-10-13 (續報・轉班 完成規則 W0.4) — the words of a close reason where a screen names it
 * (學生紀錄's 回覆老師 card, utils/transferTimelineEvents; the H-03 record line). Only the
 * reasons that have words of their own; the older ones read through their cards
 * (utils/transferCancellation). MIRROR of TRANSFER_CANCELLATION_REASON_LABELS in
 * functions/src/config/appConfig.ts (check-config-mirror).
 */
export const TRANSFER_CANCELLATION_REASON_LABELS = {
  [TRANSFER_CANCELLATION_REASONS.PARENT_RENEWED_SOURCE]: '家長續報原班',
  [TRANSFER_CANCELLATION_REASONS.ADMIN_HANDLED_ELSEWHERE]: '已另行安排',
} as const;

/**
 * 確定轉入's result `outcome` (functions programWaitlist/transferActions.ts). FINISHED_ON_REAL_SEAT
 * — C2 (PO 2026-10-04, docs/tools/seat-renewal-fixes.html item 5): the student already held a
 * real seat in the new class and no date was picked; the transfer finished and that seat was left
 * as it is (utils/transferConfirmIn.ts confirmInToastOf picks the toast by it). MIRROR of
 * TRANSFER_CONFIRM_OUTCOMES in functions/src/config/appConfig.ts (check-config-mirror).
 */
export const TRANSFER_CONFIRM_OUTCOMES = {
  FINISHED_ON_REAL_SEAT: 'finished-on-real-seat',
} as const;

/**
 * Notification channel values written to NotificationLogEntry.channel.
 * 'none' = admin chose not to send (will handle externally).
 * 'manual' = admin chose to send manually via LINE / phone (logged intent only).
 * 'line' = system sent via LINE OA push (post-LIFF only).
 *
 * Mirrors NOTIFICATION_CHANNEL in functions/src/config/appConfig.ts.
 */
export const NOTIFICATION_CHANNEL = {
  NONE: 'none',
  MANUAL: 'manual',
  LINE: 'line',
} as const;

export type NotificationChannelValue =
  (typeof NOTIFICATION_CHANNEL)[keyof typeof NOTIFICATION_CHANNEL];

/**
 * 2026-06-24 (Messaging subsystem / W1, design §6) — the SINGLE future-LINE flip
 * point for the parent-notice dispatch subsystem. DORMANT in Phase A: no consumer
 * reads it yet; every send defaults to MANUAL (admin copy-paste). Phase B flips
 * this single const to NOTIFICATION_CHANNEL.LINE — the ONLY change needed to switch
 * the subsystem to LINE/LIFF auto-push. Rollback = revert one const.
 *
 * Mirrors NOTICE_DISPATCH_CHANNEL in functions/src/config/appConfig.ts.
 */
export const NOTICE_DISPATCH_CHANNEL: NotificationChannelValue = NOTIFICATION_CHANNEL.MANUAL;

/**
 * 2026-06-24 (Messaging subsystem / W1, §2c color taxonomy) — the per-notice-kind
 * visual theme, so any surface that colours a notice kind reads ONE source instead of
 * hardcoding green/blue. Its first reader, the 通知家長？ picker's buttons, was removed in
 * W10.4 F3 (D26 (3)); no reader is left today — kept, not deleted, as the one home for
 * these colours (W10.5 X8 / review VR-04 asked only for this comment).
 *
 * `bannerType`/`buttonType` are Naive UI semantic-type tokens (the established
 * token system — NOT raw hex). `color` is the canonical hex for direct-style needs
 * and is the SAME green/blue the timeline already uses (StudentEnrollmentTimeline's
 * COLOR_GREEN #27ae60 / COLOR_BLUE #3498db) — no new hex is invented here.
 *
 *   booking-success / renewal → green / success  (a real / new-term 約課成功通知)
 *   lsed-update               → blue  / info     (correcting an ongoing term's LSED)
 *
 * Consumed-by check is deferred to W4 (picker + offer-modal wiring).
 */
export const NOTICE_KIND_THEME = {
  'booking-success': { bannerType: 'success', buttonType: 'primary', color: '#27ae60' },
  renewal: { bannerType: 'success', buttonType: 'primary', color: '#27ae60' },
  'lsed-update': { bannerType: 'info', buttonType: 'info', color: '#3498db' },
} as const;

export type NoticeKindThemeKey = keyof typeof NOTICE_KIND_THEME;

/**
 * Centralized catalog of every `notificationsLog.type` value written across the
 * system. See `docs/SESSION_ENROLLMENT_BUSINESS_RULES.md` Appendix E for the
 * full event taxonomy.
 *
 * Mirrors NOTIFICATION_LOG_TYPES in functions/src/config/appConfig.ts — keep in sync.
 */
/**
 * 2026-05-27 — canonical const for `manualReservationReason` field values.
 * The field is already typed as a literal union (`'續報' | '轉班' | …`), which
 * catches assignment typos at compile time. This const adds the equivalent
 * compile-time check for COMPARISONS — TypeScript allows `field === 'anyStr'`
 * even when the field is a literal-union type, so direct string comparisons
 * can typo silently. Code should reference `MANUAL_RESERVATION_REASONS.TRANSFER`
 * instead of the bare literal `'轉班'` for that reason.
 *
 * Mirrors MANUAL_RESERVATION_REASONS in functions/src/config/appConfig.ts — keep in sync.
 */
export const MANUAL_RESERVATION_REASONS = {
  RENEWAL: '續報',
  TRANSFER: '轉班',
  SPECIAL_HOLD: '特殊保留名額',
  OTHER: '其他',
} as const;

export type ManualReservationReason =
  (typeof MANUAL_RESERVATION_REASONS)[keyof typeof MANUAL_RESERVATION_REASONS];

export const NOTIFICATION_LOG_TYPES = {
  // Enrollment birth
  INITIAL_DATA_LOADED: '初始資料驗證',
  ENROLLMENT_CREATED: '報名',
  ENROLLMENT_RESURRECTED: '重新報名',
  // Renewal lifecycle
  RENEWAL_NOTIFIED: '已通知續報',
  // 2026-05-24 (post-R3 testing): Stage 2 close (markNotified via 完成續報流程)
  // now writes this distinct type — was duplicating RENEWAL_NOTIFIED. Mirrors
  // transfer flow's TRANSFER_CONFIRMED pattern. See backend appConfig.ts for
  // full rationale.
  RENEWAL_COMPLETED: '確認續報',
  RENEWAL_NOTICE_CANCELLED: '取消已通知續報',
  // 2026-05-24 rename — see backend appConfig.ts for rationale.
  RENEWAL_CONFIRMED: '系統偵測續報',
  /** 2026-06-21 (W5 / D12 A) — 延長並通知續報 on a 已過期未通知 row. */
  RENEWAL_EXTENDED_NOTIFY: '延長並通知續報',
  /** 2026-09-25 (續報 期限與提醒 M3) — 標記已再提醒 on a 已通知·待確認 row (the halfway
   *  續報提醒 was copy-pasted to the parent). channel='manual'; customizedMessage
   *  「保留至 YYYY/MM/DD」 (+ 「(原告知 …)」). NOT in NOTICE_SENT_LOG_TYPES. Must be in
   *  TYPES_WITH_DATA_MESSAGE (StudentEnrollmentTimeline) or it renders as "原因：…".
   *  See backend appConfig.ts. */
  RENEWAL_REMINDED: '已再提醒續報',
  /** 2026-06-21 (W5 / D12 B) — 已確認釋出名額 on a 已過期未通知 row. */
  RENEWAL_RELEASE_CONFIRMED: '已確認釋出名額',
  /** 2026-06-22 (Option B / W1) — 通知約課成功 on a 通知約課成功 row (real-date
   *  約課成功通知, copy-to-clipboard; clears the success-notify flag unit). */
  BOOKING_SUCCESS_NOTIFIED: '約課成功通知',
  /** 2026-06-22 (Option B / W6) — 通知約課成功 auto-cleared WITHOUT sending
   *  (30-day window elapsed OR LSED autoEnd). Audit marker. */
  BOOKING_SUCCESS_AUTO_EXPIRED: '約課成功通知·逾期未發（自動清除）',
  /** 2026-06-24 (Messaging subsystem / W1) — 標記無需通知 (per-row OR batch): the
   *  約課通知待發 to-do discharged WITHOUT sending (channel='none'). See backend
   *  appConfig.ts for full rationale. */
  BOOKING_SUCCESS_RESOLVED: '已標記無需通知（未發送）',
  /** 2026-06-24 (Messaging subsystem / W1) — 學期日期更新通知 (template ②) sent after
   *  re-anchoring an enrolled student's LSED. Distinct from BOOKING_SUCCESS_NOTIFIED.
   *  Must be in TYPES_WITH_DATA_MESSAGE (StudentEnrollmentTimeline) or its 學期末日
   *  message renders as "原因：…". See backend appConfig.ts. */
  LSED_UPDATE_NOTIFIED: '學期日期更新通知',
  /** 2026-06-24 (Messaging subsystem / W1) — 約課成功通知 re-sent via 通知家長（補送/重送）
   *  (NET-NEW #7). Same green/success theme as BOOKING_SUCCESS_NOTIFIED, DISTINCT type. */
  BOOKING_SUCCESS_RESEND: '約課成功通知（重送）',
  // Soft-end + special hold
  NOT_RENEWING: '不續報',
  /** 2026-06-21 — system auto-archive (Scan A/B/C); reason in customizedMessage. */
  SYSTEM_AUTO_ARCHIVED: '系統自動歸檔',
  SPECIAL_HOLD_SET: '設為特殊保留名額',
  SPECIAL_HOLD_REMOVED: '移除特殊保留名額',
  /** Detection auto-released a 暫時保留名額 hold after ≥AUTO_TAG_THRESHOLD real
   *  bookings arrived past LSED. See backend appConfig.ts for full rationale. */
  SPECIAL_HOLD_AUTO_RELEASED: '自動移除暫時保留名額',
  /** 2026-07-18 (seat-hold guard, PR 4c): detection observed an ACTIVE row whose
   *  seat-hold is still LIVE but with ZERO future 學期 bookings (Excel wiped its
   *  future classes). The seat is KEPT (held to LSED), NOT ended — this stamps the
   *  first zeroFutureBookingsAt observation into the timeline (write-once).
   *  channel='system'; the centralized message rides in customizedMessage (rendered
   *  directly via TYPES_WITH_DATA_MESSAGE). Mirrors backend appConfig.ts. */
  SEAT_HELD_NO_BOOKINGS: '名額保留中',
  // Manual 設定保留名額 RE-ANCHOR (2026-05-24) — distinct from auto-detected '報名'/'續報'
  RESERVATION_UPDATED: '更新保留名額',
  RESERVATION_RESET: '重設保留名額',
  // 2026-05-24 (R4 follow-up): sub-threshold manual confirm. Admin's
  // RE-ANCHOR on a row that had renewalNotifiedAt OR endingReason='完成續報但未上傳Excel'.
  // Semantically "I confirm this student renewed with partial bookings."
  // See backend appConfig.ts for full rationale. 2026-10-03: NO LONGER WRITTEN
  // (the action went with its two states); kept so 學生紀錄 still labels old entries.
  RENEWAL_MANUALLY_CONFIRMED: '手動確認已續報',
  // 2026-06-21 (W1 / D1-D3): DATE-ONLY / preserve LSED re-anchor on a committed
  // row (transfer / 暫時保留 / renewal-notified). The
  // commitment is PRESERVED, not closed. See backend appConfig.ts. Must be in
  // TYPES_WITH_DATA_MESSAGE (StudentEnrollmentTimeline) or its 最後約課日 message
  // renders as "原因：最後約課日…".
  LSED_REANCHORED: '更新學期末日',
  /** 2026-10-13 (續報・轉班 完成規則 W0.3, P1 / P4 / D-01): 結算續報 — the bookings after the old
   *  最後約課日 became the new term (decided by fact). Enrollment log; carries `lsed` and a
   *  message with UI_STRINGS.TRANSFER_REDESIGN.SUB_THRESHOLD.COMPLETION_MARKER (the words
   *  結算轉班 writes) + the count — data, so in TYPES_WITH_DATA_MESSAGE; the chip has 結算轉班's
   *  colours (utils/timelineNotificationChips.ts). Mirrors backend appConfig.ts
   *  (check-config-mirror pair). */
  RENEWAL_SETTLED: '結算續報',
  // Transfer flow
  TRANSFER_REGISTERED: '登記轉出',
  TRANSFER_SUGGESTED_BY_TEACHER: '老師建議轉班',
  /** 2026-10-13 (W0.3, P1 / J-05 / D3 (f)): 回覆老師 — 課務's reply to a teacher's suggestion.
   *  Written on the 轉班 record only, beside its `adminReplies[]` entry; 學生紀錄 draws the
   *  adminReplies entry (utils/transferTimelineEvents) and skips this log line. Mirrors
   *  backend appConfig.ts (check-config-mirror pair). */
  TRANSFER_TEACHER_REPLY: '回覆老師',
  TRANSFER_NOTIFIED: '已寄出轉班通知',
  TRANSFER_SUGGESTION_ACCEPTED: '已確認老師轉班建議',
  TRANSFER_SUGGESTION_REJECTED: '拒絕老師建議',
  TRANSFER_CONFIRMED: '確認轉入',
  TRANSFER_EXCEL_CONFIRMED: '轉入新班 Excel 已確認',
  TRANSFER_CANCELLED: '轉班取消',
  TRANSFER_PIVOTED_TO_NOT_RENEWING: '轉為不續報',
  TRANSFER_SUGGESTION_RETRACTED: '撤回轉班建議',
  /** 課務 取消建議 (D5, PO 2026-10-09) — its message is the 取消原因. */
  TRANSFER_SUGGESTION_CANCELLED: '取消老師建議',
  TRANSFER_UNDONE: '撤銷轉入',
  TRANSFER_RECOMPLETED: '重新完成轉班',
  TRANSFER_CLOSED_NO_FOLLOWUP: '關閉（無後續）',
  /** Admin clicked 結算轉班 on a 待約課 row — manual close with sub-threshold LSED. */
  TRANSFER_MANUAL_COMPLETED: '結算轉班',
  /** Admin clicked 修改新班時段 on a 待約課 row — re-picks target timeslot. */
  TRANSFER_TARGET_CHANGED: '修改新班時段',
  /** 2026-09-29 — 轉為候補: the 轉班 record closed into a new 候補; customizedMessage
   *  「候補：{班級} {時段}」 (the 學生紀錄 card's second line). See backend appConfig.ts. */
  TRANSFER_CONVERTED_TO_WAITLIST: '轉為候補',
  /** 2026-09-29 (Rule 11): releasing a 轉班 hold that 確定轉入 had put on an ENDED doc of
   *  the same student + class put the earlier enrollment back (priorLife) instead of
   *  deleting the doc. Written to that enrollment, channel 'system'. See backend appConfig.ts. */
  TRANSFER_HOLD_RESTORED: '轉班保留釋出，恢復先前的報名紀錄',
  /** Class-identity canonicalization (Option B, 2026-07): the class was renamed.
   *  channel='system'; carries 「班別更名：{舊班} → {新班}」 in customizedMessage (in
   *  TYPES_WITH_DATA_MESSAGE so it renders directly, not as 「原因：…」). Actor stamped
   *  `class-identity:{套用者}` → 「常態班更名套用 (套用者)」 via StudentEnrollmentTimeline.formatActor.
   *  D1 / D1a (PO 2026-10-09): written ONCE per record by the daily run, dated the first lesson
   *  BF's sheet names with the new name (no longer at the 套用); July 2026 lines (written at the
   *  套用) stay as they are. Mirrors backend appConfig.ts (check-config-mirror pair). */
  CLASS_IDENTITY_MIGRATED: '班別更名',
  /** 常態班更名 2026-10 (teacher-change mapping, DT5 / A10): a 換老師-only entry moved /
   *  merged / restored this enrollment onto the new teacher's timeslot; customizedMessage
   *  「老師更換：{舊老師} → {新老師}」 (in TYPES_WITH_DATA_MESSAGE). D1 (PO 2026-10-09): NOT
   *  written any more — a 換老師 writes no 學生紀錄 line; lines already stored render as before.
   *  Mirrors backend appConfig.ts (check-config-mirror pair). */
  CLASS_IDENTITY_TEACHER_CHANGED: '老師更換',
  /** D1a (2026-10-09, derived from D1) — a 套用 repaired this record: merged the record the
   *  system had created at the new class (「已合併系統自動建立的新班紀錄」) and / or restored an
   *  ending the old class's schedule caused (「原班課表結束時被誤判結束，已復原」). channel
   *  'system', the `class-identity:{套用者}` actor; customizedMessage is the sentence (in
   *  TYPES_WITH_DATA_MESSAGE); 學生紀錄 shows the grey chip 「紀錄修正」
   *  (utils/timelineNotificationChips.ts). Mirrors backend appConfig.ts (check-config-mirror
   *  pair). */
  CLASS_IDENTITY_RECORD_REPAIRED: '紀錄修正',
  /** 2026-09-29 (deep review Q1, PO 「Q1 yes」): staff confirmed 「保留名額已滿」 and the
   *  seat went through over capacity. Written by the server on the enrollment that gained
   *  the seat: `{ reserved, max, action }`, channel 'none' (reserved = N before this
   *  student, as in the slot-full refusal). 學生紀錄 shows 「超收確認 (N+1/M)」 in red
   *  (utils/timelineNotificationChips.ts). See backend appConfig.ts. */
  OVER_CAPACITY_CONFIRMED: '超收確認',
  /** 2026-10-04 (PO fail-safe, option S — tasks/todo-transfer-lock.md B3): a 轉班 action
   *  released a new-class hold the office had put on 暫時保留名額 (取消轉班 / 轉為候補 /
   *  修改新班時段) — only the hold's owner stamp went, the 暫時保留名額 stays. Written by the
   *  server on that enrollment, channel 'system'; 學生紀錄 shows a neutral chip
   *  (utils/timelineNotificationChips.ts). See backend appConfig.ts. */
  TRANSFER_HOLD_KEPT_AS_SPECIAL: '轉班保留釋出，暫時保留名額 繼續保留',
  /** PO 2026-10-05 (as-built Q7): 撤銷確定轉入 — a 已完成 record 確定轉入 finished at once went
   *  back to 安排中. Written on the 轉班 record (the new class it had and what became of it)
   *  and on the old class (back to 轉出中), channel 'system'. 學生紀錄 draws the record's; the
   *  old class's is its own history (useStudentEnrollmentTimeline CANONICAL_ON_WAITLIST_TYPES).
   *  See backend appConfig.ts. */
  TRANSFER_CONFIRM_UNDONE: '撤銷確定轉入',
} as const;

export type NotificationLogType =
  (typeof NOTIFICATION_LOG_TYPES)[keyof typeof NOTIFICATION_LOG_TYPES];

/**
 * 2026-06-24 (Messaging subsystem / W1) — the parent-notice SENT-state predicate.
 *
 * SENT-state derives from the log TYPE, NOT from channel. `channel:'system'` is
 * written at ~17 backend sites and is NOT in the NOTIFICATION_CHANNEL enum — so a
 * `channel !== 'none'` test would mis-label system events as "parent was notified."
 *   • SENT   = log TYPE ∈ NOTICE_SENT_LOG_TYPES AND channel ∈ {manual, line}
 *   • RESOLVED (discharged-without-send) = BOOKING_SUCCESS_RESOLVED (channel:'none')
 *   • AUTO-EXPIRED = BOOKING_SUCCESS_AUTO_EXPIRED (channel:'system')
 * Keep "pending" off (flag-present) + log TYPE; never the channel trichotomy.
 *
 * Mirrors NOTICE_SENT_LOG_TYPES + isParentNoticeSent in functions/src/config/appConfig.ts.
 */
export const NOTICE_SENT_LOG_TYPES: ReadonlySet<NotificationLogType> = new Set([
  NOTIFICATION_LOG_TYPES.BOOKING_SUCCESS_NOTIFIED,
  NOTIFICATION_LOG_TYPES.LSED_UPDATE_NOTIFIED,
  NOTIFICATION_LOG_TYPES.TRANSFER_NOTIFIED,
]);

/**
 * 2026-09-29 (Hub UI review CM1) — the log entries that record a 通知續報 (the parent was
 * told about renewal): 通知續報 and 延長並通知續報. RENEWAL_NOTICE_CANCELLED (撤銷通知)
 * withdraws the notice before it. Read by 近期未處理's 「已通知 M/D」 on archived rows —
 * the 不續報 write clears renewalContactedAt, the log keeps the entry. Display only;
 * not the flag-based 已通知·待確認 state.
 */
export const RENEWAL_NOTICE_LOG_TYPES: ReadonlySet<NotificationLogType> = new Set([
  NOTIFICATION_LOG_TYPES.RENEWAL_NOTIFIED,
  NOTIFICATION_LOG_TYPES.RENEWAL_EXTENDED_NOTIFY,
]);

/**
 * 2026-10-23 (名額使用情況 D80, PO 「A」: tags only for the messages sent to the family) — the
 * 學期紀錄 log entries that draw a step tag above the window grid (utils/seatUsageSteps.ts):
 * 通知續報 (and 延長並通知續報), 已再提醒續報, 約課通知 (and its 重送), and — the same kind,
 * derived — 學期日期更新通知 and 已寄出轉班通知. A record change (報名, 修改最後約課日,
 * 確認轉入, 不續報 …) draws none; 備註 › 學期紀錄 lists every entry. Display only. Each of these
 * types is written only for a message sent (the no-send discharge has its own type,
 * BOOKING_SUCCESS_RESOLVED), so the type alone decides.
 */
export const SEAT_USAGE_STEP_LOG_TYPES: ReadonlySet<NotificationLogType> = new Set([
  ...RENEWAL_NOTICE_LOG_TYPES,
  NOTIFICATION_LOG_TYPES.RENEWAL_REMINDED,
  NOTIFICATION_LOG_TYPES.BOOKING_SUCCESS_NOTIFIED,
  NOTIFICATION_LOG_TYPES.BOOKING_SUCCESS_RESEND,
  NOTIFICATION_LOG_TYPES.LSED_UPDATE_NOTIFIED,
  NOTIFICATION_LOG_TYPES.TRANSFER_NOTIFIED,
]);

/**
 * Pure predicate: was a genuine parent notice SENT? A log entry counts as SENT iff
 * its TYPE is one of the three parent-facing notice types AND its channel is a real
 * delivery channel (manual copy-paste OR future LINE push). NEVER keyed on
 * `channel !== 'none'`. Excludes BOOKING_SUCCESS_RESOLVED (channel:'none'),
 * BOOKING_SUCCESS_AUTO_EXPIRED + every `channel:'system'` entry.
 */
export function isParentNoticeSent(logEntry: { type?: string; channel?: string }): boolean {
  if (!logEntry.type) return false;
  if (!NOTICE_SENT_LOG_TYPES.has(logEntry.type as NotificationLogType)) return false;
  return (
    logEntry.channel === NOTIFICATION_CHANNEL.MANUAL ||
    logEntry.channel === NOTIFICATION_CHANNEL.LINE
  );
}

/**
 * Legacy `notificationsLog.type` values still present in production data but
 * no longer written by new code. The timeline chip mapper renders these for
 * historical entries; new writes must use NOTIFICATION_LOG_TYPES.
 */
export const LEGACY_NOTIFICATION_LOG_TYPES = {
  // 2026-05-24 rename — RENEWAL_CONFIRMED now writes '系統偵測續報'.
  RENEWAL_CONFIRMED_PLAIN: '續報',
  VERIFIED: '驗證',
  // 2026-05-26 rename — INITIAL_DATA_LOADED now writes '初始資料驗證' (was
  // '初始資料載入'). Legacy entries in historical Firestore data still render
  // via the timeline chip mapper's fallback key.
  INITIAL_DATA_LOADED_OLD: '初始資料載入',
  RESURRECTION_ACKNOWLEDGED: '確認資料衝突',
  RENEWAL_CONFIRMED_TO_CUSTOMER: '已通知續報客戶',
  TRANSFER_NOTIFIED_OLD: '已通知轉班客戶',
  TRANSFER_REGISTERED_OLD: '登記轉班',
  TRANSFER_SUGGESTED_OLD: '建議轉班',
  TRANSFER_ACKNOWLEDGED_OLD: '安排轉班',
  TRANSFER_ARRANGED_OLD: '已安排',
  TRANSFER_REJECTED_OLD: '拒絕',
  TRANSFER_CANCELLED_OLD: '取消',
  TRANSFER_TARGET_OLD: '轉班',
  TRANSFER_UNDONE_OLD: '撤銷完成',
  TRANSFER_EXCEL_CONFIRMED_OLD: '轉班完成',
  TRANSFER_CLOSED_OLD: '關閉(無後續)',
} as const;

/**
 * Retention period for resolved 轉班 and regular waitlist entries.
 * Wave 7 will swap the hardcoded 7-day window in useProgramWaitlist.ts:273 for this constant.
 *
 * Mirrors RESOLVED_RETENTION_DAYS in functions/src/config/appConfig.ts.
 */
export const RESOLVED_RETENTION_DAYS = 30;

/**
 * Teachers see status only (M5, PO 2026-09-29; design transfer-to-waitlist.html § 8):
 * the one status a teacher sees per 轉班 suggestion — TeacherReservationBanner (mobile
 * student page + desktop 學生紀錄), TeacherReservationModal, the ⇄ TransferSuggestButton —
 * never a 課務 note. `tone` = the pill's colour family (the hub chip tokens). Which record
 * gets which key is utils/teacherTransferView.ts; 已轉入 (the new class confirmed — PO
 * 2026-09-29) is followed by the new class (formatTimeslotLabel of concludedTargetTimeslotId,
 * decision 8-1). The `legacy*` keys are only for pre-backfill records that carry no `state`;
 * `handled` is a `state` this build does not know (a closed, grey line instead of a broken
 * banner — review B4).
 *
 * 2026-10-14 (續報・轉班 完成規則 W7.5 — J-06, D-07, P9; one word per key): 課務處理中 covers
 * 安排中 AND 確定轉入 while the new class waits for its bookings AND 未完成 (the former 已安排轉入
 * / 未完成 (新班未約課) — the teacher sees only that 課務 is still on it); 已轉為候補 → 已安排候補;
 * 已拒絕 → 未採用; NEW 課務已處理 = 處理 › 已另行安排, a 未完成 closed by 已處理, and the 10/9
 * 取消建議 records (teacherTransferView TEACHER_KEY_OF_CLOSE / TEACHER_KEY_OF_REASON).
 */
export const TRANSFER_TEACHER_STATUS = {
  evaluating: { label: '待課務處理', tone: 'cyan' },
  arranging: { label: '課務處理中', tone: 'amber' },
  transferred: { label: '已轉入', tone: 'green' },
  converted: { label: '已安排候補', tone: 'blueState' },
  rejected: { label: '未採用', tone: 'red' },
  handledElsewhere: { label: '課務已處理', tone: 'grey' },
  retracted: { label: '已撤回', tone: 'grey' },
  cancelled: { label: '已取消', tone: 'grey' },
  /** 2026-10-14 (續報・轉班 完成規則 W6.6, H-03 / J-06 / D-07): closed by 家長留原班. */
  parentKeptSource: { label: '已取消：家長續報原班', tone: 'grey' },
  handled: { label: '已處理', tone: 'grey' },
  legacyPending: { label: '待處理', tone: 'cyan' },
  legacyScheduled: { label: '已安排', tone: 'green' },
  legacyGivenUp: { label: '已放棄', tone: 'grey' },
} as const satisfies Record<string, { label: string; tone: HubFamily }>;
export type TransferTeacherStatusKey = keyof typeof TRANSFER_TEACHER_STATUS;

/**
 * 2026-10-14 (續報・轉班 完成規則 W7.4, §J / J-05) — `adminReplies[].outcome`: which 老師建議 ›
 * 處理 choice a reply to the teacher went with (a close's value is its cancellationReason where
 * it has one). MIRROR of the backend ADMIN_REPLY_OUTCOMES (functions config/appConfig.ts).
 */
export const ADMIN_REPLY_OUTCOMES = {
  ACKNOWLEDGED: 'acknowledged',
  CONVERTED_TO_WAITLIST: TRANSFER_CANCELLATION_REASONS.CONVERTED_TO_WAITLIST,
  HANDLED_ELSEWHERE: TRANSFER_CANCELLATION_REASONS.ADMIN_HANDLED_ELSEWHERE,
  REJECTED: 'rejected',
} as const;

export type TransferHandleChoice = keyof typeof HUB_TRANSFER_HANDLE_LABEL;

/**
 * 2026-10-14 (W7.4, J-03 / J-10) — 老師建議 › 處理's four choices, in the window's order: the
 * radio's word (hubUi HUB_TRANSFER_HANDLE_LABEL — the 老師建議 sentence reads the same), its
 * one-line hover (DERIVED ⚑ — what happens and the word the teacher then reads,
 * TRANSFER_TEACHER_STATUS), the reply outcome it records, and whether the reply to the
 * teacher is required (已另行安排 / 不採用 — the server refuses them without one).
 */
export const TRANSFER_HANDLE_CHOICES: readonly {
  key: TransferHandleChoice;
  label: string;
  title: string;
  outcome: (typeof ADMIN_REPLY_OUTCOMES)[keyof typeof ADMIN_REPLY_OUTCOMES];
  replyRequired: boolean;
}[] = [
  {
    key: 'registerTransfer',
    label: HUB_TRANSFER_HANDLE_LABEL.registerTransfer,
    title: `排入 安排中，接著選新班時段；老師看到 ${TRANSFER_TEACHER_STATUS.arranging.label}`,
    outcome: ADMIN_REPLY_OUTCOMES.ACKNOWLEDGED,
    replyRequired: false,
  },
  {
    key: 'toWaitlist',
    label: HUB_TRANSFER_HANDLE_LABEL.toWaitlist,
    title: `改成一筆候補 (資料已帶入，原班不變)；老師看到 ${TRANSFER_TEACHER_STATUS.converted.label}`,
    outcome: ADMIN_REPLY_OUTCOMES.CONVERTED_TO_WAITLIST,
    replyRequired: false,
  },
  {
    key: 'handledElsewhere',
    label: HUB_TRANSFER_HANDLE_LABEL.handledElsewhere,
    title: `已在 BF 直接報新班、家長要留原班、或之後再說：這筆建議結束，不動任何班級；老師看到 ${TRANSFER_TEACHER_STATUS.handledElsewhere.label}`,
    outcome: ADMIN_REPLY_OUTCOMES.HANDLED_ELSEWHERE,
    replyRequired: true,
  },
  {
    key: 'reject',
    label: HUB_TRANSFER_HANDLE_LABEL.reject,
    title: `不照這筆建議轉班，請寫原因；老師看到 ${TRANSFER_TEACHER_STATUS.rejected.label} 和回覆`,
    outcome: ADMIN_REPLY_OUTCOMES.REJECTED,
    replyRequired: true,
  },
];

/**
 * 2026-10-14 (W7.4, plan review #13 — the 處理 prefill table; the 10/9 D5 sentence
 * 「此轉班建議取消。」 is dropped everywhere: the suggestion is handled, not cancelled): when
 * the old class has ended (原班已結束 — the 03:00 archive / 時段下架), was 轉出, or is 不續報,
 * the window opens with PRESELECT picked and the reply filled in (editable — the teacher reads
 * it). 請長假 / 轉班中 / 原班 still running → nothing picked, nothing filled
 * (utils/transferInboxSource inboxSourceStateOf). ENDED's `md` = the day the old class
 * ended (M/D); null → the same sentence without 「於 M/D 」.
 */
export const TRANSFER_HANDLE_PREFILL = {
  PRESELECT: 'handledElsewhere' as TransferHandleChoice,
  ENDED: (md: string | null) =>
    `原班名額已${md ? `於 ${md} ` : ''}結束名額保留，將直接協助家長報名新班。`,
  /** 10/9 D6: no promise of a new class — the student is already in another one. */
  TRANSFERRED_OUT: '學生已轉出原班。',
  NOT_RENEWING: '原班已不續報，將直接協助家長報名新班。',
} as const;

/**
 * How long a resolved 轉班 stays on the teacher's views (M5; review B4 2026-09-29): a closed
 * record (cancelled / rejected / unknown state) for this many days after its lastModifiedAt
 * (else completedAt, else createdAt); a completed one after its LAST activity (the latest of
 * targetConfirmedAt / lastModifiedAt / transferConfirmedAt — 延長保留 rewrites only
 * lastModifiedAt), and never while it still waits for the new class. Open records always
 * show.
 */
export const TEACHER_TRANSFER_RETENTION_DAYS = RESOLVED_RETENTION_DAYS;

/**
 * Confirm dialog text templates for 轉班 completion paths.
 * Use {{var}} placeholders; substitute at call site with a helper:
 *   text.replace('{{student}}', studentName).replace('{{sourceTimeslot}}', ...)
 *
 * Mirrors CONFIRM_DIALOGS in functions/src/config/appConfig.ts.
 */
export const CONFIRM_DIALOGS = {
  /** Path B — target locked, BF already set: confirm release of source slot. */
  PATH_B: '將釋放 {{student}} 的原時段保留位，確認轉至 {{target}}。',
  /**
   * W10.4 F2 (D26 (2), PO 2026-10-14): the old class had ALREADY ENDED (Excel / the 03:00 run)
   * — 確定轉入 leaves it as it is (website only; useConfirmSourceEnded). No stored end day →
   * the sentence without 「於 M/D」.
   */
  PATH_B_SOURCE_ENDED: '原班已於 {{md}} 結束，不受影響，確認轉至 {{target}}。',
  PATH_B_SOURCE_ENDED_NO_DATE: '原班已結束，不受影響，確認轉至 {{target}}。',
  /** Path A — BF not yet set: confirm release of source slot + stay in BF待設定. */
  PATH_A: '將釋放 {{student}} 的原時段保留位。約課尚未設定，留在「轉班 → 待約課設定」追蹤。',
  /** Cancel transfer — returns source to active. */
  CANCEL: '確定取消 {{student}} 的轉班登記？原時段將恢復保留位。',
  /** Pivot from transfer to non-renewal. */
  PIVOT_NOT_RENEWING: '確定將 {{student}} 的轉班改為不續報？將以不續報原因結束原時段。',
  /** Stage 1 registration confirm (admin-initiated) — shown when clicking 登記轉班. */
  REGISTER_TRANSFER: '將標記 {{student}} 為待轉出，保留位繼續顯示。',
  /** Stage 2 Path B from BF待設定 row — finalize the pending BF record. */
  END_NOT_RENEWING: '確定以不續報結束 {{student}} 的課程？此操作無法復原。',
  /** Mark the enrollment as paused. */
  START_PAUSE: '確定暫停 {{student}} 的課程？位置將保留，可隨時解除停課。',
  /** Resume from paused state. */
  RESUME_FROM_PAUSE: '確定解除 {{student}} 的停課，恢復正常上課狀態？',
  /** Mark enrollment as long leave — slot is released immediately. */
  START_LONG_LEAVE: '確定為 {{student}} 登記請長假？課程位置將立即釋出。',
  /**
   * 取消轉班 from 待約課 (M4 B3, PO 2026-09-29 — the confirm says honestly what happens to
   * both seats; utils/transferCancel, frontend only): `target` = the held new class ('' =
   * the legacy 待約課設定, which holds none); `sourceLine` = one CANCEL_SOURCE_* below.
   */
  CANCEL_FROM_AWAITING: (target: string, source: string, sourceLine: string) =>
    target
      ? `取消轉班：新班 ${target} 的保留名額會釋出。原班 ${source} ${sourceLine}。`
      : `取消轉班：原班 ${source} ${sourceLine}。`,
  /**
   * Still 已轉出 → the backend puts it back. 2026-10-15 (D43 B7, sweep F5 — was 「恢復在籍」):
   * `seatLine` = the shared seat line of the old class (utils/transferCancel restoredSeatLine —
   * 「原班 {班名} 保留名額：最後約課日 M/D」 or 「原班名額已於 M/D 釋出」), in place of 「原班 {班名} …」.
   */
  CANCEL_FROM_AWAITING_RESTORED: (target: string, seatLine: string) =>
    target ? `取消轉班：新班 ${target} 的保留名額會釋出。${seatLine}。` : `取消轉班：${seatLine}。`,
  /** Already released (PR 4d / autoEnd) → stays released. `md` = the day ('' = not known). */
  CANCEL_SOURCE_RELEASED: (md: string) =>
    md ? `的名額已於 ${md} 釋出，不會重新保留` : '的名額已釋出，不會重新保留',
  /** The legacy 待約課設定: its old class is never put back (B2 — waitlist-only). */
  CANCEL_SOURCE_KEPT: (md: string) => (md ? `維持已轉出 (學期到 ${md})` : '維持已轉出'),
  /** Live and no longer 已轉出 (re-filled / reactivated): the cancel leaves it alone. */
  CANCEL_SOURCE_UNCHANGED: '維持現狀',
} as const;

/**
 * UI string constants for the 轉班 redesign surfaces.
 * All user-visible strings must come from here — never inline in components
 * (per CLAUDE.md §7).
 *
 * Placeholder syntax: {{var}} — substitute at call site.
 * Mirrors UI_STRINGS.TRANSFER_REDESIGN in functions/src/config/appConfig.ts.
 */
/** 常態班更名 (班別識別統一) apply token of a `derived` entry [B-INT4]. There is NO typed
 *  input since v2: the server still requires the token on apply and the page computes it
 *  and sends it automatically (ClassIdentityMappingPage `requiredToken`); the preview +
 *  the red button (UI_STRINGS.CLASS_IDENTITY.CONFIRM_BUTTON 「確認」) are the human
 *  confirmation. A server-compared value, not a label — do not reuse it as button text.
 *  Mirrors BE BUSINESS_RULES.CLASS_IDENTITY.CONFIRM_TOKEN_KEYWORD (check-config-mirror pair). */
const CLASS_IDENTITY_CONFIRM_KEYWORD = '確認套用';

/** Actor-token prefix of a 常態班更名 apply: the server stamps `class-identity:{套用者}` on
 *  the notificationsLog entry; 學生紀錄 renders it as UI_STRINGS.CLASS_IDENTITY.ACTOR_LABEL
 *  (origin-first, like `manual:`). Mirrors BE BUSINESS_RULES.CLASS_IDENTITY.ACTOR_PREFIX
 *  (check-config-mirror pair). */
export const CLASS_IDENTITY_ACTOR_PREFIX = 'class-identity:';

/** One literal for UI_STRINGS.CLASS_IDENTITY.NO_CHANGE_HINT and its pre-W4 alias
 *  NO_CHANGE_HINT (the W1 alias SUCCESSOR_UNCHANGED_HINT was removed in W4). */
const CLASS_IDENTITY_NO_CHANGE_HINT = '名稱與老師都沒有變更。';

/** 顯示方式 (PO 2026-10-05 第 11 段) — the three slot-label chips, ONE literal each for the chip
 *  (UI_STRINGS.CLASS_IDENTITY.SLOT_CHIP_*) and the 套用 window sentence that names it. Worded by
 *  D2 (PO 2026-10-09: 「reword to 將改老師」, 「shorten it to BF 未更新」); meaning and timing
 *  unchanged. */
const SLOT_CHIP_TEACHER_CHANGING_TEXT = '將改老師';
const SLOT_CHIP_RENAMING_TEXT = '將改班名';
const SLOT_CHIP_EXCEL_BEHIND_TEXT = 'BF 未更新';
/** 'immediate' | 'follow-excel' — the keys of the per-choice 套用 sentences. */
type ClassIdentityDisplayModeChoiceKey =
  (typeof FRONTEND_BUSINESS_RULES.CLASS_IDENTITY_LIMITS.DISPLAY_MODE_VALUES)[number];

/** The PO's BF rule (DT9 banner line ②) — ONE literal for UI_STRINGS.BF_CHANGE_REMINDER.LINES[1]
 *  and the 常態班更名 scenario guide's BF_RULE_LINE (W8 pre-check B7: they must never drift). */
const BF_CHANGE_REMINDER_BF_RULE_LINE =
  'BF 已建立且有約課的班，若要換老師，用 BF「代課」方式處理；BF 新建立班級時再換老師。';

/**
 * U+3000 IDEOGRAPHIC SPACE — the wide column gap used inside CJK parent-facing
 * copy (it separates 「第 1 堂」 from the date). Built from its code point on purpose: as a
 * literal it is an invisible character in the source and eslint rejects it
 * (`no-irregular-whitespace`), which is a rule worth keeping — this constant is
 * the one sanctioned way to emit it.
 */
const IDEOGRAPHIC_SPACE = String.fromCharCode(0x3000);

/**
 * ONE Chinese label per system endingReason code (PO 2026-09-28, Hub UI W6 follow-up:
 * 「use the second set everywhere」) — read by 學期紀錄 and SetReservationModal (via
 * TRANSFER_REDESIGN.ENDING_REASON_LABELS) and by 近期未處理 + the 排課時段表 chip (via
 * HUB.MISSED.REASON_LABELS). Display only: the stored codes are unchanged. The nightly
 * 課務 digest keeps its own copy (functions/src/sessionEnrollments/courseOpsDigest.ts).
 */
const SYSTEM_ENDING_REASON_LABELS: Record<string, string> = {
  'auto-ended': '學期到期未續報',
  'timeslot-removed': '時段下架',
  // PO 2026-10-03 (Q5 = B): the 未完成 tab's own words (was 「BF 信任鎖逾期」).
  'bf-grace-expired': '保留到期仍未收到約課資料',
  'no-active-bookings': '無未來預約',
};

/**
 * 已處理's confirm — ONE sentence on 近期未處理 and 轉班 (review P6-9; was 「無法復原」 on 轉班
 * only). Both are final: the record leaves that list and no action brings it back
 * (近期未處理: missedRenewalDismissedAt is never cleared; 轉班: `dismiss` sets the record
 * 'cancelled', which no transfer action reopens). 學生紀錄 keeps it (the 學期紀錄 timeline
 * shows 標記為已處理). `keeps` = what else stays as it is, when the reader could wonder.
 */
function hubHandledConfirm(list: string, keeps = ''): string {
  return `標記為已處理後，此紀錄將從 ${list} 清單移除 (${keeps ? `${keeps}，` : ''}學生紀錄仍可查閱)，無法復原。確定繼續？`;
}

/**
 * 轉為候補's note in both 其他處理 windows — 續報's (OTHER_HANDLING) and 轉班's
 * (HUB.TRANSFER.OTHER_NOTE): one sentence (deep review 2026-10-02, L-7). 2026-10-14 (W9.5
 * UX-17): 續報's window shows it as the radio's hover (the bare MEANING), 轉班's as the note.
 */
const CONVERT_TO_WAITLIST_MEANING = '家長想等其他班級或時段的名額';
const CONVERT_TO_WAITLIST_CHOICE_NOTE = ` (${CONVERT_TO_WAITLIST_MEANING})`;

/**
 * PO 2026-10-03: what 延長保留 14 天 does, beside 轉為候補 (which holds no seat) — the row
 * button's hover (HUB.TRANSFER.EXTEND_HOLD_TITLE) and its ⋮ note (the late payer's ⋮ lists it).
 */
/** 確定轉入's 「都不是本次轉班的」 chip (UI_STRINGS.HUB.TRANSFER.LSED_NONE_LABEL／_DETAIL). */
const TRANSFER_LSED_NONE_LABEL = '都不是本次轉班的';
const EXTEND_HOLD_MEANING = `同一個新班位子再保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天，等付款、約課`;

/**
 * 候補 已安排 / 直接已安排 confirms' question (deep review W13, 2026-09-30): 「{name} 已在
 * {class} 的 Excel 名單中？」 names the class a 保留中 row holds; 直接已安排 (no hold, no class)
 * and a class that cannot be read ask the plain 「{name} 已在 Excel 名單中？」.
 */
function waitlistExcelQuestion(name: string, classLabel = ''): string {
  return classLabel
    ? `${name} 已在 ${classLabel} 的 Excel 名單中？`
    : `${name} 已在 Excel 名單中？`;
}

/**
 * 2026-10-13 (續報・轉班 完成規則 W0.3, C-04) — what the two buttons do with the bookings after
 * the 最後約課日, ONE sentence for every hover that explains it: the 「期間已約 N 堂」 marker
 * (UI_STRINGS.RENEWAL_AWAITING.AWAITING_TABLE.MARKER_PARTIAL_TITLE) and the hover of 待確認's
 * 續報 status chip (UI_STRINGS.HUB … HINTS.RENEWAL_STATUS_HOVER, W10.4 F8 — was the 「可能是續報」
 * fact). Replaces D3 2026-10-04's sentence (only the date
 * moves; 續報 completes at an upload with ≥ 8) — no longer true once 結算續報 exists (D-01).
 * `n` = the count the row shows (待確認 passes its `lsedReviewPastCount` since W2.4); a caller
 * without a count gets 「這幾堂」.
 */
function settleOrReanchorHint(n?: number): string {
  const which = n === undefined ? '這幾堂' : `這 ${n} 堂`;
  return `結算續報 把${which}算成一期；修改最後約課日 只改日期`;
}

/**
 * 2026-10-14 (續報・轉班 完成規則 W8.3, D-01b) — the 排課時段表 date button's name on a state
 * whose button never depends on the dates (HUB … HINTS.STATUS_FACTS: 不續報, 已轉出, the end
 * tags, 暫時保留名額, 請長假, the 轉班 hold — renewalRowStateOf reads the dates only for 進行中 /
 * 自動續報 / 已通知, so the context below is never read). The rule the button itself reads
 * (FrontendConfigUtils.reservationActionKindOf → RESERVATION_ACTION_LABEL_BY_KIND), so a fact
 * names the button the row shows.
 */
function reservationButtonOf(state: ReservationEnrollmentState): string {
  const kind = FrontendConfigUtils.reservationActionKindOf(state, {
    lsedMs: null,
    lockMs: null,
    nowMs: 0,
  });
  return RESERVATION_ACTION_LABEL_BY_KIND[kind].label;
}

/**
 * W10.5 X3 (續報・轉班 完成規則; review BL-05, 2026-10-15): the 轉班 待約課 hold's 期間已約 N 堂
 * is written for ANY N now (確定轉入 / 延長保留 count the family's 學期 bookings in the new class
 * at once), so N can reach AUTO_TAG_THRESHOLD before the next upload completes the transfer by
 * itself. Then the sub-threshold sentence (「未達自動完成轉班門檻 … 可能為補課」) is false — this
 * one says what happens. `where` = the place to act, '' when the hover sits on that row already.
 * DERIVED wording (PO eye-check on the next PO page). W10.6 Y7 (review § E, 2026-10-15): one
 * name for the threshold on both sides — 「已達 8 堂」 (the release warning below says the same).
 */
const TRANSFER_PARTIAL_AT_THRESHOLD_TOOLTIP = (n: number, where: string): string =>
  `新班已有 ${n} 堂學期約課，已達 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂：下次上傳課表時會自動完成轉班；也可以現在${where ? '' : ' '}${where}結算轉班。`;

/** 「名稱 · 學生」's separator — UI_STRINGS.PARENT_MESSAGE.TITLE and its inverse TITLE_PARTS. */
const PARENT_MESSAGE_TITLE_SEP = ' · ';
/** 再次提醒家長's window name — REMINDER_WINDOW.TITLE_PLAIN and TITLE (W9, D62). */
const RENEWAL_REMINDER_WINDOW_NAME = '再次提醒家長';

/**
 * 約課切斷確認 D80 (annex § 一): 「移出目前狀態：X」 — UI_STRINGS.HUB.STATE_LEFT, also the head of
 * STATE_LEFT_TRANSFER_HOLD (one home for the words).
 */
const stateLeftLine = (name: string): string => `移出目前狀態：${name}`;
/**
 * 約課切斷確認 D80 (annex § 二.3): a date window's success toast opens with the button's name,
 * done — 「已修改最後約課日」, 「已恢復保留名額」, 「已補正最後約課日」, 「已結算續報」.
 */
const doneToastLead = (action: string): string => `已${action}`;
/**
 * 處理待確認's window title — UI_STRINGS.HUB.REVIEW.HANDLE.TITLE, and the head of its TITLE_NAMED
 * (R4.5, D53 m: one home for the words).
 */
const REVIEW_HANDLE_TITLE = '處理待確認';

/**
 * 結算轉班's 適用情境 box (TransferMarkCompleteForm), its four bullets in order — one home for
 * TRANSFER_REDESIGN.SUB_THRESHOLD.MARK_COMPLETE_* (the bullets) and HUB.TRANSFER.MARK_COMPLETE_TIP
 * (the window title's ⓘ, tasks/todo-renewal-pending-chart.md R1.2 / R-P15). Moved here verbatim
 * (THRESHOLD = 2026-06-26, TERM = fix round 1 M6, EFFECT = W9.5 UX-08; LSED was inline in the
 * window's template).
 */
const TRANSFER_MARK_COMPLETE_CASES = {
  THRESHOLD: 'BF 已收到實際預約，但未達自動完成轉班門檻 (≥8 堂)，未觸發自動結算',
  TERM: '家長確認這幾堂是這一期的學期 (可能是補課時，請改用 其他處理：取消轉班、轉為候補 或 修改新班時段)',
  LSED: '需要手動指定一個日期作為「最後約課日」，紀錄移至 已完成',
  EFFECT: '原班不會變動；新班改為一般保留名額，轉班結束',
} as const;

/**
 * 修改新班時段's 適用情境 box (TransferChangeTargetForm), each line once — one home for
 * HUB.TRANSFER.CHANGE_TARGET_* (the bullets) and HUB.TRANSFER.CHANGE_TARGET_TIP (the window
 * title's ⓘ, R1.2 / R-P15). Moved here verbatim: EXPIRED_CASE / RULE_EXPIRED = PO 2026-10-05
 * (as-built Q8, from 未完成); RULE_SPECIAL_HOLD = 2026-10-04 (B4 / B5 — the current new class stays
 * on 暫時保留名額); WRONG_SLOT_CASE / NO_TERM_CASE were inline in the window's template.
 */
const TRANSFER_CHANGE_TARGET_CASES = {
  EXPIRED_CASE: '轉班未完成 (保留已到期，或新班時段已下架)',
  WRONG_SLOT_CASE: '確定轉入時選錯目標時段',
  NO_TERM_CASE: '新班時段還沒有這一期的學期約課 (只約了幾堂、可能是補課，也可以改)',
  RULE: `修改後，原目標時段保留會解除，新目標時段重新計算 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天保留期`,
  RULE_SPECIAL_HOLD: `修改後，新目標時段重新計算 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天保留期`,
  RULE_EXPIRED: `修改後，新目標時段重新保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天，轉班回到 待約課`,
} as const;

export const UI_STRINGS = {
  /**
   * 老師簽到 — 主管代簽 (PO 2026-10-14, tasks/todo-teacher-signoff.md D1/D2/D4/D6). The three
   * refusals are MIRRORED from functions UI_STRINGS.TEACHER_SIGNOFF (check-config-mirror):
   * the window locks a control with the same sentence the server would answer with.
   * View rules: utils/teacherSignoffView.ts.
   */
  TEACHER_SIGNOFF: {
    SUBSTITUTE_ONLY: '這堂課只有代課老師，不需老師核對。',
    PROXY_TEACHER_SIGNED: '老師已自行核對，主管不能代為修改。',
    PROXY_COOP_IN_BATCH: '協同課請在課程視窗代簽。',
    /** A 代課／臨時老師 row of a 協同 class — nobody signs it. */
    SUBSTITUTE_ROW: '代課不需核對。',
    REASON_LABEL: '代簽原因',
    REASON_PLACEHOLDER: '例：老師尚無帳號',
    SIGNER_LABEL: '主管簽名',
    SIGNER_PLACEHOLDER: '輸入您的全名',
    /** 批次快速核對: the selection holds classes of other teachers; one reason covers them all. */
    BATCH_PROXY_NOTE: (n: number) => `其中 ${n} 堂由主管代簽，請填寫一次代簽原因。`,
    /** 批次快速核對 partly refused: the title, then one 「09:00 班名：原因」 per class. */
    BATCH_FAILED_TITLE: (n: number) => `部分核對失敗 (${n} 堂)`,
    /** Status line under a class or 協同 half signed by a 主管代簽 (after 「已於 」). */
    SIGNED_BY_PROXY: (time: string, by: string, reason: string) =>
      `${time} 由 ${by} ${FRONTEND_BUSINESS_RULES.APPROVAL.PROXY_SIGN.LABEL}（原因：${reason}）`,
    /** 核對歷史紀錄: the signer column and the reason line of a 主管代簽 entry. */
    HISTORY_SIGNER: (by: string) => `${by} (${FRONTEND_BUSINESS_RULES.APPROVAL.PROXY_SIGN.LABEL})`,
    HISTORY_REASON: (reason: string) => `代簽原因: ${reason}`,
    /** 已核對課程 tag tooltip, one line per signed teacher. */
    TAG_LINE: (teacher: string, by: string, reason: string) =>
      `${teacher}：${by} 代簽｜原因：${reason}`,
  },
  /**
   * 簽到機 續報提醒 (PO 2026-10-02; mockup docs/tools/kiosk-renewal-alert.html v4, every
   * string approved there). The bar above 確認簽到 is Chinese only; the card behind it
   * reads 中文 by default with an EN toggle. `md` = 「10/18」 (Taipei), `n` = Taipei
   * calendar days left, `slot` = 「週六 09:00 小海豚班」 / 「Sat 09:00 小海豚班」.
   * Who sees which variant: utils/kioskRenewalAlert.ts.
   */
  KIOSK_RENEWAL_ALERT: {
    BAR: {
      /** Not notified yet, or the deadline has passed without renewing. */
      CONTACT: '續報提醒',
      DEADLINE: (md: string) => `續報提醒：${md} 前完成`,
      /** Appended in the red (≤ 3 days) variant. */
      URGENT_SUFFIX: (n: number) => (n === 0 ? ' (今天)' : ` (剩 ${n} 天)`),
    },
    /** EN weekday names, Sunday first (the index of a timeslotId's weekday). */
    EN_WEEKDAYS: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const,
    zh: {
      TITLE: '續報提醒',
      TERM_END: (name: string, slot: string, md: string) =>
        `${name} 的「${slot}」本學期預計於 ${md} 結束。`,
      CONTACT_LINE: '下學期續報已可辦理，請聯絡櫃檯課務，或回覆課務的 LINE 訊息。',
      DAYS_LEFT: (n: number) => (n === 0 ? '今天' : `剩 ${n} 天`),
      DEADLINE_LINE_BEFORE: '課務已通知您下學期續報，期限為 ',
      DEADLINE_LINE_AFTER: '，請於期限前完成續報繳費。',
      URGENT_LINE_BEFORE: '下學期續報期限為 ',
      URGENT_LINE_AFTER: '，請儘速完成續報繳費。',
      WARN_CONTACT: '⚠ 續報期限過後，本時段的保留名額將釋出。',
      WARN_DEADLINE: '⚠ 期限過後，本時段的保留名額將釋出。',
      SMALL_CONTACT: '若已與課務聯絡，請忽略此提醒。',
      SMALL_DEADLINE: '若已回覆課務，請忽略此提醒；有任何問題請洽櫃檯。',
      CLOSE: '關閉',
    },
    en: {
      TITLE: 'Renewal Reminder',
      TERM_END: (name: string, slot: string, md: string) =>
        `${name}'s class "${slot}" is scheduled to end on ${md}.`,
      CONTACT_LINE:
        'Renewal for next term is now open. Please contact the front desk, or reply to our staff on LINE.',
      DAYS_LEFT: (n: number) => (n === 0 ? 'today' : n === 1 ? '1 day left' : `${n} days left`),
      DEADLINE_LINE_BEFORE:
        "Our staff have contacted you about next term's renewal. The deadline is ",
      DEADLINE_LINE_AFTER: '. Please complete renewal and payment by then.',
      URGENT_LINE_BEFORE: 'The renewal deadline is ',
      URGENT_LINE_AFTER: '. Please complete renewal and payment as soon as possible.',
      WARN_CONTACT:
        '⚠ After the renewal deadline, the reserved seat in this time slot will be released.',
      WARN_DEADLINE: '⚠ After the deadline, the reserved seat in this time slot will be released.',
      SMALL_CONTACT:
        'If you have already been in touch with our staff, please ignore this reminder.',
      SMALL_DEADLINE:
        'If you have already replied to our staff, please ignore this reminder. Questions? Ask at the front desk.',
      CLOSE: 'Close',
    },
    /** The language toggle — each option shown in its own language. */
    LANG_ZH: '中文',
    LANG_EN: 'EN',
  },
  /**
   * 登入頁：在 LINE／Facebook／Messenger／Instagram／WeChat 內建瀏覽器開啟時 (2026-09-22).
   * Google 不允許在這些 App 內建的瀏覽器登入（disallowed_useragent），程式端無法繞過，
   * 只能請使用者改用 Safari／Chrome。偵測邏輯在 `utils/inAppBrowser.ts`。
   */
  IN_APP_BROWSER_LOGIN: {
    APP_NAMES: {
      line: 'LINE',
      messenger: 'Messenger',
      facebook: 'Facebook',
      instagram: 'Instagram',
      tiktok: 'TikTok',
      wechat: 'WeChat',
    },
    TITLE: '請改用瀏覽器開啟',
    OPEN_IN_CHROME: '用 Chrome 開啟',
    OPEN_IN_SAFARI: '用 Safari 開啟',
    IF_NOTHING_HAPPENS: '若按了沒有反應，請依下方步驟操作：',
    BODY: (app: string) =>
      `${app} 內建的瀏覽器無法使用 Google 登入。請改用 Safari（iPhone）或 Chrome（Android）開啟本頁。`,
    STEP_LINE: '點選畫面右下角的「⋮」，再選「用預設瀏覽器開啟」。',
    STEP_OTHER: '點選畫面上的「⋯」或「⋮」選單，再選「在瀏覽器中開啟」。',
    URL_LABEL: '或複製網址，貼到 Safari／Chrome：',
    COPY_BUTTON: '複製網址',
    COPIED: '已複製，請貼到 Safari 或 Chrome 的網址列。',
    COPY_FAILED: '無法自動複製，請長按上方網址複製。',
  },
  /**
   * 招生訊息範本後台編輯 (PO 2026-09-18).
   *
   * ⚠️ 文案紀律：這一頁上有**兩個** ✏️。一個改的是「這次要發的這一則」，另一個改的
   * 是「之後每一位家長都會收到的預設」。兩者的措辭必須讓人一眼分辨，不能只靠位置或
   * 圖示區分——真正的風險不是技術面，是有人在週五下午以為自己只是微調一則訊息。
   * 全域那一個一律講「預設」「所有人」，單次那一個一律講「這一則」。
   */
  SIGNUP_TEMPLATE_EDITOR: {
    /**
     * 兩顆都用 ✏️ 的話，第一眼分辨的依據就消失了——忙的時候人先看圖示。全域這顆改用 ⚙️。
     * 標籤**不帶**「（所有人）」（PO 2026-09-21）：這顆只有主管看得到，括號讀起來像
     * 「每個人都能按」。範圍改由旁邊的 OWNER_NOTE 說明。
     */
    OPEN_BUTTON: '⚙️ 編輯預設文案',
    CLOSE_BUTTON: '收起預設文案編輯',
    OWNER_NOTE: '這裡改的是所有人的預設，不是上面那一則。',
    PANEL_TITLE: '編輯預設文案',
    PANEL_HINT: '改完存檔後，之後每一位課務發出的這一則都會用新的文字。',
    CUSTOMIZED_BADGE: '已自訂',
    SYSTEM_DEFAULT_BADGE: '系統預設',
    TOKENS_LABEL: '可用的代入欄位 (點擊插入)',
    TOKEN_REQUIRED_HINT: '必要，刪掉就不能存檔',
    CHARS_LEFT: (left: number) => `還可以輸入 ${left} 字`,
    CHARS_OVER: (over: number) => `超過 ${over} 字，LINE 單則訊息上限 5000 字`,
    SAVE: '儲存為預設',
    SAVING: '儲存中…',
    /**
     * ⚠️ 不可逆的那一個動作才需要攔一下。原本只有「還原」有確認、「儲存為預設」
     * 沒有——但還原是**救得回來的**（歷史版本＋系統預設都在），儲存卻是立刻對
     * 所有家長生效。確認擋的方向原本是反的。
     */
    SAVE_CONFIRM: (step: string) =>
      `把「${step}」的預設文案換成你剛才的版本？之後每一位課務發出的這一則都會用新文字。原本的版本會留在歷史版本。`,
    /**
     * Every confirm bubble's main button says 「確認」 — the question already says what is
     * being confirmed (PO standing rule; these were 確定換成新的預設 / 還原 / 放棄修改 /
     * 把新版帶入編輯區). The trigger buttons keep their own names.
     */
    CONFIRM_OK: '確認',
    CANCEL: '取消',
    DISCARD_CONFIRM: '編輯區的內容還沒儲存，關掉就會消失。要放棄這次的修改嗎？',
    /** 切換版本／步驟會重載編輯區——未存的草稿會不見，先問過。 */
    LEAVE_DRAFT_CONFIRM: '預設文案編輯區有還沒儲存的修改，切換後會消失。確定要離開嗎？',
    /** editOnly 步驟：這裡只改預設文案，真正要發的地方在別處。 */
    EDIT_ONLY_NOTICE:
      '這一則不從這裡發送——內容由系統依規則帶入 (日期、付款連結、金額)，在這裡手打會繞過那些檢查。這一頁只負責修改預設文案。實際發送請到：',
    RESET: '還原成系統預設',
    RESET_CONFIRM: (step: string) =>
      `還原後，「${step}」的預設文案會回到系統內建版本，之後每一位課務發出的這一則都會用內建文字。你目前的版本會留在歷史版本，仍然可以取回。`,
    /** 全頁一致用「取消」；同一個面板裡出現兩種說法會讓人多想一秒。 */
    RESET_CANCEL: '取消',
    SAVED: '已存為預設文案，所有人立即生效',
    RESTORED: '已還原成系統預設',
    /**
     * 稀疏覆蓋的代價：改過的步驟不會再收到程式碼端的改良，除非這裡講出來。
     * 不寫「你」——自訂的人可能是另一位主管。而且要給下一步，不是只陳述事實。
     */
    SEED_CHANGED: '系統內建文案已更新 (目前用的是自訂版)。可以先看看新版，需要的話再改用。',
    SEED_SHOW: '看看新版',
    SEED_HIDE: '收起',
    /** 這兩顆只是把文字**帶進編輯區**，不會儲存——名字必須說清楚，否則會被當成已套用。 */
    SEED_ADOPT: '把新版帶入編輯區',
    ADOPT_CONFIRM: '編輯區目前的內容會被系統新版取代 (尚未儲存)。要帶入嗎？',
    HISTORY_TITLE: (n: number) => `歷史版本 (${n})`,
    HISTORY_RESTORE: '帶入編輯區',
    HISTORY_NOTE: '帶入後仍要按「儲存為預設」才會生效。',
    HISTORY_EMPTY: '這一則還沒有被改過。',
    EMPTY_BODY: '文案不可以是空的。',
    UNKNOWN_TOKENS: (labels: string[]) =>
      `系統不認得這些代入欄位：${labels.map((l) => `{{${l}}}`).join('、')}。請從上方的欄位清單挑選。`,
    /** 必要欄位缺漏跟「不認得」是兩回事——系統認得它，而且非留不可。 */
    MISSING_REQUIRED: (labels: string[]) =>
      `這一則必須保留：${labels.map((l) => `{{${l}}}`).join('、')}。少了它，家長收到的訊息會沒有這段內容。`,
    /** 少打一個大括號不會命中任何樣式，會原樣寄給家長。 */
    UNPAIRED_BRACES: '文案裡有沒配對的大括號。代入欄位要長成 {{孩子名}} 這樣，前後各兩個。',
    /** 快照還沒到就開編輯器，會把別人的自訂當成「系統預設」蓋掉。 */
    LOADING: '正在載入目前的預設文案…',
    EXTERNAL_CHANGE: '這一則的預設文案剛剛被其他人更新了。你的編輯還在，存檔前請先確認。',
  },
  /**
   * 家長訊息視窗 統一 (PO 2026-09-25, docs/tools/parent-message-window-mockup.html v3) —
   * the words of the shared window/panel (components/parentMessage/). Every
   * "copy a message to the parent" place reads these, so the same button never has
   * two names. Rules: the far-right button finishes the job and is the only blue
   * one; 複製訊息 only copies (never records); a mark always asks 確認 first; staff
   * edits are never saved (only supervisors change defaults, in 招生客服說明).
   */
  PARENT_MESSAGE: {
    /** 「名稱 · 學生」 — one separator everywhere (was 「—」 in half the windows). */
    TITLE: (name: string, who: string) => `${name}${PARENT_MESSAGE_TITLE_SEP}${who}`,
    /**
     * TITLE's inverse (W9, D62): 「約課通知 · 林雅婷 - 林沐真」 → { kind, who } — cut at the FIRST
     * separator (no kind in TITLES holds one; a name may). null = not a TITLE (no separator, or
     * a side empty). NotificationTemplateModal reads it: with 名額使用情況 shown, the title is
     * the kind and the name goes into the block.
     */
    TITLE_PARTS: (title: string): { kind: string; who: string } | null => {
      const at = title.indexOf(PARENT_MESSAGE_TITLE_SEP);
      if (at <= 0) return null;
      const who = title.slice(at + PARENT_MESSAGE_TITLE_SEP.length);
      return who ? { kind: title.slice(0, at), who } : null;
    },
    TITLES: {
      RENEWAL_NOTICE: '續報通知',
      EXTEND: '延長並通知續報',
      /**
       * ⑪ 約課通知 — ONE name wherever it opens (約課通知待發 → 發約課通知, 設定最後約課日 →
       * 發送通知, 確定轉入, 通知家長 (補送/重送)). It was 約課通知 / 新一期約課成功通知 /
       * 約課成功通知 / 學期日期更新通知 by entry point (combined test 2026-09-27, PO 「fix
       * all」); the ① / ② messages were merged into this one on 2026-07-18. The logged type
       * (學期日期更新通知 for a date correction) still records why it was sent.
       */
      BOOKING: '約課通知',
      BOOKING_RESEND: '約課通知 (補送/重送)',
      WAITLIST_OFFER: '推薦訊息',
    },
    RESET: '還原預設文案',
    COPY: '複製訊息',
    /** Shown ON the 複製訊息 button for COPIED_FEEDBACK_MS (fixed width — nothing moves). */
    COPIED: '✓ 已複製',
    /** Screen-reader announcement of the same moment (not shown on screen). */
    COPIED_SR: '已複製到剪貼簿',
    COPY_FAILED: '複製失敗，請直接在上面的訊息框選取文字複製。',
    /** Staff typed in a window: shown under the box (the text is never saved). */
    EDITED: '已修改。修改不會儲存，下次開啟會回到預設文案。',
    /** 招生客服說明: typed text no longer follows the fields. */
    EDITED_FIELDS:
      '已修改：欄位的變更不會再套用到這段文字。如要套用，請按「還原預設文案」(目前的修改將會遺失)。',
    MARK_HINT: (label: string) => `複製後貼到 LINE 送出，再按「${label}」`,
    /** PO 2026-09-25: a confirm's buttons are only 取消／確認 — the question says the rest. */
    MARK_OK: '確認',
    MARK_CANCEL: '取消',
    /** Supervisor-only link; hidden on the 招生客服說明 page itself. */
    POINTER_PREFIX: '編輯預設文案：',
    POINTER_PATH: (title: string) => `招生客服說明 > ${title}`,
    /** Typed text is about to be replaced (招生客服說明 type buttons / 合併發送). */
    SWITCH_CONFIRM: '切換後，目前的修改將會遺失。確定要切換嗎？',
    COMBINE_CONFIRM: '勾選後，目前的修改將會遺失。確定要勾選嗎？',
    CHAR_COUNT: (n: number) => `共 ${n} 字`,
    UNFILLED: (n: number) => `・還有 ${n} 處沒填`,
    ALL_FILLED: '・都填妥了',
    /**
     * The 招生客服說明 prompts that stand in for a value staff still have to pick. ONE
     * definition: the composer writes them and `utils/unfilledPlaceholders.ts` counts
     * them. They were typed in four places and drifted — 報03 with no slot ticked read
     * 「都填妥了」 (combined test 2026-09-27). Half-width brackets (PO 2026-09-24).
     */
    UNFILLED_PROMPTS: {
      SLOTS: '(請於上方勾選時段)',
      TICKET: '(請先挑選票種)',
      FEEDBACK: '(請先挑選回饋)',
    },
    BOX_LABEL: '訊息內容',
    /** Half-width brackets everywhere (PO 2026-09-24). */
    BOX_LABEL_SAMPLE: '訊息內容 (範例)',
    /** 通知續報／延長／約課通知 — the one mark label for "the parent was notified". */
    NOTIFY_MARK: '送出後標記已通知',
    NOTIFY_CONFIRM: (kind: string, student: string) => `確定已把${kind}送給 ${student} 的家長？`,
    EXTEND_CONFIRM: (student: string, md: string) =>
      `確定已把續報通知送給 ${student} 的家長？標記後會重新保留至 ${md}。`,
    /** 延長 red banner tail: the other path, where the reason is. */
    EXTEND_PIVOT_PREFIX: '不延長？',
    EXTEND_PIVOT_LINK: '改為其他處理',
    /**
     * The backend refused a notify mark because the 保留期限 moved after the window
     * opened (review F-17): the window stays open with the rebuilt message.
     * Same wording as RENEWAL_AWAITING.REMINDER_WINDOW.DEADLINE_CHANGED.
     */
    DEADLINE_CHANGED: (md: string) =>
      `保留期限剛改為 ${md}，下方訊息已更新。若已把舊訊息傳給家長，請重新複製、再傳一次，然後再標記。`,
    /** No new lock in the refusal: the window stays open (as 再次提醒家長) and says so. */
    DEADLINE_CHANGED_FALLBACK: '保留期限已變更，請關閉視窗後重新開啟。',
    /** Toast after every successful mark (mockup rule 7). */
    MARKED: (student: string, what: string) => `已標記：${student} ${what}`,
    MARKED_NOTIFIED: '已通知',
    MARKED_FEEDBACK_DONE: '體驗回饋已完成',
  },
  /**
   * 額滿提醒 (PO 2026-09-29, answers page § 14 → A; plan tasks/todo-capacity-warning.md)
   * — every action that puts a student into a class warns with the SAME 「保留名額 N/M」
   * staff see on 排課時段表 (useSessionEnrollments/slotCapacity.ts), as soon as its window
   * opens; staff may continue after confirming, and only then the request carries
   * `acknowledgeOverCapacity: true` (composables/useSlotCapacityGuard.ts).
   */
  SLOT_CAPACITY: {
    /**
     * `details.reason` of the backend's full-slot refusal (failed-precondition,
     * details { reason, reserved, max }). Mirror of the backend
     * UI_STRINGS.SLOT_CAPACITY.REFUSAL_REASON (functions/src/sessionEnrollments/
     * slotCapacity.ts); the concurrency refusal has no details, so it never matches.
     */
    REFUSAL_REASON: 'slot-full',
    /** The red line in the window, shown as soon as it opens (before any copy). */
    WARNING: (reserved: number, max: number, next: string) =>
      `保留名額已滿 (${reserved}/${max})：名額可能已由候補家庭補上。請先確認，${next}。`,
    /** `next` of WARNING — the step that follows the check, per action. */
    NEXT: {
      /** 通知續報 / 延長並通知續報 / 確定轉入 (their windows hold the parent message). */
      MESSAGE: '再傳訊息給家長',
      /** The 保留名額 window (修改最後約課日 / 恢復保留名額 / 確認保留名額): the line shows only when
       *  the student holds no seat here yet (slotCapacityOf `selfCounted`), so it names the effect. */
      SET_LSED: '再保留名額',
      SPECIAL_HOLD: '再設定暫時保留名額',
      CHANGE_TARGET: '再修改新班時段',
      /** 取消轉班 on 待約課 — the source seat comes back (deep review C1). */
      CANCEL_TRANSFER: '再取消轉班',
      /** 候補 保留名額 on a 正課 class (PO 2026-09-30). */
      WAITLIST_HOLD: '再保留名額',
    },
    /**
     * A send-then-mark window after the server refused slot-full (deep review C7): the
     * parent may already have the message, so the red line's next step is the mark itself.
     * `step` = AFTER_SEND_STEP.* (the window's own action).
     */
    REFUSED_AFTER_SEND: (reserved: number, max: number, step: string) =>
      `${step}未完成：保留名額已滿 (${reserved}/${max})。訊息若已傳出，按「確認」即${step}`,
    AFTER_SEND_STEP: {
      /** 通知續報 / 延長並通知續報 / 簽到表 🔔 (送出後標記…). */
      MARK: '標記',
      /** 確定轉入 (its form holds the parent message). */
      TRANSFER_IN: '確定轉入',
    },
    /**
     * 保留名額 N/M shown with N > M (deep review Q1, PO 2026-09-29): the title of the red
     * count on 排課時段表 and every other place that shows the same N/M.
     */
    OVER_TITLE: (reserved: number, max: number) => `超過班級人數上限 (${reserved}/${max})`,
    /**
     * 學生紀錄 › 學期紀錄 event for NOTIFICATION_LOG_TYPES.OVER_CAPACITY_CONFIRMED: the
     * count the confirm said it would become — the log's `reserved` is N before this
     * student (the refusal's number), so the chip shows (N + 1)/M.
     */
    OVER_CAPACITY_CHIP: (reserved: number, max: number) => `超收確認 (${reserved + 1}/${max})`,
    /** Added to the final confirm (its button stays 「確認」 — PO standing rule). */
    CONFIRM: (reserved: number, max: number) =>
      `此時段保留名額已滿 (${reserved}/${max})，送出後會變成 (${reserved + 1}/${max})。`,
    /**
     * After CONFIRM where there is no red line (the 延長保留 bubble is the whole window)
     * and in the dialog below.
     */
    WAITLIST_HINT: '名額可能已由候補家庭補上。',
    /**
     * The 「保留名額已滿」 dialog — an action with no confirm of its own (暫時保留名額),
     * and the ask-again after the server refused (暫時保留名額, 修改新班時段, 延長保留,
     * 取消轉班): title; CONFIRM + WAITLIST_HINT + this question; 確認 / 取消.
     */
    DIALOG_TITLE: '保留名額已滿',
    DIALOG_QUESTION: '仍要送出嗎？',
    DIALOG_CANCEL: '取消',
  },
  /**
   * 廠商銀行帳號變更防護 (2026-09-17).
   *
   * ⚠️ 文案紀律：這個功能**擋不住變更匯款帳號詐騙**。變更依據記錄的是「已經被
   * 騙的那個人」自己的說法 ——「廠商來函」通常是真的，那封偽造的信確實寄到了。
   * 所以任何一句話都**不得**寫成系統驗證過帳號（不要出現「已驗證」「確認無誤」
   * 這類字）。要講的是：留下證據，並在錢出去前多一雙眼睛。
   */
  PAYEE_BANK_CHANGE: {
    DIALOG_TITLE: '變更收款帳號',
    /** 說明這次改動會影響什麼 —— 筆數由呼叫端填入。 */
    IMPACT_NONE: '這筆變更不影響任何在途請款單。',
    /** 單筆請款單的「編輯銀行資料」—— 只動這一張，不要拿在途筆數嚇人。 */
    IMPACT_SINGLE_REQUEST: '此變更只影響這一張請款單。',
    IMPACT_SOME: (n: number) =>
      `儲存後，${n} 張在途請款單（含待審核／已核准）的收款帳號會一併改成新的帳號，` +
      // 標籤名稱必須與畫面上真的出現的那一個一致 —— 這裡講的是
      // REPAYMENT.CHIP_LABELS.bankChanged。原本寫「核准後變更」是畫面上不存在
      // 的第四種說法，而且對「待審核」的單講「核准後」本身就自相矛盾。
      `並在審核、付款與匯出畫面標示「銀行資料已變更」。`,
    REASON_LABEL: '變更依據',
    REASON_PLACEHOLDER: '請選擇',
    REASON_REQUIRED: '請選擇變更依據',
    NOTE_LABEL: '備註',
    NOTE_PLACEHOLDER: '選填，例如來函日期、回撥的電話號碼、對方窗口',
    /** 選了「其他」時備註變必填 —— 伺服器也擋（utils/bankCodes.ts）。 */
    NOTE_REQUIRED_FOR_OTHER: '選擇「其他」時請填寫備註',
    /** 誠實定位，直接寫在對話框裡給出納／主管看。 */
    HONESTY:
      '系統不會、也無法驗證新帳號是否真的屬於這家廠商。這裡留下的是「誰改的、依據什麼」，' +
      '供日後查核與匯款前再確認。',
    CONFIRM: '確認變更',
    CANCEL: '取消',
    /** 廠商名冊列上的近期變更標記；天數由 BANK_CHANGE.RECENT_CHANGE_DAYS 決定。 */
    RECENT_BADGE: (days: number) => `${days} 日內變更帳號`,
    /** 名冊列上的新建標記 —— 針對「停用舊的＋同名新建」這條繞過路徑。 */
    RECENT_CREATED_BADGE: (days: number) => `${days} 日內新建`,
  },
  /**
   * 改名勸告（PO 2026-09-17，取代原本的統編勸告 —— 廠商通常不填統編，是個死訊號）。
   * 只勸告，永不阻擋：改名有正當理由（打錯字、法人更名），而系統分不出
   * 「同一家公司改名」與「換成另一家公司」。
   */
  PAYEE_RENAME_ADVICE: {
    TITLE: '確認要改名嗎？',
    /** 全名由一個值改成另一個值。 */
    FULL_NAME:
      '全名改掉之後，過去的請款單仍然顯示舊名稱（名稱是建單當下的快照，不會回頭改寫）。' +
      '如果這是「換一家公司」而不是「同一家公司改名」，請改為「停用舊廠商、另外新建一筆」，' +
      '帳務與憑證才不會混在同一個廠商底下。',
    /** 簡稱改掉，而且這個廠商已經有請款紀錄。 */
    SHORT_NAME:
      '這個廠商已經有請款紀錄。改簡稱不會改寫既有請款單上的名稱，日後對帳時' +
      '新舊簡稱會並存。若是換了另一家公司，請停用舊廠商、另外新建一筆。',
    CONFIRM: '仍要改名',
    CANCEL: '再想一下',
  },
  /**
   * 主管審核 › 待審核請款單 — the 送出日 column (PO 2026-10-27, todo-approval-overdue-reminder
   * D4). The day count and the 3-day rule are the server's (`approvalWaitDays` /
   * `approvalOverdue` on each queue row); the page only prints them.
   */
  APPROVAL_WAIT: {
    COLUMN_TITLE: '送出日',
    overdueTag: (days: number) => `已等 ${days} 天`,
  },
  /**
   * 發票日期・消費日期 需確認 (tasks/todo-invoice-date-check.md D1–D6; mockup
   * docs/tools/invoice-date-check-mockup.html). Orange, soft — the numbers are
   * FRONTEND_BUSINESS_RULES.PAYMENT.LINE_DATE_CHECK, the rule utils/lineDateCheck.ts.
   * `{label}` arguments are the on-screen date names (發票日期／收據日期／憑證日期／
   * 消費日期／費用日期); `noun` is the screen's paper word (發票／憑證).
   */
  LINE_DATE_CHECK: {
    /** 待付款 card tag, beside 缺通匯代碼. */
    TAG: '日期需確認',
    /** (a) — `months` null when the rounded number would not exceed the limit (F9). */
    olderThanBase: (base: 'today' | 'submitted', months: number | null) =>
      `比${base === 'today' ? '今天' : '送出日'}早${
        months === null
          ? `超過 ${FRONTEND_BUSINESS_RULES.PAYMENT.LINE_DATE_CHECK.MAX_AGE_MONTHS} 個月`
          : ` ${months} 個月`
      }`,
    /** (b) */
    AFTER_TODAY: '晚於今天',
    /** (c) — `otherDate` is printed only where the other date is not already on screen. */
    apart: (otherLabel: string, otherDate: string | null, months: number | null) =>
      `和${otherLabel}${otherDate ? ` ${otherDate} ` : ''}差${
        months === null
          ? `超過 ${FRONTEND_BUSINESS_RULES.PAYMENT.LINE_DATE_CHECK.MAX_APART_MONTHS} 個月`
          : ` ${months} 個月`
      }`,
    /** 出差旅費 (D6) — 憑證日期 more than OTHER_LINE_PRE_TRIP_DAYS before the trip start. */
    beforeTrip: (months: number) => `比出差起日早 ${months} 個月`,
    /** Lead of the compact one-line indicator (批次核准確認, 付款 second step, 批次付款). */
    LEAD: '日期需確認：',
    /** Lead of the listed indicator inside 「核准 — 請確認」. */
    approveLead: (noun: string) => `日期需確認，請對照${noun}再核准：`,
    /** 送出 window (D2, D5). The body leaves out the title and the count (D20, PO Q10 「B」). */
    DIALOG_TITLE: '日期需確認',
    dialogBody: (noun: string) => `請對照${noun}，日期正確再送出。`,
    DIALOG_BACK: '返回修改',
    DIALOG_CONFIRM: '確認',
    /** 編輯／更正 form: its on-screen name for 消費日期 (F12) — (c) reads 「和費用日期差 N 個月」. */
    EDIT_FORM_EXPENSE_LABEL: '費用日期',
    /** Every other screen's name for 消費日期 — the field's own name, and (c)'s default other date. */
    EXPENSE_LABEL: '消費日期',
    /** A line with no 項目說明 yet, as a sentence names it: 「#1 未命名項目」. */
    UNNAMED_LINE: '未命名項目',
  },
  /**
   * The 消費日期 box where 出納／主管 type a 發票日期 after 送出 (tasks/todo-invoice-date-check.md
   * D22–D24, Wave 7): 核准 and 付款 in the request window, 發票更正, 填寫發票並驗證. The rules
   * are utils/expenseDateBox.ts; the trip window utils/travelExpenseWindow.ts.
   */
  EXPENSE_DATE_BOX: {
    /** D23 — under the locked box on a delivered month (anyone but the 主管). */
    DELIVERED_LOCKED: '此月已交付，消費日期請主管修改',
    /** W7-F7 — under a 出差旅費 line's box typed outside the trip (red). */
    outsideTrip: (from: string, to: string) => `不在出差期間 (${from}–${to})`,
    /** W7-F7 — 「其他」 may start before the trip (TRAVEL.OTHER_LINE_PRE_TRIP_DAYS). */
    outsideClaimable: (from: string, to: string) => `不在可列報期間 (${from}–${to})`,
    /** The toast when the save is stopped on such a box: 「交通費「機票」的消費日期不在出差期間 (…)」. */
    tripToast: (line: string, reason: string) => `${line}的消費日期${reason}`,
    /** 核准／付款 confirm window (W7-F10) — it lists 消費日期 too, so it no longer says 發票. */
    CONFIRM_TITLE: '資料變更確認',
    CONFIRM_BODY: '以下資料將隨此操作一併儲存：',
    /** Its confirm button (CLAUDE.md § 7: 「確認」). The batch 發票資料變更確認 keep theirs. */
    CONFIRM_POSITIVE: '確認',
  },
  /**
   * 代墊 — a 出差單 whose 請款人 is not one of the 出差人 (`travelDetails.filedOnBehalf`,
   * server-computed). PO 2026-09-17: the 代墊 fact must be visible at EVERY decision
   * point — 主管核准時、出納付款前、付款後 — and in the LIST, not only after opening
   * the detail. Company SOP is that the approver and treasury personally verify the
   * trip with the actual traveller before approving/paying; that human check is the
   * compensating control for the supervisor-self-approve path (AUDIT_ACCEPTED_RISKS
   * Travel T3/T4), so the screens have to put the fact in front of them.
   *
   * One definition, every surface: 審核佇列 chip, 待付款 card title + flag chip,
   * 批次付款 card and confirm step, 已付款 list, detail card.
   */
  TRAVEL_ON_BEHALF: {
    /** Short chip, used wherever a row already carries chips. */
    CHIP: '代墊',
    /** Chip on a surface that is ABOUT to move money — names where it goes. */
    chipWithPayee: (payeeName: string) => `代墊 · 匯入 ${payeeName}`,
    /** 待付款 card title suffix: who travelled vs whose account receives it. */
    queueTitleSuffix: (payeeName: string) => `（代墊 · 匯入 ${payeeName}）`,
    /** 批次付款 / 付款對話框: the payee is on screen, so name the traveller instead. */
    payeeSuffix: (travellerNames: string) => `代墊（出差人 ${travellerNames}）`,
    /**
     * Shown to the approver / treasury. Deliberately an INSTRUCTION to verify with
     * a person, not a tick: the system cannot witness that conversation, and a
     * checkbox would only record a self-assertion (PO 2026-09-17).
     */
    verifyHint: (travellerNames: string) =>
      `此單為代墊，全部費用由請款人先行支付；核准／付款前請親自向出差人 ${travellerNames} 查證此單屬實。`,
    /** Shown to the FILER while the traveller list excludes them. */
    FILER_HINT:
      '出差人不含您本人 —— 此單將整張以「代墊」列報，款項匯入您的帳戶。核准者與出納會向出差人查證；報告單會載明代墊關係。',
  },
  /**
   * The shared student search (components/StudentSearchField.vue; PO 2026-09-29,
   * docs/tools/waitlist-student-link.html v2). One field everywhere a student is looked up:
   * 候補 新增 / 編輯 (「學生 / LINE 姓名」, with the LINE 姓名 option), 學生紀錄, 老師手機
   * 學生查詢, 簽到表 臨時新增學生. Results come from the 學生名冊 (STUDENT_DIRECTORY_CONFIG).
   */
  STUDENT_SEARCH: {
    PLACEHOLDER: '輸入學生姓名或電話搜尋',
    /** 候補 新增 / 編輯 — the one field (decision 3). */
    FIELD_LABEL: '學生 / LINE 姓名',
    WAITLIST_PLACEHOLDER: '搜尋姓名或電話，或輸入 LINE 姓名',
    LIST_LABEL: '符合的學生',
    /** A result's second line: the classes the student is in now (active / paused). */
    CURRENT: (labels: string) => `目前：${labels}`,
    NOT_ENROLLED: '目前沒有上課',
    /** The last option of the 候補 field (allowManual) — 新增 / 編輯. */
    MANUAL_OPTION_ADD: (text: string) => `不是既有學生 → 以 LINE 姓名「${text}」新增`,
    MANUAL_OPTION_EDIT: (text: string) => `不是既有學生 → 改用 LINE 姓名「${text}」`,
    /** 簽到表 臨時新增學生's last option: the only way to a 臨時名稱 (PO 2026-11-02). */
    MANUAL_OPTION_TEMP: (text: string) => `不是既有學生 → 以臨時名稱「${text}」新增`,
    /** The state line under the 候補 field. */
    MANUAL_STATE_ADD: (text: string) => `將以 LINE 姓名「${text}」新增 (未連結學生)`,
    MANUAL_STATE_EDIT: 'LINE 姓名 · 要連結既有學生，在這裡打姓名或電話搜尋',
    /** 編輯 a linked row after ✕: saving unlinks it (deep review C5). */
    UNLINK_STATE: (text: string) => `儲存後取消連結，改用 LINE 姓名「${text}」`,
    /** 編輯 an unlinked row whose typed name differs from the stored one (C5). */
    RENAME_STATE: (text: string, stored: string) =>
      `儲存後 LINE 姓名改為「${text}」(原為「${stored}」)`,
    /**
     * 候補: the typed text looks like a phone number (C6) — a warning, never a block (PO:
     * never guess who it is).
     */
    PHONE_WARNING_ADD: '這看起來是電話號碼，請從清單選擇學生；若 LINE 姓名 就是數字，可直接新增',
    PHONE_WARNING_EDIT: '這看起來是電話號碼，請從清單選擇學生；若 LINE 姓名 就是數字，可直接儲存',
    /** 簽到表 臨時新增學生: text typed, nothing chosen from the list (PO 2026-11-02). */
    TEMP_NOT_CHOSEN: '還沒選擇學生',
    /** 簽到表 臨時新增學生: the chip of a chosen 臨時名稱. */
    TEMP_CHIP_LABEL: '臨時名稱',
    LINKED_STATE: '已連結既有學生',
    /** The chip's ✕ (its accessible name). */
    UNPICK: (name: string) => `取消選擇 ${name}`,
    NO_MATCH: '找不到符合的學生',
    MORE: (shown: number, total: number) =>
      `顯示前 ${shown} 筆，共 ${total} 筆，請輸入更完整的姓名或電話`,
    LOADING: '載入學生名冊…',
    /** The directory doc does not exist yet (not built / an older server). */
    MISSING: '學生名冊尚未建立',
    LOAD_FAILED: '學生名冊載入失敗',
    REFRESH: '重新整理名冊',
    REFRESH_DONE: (count: number) => `學生名冊已更新 (${count} 位學生)`,
    /** The server skipped the rebuild — built < 60 s ago (C12); the list was re-read. */
    REFRESH_RECENT: (count: number) => `名冊剛更新過 (${count} 位學生)`,
    /** The rebuild ran but the re-read failed: the field still holds the earlier list. */
    REFRESH_READ_FAILED: '名冊已重建，但重新讀取失敗，目前顯示的仍是先前的名冊，請稍後再試',
  },
  /**
   * D-11 (PO 2026-10-02): the 課務 / 主管 ⟳ in the time-slot pickers (選擇時段 drawer, the
   * 轉班 班級系列 picker) — rebuilds the stored timeslot list now (useTimeSlotData
   * rebuildTimeslotDirectory) instead of waiting for the next upload / 03:10.
   */
  TIMESLOT_LIST: {
    /** The button's tooltip / accessible name, and the toast title. */
    REFRESH: '重新整理時段清單',
    REFRESH_DONE: (count: number) => `時段清單已更新 (${count} 個時段)`,
  },
  /**
   * 保留名額未約課名單 + 保留徽章 (PO 2026-09-17, decisions D1 / D19 / D21).
   *
   * The list sits collapsed under the attending students and holds the people
   * who hold a seat in this timeslot but have no booking for this class, which
   * is normally 請假 (BF is the source of truth, so they vanish from the roster
   * entirely today). The badge marks the same fact on a row that IS present.
   *
   * Group labels are NOT defined here: they reuse
   * `RENEWAL_WORKFLOW_CONFIG.TOOLTIPS.HEADER_CARD.GROUP_PILL_*`, already on
   * 排課時段表 since 2026-06-18, via `useSessionEnrollments/reservedGroup`.
   *
   * Wording rules applied: 書面語, no 破折號, and the term 「最後約課日」 is never
   * shortened to an acronym in anything a user reads.
   */
  RESERVED_UNBOOKED: {
    /** Collapsed header; `n` is how many held seats have no booking this class. */
    SECTION_TITLE: (n: number) => `保留名額未約課 ${n} 人`,
    /** Shown once expanded, above the rows. */
    SECTION_HINT: '這些學生在本時段有保留名額，但本堂沒有約課。',
    /** The list renders nothing at all when empty; kept for the expanded case. */
    EMPTY: '本堂沒有未約課的保留名額。',
    /** Action on a row, admin and supervisor only, day of the class only. */
    ADD_TO_CLASS: '加入本堂',
    /** Badge meaning, on hover or tap. */
    BADGE_TOOLTIP: '此學生在本時段持有保留名額',
    /** 臨時新增學生 name field: search the directory; a non-member is the list's last option. */
    PICKER_PLACEHOLDER: '搜尋學生姓名或電話',
    PICKER_NO_MATCH: '查無符合的學生',
    /** The picked student already has a row in this class — refuse up front. */
    ALREADY_ON_ROSTER_TITLE: '這位學生已經在名單上',
    ALREADY_ON_ROSTER_BODY: (name: string) =>
      `${name} 已經在本堂課的名單上，不需要再新增一次。若要修改，請直接在名單上操作。`,
    /** Row affordances that mirror the roster row's own links. */
    OPEN_RECORD: '點入查學生紀錄',
    TICKET_LINK: '票券',
    /** 首堂課 was ticked for a child who already has one. A warning, not a block. */
    FIRST_CLASS_DUPLICATE_TITLE: '這位學生已經有首堂課紀錄',
    FIRST_CLASS_DUPLICATE_BODY: (name: string, count: number) =>
      `${name} 另外有 ${count} 筆標記為首堂課的紀錄。這堂課仍然已新增並標記為首堂課；` +
      `如果不是首堂課，請在名單上取消標記，避免重複建立體驗回饋表單。`,
    /**
     * The three 臨時新增 kinds (FRONTEND_BUSINESS_RULES.TICKET_TYPES.MANUAL_ADD_CHOICES,
     * PO 2026-10-11): 首堂體驗 = trial ticket + 首堂課 in one click; 體驗（已體驗過） =
     * a trial ticket without it (a child who trialled before — 「已體驗過」 pricing exists).
     */
    MANUAL_ADD_CHOICE_LABELS: {
      regular: '一般學生',
      firstTrial: '首堂體驗',
      repeatTrial: '體驗（已體驗過）',
    } as Record<string, string>,
    /**
     * 確認新增學生, when nothing was picked from the 名冊 (PO 2026-10-11, red): the note
     * stays on that day only, and anyone who ever booked — any ticket — is in the 名冊.
     */
    TEMP_NAME_WARNING_LINES: [
      '未選擇名冊學生，將以臨時名稱新增，記錄只留在當天。',
      '曾約過課的學生（學期、任選、體驗）都在名冊，請先搜尋並選擇。',
    ],
    /** 新增 is disabled until a kind is chosen; this is the belt-and-braces message. */
    CHOICE_REQUIRED_TITLE: '請先選擇 一般學生、首堂體驗 或 體驗（已體驗過）',
    /** 加入本堂 fills the 臨時新增學生 field further down; say so. */
    CONVERT_PREFILLED_TITLE: '已帶入臨時新增學生',
    CONVERT_PREFILLED_BODY: (name: string) => `請選擇票種後按新增，即可將 ${name} 加入本堂。`,
  },
  /**
   * 救生員模式 ↔ 老師模式 switch on the teacher mobile pages, for accounts that
   * hold both 老師 and 救生員 (PO 2026-09-16). The label names the mode you go TO.
   */
  ROSTER_MODE: {
    TO_LIFEGUARD: '救生員模式',
    TO_TEACHER: '老師模式',
  },
  /**
   * Student photo capture on the teacher / 救生員 phone roster (PO P4, 2026-09-16;
   * docs/LIFEGUARD_MOBILE_ACCESS_SPEC.md). Server refusals (time window, 不同意,
   * not on the roster) arrive as the backend's own message.
   */
  PHOTO_CAPTURE: {
    TAKE_PHOTO: '拍照',
    /** Under the enlarged photo when a new one may be taken (PO 2026-09-16:
     *  the photo + these two buttons are self-explanatory — no sentence). */
    REPLACE_POSITIVE: '拍新照片',
    CLOSE: '關閉',
    VIEW_PHOTO_LABEL: (name: string) => `查看 ${name} 的照片`,
    BUSY: '上一張照片仍在處理中，請稍候。',
    SUCCESS: '照片已更新',
    /** Resize / upload / download-URL failures (not a server refusal). */
    UPLOAD_FAILED: '照片上傳失敗，請再拍一次。',
    CAMERA_BADGE_LABEL: (name: string) => `為 ${name} 拍照`,
    /**
     * Shown only on the HEIC fallback path (an Android gallery pick the canvas
     * cannot decode). heic2any decodes on the main thread, so the phone stops
     * responding for a few seconds — the teacher needs to see a reason rather
     * than a frozen page.
     */
    CONVERTING: '正在轉換照片格式，請稍候…',
    /** Same wording as the backend refusal (functions utils/studentPhotoAccess.ts). */
    OUTSIDE_WINDOW: (minutes: number) =>
      `只能在上課前 ${minutes} 分鐘到下課後 ${minutes} 分鐘內拍照。`,
  },
  /**
   * The three 備註 fields, deliberately named so they cannot be confused.
   *
   * All three are free text and all three are exported to the CPA sheet, so a
   * note filed in the "wrong" one is still delivered — just under a different
   * column. The labels disambiguate by SCOPE (整張單 vs 單列) and by SUBJECT
   * (請款 vs 發票), because before this they were all literally 「備註」 with
   * generic placeholders on three different screens.
   *
   * PO decision (2026-08-22): do NOT surface "會匯出至會計師報表" to the user —
   * the placeholder examples carry the guidance instead.
   */
  PAYMENT_NOTES: {
    /** Requester, whole request — PaymentRequestDoc.notes */
    REQUEST_LABEL: '請款備註 — 整張單',
    REQUEST_PLACEHOLDER: '例：本次採購用途、特殊付款條件、需一併說明的背景。',
    /** GA / treasury, whole request — PaymentRequestDoc.receiptInfo.notes */
    RECEIPT_LABEL: '發票備註 — 整張單',
    RECEIPT_PLACEHOLDER: '例：發票另附於他單、廠商重開中、金額與發票不符。',
    /** Per line — PaymentRequestItem.itemNote */
    ITEM_LABEL: '費用備註 — 單列',
    /**
     * Placeholders must fit the 180px stacked cell — roughly 11 full-width
     * characters, or they truncate mid-sentence. Longer detail goes in the
     * tooltip, and both stay terse: say the thing, don't explain it.
     */
    ITEM_PLACEHOLDER: '補充說明（選填）',
    ITEM_TOOLTIP: '例：憑證遺失原因、金額與發票不符。',
    /**
     * 憑證另附 has no invoice-number field, so the number — when the requester
     * happens to have it — belongs in this note. Ask for it directly.
     */
    ITEM_PLACEHOLDER_DOC_ELSEWHERE: '發票號碼或在哪張單',
    ITEM_TOOLTIP_DOC_ELSEWHERE: '有發票號碼就填號碼；沒有就寫發票在哪張單。',
    /** Excel column headers, mirroring the three labels above. */
    EXPORT_COLUMN_REQUEST: '請款備註',
    EXPORT_COLUMN_RECEIPT: '發票備註',
    EXPORT_COLUMN_ITEM: '費用備註',
    /** Single-cell Excel export — an unbounded paste makes the CPA sheet unreadable. */
    ITEM_MAXLENGTH: 200,
    REQUEST_MAXLENGTH: 500,
  },

  TRANSFER_REDESIGN: {
    /** Red soft-phase banner shown on renewal-due rows within 7 days of auto-end. */
    SOFT_PHASE_BANNER: '即將自動結算',
    /** LINE binding indicator — student account has a linked LINE userId. */
    LIFF_BINDING_BOUND: '綁定 LINE 中',
    /** LINE binding indicator — student account has no linked LINE userId. */
    LIFF_BINDING_UNBOUND: '未綁定',
    /** Button label to send LIFF binding link to parent (post-LIFF feature). */
    LIFF_BINDING_SEND_LINK: '發送綁定連結',
    /**
     * Canonical note written to pendingTransferNote when a transferring enrollment's
     * source timeslot is deleted mid-transfer. UI uses this exact string to
     * distinguish system-flagged BF待設定 rows from admin-Path-A rows (R7-C).
     * MUST match the backend constant of the same name.
     */
    TIMESLOT_REMOVED_NOTE: '原時段已下架,請追蹤新時段安排',
    /**
     * Deep review 2026-09-30 (L1): the pendingTransferNote the backend writes when a 課表
     * upload removed a 待約課 record's NEW class and re-routed the record to 未完成 — the
     * 未完成 row shows it as its 「新班時段已下架」 flag (utils/transferRowView). MIRROR of the
     * backend constant of the same name (check-config-mirror).
     */
    TARGET_TIMESLOT_REMOVED_NOTE: '新班時段已下架',
    /**
     * 來源時段 signal when a 轉出中/已轉出 source was auto-released because it has
     * zero future 學期 bookings (PR 4d) or passed its LSED. TITLE = cause, SUB =
     * outcome (retained for reuse; no longer rendered as a separate chip —
     * 2026-07-02 the ACTION banner now carries both status + next step).
     * ACTION is gated on isSourceOrphaned and shown as the full-width banner.
     * MUST match the backend mirror of the same names.
     */
    SOURCE_RELEASED_NO_BOOKINGS_TITLE: '原班已無約課',
    SOURCE_RELEASED_NO_BOOKINGS_SUB: '名額釋出',
    SOURCE_RELEASED_ACTION:
      '原班已無約課 (保留名額釋出)。新班有名額請按 確定轉入；新班還沒有名額可選 其他處理 › 轉為候補 (資料會帶過去)；不轉了請選 其他處理 › 取消轉班。',
    // R2-W1-G: REGISTER_TRANSFER_CONFIRM removed from UI_STRINGS — duplicated CONFIRM_DIALOGS.REGISTER_TRANSFER.
    // Components must use CONFIRM_DIALOGS.REGISTER_TRANSFER for the confirm text.
    /**
     * Topbar badge tooltip template.
     * {{n1}} = 續報未通知 count, {{n2}} = 轉班待處理 count, {{n3}} = 保留待BF確認 count.
     */
    TOPBAR_BADGE_TOOLTIP: '{{n1}} 位學生續報未通知,{{n2}} 件轉班待處理,{{n3}} 件保留待 BF 確認',
    /** Empty state text when 學期轉班 tab has no pending entries. */
    EMPTY_TRANSFER_REQUESTS: '目前無轉班',
    /**
     * Teachers see status only (M5, PO 2026-09-29 — replaces the 評估中 / 進行中
     * placeholders and the 課務備註 line): TeacherReservationBanner, TeacherReservationModal
     * and the ⇄ TransferSuggestButton show the status pill (TRANSFER_TEACHER_STATUS), the
     * dates and the teacher; the teacher's own 建議原因 as a grey line (8-3); 拒絕原因 when
     * rejected (8-2). Built by utils/teacherTransferView.ts.
     */
    TEACHER_VIEW: {
      BANNER_TITLE: '轉班',
      /**
       * 「Sunny 9/20 建議」 — who made the suggestion and the day. 2026-10-15 (PO 「ok, change to
       * this」): the name sits beside 建議 — at the end of the line it read as who resolved it.
       */
      SUGGESTED_BY: (by: string, md: string) => (by ? `${by} ${md} 建議` : `${md} 建議`),
      /**
       * 「9/25 未採用」 — the day a resolved record's status took effect, with its verb (§ 8c):
       * 已轉入 = the new class confirmed (targetConfirmedAt; none for an older record inferred
       * confirmed); a closed record = its last write. 課務處理中 (W7.5) has no day.
       */
      RESOLVED_ON: (md: string, verb: string) => `${md} ${verb}`,
      // W7.5 (J-06): 課務處理中 has no day (it is not resolved); 處理 / 安排候補 / 不採用 follow
      // the new words (課務已處理 / 已安排候補 / 未採用). 2026-10-14 (W9.5 UX-05, J-06): rejected
      // reads the teacher's word 未採用 (不採用 is the admin's choice).
      RESOLVED_VERB: {
        transferred: '轉入',
        rejected: '未採用',
        retracted: '撤回',
        cancelled: '取消',
        // W6.6 (DERIVED ⚑): 家長留原班 is a 取消轉班 — its day reads 「M/D 取消」 as 已取消's.
        parentKeptSource: '取消',
        converted: '安排候補',
        handledElsewhere: '處理',
      } as Record<string, string>,
      /** A record from before the 回覆 list (no adminReplies) keeps these lines (W7.5). */
      REJECT_REASON: (reason: string) => `拒絕原因：${reason}`,
      /** D5 (PO 2026-10-09): under 已取消 when 課務 取消建議 with a reason. */
      CANCEL_REASON: (reason: string) => `取消原因：${reason}`,
      /**
       * 2026-10-14 (W7.5, J-05): 課務's latest reply to the teacher (adminReplies) — replaces
       * the 拒絕原因 / 取消原因 lines; `md` = the reply's day (M/D).
       */
      ADMIN_REPLY: (text: string) => `課務回覆：${text}`,
      /**
       * 2026-10-15 (PO 「ok, change to this」 — no repeated day): the reply keeps its day only when
       * the meta line does not already show it (課務處理中 has no day; a reply from an earlier step).
       */
      ADMIN_REPLY_ON: (md: string, text: string) => `${md} 課務回覆：${text}`,
      /** W7.5 (H-02): under 待課務處理 when the old class ended (transferContext.sourceEndedAt). */
      SOURCE_ENDED: '原班已結束',
      RETENTION_HELP: (days: number) => `已處理的紀錄保留 ${days} 天`,
      MODAL_TITLE: (student: string) => (student ? `建議轉班 · ${student}` : '建議轉班'),
      /** Under the open suggestion's status line; the form stays disabled meanwhile. */
      PENDING_GUARD: '課務處理完畢前不能再提出新的建議。',
      RETRACT: '撤回此建議',
      RETRACT_CONFIRM: '撤回後這筆建議會標記為 已撤回。確定要撤回嗎？',
      RETRACTED_TOAST: '已撤回此轉班建議',
      /**
       * The ⇄ button's hover by its colour (design § 8d; review B2): cyan = a suggestion is
       * 待課務處理 / 課務處理中, green = the most recent one was 已轉入, grey = it ended — or
       * `none`, nothing shown (the plain action).
       */
      BUTTON: {
        none: '建議轉班',
        pending: '此學生已有處理中的轉班建議',
        transferred: '最近的建議已轉入',
        /** M7 9B-2: grey like `ended`; W7.5 (J-06): 已安排候補's word. */
        converted: '最近的建議已安排候補 (等候名額)',
        ended: '最近的建議已結束',
      },
      /**
       * M7 9B-2 (PO 2026-09-30): 已安排候補 (W7.5 — was 已轉為候補) names what the family waits
       * for — 「已安排候補 週六 09:00 小企鵝班」, with more slots 「… 週六 09:00 等 2 個時段 小企鵝班」
       * (the record's convertedToTimeslots / convertedToProgramGroup; utils/teacherTransferView).
       * `waited` = HUB.ROW.WAITED_SLOTS through programWaitlistUtils.waitedSlotsLabel — the words
       * the 續報 候補中 flag uses for the same fact (9B-5 U6); '' = the plain label.
       */
      CONVERTED_LABEL: (base: string, waited: string) => [base, waited].filter(Boolean).join(' '),
      /** Its accessible name: the action, then the hover sentence 「建議轉班 (…)」. */
      BUTTON_ARIA: (action: string, status: string) => `${action} (${status})`,
    },
    /**
     * 2026-06-05 — Transfer ↔ Renewal guard (frontend-only; client-side soft
     * reminder, no backend mirror). Surfaces a pending teacher-suggested /
     * processing transfer when admin reaches for 續報 on the same
     * (student, timeslot).
     */
    RENEWAL_GUARD: {
      /** Persistent badge on a renewal row whose student has a teacher suggestion. */
      BADGE_LABEL: '老師建議轉班',
      /** Badge variant when the transfer is already being processed (in-progress / bf-pending). */
      BADGE_LABEL_PROCESSING: '轉班處理中',
      /** Soft-confirm title shown before 通知續報 / 已通知 proceeds. */
      CONFIRM_TITLE: '此學生已有轉班安排',
      /** Confirm body. {{kind}} = 老師建議轉班 / 轉班處理中; {{date}} = 申請日期 M/D. */
      CONFIRM_BODY:
        '此學生在此時段已有{{kind}} ({{date}} 提出)。通常轉班與續報會同時處理，建議改走轉班流程。仍要送出續報通知嗎？',
      /** Confirm primary — jump to the 轉班 tab focused on this record. */
      GOTO_TRANSFER: '前往轉班',
      /** Confirm secondary — proceed with the renewal notification anyway. */
      PROCEED_RENEWAL: '仍要續報',
      /**
       * 2026-09-29 (PO, design § 2b) — 已另報 guard: before 通知續報 / 再次提醒家長 /
       * 延長並通知續報 when the student has another active 學期 enrollment that started
       * after the row entered the 續報 window (utils/enrolledElsewhere.ts). Four buttons:
       * 取消 · 不續報… (opens 其他處理, 不續報 pre-selected) · 已改報其他時段 (writes at
       * once — this dialog is its confirm) · the notice anyway. Runs after CONFIRM_* above.
       */
      ELSEWHERE_TITLE: '此學生另有報名',
      ELSEWHERE_BODY: (student: string, md: string, slot: string) =>
        `${student} ${md} 起在 ${slot} 有學期報名 (Excel 建立)。可能是改報時段，也可能是加報第二班。`,
      /** The body's second line — what 已改報其他時段 does (M2 review). */
      ELSEWHERE_SWITCHED_HINT:
        '按 已改報其他時段：這一時段記為不續報並釋出名額 (列移到 近期未處理)。',
      ELSEWHERE_CANCEL: '取消',
      ELSEWHERE_NOT_RENEWING: '不續報…',
      ELSEWHERE_SWITCHED: '已改報其他時段',
      /** The blue button — the notice that was clicked, sent anyway. */
      ELSEWHERE_PROCEED: {
        notify: '仍要送出續報通知',
        remind: '仍要再次提醒家長',
        extend: '仍要延長並通知續報',
      },
      /**
       * After 已改報其他時段 was recorded (the child's name) — ONE toast for both ways in:
       * this dialog's button and 其他處理 › 不續報 › 已改報其他時段 (M2 review).
       */
      ELSEWHERE_SWITCHED_TOAST: (student: string) => `已標記 ${student} 不續報 · 已改報其他時段`,
      /** 其他處理 › 不續報 with any other detail (the child's name). */
      NOT_RENEWING_TOAST: (student: string) => `已標記 ${student} 不續報`,
    },
    /** Fallback error message when createTransfer returns no message. */
    SUBMIT_FAILED: '提交失敗',
    /**
     * User-facing error messages mirrored from functions/src/config/appConfig.ts.
     * Concrete reason + actionable next step. 2026-05-10 rewrite: dropped the
     * single conflated "no active enrollment" message; per-case strings now.
     */
    ERROR_STUDENT_NOT_FOUND: '找不到此學生，請聯絡課務人員確認姓名拼寫是否正確',
    ERROR_STUDENT_AMBIGUOUS_NAME: '系統中有多位學生名為「{name}」，請聯絡課務人員協助辨識',
    ERROR_NOT_CLASS_TEACHER: '您並非此班級的老師，無法建議此學生轉班',
    ERROR_CLASS_NOT_FOUND: '找不到此班級資料，請重新整理頁面後再試',
    ERROR_GENERIC_TRANSFER_FAILED: '送出失敗，請稍後重試，或聯絡課務協助處理',
    ERROR_RETRACT_NOT_OWN_RECORD: '只能撤回自己建議的轉班紀錄',
    ERROR_RETRACT_AFTER_ADMIN_REVIEW: '課務已開始處理此建議，如需修改請直接聯絡課務',
    /** Mirror of ERROR_DUPLICATE_OPEN_TRANSFER (functions/src/config/appConfig.ts). */
    ERROR_DUPLICATE_OPEN_TRANSFER:
      '此學生此時段已有進行中的轉班紀錄，請至課務總覽-轉班查看或先取消既有紀錄',
    /**
     * Human-readable labels for SessionEnrollment endingReason values.
     * Backend writes raw English strings for system-generated reasons;
     * this map converts them for display throughout the UI.
     * Read by StudentEnrollmentTimeline and SetReservationModal; the system codes'
     * words come from SYSTEM_ENDING_REASON_LABELS (the same as 近期未處理).
     */
    ENDING_REASON_LABELS: {
      不續報: '不續報',
      轉班: '轉班',
      停課: '停課',
      請長假: '暫停課程 (請長假)', // legacy pre-2026-05-15 top-level endingReason value; key must stay '請長假' to match old stored data
      其他: '其他',
      取消保留: '取消保留',
      ...SYSTEM_ENDING_REASON_LABELS,
    } as Record<string, string>,
    /**
     * Human-readable labels for system-generated actor tokens written to
     * `lastModifiedBy` / `createdBy` audit fields. Backend writes the raw
     * English token (mirror of BUSINESS_RULES.SESSION_ENROLLMENT.DETECTION_TRIGGERS
     * in functions/src/config/appConfig.ts); this map converts them for display.
     *
     * Labeling principle: when the actor is a system token, the label must
     * describe the CAUSE (why the row changed), not the trigger name. Readers
     * already know "the system" did it; what they need is the meaning.
     *
     * `manual:USERNAME` tokens are handled at call site by stripping the prefix.
     * Tokens not listed here (currently `scheduled`, `backfillTimeslotId`) are
     * defined in the backend constants but never written to any audit field
     * in the codebase — they would not appear in the timeline.
     */
    SYSTEM_ACTOR_LABELS: {
      'post-schedule-upload': '課表上傳',
      'post-rescue-upload': '課表上傳 (救援模式)',
      autoEndExpiredSessionEnrollments: '未在規定內確認約課 (系統自動)',
      // 2026-05-23: cron tokens for admin-facing timeline. Backend stamps the
      // raw token in lastModifiedBy; without translation it surfaces as English
      // "cron-auto-end-transferring-source" which leaks internal naming.
      'cron-auto-end-transferring-source': '系統自動結束 (轉班逾期)',
      'cron-auto-abandoned-transfer': '系統自動取消轉班 (逾期)',
      // PO 2026-10-03: the 03:30 run's 未完成 自動歸檔 (functions programWaitlist/transferAutoClose).
      'cron-transfer-auto-close': '系統自動',
      // PO 2026-10-03 (Q5 = B): the 03:30 grace expiry that ends a 轉班 hold (maintenance.ts
      // executeGraceExpiry) — 學生紀錄's 「結束」 card showed the raw token.
      'system-bf-grace': '系統自動 (轉班保留到期)',
      // W10.4 F1 (D26 (1)): the upload check re-run right after 取消轉班 / 轉為候補 / 家長留原班
      // removed a new-class hold (functions programWaitlist/transferHoldRedetect.ts) — the
      // cause, as 課表上傳 is (DETECTION_TRIGGERS.TRANSFER_HOLD_RELEASE).
      'transfer-hold-release': '課表偵測 (轉班保留釋出後)',
    } as Record<string, string>,
    /**
     * Sub-threshold (< SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD, currently 8)
     * manual transfer-completion cautions + badge (Option C, 2026-06-26).
     * Centralized from 3 prior inline transfer-domain literals (TransferAdminTable,
     * buildChips transfer branch, TransferMarkCompleteForm). The renewal-domain
     * sibling (未達自動續報門檻, buildChips.ts:635) stays inline — different domain.
     * The "≥8" wording tracks AUTO_TAG_THRESHOLD; if the threshold ever changes,
     * update the config value AND this copy together. PICKER_CAUTION carries a `{{n}}`
     * placeholder the call site replaces with the real count; GRID_CHIP_TOOLTIP is a
     * function of the count and reads the threshold (Hub UI B20, 2026-09-28 — it quoted a
     * literal "N"); W10.5 X3: AWAITING_BADGE_TOOLTIP too, and both (with
     * TARGET_PARTIAL_RELEASE_WARNING) say what happens at N ≥ the threshold
     * (TRANSFER_PARTIAL_AT_THRESHOLD_TOOLTIP).
     */
    SUB_THRESHOLD: {
      /** Canonical threshold phrase — used in the pre-completion CAUTIONS (warnings). */
      THRESHOLD_LABEL: '未達自動完成轉班門檻 (≥8 堂)',
      /**
       * POST-completion MARKER (2026-06-26 — affirming, per feedback_wording_parallel_consistency):
       * a <8 manual completion IS a verified transfer 學期 session (verificationStatus='已驗證' —
       * the admin picked its last lesson in 確定轉入 / 結算轉班; the 「我確認這是轉班學期課程」 tick
       * was dropped 2026-10-15, D43 B9 — the warning stays). The label AFFIRMS that — it does NOT lead with the
       * negative 未達門檻 (which read as "not a real session"). ROW_BADGE_LABEL = the compact
       * 已完成-row badge; COMPLETION_MARKER = the inline suffix on the 確認轉入/結算轉班 timeline
       * entry; BADGE_TOOLTIP = the badge's hover explanation.
       */
      ROW_BADGE_LABEL: '課務手動確認',
      COMPLETION_MARKER: '課務手動確認・未滿 8 堂',
      BADGE_TOOLTIP: '已確認此為轉班學期課程；未滿 8 堂，由課務手動確認完成。',
      /** 2026-10-13 (續報・轉班 完成規則 W2.6, P11): the same badge on a 結算續報 row (renewalChips). */
      RENEWAL_BADGE_TOOLTIP: '已確認此為續報學期課程，由課務以 結算續報 手動確認。',
      /** 結算轉班 modal 適用情境 — first bullet (was TransferMarkCompleteForm.vue:15). */
      MARK_COMPLETE_HINT: TRANSFER_MARK_COMPLETE_CASES.THRESHOLD,
      /**
       * 結算轉班 適用情境 — fix round 1 (UX review M6): the one window that records the term
       * asks whether these bookings are this term; 補課 names the same exits as the tooltips.
       */
      MARK_COMPLETE_TERM_HINT: TRANSFER_MARK_COMPLETE_CASES.TERM,
      /**
       * 結算轉班 適用情境 — the third bullet (R1.2, 2026-11-02: was inline in
       * TransferMarkCompleteForm's template; the component is re-pointed by a later task).
       */
      MARK_COMPLETE_LSED_HINT: TRANSFER_MARK_COMPLETE_CASES.LSED,
      /**
       * 結算轉班 適用情境 — the last bullet: what the window does to the two classes. 2026-10-14
       * (W9.5 UX-08): screen words only (was 「原班不會變動，目標時段保留鎖會解除」, inline).
       */
      MARK_COMPLETE_EFFECT_HINT: TRANSFER_MARK_COMPLETE_CASES.EFFECT,
      /**
       * 結算轉班's body line naming the child (W9.5 UX-08): the confirm bubble now reads K-01's
       * shared SET_RESERVATION.SECOND_CONFIRM, so the child stays visible in the window.
       */
      MARK_COMPLETE_STUDENT_LABEL: '學生：',
      /**
       * 學期轉班 awaiting-row 期間已約 caution tooltip (was TransferAdminTable.vue:466-470).
       * PO 2026-10-02 (D-1): names both ways — the bookings may be this term (結算轉班) or
       * 補課 (取消轉班 / 轉為候補 / 修改新班時段, all usable again with 1–7 bookings).
       */
      AWAITING_BADGE_TOOLTIP: (n: number) =>
        n >= SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD
          ? TRANSFER_PARTIAL_AT_THRESHOLD_TOOLTIP(n, '')
          : `上次 Excel 上傳出現 ${n} 堂新學期票課程，但未達自動完成轉班門檻 (≥${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂)。可能為補課、預約錯誤、或真實但短期的轉班，請先問家長。確認是這期學期 → 結算轉班；可能是補課 → 其他處理：取消轉班、轉為候補 或 修改新班時段。`,
      /**
       * TimeSlot grid chip caution tooltip, transfer branch (was buildChips.ts:634) — both ways,
       * as above (fix round 1, UX LOW 16: ask the family first, then where to act).
       */
      GRID_CHIP_TOOLTIP: (n: number) =>
        n >= SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD
          ? TRANSFER_PARTIAL_AT_THRESHOLD_TOOLTIP(n, '到 課務總覽 › 轉班 ')
          : `上次 Excel 上傳出現 ${n} 堂新學期票課程，但未達自動完成轉班門檻 (≥${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂)。請和家長確認後，到 課務總覽 › 轉班 處理：確認是這期學期 → 結算轉班；可能是補課 → 其他處理：取消轉班、轉為候補 或 修改新班時段。`,
      /** 確定轉入 (Path B) caution shown when the PICKED session is sub-threshold (D1, W3). */
      PICKER_CAUTION:
        '此次轉入的學期約課僅 {{n}} 堂，未達自動完成轉班門檻 (≥8 堂)，請確認後再完成轉班。',
      /**
       * 確定轉入 (Path B) caution shown when admin picks 「都不是本次轉班的」 — applies to
       * BOTH ≥8 and <8 surfaced counts (PO 2026-06-26). It defers (→ 待 BF), does NOT
       * complete; the surfaced bookings are NOT lost (they live in `attendance`), and
       * the row can still be settled later via 結算轉班.
       */
      NOT_A_SESSION_CAUTION:
        '已選「都不是本次轉班的」：系統會忽略目前 {{n}} 堂轉班後新約課，改以 14 天「待 BF」名額保留，不會立即完成轉班。若這其實是本次轉班的學期課程，請改挑最後一堂直接完成；或之後於「待約課」列用「結算轉班」補完成 (約課資料不會遺失)。',
    },
  },
  /**
   * Eager-mount preload progress banner (admin/supervisor only).
   * Banner renders bottom-right while preload promises are in flight,
   * then fades out. Each LABELS value is the human-readable name of one
   * preload bucket; the banner joins active labels with the existing
   * separator and surrounds with PREFIX/SUFFIX.
   */
  PRELOAD: {
    BANNER_PREFIX: '預先載入中：',
    BANNER_SUFFIX: '…',
    LABELS: {
      RENEWAL: '學期續報',
      RESERVATION: '保留追蹤',
    },
    // Excluded from banner labels:
    //   - 轉班申請 — realtime onSnapshot listener (no request-response phase).
    //   - 時段資料 — already preloaded via INSTANT_PREFETCH_REGISTRY entry
    //     "timeslot-discovery" through useInstantPrefetch.runIdlePrefetch();
    //     wiring it into the banner would require exposing per-entry promises
    //     from that pipeline, which is scope creep for a status indicator.
  },
  /**
   * 首堂課與體驗回饋 — all UI copy (docs/FIRST_TRIAL_FEEDBACK_MOCKUP.html v4.1,
   * verbatim). Never render `now` or the current viewer as a record (spec §0.3):
   * every actor/time shown here comes from a stored field.
   */
  FIRST_TRIAL: {
    PILL: {
      FIRST: '首堂課',
      NOT_FIRST: '非首堂',
      /** D-T (PO 2026-10-13): the pill states the fact; the date is in the hover line. */
      TRIALLED: '首堂已上',
      /** 家庭帳號 待確認 (D-B, PO 2026-10-12): the ? badge and › are drawn by the pill. */
      REVIEW: '首堂待確認',
    },
    /**
     * 首堂課 檢查 — the confirm box a click on the pill opens (D-Z: no hover card;
     * plan todo-first-trial-family-check W1.3; texts = mockup §1–§3 and its §2 table,
     * accepted v8.1). Order of the five checks = the PO's priority (spec §4).
     */
    CHECK: {
      HEADER: (name: string, mmdd: string, className: string) => `${name} · ${mmdd} ${className}`,
      CONFIRM_TITLE: '這是首堂課嗎？',
      /** Any confirm box's title: the row, then the question (PO 2026-11-02: 「廖曉君 · 10/11 小鴨班 — 這是首堂課嗎？」). */
      CONFIRM_TITLE_FOR: (title: string, header: string) => `${header} — ${title}`,
      /** Joins the parts of one check line (「… · 可能是 孫廷睿」, the sub-line parts). */
      JOIN: ' · ',
      /** Screen-reader words for the four check icons (mockup §2 legend). */
      ICON_LABELS: {
        ok: '支持首堂課',
        no: '反對',
        warn: '要看一下',
        none: '沒有資訊',
      },
      OPEN_FAMILY: '打開家庭帳號 ›',
      /** Beside 打開家庭帳號 › in the confirm box; n = names on the account. */
      OPEN_FAMILY_HINT: (n: number) => `看家庭帳號 ${n} 人的上課與回饋紀錄`,
      /** Tag on the suggested button (green / red only, mockup §3). */
      SUGGESTED: '建議',
      LABELS: {
        IPAD: 'iPad 簽到',
        FAMILY: '家庭帳號',
        OWN: '出席狀況',
        CHILD: '小孩是誰',
        TICKET: '票種',
      },
      /** The box's leading title (D-AB, PO 2026-10-13): 「建議：很可能是首堂課」 / 「說明：…」. */
      HEADLINE_PREFIX: '建議：',
      REASON_PREFIX: '說明：',
      LIKELIHOOD_TITLES: {
        'likely-first': '很可能是首堂課',
        'needs-check': '需要確認',
        'likely-not': '很可能不是首堂課',
      },
      /** Headline sub-line per FIRST_TRIAL.LIKELIHOOD_REASONS (mockup §1–§3 examples). */
      LIKELIHOOD_REASONS: {
        CLEAR_ONLY_NAME: (parentYes: boolean) =>
          parentYes ? '家長說是，此帳號沒有其他家庭帳號' : '此帳號沒有其他家庭帳號',
        CLEAR_FAMILY_NONE: (parentYes: boolean) =>
          parentYes ? '家長說是，家庭帳號其他人沒上過課' : '家庭帳號其他人沒上過課',
        /** A sibling's trial (D-L): the family's attendance does not count. */
        CLEAR_SELF_NONE: (parentYes: boolean) =>
          parentYes ? '家長說是，本人沒上過課' : '本人沒上過課',
        OWN_PRIOR: (mmdd: string) => `本人 ${mmdd} 已上過課`,
        ONLY_CHILD_ATTENDED: (mmdd: string, feedbackDone: boolean) =>
          `此帳號只有一位小孩，${mmdd} 已上過課${feedbackDone ? '並完成回饋' : ''}`,
        ONLY_CHILD_PARENT_NO: (mmdd: string) =>
          `家長說不是第一次；此帳號唯一的小孩 ${mmdd} 已上過課`,
        PARENT_NO_FAMILY_PRIOR: (mmdd: string) => `家長說不是第一次；家庭帳號其他人 ${mmdd} 上過課`,
        MANY_CHILDREN: (n: number) => `此帳號有 ${n} 位小孩，其中一位上過課`,
        PARENT_YES_FAMILY_PRIOR: (n: number) =>
          n >= 2
            ? `家長說是第一次，但此帳號有 ${n} 位小孩，其中一位上過課`
            : '家長說是第一次，但家庭帳號其他人上過課',
        OTHER_ADULT: '家庭帳號另一位大人上過課',
        PARENT_NO_ALONE: '家長說不是第一次，但查不到先前的出席',
      },
      IPAD: {
        /** answer = FIRST_TRIAL_DISPLAY.PARENT_ANSWER_LABELS, when = 日期 時間 */
        ANSWER_AT: (answer: string, when: string) => `${answer} ${when}`,
        NONE: '未回答',
        NONE_SUB: '家長在 iPad 簽到但沒點',
        NOT_KIOSK: '未在 iPad 簽到',
        NOT_KIOSK_SUB: '課務在簽到表簽到，iPad 沒有問家長',
        NOT_YET: '尚未簽到',
        NOT_YET_SUB: '簽到時會問',
      },
      FAMILY: {
        ONLY_NAME: '此帳號沒有其他家庭帳號',
        /** No 會員帳號 on the row (W0.0 Q6-g): never claim 「沒有其他家庭帳號」. */
        NO_ACCOUNT: '沒有會員帳號，查不到家庭帳號',
        /** The family read failed (P13): the system made no automatic decision. */
        UNKNOWN: '家庭紀錄讀取失敗，請打開家庭帳號查看',
        NO_TRIAL: '無體驗紀錄',
        /** Names are numbered 1. 2. (D-C, PO round 4). */
        NUMBERED: (n: number, name: string) => `${n}. ${name}`,
        /** Merged two-tone pill 首堂課｜日期 班名 (D-C): left and right segments. */
        PILL_FIRST: '首堂課',
        /** The same pill when that name's row is itself 首堂待確認 (= PILL.REVIEW, D-B). */
        PILL_REVIEW: '首堂待確認',
        PILL_DATE_CLASS: (mmdd: string, className: string) => `${mmdd} ${className}`,
        FEEDBACK: (simpleId: string, state: string) =>
          simpleId ? `回饋 ${simpleId} ${state}` : `回饋 ${state}`,
        FEEDBACK_DONE: '已完成',
        FEEDBACK_PENDING: '未完成',
        FEEDBACK_NONE: '未建立',
        ONE_CHILD: '此帳號只有這一位小孩',
        CHILDREN: (n: number) => `此帳號有 ${n} 位小孩`,
        NO_CHILD: '此帳號沒有小孩',
      },
      OWN: {
        /** programme = CHECK.PROGRAMME_NAMES */
        NONE: (programme: string) => `${programme} 300 天內無出席`,
        /** Under the line on a 首堂待確認 row: the check is about this name only. */
        SELF_SUB: (name: string) => `${name} 本人的出席`,
        PRIOR: (mmdd: string, className: string) => `${mmdd} ${className} 已出席`,
        PRIOR_SUB: '非首堂',
        EXPIRED: (mmdd: string) => `上次出席 ${mmdd}，已超過 300 天，重新算首堂`,
        /** status = 請假／缺席 */
        MISSED: (mmdd: string, status: string) => `${mmdd} ${status}，不算出席`,
        MISSED_SUB: (n: number) => `三堂體驗第 ${n} 堂`,
        OTHER_PROGRAMME_SUB: (other: string) => `${other}的出席不計`,
      },
      /** The card's programme words (FEEDBACK_PROGRAMMES keys). */
      PROGRAMME_NAMES: { SWIM: '游泳課', FMS: 'FMS' } as Record<string, string>,
      CHILD: {
        NAMED_SUB: '預約已指定小孩',
        ADULT_ONLY: (adult: string) => `預約只有大人 ${adult}，沒指定小孩`,
        /** 「可能是」 is a hint to a person; the system never fills a child (家庭帳號 D9). */
        MAYBE: (names: string[]) => `可能是 ${names.join(' 或 ')}`,
        MAYBE_ONE_SUB: '此帳號唯一的小孩',
        MAYBE_MANY_SUB: (n: number) => `此帳號有 ${n} 位小孩`,
        NO_CHILD_SUB: '此帳號沒有小孩',
        SELF_BOOKED: '家長自行預約無法選同行者',
        NOTES: (text: string) => `約課備註：「${text}」`,
        NO_NOTES: '無約課備註',
      },
    },
    /** 學生紀錄 › 已完成課程 card (D-D, mockup §5). */
    COMPLETED_CARD: {
      PERIOD: '上課期間',
      /** 上課期間 of several classes: first – last attended day. */
      PERIOD_RANGE: (from: string, to: string) => `${from} – ${to}`,
      COUNT: (n: number) => `已上 ${n} 堂`,
      /** Merged ticket pill right segment. */
      TIMES: (n: number) => `×${n}`,
      STRIP_FIRST: (day: string) => `首堂課 ${day}`,
      /**
       * The strip's 回饋 state — the same three states the 課程進度 tab shows
       * (courseProgressRows: PDF ready / form without PDF / no form). The phone leaves
       * the 編號 out (mockup §5b 「回饋已完成」: the teacher phone shows no 編號 today).
       */
      FEEDBACK: (simpleId: string, state: string) =>
        simpleId ? `回饋 ${simpleId} ${state}` : `回饋 ${state}`,
      FEEDBACK_DONE: '已完成',
      FEEDBACK_PENDING: '未完成',
      FEEDBACK_NONE: '未建立',
      /** D-O (PO 2026-10-13): named after where it goes (看記錄 › already opens 學習歷程). */
      VIEW_PROGRESS: '課程進度 ›',
    },
    /**
     * 學生紀錄 › 約課時段 as two lists (PO 2026-10-16, mockup
     * docs/tools/student-record-one-card-per-class.html): one card per class the child is
     * still in, its 已上 as a light-grey line; 已完成課程 = classes with nothing left.
     */
    SLOT_LISTS: {
      /** List headings, no caption under either (PO: 「just simplify to: 正在上的課」). */
      LIVE_HEADING: '正在上的課',
      DONE_HEADING: '已完成課程',
      LIVE_EMPTY: '沒有正在上的課',
      /**
       * 「已上 N 堂 · 期間」 — the 正在上的課 card's line and the 家庭帳號 panel's 此班／其他班,
       * one writer so one fact reads the same (period = COMPLETED_CARD 上課期間 writer).
       */
      ATTENDED: (n: number, period: string) =>
        period ? `已上 ${n} 堂 · ${period}` : `已上 ${n} 堂`,
    },
    TOOLTIP: {
      /** D-Z (PO 2026-10-13): the hover of a pill that opens the confirm box — the box holds the checks. */
      OPEN_FOR_DETAILS: '點開看說明',
      SYSTEM_EXPECTED: '系統預先標記 · 出席後自動確認；請假不消耗，下一堂仍是首堂',
      SYSTEM_BASIS_NONE: '系統依據：同課程 300 天內無其他出席',
      SYSTEM_BASIS_PRIOR: (mmdd: string, className: string) =>
        `系統依據：同課程曾出席 ${mmdd}（${className}）`,
      CONFIRMED: (source: string, by: string, when: string) => `${source} · ${by} · ${when}`,
      /** An automatic verdict: the named person checked the student in; they did not decide. */
      CONFIRMED_AUTO: (source: string, when: string, by: string) =>
        by ? `${source} · ${when}（簽到：${by}）` : `${source} · ${when}`,
      /** Checked in, no 首堂課 verdict stored (pre-launch history, or a check-in whose lookback failed). */
      HISTORIC_UNCONFIRMED: '已出席，但沒有首堂課判定紀錄',
      PILL_LOCKED: '上課日當天起才能修改',
      /** 已核對 class outside edit mode — the existing 簽到作業 lock. */
      PILL_LOCKED_APPROVED: '此班已核對，請先按下方「已審核｜點擊編輯」再修改',
      LEGACY: '首堂課・舊資料（確認時間不詳）',
      /** D-S (PO 2026-10-13): 「首堂課已上 10/06 小鴨班；回饋表未完成」 — the form part staff only (D-U). */
      TRIALLED: (mmdd: string, className: string, form: string | null) =>
        `首堂課已上 ${mmdd} ${className}${form ? `；回饋表${form}` : ''}`,
      // 「紀錄為」 not 「系統判為」: the stored verdict may be a person's (spec §0.3).
      DISCREPANCY_PARENT_NO: '家長回答「不是第一次」，但紀錄為首堂課 · 點一下確認或取消',
      DISCREPANCY_PARENT_YES: '家長回答「是，第一次上課」，但紀錄為非首堂 · 點一下確認或取消',
      NON_TRIAL: '非體驗票，無首堂課標籤；圖示照常可用（建立時填原因）',
      FMS_DISABLED: 'FMS 回饋表尚未建立',
      FUTURE_LOCKED: '課程尚未開始，無法建立回饋',
      STAGED_ROW: '儲存變更後才能建立回饋',
      STATUS_LOADING: '回饋狀態讀取中',
    },
    CONFIRM: {
      TO_NOT_FIRST_TITLE: '改為非首堂？',
      TO_NOT_FIRST_OK: '改為非首堂',
      TO_FIRST_TITLE: '改為首堂課？',
      TO_FIRST_OK: '改為首堂課',
      /** Pre-deploy history: checked in, no verdict stored. */
      HISTORIC_TITLE: '這是首堂課嗎？',
      /** The pill writes at once, also in edit mode (取消編輯 does not undo it). */
      SAVES_NOW: '此變更立即儲存，不隨「取消編輯」還原。',
      DISCREPANCY_TITLE: '家長回答與系統不一致',
      PARENT_SAID: (answer: string) => `家長在 iPad 回答：${answer}`,
      RESOLVE_FIRST: '是首堂課',
      RESOLVE_NOT_FIRST: '不是首堂課',
      CANCEL: '取消',
    },
    ICON: {
      OTHER_ROW: (mmdd: string) => `此學生 ${mmdd} 已有回饋（另一堂課）`,
      /** PO 2026-09-16: say what is actually wrong — the teacher has no LINE id. */
      NOT_NOTIFIED: '老師未設定 LINE',
      RESEND_HINT: '點開可重新通知老師',
      DISABLED_BEFORE: '（先前建立的回饋已停用）',
    },
    COLUMN_HELP:
      '系統會自動預標首堂課（虛線橘），出席後自動轉綠；家長回答不一致時出現 ⚠。點首堂課標籤可改判（會先確認）。點回饋圖示建立體驗回饋表並通知老師。',
    /**
     * 體驗回饋頁「待建立體驗回饋」面板 (PO 2026-09-18): the drill-down of the day
     * list's 尚未建立回饋 marker — every ended 首堂課 still without a form.
     */
    PENDING_PANEL: {
      TITLE: '待建立體驗回饋',
      /** Subtitle: the two states side by side, so 未建立 reconciles with the day list's marker. */
      // D8 (2026-11-01): the 佇列 is hidden, so 排隊中 is named only while a legacy queued row exists.
      SUMMARY: (none: number, queued: number) =>
        queued > 0 ? `未建立 ${none} · 排隊中 ${queued}` : `未建立 ${none}`,
      // WINDOW_HINT 「近 N 天已下課的首堂課」 retired (D24, R5.1): a 待建立 row never leaves on its
      // own, so the panel names no time window.
      COL_CLASS_TIME: '上課時間',
      COL_CLASS: '課程',
      COL_TEACHER: '老師',
      COL_STUDENT: '學生',
      // COL_STATUS 「狀態」 retired (R5.1, card B1): the column draws the 簽到表's pill under the
      // hub's header UI_STRINGS.HUB.FEEDBACK.PENDING_STAGE_COLUMN 「首堂課」.
      COL_ACTION: '操作',
      STATUS_NONE: '未建立',
      STATUS_QUEUED: '排隊中',
      /** 排隊中 is judged per STUDENT (same programme, 300-day window) — the item may stand for another row of the same child. */
      QUEUED_BY: (who: string) => `該生已在準備清單 (同課程)，由 ${who} 排入`,
      // DISCREPANCY_HINT retired (D28, B3／Q10): a ⚠ row's 操作 is 確認狀態, opening the resolve
      // window in place — no hover sends 課務 to 簽到作業 any more.
      // WAITING_DAYS 「已下課 N 天」 retired (Q27 b, R5.1): the panel says the hub's 第 N 天
      // (UI_STRINGS.HUB.ROW.DAY_N／DAYS; the class day = 第 1 天, red from 第 4 天).
      CREATE: '建立回饋',
      REFRESH: '重新整理',
      COLLAPSE: '收合',
      EXPAND: '展開',
      LOAD_FAILED: '待建立清單載入失敗，請重新整理。',
      /**
       * Empty state (PO 2026-09-21). The panel used to disappear entirely when
       * the list was empty, which is indistinguishable from "the feature is not
       * here" — the PO looked for it on a quiet day and could not tell whether
       * it had shipped. It now always occupies its place, greyed and collapsed,
       * so an empty list reads as 「查過了，沒有」 rather than as nothing.
       */
      EMPTY: '目前沒有待建立的體驗回饋',
      /** D-AH: 首堂待確認 rows — confirm here; a 首堂課 then moves up to 待建立. */
      REVIEW_SUMMARY: (n: number) => `待確認 ${n}`,
      /** 首堂待確認 row's action, beside 建立回饋 (PO 2026-10-13: 「reword to 確認狀態」). */
      CONFIRM_STATUS: '確認狀態',
      REVIEW_LOAD_FAILED: '首堂待確認清單載入失敗，請重新整理。',
      /** D-AM: a 核對確認 class's 首堂課 is locked here as on 簽到作業 (TOOLTIP.PILL_LOCKED_APPROVED). */
      REVIEW_LOCKED_APPROVED: '此班已核對，請先到簽到作業按「已審核｜點擊編輯」再確認',
      /**
       * D32-23: a 主管簽核 class's 首堂課 is locked for good — the hover names that block in the
       * server's own words (BE UI_STRINGS.FIRST_TRIAL.CLASS_SIGNED_OFF, first sentence), not
       * REVIEW_LOCKED_APPROVED's path, which ends in the server's refusal there.
       */
      LOCKED_SIGNED_OFF: '此課程已有主管簽核記錄，無法變更。',
    },
    CLASS_MARKER: {
      NO_FEEDBACK: (n: number) => `${n} 位首堂課尚未建立回饋`,
      DISCREPANCY: (n: number) => `${n} 位家長回答與系統不一致`,
      /** 首堂待確認 rows (D-I; P15: from the class day, never on a signed-off class). */
      REVIEW: (n: number) => `${n} 位首堂待確認`,
      /** Class-list chip without a 首堂 segment (the long-standing 「體驗 N 人」). */
      TRIAL_FULL: (n: number) => `體驗 ${n} 人`,
      /** Day-list chip: the red segment drops 人 when the 首堂 segment is shown (PO 2026-09-15: 不能太長). */
      TRIAL_COMPACT: (n: number) => `體驗 ${n}`,
      FIRST_COUNT: (n: number) => `首堂 ${n}`,
      /** Orange segment — class not ended: expected first + already confirmed. */
      FIRST_UPCOMING: (n: number) => `首堂課 ${n} 位（含預計首堂；下課後只算出席）`,
      /** Green segment — class ended: attended 首堂課 only. */
      FIRST_ATTENDED: (n: number) => `首堂課出席 ${n} 位（已下課）`,
    },
    MODAL: {
      TITLE: '建立體驗回饋表',
      FACT_SYSTEM_FIRST: '首堂課 · 系統判定（同課程 300 天內無其他出席）',
      FACT_EXISTING: (mmdd: string, teacher: string, stage: string) =>
        `已有回饋：${mmdd} ${teacher} · ${stage}`,
      FACT_NON_TRIAL: '非體驗票 · 同課程 300 天內無其他出席',
      /** The lookback did not run (or failed): state only the ticket fact (§0.3). */
      FACT_NON_TRIAL_ONLY: '非體驗票',
      FACT_NOT_FIRST_PRIOR: (mmdd: string, className: string) =>
        `非首堂 · 系統依據：同課程曾出席 ${mmdd}（${className}）`,
      FACT_NOT_FIRST_BY: (source: string) => `非首堂 · ${source}`,
      FACT_TRIALLED: (mmdd: string, className: string) => `首堂課在 ${mmdd}（${className}）`,
      FACT_NON_TRIAL_PRIOR: (mmdd: string, className: string) =>
        `非體驗票 · 同課程曾出席 ${mmdd}（${className}）`,
      FACT_NOT_CHECKED_IN: '此列尚未簽到出席',
      /** P16: a 首堂待確認 row — a reminder, never a block, no reason asked. */
      FACT_REVIEW: '首堂待確認 · 請先點標籤確認是不是首堂課，再建立回饋',
      /** D-E: a family member already has a form in this programme. Half-width ( ) (D-E, W9 UX-10 a). */
      FACT_FAMILY_FEEDBACK: (name: string, simpleId: string, mmdd: string) =>
        `家庭帳號 ${name} 已有回饋 ${simpleId} (${mmdd})`,
      /** The child's 準備清單 item is on another row; the create removes it (feedback-createFeedbackForAttendance queuedItemId). */
      FACT_QUEUED: (by: string, when: string) =>
        `此學生在準備清單中${by || when ? `（${[by, when].filter(Boolean).join(' · ')}）` : ''}：建立後會一併移除該項目`,
      FACT_DISCREPANCY: '⚠ 家長回答與系統不一致，請先點首堂課標籤處理',
      LABEL_TEACHER: '老師',
      LABEL_TIME: '時段',
      LABEL_CLASS: '課程',
      LABEL_PARENT: '大人',
      LABEL_CHILD: '小孩',
      LABEL_LANGUAGE: '表單語言',
      LANG_ZH: '🇹🇼中文',
      LANG_EN: '🇬🇧English',
      /** A form already exists → 再建立一份; a non-trial ticket's first form → 建立原因. */
      LABEL_REASON: '再建立一份的原因',
      LABEL_REASON_FIRST: '建立原因',
      REASON_PLACEHOLDER: '請選擇原因',
      NO_LINE_ID: (teacher: string) => `${teacher} 尚未設定 LINE ID：只建立表單，不會通知`,
      /** 協同班 explainer under the teacher field (spec §12, PO 2026-09-15 wording). */
      COOP_CLASS_TEACHERS: (names: string) => `這堂課安排 ${names} 上課`,
      COOP_DEFAULT: (main: string) => `時段預設主教：${main}`,
      /** Added when the 主教 does not teach this class. */
      COOP_DEFAULT_AWAY: '（已換人請重選）',
      COOP_UNSET: '時段還沒設定主教老師',
      /** The teacher select's placeholder when it starts empty. */
      COOP_PICK: '請選這堂課的主教老師',
      /** The latest form of THIS class went to a teacher no longer on the class (e.g. teacher changed after sending). */
      FACT_TEACHER_CHANGED: (simpleId: string, teacher: string) =>
        `${simpleId} 的老師 ${teacher} 已不在這堂課：建立新的一份給正確的老師後，請到體驗回饋頁停用 ${simpleId}`,
      SUBMIT_NOTIFY: '建立並通知老師',
      SUBMIT_FORM_ONLY: '建立表單',
      CANCEL: '取消',
      TOAST_NOTIFIED: (simpleId: string, teacher: string) =>
        `已建立 ${simpleId}，已通知 ${teacher}`,
      TOAST_FORM_ONLY: (simpleId: string) => `已建立 ${simpleId}（未通知老師）`,
      TOAST_ALREADY_CREATED: (simpleId: string) => `已建立 ${simpleId}（先前的送出已完成）`,
      TOAST_LINE_PENDING: (simpleId: string) => `已建立 ${simpleId}，LINE 通知傳送中`,
      TOAST_LINE_FAILED: (simpleId: string) =>
        `已建立 ${simpleId}，但 LINE 傳送失敗；可點圖示重新通知老師`,
      /** Existing form whose LINE failed / was never sent (spec §3 LINE 傳送失敗 → 重送). */
      RESEND: '重新通知老師',
      RESEND_DONE: (simpleId: string) => `已重新通知老師（${simpleId}）`,
      RESEND_FAILED: (simpleId: string) => `重新通知失敗（${simpleId}），請稍後再試`,
      /** Secondary button when the latest form only needs 重新通知老師. */
      CREATE_ANOTHER: '另建一份',
      LOAD_FAILED: '無法讀取這位學生的回饋狀態',
      RETRY: '重試',
      MISSING: (fields: string) => `尚未填寫：${fields}`,
    },
    /** 體驗回饋頁 (spec §5 D5; mockup §7). Moved page strings keep their old wording. */
    FEEDBACK_PAGE: {
      STAGE_COLUMN: '階段',
      /** Stepper label of the last stage (STAGE_LABELS.completed is the tooltip wording). */
      STEP_COMPLETED: '已完成',
      APPROVED_AT: (when: string) => `審核於 ${when}`,
      APPROVED_NO_TIME: '表單已審核',
      REJECTED_NOTE: '主管審核未通過',
      /** The stage line of a denied form (fix-choices B111): what happened, then the way forward. */
      REJECTED_LINE: '主管審核未通過 · 請老師用 重新編輯 修改',
      /**
       * The stage line of a review / denied form whose Jotform submission no longer exists
       * (fix-choices B104: `jotformSourceMissingAt` on a not-yet-approved form). 停用 is the exit.
       * Never names the vendor (A2, PO 2026-11-02 「the team doesnt know what is jotform」).
       */
      SOURCE_MISSING_LINE: '原始表單已不存在',
      LINE_SENT: 'LINE 通知已發送',
      LINE_SENDING: 'LINE 傳送中',
      LINE_FAILED_TIP: (error: string) => `LINE 通知失敗: ${error}`,
      LINE_ERROR_UNKNOWN: '未知錯誤',
      LINE_OTHER: (status: string) => `LINE: ${status}`,
      LINE_UNPROCESSED: '未處理',
      TEACHER_LINK_COPY: (url: string) => `點擊複製: ${url}`,
      TEACHER_LINK_MISSING: '短網址失敗或未建立',
      COPIED: '已複製到剪貼簿',
      NOTHING_TO_COPY: '沒有內容可以複製',
      /**
       * 連結 column — the teacher's own form. 老師填寫 is rendered for the teacher
       * role only: staff hand the link over from 操作 instead, so nobody files a
       * submission in a teacher's name (PO 2026-09-16).
       */
      FILL_FORM: '老師填寫',
      FILL_FORM_TIP: '開啟老師的回饋表 (老師填寫用)',
      EDIT_FORM: '重新編輯',
      EDIT_FORM_TIP: '開啟老師已送出的回饋表：老師可以修改，課務可以查看',
      /** 操作 column — 課務／主管 only (canEditFeedback). */
      COPY_LINK: '複製老師連結',
      COPY_LINK_TIP:
        '複製老師填寫用的表單連結 (就是系統用 LINE 傳給老師的那一個)，可以自己貼給老師；90 天內有效。這不是家長的回饋 PDF。',
      DOWNLOAD_PDF: '下載PDF',
      DOWNLOAD_PDF_TIP: '開啟老師回饋 PDF',
      REVIEW_BUTTON: '主管審核',
      /** A2 (PO 2026-11-02): says what opens, never the vendor's name. */
      REVIEW_TIP: '開啟主管審核頁 (主管)',
      RESEND_BUTTON: '重新通知',
      RESEND_TIP: '再傳一次 LINE 給老師 (同一份表單)',
      RESEND_CONFIRM: (simpleId: string, teacher: string) =>
        `重新用 LINE 通知 ${teacher} (${simpleId})？`,
      RESEND_OK: (simpleId: string) => `已重新通知老師 (${simpleId})`,
      COMPLETE_ITEM: '完成',
      DISABLE_ITEM: '停用',
      /**
       * ⋯ 完成 / 停用 confirms (fix-choices B110): they name the row — 「#1234 陳小安」, see
       * HUB.FEEDBACK.ROW_LABEL — and, for 停用, the teacher the LINE goes to.
       */
      COMPLETE_CONFIRM: (row: string) => `確定將 ${row} 的回饋表標記為已完成？這筆會移到 已完成。`,
      DISABLE_CONFIRM: (row: string, teacher: string) =>
        `確定停用 ${row} 的回饋表？系統會用 LINE 通知老師 ${teacher}，停用後無法復原。`,
      DISABLE_OK: '項目已停用，並已通知老師',
      NOTE_SAVED: '備註已更新',
      MORE_ACTIONS: '更多動作',
      /**
       * 手動通知老師 (PO 2026-09-16): one click copies the teacher's form link and
       * marks the row 已手動通知, so a second person does not send it again. It
       * records the copy, never a delivery — LINE is outside this system.
       */
      MANUAL_NOTIFY: '手動通知老師',
      MANUAL_NOTIFY_TIP:
        '複製老師的表單連結，並把這筆標記成「已手動通知」(記下時間)；別人一看就知道不用再傳一次。連結 90 天內有效。',
      MANUAL_NOTIFY_OK: '已複製連結，並標記為已手動通知',
      MANUAL_NOTIFY_COPY_FAILED: '沒有複製成功，這一筆沒有標記',
      MANUAL_NOTIFIED_LABEL: (when: string) => `已手動通知 · ${when}`,
      MANUAL_NOTIFIED_TIP: (when: string) =>
        `課務按了「手動通知老師」：連結已複製，這一筆標記為已手動通知 (${when})。\n系統只記自己知道的事，不會宣稱老師已經收到。`,
      MANUAL_NOTIFY_UNDO: '取消已手動通知',
      MANUAL_NOTIFY_UNDO_TIP:
        '按錯時把標記拿掉，這一行會變回原本的「老師未設定 LINE」或「LINE 傳送失敗」。',
      MANUAL_NOTIFY_UNDO_OK: '已取消「已手動通知」的標記',
      /** 多一行 after the teacher submits — LINE no longer matters, the review does. */
      TEACHER_SUBMITTED: '老師已填寫',
      /** 備註 cell links under the box. */
      NOTE_HISTORY: (n: number) => `歷史 ${n}`,
      NOTE_RESYNC: '重新同步',
      /** 重新同步's confirmation (the page's 備註 cell and the 課務總覽 row). */
      RESYNC_CONFIRM:
        '重新同步老師推薦班級？將從老師填寫表取得最新內容並覆寫目前備註 (歷史記錄保留)。',
      /** 學生 column sub-line / 學生紀錄: 「第 2 份 · 補發」. */
      ORDINAL: (n: number) => `第 ${n} 份`,
      /** PO 2026-09-16: the button copies a message staff paste into LINE themselves. */
      TEMPLATE_BUTTON: '通知家長',
      TEMPLATE_NO_PDF: '主管審核通過、產生 PDF 後才能使用',
      TEMPLATE_FORM_DISABLED: '此回饋已停用',
      /** 訊息範本 window (PO 2026-09-15): editable, same actions as 招生訊息 體06. */
      TEMPLATE_TITLE: (child: string) => `通知家長 · ${child}`,
      /**
       * fix-choices B113: the title when the form's child differs from the BF 會員姓名 — LINE
       * contacts are found by 會員姓名: 「通知家長 · 林建宏 - 林可可 (回饋表：Coco)」.
       */
      TEMPLATE_TITLE_MEMBER: (member: string, child: string) =>
        `通知家長 · ${member} (回饋表：${child})`,
      TEMPLATE_DONE: '已標記完成。家長再要連結時，可以從這裡再複製一次。',
      /** 家長訊息視窗 統一 (PO 2026-09-25): the blue far-right button — no ✅ on blue. */
      MARK_BUTTON: '送出後標記回饋完成',
      MARK_CONFIRM: (child: string) =>
        `確定已把體驗回饋送給 ${child} 的家長？標記後這筆會移到「已完成」。`,
      MARK_CANCEL: '取消',
      /** Confirm-dialog primary button — always 「確認」; the question already says what is confirmed (PO standing rule). */
      CONFIRM_OK: '確認',
    },
    /** 體驗回饋頁 → 「設定協同班主教」 window (spec §12, PO 2026-09-15; renamed from 協同班回饋老師 PO 2026-09-21). */
    COOP_TEACHERS: {
      BUTTON: '設定協同班主教',
      TITLE: '設定協同班主教',
      HINT: (days: number) =>
        `列出未來 ${days} 天課表裡的協同班。建立體驗回饋表時，主教老師當天有上這堂課才會預設選好；不在時要自己選。`,
      COL_TIME: '時段',
      COL_CLASS: '班級',
      COL_TEACHER: '主教老師',
      UNSET: '未設定',
      EMPTY: (days: number) => `未來 ${days} 天的課表裡沒有協同班`,
      LOAD_FAILED: '無法讀取協同班清單',
      RETRY: '重試',
      CANCEL: '取消',
      SAVE: '儲存',
      SAVED: '已儲存協同班主教',
      LAST_SET: (by: string, when: string) => `${by} · ${when}`,
      WEEKDAYS: ['週日', '週一', '週二', '週三', '週四', '週五', '週六'] as const,
    },
    /** 學生紀錄 → 「課程進度」 tab (spec §5 D6, PO 2026-09-15): the 首堂體驗課 and its 老師回饋. */
    COURSE_PROGRESS: {
      TAB: '課程進度',
      TITLE: '首堂體驗課',
      COL_DATE: '日期',
      COL_TIME: '時段',
      COL_CLASS: '班級',
      COL_TEACHER: '老師',
      COL_FEEDBACK: '老師回饋',
      /** The link text: opens the teacher's feedback (PDF). */
      FEEDBACK_LINK: '老師回饋',
      /** A form exists, the teacher's feedback is not ready yet. */
      FEEDBACK_PENDING: '未完成',
      FEEDBACK_NONE: '未建立',
      EMPTY: '尚無首堂體驗課紀錄',
    },
    KIOSK: {
      QUESTION: '這是寶貝的第一堂體驗課嗎？',
      QUESTION_SUB: '請點選以利老師準備課後回饋！',
    },
    /** 核對歷史 modal: an entry written by the pill's confirm box. */
    HISTORY: {
      LABEL: '首堂課判定',
      ACTOR: '操作者',
    },
    /** Mockup §9: the reminder is read because the button says it (A5). */
    MANUAL_ENTRY_SUBMIT: '仍要手動加入',
    MANUAL_ENTRY_SOFT_BLOCK:
      '這裡新增的回饋不會出現在學生紀錄與簽到表。學生已有預約列時，請改從簽到表的圖示建立。',
  },

  /**
   * 2026-09-24 — 續報 期限與提醒 (tasks/todo-renewal-deadline-reminder.md; spec
   * docs/SESSION_ENROLLMENT_BUSINESS_RULES.md §續報日期規則). Staff-facing only; every
   * number comes from SESSION_ENROLLMENT_CONFIG so a rule change cannot leave a stale
   * sentence behind. Staff UI brackets are half-width. `md` = M/D (formatShortTaipeiDate),
   * `days` = Taipei calendar days (FrontendConfigUtils.taipeiCalendarDaysUntil, D-4).
   */
  RENEWAL_AWAITING: {
    /** 通知續報 window, staff-only line above the message (never copied). */
    NOTICE_INFO_ON_TIME: (md: string, days: number) =>
      `保留至 ${md} (${days <= 0 ? '今日到期' : `家長有 ${days} 天`})`,
    /** Same line when the notice is late (LSED−23 onward): the lock moved past LSED−14. */
    NOTICE_INFO_LATE: (md: string, days: number) =>
      `⏱ 較晚通知：保留至 ${md} (${days <= 0 ? '今日到期' : `家長有 ${days} 天`})`,
    /** 延長並通知續報 window banner (ends with 「不延長？改為其他處理」; last class day: 今日到期). */
    EXTEND_BANNER: (md: string, days: number) =>
      `此名額已過續報期限並釋出。確認後將重新保留至 ${md} (${days <= 0 ? '今日到期' : `家長有 ${days} 天`}) 並記錄今日為通知日期。\n` +
      '請確認已實際聯繫家長後再操作。',
    /** Countdown on the deadline day itself (count = 0). */
    DUE_TODAY: '今日到期',
    /** 待通知 cell on the release day: the seat releases at a set time (LSED is stored at 12:00). */
    DUE_TODAY_AT: (hm: string) => `今日 ${hm} 到期`,
    /** 待通知 保留期限 cell tooltips (2026-09-25 M1 review F3). `md`/`hm` = the release instant. */
    COUNTDOWN_NORMAL: (days: number, md: string, hm: string) =>
      `剩 ${days} 天到名額釋出 (${md} ${hm}，最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天)。請適時聯絡家長確認是否續報。`,
    COUNTDOWN_URGENT: (days: number, md: string, hm: string) =>
      `剩 ${days} 天到名額釋出 (${md} ${hm}，最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天)。` +
      '若沒通知，名額會在那時釋出，並在之後的凌晨 03:00 自動歸檔為「課務未通知」。請儘快聯絡家長確認是否續報。',
    COUNTDOWN_TODAY: (hm: string) =>
      `今日 ${hm} 名額釋出：${hm} 前可按「通知續報」；之後此列移到「已過期未通知」，可用「延長並通知續報」(家長一樣有 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS} 天)。` +
      '沒處理的列會在之後的凌晨 03:00 自動歸檔為「課務未通知」。',
    COUNTDOWN_RELEASED:
      `已過名額釋出時間 (最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天)。系統會在之後的凌晨 03:00 自動歸檔為「課務未通知」；` +
      '家長若要續報，請到「已過期未通知」用「延長並通知續報」。',
    /**
     * 排課時段表 已通知·待確認 chip tooltip — the real 保留至 date and time (late rows
     * included; review F-01: the seat releases at that time, not at midnight).
     */
    AWAITING_CHIP_TOOLTIP: (md: string, hm: string) =>
      `已通知續報，等約課完成上傳 Excel。名額保留至 ${md} ${hm}。`,
    AWAITING_CHIP_TOOLTIP_NO_DATE: '已通知續報，等約課完成上傳 Excel。',
    /** 已通知・已逾期 chip — a late row's seat released at its own 保留至, not LSED−14. */
    LAPSED_CHIP_TOOLTIP: '已通知續報，但家長未在保留期限內完成；名額已於保留期限後釋出。',
    /** Appended only where the chip carries its ✓ (not in the read-only 學生紀錄 — review P9-F). */
    LAPSED_CHIP_ACK_HINT: '確認知悉後按右側 ✓ 消除提醒。',
    /**
     * 2026-10-14 (續報・轉班 完成規則 W9.6 P6-03 — 只寫系統自己做的事): 約課通知待發 chip
     * (useEnrollmentStatusChips/renewalChips). 「已自動續報，」 only when the row carries
     * autoRenewedAt (ReservationInfo.isAutoRenewed); the message by its on-screen name — the
     * 發約課通知 button opens the 約課通知 window (PARENT_MESSAGE.TITLES.BOOKING).
     */
    /**
     * W10.4 F15 (D26 (15), PO 2026-10-14): the hover of 約課通知待發's row button 無需通知
     * (beside 發約課通知 on every row; its confirm window unchanged).
     */
    SKIP_NOTIFY_TITLE: '不發約課通知，標記完成並記錄 (不會發送訊息)',
    NOTIFY_PENDING_CHIP_TOOLTIP: (autoRenewed: boolean) =>
      `${autoRenewed ? '已自動續報，' : ''}請送出真實日期的 約課通知。送出後此提醒自動消除。`,
    /** Student menu (排課時段表) action titles. */
    NOTIFY_ACTION_TITLE:
      `通知此學生續報並保留名額至續報期限 (最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天；` +
      `較晚通知為通知日起 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS} 天，最晚到學期結束)，等待 BF／付款`,
    RENOTIFY_ACTION_TITLE: (tag: string) => `撤銷「${tag}」標記並重新通知續報 (名額保留至續報期限)`,
    EXTEND_ACTION_TITLE:
      `此名額已過續報期限並釋出；確認後重新保留名額 (通知日起 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS} 天，` +
      '最晚到學期結束) 並記錄通知，回到續報流程',
    /** 學生紀錄 timeline meta line for a 延長並通知續報 entry. */
    EXTEND_TIMELINE_META: '重新保留名額並記錄通知',
    /**
     * 已確認釋出名額's confirm — ONE text for 排課時段表 and 課務總覽 (review P6-9 / H-6). The
     * write (confirmReleaseLapsed) stamps 課務未通知 and dismisses the row; never 不續報.
     */
    RELEASE_CONFIRM_QUESTION: (name: string) => `確認 ${name} 的名額已釋出給候補？`,
    RELEASE_CONFIRM_DETAIL:
      `名額已於最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天釋出；` +
      '此操作僅作確認，記錄為課務未通知並移出未處理列表 (不會標記為不續報)。',
    /** Hover of 課務總覽's 已確認釋出名額 button (review F10 — was inline in RenewalPendingRow). */
    RELEASE_ACTION_TITLE: '確認此名額已釋出給候補 (不再保留；不等同不續報)',
    /**
     * 移除暫時保留's confirm (review F10 — was inline in RenewalPendingRow and
     * StudentLifecycleStatusCell). The consequence line is ONE text for both surfaces; each
     * question names its own surface's button (課務總覽 移除暫時保留 / 排課時段表 移除暫時保留名額
     * — the two names are a recorded PO wording question, AUDIT_ACCEPTED_RISKS § Hub H6 P9-L).
     */
    CLEAR_HOLD_CONFIRM_QUESTION: (name: string) => `確定要移除 ${name} 的暫時保留嗎？`,
    CLEAR_HOLD_CONFIRM_QUESTION_GRID: (name: string) => `確定要移除 ${name} 的暫時保留名額嗎？`,
    CLEAR_HOLD_CONFIRM_DETAIL: '移除後恢復為一般紀錄；若無實際課程資料，隔日凌晨將自動結束。',
    /** 排課時段表 status legend row. */
    LEGEND_TRUST_LOCK: `信任鎖 (續報：保留至續報期限；轉班：${TRANSFER_IN_HOLD_LABEL}，保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天)`,

    // ── M3 (2026-09-25) — 已通知·待確認 redesign + 再次提醒家長 (plan T23). Row values are
    // built once per list change by useSessionEnrollments/renewalAwaitingDisplay.ts; the
    // M3 components (table, 首次通知 popover, reminder window, explainer link) read the
    // rest. `md` = M/D (formatShortTaipeiDate); `days` = Taipei calendar days (D-4); a
    // count of 0 reads 「今天」 (PO Q10).
    /** 已通知·待確認 table (RenewalAwaitingTable). */
    AWAITING_TABLE: {
      // 2026-09-28 (Hub UI W2.6): the column names live in UI_STRINGS.HUB.COLUMNS (A2.2)
      // and the 最後約課日 line in UI_STRINGS.HUB.ROW.LSED (A4.3 — the 注意 column is wide
      // enough for the full name, so the short 最後日 goes).
      /**
       * On the deadline day the row leaves this list at the lock's time (12:00 on time,
       * the click time when late), so it reads 「今日 HH:MM 到期」 — the 待通知 wording
       * (DUE_TODAY_AT). `hm` = the lock's Taipei HH:MM (review F-01).
       */
      HOLD_UNTIL: (md: string, days: number, hm: string) =>
        days <= 0 ? `今日 ${hm} 到期` : `保留至 ${md} (剩 ${days} 天)`,
      /**
       * W10.4 F14 (D26 (14), PO 2026-10-14 「不改門檻 (說明放滑過)」): the 剩 N 天 count's second
       * hover line — what it counts to and why the colour (lockEscalation: ≤ URGENT amber, ≤
       * FINAL_WARNING red). `md` = the 保留至 day.
       */
      HOLD_COUNT_RULE: (md: string) =>
        `算到 保留至 ${md}；${SESSION_ENROLLMENT_CONFIG.RENEWAL_LOCK_URGENT_DAYS} 天內 橘、${SESSION_ENROLLMENT_CONFIG.RENEWAL_LOCK_FINAL_WARNING_DAYS} 天內 紅`,
      /** `date` = YYYY/MM/DD; `hm` = the lock's Taipei HH:MM. */
      HOLD_TITLE: (date: string, days: number, hm: string) =>
        days <= 0
          ? `已通知家長續報，等待付款並由 BF 上傳新學期約課資料。名額保留至今日 ${hm}；` +
            '之後此列移到「近期未處理」，隔日凌晨 03:00 歸檔。'
          : `已通知家長續報，等待付款並由 BF 上傳新學期約課資料。名額保留至 ${date} (剩 ${days} 天)；` +
            '到期後系統自動釋出名額，並於隔日凌晨 03:00 歸檔。',
      /**
       * Under the 保留至 date when a late notice moved the lock past LSED−14 — words, not
       * the old ⏱ (PO round 16, 2026-09-28); LATE_TITLE is its hover title.
       */
      LATE_MARK: '較晚通知',
      LATE_TITLE: (extraDays: number, standardMd: string, lockMd: string) =>
        `較晚通知：保留至超過最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天，` +
        `名額保留延長 ${extraDays} 天 (由 ${standardMd} 延至 ${lockMd})`,
      // 2026-09-28 (Hub UI A4.8): the flag form carries the warning icon, not a ⚠ glyph.
      MARKER_REANCHORED: '最後約課日已更新',
      /**
       * Its hover on the 續報 lists. D3 (PO 2026-10-04, docs/tools/seat-renewal-fixes.html
       * item 2 — 「續報 頁 … 拿掉做不到的「再次通知續報」」): 通知續報 is not pressable on a told
       * family and 再次提醒家長 does not clear the flag; the upload's automatic 續報 does. The
       * 排課時段表 chip reads the same (MARKER_REANCHORED_CHIP_TITLE).
       */
      MARKER_REANCHORED_TITLE: (prevDate: string, nextDate: string) =>
        `最後約課日已更新 (${prevDate} → ${nextDate})，請重新確認續報通知時機；` +
        `上傳後約滿 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂自動續報時，此提醒自動消除。`,
      /**
       * The same hover on the 排課時段表 chip (useEnrollmentStatusChips/renewalChips.ts, D3).
       * `moveLine` = 「最後約課日已更新：M/D → M/D。」 or 「最後約課日已更新。」.
       */
      MARKER_REANCHORED_CHIP_TITLE: (moveLine: string) =>
        `${moveLine}請重新確認續報通知時機；` +
        `上傳後約滿 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂自動續報時，此提醒自動消除。`,
      /**
       * The same chip on a row waiting for its 約課通知 (audit item 2, PO 2026-10-06): sending
       * it tells the parent the new date and clears the chip (the server's
       * reanchorChipClearFields, sessionEnrollments/core.ts).
       */
      MARKER_REANCHORED_NOTICE_TITLE: (moveLine: string) =>
        `${moveLine}發約課通知 告訴家長新的日期後，此提醒自動消除。`,
      // 2026-09-28 (Hub UI A4.9 / B20): the flag form carries the warning icon (no ⚠);
      // the title states the real count and reads the threshold from config. Also the
      // 排課時段表 chip's title (useEnrollmentStatusChips/renewalChips.ts).
      MARKER_PARTIAL: (n: number) => `期間已約 ${n} 堂`,
      // 2026-10-13 (續報・轉班 完成規則 W0.3, C-04): what 結算續報 and 修改最後約課日 do with
      // these bookings (settleOrReanchorHint — one sentence for every such hover).
      MARKER_PARTIAL_TITLE: (n: number) => settleOrReanchorHint(n),
      FIRST_CONTACT: (md: string, by: string) => (by ? `${md} 首次通知 (${by})` : `${md} 首次通知`),
      /** Hover hint on the 首次通知 pill (PO 2026-09-25 decision 1 B). */
      FIRST_CONTACT_TITLE: '點一下可查看通知時間或撤銷通知',
      /**
       * The 再次提醒 column's state line, stacked above the 再次提醒家長 button (A3.2 /
       * PO round 16 option B) — before / on / after the reminder day. 「N 天後」, not
       * 「剩」: the 保留至 number already counts down to the deadline (review F-19, PO Q4).
       * The button's own words are HUB_OPEN_ACTIONS.remindParent.
       */
      REMIND_LATER: (md: string, days: number) => `提醒日 ${md} · ${days} 天後`,
      REMIND_TODAY: (md: string) => `提醒日 ${md} · 今天`,
      REMIND_OVERDUE: (md: string, days: number) => `提醒日 ${md} · 已過 ${days} 天`,
      /** No reminder for this row (muted); REMIND_NONE_TITLE says why. */
      REMIND_NONE: '不提醒',
      REMIND_NONE_TITLE: {
        noContact: '這一輪沒有首次通知紀錄，不另外提醒',
        noLock: '名額保留已結束，不另外提醒',
        shortSpan: `通知時距保留期限不到 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_MIN_SPAN_DAYS} 天，不另外提醒`,
      },
      /**
       * The 已再提醒 fact once marked (PO Q2: remind once — per told date since 2026-10-04,
       * D2(b): when 保留至 moved after it, 再次提醒家長 comes back under this line).
       */
      REMINDED: (md: string, by: string) => (by ? `${md} 已再提醒 (${by})` : `${md} 已再提醒`),
      /**
       * Flag in 注意 when 保留至 moved after the reminder (PO Q2 fourth round) — flag form,
       * no ⚠, no 。 (A4.7); it says why 再次提醒家長 is offered again (D2(b)). The 最後警示
       * banner row is gone (PO Q2 2026-09-28: the red band and the big number carry it).
       */
      DEADLINE_CHANGED: (md: string) => `期限已改為 ${md}`,
      /**
       * The hover of 期限已改為 (W10.4 F11, D26 (11), PO 2026-10-14): the date the parent was told
       * (renewalNoticeDeadline) → the 保留至 now. Earlier than told (spec E-03 (c) — the row is
       * in 今天要再次提醒, the flag red) asks for the reminder; later stays amber.
       */
      DEADLINE_CHANGED_TITLE: (toldMd: string, lockMd: string, earlier: boolean) =>
        earlier
          ? `原告知 ${toldMd} → 現在 ${lockMd}，比告知早，請再次提醒家長`
          : `原告知 ${toldMd} → 現在 ${lockMd} (比告知晚)`,
      TODAY_CHIP: (n: number) => `今天要再次提醒 ${n} 位`,
      /** Hover on the 已通知·待確認 sub-tab while its reminder dot shows (review F-22). */
      SUBTAB_DUE_TITLE: (n: number) => `目前有 ${n} 位學生今天要再次提醒 — 點此查看`,
      SHOW_ALL: (n: number) => `顯示全部 (${n})`,
      EMPTY_TODAY: '今天沒有需要再次提醒的家長',
      EXPLAINER_LINK: '期限怎麼算？',
    },
    /** The ONE shared 首次通知 popover (RenewalContactPopover). `when` = YYYY/MM/DD HH:MM. */
    CONTACT_POPOVER: {
      ARIA_LABEL: '首次通知',
      WHEN: (when: string, by: string, daysAgo: number) =>
        `${when} ${by ? `由 ${by} ` : ''}首次按下「通知續報」(${daysAgo <= 0 ? '今天' : `${daysAgo} 天前`})`,
      UNDO: '撤銷通知',
      /** Also for StudentLifecycleStatusCell (T35) — the old text cited the retired 完成續報紀錄. */
      UNDO_CONFIRM:
        '撤銷此筆通知？保留的名額將釋出，這一輪的通知與已再提醒標記也會一併清除 (學生紀錄仍保留)。家長若仍想續報，需重新按「通知續報」。',
      /** PO 2026-09-25: a confirm's primary button reads only 「確認」 (red type kept). */
      UNDO_OK: '確認',
      /** Closes the whole popover (review F-03) — not 回上一步, which returns to a menu. */
      UNDO_CANCEL: '取消',
    },
    /** 再次提醒家長 window (RenewalReminderWindow on the shared ParentMessageWindow — the
     *  shared words live in UI_STRINGS.PARENT_MESSAGE). */
    REMINDER_WINDOW: {
      TITLE: (name: string) => `${RENEWAL_REMINDER_WINDOW_NAME}${PARENT_MESSAGE_TITLE_SEP}${name}`,
      /** D62 (W9): the title while 名額使用情況 shows — the name is in the block. */
      TITLE_PLAIN: RENEWAL_REMINDER_WINDOW_NAME,
      /** 家長訊息視窗 統一 (PO 2026-09-25): the blue far-right button — no ✅ on blue. */
      MARK_BUTTON: '送出後標記已再提醒',
      /** `name` = the student's name only (not 「學生 - 家長」). */
      MARK_CONFIRM: (name: string) => `確定已把提醒送給 ${name} 的家長？`,
      MARKED_TOAST: (md: string, by: string) =>
        by ? `已標記：${md} 已再提醒 (${by})` : `已標記：${md} 已再提醒`,
      /** The backend answered no-op: someone had already marked this cycle. `md` = that mark's day. */
      ALREADY_MARKED: (md: string, name: string) =>
        name ? `${md} 已由 (${name}) 標記已再提醒` : `${md} 已標記已再提醒`,
      /** Shown when the date the parent was told ≠ today's 保留至 (mockup §②). */
      DIVERGED_INFOBAR: (lockMd: string, toldMd: string) =>
        `此列通知後最後約課日有更新：訊息寫的是目前的保留日 ${lockMd} (原本告知家長 ${toldMd})。` +
        `標記後，${lockMd} 就是家長被告知的最新期限。`,
      /**
       * FE-owned refusal texts (PO 2026-09-25 decision 3 B) — the window shows these,
       * not the backend's message, for the two refusals it handles itself.
       * DEADLINE_CHANGED: the window has rebuilt the text with the new lock (`md`).
       */
      DEADLINE_CHANGED: (md: string) =>
        `保留期限剛改為 ${md}，下方訊息已更新。若已把舊訊息傳給家長，請重新複製、再傳一次，然後再標記。`,
      /** DEADLINE_CHANGED without the backend's current lock (cannot rebuild the text). */
      DEADLINE_CHANGED_FALLBACK: '保留期限已變更，請關閉視窗後重新開啟。',
      /** CHANGED_SINCE_RENDER — a retry from the same window cannot succeed, so it closes. */
      /** Review L-2: the window has already closed itself — say so, don't ask to close it. */
      CHANGED_SINCE_RENDER: '這筆提醒紀錄剛有變動，視窗已關閉，請重新開啟。',
      /** Shown inside the window when it opens after 保留至 passed (copy + mark disabled). */
      LOCK_EXPIRED_ON_OPEN: '保留期限已過、名額已釋出。',
      /** The lock passed while the window stayed open (checked again before marking). */
      LOCK_EXPIRED: '保留期限已過、名額已釋出，無法標記已再提醒。',
    },
    /**
     * Values the 續報提醒 composer step (RENEWAL_REMINDER) interpolates — parent-facing.
     * The sentence itself lives in the step (supervisor-editable); only the day phrase
     * and the ⚠️ lead are computed per row (useRenewalNoticeText.buildReminderText).
     */
    REMINDER_VALUES: {
      DAYS_LEFT: (days: number) => (days <= 0 ? '今天' : `最後 ${days} 天`),
      URGENT_MARK: '⚠️ ',
    },
    /**
     * 「期限怎麼算？」 window (RenewalDeadlineExplainer — mockup §③, T37): a calculator —
     * pick the notice day, read 保留至 / 再次提醒 / 家長有 / 遞補時間. Opened from the ONE
     * link at the 續報 tab level. No row-colour legend (PO 2026-09-25). The dates come
     * from src/utils/renewalDeadlineExplainer.ts (computeRenewalLockMs +
     * renewalReminderDayMs); every number here comes from SESSION_ENROLLMENT_CONFIG.
     */
    EXPLAINER: {
      TITLE: '期限怎麼算？',
      HOLD_LABEL: '保留至',
      HOLD_RULE:
        ` (也是告知家長的期限)＝「最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天」` +
        `與「通知日＋${SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS} 天」取較晚，但不超過最後約課日。`,
      REMIND_LABEL: '再次提醒',
      REMIND_RULE:
        '＝首次通知與保留至的中間那天 (除不盡取較早；' +
        `家長只有 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_MIN_SPAN_DAYS - 1} 天以內時不提醒)。`,
      LATE_LABEL: '⏱ 較晚通知',
      LATE_RULE: `＝保留至超過最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天。`,
      /** `lsed` = YYYY/MM/DD of the example (the day the window opens + 33 days). */
      EXAMPLE: (lsed: string) => `例：最後約課日 ${lsed}。拖拉「通知日」試試看：`,
      PRESETS_LABEL: '範例',
      PRESETS: [
        { daysBefore: 31, label: '準時 (前 31 天)' },
        { daysBefore: 26, label: '仍準時 (前 26 天)' },
        { daysBefore: 20, label: '⏱ 較晚通知 (前 20 天)' },
        {
          daysBefore: SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS,
          label: `最後可按 通知續報 (前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天)`,
        },
        { daysBefore: 8, label: '已過期未通知 (前 8 天)' },
      ],
      SLIDER_LABEL: '通知日',
      SLIDER_VALUE: (days: number, md: string) => `最後約課日前 ${days} 天 (${md})`,
      /** Axis stretches — where a notice on that day falls (the 待通知 zone rule). */
      TRACK_ZONE: {
        green: '🟢 理想',
        amber: '🟡',
        red: '🔴 最後機會',
        released: '⚫ 已過 通知續報 時間',
      },
      TICK_BEFORE: (days: number, md: string) => `前 ${days} 天 ${md}`,
      TICK_LAST: (md: string) => `最後日 ${md}`,
      PIN_NOTICE: (md: string) => `通知 ${md}`,
      PIN_REMIND: (md: string) => `再次提醒 ${md}`,
      PIN_HOLD: (md: string) => `保留至 ${md}`,
      BAND_PARENT: (days: number) => `家長 ${days} 天`,
      BAND_MARKET: (days: number) => `遞補 ${days} 天`,
      RESULT_HOLD: '保留至',
      RESULT_PARENT: '家長有',
      RESULT_REMIND: '再次提醒',
      RESULT_REMIND_AFTER: (days: number) => `通知後第 ${days} 天`,
      RESULT_REMIND_NONE: '不提醒',
      RESULT_REMIND_NONE_NOTE: `家長只有 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_MIN_SPAN_DAYS - 1} 天以內`,
      RESULT_MARKET: '遞補時間剩',
      RESULT_BUTTON: '按鈕',
      BUTTON_NOTIFY: '通知續報',
      BUTTON_EXTEND: '延長並通知續報',
      DAYS: (n: number) => `${n} 天`,
      /**
       * One line under the results — the stretch the chosen notice day falls in. The zone
       * bounds are renewalPendingZone's (RENEWAL_IDEAL_WINDOW_DAYS / RENEWAL_LASTCHANCE_DAYS);
       * CAP / RUNWAY appear only where the sentence describes the 保留至 rule itself.
       */
      ZONE_NOTE: {
        green: `🟢 理想通知期：家長 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_IDEAL_WINDOW_DAYS} 天以上，遞補時間完整。`,
        amber:
          `🟡 家長 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_LASTCHANCE_DAYS}–${SESSION_ENROLLMENT_CONFIG.RENEWAL_IDEAL_WINDOW_DAYS - 1} 天；` +
          `保留至仍是最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天，遞補時間完整。`,
        red:
          `🔴 最後機會：家長 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_MIN_RUNWAY_DAYS} 天，` +
          `保留至超過最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天，遞補時間變短。`,
        released: `⚫ 已過「最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天」：只剩「延長並通知續報」(僅限從未通知過的學生)。`,
      },
    },
  },

  /**
   * The windows where staff hand-pick a 最後約課日 (PO 2026-10-04, docs/tools/
   * seat-renewal-fixes.html items 1–2; tasks/todo-transfer-lock.md D1 / D2): 設定最後約課日
   * (SetReservationModal) and 轉班 › 確定轉入 / 結算轉班 (TransferCompleteForm /
   * TransferMarkCompleteForm). Reminders and alerts only — never a block. Words: 釋出保留名額
   * (never 候補), 「已約的課 (可能也有補課)」. M/D = formatShortTaipeiDate.
   */
  SET_RESERVATION: {
    /**
     * D1 — above the date list in all three windows. No class count (if one is ever
     * needed: SESSION_ENROLLMENT_CONFIG.SESSION_TYPICAL_CLASS_COUNT).
     */
    PICK_DATE_REMINDER:
      '下面列的是這位學生在這一班已約的課 (可能也有補課)。最後約課日決定續報提醒、釋出保留名額的時間；如果這一期的課還沒全部約好，請先確認再選。',
    /**
     * D2(a) — the second 點擊確認後 line for a family already told about 續報 (已通知·待確認):
     * the save keeps 續報 going and moves 保留至 (functions sessionEnrollments/manual.ts,
     * renewal-notified branch). `contactMd` = 首次通知; `lockMd` = the new 保留至.
     */
    TOLD_FAMILY_EFFECT: (contactMd: string, lockMd: string) =>
      `已通知續報 (${contactMd})：續報照常，名額保留至 ${lockMd}`,
    /**
     * D2(c) — the new 保留至 falls on an earlier day than the date the parent was told:
     * an alert, 確認 stays. `toldMd` = the told date; `lockMd` = the new 保留至.
     */
    TOLD_EARLIER_ALERT: (toldMd: string, lockMd: string) =>
      `家長被告知的續報期限是 ${toldMd}。改成這一天後，${lockMd} 就會釋出保留名額，比告知的早。確認後請通知家長新的期限。`,
    /**
     * 2026-10-13 (續報・轉班 完成規則 W0.3, E-00 / K-01; chart lane 1) — the state line on top of
     * the 修改最後約課日 / 恢復保留名額 / 確認保留名額 window: what this row is now. One entry per
     * RenewalRowState (a Record, so a new state is a type error until it has a line); each
     * reads only the args its words need (M/D = formatShortTaipeiDate). 一般 has no line (E-00
     * names none) → null. Also read by the 登記轉班 warning (W5.3). Read by the window since W2.2.
     * 2026-10-14 (W9.5 TP-01, spec G-10 D-04c): the two told lines end 「，M/D 已再提醒」 when the
     * row was reminded in this cycle (stateLineRemindedPart).
     */
    STATE_LINE: {
      general: null,
      'pending-notify': (a: RenewalStateLineArgs) =>
        `${RENEWAL_STATE_LINE_PREFIX}待通知 (剩 ${a.daysLeft} 天)`,
      'expired-unnotified': () => `${RENEWAL_STATE_LINE_PREFIX}已過期未通知`,
      'notified-awaiting': (a: RenewalStateLineArgs) =>
        `${RENEWAL_STATE_LINE_PREFIX}已通知·待確認 (${a.contactMd} 首次通知，保留至 ${a.lockMd}${stateLineRemindedPart(a)})`,
      'notified-overdue': (a: RenewalStateLineArgs) =>
        `${RENEWAL_STATE_LINE_PREFIX}已通知·已逾期 (${a.contactMd} 首次通知，保留至 ${a.lockMd} 已過${stateLineRemindedPart(a)})`,
      'booking-notice-pending': () => `${RENEWAL_STATE_LINE_PREFIX}約課通知待發`,
      'not-renewing': () => '不續報',
      'transferred-out': () => '已轉出',
      'released-admin-unnotified': () => '名額已釋出 (課務未通知)',
      'released-expired': () => '名額已釋出 (期限內未結案)',
      // 2026-10-13 (W1.4, E-00c): 其他 (結束狀態) — the system ended the row; hover below.
      'released-other': () => '名額已釋出',
      'special-hold': () => '暫時保留名額',
      'long-leave': () => '請長假',
      transferring: () => '轉出中',
      none: () => '沒有報名紀錄',
    } satisfies Record<RenewalRowState, ((a: RenewalStateLineArgs) => string) | null>,
    /** The state line's hover, where its words need one (轉出中: the row belongs to 轉班). */
    STATE_LINE_TITLE: {
      transferring: '由 轉班 頁處理',
      'released-other': '這一班的保留名額已釋出；恢復保留名額 會用選的日期重新保留',
    } satisfies Partial<Record<RenewalRowState, string>>,
    /**
     * K-01 ①–⑤ — the preview lines under the picked date (utils/reanchorPreviewLines.ts builds
     * them from FrontendConfigUtils.renewalReanchorOutcome). `from` / `to` / `md` = M/D,
     * `cls` = the class name as the screen shows it.
     */
    /**
     * 約課切斷確認 D80 (annex § 一 / § 二.5): {booked} = 結算's 「(已約 N 堂)」 on this line (moved
     * from the old 「● 確認後」 cell — these bookings move the date); null = none.
     */
    PREVIEW_LSED: (from: string, to: string, booked: number | null = null) =>
      booked === null
        ? `最後約課日：${from} → ${to}`
        : `最後約課日：${from} → ${to} (已約 ${booked} 堂)`,
    PREVIEW_HOLD: (cls: string, md: string) => `名額保留：${cls} 保留至 ${md}`,
    /**
     * 約課切斷確認 annex § 五 (PO 2026-10-25 「the rest all agree with recommeneded」): no date —
     * 保留至 is written once, in PREVIEW_HOLD above it (D69).
     */
    PREVIEW_CYCLE_KEPT: '續報：續報週期維持',
    PREVIEW_CYCLE_RESTART: (md: string) => `續報：續報週期重新開始，${md} 出現在 待通知`,
    /** ③ when the new date is already inside the renewal window (chart lane 1). */
    PREVIEW_CYCLE_RESTART_NOW: (n: number) =>
      `續報：續報週期重新開始，已在續報時間內，發完 約課通知 當天就出現在 待通知 (剩 ${n} 天)`,
    PREVIEW_NOTICE_LISTED: '約課通知待發：列入',
    /**
     * 約課切斷確認 D69 / annex § 一 (PO 2026-10-25): ④ of an 已通知·待確認 row whose new date is
     * outside 續報 time (E-03 (a), the cycle restarts) when the parent was told a date — the
     * 「(家長收過的是 M/D)」 the 「● 確認後」 line no longer carries. {md} = that told date.
     */
    PREVIEW_NOTICE_LISTED_TOLD: (md: string) => `約課通知待發：列入 (家長收過的是 ${md})`,
    /**
     * 約課切斷確認 D69 / annex § 一: ④ of a 約課通知待發 row (E-06 — the rule always keeps it
     * listed; the 30 days restart, the 來源 stays) — the facts the 「● 確認後」 line no longer carries.
     */
    PREVIEW_NOTICE_STILL_LISTED: `約課通知待發：仍列入 (${SESSION_ENROLLMENT_CONFIG.RENEWAL_SUCCESS_NOTIFY_WINDOW_DAYS} 天重新算，來源 不變)`,
    /**
     * 約課切斷確認 D63 14d (annex § 二.3): the one list line under 確定轉入 › 維持 最後約課日 — the
     * 'keep' 轉入 neither sets nor clears the new-class seat's 約課通知待發 (transferActions.ts
     * finishesOnRealSeat writes only notificationsLog).
     */
    PREVIEW_NOTICE_UNCHANGED: '約課通知待發：不變',
    PREVIEW_NOTICE_NOT_LISTED: '約課通知待發：不列入 (家長已收過這個日期)',
    /** ④ not listed for any other reason — the notice is kept (P5 (i)), or a never-told 修改 (P5 (iii)). */
    PREVIEW_NOTICE_NOT_LISTED_PLAIN: '約課通知待發：不列入',
    /** ⑤ 結算 with fewer than AUTO_TAG_THRESHOLD booking days. */
    PREVIEW_SUB_THRESHOLD: (n: number) => `只有 ${n} 堂`,
    /** E-03 (c): the new 保留至 falls on an earlier day than the told date (red). */
    PREVIEW_LOCK_EARLIER: (md: string) => `新的 保留至 ${md} 比家長被告知的日期早，請告知家長`,
    /** The second confirm after 確認 (K-01). */
    SECOND_CONFIRM: (md: string) => `確定把 最後約課日 改成 ${md}？`,
    /** K-01: the preview block's title (components/ReanchorPreview — W9.5 VR-05, was inline). */
    PREVIEW_TITLE: '點擊確認後：',
    /**
     * 2026-10-14 (W9.5 VR-05 — moved verbatim from components/SetReservationDateList): the date
     * list's states — loading, retry, the two empty sentences (each followed by
     * RENEWAL_WORKFLOW_CONFIG.SEAT_HOLD.RESERVE_MODAL_EMPTY_POINTER), its prompt and the two tags.
     */
    DATE_LIST_LOADING: '計算中…',
    DATE_LIST_RETRY: '重試',
    DATE_LIST_EMPTY: '此學員此時段尚無未來學期票預約，沒有日期可選。',
    DATE_LIST_NONE_SELECTABLE: '目前無可選擇的日期，請等 BF 上傳更晚的預約資料後再設定。',
    DATE_LIST_PROMPT: '選擇 最後約課日',
    /** D41 (h) / D37 (2026-11-02): 「目前」, the date chips' word (mockup v4; was 「目前設定」). */
    DATE_LIST_CURRENT_TAG: '目前',
    DATE_LIST_DEFAULT_TAG: '(預選)',
    /**
     * 2026-10-14 (W9.5 UX-20): the list's load failure — always this sentence (never the error's
     * own message); the error itself goes through useStandardizedErrors where it is fetched.
     */
    DATE_LIST_LOAD_FAILED: '無法載入預約資料',
    /**
     * The reclaim line above the list (utils/reanchorPreviewLines reclaimBannerText — W9.5 VR-05,
     * moved verbatim): an ended row (`reason` = its label, null = none known); a 不續報 row
     * (UX-14 — the words of the soft-end-not-renewing hover, no 標記 / 循環); the renewal-specific
     * reason labels the shared ENDING_REASON_LABELS map does not carry.
     */
    RECLAIM_ENDED: (reason: string | null) =>
      reason
        ? `目前狀態：已結束 (${reason})。確認後將恢復為進行中。`
        : '目前狀態：已結束。確認後將恢復為進行中。',
    RECLAIM_NOT_RENEWING: '目前是 不續報。確認後取消 不續報，重新開始續報週期。',
    RECLAIM_REASON_LABELS: {
      完成續報但未上傳Excel: '完成續報但未上傳 Excel',
      課務未通知: '課務未通知',
      期限內未結案: '期限內未結案',
    } as Record<string, string>,
    /**
     * R4.5 (D48 i, D53 m — moved verbatim from SetReservationBody／SetReservationModal and
     * HUB.REVIEW.HANDLE.UNCHANGED): the optional 原因 field under the date list, and the footer line
     * when the pick is the current 最後約課日 (no write) — in 修改最後約課日 and 處理待確認 alike.
     */
    REASON_LABEL: '原因 (選填)',
    REASON_PLACEHOLDER: '例如：家長想保留名額，下學期再排',
    UNCHANGED: '已選目前的最後約課日，未變更',
  },

  /**
   * 名額使用情況 (tasks/todo-seat-usage-facts.md § Business-logic homes 「Every on-screen word」,
   * P17). Every word is copied from the PO-signed mockup docs/tools/renewal-notice-seat-usage-
   * mockup.html (line numbers below) or annex § 三之二 — no new wording (D19: facts only, what
   * was recorded, never why). Builders return exactly the mockup's strings; the view model
   * (utils/seatUsageView.ts) formats dates (YYYY/MM/DD — the house formatDateForDisplay, UX-14;
   * M/D) and passes them in. The 轉入待 Excel
   * tag is the existing TRANSFER_IN_HOLD_LABEL, read as is (P6); the held seat's flag hover is
   * the existing buildLsedMarkerTooltip / RENEWAL_WORKFLOW_CONFIG.SEAT_HOLD.MARKER_TOOLTIP.
   */
  SEAT_USAGE: {
    /** The block title (mockup:500). */
    TITLE: '名額使用情況',
    /**
     * The ? next to the title, one sentence (mockup:500, D8). D48 (PO 2026-10-22 「2. A」): 此名額
     * is named for every window — four of the six send no notice (其他處理, both 登記轉班, 驗證).
     * D76 (PO 2026-10-23): a square is the one hover left in the grid, so the sentence names it alone.
     */
    HELP: '這個家庭帳號的約課與出席：一列一個時段，一格一週。虛線框＝此名額 (這個視窗處理的那個保留名額)；沒有保留名額時 (新時段報名)，虛線框＝此時段；旗子＝最後約課日。滑鼠停在每一格上，看那一堂的日期和狀態。',
    /** Before the data lands (mockup:566, D2). */
    LOADING: '名額使用情況 查詢中…',
    /** No data within FETCH_TIMEOUT_MS, or an error (mockup:615, D2). */
    UNAVAILABLE: SEAT_USAGE_UNAVAILABLE,
    /**
     * D52 (PO 2026-10-22 「6. A」): the link after UNAVAILABLE in the six windows (never on a
     * 學生紀錄 card, D42) — one more read, the buttons wait again (seatUsageBlock.ts `retry`).
     */
    RETRY: '重新查詢',
    /** The block's last line, right-aligned; n = every note of the family (mockup:577, D14). */
    NOTES_BUTTON: (n: number): string => `${SEAT_USAGE_NOTES_WORD} ${n} ${SEAT_USAGE_NOTES_TAIL}`,
    /** The same button as drawn: the word, N as a blue badge, the › (mockup .nbtn / .n, 228-229). */
    NOTES_BUTTON_WORD: SEAT_USAGE_NOTES_WORD,
    NOTES_BUTTON_TAIL: SEAT_USAGE_NOTES_TAIL,
    /** Above the grid, the word only (D21, mockup:1078). */
    TODAY: '今天',
    /**
     * 約課切斷確認 D70 (1) / D71 (i): the red cut line's label above the grid, in the 補正最後約課日
     * window only — {md} = the upload end's M/D, no year (chart mockup § 二 「課表上傳到 1/22」).
     */
    CUT_LINE: (md: string): string => `課表上傳到 ${md}`,
    /** Axis month labels (mockup:985): M月; January after the first column carries its year. */
    MONTH: (m: number): string => `${m}月`,
    MONTH_JANUARY_WITH_YEAR: (y: number): string => `${y}年1月`,
    /** Each cell state's word (mockup ST `w`, 932-943; `out` from annex § 三之二). */
    CELL_LABELS: {
      at: '出席',
      lv: '請假',
      ot: '其他',
      ab: '缺席',
      ns: '未簽到',
      ap: '未簽到',
      sub: '已約・代課',
      cx: '取消',
      bk: '已約',
      no: '沒有約',
      nr: '沒有紀錄',
      nc: '沒有課',
      sc: '停課',
      bs: '已約・停課',
      out: '超出 Excel 上傳範圍',
    } satisfies Record<SeatUsageCellState, string>,
    /** Each cell state's glyph inside the square (mockup ST `g`, 932-943; '' = an empty square). */
    CELL_GLYPHS: {
      at: '✓',
      lv: '假',
      ot: '其',
      ab: '缺',
      ns: '',
      ap: '',
      sub: '代',
      cx: '✕',
      bk: '',
      no: '',
      nr: '',
      nc: '–',
      sc: '–',
      bs: '',
      out: '',
    } satisfies Record<SeatUsageCellState, string>,
    /**
     * The 「(…)」 reason a cell's hover adds (mockup ST `why`, 932-943); states without one: none.
     * `sub` = D54 (PO 2026-10-22): 「原本那堂換成代課」 alone — 代 is drawn only when no 代課 class
     * is linked that day (functions sessionEnrollments/seatUsageGridCells.ts), so 「代課那堂的名單上沒有他」 described ✕, not 代.
     */
    CELL_WHY: {
      ns: '簽到表核可了，沒有選狀態',
      ap: '簽到表還沒核可',
      sub: '原本那堂換成代課',
      nr: '系統沒有這個人這一堂的資料',
      cx: '原本約了，上傳時名單上沒有',
      no: '這堂有開，名單上沒有',
      bs: '名單上有，這堂停課',
    } satisfies Partial<Record<SeatUsageCellState, string>>,
    /** A 沒有約 cell outside the seat's stored-date span (mockup:1013). */
    CELL_WHY_OUTSIDE_STORED_DATES: '不在這個名額存的約課日裡',
    /** A 取消 cell when another name of the account is on that class's roster (annex § 三之二 取消). */
    CELL_WHY_SAME_CLASS: (name: string): string => `同一堂名單上有 ${name}`,
    /** P17 (b): joins 「同一堂名單上有 ○○○」 to the standard reason inside one 「(…)」. */
    WHY_JOIN: '；',
    /**
     * A cell's hover (mockup:1014): 「YYYY/MM/DD (週幾) HH:mm · word (why) · 最後約課日」 — the
     * reason only when there is one, 「 · 最後約課日」 only on the flag's cell.
     */
    CELL_TIP: (
      ymd: string,
      weekday: string,
      time: string,
      word: string,
      why: string | null,
      isLsed: boolean,
    ): string =>
      `${ymd} (${weekday}) ${time}${SEAT_USAGE_SEP}${word}${why ? ` (${why})` : ''}${isLsed ? `${SEAT_USAGE_SEP}${SEAT_USAGE_LSED_WORD}` : ''}`,
    /**
     * The titled box of the other names on the account (D23, mockup:908); a small grey caption
     * since W9 (D65 (a) — the people's headings inside it carry the weight).
     */
    OTHERS_TITLE: '家庭帳號其他人',
    /** Tags (mockup tagHtml 948-954, data 890-916); 此名額 = the dashed frame's label (D66). */
    TAGS: {
      HERE: '此名額',
      ADULT_ONLY: '只有大人',
      SELF: '本人',
      HOLD: SEAT_USAGE_HOLD_WORD,
      /** 候補 tag on the person's heading (mockup:899; D62). */
      WAITLIST: (className: string): string => `候補中 ${className}`,
    },
    /**
     * A seat row's tag word by its 續報 row state (P6 review 3 — the drawn short words; the head of
     * UI_STRINGS.SET_RESERVATION.STATE_LINE with 「續報：」 stripped, pinned by
     * seatUsageTagWords.test.ts). 一般 and 沒有報名紀錄 draw no tag (null); an ended row is tagged
     * by its endingReason in seatUsageView (never 沒有報名紀錄), the seat she moves into by
     * TRANSFER_IN_HOLD_LABEL.
     */
    TAG_BY_ROW_STATE: {
      general: null,
      'pending-notify': '待通知',
      'expired-unnotified': '已過期未通知',
      'notified-awaiting': '已通知·待確認',
      'notified-overdue': '已通知·已逾期',
      'booking-notice-pending': '約課通知待發',
      'not-renewing': '不續報',
      'transferred-out': '已轉出',
      'released-admin-unnotified': '名額已釋出 (課務未通知)',
      'released-expired': '名額已釋出 (期限內未結案)',
      'released-other': '名額已釋出',
      'special-hold': SEAT_USAGE_HOLD_WORD,
      'long-leave': '請長假',
      transferring: '轉出中',
      none: null,
    } satisfies Record<RenewalRowState, string | null>,
    /** 備註 drawer (D5, D13, D24, D25; mockup 1174-1217). */
    DRAWER: {
      /** The kind pill's word per note kind (mockup K, line 1176). */
      KIND_NAMES: {
        track: '追蹤備註',
        bf: '約課備註',
        adm: '課務備註',
        slot: '排課備註',
        tr: '轉班備註',
        wl: '候補備註',
        tea: '老師備註',
        prog: '學習歷程記錄',
        fb: '體驗備註',
      } satisfies Record<SeatUsageNoteKind, string>,
      /** The page a card's meta line adds for the three kinds whose column is just 「備註」 (mockup PLACE, 1192). */
      PLACE: {
        track: '課務總覽 › 續報',
        tr: '課務總覽 › 轉班',
        wl: '課務總覽 › 候補',
      } satisfies Partial<Record<SeatUsageNoteKind, string>>,
      TABS: { NOTES: '備註', TIMELINE: '學期紀錄' },
      ALL: '全部',
      EMPTY: '沒有備註',
      /** The header's 家庭帳號 label before the name chips (mockup:1199). */
      FAMILY_LABEL: '家庭帳號',
      /**
       * Opens the regular 學生紀錄總覽 of the name viewed (D83, PO 2026-10-24; replaces D5's
       * 「家庭帳號 ›」) — the 簽到表 panel's own words for the same window (FAMILY_NOTES_UI.STUDENT_RECORD).
       */
      RECORD_LINK: '學生紀錄 ›',
      /** After the seat holder's name on a chip (mockup:1200), with its leading space. No name
       *  carries a hover (D75, PO 2026-10-23). */
      SELF_SUFFIX: ' · 本人',
      /** A 追蹤備註 entry that cleared the note (mockup:1216). */
      CLEARED: '(清空)',
      /** A 約課備註's dates (mockup:1257-1259): 「M/D、M/D · 共 N 堂」, N = dates.length. */
      BF_DATES: (mds: readonly string[]): string =>
        `${mds.join('、')}${SEAT_USAGE_SEP}共 ${mds.length} 堂`,
      /** A card's meta line (mockup:1216): by, then PLACE, then the BF dates — each only when present. */
      CARD_META: (by: string, place: string | null, bfDates: string | null): string =>
        [by, place, bfDates].filter((part): part is string => !!part).join(SEAT_USAGE_SEP),
    },
    // ── Round 2 (tasks/todo-renewal-pending-chart.md § Build round 2 — the block opens every
    //    待確認／續報／轉班 window; mockup v4 docs/tools/hub-chart-windows-mockup.html, D31) ──
    /**
     * D33 (ii) / R-P3 (moved up from STRIP, R1.2): a named class with no booking or attendance
     * square in range draws this instead of squares (D9 (a)／D10 — the strip's words).
     */
    NO_BOOKING: '還沒有約課',
    /**
     * D28 / D33 (ii) (moved up from STRIP, R1.2): the role chip that heads a class line — 原班 grey,
     * 新班 green (D19 (a) 決定 C `.role`).
     */
    ROLE: { SOURCE: '原班', TARGET: '新班' },
    /**
     * D32: the dashed frame's label in the per-student mode (a 待確認 › 新時段 row has no seat yet) —
     * the time slot the window is about; TAGS.HERE (此名額) stays the label of a held seat.
     */
    FOCUS_LABEL: '此時段',
    /** D33 (i) / D40: the identity line's stage pill — 「續報 › 待通知」 (family › tab). */
    STAGE_PILL: (family: string, tab: string): string => `${family} › ${tab}`,
    /**
     * D33 (ii) / R-P13 / R-P14: a class line's second row (small grey host text) — the seat's own
     * dates; the seat-state tag stays on row 1 (R-P1, said once).
     */
    LINES: {
      HOLD: (lsedMd: string, holdMd: string, daysLeft: number): string =>
        `${SEAT_USAGE_LSED_WORD} ${lsedMd}${SEAT_USAGE_SEP}保留至 ${holdMd} (剩 ${daysLeft} 天)`,
      HOLD_EXPIRED: (lsedMd: string, holdMd: string): string =>
        `${SEAT_USAGE_LSED_WORD} ${lsedMd}${SEAT_USAGE_SEP}保留至 ${holdMd} 已過`,
      ENDED: (md: string): string => `${md} 結束`,
    },
  },

  /**
   * 2026-10-14 (續報・轉班 完成規則 W9.5, review UX-16 / VR-05): the two words of every confirm
   * bubble (n-popconfirm) in the 最後約課日 and 轉班 windows — the house rule: confirm says 確認,
   * cancel says 取消 (K-02: 取消 sends nothing). One word per thing, read by every such bubble.
   */
  CONFIRM_BUBBLE: {
    OK: '確認',
    CANCEL: '取消',
  },

  /** 價目與方案設定 (PricingEditorModal) — review F-12. BE refusal: UI_STRINGS.SIGNUP_PRICING. */
  SIGNUP_PRICING: {
    TERM_PLAN_LOCKED:
      '學期方案不能刪除：續報通知、轉班繳費、候補推薦訊息的學期價格都讀它 (名稱與價格可以改)',
    TERM_PLAN_MISSING:
      '四個學期方案 (游泳 週末／平日、FMS 親子／獨立) 不能刪除：續報通知、轉班繳費、候補推薦訊息的學期價格都讀它們。',
  },

  TIMESLOT_PAGE: {
    /** Tooltip on the "+N" chip beside the 約課數 pill — N = other-timeslot count. */
    OTHER_TIMESLOT_TOOLTIP: '另有 {{n}} 個約課時段',
  },
  /** OtherHandlingModal (課務總覽 續報 / 待確認, 排課時段表) — names the student it acts on. */
  OTHER_HANDLING: {
    /** Review P6-7 (UI_DESIGN_SPEC §7a-1 item 7): a dialog that commits 不續報 names who. */
    TITLE: (studentName: string) => (studentName ? `其他處理 · ${studentName}` : '其他處理'),
    /**
     * 不續報 › 已改報其他時段 (PO 2026-09-29, design § 2c): the student moved to another
     * slot. The 新時段 field is required; it starts with the 已另報 slot when the row carries
     * that fact. Written as endingReasonDetail '改報其他時段' + endingReasonNote = the field.
     */
    SWITCHED_LABEL: '已改報其他時段',
    /**
     * The field (M2 review): a visible label (also its accessible name), the placeholder
     * and the error. A text field: neither host holds the live slot list without a new load.
     */
    SWITCHED_SLOT: '新時段',
    SWITCHED_SLOT_PLACEHOLDER: '必填，例：週二 16:00 小水獺班',
    SWITCHED_SLOT_REQUIRED: '新時段為必填',
    /** Under the field while 已改報其他時段 is chosen — what 確認 does. */
    SWITCHED_CONSEQUENCE: '確認後：這一時段記為不續報、名額釋出，列移到 近期未處理',
    /**
     * The 不續報 option's explanation on the tab variant (M2 review — names the third detail).
     * 2026-10-14 (W9.5 UX-17): the radio's hover, the label stays short (was an inline note).
     */
    NOT_RENEWING_TITLE: '家長不繼續、暫時休息，或已改報其他時段',
    /** W9.5 UX-17: 設為暫時保留名額's hover (was the inline note, OtherHandlingModal). */
    SPECIAL_HOLD_TITLE: '特殊暫時保留需求，無期限',
    /**
     * 轉為候補 (PO 2026-10-02, Part 3): the tab variant's choice on 續報 › 已過期未通知 / 待通知
     * / 已通知·待確認 rows (enableConvertToWaitlist). 確認 closes this window and opens the
     * 候補 window pre-filled from the row (useRenewalToWaitlist).
     */
    CONVERT_TO_WAITLIST: '轉為候補',
    /** W9.5 UX-17: the radio's hover (the 轉班 window keeps the note: HUB.TRANSFER.OTHER_NOTE). */
    CONVERT_TO_WAITLIST_TITLE: CONVERT_TO_WAITLIST_MEANING,
    /**
     * 2026-10-13 (續報・轉班 完成規則 W3.3, F-02 / D-12): 已通知·待確認 rows only — opens the
     * 再次提醒家長 window (the row's main button's own words, one word per thing); the hover
     * says what the window does. Every mark there is recorded (第 N 次再提醒, W3.1).
     */
    REMIND_PARENT: HUB_BUTTON_KIND.remindParent.label,
    REMIND_PARENT_TITLE: '訊息帶目前的 保留至；已再提醒過也可以再提醒，每一次都記在 學期紀錄',
    /**
     * 排課時段表 › 移除保留名額 when the row carries 已另報 (M2 review): the fact, at the top
     * of the window. `md` = the other enrollment's first class.
     */
    ELSEWHERE_LINE: (md: string, slot: string) => `此學生 ${md} 起在 ${slot} 有學期報名`,
  },
  /**
   * 課務總覽 » 候補 分頁 — filter bar (2026-09-11). 時段 and 類型 sit beside the
   * existing 班級 select and follow its wording: a 「全部」 first option, label
   * with a full-width colon. 時段 values are the teacher-agnostic 候補 slot
   * ("週六 12:10-13:00"), so the select needs a specific 班級 to be meaningful.
   */
  WAITLIST_FILTERS: {
    GROUP_LABEL: '班級：',
    TIMESLOT_LABEL: '時段：',
    TYPE_LABEL: '類型：',
    ALL_OPTION: '全部',
    TIMESLOT_NEEDS_GROUP: '請先選擇班級',
    /** Families listed under a 時段 option (same unit as the 候補 card's 共 N 位). */
    OPTION_COUNT: (n: number) => `${n} 位`,
    /** 時段 menu header: the option counts follow the other filters, unlike the 時段頁 「候補 N」. */
    COUNT_NOTE: '人數依目前的類型與搜尋條件計算',
    /**
     * Row hint for a family still stored under the class's pre-rename name.
     * 「原班名」 is the existing term (CLASS_IDENTITY: 單堂課紀錄仍照 Excel 顯示原班名).
     */
    FORMER_GROUP: (group: string) => `原班名：${group}`,
  },
  /**
   * 候補 推薦訊息 — the 課務-facing bits only.
   *
   * ⚠ The parent-facing WORDING is NOT here. It lives in `signupSteps.ts` as the
   * `WAITLIST_OFFER` step's four variants, so a supervisor edits it through the
   * same 編輯預設文案 panel as every other template. What remains below is the
   * VALUES those bodies interpolate (class line, date lines, 這兩個日期) plus the
   * hints and blocked-copy reasons the parent never sees.
   *
   * Writing rules that apply here (`feedback_parent_message_writing`):
   * no internal lookup fields, no restating what the parent said, plain tone.
   * ⚠ Tone still to be reviewed by the PO against the 預存回覆 sheet.
   */
  WAITLIST_OFFER: {
    /** What we are offering, per stored `type`. */
    TYPE_WORD: {
      體驗: '體驗課',
      體驗三堂: '體驗三堂',
      單堂: '單堂課',
      單堂補課: '補課',
      正式課程: '正式課程',
    } as Record<string, string>,
    // No greeting line (PO 2026-09-21): the 課務 is replying inside an existing
    // LINE conversation, so 「…您好，這裡是 Oovi。」 restated context the parent
    // already had. The message opens straight on the offer.
    /** One line naming the class, so the parent knows which waitlist this is. */
    CLASS_LINE: (slotLabel: string, className: string, teacher: string) =>
      teacher ? `${slotLabel} ${className} (${teacher} 老師)` : `${slotLabel} ${className}`,
    DATE_LINE: (label: string) => `・${label}`,
    // The wide gap after 第 N 堂 comes from IDEOGRAPHIC_SPACE (see its doc):
    // emitting U+3000 from its code point keeps the source free of invisible
    // characters. Same output, lint-clean.
    DATE_LINE_NTH: (n: number, label: string) => `・第 ${n} 堂${IDEOGRAPHIC_SPACE}${label}`,
    /**
     * 「這個日期」/「這兩個日期」/「這三個日期」. offerCount is capped at 3 by
     * OFFER_RULES, so this covers every reachable count; anything else falls
     * back to the plain 「這些日期」.
     */
    DATES_WORD: { 1: '這個日期', 2: '這兩個日期', 3: '這三個日期' } as Record<number, string>,
    DATES_WORD_FALLBACK: '這些日期',
    /** Blocks 複製訊息 while a required link/plan is missing — never a placeholder. */
    COPY_BLOCKED: '請先挑選付款方式才能複製',
    /** The 正課 payment-method picker's own label. */
    PLAN_LABEL: '學期方案',
    PLAN_PLACEHOLDER: '請挑選學期方案',
    PLAN_HINT_PICK:
      '正課走銀行匯款，請挑選方案，訊息才會帶出金額與帳號；家長用剩餘票券時選「不需付款」。',
    /**
     * Q1 (PO 2026-09-30: 「some times require if they run out of ticket, sometimes not, it's
     * human decision」): the 正課 picker's first option — the family books with the tickets
     * it has left, so the message (候04) carries no amount and no account. Never pre-selected.
     */
    PREPAID_OPTION: '不需付款 (使用剩餘票券)',
    PREPAID_HINT: '不需付款：訊息不帶金額與帳號，家長確認後用剩餘票券幫他預約。',
    /**
     * A 候補 that came from a 轉班 (M7, derived from the PO's 「it's human decision」): the
     * 正課 picker starts empty and says why — the 課務 chooses 不需付款 or a 學期方案.
     */
    FROM_TRANSFER_HINT: '這家由轉班轉入：請選 不需付款 或 學期方案',
    /**
     * The same for a 候補 that came from a 續報 row (續報 › 其他處理 › 轉為候補 — its
     * convertedFromEnrollmentId; fix round 1 decision 6, from the same 「human decision」).
     */
    FROM_RENEWAL_HINT: '這家由續報轉入：請選 不需付款 或 學期方案',
    /**
     * 單堂 picker (PO 2026-09-21). 預設 單堂 原價；任選 6 堂 是同一個 單堂 的
     * 票券包（折扣），不是另一種課 —— 見 OFFER_SINGLE_BUNDLE_PLAN_IDS 的 命名鐵律。
     */
    SINGLE_HINT_BUNDLE: '可選「單堂」、或「任選 6 堂」(同樣是單堂課的票券包，買 6 堂比較優惠)。',
    /** Leads the 任選 6 堂 rows in the 付款方式 picker, in this tab's wording. */
    BUNDLE_TERM: '任選 6 堂 (匯款)',
    COPY_BLOCKED_PLAN: '請先挑選學期方案 (或「不需付款」) 才能複製',
    /**
     * A link is picked but the box text lacks it — usually the 課務 deleted the URL line,
     * rarely a default without {{付款連結}} (家長訊息視窗 review A7: the in-window
     * 「編輯預設文案」 editor is gone; defaults are edited only in 招生客服說明).
     */
    COPY_BLOCKED_LINK_DROPPED: '訊息裡沒有付款連結，無法複製',
    LINK_HINT_DROPPED:
      '訊息裡沒有付款連結——請按「還原預設文案」；若預設文案本身缺少 {{付款連結}}，請主管到 招生客服說明 補上。',
    LINK_HINT_PICK: '這個班級／類型沒有預設連結，請自行挑選後再複製。',
    LINK_HINT_NONE: '目前沒有可用的付款連結——請到 報名流程 → 付款連結管理 建立或啟用。',
    LINK_HINT_INACTIVE:
      '這個票種還沒有可用的連結 (未建立或已停用)——請到 報名流程 → 付款連結管理 處理，或先改挑別的。',
    /** Suffix on a ticket the supervisor has not wired up yet (leading space: half-width rule). */
    LINK_UNAVAILABLE: ' (尚未建立連結)',
    LINK_HINT_MAKEUP: '補課是從已購買的票券扣堂，通常不需要再付一次款。',
    /** 推薦訊息 title when the entry has no contact name (half-width brackets). */
    NO_NAME: '(未填姓名)',
    /** The offer this draft described no longer exists (entry deleted / class closed). */
    GONE: '這筆推薦已經不存在了 (候補紀錄可能被刪除，或這個班級已無名額)。請關閉後重新挑選。',
    /** Shown when the offer moved under an edited draft (review finding #1). */
    OUTDATED:
      '⚠ 這筆候補的可提供內容已經變了 (可能是有人改了資料，或跨日重算)。你的修改還留著；要換成最新版本請按「還原預設文案」。',
    /** Shown to the 課務 above the draft, never sent: the run is incomplete. */
    SHORT_WARNING: (found: number, required: number) =>
      `⚠ 目前只湊得出 ${found} 堂 (需要 ${required} 堂)。送出前請先確認要不要改約其他時段。`,
  },
  /**
   * 候補 保留名額 → 已安排 (PO 2026-09-30; contract main repo
   * tasks/todo-waitlist-placement-hold.md, design docs/tools/waitlist-placement-hold.html
   * v3). Button labels live in HUB_BUTTON_KIND (holdSeat / scheduled / cancelHold /
   * giveUp); the 候補 panel's title and caption in HUB_PANEL_VARIANT.waitlistHolding.
   */
  WAITLIST_PLACEMENT: {
    // ── 候補 row, stage 2 (保留中) ──
    /** The name's second line: 「9/29 保留 · Lucy」. */
    PLACED_LINE: (md: string, by: string) => (by ? `${md} 保留 · ${by}` : `${md} 保留`),
    /** Leads the held dates of a per-week hold (體驗 / 單堂 / 補課). */
    DATES_LABEL: '保留',
    /** The 名額保留期限 column's line under 「剩 N 天」: 「保留至 11/11 (三)」 (D2). */
    UNTIL: (md: string) => `保留至 ${md}`,
    // ── 名額保留期限 column (PO 2026-10-31, D2 / D6 — useWaitlistPlacements/holdCount; the
    //    header is UI_STRINGS.HUB.COLUMNS.RELEASE_DEADLINE, the units UI_STRINGS.HUB.ROW) ──
    /** An expired hold: 「0 天」 over 「11/11 已到期」 (the 轉班 wording; said once, D6). */
    COUNT_EXPIRED_ON: (md: string) => `${md} 已到期`,
    /** The last day: 「今日 到期」 (the 轉班 count's words). */
    COUNT_DUE: '到期',
    // ── 預計開始上課日 of a 正課 hold (PO 2026-10-31, D1 / D4 / D14 — utils/waitlistStartDate) ──
    START_LABEL: '預計開始上課日',
    /** The checkbox beside the date: ticked = null is stored (還沒確定). */
    START_UNSURE: '還沒確定',
    /**
     * Neither a date nor 還沒確定 — the 保留名額 window's 確認 waits for one; 編輯 says it when the
     * hold's stored date or 還沒確定 was cleared (its 儲存 waits then; a hold never asked stays quiet).
     */
    START_MISSING: '還缺 預計開始上課日',
    /** The blue pill under 類型 and after a name on the 排課時段表 cards: 「11/15 起」. */
    START_PILL: (md: string) => `${md} 起`,
    /** A 正課 hold with no date (null or never asked) — amber while unexpired, else grey (B11). */
    START_UNSET: '開始日未定',
    /**
     * The amber 開始日未定 pill's hover (D11 h, UX-17): what it causes, said nowhere else on the
     * row — the hold's 暫＋1 shows in every week.
     */
    START_UNSET_HELD_TITLE: '排課時段表 每一週都顯示 暫＋1',
    /**
     * The muted line when a picked date stopped fitting the class (D11 b) — it was cleared, never
     * moved: the Taipei day passed it, the class changed, or its week turned out 停課.
     * `label` = 「11/14 (六)」.
     */
    START_NOTE_PAST: (label: string) => `${label} 已過`,
    START_NOTE_NOT_CLASS_DAY: (label: string) => `${label} 不是這班的上課日`,
    START_NOTE_CANCELLED: (label: string) => `${label} 停課`,
    /**
     * A hold placed before the latest 課表 upload (deep review Q5 a) — a grey hint that never
     * ends anything: the family may be in the list now.
     */
    UPLOADED_HINT: (md: string) => `${md} 課表已上傳，請核對名單`,
    /** A per-week hold's held dates that are already past (deep review Q5 b) — grey. */
    DATES_PAST: (labels: readonly string[]) => `${labels.join('、')} 已過`,
    // ── 「已在 Excel」 (PO 2026-10-01 § K): LINKED holds only — a reminder that ends nothing ──
    /** A linked 正課 hold whose student the Excel put on the class list — green. */
    EXCEL_TERM: '✓ 已在 Excel 名單 — 請按「已安排」',
    /**
     * The same on the 排課時段表 header 保留名額 card, whose list above shows the student:
     * 已安排 ends the hold, so N/M becomes (N − 1)/M — the caller passes N − 1.
     */
    EXCEL_TERM_LIST: (reserved: number, max: number) =>
      `✓ 已在上面的名單 — 按「已安排」後變 ${reserved}/${max}`,
    /** Every held day is in the Excel (one day: no 都) — green. */
    EXCEL_DATES_ALL: (labels: readonly string[]) =>
      `✓ ${labels.join('、')} ${labels.length > 1 ? '都' : ''}已在 Excel — 請按「已安排」`,
    /** Some held days are in the Excel — green, followed by EXCEL_DATES_MISSING. */
    EXCEL_DATES_SOME: (labels: readonly string[]) => `✓ ${labels.join('、')} 已在 Excel`,
    /** The upcoming held days the Excel does not have yet — amber, right after the green. */
    EXCEL_DATES_MISSING: (labels: readonly string[]) => `；${labels.join('、')} 還沒有`,
    /** A held day the Excel has — its chip. */
    EXCEL_DAY: (label: string) => `✓ ${label}`,
    /** After UPLOADED_HINT on an unlinked hold: why there is no ✓ (never matched by name). */
    UNLINKED_NO_CHECK: ' (未連結學生，無法自動核對)',
    // (EXPIRED 「保留已到期」 retired 2026-10-31, D6: the 名額保留期限 column says it once —
    //  COUNT_EXPIRED_ON; the what-to-do EXPIRED_HINT stays.)
    /** ⋮ on a stage-1 row: today's 已安排 without a hold (Excel already has the family). */
    DIRECT_ARRANGE: '直接已安排',
    /** A held row's 類型 / 班級 are locked — the server refuses the change (reason 'holding'). */
    HOLDING_LOCKED: '保留中，請先取消保留',
    /**
     * 編輯 of a held row: 班級 is locked (the server refuses the change) — one line under
     * the 班級 select. 可上時段 stays editable (D-9, PO 2026-10-02) — the muted line under
     * 可上時段 says so (HOLDING_SLOTS_EDITABLE; fix round 1, UX review LOW 13).
     */
    HOLDING_LOCKED_EDIT: '保留中：班級無法修改 (請先取消保留)',
    HOLDING_SLOTS_EDITABLE: '可上時段可以加減，保留的名額不受影響',
    /**
     * The same lock under a held row's 類型 face, visible (a disabled button's title shows on
     * hover only — deep review W13 f); short enough for the 90px 類型 column.
     */
    TYPE_LOCKED_HINT: '取消保留後可修改',
    /**
     * 保留已到期 — what to do (deep review W13 d), and — a 正課 hold whose family is not on the
     * class list yet — the day the 03:30 run cancels it by itself (audit item 6, PO 2026-10-06 —
     * PLACEMENT_EXPIRED_RELEASE_DAYS after 保留至; functions programWaitlist/holdExpiryRelease).
     * `releaseMd` null = the run leaves this hold (a 體驗 / 單堂 hold, or 已安排 is due).
     */
    EXPIRED_HINT: (releaseMd: string | null) =>
      releaseMd
        ? `名額已釋出，請按「已安排」或「取消保留」(${releaseMd} 凌晨自動取消保留，回到候補)`
        : '名額已釋出，請按「已安排」或「取消保留」',
    ARRANGE_TITLE: '標記為已安排',
    // ── 保留紀錄 (D32, PO 2026-10-07 「do B」): the 備註 cell's lines under the note — a hold
    //    of this row that ended WITHOUT 已安排 (useWaitlistPlacements/holdHistory) ──
    /** The 03:30 run gave the row back (保留已到期 — audit item 6 / D22). */
    HISTORY_SYSTEM: (endedMd: string) => `${endedMd} 系統：保留已到期，自動取消`,
    /** 課務 pressed 取消保留 — the name stored on the hold (none stored: no name). */
    HISTORY_CANCEL: (endedMd: string, by: string) =>
      by ? `${endedMd} ${by}：取消保留` : `${endedMd} 取消保留`,
    /** What a 正課 hold held: 「9/8 保留 週二 10:00 小鴨班 (Phyllis)，至 9/22」. */
    HISTORY_HELD_TERM: (placedMd: string, cls: string, untilMd: string) =>
      `${placedMd} 保留 ${cls}，至 ${untilMd}`,
    /** What a 體驗類 hold held: 「9/15 保留 週二 10:00 小鴨班 (Phyllis) 9/22」. */
    HISTORY_HELD_DATES: (placedMd: string, cls: string, days: string) =>
      `${placedMd} 保留 ${cls} ${days}`,
    HISTORY_LINE: (outcome: string, held: string) => `${outcome} (${held})`,
    /** More such holds than the cell shows — every one is in the lines' hover. */
    HISTORY_MORE: (n: number) => `另有 ${n} 次`,
    /**
     * `classLabel`: the held class (deep review W13 e) — 「週日 13:45 小鴨班 (Carol)」. The 候補
     * tab and — with its 5-s 復原 since deep review Q6 — the 排課時段表 card.
     */
    ARRANGE_CONFIRM: (name: string, classLabel = '', days: readonly string[] = []) =>
      `${waitlistExcelQuestion(name, classLabel)}標記為已安排後${days.length > 0 ? ` ${days.length} 天保留 (${days.join('、')}) 一起結束` : '保留結束'}，此筆候補將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
    CANCEL_TITLE: '取消保留',
    /**
     * `days` (a date hold — PO 2026-10-01 § I): one press ends every held day, so the confirm
     * names them; a 正課 hold keeps the sentence.
     */
    CANCEL_CONFIRM: (name: string, days: readonly string[] = []) =>
      days.length > 0
        ? `取消 ${name} 保留的 ${days.length} 天 (${days.join('、')})？名額會釋出，此筆候補回到一般候補。`
        : `取消 ${name} 的保留名額？名額會釋出，此筆候補回到一般候補。`,
    /**
     * The 排課時段表 card asks inside itself (PO 2026-10-01, option A — mockup § H): the two
     * buttons of the confirm under the family's line; the main one says only 「確認」.
     */
    ASK_CANCEL: '取消',
    ASK_CONFIRM: '確認',
    /** ⋮ 放棄 on a 保留中 row — names the family like its siblings (deep review W13 b). */
    GIVE_UP_CONFIRM: (name: string) =>
      `將 ${name} 標記為放棄？保留名額一併取消，此筆候補將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
    /** ⋮ 刪除 on a 保留中 row — it ends the hold as 放棄 does (deep review W13 a). */
    DELETE_CONFIRM_HELD: (name: string) =>
      `刪除 ${name} 的候補紀錄？保留名額一併取消，此筆將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
    /** ⋮ 直接已安排 — the Excel must already have the family; no seat is held (W13 c). */
    DIRECT_ARRANGE_CONFIRM: (name: string) =>
      `${waitlistExcelQuestion(name)}直接標記為已安排 (不保留名額)，此筆候補將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
    PLACED_TOAST: (name: string) => `已保留名額：${name}`,
    /** Also the 排課時段表 card's 復原 toast, next to 「復原 (N 秒)」 (deep review Q6). */
    ARRANGED_TOAST: (name: string) => `已安排：${name}`,
    CANCELLED_TOAST: (name: string) => `已取消保留：${name}`,
    /** The card's 復原 went through: the row and its hold are back (deep review Q6). */
    UNDONE_TOAST: (name: string) => `已復原：${name}`,
    // ── 繼續候補其他班級 (PO 2026-09-30, decision 9 — the row stays, no 復原) ──
    /** 已安排 on a held row that keeps waiting in other classes (候補 tab). */
    ARRANGE_CONFIRM_KEEP: (name: string, slots: readonly string[], classLabel = '') =>
      `${waitlistExcelQuestion(name, classLabel)}標記為已安排後保留結束，此筆候補繼續候補：${slots.join('、')}。`,
    /** The same words on the 排課時段表 card. */
    ARRANGE_CONFIRM_CARD_KEEP: (name: string, slots: readonly string[], classLabel = '') =>
      `${waitlistExcelQuestion(name, classLabel)}標記為已安排後保留結束，此筆候補繼續候補：${slots.join('、')}。`,
    ARRANGED_KEEP_TOAST: (name: string, slots: readonly string[]) =>
      `已安排：${name} (繼續候補 ${slots.join('、')})`,
    /** 保留名額 with 繼續候補 ticked — the same words as ARRANGED_KEEP_TOAST (deep review W14). */
    PLACED_KEEP_TOAST: (name: string, slots: readonly string[]) =>
      `已保留名額：${name} (繼續候補 ${slots.join('、')})`,
    /** A 保留中 row's grey line: the classes the family still waits in (deep review W14). */
    KEEP_LINE: (slots: readonly string[]) => `繼續候補：${slots.join('、')}`,
    /** An open row's 登記 cell after 已安排 kept it waiting: 「10/12 已安排 週日 13:45」. */
    ARRANGED_HISTORY: (md: string, slot: string) =>
      slot ? `${md} 已安排 ${slot}` : `${md} 已安排`,
    /** handleError contexts. */
    ERR_PLACE: '保留名額',
    ERR_END: '結束保留名額',
    /** The card's 復原 — the 候補 tab's words (useProgramWaitlist undoComplete). */
    ERR_UNDO: '復原候補紀錄',
    // ── 保留名額 window (WaitlistPlaceModal) ──
    MODAL_TITLE: (name: string, typeLabel: string) => `保留名額 · ${name} (${typeLabel})`,
    INTRO_TERM: '家長答應了、要在 BF 約課：先佔這一班的名額，Excel 上傳、確認後再按「已安排」',
    INTRO_DATES:
      '家長答應了、要在 BF 約課：先佔這一班那幾週的名額，Excel 上傳、確認後再按「已安排」',
    OTHER_CLASSES: '其他班級…',
    OTHER_CLASSES_HINT: '同一系列所有時段',
    OTHER_CLASSES_PLACEHOLDER: '選擇班級',
    NO_ROW_CLASSES: '這筆候補的可上時段目前沒有開課，請從其他班級選擇',
    /** The chosen class's tag, when the row's other classes can keep waiting (decision 9). */
    HOLD_THIS: '保留這班',
    /** The yellow checkbox tag on the row's other classes — default unticked (PO 9a). */
    KEEP_WAITING: '繼續候補',
    SEATS: (reserved: number, max: number) => `保留名額 ${reserved}/${max}`,
    SEATS_FULL: (reserved: number, max: number) => `保留名額 ${reserved}/${max} 已滿`,
    DATES_PROMPT_OFFER: '勾家長實際約的日期 (推薦訊息的日期已先勾好，可改)',
    DATES_PROMPT: '勾家長實際約的日期',
    WEEK_FULL: '額滿',
    /**
     * § L (PO 2026-10-01): the label before a 正課 class's count pill (the 候補 row's
     * 「保留名額 [6/8]」), and the red word after a full one.
     */
    SEATS_LABEL: '保留名額',
    SEATS_FULL_WORD: '已滿',
    /**
     * Fix round 1 (L7 / UX LOW 19, D-8): the chosen 正課 class already counts this row's linked
     * student (a seat or another hold) — the pill may read 已滿, but this hold adds nobody, so
     * there is no 額滿 line and no 超收確認. One line says why.
     */
    SELF_SEATED_NOTE: '這位學生已算進這一班的保留名額：保留不會多佔名額',
    /**
     * § L: the window's note under the weeks when the class has 候補正課名額暫留 — they close no
     * week; each one's 暫＋1 starts at its own 預計開始上課日 (WS-4, PO 2026-10-31 D1): `start`
     * = START_PILL 「11/14 起」 or START_UNSET (every week from today).
     */
    WINDOW_TERM_NOTE: (holds: readonly { name: string; start: string }[]) =>
      `暫＋${holds.length} ＝ ${holds.map((h) => `${h.name} (${h.start})`).join('、')} 正課名額暫留中；從開始那週起，每週多 1 位。`,
    SHOW_MORE_WEEKS: '顯示更多週次',
    SHOW_FEWER_WEEKS: '只顯示近期週次',
    PICKED: (n: number) => `已勾 ${n} 天`,
    /** 體驗三堂 with ≠ 3 ticked — a note, never a block (PO 2026-09-30). */
    EXPECTED_NOTE: (typeLabel: string, expected: number, picked: number) =>
      `${typeLabel} 通常是 ${expected} 堂，目前勾了 ${picked} 堂 (仍可送出)`,
    WEEKS_LOADING: '載入這一班的週次…',
    /** A failed week load — 重試 sits right after it (deep review W12). */
    WEEKS_FAILED: '無法載入這一班的週次',
    /**
     * A 正課 window shows no weeks: it loads the class's 停課 weeks for the 預計開始上課日 calendar
     * (D11 h, UX-9) — on failure a 停課 day may still be clickable.
     */
    WEEKS_LOADING_TERM: '載入這一班的停課週次…',
    WEEKS_FAILED_TERM: '無法載入這一班的停課週次',
    WEEKS_RETRY: '重試',
    WEEKS_EMPTY: '這一班接下來沒有可約的週次',
    NEED_CLASS: '請先選擇班級',
    /** Same words as the server's refusal (functions UI_STRINGS.WAITLIST_PLACEMENT.UNKNOWN_TYPE). */
    UNKNOWN_TYPE: (type: string) => `這筆候補的類型「${type}」無法保留名額，請先修改類型。`,
    NEED_DATES: '請至少勾選一天',
    // ── 排課時段表 (header 保留名額 card + 總計 ＋k card) ──
    // § L (PO 2026-10-01 「ok done, go」): two kinds, one name and one look each on every
    // surface — filled violet = 候補日期保留 (counted in its weeks), white + dashed violet =
    // 候補正課名額暫留 (a 正課 hold: in the header 保留名額; per week a reminder, 暫＋k).
    CARD_GROUP_DATES: '候補日期保留',
    CARD_GROUP_TERM: '候補正課名額暫留',
    /** The dashed chip of a week's 正課 holds (總計, the 保留名額 window). */
    TERM_CHIP: (n: number) => `暫＋${n}`,
    /** The dashed tag on a 保留中 正課 row. */
    TERM_ROW_TAG: '正課名額暫留',
    /**
     * The grey line under a 候補 row's per-week class line. `starts`: one label per hold
     * (START_PILL / START_UNSET — useWaitlistPlacements/holdCount termHoldStarts); a label
     * several holds share is named once (WS-4).
     */
    TERM_CLASS_LINE: (starts: readonly string[]) =>
      `這班有 ${starts.length} 位 候補正課名額暫留 (${[...new Set(starts)].join('、')})`,
    /** 「9/29 保留 · Lucy · 至 10/13」 */
    CARD_META: (md: string, by: string, untilMd: string) =>
      [`${md} 保留`, by, `至 ${untilMd}`].filter(Boolean).join(' · '),
    LINKED: '已連結學生',
    UNLINKED: '未連結學生',
    CARD_PIN_HINT: '點一下「保留名額」固定這張卡，才能按按鈕',
    SUMMARY_TITLE: (md: string) => `${md} 候補保留`,
    SUMMARY_COUNT: (n: number) => `共 ${n} 位`,
    /** The chip as shown (「＋3」 / 「暫＋1」) — parallel to CARD_PIN_HINT (PO 2026-10-01 § J / § L). */
    SUMMARY_PIN_HINT: (chip: string) => `點一下「${chip}」固定這張卡，才能按按鈕`,
    /** A date hold's line on the card: 「保留 3 天：」 + its days (PO 2026-10-01 § I). */
    HELD_DAYS_PREFIX: (n: number) => `保留 ${n} 天：`,
    /** The 總計 chips' accessible name — the kinds the week has (§ L). */
    SUMMARY_TRIGGER_LABEL: (dated: number, term: number) =>
      [dated > 0 ? `候補日期保留 ${dated} 位` : '', term > 0 ? `候補正課名額暫留 ${term} 位` : '']
        .filter(Boolean)
        .join('、'),
  },
  /**
   * Narrowed-list banner + filtered empty state — the 課務總覽 pattern
   * (TransferRequestsTab: 「目前只顯示符合條件的 N 筆紀錄（共 M 筆）」 + 「顯示全部 (M)」).
   * A filtered list must always say it is filtered and offer the way back.
   */
  FILTER_RESULT: {
    /** Hub UI W5.3 / text-fix B5: half-width brackets (also 財務 › 待付款). */
    SHOWING: (shown: number, total: number) =>
      `目前只顯示符合條件的 ${shown} 筆紀錄 (共 ${total} 筆)`,
    SHOW_ALL: (total: number) => `顯示全部 (${total})`,
    EMPTY_WAITLIST: '沒有符合條件的候補紀錄。',
    EMPTY_PAYMENT_QUEUE: '沒有符合條件的待付款。',
  },
  /**
   * 2026-09-28 (Hub UI redesign, W0.3) — 課務總覽 shared strings. Every band label is
   * built from the SAME constant that puts a row in that band (待通知:
   * FrontendConfigUtils.renewalPendingZone; 已通知·待確認: lockEscalation; 約課通知待發:
   * successNotifyAge — utils/renewalRowEscalation.ts), so moving a threshold moves the
   * words with it. Captions carry no trailing 。 (text-fix C8).
   */
  HUB: {
    /**
     * R4.5 (D46, D48 f, D49 f, D53 e): the 課務總覽 main tabs a window's row sits under, as the tab
     * bar names them — the family word of the identity line's stage pill 「續報 › 待通知」
     * (SEAT_USAGE.STAGE_PILL). 待確認 is its page's own title (one page under that tab). Not
     * NOTIFY.SOURCE_LABEL: that is a notice's 來源, another fact that happens to share the words.
     */
    FAMILY: {
      review: HUB_PAGE_IDENTITY.lsedReview.title,
      renewal: '續報',
      transfer: '轉班',
    },
    /**
     * 課務總覽 top tabs (體驗回饋 → 課務總覽 D1／D3, 2026-11-01): the five 名額・約課 tabs and
     * the 課後 tab 體驗回饋. The fifth tab's label lives in HUB.NOTIFY.TAB (約課通知, D3) because
     * that block owns the whole 約課通知 page; config/hubTabs.ts assembles the six.
     */
    TABS: {
      LSED_REVIEW: '待確認',
      RENEWAL: '續報',
      TRANSFER: '轉班',
      WAITLIST: '候補',
      TRIAL_FEEDBACK: '體驗回饋',
    },
    /** The two groups of the top tabs (D1): 名額・約課 (the five above), 課後 (體驗回饋, at the right). */
    TAB_GROUPS: { SEATS: '名額・約課', AFTER: '課後' },
    /**
     * The dot on the 體驗回饋 top tab (D13, spec § 5.3; mockup § 四 「橘點 = 課務 這裡有事要做；
     * 紅點：待通知家長 有人到第 4 天」) — its hover and screen-reader name. Red = the 待通知家長 red
     * band's own day (BANDS.NOTIFY.red.caption).
     */
    TRIAL_FEEDBACK_DOT: {
      orange: '課務有事要做',
      red: `待通知家長 有人到第 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS + 2} 天`,
    },
    BANDS: {
      /** 待通知 — red < RENEWAL_LASTCHANCE_DAYS ≤ amber < RENEWAL_IDEAL_WINDOW_DAYS ≤ green. */
      PENDING: {
        red: {
          label: `剩 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_LASTCHANCE_DAYS - 1} 天內`,
          caption: '最後機會',
        },
        amber: {
          label: `剩 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_LASTCHANCE_DAYS} 到 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_IDEAL_WINDOW_DAYS - 1} 天`,
          caption: '',
        },
        green: {
          label: `剩 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_IDEAL_WINDOW_DAYS} 天以上`,
          caption: '理想通知期',
        },
      },
      /** 已通知·待確認 — days to 保留至: ≤ FINAL_WARNING red, ≤ URGENT amber (Q3), else green. */
      AWAITING: {
        red: {
          label: `${SESSION_ENROLLMENT_CONFIG.RENEWAL_LOCK_FINAL_WARNING_DAYS} 天內到期`,
          caption: '最後警示',
        },
        amber: {
          label: `${SESSION_ENROLLMENT_CONFIG.RENEWAL_LOCK_URGENT_DAYS} 天內到期`,
          caption: '緊急',
        },
        green: { label: '等家長回覆', caption: '依 保留至 由近到遠' },
      },
      /** 約課通知待發 — red once more than RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS whole days passed. */
      NOTIFY: {
        red: {
          label: `等超過 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS + 1} 天`,
          caption: `第 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS + 2} 天起`,
        },
        green: {
          label: `${SESSION_ENROLLMENT_CONFIG.RENEWAL_SUCCESS_NOTIFY_URGENT_DAYS + 1} 天內`,
          caption: '依等待天數，久的在前',
        },
      },
      /** 近期未處理 — the two groups, in this order. */
      MISSED: {
        lapsed: { label: '已通知·已逾期', caption: '夜間會自動歸檔，還可用 其他處理' },
        archived: { label: '已歸檔', caption: '紅色＝系統自動歸檔 (流程缺口)' },
      },
    },
    /** The notes cell (components/hub/HubNoteCell.vue, style sheet § 4.9). */
    NOTE: {
      ADD: '新增備註',
      EDIT: '編輯備註',
      HISTORY: '查看歷史紀錄',
      SAVING: '儲存中…',
      SAVE_FAILED: '儲存失敗，請再試一次',
      /** Shown only while typing — the empty cell is the pencil (text-fix C6). */
      PLACEHOLDER: '備註',
      /** 「{欄位名} · 歷史紀錄」 (text-fix C5). */
      HISTORY_TITLE: (field: string) => `${field} · 歷史紀錄`,
      /** The read-only history link of an archived row (近期未處理, v9 § 5). */
      HISTORY_LINK: '紀錄',
    },
    /** Column headers of the 課務總覽 card lists (text-fix § A2). */
    COLUMNS: {
      STUDENT: '學生',
      TIMESLOT: '課程時段',
      ATTENTION: '注意',
      NOTE: '備註',
      NOTE_LOG: '備註 · 紀錄',
      ACTIONS: '操作',
      RELEASE_DEADLINE: '名額保留期限',
      HOLD_DAYS: '保留天數',
      SEAT: '名額',
      HOLD_UNTIL: '保留至',
      REMIND: '再次提醒',
      WAIT_DAYS: '等待天數',
      TIME: '時間',
      SELECT: '選取',
      /** 轉班's 原班 and 新班 in one column, one line per class (M5, PO 2026-09-29). */
      TRANSFER_CLASSES: '原班／新班',
      /** 體驗回饋's class column: the 首堂課's date, time, class and teacher (mockup § 五 #13). */
      TRIAL_CLASS: '體驗課',
    },
    /** One 續報 row's shared lines (v9 § 1–6, style sheet § 4.6; text-fix A4.3 / A4.4). */
    ROW: {
      LSED: (md: string) => `最後約課日 ${md}`,
      /** Hover of a 待通知 number when the row has no 最後約課日. */
      NO_LSED: '此學生尚無最後約課日資料',
      REASON: '原因：',
      LEFT: '剩',
      DAYS: '天',
      DAY_N: '第',
      TODAY: '今日',
      DUE_AT: (hm: string) => `${hm} 到期`,
      RELEASE_ON: (date: string) => `${date} 釋出`,
      ENDS_IN: '天後結束',
      ENDS: '結束',
      SEAT_RELEASED: '名額已釋出',
      /** A past-deadline row whose seat is still held (a live lock): 「M/D (剩 N 天)」. */
      HELD_UNTIL: (md: string, days: number) => `${md} (剩 ${days} 天)`,
      /**
       * 已過期未通知 with a live lock (期壬, PO 2026-09-29 option A): the ONE flag saying the
       * seat is not released yet; merged with 目前無約課 when the row is also bookingless
       * (one flag, not two — the old 名額保留中·目前無約課 flag needs the same live lock).
       */
      SEAT_HELD_FLAG: (md: string) => `名額保留至 ${md}`,
      SEAT_HELD_NO_BOOKINGS_FLAG: (md: string) => `名額保留至 ${md} · 目前無約課`,
      /** R5.2 (E8): the merged flag's second half alone — 已過期未通知's window, whose seat line says 保留至. */
      NO_BOOKINGS_FLAG: '目前無約課',
      HOLD_SINCE: (md: string) => `${md} 起保留`,
      WAIT_SINCE: (md: string) => `${md} 起待通知`,
      SOURCE: (source: string) => `來源：${source}`,
      /**
       * 已另報 (PO 2026-09-29, design § 2a): the flag on every 轉班 / 續報 row whose student
       * has another active 學期 enrollment that started after the row's event (the rule:
       * ENROLLED_ELSEWHERE / utils/enrolledElsewhere.ts). The ⇄ comes from the status
       * (hubUi `enrolledElsewhere`); the hover ends with the row's own next steps. `md` =
       * the other enrollment's first class (its enrolledAt); `createdMd` = the day Excel
       * created it (its createdAt — M2 review: 「{M/D} 新增 · 」, '' = unknown → no prefix).
       * No step the row shows ('' ) → the sentence ends at 「請先確認。」.
       */
      ENROLLED_ELSEWHERE: (slot: string) => `已另報 ${slot}`,
      /** D69 (1): on that class's own line in a window's chart the slot is the line's — the word alone. */
      ENROLLED_ELSEWHERE_ON_LINE: '已另報',
      ENROLLED_ELSEWHERE_TITLE: (createdMd: string, md: string, slot: string, nextSteps: string) =>
        `${createdMd ? `${createdMd} 新增 · ` : ''}此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)。可能是改報時段，也可能是加報第二班。${nextSteps ? `請先確認，再決定：${nextSteps}。` : '請先確認。'}`,
      /**
       * The hover's next steps, per list, in order (M2 review 2026-09-29): the buttons that
       * list can show — a row names only those it actually shows (its row view passes them;
       * utils/enrolledElsewhere), joined with 「 / 」. 待約課 names 確定轉入 for the legacy
       * 待約課設定, which offers it instead of 結算轉班.
       */
      ENROLLED_ELSEWHERE_STEPS: {
        transferArranging: ['editProposal', 'cancelTransfer', 'convertToWaitlist'],
        // W7.3 (J-02): 老師建議's one button — the four choices are inside its window.
        transferInbox: ['handle'],
        transferAwaiting: ['settle', 'confirmTransferIn', 'cancelTransfer', 'convertToWaitlist'],
        // M7 9B-9: 已處理 too — the cleanest exit for a family that enrolled elsewhere (the
        // hover still names only the buttons the row shows, elsewhereNextSteps).
        transferExpired: ['extendHold', 'convertToWaitlist', 'notRenewing', 'markHandled'],
        /** 待通知. */
        renewalPending: ['notifyRenewal', 'otherHandling'],
        /** 已通知·待確認. */
        renewalAwaiting: ['remindParent', 'otherHandling'],
        /** 已過期未通知. */
        renewalPast: ['extendAndNotify', 'otherHandling'],
        /** 暫時保留名額 (its only button). */
        renewalHold: ['removeSpecialHold'],
        /** 近期未處理 › 已通知·已逾期 (still open). */
        renewalLapsed: ['otherHandling'],
        /** 近期未處理 › 已歸檔. */
        renewalArchived: ['markHandled'],
      },
      /** Each step as the hover names it (the button's own words). */
      ENROLLED_ELSEWHERE_STEP_LABEL: {
        editProposal: '編輯提案',
        cancelTransfer: '取消轉班',
        convertToWaitlist: '轉為候補',
        handle: HUB_BUTTON_KIND.handleSuggestion.label,
        settle: '結算轉班',
        confirmTransferIn: '確定轉入',
        extendHold: '延長保留',
        notRenewing: '轉為不續報',
        notifyRenewal: '通知續報',
        remindParent: '再次提醒家長',
        extendAndNotify: '延長並通知續報',
        otherHandling: '其他處理 › 已改報其他時段',
        removeSpecialHold: '移除暫時保留',
        markHandled: '已處理',
        undoConfirm: '撤銷確定轉入',
      },
      /**
       * A step the row keeps under its ⋮ 其他處理 (轉班 rows, PO 2026-10-02 — the main button
       * stays on the row): the hover names where it is, as 續報's 「其他處理 › 已改報其他時段」.
       */
      ENROLLED_ELSEWHERE_MENU_STEP: (label: string) => `其他處理 › ${label}`,
      /**
       * 已在候補時段報名 (PO 2026-09-30, 9B-5 option A): a linked 候補 row whose student has a
       * 學期 enrollment — the 已另報 rule, anchored on the row's first registration — in one
       * of the row's own 可上時段 (its 班級, same weekday + start time). Green, in place of
       * 已另報 (utils/waitlistElsewhere). `md` / `createdMd` as ENROLLED_ELSEWHERE_TITLE;
       * `step` = what to press: the row's 已安排 button — a green open row with the placement
       * capability shows it as its main button (9B-5 U4, PO 2026-10-01; 保留名額 moves into ⋮).
       */
      ENROLLED_IN_WAITED_SLOT: '已在候補時段報名',
      ENROLLED_IN_WAITED_SLOT_TITLE: (createdMd: string, md: string, slot: string, step: string) =>
        `${createdMd ? `${createdMd} 新增 · ` : ''}此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)，正是這一列候補的時段。可按 ${step}。`,
      /**
       * The same green flag on a 保留中 row whose student enrolled in the HELD class (UI/UX
       * review 2026-09-30: the class being held is the row's class now, even one picked from
       * 其他班級 outside its 可上時段) — 已安排 ends the hold there.
       */
      ENROLLED_IN_HELD_CLASS_TITLE: (createdMd: string, md: string, slot: string, step: string) =>
        `${createdMd ? `${createdMd} 新增 · ` : ''}此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)，正是這一列保留名額的班級。可按 ${step}。`,
      /**
       * ⇄ 已另報 on a 候補 row (9B-5 option A — the approved wording 「…可能決定留在原班，請確認
       * 是否還要候補：已安排 / 放棄。」): the shared fact; 「可能決定留在原班，」 when the other
       * class is the class the row came from (convertedFromSourceTimeslotId — a 轉為候補
       * family went back), else the shared 已另報 sentence; then the row's question — a 保留中
       * row asks about its 保留名額 — and ITS OWN buttons (`steps`, utils/waitlistElsewhere).
       */
      WAITLIST_ELSEWHERE_TITLE: (
        createdMd: string,
        md: string,
        slot: string,
        backToOrigin: boolean,
        holding: boolean,
        steps: string,
      ) =>
        `${createdMd ? `${createdMd} 新增 · ` : ''}此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)。${backToOrigin ? '可能決定留在原班，' : '可能是改報時段，也可能是加報第二班。'}${holding ? '請確認是否還要保留名額' : '請確認是否還要候補'}：${steps}。`,
      /**
       * 9B-5 U5 (PO 2026-10-01): the ⇄ 已另報 fact as a warning line in the row's 推薦訊息 and
       * 保留名額 windows (utils/waitlistElsewhere waitlistElsewhereWarning) — the family may
       * have decided already. The hover's fact and reading without the 「M/D 新增」 prefix or
       * the buttons, ending with the row's question. `md` = the other enrollment's first
       * class; `backToOrigin` / `holding` as WAITLIST_ELSEWHERE_TITLE.
       */
      WAITLIST_ELSEWHERE_WARNING: (
        md: string,
        slot: string,
        backToOrigin: boolean,
        holding: boolean,
      ) =>
        `此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)。${backToOrigin ? '可能決定留在原班，' : '可能是改報時段，也可能是加報第二班，'}${holding ? '請先確認是否還要保留名額。' : '請先確認是否還要候補。'}`,
      /**
       * 9B-5 U5: the green 已在候補時段報名 fact as the same windows' warning line — the booked
       * class is one of this row's 可上時段 (a 保留中 row: its held class). `md` as
       * WAITLIST_ELSEWHERE_WARNING; `step` = the row's 已安排 button. It sends staff out of
       * the window, to the ROW's button (vue review L1, 2026-10-01): the 保留名額 window has its
       * own 確認, which would hold the seat the family already has.
       */
      WAITED_SLOT_WARNING: (md: string, slot: string, holding: boolean, step: string) =>
        `此學生 ${md} 起在 ${slot} 有學期報名 (Excel 建立)，正是這一列${holding ? '保留名額的班級' : '候補的時段'}。不需要推薦或保留名額，請關閉視窗後在這一列按 ${step}。`,
      /** A step in the row's ⋮ menu, as a hover names it: 「⋮ 直接已安排」. */
      MENU_STEP: (label: string) => `⋮ ${label}`,
      /**
       * What a family waits for, by time (9B-5 U6, PO 2026-10-01): the earliest 可上時段 in
       * the week by its start, then the 班級 — 「週六 09:00 小企鵝班」, with more slots
       * 「週六 09:00 等 2 個時段 小企鵝班」; no slot → the 班級 alone. ONE wording for the 續報
       * 候補中 flag (WAITLISTING) and the teacher's 已轉為候補 pill
       * (TRANSFER_REDESIGN.TEACHER_VIEW.CONVERTED_LABEL), built by
       * programWaitlistUtils.waitedSlotsLabel so the two never drift.
       */
      WAITED_SLOTS: (slot: string, count: number, group: string) =>
        [count > 1 ? `${slot} 等 ${count} 個時段` : slot, group].filter(Boolean).join(' '),
      /**
       * 候補中 {時段} {班級} (9B-5 option A; the time since U6, PO 2026-10-01): the grey flag on
       * every 續報 row whose student waits in an open 候補 row linked to them
       * (utils/renewalWaitlisting) — one part per 班級 (WAITED_SLOTS: 「週六 09:00 小企鵝班」,
       * 「週六 09:00 等 2 個時段 小企鵝班」, a 班級 without a slot alone), joined with 「、」, the
       * earliest registration first; it may take two lines. The hover names each 班級 with its
       * 可上時段, one clause per 班級 (WAITLISTING_CLAUSE).
       */
      WAITLISTING: (parts: readonly string[]) => `候補中 ${parts.join('、')}`,
      WAITLISTING_TITLE: (clauses: readonly string[]) => `此學生在候補名單：${clauses.join('；')}`,
      /** One 班級 of that hover — 「小鴨班 (週六 09:00-09:40、週日 13:45-14:25)」. */
      WAITLISTING_CLAUSE: (group: string, slots: readonly string[]) =>
        slots.length > 0 ? `${group} (${slots.join('、')})` : group,
    },
    /** 續報 tab (W2): the sub-tab strip and its empty states (text-fix C8: no trailing 。). */
    RENEWAL: {
      TABLIST_LABEL: '續報階段',
      /** Hover of the row's 通知續報 button (text-fix A4.15 — was 「通知家長付款 (Path A)」). */
      NOTIFY_TITLE: '通知家長續報',
      /** The alert dot's name on an unselected sub-tab (its hover title and aria-label). */
      // 2026-10-24 (約課切斷確認 W3N.1): DOT_NOTIFY (the 約課通知待發 sub-tab's green dot) and
      // EMPTY_NOTIFY left with that sub-tab — the list is the 通知 tab now (NOTIFY.EMPTY below).
      DOT_PAST: (n: number) => `目前有 ${n} 位學生已過期未通知 — 點此查看`,
      DOT_HOLD: (n: number) => `目前有 ${n} 位學生屬於暫時保留名額 — 點此查看`,
      EMPTY_PAST: '目前沒有已過期未通知的學生',
      EMPTY_PENDING: '目前沒有待通知續報',
      EMPTY_AWAITING: '目前沒有已通知·待確認的學生',
      EMPTY_HOLD: '目前沒有暫時保留名額',
      /**
       * 暫時保留名額 filtered to one timeslot (the 排課時段表 header chip): the banner, and the
       * one sentence when nothing there — as 待確認's filter (review ES7 / FC4).
       */
      HOLD_FILTERED: (slot: string, shown: number, total: number) =>
        `目前只顯示「${slot}」的 ${shown} 筆暫時保留 (共 ${total} 筆)`,
      HOLD_FILTERED_NONE: (slot: string) => `目前只顯示「${slot}」的暫時保留`,
      EMPTY_HOLD_FILTERED: '此時段目前沒有暫時保留名額',
      EMPTY_MISSED: (days: number) => `過去 ${days} 天內沒有未處理的續報`,
      EMPTY_SOURCE: '此來源目前沒有待發通知',
      /** Hover of a 暫時保留名額 day count: the hold never expires on its own. */
      HOLD_POLICY_TITLE:
        '暫時保留名額為特殊暫時保留需求，不會自動歸檔。如需釋出此名額，請使用「移除暫時保留」。',
      /**
       * R-P13 (tasks/todo-renewal-pending-chart.md, 2026-11-02): the 續報 windows' seat-line second
       * row (utils/renewalWindowLines) — 已通知·待確認, and 暫時保留名額 (no 保留至: it never expires).
       * 待通知／已過期未通知 read SEAT_USAGE.LINES.HOLD.
       */
      LINES: {
        AWAITING: (lsedMd: string, holdMd: string, firstMd: string, remindMd: string): string =>
          `最後約課日 ${lsedMd} · 保留至 ${holdMd} · 首次通知 ${firstMd} · 提醒日 ${remindMd}`,
        /**
         * R3.2 (2026-11-02): 「自 M/D 保留」 — {sinceMd} = the hold's start (getSpecialHoldStartMs, the
         * row's 「M/D 起保留」). R-P13 wrote 「暫時保留名額 · 自 M/D」; the seat's row-1 tag already
         * says 暫時保留名額 (SEAT_USAGE.TAG_BY_ROW_STATE['special-hold']), so the line drops it — said
         * once — and keeps 保留 so it is not a bare date. Was the string 「暫時保留名額 (無期限)」, read
         * by nothing; no 保留至 on the line already says it does not run out (D42 d). Also the 轉班 新班
         * line of a 待約課 on 暫時保留名額 (utils/transferWindowLines) — one fact, one wording.
         */
        SPECIAL_HOLD: (sinceMd: string): string => SEAT_USAGE_HELD_SINCE(sinceMd),
        /**
         * R3.2: AWAITING with a fact the 已通知·待確認 row does not show left out — no live 保留至,
         * no 首次通知 this cycle, no 提醒日 — and 「已再提醒 M/D」 in place of 提醒日 once reminded
         * (the row's 已再提醒 line). With all four and a 提醒日 it reads exactly as AWAITING (pinned).
         */
        AWAITING_PARTIAL: (
          lsedMd: string,
          holdMd: string | null,
          firstMd: string | null,
          remind: { md: string; done: boolean } | null,
        ): string =>
          [
            `${SEAT_USAGE_LSED_WORD} ${lsedMd}`,
            holdMd === null ? null : `保留至 ${holdMd}`,
            firstMd === null ? null : `首次通知 ${firstMd}`,
            remind === null ? null : `${remind.done ? '已再提醒' : '提醒日'} ${remind.md}`,
          ]
            .filter((part): part is string => part !== null)
            .join(SEAT_USAGE_SEP),
      },
    },
    /** 待確認 tab (W3; v9 § 7; text-fix A2.8 / A5.3–A5.5 / B18). */
    REVIEW: {
      COL_SLOT: '時段',
      /**
       * Item 7 (PO 2026-10-06 「ok, accepted, go」 — docs/tools/transfer-ui-polish.html § 7): the
       * 偵測 column became one grey line under 證據 — {when} = 「N 天前」 / 「今天」
       * (formatRelativeDays); its hover is the M/D.
       */
      DETECTED_LINE: (when: string) => `偵測 ${when}`,
      /** Not filtered and nothing to review (no emoji — W3.1). */
      ALL_CLEAR: '目前沒有需要確認的最後約課日',
      /** Filtered to one timeslot (the 排課時段表 header chip) and nothing there. */
      FILTERED_EMPTY: '此時段目前沒有待確認',
      /** The BF block of an existing row (A5.3 / A5.4). PO 2026-10-08 (「admin booking emails
       *  too」): BF also emails when 課務 books for a family — no 「家長」 in the title. */
      BF_TITLE: '什麼時候約的',
      BF_SOURCE: '(透過 BF 通知信得知)',
      BF_BOOKED: (md: string, ago: string, n: number) => `${md} (${ago}) 一次約了 ${n} 堂`,
      BF_CANCELLED: (md: string, ago: string) => `${md} (${ago}) 取消約課`,
      BF_APPROX: '依星期/課程比對',
      BF_APPROX_TITLE:
        '此為較早的 BF 通知信 (不含老師資訊)，以 星期＋時間＋課程＋報名者比對；此類時段通常唯一，比對仍可靠',
      /**
       * Item 7: no BF record → this one grey line instead of the block. Item 8 (PO 2026-10-06 =
       * A, 「what does this email mean」): it begins with the block's own title (BF_TITLE), so
       * the line says what it is about.
       */
      BF_NONE: '找不到',
      /** Item 7 (review 2026-10-06 L3): loading / a failed read are one grey line too, so a row
       *  with no record never grows into the block and back — {state} = BF_LOADING / BF_RETRY;
       *  the same lead as BF_NONE. */
      BF_LINE: (state: string) => `什麼時候約的：${state}`,
      BF_LOADING: '讀取中…',
      /**
       * R5.2 (B1): 處理待確認's BF line — fetched with no record 「BF 通知信：找不到」 (HANDLE.BF_PREFIX
       * + BF_NONE); the read failed 「BF 通知信：查不到」 (+ BF_FAILED) with BF_RETRY, the block's
       * own 重新查詢 (one home: SEAT_USAGE.RETRY). BF_LINE／BF_LOADING: freed, kept (D55 g).
       */
      BF_FAILED: '查不到',
      get BF_RETRY(): string {
        return UI_STRINGS.SEAT_USAGE.RETRY;
      },
      // ── Round 2 (tasks/todo-renewal-pending-chart.md § Build round 2, D31 cards 3 A / 4 A / 9 A;
      //    mockup v4 docs/tools/hub-chart-windows-mockup.html) ──
      /**
       * D36 / R-P11: the 證據 column becomes 新約課 — a HubCount 「約 N 堂」 (新時段／既有報名) or
       * 「差 N 天」 (約課切斷確認), units small grey (D29 (a)); the sub lines under the number.
       */
      COL_NEW_BOOKINGS: '新約課',
      NEW_BOOKINGS_COUNT: { PREFIX: '約', SUFFIX: '堂' },
      GAP_DAYS: { PREFIX: '差', SUFFIX: '天' },
      /** {firstMd} = the first Excel 學期 date, {detected} = DETECTED_LINE's 「偵測 N 天前」. */
      NEW_BOOKINGS_SUB: (firstMd: string, detected: string): string =>
        `${firstMd} 起 · ${detected}`,
      /** The same sub when the row has no stored detection time (R2.4): 「M/D 起」 alone. */
      NEW_BOOKINGS_SUB_FIRST_ONLY: (firstMd: string): string => `${firstMd} 起`,
      /** 約課切斷確認 rows: {md} = the upload range end. */
      UPLOADED_TO: (md: string): string => `上傳到 ${md}`,
      /**
       * R-P6 / D31 card 4 A: 處理待確認 (LsedReviewHandleModal) — the 處理方式 radios (the first one's
       * label is the row button's today: RESERVATION_ACTION_LABEL_BY_KIND create／reanchor／restore,
       * or HUB_BUTTON_KIND.fixCutoffLsed) with their subs, and the one BF line under the chart (D36: the same stored BF facts the 證據 block showed;
       * the M/D only — the 「(N 天前)」 of the old block is not kept on the line).
       */
      HANDLE: {
        TITLE: REVIEW_HANDLE_TITLE,
        METHOD: '處理方式',
        CONFIRM_SUB: '建立這位學生在這一班的報名紀錄',
        DISMISS: HUB_BUTTON_KIND.dismiss.label,
        DISMISS_SUB: '這幾堂是補課或別的用途，不是新學期',
        CUTOFF_FIX_SUB: `確認後開啟 ${HUB_BUTTON_KIND.fixCutoffLsed.label}，選實際的最後一堂`,
        /**
         * 「BF 通知信：M/D 一次約了 N 堂、M/D 一次約了 N 堂；M/D 已在 M/D 取消」 = BF_PREFIX + the booked
         * bursts (joined by BF_ITEM_JOIN) + BF_JOIN + the cancellations (joined by BF_ITEM_JOIN);
         * utils/bfEventsLine.ts.
         */
        BF_PREFIX: 'BF 通知信：',
        BF_BOOKED: (md: string, n: number): string => `${md} 一次約了 ${n} 堂`,
        BF_CANCELLED: (md: string, onMd: string): string => `${md} 已在 ${onMd} 取消`,
        BF_JOIN: '；',
        BF_ITEM_JOIN: '、',
        /** The BF line's hover. */
        BF_TITLE: '資料來源：BF 通知信，僅供核對；正式名單以 Excel 上傳為準',
        /** 新時段 (pair): the focus line's second row (D33 (ii)). */
        PAIR_SEAT_LINE: (n: number): string => `新約課 ${n} 堂 · 沒有報名紀錄`,
        /** 約課切斷確認: the seat line's second row — D41 (c) / RW-12 b, the row's 「設定 M/D · 誰」. */
        CUTOFF_SEAT_LINE: (lsedMd: string, setMd: string, setBy: string): string =>
          `最後約課日 ${lsedMd} (${setMd} 設定 · ${setBy})`,
        /**
         * D40 / D53 (f): 之後出現的課 is the seat line's hover (utils/reviewHandleView — the dates the
         * row's chips showed); {dates} = 「M/D、M/D」.
         */
        CUTOFF_LATER: (dates: string): string => `之後出現的課：${dates}`,
        /** CUTOFF_LATER's {dates} joiner. */
        DATE_JOIN: '、',
        /**
         * R3.5 (RW-19, D62): the title when the viewer cannot see 名額使用情況 (the block hidden,
         * so the title keeps the student and the class, as the 忽略 dialog's).
         */
        TITLE_NAMED: (name: string, slot: string): string =>
          `${REVIEW_HANDLE_TITLE} — ${name} @ ${slot}`,
        /**
         * R3.5 (D40): the identity line's stage pill names the section — 「待確認 › 新時段報名」
         * (UI_STRINGS.SEAT_USAGE.STAGE_PILL with HUB.FAMILY.review; the mockup's words).
         */
        STAGE: { pair: '新時段報名', existing: '既有報名', cutoff: '約課切斷確認' },
        /**
         * 既有報名: the seat line's second row — 「最後約課日 M/D · 保留至 M/D · 新約課 N 堂」 (the
         * row's 最後約課日；保留至 flag moves here, said once; no 保留至 → left out). F5-2 (D61 b,
         * B-03): a 暫時保留名額 seat has no 保留至 — its stored start fills the slot, 「自 M/D 保留」
         * (`sinceMd`); none stored → the slot is left out (its 暫時保留名額 tag says the state).
         */
        EXISTING_SEAT_LINE: (lsedMd: string, holdMd: string, n: number, sinceMd = ''): string =>
          [
            `${SEAT_USAGE_LSED_WORD} ${lsedMd}`,
            ...(holdMd ? [`保留至 ${holdMd}`] : sinceMd ? [SEAT_USAGE_HELD_SINCE(sinceMd)] : []),
            `新約課 ${n} 堂`,
          ].join(SEAT_USAGE_SEP),
        /** 約課切斷確認: 忽略's sub (使用說明 item 4: the BF check finds no later class). */
        CUTOFF_DISMISS_SUB: 'BF 上沒有更後面的課',
        /**
         * 補正最後約課日 第 1 步 with the block (RW-3, C12): the upload end the chart's red line does
         * not draw, after the seat line — 「… · 課表已上傳到 M/D」 (or CUTOFF.WINDOW.RANGE_LOADING／
         * RANGE_FAILED as {range}).
         */
        CUTOFF_UPLOADED_TO: (md: string): string => `課表已上傳到 ${md}`,
        WITH_RANGE: (line: string, range: string): string => `${line}${SEAT_USAGE_SEP}${range}`,
        /** The grey 確認's hover while no 處理方式 is picked. */
        CHOICE_REQUIRED: '請先選擇 處理方式',
      },
      /**
       * R-P5 / D29 (b) / D39: the 忽略 dialog (ReviewDismissDialog — the row's 忽略 and the window's
       * 忽略 choice), the title keeps the name (D34: no block). REMEMBER／COMEBACK = 新時段 and 既有報名
       * ({dates} = the dates the dismiss payload carries); CUTOFF = 約課切斷確認 (D41 (c): bound to
       * that date, no later dates, no automatic return).
       */
      DISMISS_DIALOG: {
        TITLE: (name: string, slot: string): string =>
          `${HUB_BUTTON_KIND.dismiss.label} — ${name} @ ${slot}`,
        REMEMBER: (n: number, dates: string): string =>
          `這一列移到 已忽略，記住這 ${n} 天：${dates}`,
        COMEBACK: (n: number): string =>
          `之後出現這 ${n} 天以外的新約課，這一列會自動回來；要撤回可到 已忽略 按 ${HUB_BUTTON_KIND.restore.label}。`,
        CUTOFF: (md: string): string =>
          `最後約課日 ${md} 到這裡結束。這一列移到 已忽略；要撤回可到 已忽略 按 ${HUB_BUTTON_KIND.restore.label}。`,
      },
      /**
       * 使用說明's ★ lines on which tickets count (P1, PO 2026-10-02 — review-queue proposal
       * § 1 「之後」; replaced 「★ 學期票券以外的約課都不標記。」). Approved wording, verbatim.
       */
      EXPLAINER_TICKET_RULE: '★ 只算票券名稱含「學期」的約課，其他票券一律不算。',
      EXPLAINER_BF_NOTE:
        '★ 處理待確認 視窗裡的 BF 通知信 不分票券，也可能包含兄弟姊妹的約課，請以 Excel 日期為準。',
      /** P3 (contract C1) — rows that can no longer lead to an action are not listed. */
      EXPLAINER_NO_ACTION: (capDays: number, minCount: number) =>
        `★ 已無法再處理的列：日期都在 ${capDays} 天內 (最後約課日已不能再往後設定)，或剩不到 ${minCount} 堂 → 不列出；之後若有更晚的約課，上傳後會自動回來。`,
      /**
       * W10.8 (D33 / D34, PO 2026-10-15 — 「when a row already exist in 續報 or transfer, it's
       * better to keep it in one place」): a student whose 續報 row offers 結算續報
       * (LSED_REVIEW_DEFER_STATES) or whose class an open 轉班 locks is not listed here and not
       * counted (utils/lsedReviewLink); it comes back once it leaves those lists — after a click
       * there (已處理 / 已確認釋出名額 / 不續報 / 設為暫時保留名額) only for lessons booked after it
       * (D43 B5, lsedReviewHandledDates). Supersedes
       * 2026-10-06 item 10 (locked rows listed and counted, greyed with 「前往 轉班」).
       */
      EXPLAINER_OWNED_ELSEWHERE:
        '★ 已在 續報 (待通知、已通知·待確認、已過期未通知、近期未處理) 或 轉班 處理中的學生不在這裡重複列出，請在該列處理；離開那些清單後會回到這裡 (在那裡處理過的，之後又有新約課才會回來)。',
      /**
       * W10.8 (D33 / D34): the 「期間已約 N 堂」 hover lines of a row whose 待確認 row is not
       * listed because its 續報 row offers 結算續報 — the evidence the hidden row showed (its
       * dates, Taipei M/D in date order; the 偵測 day, '' = no stored time) and where 結算續報
       * is: the row's own button on every 續報 list since 2026-10-15 (D44 C13 / D43 B6 / D46 R3 —
       * 已通知·待確認, 待通知, 已過期未通知, 近期未處理; was ⋮ 其他處理 / 其他處理 on all but
       * 已通知·待確認). Appended under the marker's own sentence — the same words on the 續報 rows
       * and 排課時段表's chip (utils/lsedReviewLink lsedReviewFlagNoteOf).
       */
      DEFERRED_BOOKINGS_LINES: (dates: readonly string[], detectedMd: string): string => {
        const evidence = `預約日期：${dates.join('、')}${detectedMd ? ` (偵測 ${detectedMd})` : ''}`;
        return `${evidence}\n請在 續報 這一列按 結算續報`;
      },
      /**
       * B5 / L9 (PO 2026-10-03), Item 7 (PO 2026-10-06): the labels — in the 注意 column now.
       * 2026-10-14 (W9.5 UX-21): 仍由課務判斷 (the manual names the role, never 「你」).
       */
      EXPLAINER_LABELS:
        '★ 注意 欄的標籤是提示：寫這一列可能是什麼，滑過看根據；只供參考，仍由課務判斷。',
      /**
       * The ※ line (inline in components/LsedReviewExplainer before 約課切斷確認 W3.5). {exception}
       * = CUTOFF.EXPLAINER_IGNORED_PARENTHESIS (D39) — '' gives the line as it read before.
       */
      EXPLAINER_IGNORED: (exception: string) =>
        `※ 已忽略的項目收合於下方 已忽略 區；若之後出現新的預約，會自動重新出現${exception}。`,
      /**
       * 2026-10-14 (W9.5 UX-03, C-04): rule 1's ask (inline in components/LsedReviewExplainer
       * before). 2026-10-15 (D49, PO's words): 補課 → 忽略, a longer term → 修改最後約課日 —
       * 待確認 has no 結算續報 since D45; may change again with tasks/todo-lsed-same-sitting.md Q-M.
       */
      EXPLAINER_SINGLE_SLOT_ASK:
        '(若是補課，請按「忽略」；需延長學期，請按「處理」，在視窗選「修改最後約課日」)。',
      /** B5 / L9: the heading of the ★ zone (was 「以下狀態不會出現 (請在約課時自行注意)」). */
      EXPLAINER_NOT_LISTED_TITLE: '以下不會出現在這裡：',
      // W10.8 (D33 / D34): the six LOCK_EXISTING_* / LOCK_PAIR_* reason lines of a 轉班-locked
      // 待確認 row went with that row — such a row is not listed any more (utils/lsedReviewLink).
      /**
       * C6 #4 (PO 2026-10-03): 2+ 新時段 rows of the same upload (identical firstDetectedAt —
       * utils/pairReviewGroups classMoveGroups) moved from the same class to the same class
       * (疑似換時段) — one line above them. A hint, not a fact (Round 4 review): 「疑似」, as the
       * rows' own 注意 flag. {from} / {to} = LSED_REVIEW.SLOT_WITH_TEACHER, so a move that only
       * changes the teacher reads as one. 常態班更名 is a 主管 page (permissions
       * canManageClassIdentity): who cannot open it reads 「請告知主管」 (the upload preview's
       * sentence) and gets no link. Pass 11 (A): 「改班名或換老師」, never 「換時間」 — 常態班更名
       * keeps the weekday and the time (it changes only the class name and the permanent
       * teachers).
       */
      CLASS_MOVE: (n: number, from: string, to: string) =>
        `${n} 位學生疑似從 ${from} 換到 ${to}：如果是整班改班名或換老師，請用 常態班更名 一次處理`,
      CLASS_MOVE_ASK_SUPERVISOR: (n: number, from: string, to: string) =>
        `${n} 位學生疑似從 ${from} 換到 ${to}：如果是整班改班名或換老師，請告知主管用 常態班更名 一次處理`,
      CLASS_MOVE_LINK: '前往 常態班更名',
      /** The 常態班更名 page (router/routes.ts 'admin/class-identity'). */
      CLASS_MOVE_PATH: '/admin/class-identity',
      /**
       * 已忽略 › 恢復 succeeded. A row that could not come back to the list is never restored
       * (PO 2026-10-03: 恢復 is greyed with RENEWAL_WORKFLOW_CONFIG.PAIR_REVIEW.RESTORE_BLOCKED_*).
       */
      RESTORED: '已恢復此項目',
      /**
       * The 待確認 labels (Round 2 H6–H8, PO 「go」 2026-10-02 on
       * docs/tools/review-queue-hints-v2.html 「新的寫法」; in the 注意 column since Item 7, PO
       * 2026-10-06): the label says what the row PROBABLY is, its tooltip the fact it rests on
       * (PO: 「可能是」, never 「多半是」; a label names the same thing as its fact). Built by
       * utils/reviewRowHints.ts; the colours (option A) are there too. M/D = a Taipei date;
       * {slot} = formatTimeslotLabel.
       */
      HINTS: {
        /** Both row types: the student has a live enrollment at another slot — a fact, not a
         *  guess (neutral colour). Tooltip = one LSED_REVIEW.OTHER_SLOT line per slot (PO
         *  2026-10-03: a label + tooltip instead of the 「其他時段：…」 line under the name). */
        OTHER_SLOTS: '有其他時段',
        /** 新時段: another live enrollment of the student stops before this row's first date. */
        SLOT_CHANGE: '可能換新時段',
        /** {md} = that slot's latest future 學期 booking; '' = it has none. */
        SLOT_CHANGE_FACT: (slot: string, md: string) =>
          md ? `另一班 (${slot}) ${md} 之後沒有約課` : `另一班 (${slot}) 已沒有約課`,
        /** 新時段: the other slot is still booked on / after this row's first date. */
        EXTRA_CLASS: '可能加報或補課',
        EXTRA_CLASS_FACT: (slot: string, md: string) => `另一班 (${slot}) 同時也有約課，到 ${md}`,
        /** 新時段 · 全新時段: ≥ LSED_REVIEW_MIN_COUNT past 學期 classes here, no enrollment.
         *  「約過」, not 「已上」 (C2, PO 2026-10-03): detection counts bookings, a 請假 too. */
        SETTLED_HERE: '可能已固定在此班',
        SETTLED_HERE_FACT: (first: string, last: string, n: number) =>
          first === last
            ? `此時段 ${first} 約過 ${n} 堂學期課，沒有報名紀錄`
            : `此時段 ${first}–${last} 約過 ${n} 堂學期課，沒有報名紀錄`,
        /** 新時段 · 全新時段 only (R4-7b): a past 體驗 class here and no other live enrollment. */
        NEW_STUDENT: '可能是新生',
        NEW_STUDENT_FACT: (md: string) => `${md} 在此時段約過體驗課`,
        /** 新時段: a 待約課 / 未完成 轉班 of the student whose new class is this slot ({stage}
         *  = the stage's name, HUB_PAGE_IDENTITY). */
        TRANSFER_BOOKINGS: '轉班的約課',
        TRANSFER_BOOKINGS_FACT: (stage: string) =>
          `學生有一筆轉班 (${stage})，新班就是此時段，請到 轉班 處理`,
        /**
         * 既有報名 · W10.4 F8 (D26 (8), PO 2026-10-14; spec C-04 10/12): the hover of the row's
         * 續報 status chip (待通知 / 已通知·待確認 / 已過期未通知 / 已通知·已逾期 — the 排課時段表
         * chip, useEnrollmentStatusChips reviewStatusChips) on a row that offers 結算續報 — what
         * the two buttons do with the row's N bookings (settleOrReanchorHint). Replaces the
         * guess label 「可能是續報」 and its fact (「M/D 已通知家長續報；…」), retired by C-04.
         */
        RENEWAL_STATUS_HOVER: (n?: number) => settleOrReanchorHint(n),
        /** 既有報名: created by 確定轉入 and no renewal / 最後約課日 change since ({md} = its
         *  latest transfer-in entry; '' = none logged) — R4-6 / B1, utils/reviewRowHints. */
        RENEWAL_OR_TRANSFER: '可能是續報或轉入',
        RENEWAL_OR_TRANSFER_FACT: (md: string) =>
          md
            ? `${md} 轉班轉入；之後的課可能是下一期，也可能還是這次轉入的課`
            : '轉班轉入；之後的課可能是下一期，也可能還是這次轉入的課',
        /**
         * 既有報名 · R4-5 (PO 2026-10-03, C1): the student's own state at this slot, first in
         * the row — a fact (white). The label is the state's name (derived.ts
         * ENROLLMENT_STATE_LABEL, classifyEnrollmentState); its tooltip = what the row's date
         * button does in that state (functions sessionEnrollments/manual.ts re-anchor branches),
         * named as the row shows it (reservationButtonOf — W8.3, D-01b: 恢復保留名額 on every
         * ending mark, 暫時保留名額 and 請長假; 修改最後約課日 on the 轉班 hold). Keyed by
         * EnrollmentState; a state not listed (進行中, 已通知續報, …) gets no label.
         * 轉班處理中 (轉出中): 轉班鎖定 (PO 2026-10-04) greys the date button on such a row, so its
         * fact names where the seat's actions are handled, not what the button would do.
         */
        STATUS_FACTS: {
          'soft-end-not-renewing': `${reservationButtonOf('soft-end-not-renewing')} 會取消不續報，重新開始續報`,
          // PO 2026-10-08 (「4. ok」): 一般保留名額 — the 排課時段表 保留名額 group's name
          // (GROUP_PILL_GENERAL); a seat is 保留名額, never 報名 (CLAUDE.md § 7).
          'special-hold': `${reservationButtonOf('special-hold')} 會結束暫時保留，改為一般保留名額`,
          transferring: '學生正在轉出這一班；保留名額、最後約課日、續報 請到 轉班 處理',
          paused: `${reservationButtonOf('paused')} 會結束請長假，改為一般保留名額`,
          'soft-end-transferred': `${reservationButtonOf('soft-end-transferred')} 會取消已轉出，重新保留這一班的名額`,
          // D1 (PO 2026-10-03 「ok」): the other end tags and the 轉班 hold — manual.ts sends
          // the end tags through the active-clean reset and settles a hold like 結算轉班.
          'soft-end-scan-a': `${reservationButtonOf('soft-end-scan-a')} 會取消結束標記，重新保留這一班的名額`,
          'soft-end-scan-b': `${reservationButtonOf('soft-end-scan-b')} 會取消結束標記，重新保留這一班的名額`,
          'soft-end-other': `${reservationButtonOf('soft-end-other')} 會取消結束標記，重新保留這一班的名額`,
          // 轉班鎖定 (PO 2026-10-04): while the 轉班 is open the lock greys the date button here;
          // only a hold no open record owns is settled by it (manual.ts, like 結算轉班).
          'transfer-awaiting': `這是 轉班 的保留名額；轉班 進行中時 最後約課日、續報 請到 轉班 處理，否則 ${reservationButtonOf('transfer-awaiting')} 會把這筆 轉班 結案 (和 結算轉班 一樣)`,
        } as Readonly<Record<string, string>>,
        /**
         * 新時段 · C6 #3 (PO 2026-10-03): the student (候補 連結既有學生 — linkedStudentId) holds an
         * unexpired 候補 保留名額 (正式課程) in this class — counted in 保留名額 N, and once the
         * enrollment exists counted again until 候補 › 已安排. A fact (white). The tooltip is
         * also the note after 確認報名.
         */
        WAITLIST_HOLD: '候補保留中',
        WAITLIST_HOLD_FACT:
          '這位學生在這一班也有候補保留名額；確認保留名額後請到 候補 按「已安排」，否則會算兩次',
        /**
         * 新時段 · C6 #5 (PO 2026-10-03): other rows of the same parent account (the 886 phone of
         * the studentId) — listed next to each other. A fact (white); {n} counts this row too.
         */
        SAME_PARENT: (n: number) => `同一家長 ${n} 列`,
        SAME_PARENT_FACT: (others: readonly string[]) => `同一個家長帳號還有：${others.join('、')}`,
        SAME_PARENT_ITEM: (child: string, slot: string) => `${child} (${slot})`,
      },
      /**
       * 2026-10-24 (約課切斷確認, tasks/todo-lsed-same-sitting.md Spec 2 / 5 / 7 / 8 / 14; mockup
       * docs/tools/lsed-cutoff-fix-mockup.html § 2 / § 3 / § 8.2 (a) / § 10.1 A / § 10.5) — every
       * string of the 約課切斷確認 section, its row, the 補正最後約課日 window and the 使用說明
       * additions. Verbatim from the mockup; M/D = formatShortTaipeiDate, YYYY/MM/DD (X) = the
       * date with its weekday as the window shows it. The window also reuses
       * UI_STRINGS.SET_RESERVATION.PREVIEW_TITLE (「點擊確認後：」) and PREVIEW_NOTICE_LISTED /
       * PREVIEW_NOTICE_NOT_LISTED_PLAIN (the fourth `<li>`, the same words as 修改最後約課日's ④),
       * and RENEWAL_WORKFLOW_CONFIG.PAIR_REVIEW.DISMISS_REASON_LABEL (「原因 (選填)」).
       */
      CUTOFF: {
        /** The section heading (待確認, after 既有報名待確認, before 已忽略). */
        SECTION_TITLE: '約課切斷確認',
        /** The section's grey caption (Spec 2). */
        SECTION_NOTE: `最後約課日 設在上傳範圍的最後 ${SESSION_ENROLLMENT_CONFIG.LSED_CUTOFF_WINDOW_DAYS} 天內，BF 可能還有後面的課：到 BF 查最後一堂，按 處理 補正最後約課日，或按 忽略`,
        /**
         * The 注意 column flag. N15 (R2-B-08): NO hover on a 待確認 row — the evidence line under
         * it prints the same facts. The 轉班 row's flag (Spec 14 (e)) has TRANSFER_ROW_FLAG_TITLE.
         * D72 (DERIVED from D66 「the same orange label as we use in 待確認」): no ⚠ in the text —
         * the flag draws the shared warning icon (cutoffHint), as every hint of this list does.
         */
        FLAG: '約課可能被切斷',
        /**
         * W3.8: the same words on the 轉班 row's flag beside 原時段又約 — no ⚠ in the text: the
         * row's flag draws its own warning icon (HubFlag, utils/transferCutoffHint).
         */
        TRANSFER_ROW_FLAG: '約課可能被切斷',
        /**
         * 約課切斷確認 D63 item 8 (annex § 三.4): the same flag on a 轉班 › 安排中 row for the NEW
         * class's seat while that seat is listed in 約課切斷確認 (utils/transferCutoffHint); its
         * hover is TRANSFER_ROW_FLAG_TITLE with that seat's mark.
         */
        TRANSFER_ROW_FLAG_NEW_CLASS: '新班 約課可能被切斷',
        /**
         * Spec 14 (e) / W3.8: the hover of the flag beside 原時段又約 on a 轉班 row (that screen
         * prints no evidence). {lsed} / {setDay} / {rangeEnd} = M/D; {setBy} = the setter's
         * 學期紀錄 label (系統偵測續報, 修改最後約課日, 確定轉入 …). W5.4 UX-L6: no 「上傳後」 — untrue
         * when the setter is 修改最後約課日／確定轉入.
         */
        TRANSFER_ROW_FLAG_TITLE: (
          lsed: string,
          setDay: string,
          setBy: string,
          rangeEnd: string,
          gapDays: number,
        ) =>
          `最後約課日 ${lsed} 是 ${setDay} 由 ${setBy} 設定的；當時課表只上傳到 ${rangeEnd}，只差 ${gapDays} 天`,
        /** Spec 2 / mockup § 16 Q4 A: the stage words beside the flag on a row in an open 轉班. */
        STAGE_TRANSFERRING: '轉班 安排中',
        STAGE_TRANSFER_TARGET: '轉班 安排中 · 新班',
        /** 證據 line 1 (mockup § 2; N15: 「(差 N 天)」 ends it). Same args as the hover above. */
        EVIDENCE: (
          lsed: string,
          setDay: string,
          setBy: string,
          rangeEnd: string,
          gapDays: number,
        ) =>
          `最後約課日 ${lsed} · ${setDay} 設定 (${setBy}) · 當時上傳到 ${rangeEnd} (差 ${gapDays} 天)`,
        /** W3.4: the toast after a 忽略 — 既有報名待確認's words (one home, W5.4 FE-L7). */
        DISMISSED_TOAST: RENEWAL_WORKFLOW_CONFIG.LSED_REVIEW.DISMISSED_TOAST,
        /** The window 補正最後約課日 (mockup § 3; 500 px, two steps). */
        WINDOW: {
          /** The window title = the row button's word (hubUi HUB_MUTATING_ACTIONS.fixCutoffLsed). */
          TITLE: HUB_BUTTON_KIND.fixCutoffLsed.label,
          /** 「秦培芳 - 曾朋彥 · 週四 10:00 小青蛙班 (Diana)」 — both steps. */
          HEADER: (student: string, classLabel: string, teacher: string) =>
            `${student} · ${classLabel} (${teacher})`,
          /**
           * Step 1: {lsed} = YYYY/MM/DD (X); {rangeEnd} = YYYY/MM/DD. D70 (3) / D71 (iii): while the
           * 名額使用情況 chart below draws the red 「課表上傳到 M/D」 line for this same day, the line
           * is CURRENT_NO_RANGE (the date is written once, on the chart — D69).
           */
          CURRENT: (lsed: string, rangeEnd: string) =>
            `目前 最後約課日 ${lsed} · 課表已上傳到 ${rangeEnd}`,
          /** Step 1's red notice. */
          NOTICE:
            '此約課的實際最後約課日，可能因 BF 上傳範圍限制被切斷，請前往 BF 確認後修改日期設定。',
          /** {weekday} = 「週四」. */
          PROMPT: (weekday: string) => `點選 最後約課日 (只能點 ${weekday})`,
          /** {picked} = YYYY/MM/DD (X); {currentMd} = M/D; {weeks} = whole weeks later. */
          PICKED: (picked: string, uploaded: boolean, currentMd: string, weeks: number) =>
            `已選：${picked} · ${uploaded ? '已上傳' : '尚未上傳'} · 比目前的 ${currentMd} 晚 ${weeks} 週`,
          /**
           * D63 item 5 / D71 (iv) (chart mockup § 二 之後): the grey line under 已選 when the picked
           * day (on or before the upload end) has a 此名額 square that is not a class
           * (LSED_CUTOFF_FIX_BOOKED_CELL_STATES) — information only, never a block.
           */
          NO_BOOKING_ON_PICK: '課表上這一天沒有這位學生的約課',
          REASON_PLACEHOLDER: '例：BF 約到 4/29，上傳範圍只到 4/13',
          /** Step 2's date line: {from} / {to} = YYYY/MM/DD. */
          STEP2_HEADER: (from: string, to: string, weeks: number, uploaded: boolean) =>
            `最後約課日 ${from} → ${to} (延後 ${weeks} 週，${uploaded ? '已上傳' : '課表尚未上傳到這一天'})`,
          /** Step 2's red box: the bold lead, then the policy (D31). */
          POLICY_LEAD: '延後最後約課日＝名額被佔用更久，請確認符合政策。',
          POLICY:
            '【政策】確認好的學期決定最後約課日，之後不改。後續取消約課都是「請假補課」用途，不延後最後約課日 (續報繳費時間以它為準)。',
          /**
           * {md} = the picked M/D (pure M/D, D63 item 2 — also the input's placeholder); 確認
           * enables only when the typed day equals it.
           */
          TYPE_LABEL: (md: string) => `請輸入 ${md} 以確認`,
          /**
           * 「點擊確認後：」 `<li>` 1 — {md} = the picked M/D (D64, chart mockup § 三 之後: the new
           * date is the step's first line; this line says what it means).
           */
          LI_DATE: (md: string) => `到 ${md} 為止的約課都算這一期`,
          /**
           * `<li>` 2 (N16) on 一般 / 待通知 / 已過期未通知, and on 已通知·待確認 when the new date is
           * outside 續報 time (D69) — {picked} = the picked M/D, {pendingMd} = the 待通知 day
           * (renewalWindowOpenMs). 已通知·待確認 inside 續報 time and 約課通知待發 get
           * SET_RESERVATION.PREVIEW_HOLD instead; 轉出中 has no line 2.
           */
          LI_CYCLE: (picked: string, pendingMd: string) =>
            `續報 的日期以 ${picked} 算 (待通知 從 ${pendingMd} 起)`,
          /**
           * `<li>` 2 on 暫時保留名額 (mockup § 8.2 (a), PO 10/13 「8.2 ok」). D80 (annex § 一): no
           * 「暫時保留名額 維持；」 — the list's first line 「不會變更目前狀態：暫時保留名額」 says it.
           */
          LI_SPECIAL_HOLD: (picked: string) => `移除暫時保留名額 之後，續報 的日期以 ${picked} 算`,
          /** `<li>` 3. */
          LI_REMOVED: '完成後這一列將被移除',
          /** W5.4 UX-M2: the 原因 typed in step 1, under step 2's 「點擊確認後：」 list. */
          REASON_LINE: (text: string) => `原因：${text}`,
          /**
           * W5.4 UX-L3: in place of the words that need the upload range end (「課表已上傳到 M/D」,
           * 已上傳／尚未上傳) while it is being read, and after the read failed — never the
           * no-upload wording (*_NO_RANGE) for either.
           */
          RANGE_LOADING: '讀取中…',
          RANGE_FAILED: '無法讀取上傳範圍',
          /** The three range lines with RANGE_LOADING / RANGE_FAILED as {range}. */
          CURRENT_RANGE_PENDING: (lsed: string, range: string) =>
            `目前 最後約課日 ${lsed} · ${range}`,
          PICKED_RANGE_PENDING: (picked: string, range: string, currentMd: string, weeks: number) =>
            `已選：${picked} · ${range} · 比目前的 ${currentMd} 晚 ${weeks} 週`,
          STEP2_HEADER_RANGE_PENDING: (from: string, to: string, weeks: number, range: string) =>
            `最後約課日 ${from} → ${to} (延後 ${weeks} 週，${range})`,
          NEXT: '下一步',
          BACK: '上一步',
          CONFIRM: '確認',
          /** D80 (annex § 二.3): the success toast's first words, then the list (utils/cutoffFixLines). */
          DONE_LEAD: doneToastLead(HUB_BUTTON_KIND.fixCutoffLsed.label),
          /**
           * W3.3 (Spec 5 「null → the words are left out」): the lines above when no completed
           * 課表上傳 is known (useLatestUploadRangeEnd → null) — no 「課表已上傳到」, no
           * 已上傳／尚未上傳; and HEADER for a timeslot id that names no teacher.
           */
          CURRENT_NO_RANGE: (lsed: string) => `目前 最後約課日 ${lsed}`,
          PICKED_NO_RANGE: (picked: string, currentMd: string, weeks: number) =>
            `已選：${picked} · 比目前的 ${currentMd} 晚 ${weeks} 週`,
          STEP2_HEADER_NO_RANGE: (from: string, to: string, weeks: number) =>
            `最後約課日 ${from} → ${to} (延後 ${weeks} 週)`,
          HEADER_NO_TEACHER: (student: string, classLabel: string) => `${student} · ${classLabel}`,
        },
        /**
         * 使用說明 item 4 (mockup § 10.5 之後, Spec 7) — the body after the bold 「4)」 the
         * explainer draws (as items 1–3). D63 item 4 (PO 2026-10-25): + how to undo a wrong 補正.
         * D75 / D80 (annex § 三): 「不處理不會消失。」 → each upload re-checks a pending row.
         */
        EXPLAINER_ITEM_4: `約課切斷確認：最後約課日 設在上傳範圍的最後 ${SESSION_ENROLLMENT_CONFIG.LSED_CUTOFF_WINDOW_DAYS} 天內，BF 可能還有後面的課。請到 BF 查這一期的最後一堂：按 處理，在視窗選 補正最後約課日，再在日曆上點 BF 的最後一堂 (可以是尚未上傳的日期)；真的到這裡結束就按 忽略。之後上傳的課表 超過 最後約課日 ${SESSION_ENROLLMENT_CONFIG.LSED_CUTOFF_WINDOW_DAYS} 天，而且 最後約課日 之後沒有約課，這一列會自動移除。補正錯了：用 修改最後約課日 改回上傳範圍內的日期，這一列會再出現，再 補正 一次。`,
        /**
         * Spec 7 / D50: inserted after EXPLAINER_STAR_ANCHOR of EXPLAINER_OWNED_ELSEWHERE (the
         * ★ line: students in 續報 or 轉班 處理中 are not listed again here — except these rows).
         * § 10.5 drew 「 (約課切斷確認 的列在 續報 中仍會列出)」 before D50; § 11.2 (PO 「11.2 ok」,
         * D50) changed it to 「(約課切斷確認 的列除外)」 — the words used here (W3.4／W3.5; one
         * clause for 續報 and 轉班, as the ※ line's D39 「除外」).
         */
        EXPLAINER_STAR_PARENTHESIS: LSED_CUTOFF_EXCEPT_PARENTHESIS,
        /** The words of the ★ line the parenthesis above follows (once in that line). */
        EXPLAINER_STAR_ANCHOR: '不在這裡重複列出',
        /** Spec 7 / D39 (PO 10/13 「just say 約課切斷確認列除外」): appended to the ※ line. */
        EXPLAINER_IGNORED_PARENTHESIS: LSED_CUTOFF_EXCEPT_PARENTHESIS,
        /** 使用說明's middle zone (mockup § 10.5 之後). */
        POLICY_TITLE: '【政策】',
        POLICY_BULLETS: [
          '• 確認好的學期決定最後約課日，之後不改。',
          '• 後續取消約課都是「請假補課」用途，不延後最後約課日 (續報繳費時間以它為準)。',
          '• 延後最後約課日＝名額被佔用更久；只有被 BF 上傳範圍切斷時，用 補正最後約課日。',
        ],
        /**
         * Spec 8 (D20 / D21 / D53): every 約課通知 window (RENEWAL_TEXT_STEPS.BOOKING) while the
         * student's 約課切斷確認 is pending for the current 最後約課日; the 確定轉入 draft keeps
         * the date test (W4.3).
         */
        WARNING: '約課可能有被切斷，先確認 BF 最後約課日是否正確！',
      },
    },
    /**
     * 2026-10-24 (約課切斷確認 Spec 13, D47–D49 / D52 / D54–D56 / D62; mockup § 12.2 / § 15.1) —
     * the fifth top tab of 課務總覽 (after 候補; 「通知」 until 2026-11-01, 「約課通知」 since — D3); the page inside keeps its title
     * 「約課通知待發」 (D49). SOURCE_LABEL = the 來源 of a notice (bookingNoticeSource.page), in
     * BOOKING_NOTICE_SOURCE_PAGES order (the filter: 全部 · 續報 · 轉班 · 新學期 · 待確認 · 排課時段表).
     * CAPTION = the page's purpose sentence on its identity line (W3N.1): the line draws the bold
     * title 「約課通知待發」 (HUB_PAGE_IDENTITY.renewalNotify, unchanged — D49) and then this
     * sentence, which is how Spec 13's 「約課通知待發：家長還沒收到…」 reads on screen (mockup
     * § 12.2 draws title + sentence; the title is not repeated inside the sentence).
     */
    NOTIFY: {
      /** 體驗回饋 → 課務總覽 D3 (2026-11-01): the tab is 約課通知 everywhere the tab name appears; was 通知. */
      TAB: '約課通知',
      /** 約課切斷確認 D70 (4) (annex § 三.3): no 「 · 等最久的在前」 tail — the list shows its order. */
      CAPTION: '家長還沒收到最新的 最後約課日，請送出真實日期的約課通知並標記完成',
      /** The tab's empty list (one sentence, no header row — W0.14; was HUB.RENEWAL.EMPTY_NOTIFY). */
      EMPTY: '目前沒有約課通知待發的學生',
      /** The 來源 filter's first chip (W3N.2). */
      SOURCE_ALL: '全部',
      SOURCE_LABEL: {
        renewal: '續報',
        transfer: '轉班',
        new: '新學期',
        review: '待確認',
        timeslot: '排課時段表',
      } satisfies Record<BookingNoticeSourcePage, string>,
    },
    /**
     * 體驗回饋 tab (體驗回饋 → 課務總覽, spec docs/TRIAL_FEEDBACK_HUB_SPEC.md § 5; plan
     * tasks/todo-trial-feedback-hub.md D5, D6, D15, D20, P7–P9). Read by utils/feedbackHubView.ts
     * and the tab's components. Strings that already exist are NOT copied here (one home each):
     * - 待通知家長's two bands = HUB.BANDS.NOTIFY (等超過 3 天 · 第 4 天起 / 3 天內 · 依等待天數，
     *   久的在前) — the same successNotifyAge rule as 約課通知 (D9, F-A10);
     * - the column header 等待天數 = HUB.COLUMNS.WAIT_DAYS; 第 N 天 = HUB.ROW.DAY_N + N + HUB.ROW.DAYS;
     * - 設定協同班主教 = FIRST_TRIAL.COOP_TEACHERS.BUTTON; 「第 2 份 · 補發」 = FEEDBACK_PAGE.ORDINAL +
     *   the REASON_LABELS;
     * - the 體驗回饋 page's 待建立 panel keeps FIRST_TRIAL.PENDING_PANEL.LOAD_FAILED／REVIEW_LOAD_FAILED
     *   (its button is 重新整理); this tab has its own lines below, naming its 刷新 (B59);
     * - the tab's ⓘ sentence and its 使用說明 link = HUB_PAGE_IDENTITY.trialFeedback (every ⓘ
     *   reads that table; hubPageIdentity.ts cannot import this file).
     */
    FEEDBACK: {
      /**
       * Sub-tabs in work order (D5: 待建立 › 等老師填寫 › 等主管審核 › 待通知家長 › 已完成 ｜ 停用 —
       * a thin divider before 停用). The keys are the sub-tab ids (FeedbackHubSubTab).
       */
      SUB_TABS: {
        pending: '待建立',
        teacher: '等老師填寫',
        supervisor: '等主管審核',
        parent: '待通知家長',
        completed: '已完成',
        disabled: '停用',
      },
      /**
       * 等老師填寫's two bands (P8, mockup § 四): red = the LINE failed, was never sent, or is
       * still 傳送中 after FIRST_TRIAL_DISPLAY.LINE_PENDING_STALE_MS; green = sent or 已手動通知.
       * The green band has no caption: the sub-tab already says 等老師填寫 (say each fact once).
       */
      TEACHER_BANDS: {
        red: { label: '老師沒收到通知', caption: 'LINE 沒送到，或還沒通知老師' },
        green: { label: '已通知老師', caption: '' },
      },
      /**
       * The third column's header on 待建立 (card B1: the cell draws the 簽到表's 首堂課 pill); the
       * form sub-tabs keep FIRST_TRIAL.FEEDBACK_PAGE.STAGE_COLUMN 「階段」. A header of its own,
       * not the pill's state word FIRST_TRIAL.PILL.FIRST (same text, another meaning).
       */
      PENDING_STAGE_COLUMN: '首堂課',
      /**
       * D6: the grey line under the 學生 cell when the child's name on the form differs from the
       * BF 會員姓名 (mockup § 五 #14: 「回饋表：小孩 Coco」).
       */
      FORM_NAME: (child: string) => `回饋表：小孩 ${child}`,
      /** D20: under a blocked 通知家長 while the Hub's PDF copy is being saved (pdfCopyState pending). */
      PDF_SAVING: 'PDF 儲存中',
      /**
       * Q8: in place of PDF_SAVING once the hourly job has stopped trying on its own
       * (`pdfCopyAttempts` ≥ SYSTEM_CONSTANTS.FEEDBACK_PDF_COPY_MAX_AUTO_ATTEMPTS); still blocked,
       * 重新儲存 still beside it.
       */
      PDF_SAVE_FAILED: 'PDF 儲存失敗',
      /** fix-choices B114: the composer's blocked notice — why, and where to fix it. */
      PDF_SAVING_WHERE: 'PDF 儲存中 · 請到 課務總覽 › 體驗回饋 › 待通知家長 按 重新儲存',
      /** The same notice once the automatic tries have stopped (Q8, PDF_SAVE_FAILED). */
      PDF_SAVE_FAILED_WHERE: 'PDF 儲存失敗 · 請到 課務總覽 › 體驗回饋 › 待通知家長 按 重新儲存',
      /** The same notice while the parent link is stopped (Q23, ROW_LABELS.LINK_STOPPED_HINT). */
      LINK_STOPPED_WHERE: '連結已停止 · 請到 課務總覽 › 體驗回饋 › 待通知家長 按 重新產生連結',
      /**
       * The composer's warning when the picked row has no PDF link yet (ComposerFeedbackPicker;
       * moved from the component, A2 — the pre-existing words named the vendor). The picker lists
       * approved forms only, so a missing link means the PDF copy is not saved yet (D32-10): the
       * line names the missing thing and where it is handled, never a guessed reason.
       */
      PDF_LINK_MISSING_WHERE:
        '⚠️ 這筆回饋還沒有 PDF 連結，請到 課務總覽 › 體驗回饋 › 待通知家長 查看。',
      /** D20: beside it — tries the save again right away. */
      PDF_RETRY: '重新儲存',
      /**
       * fix-choices B74: inside the 通知家長 window of a form whose copy was never made and whose
       * Jotform submission is gone (pdfCopyState gone, no pdfCopy). 通知家長 stays usable (D17).
       */
      PDF_SOURCE_GONE: '原始 PDF 已不存在，這個連結打不開',
      /** One form named in a confirm or a toast (B103, B110): 「#1234 陳小安」. */
      ROW_LABEL: (simpleId: string, child: string) =>
        [simpleId ? `#${simpleId}` : '', child].filter(Boolean).join(' '),
      /** ⋮ on a denied form (B111): copies the teacher's 重新編輯 link to pass on. */
      COPY_EDIT_LINK: '複製 重新編輯 連結',
      COPY_EDIT_LINK_OK: '已複製 重新編輯 連結',
      /**
       * ⋮ items for the parent link (utils/feedbackActionsView keys `send-original`,
       * `stop-link`, `regenerate-link`): 改寄原始連結 = 主管, after the Hub's copy can no longer
       * be made by itself (fix-choices Q15 b); 停止連結／重新產生連結 = 課務＋主管 (Q23).
       */
      LINK_MENU: {
        SEND_ORIGINAL: '改寄原始連結',
        STOP: '停止連結',
        REGENERATE: '重新產生連結',
      },
      /**
       * The confirms and toasts of those three items (composables/useFeedbackLinkActions, plan
       * R4.12; decision page Q15／Q23). `form` = ROW_LABEL with the full 會員姓名 (「#Lu04 廖曉君 -
       * 孫廷睿」); the titles are the LINK_MENU words; the buttons 確認／取消. 停止連結 asks twice
       * (Golden Rule 12 b): STOP_CONFIRM names the form and what happens, then STOP_FINAL restates
       * the record the function receives (simpleId + the PDF version) — only its 確認 stops the
       * link. 重新產生連結 and 改寄原始連結 ask once (nothing is lost). SEND_ORIGINAL_CONFIRM names
       * the login caveat once; the parent message carries ORIGINAL_LINK_NOTE on its own.
       */
      LINK_ACTIONS: {
        STOP_CONFIRM: (form: string) =>
          `確定停止 ${form} 的家長連結？家長已收到的連結將打不開，無法復原；之後可按 重新產生連結 產生新連結。`,
        STOP_FINAL_TITLE: '再次確認：停止連結',
        STOP_FINAL: (simpleId: string, version: number) =>
          `#${simpleId} · 第 ${version} 版 的家長連結將停止，無法復原`,
        REGENERATE_CONFIRM: (form: string) =>
          `確定重新產生 ${form} 的家長連結？已停止的舊連結仍打不開；產生新連結後，請重新通知家長。`,
        SEND_ORIGINAL_CONFIRM: (form: string) =>
          `確定為 ${form} 改寄原始連結？之後按 通知家長 會寄出原始連結，開啟時可能需要登入。`,
        STOP_DONE: '連結已停止',
        REGENERATE_DONE: '已產生新連結',
        SEND_ORIGINAL_DONE: '已改為寄原始連結',
        /** The row has no saved PDF version to name (the ⋮ offers neither item then, R3.3). */
        NO_COPY: '這份回饋表還沒有存好的 PDF',
      },
      /**
       * Q15 b: the line the parent message carries under the link once a 主管 has allowed
       * 改寄原始連結 and the Hub still has no copy (feedbackActionsView feedbackParentLinkNote).
       * The original address may ask for a login; no vendor name (A2).
       */
      ORIGINAL_LINK_NOTE: '※ 此連結開啟時可能需要登入。',
      /** D15: the worded button at the right end of the toolbar — the 體驗回饋 page's words (:34). */
      REFRESH_BUTTON: '刷新 & 取得主管審核狀態',
      /** 篩選老師 on the toolbar's left — the page's select placeholder and first option (:47, :223). */
      TEACHER_FILTER: '篩選老師',
      TEACHER_ALL: '所有老師',
      /**
       * 刷新's answers (fix-choices B64 — from the server's `updated` and the ids sent): nothing in
       * 等主管審核; N asked, M approved; N asked, nothing new. No vendor name (A2).
       */
      REFRESH_NOTHING: '沒有等主管審核的回饋表；已重新讀取 待建立',
      REFRESH_FOUND: (asked: number, approved: number) =>
        `已查 ${asked} 份審核狀態：${approved} 份已審核，已移到 待通知家長`,
      REFRESH_NONE_NEW: (asked: number) => `已查 ${asked} 份審核狀態：沒有新的審核結果`,
      /**
       * 刷新 where Jotform did not answer for some forms (F-B6, B103): the rows by name
       * (ROW_LABEL, joined with 、), forms not on screen as 「另 N 份」.
       */
      REFRESH_NOT_ANSWERED: (rows: string) => `${rows} 的審核狀態沒有查到，請再按一次`,
      REFRESH_NOT_ANSWERED_MORE: (n: number) => `另 ${n} 份`,
      /** …when none of them is on screen: 「3 份」. */
      REFRESH_NOT_ANSWERED_COUNT: (n: number) => `${n} 份`,
      /** 刷新's edit sweep (fix-choices B120): Jotform's edit list could not be read. */
      SWEEP_LIST_FAILED: '無法取得老師的修改紀錄，請再按一次',
      /** 刷新's edit sweep (B120): `n` edited forms whose new PDF copy failed. */
      SWEEP_SAVE_FAILED: (n: number) => `有 ${n} 份修改後的 PDF 沒有存好，系統每小時會再試`,
      /** 重新儲存 (D20) saved a new copy of the PDF. */
      RETRY_PDF_DONE: 'PDF 已重新儲存',
      /** 重新儲存 found the form unchanged since the saved copy — the row is free (B64). */
      RETRY_PDF_UNCHANGED: 'PDF 已存好 (內容沒有變更)',
      /** 重新儲存 failed and the server gave no reason. */
      RETRY_PDF_FAILED: 'PDF 儲存失敗，請稍後再試',
      /** Title of the error toast when 刷新 reports forms Jotform did not answer for (W6.5). */
      REFRESH_FAILED_TITLE: '審核狀態更新',
      /** Title of the error toast when 重新儲存 answers not ok (W6.5). */
      PDF_RETRY_FAILED_TITLE: 'PDF 儲存',
      /**
       * Empty sub-tab: one sentence naming what is not there (W0.14; hint names the missing).
       * 待建立 names no time window (D24 replaces B56's 「近 N 天」): its rows never leave on
       * their own, so an empty list means nothing is waiting, however old.
       */
      EMPTY: {
        pending: '目前沒有要建立回饋表的首堂課',
        teacher: '目前沒有等老師填寫的回饋表',
        supervisor: '目前沒有等主管審核的回饋表',
        parent: '目前沒有待通知家長的回饋表',
        completed: '目前沒有已完成的回饋表',
        disabled: '目前沒有停用的回饋表',
      },
      /** The empty sentence while 篩選老師 narrows the list: names the teacher too (W0.14). */
      FILTERED_EMPTY: (teacher: string, empty: string) => `篩選老師 ${teacher}：${empty}`,
      /**
       * 已完成 narrowed by 篩選老師 while more pages exist (fix-choices B46): only the loaded
       * pages were looked at — 載入更多 stays under it.
       */
      FILTERED_EMPTY_LOADED: (teacher: string) => `篩選老師 ${teacher}：已載入的已完成回饋表裡沒有`,
      /**
       * The open-forms listener or 已完成's first page failed (fix-choices B51): instead of the
       * empty sentence, and no count on the stage sub-tabs.
       */
      LIST_LOAD_FAILED: '回饋表清單載入失敗，請按 刷新 再試一次',
      /** 待建立's failure lines on this tab name its own 刷新 button (fix-choices B59). */
      PENDING_LOAD_FAILED: '待建立清單載入失敗，請按 刷新 再試一次',
      PENDING_REVIEW_LOAD_FAILED: '首堂待確認清單載入失敗，請按 刷新 再試一次',
      /**
       * A LATER 待建立 re-read failed (fix-choices B112): the last good list stays drawn and
       * counted; `hm` = when it was read (Taipei HH:mm).
       */
      PENDING_STALE: (hm: string) =>
        `待建立清單更新失敗，顯示的是 ${hm} 讀到的清單，請按 刷新 再試一次`,
      /**
       * A 待建立 row (R4.7, decision page card B1): the hover of the class time line, which
       * opens that day's class on 簽到作業 in a new tab.
       */
      OPEN_SIGN_IN_SHEET: '開啟簽到表',
      /**
       * The one line under a 待建立 row's 首堂課 pill (pendingPillLineView): a stored verdict's
       * source and day — 「系統判定 · 10/31」, 「課務 Yihung · 10/31」 (`by` only for a person's
       * verdict; `md` = the hub's short date, '' when unknown).
       */
      PENDING_SOURCE_LINE: (source: string, by: string, md: string) =>
        [by ? `${source} ${by}` : source, md].filter(Boolean).join(' · '),
      /**
       * The same line on a ⚠ row: the two answers — 「家長 iPad 答「不是」· 系統判定「是」」.
       * `record` = the stored verdict's source (SOURCE_LABELS), or PENDING_RECORD.
       */
      PENDING_DISCREPANCY_LINE: (parentNo: boolean, record: string) =>
        parentNo
          ? `家長 iPad 答「不是」· ${record}「是」`
          : `家長 iPad 答「是」· ${record}「不是」`,
      /** The record's side of a ⚠ line when no stored verdict names its source. */
      PENDING_RECORD: '紀錄',
      /** The ⚠ line's colour (a CSS custom property, never hex): the orange of an open question. */
      PENDING_DISCREPANCY_COLOR_VAR: '--pay-panel-orange-text',
      /** End of a row's grey line under 階段 (mockup § 五 #10/#11): 「#1234 · Sherry 10/25 12:40 建立」. */
      META_CREATED: (whoWhen: string) => `${whoWhen} 建立`,
      /** 已完成 rows (fix-choices B73): 「Amy 11/2 通知」 from completedBy／completedAt. */
      META_NOTIFIED: (whoWhen: string) => `${whoWhen} 通知`,
      /** 停用 rows (fix-choices B72): 「Lucy 11/2 停用」 from disabledBy／disabledAt. */
      META_DISABLED: (whoWhen: string) => `${whoWhen} 停用`,
      /**
       * D29 (Q21／Q23): the coloured label line under the stage pill — a rare row event, never
       * words on the grey meta line. Words as the decision page draws them
       * (docs/tools/trial-feedback-hub-decisions-2026-11-02.html Q21／Q23); `v` = the PDF copy's
       * version, `mmdd` = MM/DD of the save／stop／new link, `mmdd2` = MM/DD of the 通知 after it,
       * `by` = who stopped the link. The state is chosen by feedbackHubRowView `rowLabel(doc)`;
       * the colour is ROW_LABEL_TONES. A version saved before any 通知 gets no label (D29).
       */
      ROW_LABELS: {
        VERSION_UNNOTIFIED: (v: number, mmdd: string) => `第 ${v} 版 · ${mmdd} 更新 · 未通知`,
        VERSION_NOTIFIED: (v: number, mmdd: string, mmdd2: string) =>
          `第 ${v} 版 · ${mmdd} 更新 · ${mmdd2} 通知`,
        LINK_STOPPED: (mmdd: string, by: string) => `連結已停止 · ${mmdd} ${by}`,
        LINK_NEW_UNNOTIFIED: (mmdd: string) => `新連結 · ${mmdd} · 未通知`,
        LINK_NEW_NOTIFIED: (mmdd: string, mmdd2: string) => `新連結 · ${mmdd} · ${mmdd2} 通知`,
        /** Hover of the greyed 通知家長 (and the ⋮ item) while the link is stopped (Q23). */
        LINK_STOPPED_HINT: '連結已停止，請先 重新產生連結',
      },
      /**
       * D29: the label line's colour by state — orange = the parent has not been told (an
       * action), grey = told (the fact stays drawn), red = 連結已停止.
       */
      ROW_LABEL_TONES: {
        VERSION_UNNOTIFIED: 'orange',
        VERSION_NOTIFIED: 'grey',
        LINK_STOPPED: 'red',
        LINK_NEW_UNNOTIFIED: 'orange',
        LINK_NEW_NOTIFIED: 'grey',
      } as const,
      /** 操作 of a 停用 row (mockup § 五 #22) — the 體驗回饋 page's tag. */
      DISABLED_TAG: '已停用',
      /** 已完成 / 停用: the next page (spec § 5.5). */
      LOAD_MORE: '載入更多',
      /** The ⓘ's 使用說明: the 體驗回饋 guide, opened in a new tab. */
      GUIDE_PATH: '/guides/first-trial-feedback-guide.html',
      /**
       * D25 (decision page § B6): the search box at the left of the toolbar on every sub-tab
       * (FeedbackHubSearch) and its results across every stage (FeedbackHubSearchResults). The
       * numbers come from FEEDBACK_SEARCH.
       */
      SEARCH: {
        PLACEHOLDER: '會員姓名／編號',
        /** Under the toolbar while fewer than FEEDBACK_SEARCH.MIN_CHARS characters are typed. */
        TOO_SHORT: (n: number) => `請輸入至少 ${n} 個字`,
        /**
         * A server query came back with FEEDBACK_SEARCH.SERVER_LIMIT docs (n): more may exist.
         * R8 UX-8: names what is missing and what to type, never how the system reads.
         */
        TRUNCATED: (n: number) => `符合的超過 ${n} 筆，只列出前 ${n} 筆，請輸入完整的姓名或編號`,
        /** The server lookup failed: only the rows already loaded were searched (UX-8). */
        SERVER_FAILED: '搜尋沒有完成，只列出已找到的，請再試一次',
        /**
         * Nothing matched (UX-8): the typed text, and what was looked for — a person may exist
         * without a form. Not drawn beside SERVER_FAILED (the search did not finish).
         */
        EMPTY: (query: string) => `沒有符合「${query}」的回饋表或首堂課`,
        /** The results list's accessible name (read by a screen reader, not drawn). */
        RESULTS_LABEL: (text: string) => `搜尋「${text}」`,
      },
    },
    /**
     * 約課切斷確認 D76 / D80 (PO 2026-10-25 「附錄 照畫的」 — docs/tools/lsed-cutoff-chart-mockup-
     * annex.html § 一 is the text; supersedes the coloured 「● 確認後」 line, D51 / D69): the FIRST
     * line of the 「點擊確認後：」 list in 補正最後約課日, 修改最後約課日, 結算續報, 確定轉入 (incl.
     * 維持 最後約課日) and 結算轉班 — only when the seat has a state. {name} = the state's on-screen
     * name (LIST_STATE_NAME_BY_STATE); which line a state gets is utils/listStateLine.
     */
    STATE_KEPT: (name: string) => `不會變更目前狀態：${name}`,
    STATE_LEFT: stateLeftLine,
    /** 轉班保留 × 修改 / 確定轉入 (a date or 維持) / 結算轉班 — the 轉班 completes. */
    STATE_LEFT_TRANSFER_HOLD: `${stateLeftLine('轉班保留')} (轉班 完成)`,
    /** 續報 › 近期未處理 rows (v9 § 5; text-fix A2.4 / A4.2 / A4.5 / B7 / B8). */
    MISSED: {
      LAPSED: '已通知·已逾期',
      CONTACTED_ON: (date: string) => `通知 ${date}`,
      ENDED_ON: (date: string) => `結束 ${date}`,
      ENDS_ON: (date: string) => `預計 ${date} 結束`,
      TOLD: (md: string) => `已通知 ${md}`,
      NOT_TOLD: '未通知',
      /** 已處理 on an archived row (review P6-9 — the same sentence as 轉班's). */
      HANDLED_CONFIRM: hubHandledConfirm('近期未處理'),
      /** The ending reason of an archived row (系統自動歸檔 gets the 「自動歸檔 —」 prefix). */
      AUTO_ARCHIVED: (reason: string) => `自動歸檔 — ${reason}`,
      NOT_RENEWING: '不續報',
      NOT_RENEWING_LONG_LEAVE: '不續報 · 暫停課程 (請長假)',
      NOT_RENEWING_OTHER: '不續報 · 其他',
      NOT_RENEWING_OTHER_NOTE: (note: string) => `不續報 · 其他：${note}`,
      /**
       * 不續報 › 已改報其他時段 (PO 2026-09-29, design § 2c): a decision, grey like every
       * 不續報; endingReasonNote = the new slot. One form everywhere (M2 review) —
       * 「不續報 · 已改報 週二 16:00 小水獺班」 (utils/renewalMissedRowView notRenewingLabel).
       */
      NOT_RENEWING_SWITCHED: '不續報 · 已改報其他時段',
      NOT_RENEWING_SWITCHED_TO: (slot: string) => `不續報 · 已改報 ${slot}`,
      REASON_LABELS: {
        ...SYSTEM_ENDING_REASON_LABELS,
        轉班: '已轉班',
      } as Record<string, string>,
    },
    /**
     * 轉班 tab (W4; v9 § 8 and the Q6a drawing for 待約課 / 已完成 / 未完成). Flag words from
     * text-fix A4.10 / A4.11 / A4.11b / A4.17 — 堂 everywhere. 原時段 takes 又約 (the parent
     * booked the OLD slot again), 新時段 takes 已約 (bookings arrived where the student is
     * going). The sub-state hovers are the old chips' tooltips.
     */
    TRANSFER: {
      TABLIST_LABEL: '轉班階段',
      // 原班 and 新班 are ONE column since M5 (PO 2026-09-29): COLUMNS.TRANSFER_CLASSES.
      COL_HOLD: '名額保留',
      /**
       * The record's one note (M5, PO 2026-09-29): its history window's field name, and the
       * warning when ✎ 編輯提案 saves while the row's cell is still saving the same note.
       */
      NOTE_FIELD: '轉班備註',
      NOTE_BUSY: '備註儲存中，請稍候再試',
      /**
       * ✎ 編輯提案 that removes a proposed 新班 (TransferProposalForm): the confirm names what
       * changes on the row, in the row's words (tasks/todo-renewal-pending-chart.md D19 (g), review
       * UX-11 — was 「停止追蹤／已約堂數會立即清除／下次 Excel 上傳後重新計算」). The lists are
       * 「• 時段」 lines; `count` = the removed classes.
       */
      PROPOSAL_TARGET_REMOVED: {
        /** D68 (P-08, PO 「A」): was 修改追蹤目標. */
        TITLE: '修改新班',
        CONTENT: (removedLines: string, addedLines: string, count: number): string => {
          const it = count > 1 ? '這些時段' : '這個時段';
          const added = addedLines ? `\n\n新加的「新班」時段：\n${addedLines}` : '';
          return `這一筆轉班的「新班」不再是以下時段：\n${removedLines}${added}\n\n確認後，列上不再有${it}的「新班」那一條。${it}的「已約」也不再算進這一筆轉班。\n\n是否確認修改？`;
        },
      },
      /** The 注意 column's first line (A3.7, PO round 4). */
      FROM_TEACHER: '老師建議',
      FROM_ADMIN: '課務登記',
      ORIGIN_HEAD: (source: string, md: string) => `${source} · ${md}`,
      /** 待約課 / 已完成 / 未完成 (the Q6a drawing): 「確定轉入 9/22」. */
      CONCLUDED_HEAD: (md: string) => `確定轉入 ${md}`,
      /**
       * The line under it (W8-F1, PO 2026-09-29 「fixed properly」): the latest restart of
       * the 14-day hold after 確定轉入 (holdRestart.action) — 「延長保留 9/29 · Name」 /
       * 「修改新班時段 9/29 · Name」. 2026-10-04 (轉班鎖定 B3): 暫時保留名額 set on a 未完成
       * record's new class puts it back to 待約課 — 「暫時保留名額 10/4 · Name」.
       */
      HOLD_RESTART_LABEL: {
        extend: '延長保留',
        'change-target': '修改新班時段',
        'special-hold': '暫時保留名額',
        // 移除暫時保留名額 on the 待約課's hold: the 14 days run again from that day.
        'special-hold-cleared': '移除暫時保留名額',
      },
      HOLD_RESTART_LINE: (label: string, md: string, by: string) =>
        by ? `${label} ${md} · ${by}` : `${label} ${md}`,
      // The hover of that line's 「N 天前」 (the age keeps its colours, PO Q5) is 安排中's
      // identity hover (HUB_PAGE_IDENTITY.transferArranging.hoverTitle, read by
      // utils/transferRowView.ts) — one sentence, review H-11.
      // SUGGESTION_STAMP (「建議內容 · Name」) removed 2026-10-14 (W7.3): the 老師建議 list has
      // its 建議內容 column, and the row's 「老師建議 · M/D · N 天前 · Name」 names the teacher.
      /** 名額保留 (待約課 / 未完成). */
      HOLD_LEFT: '保留剩',
      /**
       * W10.4 F14 (D26 (14), PO 2026-10-14 「不改門檻 (說明放滑過)」): 待約課's 保留剩 N 天 hover —
       * what it counts to and why the colour (utils/transferHoldCount: red within
       * TRANSFER_BF_WARNING_DAYS, green before — the 轉班 hold has no amber step). `md` = the
       * new class's hold day (確定轉入 + TRANSFER_BF_GRACE_DAYS).
       */
      HOLD_COUNT_RULE: (md: string) =>
        `算到 新班保留到期 ${md}；${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_WARNING_DAYS} 天內 紅`,
      DUE: '到期',
      DUE_ON: (date: string) => `${date} 到期`,
      EXPIRED_ON: (date: string) => `${date} 已到期`,
      /**
       * 2026-10-14 (續報・轉班 完成規則 W6.5, M-07 — utils/transferHoldCount): a 待約課 past its
       * hold day that the 03:30 grace expiry keeps (the new class still has a future 學期票券
       * lesson — the record's targetLastTermBookingAt): 「等新班約課」 over 「(最後一堂 M/D)」 in
       * place of 今日 到期, body colour, no red rail; the hover (DERIVED ⚑ wording).
       */
      WAITING_TARGET_BOOKINGS: '等新班約課',
      WAITING_TARGET_LAST: (md: string) => `(最後一堂 ${md})`,
      WAITING_TARGET_TITLE: (md: string) =>
        `保留期限已過，但新班還有 學期票券 約課到 ${md}，系統不會把這筆改成 未完成`,
      /**
       * 待約課 whose new class is on 暫時保留名額 (option S — decision 7, PO 2026-10-04: such a
       * 轉班 never turns 未完成 by itself): 名額保留 shows 「—」, no red rail, this hover.
       */
      HOLD_SPECIAL_TITLE: '暫時保留名額，不會到期',
      /**
       * Q13b (PO 2026-10-05): under that 「—」 — since when and by whom (the new class's
       * 設為特殊保留名額 entry, utils/transferHoldCount); amber once
       * TRANSFER_SPECIAL_HOLD_AGE_DAYS old, its hover saying why.
       */
      SPECIAL_HOLD_SINCE: (md: string, by: string) =>
        by ? `暫時保留名額 ${md} 起 · ${by}` : `暫時保留名額 ${md} 起`,
      SPECIAL_HOLD_SINCE_AGED_TITLE: (days: number) =>
        `已暫時保留 ${days} 天，請確認是否還需要；不需要時，請到 排課時段表 按「移除暫時保留名額」`,
      /**
       * Q13a (PO 2026-10-05): 安排中's 原班 line — the old class (轉出中) counts in 保留名額 only
       * until its 最後約課日 − RENEWAL_SYSTEM_CAP_DAYS (utils/transferClassLines
       * sourceHoldUntilOf); none while the old class is on 暫時保留名額 (its purple flag).
       */
      SOURCE_HOLD_UNTIL: (md: string) => `原班保留至 ${md}`,
      // 2026-10-14 (W9.5 UX-15): the three hovers name the ⋮ 其他處理 choice 設為暫時保留名額.
      SOURCE_HOLD_UNTIL_TITLE: `原班名額在最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天釋出；要留久一點，請在原班 ⋮ 其他處理 選 設為暫時保留名額`,
      /**
       * 2026-10-13 (續報・轉班 完成規則 W5.5, G-10 / G-12 / M-06 — utils/transferClassLines
       * sourceHoldUntilOf): the hover when the date is the 保留至 登記轉班 kept (the told date —
       * FrontendConfigUtils.transferSourceHoldUntilMs takes the later of it and 最後約課日 −
       * RENEWAL_SYSTEM_CAP_DAYS, W9.6 P5-02 (a); this hover only while the day IS the kept 保留至),
       * the line once that day has passed (M-06,
       * `md` = the day) and the red label in the 緊急 slot within RENEWAL_LOCK_URGENT_DAYS on
       * 安排中 (G-12 — label and hover in the spec's words). DERIVED ⚑: the two hovers.
       */
      SOURCE_HOLD_UNTIL_TOLD_TITLE:
        '原班名額保留到 登記轉班 前告知家長的日期；要留久一點，請在原班 ⋮ 其他處理 選 設為暫時保留名額',
      SOURCE_HOLD_RELEASED: '原班名額已釋出',
      SOURCE_HOLD_RELEASED_TITLE: (md: string) =>
        `${md} 已過，原班名額已釋出；家長若留原班，按 取消轉班 › 恢復 會重新保留名額，原班已滿會先提醒`,
      SOURCE_RELEASE_SOON: (md: string) => `原班名額 ${md} 釋出`,
      SOURCE_RELEASE_SOON_TITLE: '轉班還沒決定；那天原班名額釋出，之後只能 轉為候補',
      /**
       * 2026-10-15 (續報・轉班 完成規則 W10.9, D36 / D37 — utils/transferRefill; was W6.3's pinned
       * panel with 繼續轉班): 原班又約 on a 轉班 row. The row stays in its stage with its normal
       * button; 家長留原班 sits beside it from LSED_REVIEW_MIN_COUNT lessons. LABEL = the red label
       * in the 緊急 slot at AUTO_TAG_THRESHOLD+ (`n` = 安排中's sourceBookingsAfter.count, 待約課's
       * transferSourceRefillCount); 3–7 keeps 「原時段又約 N 堂」 (SOURCE_BOOKED). The hovers = the
       * dates (or the 原班 最後約課日) + what 家長留原班 does (D36 (b)); SEAT_LINE = the bubble's /
       * window's first line, the PO's words (D36 (d)) — SEAT_LINE_DATE_ONLY when the 3–7 window's
       * pick does not start a new term (the server writes a 修改 — P4 by fact; review F-L6);
       * LEAD_PARTIAL_AWAITING names no 確定轉入 (the lessons may predate it — review F-M1) and is
       * also 未完成's information hover (review F-L9); KEEP_CONFIRM = the 8+ bubble's sentence
       * (`target` = the new class's on-screen name whose 保留名額 is released; '' = none — 安排中, or
       * the new class on 暫時保留名額); BLOCKED_SPECIAL_HOLD = the 3–7 refusal (D37 (1)), the twin
       * of the server's ERROR_KEEP_SOURCE_SPECIAL_HOLD.
       */
      SOURCE_REFILL: {
        LABEL: (n: number) => `原班已約滿 ${n} 堂`,
        LEAD_ARRANGING: '登記轉班 之後，Excel 在原班又約滿一期。',
        LEAD_AWAITING: '確定轉入 之後，Excel 在原班又約滿一期，原班已重新保留名額。',
        LEAD_PARTIAL_AWAITING: (n: number) => `原班在最後約課日之後又約了 ${n} 堂。`,
        LSED_LINE: (md: string) => `原班最後約課日：${md}`,
        KEEP_LINE_FULL:
          '家長留原班：轉班關閉，記錄「取消轉班 · 家長續報原班」；原班回到續報週期，列入 約課通知待發。',
        KEEP_LINE_PARTIAL:
          '家長留原班：選原班的最後約課日 (同 結算續報)；轉班關閉，記錄「取消轉班 · 家長續報原班」；原班保留名額，列入 約課通知待發。',
        KEEP_LINE_TARGET: (target: string) => `新班 ${target} 的保留名額會釋出。`,
        SEAT_LINE: (md: string, n: number) =>
          `原班 保留名額：最後約課日 ${md} (這 ${n} 堂算新一期)`,
        // D43 B7: also the shared line of every restore (取消轉班, 轉為候補's 原班 繼續保留 —
        // utils/transferCancel restoredSeatLine): `slot` = the class's name ('' = none — the
        // window names it in its title), `md` '' = no date; SEAT_RELEASED_LINE once the old
        // class's hold day (FrontendConfigUtils.transferSourceHoldUntilMs) has passed.
        SEAT_LINE_DATE_ONLY: (md: string, slot = '') =>
          `原班${slot ? ` ${slot}` : ''} 保留名額${md ? `：最後約課日 ${md}` : ''}`,
        SEAT_RELEASED_LINE: (md: string) => `原班名額已於 ${md} 釋出`,
        KEEP_CONFIRM: (target: string) =>
          `轉班關閉${target ? `；新班 ${target} 的保留名額會釋出` : ''}`,
        KEEP_DONE: (name: string) => `已取消轉班：${name} (家長續報原班)`,
        KEEP_DONE_SETTLED: (name: string, md: string) =>
          `已取消轉班：${name} (家長續報原班)；原班最後約課日 ${md}`,
        BLOCKED_SPECIAL_HOLD:
          '原班是 暫時保留名額，家長留原班 不能在這裡選最後約課日；請按 取消轉班，再到 續報 處理原班',
        /**
         * D43 B12 (2026-10-15): the same refusal on 未完成, which has no 取消轉班 — the twin of
         * the server's ERROR_KEEP_SOURCE_SPECIAL_HOLD_UNFINISHED (utils/transferRefill).
         */
        BLOCKED_SPECIAL_HOLD_UNFINISHED:
          '原班是 暫時保留名額，家長留原班 不能在這裡選最後約課日；請按 已處理，再到 續報 處理原班',
        /**
         * D43 B12 / R4 (D46): 未完成 whose new class is live in the feed again — a student never
         * holds two seats; the pattern of BLOCKED_EXTEND_ALIVE (utils/transferRowActions).
         */
        BLOCKED_TARGET_ALIVE: '新班已保留名額，無法按 家長留原班；請按列上的 已處理',
        /**
         * D43 B8 / D44 / D45 (R10 — window lines, never a message): 確定轉入 on a row whose old
         * class was booked again from LSED_REVIEW_MIN_COUNT lessons (utils/transferConfirmIn
         * confirmInRefillNotices); `keep` / `restore` = the buttons' names from their homes
         * (transferActionLabel('parentKeptSource'), RESERVATION_ACTION_LABEL_BY_KIND.restore).
         * The old class on 暫時保留名額 (確定轉入 keeps it — B5): BOOKED_SPECIAL_HOLD alone.
         */
        CONFIRM_IN_BOOKED: (n: number) => `原班已約的 ${n} 堂：BF 約課不會取消，原班不再保留名額`,
        CONFIRM_IN_BOOKED_SPECIAL_HOLD: (n: number) =>
          `原班已約的 ${n} 堂：BF 約課不會取消，原班仍是 暫時保留名額`,
        CONFIRM_IN_KEEP_LATER: (keep: string, restore: string) =>
          `家長之後想回原班：轉班完成前按 ${keep}；完成後到 排課時段表 › 原班 › ${restore}。`,
      },
      /**
       * 2026-10-13 (W5.5, G-12 / D5 — utils/transferRenewalRestore): the white label of a 轉班 whose
       * old class had a 首次通知 when 登記轉班 fired (the record's sourceRenewalAtRegistration) and
       * would come back on 取消轉班; its hover; the 排課時段表 待轉出 chip's extra hover line.
       */
      RENEWAL_KEPT_LABEL: '續報已通知',
      RENEWAL_KEPT_TITLE: (contactMd: string, toldMd: string) =>
        `首次通知 ${contactMd}，告知家長保留至 ${toldMd}；取消轉班 時可恢復`,
      RENEWAL_KEPT_CHIP_LINE: (title: string) => `續報已通知：${title}`,
      /**
       * 2026-10-14 (W9.5 VR-05): the 排課時段表 待轉出 chip's base hover (was inline in
       * composables/useEnrollmentStatusChips/transferChips.ts; RENEWAL_KEPT_CHIP_LINE follows it).
       */
      TRANSFER_OUT_CHIP_TITLE: '已登記轉班、準備轉出此時段。轉班完成前此名額仍保留。',
      /**
       * 2026-10-14 (W9.5 VR-05): 取消轉班's 安排中 sentence (head + the old class in bold + tail)
       * and its 取消原因 field (was inline in components/TransferCancelPanel.vue).
       */
      CANCEL_ASK_HEAD: (child: string) => `確認取消 ${child} 的轉班？學生將留在原時段`,
      CANCEL_ASK_TAIL: '，不會轉至其他時段。',
      CANCEL_REASON_LABEL: '取消原因 (選填)',
      CANCEL_REASON_PLACEHOLDER: '取消原因說明 (選填)',
      /**
       * 2026-10-14 (W9.5 UX-09): the two 登記轉班 forms (TransferRegisterModal /
       * TransferProposalForm) — the new-class field takes the column head's word 新班時段 (was
       * 推薦時段 / 提案時段); their 原班 label reads HANDLE.SOURCE_LABEL.
       */
      REGISTER_FORM: {
        TARGET_LABEL: '新班時段',
        TARGET_PLACEHOLDER: '選擇新班時段 (必填，可多選)',
        TARGET_REQUIRED: '請選擇至少一個新班時段',
        TARGET_FIRST: '請先選擇新班時段',
      },
      /**
       * 2026-10-14 (W9.6 P1-01, D24 — utils/transferCancellation): the part 恢復 adds to the
       * record's own 轉班取消 entry, 「已恢復 續報 狀態 (保留至 M/D)」 — the 學生紀錄 取消轉班 card
       * finds it by this prefix. MIRROR of the backend's (check-config-mirror).
       */
      RESTORED_RENEWAL_LOG_PREFIX: '已恢復 續報 狀態',
      /**
       * 2026-10-13 (W5.4 / W5.2b, G-11 / P7 — components/RenewalRestoreChoice): 取消轉班 and 轉為候補
       * (原班 繼續保留) when the old class had a 首次通知 and is in renewal time — the line on top,
       * the two required choices with their hovers (the plan's words), the disabled button's
       * hover, the 取消轉班 toast per outcome, and 轉為候補's 原班 line after 恢復 (DERIVED ⚑).
       * W9.6 P6-07 (D24, K-01: show 保留至 before a date-changing write): RESTORE_TITLE and
       * CONVERT_KEEP_LINE name the 保留至 the server will write — `md` =
       * FrontendConfigUtils.computeRenewalLockMs(最後約課日, now) (utils/transferRenewalRestore).
       */
      RENEWAL_RESTORE: {
        BEFORE_LINE: (md: string) => `登記轉班 前這一列是 已通知·待確認 (保留至 ${md})`,
        RESTORE: '恢復 續報 狀態',
        RESTORE_TITLE: (md: string) => `回到 已通知·待確認，保留至 ${md}`,
        NO_RESTORE: '不恢復',
        NO_RESTORE_TITLE: '回到 待通知',
        REQUIRED: '請先選擇 恢復 續報 狀態 或 不恢復',
        CANCELLED_TOAST: {
          restored: (student: string) => `已取消轉班：${student} (已恢復 續報 狀態)`,
          notRestored: (student: string) => `已取消轉班：${student} (未恢復 續報 狀態)`,
        },
        // D43 B7: `seatLine` = the shared seat line (utils/transferCancel restoredSeatLine — was
        // 「原班 {班名} 恢復在籍」); 恢復 writes a new 保留至, so it is always the held form.
        CONVERT_KEEP_LINE: (seatLine: string, md: string) =>
          `${seatLine}，續報 回到 已通知·待確認 (保留至 ${md})`,
      },
      /**
       * 2026-10-13 (W5.3, D6 — PO: 「…it must warn so, and explain that transfer will take
       * precedence, till it's canceled or completed…」; utils/registerRenewalWarning): the amber
       * block of 登記轉班 (排課時段表) and of 老師建議 › 處理 › 登記轉班 when the old class is in a
       * 續報 state. TITLE = the row's state line (SET_RESERVATION.STATE_LINE without its
       * 「續報：」); ①②③ the plan's proposed words (the PO confirms them at W10); `who` = 這一列
       * (排課時段表) / 原班 (the 老師建議 window); `md` = the told 保留至, else 最後約課日 − 14 天;
       * the confirm button's label while the block shows.
       */
      REGISTER_RENEWAL_WARNING: {
        TITLE: (state: string) => `這位學生正在續報中：${state}`,
        WHO_ROW: '這一列',
        WHO_SOURCE: '原班',
        PRECEDENCE: (who: string) =>
          `① 登記轉班 後，${who}離開 續報 清單：不再提醒 通知續報 或 再次提醒，轉班 優先，直到 取消轉班 或 確定轉入。`,
        HOLD: (md: string) =>
          `② 原班名額保留到 ${md} 不變 (轉班 列會寫 原班保留至 ${md}，剩 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_LOCK_URGENT_DAYS} 天時提醒)；過了那天名額釋出。原班約課全部移到新班並上傳後，名額當場釋出。`,
        /** DERIVED ⚑: ② when that day has already passed (已過期未通知 — the seat is out already, M-06). */
        HOLD_RELEASED: (md: string) => `② 原班名額已在 ${md} 釋出 (轉班 列會寫 原班名額已釋出)。`,
        CANCEL:
          '③ 家長若留原班：到 轉班 按 取消轉班 — 已通知過的可選 恢復 續報 狀態 (回到 已通知·待確認) 或 不恢復 (回到 待通知)；原班名額已釋出的話，恢復 會重新保留名額，原班已滿會先提醒。',
        CONTINUE: '繼續登記轉班',
      },
      /**
       * 2026-10-13 (W5.3 — TransferExplainer, the 使用說明): the note under 常見情況 (G-10 / G-11:
       * 登記轉班 keeps the 續報 notice, 取消轉班 can restore it — was 「放進 安排中 會清掉原班的
       * 續報 通知紀錄；…續報 要重新通知」) and item 7's 新建議 sentence (H-02 / H-03: a 新建議
       * never closes by itself; 原班約滿 is the admin's call — was 「隔一晚凌晨也自動關閉」).
       */
      EXPLAINER_RENEWAL_KEPT:
        '登記轉班 不會清掉原班的 續報 通知：原班名額保留到告知家長的日期。之後決定不轉 (取消轉班)，在續報時間內已通知過的可選 恢復 續報 狀態 或 不恢復。',
      EXPLAINER_SUGGESTION_KEPT:
        '老師建議 的學生已離開原班 (不續報、轉到別班、這一期已結束)，建議不會自動關閉：原班狀態 寫 原班已結束 (或 已不續報、已轉出)，由課務 處理 › 已另行安排；安排中 的原班又約，家長留原班 就按 家長留原班。',
      EXTEND_HOLD: `延長保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天`,
      /**
       * 使用說明 › 安排中's seat sentence (P7, PO 2026-10-02 「改說明」 — was 「原班名額照樣保留
       * (直到原班的約課上完)」): after 登記轉班 the source seat counts until 最後約課日 −
       * RENEWAL_SYSTEM_CAP_DAYS, the same release rule as a student not notified for 續報.
       * 2026-10-04 (轉班鎖定 Phase 2b, the PO's wording rule — never name 候補 as a
       * consequence): 「之後開放給候補」 → 「之後釋出保留名額」.
       */
      EXPLAINER_ARRANGING_SEAT: `原班名額保留到最後約課日前 ${SESSION_ENROLLMENT_CONFIG.RENEWAL_SYSTEM_CAP_DAYS} 天，之後釋出保留名額 (學生照樣上完已約的課)`,
      /**
       * 2026-10-14 (W9.5 UX-13): 使用說明 › 老師建議's reply sentence — the page's own name
       * 學生紀錄 (was inline 「…老師在學生頁看得到。」 in transfer/TransferExplainer.vue).
       */
      EXPLAINER_REPLY_SEEN: (replyLabel: string) =>
        `後兩項要寫 ${replyLabel}，老師在 學生紀錄 看得到。`,
      /**
       * 使用說明's ★ line on 轉班鎖定 (review 2026-10-04 W-8 — the guides' glossary 轉班中). Its
       * 續報 / 候補 siblings were removed (PO 2026-10-06: 「remove this, it's intuitive enough」 —
       * the box on the row says it); this one tells the 轉班 user what the other pages do.
       */
      EXPLAINER_TRANSFER_LOCK:
        '★ 轉班中 (安排中、待約課、未完成)：原班、新班的名額、最後約課日、續報只在這一頁處理；其他頁面的按鈕是灰的，寫一句原因並有「前往 轉班」。暫時保留名額 不受限。',
      /** Its popconfirm (review H-1: the days from the same constant as the label). */
      EXTEND_HOLD_CONFIRM: `將延長新班時段保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天，紀錄回到 待約課。確定繼續？`,
      /**
       * M7 9B-6 (PO 2026-09-30): which 不續報 it is — 一般不續報 or 已改報其他時段 (the 續報
       * side's detail; its 新時段 pre-filled from the row's 已另報) — asked by 轉為不續報's
       * bubble (未完成 / an expired 待約課) and by 取消轉班's 同時一併不續報
       * (components/transfer/TransferPivotChoice). The row's lead keeps what its former
       * confirm said (review P6-9): the backend (pivot-not-renewing-from-bf-pending) sets the
       * source to 不續報 and deletes this transfer's hold in the new slot (already released on
       * 未完成), so, unlike 延長保留, the new slot keeps no seat for the student.
       */
      PIVOT_CHOICE_LEAD: '新班時段不再為此學生保留名額，原時段的結束原因由「轉班」改為：',
      PIVOT_CANCEL_LEAD: (child: string, source: string) =>
        `將取消轉班，並把 ${child} 在原時段 ${source} 的報名標記為：`,
      PIVOT_CHOICE_PLAIN: '一般不續報',
      /** The toasts after them — 「· 已改報其他時段」 when that was chosen. */
      PIVOT_TOAST_TITLE: '已標記不續報',
      PIVOT_TOAST: (elsewhere: boolean) =>
        `原時段已標記不續報${elsewhere ? ' · 已改報其他時段' : ''}，轉班紀錄已關閉`,
      PIVOT_CANCEL_TOAST: (elsewhere: boolean) =>
        `已取消轉班並標記原時段為不續報${elsewhere ? ' · 已改報其他時段' : ''}`,
      /**
       * M7 9B-7 (PO 2026-09-30): under each proposed new class of an 安排中 row — the same
       * 「保留名額 N/M」 as the 候補 row's slot line (the ONE count, useSessionEnrollments
       * slotCapacity), 「· 額滿」 when N ≥ M.
       */
      PROPOSAL_SEATS_LABEL: '保留名額',
      PROPOSAL_SEATS_FULL: '額滿',
      /**
       * 已處理 on 已完成 and the late payer's 未完成 (the student stays in the new slot; it counts
       * 轉入 — PO 2026-10-03: 「新班已報名：關閉並算轉入」, on the button's confirm since that
       * row's 已處理 is its main button) / 未完成 — 近期未處理's sentence.
       */
      HANDLED_CONFIRM_DONE: hubHandledConfirm('轉班', '算轉入，學生留在新班時段'),
      HANDLED_CONFIRM_EXPIRED: hubHandledConfirm('轉班'),
      /**
       * ⋮ 其他處理 (PO 2026-10-02 「ok, build this」 — TransferOtherHandlingModal): every action
       * but the row's main button is a choice here — the button's own label plus a note in the
       * 續報 window's style (OTHER_HANDLING: when to use it; 轉為候補's the same words).
       */
      OTHER_NOTE: {
        convertToWaitlist: CONVERT_TO_WAITLIST_CHOICE_NOTE,
        extendHold: ` (${EXTEND_HOLD_MEANING})`,
        editProposal: ' (修改提議的新班或訊息)',
        changeTarget: ` (改到另一個新班時段，重新保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天)`,
        notRenewing: ' (家長不轉了，原班也不續報)',
        markHandled: ' (關閉這筆轉班，不改任何班級)',
        undoConfirm: ' (確定轉入 選錯班時，轉班回到 安排中)',
      } satisfies Partial<Record<TransferAction, string>>,
      /**
       * PO 2026-10-05 (as-built Q7) — ⋮ 其他處理 › 撤銷確定轉入 on 已完成 (utils/
       * transferUndoConfirm; backend programWaitlist/transferUndoConfirm.ts): the window's
       * 「撤銷後」 lines (what comes back, in the PO's seat words — 保留名額), the alert when a
       * 約課通知 was already sent (a reminder, never a block — PO 2026-10-04), the toast.
       */
      UNDO_CONFIRM_LEAD: '撤銷後，這筆轉班回到 安排中 (之後可以重新 確定轉入，或 取消轉班)：',
      UNDO_CONFIRM_SOURCE_LINE: (slot: string) =>
        `原班 (${slot})：回到 ${TRANSFER_OUT_SEAT_LABEL}，重新保留名額`,
      // Review 2026-10-05 L3: on 暫時保留名額 the old class never stopped holding its seat.
      UNDO_CONFIRM_SOURCE_HELD_SPECIAL: (slot: string) =>
        `原班 (${slot})：回到 ${TRANSFER_OUT_SEAT_LABEL} (暫時保留名額不變)`,
      // Review 2026-10-05 M1: Excel / the 03:00 run ended the 已轉出 old class since — it stays
      // ended (what those runs would have done without the click).
      UNDO_CONFIRM_SOURCE_LEFT_ENDED: (slot: string) => `原班 (${slot})：已結束，保留名額維持釋出`,
      // Review 2026-10-05 H1: the old class had been ended before the click — that end comes back.
      UNDO_CONFIRM_SOURCE_RESTORED_ENDED: (slot: string) =>
        `原班 (${slot})：保留名額維持釋出 (確定轉入 之前就已釋出)`,
      UNDO_CONFIRM_TARGET_REMOVED: (slot: string) =>
        `新班 (${slot})：確定轉入 建立的保留名額會移除 (BF 的約課不會取消)`,
      UNDO_CONFIRM_TARGET_KEPT: (slot: string) =>
        `新班 (${slot})：名額不變 (確定轉入 之前學生就已在這一班保留名額)`,
      UNDO_CONFIRM_TARGET_NONE: (slot: string) => `新班 (${slot})：沒有保留名額要移除`,
      // Review 2026-10-05: the system only knows staff marked the notice as sent — never
      // that the parent received or read it (PO 2026-10-04 「don't over-assume」).
      UNDO_CONFIRM_NOTICE_ALERT: (md: string) => `${md} 已標記發送約課通知。撤銷後，請再通知家長。`,
      UNDO_CONFIRM_DONE: (name: string) => `已撤銷確定轉入：${name} 回到 安排中`,
      /**
       * PO 2026-10-05 (as-built Q8) — 修改新班時段 on 未完成 (TransferChangeTargetForm): the
       * current class's line when the row says 新班時段已下架, the window's 適用情境 line and
       * its rule line (the ended hold is not released — it already ended).
       */
      CHANGE_TARGET_REMOVED_SUFFIX: ' (已下架)',
      CHANGE_TARGET_EXPIRED_CASE: TRANSFER_CHANGE_TARGET_CASES.EXPIRED_CASE,
      CHANGE_TARGET_RULE_EXPIRED: TRANSFER_CHANGE_TARGET_CASES.RULE_EXPIRED,
      /**
       * R1.2 (2026-11-02): the window's two other 適用情境 lines (were inline in
       * TransferChangeTargetForm's template; the component is re-pointed by a later task) —
       * WRONG_SLOT always, NO_TERM when the record is not 未完成.
       */
      CHANGE_TARGET_WRONG_SLOT_CASE: TRANSFER_CHANGE_TARGET_CASES.WRONG_SLOT_CASE,
      CHANGE_TARGET_NO_TERM_CASE: TRANSFER_CHANGE_TARGET_CASES.NO_TERM_CASE,
      /**
       * The 修改新班時段 toast (useProgramWaitlist changeConcludedTarget) — review 2026-10-05
       * L4: from 未完成 the row leaves for 待約課, so the toast says where it is now (from
       * 待約課 it stays there — the same sentence holds).
       */
      CHANGE_TARGET_DONE: `新班時段已更新，重新保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天；轉班在 待約課`,
      /**
       * PO 2026-10-03: 延長保留 14 天's hover — what it does, beside 轉為候補 (which holds no
       * seat). On the row's main button while it can run (a refused one shows its reason).
       */
      EXTEND_HOLD_TITLE: EXTEND_HOLD_MEANING,
      /**
       * UI/UX review 2026-10-03: the first line of a ⋮ window whose every choice is refused
       * (the late payer — its one action is the row's button).
       */
      OTHER_ALL_BLOCKED: '以下處理目前都無法使用，原因寫在各項下方',
      /** The ✎ of a 待約課 row (TransferChangeTargetForm) — hubUi's one label (LOW 16). */
      CHANGE_TARGET: HUB_CHANGE_TARGET_LABEL,
      /**
       * That window (修改新班時段): its third 適用情境 line, and the 2026-10-04 one line when the
       * current new class is on 暫時保留名額 (B4 / B5 — the backend keeps it as a plain
       * 暫時保留名額, so the rule line drops 「原目標時段保留會解除」 then).
       */
      CHANGE_TARGET_RULE: TRANSFER_CHANGE_TARGET_CASES.RULE,
      CHANGE_TARGET_RULE_SPECIAL_HOLD: TRANSFER_CHANGE_TARGET_CASES.RULE_SPECIAL_HOLD,
      CHANGE_TARGET_SPECIAL_HOLD_LINE: (slot: string) =>
        `原本的新班 (${slot}) 是暫時保留名額，${HUB_CHANGE_TARGET_LABEL} 後會繼續保留在那一班。不需要時，請到 排課時段表 按「移除暫時保留名額」。`,
      URGENT: '緊急',
      URGENT_TITLE: '老師標記為緊急',
      SLOT_REMOVED: '原時段已下架',
      SLOT_REMOVED_TITLE: '原時段已下架，請追蹤新時段安排',
      /**
       * Deep review L1: a 未完成 row the backend re-routed because the 課表 upload removed its
       * NEW class (pendingTransferNote = TRANSFER_REDESIGN.TARGET_TIMESLOT_REMOVED_NOTE) —
       * the same red-outline flag as 原時段已下架.
       */
      TARGET_SLOT_REMOVED: '新班時段已下架',
      TARGET_SLOT_REMOVED_TITLE:
        '新班時段已從課表下架，新班的保留已結束；請改用 其他處理：修改新班時段、轉為候補、轉為不續報 或 已處理',
      /** The released-source flag; its sentence (TRANSFER_REDESIGN.SOURCE_RELEASED_ACTION) is the hover. */
      SOURCE_RELEASED: '原班已無約課，名額已釋出',
      SOURCE_BOOKED: (n: number) => `原時段又約 ${n} 堂`,
      TARGET_BOOKED: (n: number) => `新時段已約 ${n} 堂`,
      TARGET_BOOKED_MULTI: (n: number) => `新時段共約 ${n} 堂`,
      // 2026-10-15 (W10.9, D37 (2) — one label per fact): 待約課's 「原班又保留名額 (Excel 約了 N
      // 堂)」 (SOURCE_AUTO_EXTENDED, PO 10/04 decision 5) went; the red SOURCE_REFILL.LABEL says it.
      /**
       * 暫時保留名額 on the 轉班's classes (PO 2026-10-04 fail-safe, decision 6; plan B5 —
       * utils/transferSpecialHold): the row's purple flags 「原班 暫時保留名額」 / 「新班 暫時保留名額」
       * and their hover (`classes` = 「原班 (週一 16:00 小鴨班)」 …, the rule as the proposal
       * page § 4 states it).
       */
      SOURCE_SPECIAL_HOLD_FLAG: '原班 暫時保留名額',
      TARGET_SPECIAL_HOLD_FLAG: '新班 暫時保留名額',
      SPECIAL_HOLD_FLAG_TITLE: (classes: string) =>
        `${classes} 是暫時保留名額：名額一直保留，直到課務按「移除暫時保留名額」，或在 BF 約滿 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂 (系統照現在的規則自動移除)。`,
      /**
       * B5 — the ONE line a 轉班 window shows when the old or new class is on 暫時保留名額
       * (the proposal page § 4; PO #5 = B for the 不續報 ones): `classes` names them
       * (SPECIAL_HOLD_SOURCE_CLASS / _TARGET_CLASS, joined by 「、」), `action` = the window's
       * SPECIAL_HOLD_ACTION. Every transfer action keeps it (KEEP_LINE); 不續報 releases it
       * (RELEASE_LINE — `keepWay` = how to keep the seat instead; '' = no such sentence).
       */
      SPECIAL_HOLD_SOURCE_CLASS: (slot: string) => `原班 (${slot})`,
      SPECIAL_HOLD_TARGET_CLASS: (slots: string) => `新班 (${slots})`,
      SPECIAL_HOLD_KEEP_LINE: (classes: string, action: string, keptNote = '') =>
        `${classes} 是暫時保留名額，${action} 後會繼續保留${keptNote ? ` (${keptNote})` : ''}。不需要時，請到 排課時段表 按「移除暫時保留名額」。`,
      /**
       * D69 (1) (PO 2026-11-03 「all ok」, page § 1 — 確定轉入 第 1 步): a window whose chart draws
       * 🔒 暫時保留名額 on the class line says the kept classes by role only — 「確定轉入 後原班會繼續
       * 保留名額。」 (`roles` = SPECIAL_HOLD_ROLE_SOURCE／_TARGET, joined by SPECIAL_HOLD_ROLES_JOIN).
       * 轉為候補 has no chart and keeps KEEP_LINE (utils/transferSpecialHold CHART_WINDOWS).
       */
      SPECIAL_HOLD_KEEP_LINE_ON_CHART: (roles: string, action: string, keptNote = '') =>
        `${action} 後${roles}會繼續保留名額${keptNote ? ` (${keptNote})` : ''}。不需要時，請到 排課時段表 按「移除暫時保留名額」。`,
      SPECIAL_HOLD_ROLE_SOURCE: '原班',
      SPECIAL_HOLD_ROLE_TARGET: '新班',
      SPECIAL_HOLD_ROLES_JOIN: '和',
      /**
       * Q5 = A (PO 2026-10-05): KEEP_LINE's `keptNote` for the NEW class when the kept
       * 暫時保留名額 sits on a real term after the action (確定轉入 with a date or on the
       * class's real seat (C2), 結算轉班) — the class leaves the 續報 flow while it stays.
       */
      SPECIAL_HOLD_KEPT_ON_TERM_NOTE: '留著的期間不會提醒續報、不會發約課通知',
      /**
       * Q13c (PO 2026-10-05): the UNticked box on 取消轉班 / 轉為候補 (待約課) and 修改新班時段
       * when the new class is this record's hold on 暫時保留名額 (utils/transferSpecialHold
       * offersTargetSpecialHoldRelease); ticked → releaseTargetSpecialHold, and the new class's
       * kept line turns into TARGET_RELEASE_LINE (`classes` = 「新班 (…)」).
       */
      RELEASE_TARGET_SPECIAL_HOLD: '一併移除新班的暫時保留名額',
      SPECIAL_HOLD_TARGET_RELEASE_LINE: (classes: string) =>
        `${classes} 的暫時保留名額 會一併移除，釋出保留名額。`,
      SPECIAL_HOLD_RELEASE_LINE: (classes: string, action: string, keepWay: string) =>
        `${classes} 是暫時保留名額，${action} 後會一併移除，釋出保留名額。${keepWay ? `要保住名額，${keepWay}。` : ''}`,
      SPECIAL_HOLD_ACTION: {
        confirmTransferIn: HUB_BUTTON_KIND.confirmTransferIn.label,
        cancelTransfer: HUB_BUTTON_KIND.cancelTransfer.label,
        cancelPivot: `${HUB_BUTTON_KIND.cancelTransfer.label} 並 同時一併不續報`,
        convertToWaitlist: HUB_BUTTON_KIND.convertToWaitlist.label,
        convertNotRenewing: `${HUB_BUTTON_KIND.convertToWaitlist.label} 並 原班不續報`,
        notRenewing: HUB_BUTTON_KIND.convertToNotRenewing.label,
        markHandled: HUB_BUTTON_KIND.markHandled.label,
        // Review 2026-10-04 W-1: 結算轉班 on this record's hold on 暫時保留名額 — kept (the
        // backend's settledHoldFields keeps it as an overlay on the new term).
        markComplete: HUB_BUTTON_KIND.settleTransfer.label,
        // 2026-10-14 (W6.3, H-03): 家長留原班 runs the 取消轉班 paths unchanged — kept, as there.
        parentKeptSource: HUB_BUTTON_KIND.parentKeptSource.label,
      },
      /**
       * RELEASE_LINE's way to keep the seat: 轉為不續報 → the row's own 取消轉班 / 已處理
       * (those it offers, joined by 「 或 」); 同時一併不續報 → its other button; 轉為候補's
       * 原班 不續報 → the window's keep choice (`keep` = its label).
       */
      SPECIAL_HOLD_KEEP_WAY_USE: (ways: string) => `請改用 ${ways}`,
      SPECIAL_HOLD_KEEP_WAY_OR: ' 或 ',
      SPECIAL_HOLD_KEEP_WAY_PIVOT: '請不要按 同時一併不續報',
      SPECIAL_HOLD_KEEP_WAY_CONVERT: (keep: string) => `請改選「${keep}」`,
      // 學生重複預約 (DUPLICATE / DUPLICATE_TITLE) was replaced by 已另報 on 2026-09-29 (PO,
      // design § 2a): HUB.ROW.ENROLLED_ELSEWHERE — one flag for the fact on every list.
      /** The sub-state flags (A3.6 / A4.17). */
      ARRANGED: TRANSFER_IN_HOLD_LABEL, // PO 2026-10-06 「go with A」 — was 「已安排 (待 Excel 約課)」
      ARRANGED_TITLE: (target: string) =>
        `已安排轉班至 ${target}。系統保留此名額 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天，等待家長付款並完成課程預約；期間若未完成，名額將自動釋出。`,
      /**
       * …when the new class is on 暫時保留名額 (decision 7 — HOLD_SPECIAL_TITLE): the seat does
       * not run out, so the hover never says it is released.
       */
      ARRANGED_TITLE_SPECIAL_HOLD: (target: string) =>
        `已安排轉班至 ${target}。新班是暫時保留名額，不會到期；等待家長付款並完成課程預約。`,
      EXPIRED: '已過期',
      /** PO 2026-09-29 (design § 2a, the approved text — M2 review): the three usual reasons, then the flags. */
      EXPIRED_TITLE: `保留 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天內目標時段沒有收到學期約課。學生可能留在原班、改報其他時段或尚未付款，請看旗子後處理。`,
      DONE: '已完成',
      DONE_TITLE: (target: string) => `Excel 已收到 ${target} 的實際約課，轉班完成`,
      BOOKED_DURING: (n: number) => `期間已約 ${n} 堂`,
      /** Detection-signal hovers (A4.11b): the stage prefix + 又約 / 已約 + the count. */
      SIGNAL_PREFIX: {
        evaluating: '建議提出後',
        'in-progress': '待轉班期間',
        'bf-pending': '待約課期間',
      } as Record<string, string>,
      SIGNAL_COUNT: (n: number) => `${n} 堂`,
      SIGNAL_COUNT_MULTI: (places: number, n: number) => `(${places} 處，${n} 堂)`,
      SOURCE_SENTENCE: (tail: string) => `原時段${tail} — 家長可能改變主意，建議至 轉班 處理。`,
      TARGET_SENTENCE: (tail: string) =>
        `新時段${tail} — BF 已帶來預約，建議至 轉班 點 確定轉入 結案。`,
      DETECTED_AT: (when: string) => `偵測於 ${when}`,
      BOOKED_DATES: (dates: string) => `預約日期：${dates}`,
      /** Empty stage (W0.14 — one sentence, no header row). */
      EMPTY: {
        // W7.2 (J-01): the 老師建議 tab.
        inbox: '目前沒有待處理的老師建議',
        'in-progress': '目前無安排中的轉班紀錄',
        awaiting: '目前無待約課紀錄',
        confirmed: '目前無已完成紀錄',
        expired: '目前無未完成紀錄',
      } as Record<'inbox' | 'in-progress' | 'awaiting' | 'confirmed' | 'expired', string>,
      /** Opened from a 排課時段表 chip and nothing matches (C8; the banner is FILTER_RESULT). */
      FILTERED_EMPTY: '沒有符合條件的轉班紀錄',
      ARCHIVED: '已歸檔',
      /**
       * The toast after 登記轉班 — 排課時段表's window and 老師建議 › 處理 › 登記轉班 alike
       * (續報・轉班 完成規則 W10.4 F4, D26 (4), PO 2026-10-14 「4. A」: one set for both entries;
       * 處理's was 已登記轉班, W7.4). Named with the child only (formatStudentOnlyName).
       */
      REGISTERED_TOAST: (student: string) => `已登記 ${student} 的轉班申請`,
      /**
       * W10.4 F4: the line on a 登記轉出 card made by 老師建議 › 處理 › 登記轉班 (the record's own
       * `source` is 'teacher' — its 登記轉出 entry comes only from that action).
       */
      REGISTERED_FROM_SUGGESTION: '來自 老師建議',
      /**
       * W10.4 F13 (b) (D26 (13), PO 2026-10-14): 學生紀錄's card for a 不採用 suggestion — the
       * 處理 choice's word (was 拒絕老師建議 · 拒絕原因：…); the stored log type stays 拒絕老師建議
       * and the teacher's side keeps 未採用 (J-06).
       */
      REJECTED_TIMELINE_TITLE: '不採用老師建議',
      REJECTED_TIMELINE_REASON: (reason: string) => `原因：${reason}`,
      /**
       * TransferCompleteForm's toasts after 確定轉入 / 取消轉班 — both used to read the
       * generic 「已完成操作」 (PO 2026-09-29 leftovers). Named with the child only
       * (formatStudentOnlyName), as the form's confirm sentences are.
       */
      CONFIRMED_IN_TOAST: (student: string) => `已確定轉入：${student}`,
      /**
       * C2 (PO 2026-10-04, docs/tools/seat-renewal-fixes.html item 5): 確定轉入 without a
       * picked date while the student already holds a real seat in the new class — the
       * backend finishes the transfer and leaves that seat as it is (result `outcome`
       * 'finished-on-real-seat'): the note under 「都不是本次轉班的」 (or under the class when
       * there is no date list), `md` = that seat's 最後約課日; and the toast.
       */
      CONFIRM_REAL_SEAT_NOTE: (md: string) =>
        `學生在新班已保留名額 (到 ${md})；確定後直接完成轉班，新班名額不變。`,
      CONFIRMED_ON_REAL_SEAT_TOAST: (student: string) => `已完成轉班：${student} (新班名額不變)`,
      /**
       * C1 (PO 2026-10-04, seat-renewal-fixes.html item 4 — an alert, never a block): the
       * picked date is earlier than the new class's 最後約課日 — the backend saves the pick
       * (`was` = the class's date now, `picked` = the pick, both M/D).
       */
      CONFIRM_LSED_EARLIER_ALERT: (was: string, picked: string) =>
        `新班原本的最後約課日是 ${was}。改成 ${picked} 後，續報提醒和釋出保留名額都照 ${picked} 算 (比原本早)。請把新的最後約課日告訴家長 (下方訊息已帶入 ${picked})。`,
      /**
       * 2026-10-24 (約課切斷確認 D61, R2-B-01; W3.9): the 確定轉入 list's first choice, shown only
       * when the new class's seat keeps a 最後約課日 later than every listed class (a 補正 past the
       * upload) — picking it keeps that date (utils/transferConfirmIn keepLsedOfferMs). `md` = M/D.
       */
      KEEP_LSED: (md: string) => `維持 最後約課日 ${md} (已補正)`,
      CANCELLED_TOAST: (student: string) => `已取消轉班：${student}`,
      /** M4 B3: the backend did not put the old class back (`sourceRestored` false). */
      CANCELLED_TOAST_NOT_RESTORED: (student: string) =>
        `已取消轉班：${student} (原班名額未重新保留)`,
      /** The 確認取消轉班 bubble after the 待約課 sentence (CONFIRM_DIALOGS.CANCEL_FROM_AWAITING). */
      CANCEL_CONTINUE: '確定繼續？',
      /**
       * A row's record changed under the click (its optimistic-concurrency token is gone) —
       * W4.5: was 「記錄資料異常，請重新整理頁面」, which contradicted 即時更新中，無需手動重整.
       */
      STALE_DATA: '紀錄已更新，請確認最新狀態後再操作',
      /** The freshness gate timed out (transport paused / offline) — a warning toast (P6-1). */
      PAUSED_RETRY: '連線暫停，請重試',
      /**
       * 轉為候補 (PO 2026-09-29, plan M1; docs/tools/transfer-to-waitlist.html § 1): the 候補
       * window in create mode over this tab (composables/useTransferToWaitlist) — its
       * title, the sentence above its footer per stage (安排中 and 待約課設定 / 待約課 /
       * 未完成; `dest` = CONVERT_DESTINATION, following the class picked in the window;
       * 待約課's first clause is bold), the read-only 登記日, the pre-fill hints, the
       * warnings and the toast.
       */
      CONVERT_TITLE: (student: string) => `轉為候補 · ${student}`,
      CONVERT_EXPLAIN_ARRANGING: (dest: string) =>
        `儲存後：這筆轉班會關閉 (學生紀錄 › 學期紀錄 保留整段歷程)；候補列出現在 ${dest}；不會通知家長。`,
      CONVERT_EXPLAIN_AWAITING_STRONG: (target: string) =>
        `儲存後：新班 ${target} 的保留名額會釋出`,
      /**
       * …when that hold is on 暫時保留名額 (B4 / B5, option S — the backend keeps it as a plain
       * 暫時保留名額: TRANSFER_HOLD_KEPT_AS_SPECIAL's words), so the seat is NOT released.
       */
      CONVERT_EXPLAIN_AWAITING_STRONG_KEPT: (target: string) =>
        `儲存後：新班 ${target} 的轉班保留釋出，暫時保留名額 繼續保留`,
      /** `sourceLine` = one of the three CONVERT_SOURCE_* below (the source is never touched). */
      CONVERT_EXPLAIN_AWAITING: (sourceLine: string, dest: string) =>
        `；原班 ${sourceLine}；這筆轉班會關閉；候補列出現在 ${dest}；不會通知家長。`,
      CONVERT_EXPLAIN_EXPIRED: (sourceLine: string, dest: string) =>
        `儲存後：這筆轉班會關閉 (新班名額已在保留到期時釋出)；原班 ${sourceLine}；候補列出現在 ${dest}；不會通知家長。`,
      /** The live source enrollment is still 已轉出 (`md` = its 最後約課日; '' = none). */
      CONVERT_SOURCE_TRANSFERRED_OUT: (slot: string, md: string) =>
        md ? `${slot} 維持 已轉出 (學期到 ${md})` : `${slot} 維持 已轉出`,
      /** No live source enrollment (its seat was released). */
      CONVERT_SOURCE_RELEASED: (slot: string) => `${slot} 名額已釋出`,
      /** A live source that is no longer 已轉出 (re-filled / reactivated), and 未完成 — § 1d 「原班維持現狀」. */
      CONVERT_SOURCE_UNCHANGED: (slot: string) => `${slot} 維持現狀`,
      /** Where the new 候補 row appears; just 「候補」 while no class is picked. */
      CONVERT_DESTINATION: (prefix: string, group: string) => `候補 › ${prefix} › ${group}`,
      CONVERT_DESTINATION_NONE: '候補',
      CONVERT_QUEUE_DATE: (md: string) => `${md} (沿用轉班登記日 · 排隊順序不變)`,
      /** A legacy 轉班 without createdAt: the backend stamps now — the family queues last. */
      CONVERT_QUEUE_DATE_MISSING: '今天 (此轉班沒有登記日，排到最後)',
      CONVERT_MIXED_GROUPS_HINT: (first: string, others: readonly string[]) =>
        `目標時段分屬 ${[first, ...others].join('、')}；一筆候補只能選一個班級，已帶入 ${first} 的時段。另一個班級請到 候補 › 新增候補 (登記日為今天)。`,
      /** No proposal at all / proposals that are not on the live schedule any more. */
      CONVERT_NO_TARGET_HINT: '這筆轉班還沒有目標時段，請選擇班級與可上時段',
      CONVERT_TARGET_GONE_HINT: '目標時段目前不在課表上 (可能已下架)，請選擇班級與可上時段',
      /** The slot list is still empty after loading — the window does not open. */
      CONVERT_SLOTS_FAILED: '時段資料載入失敗，請重試',
      /** 儲存 with a class whose 課程類別 cannot be told (the form keeps the edits). */
      CONVERT_PREFIX_UNKNOWN: '無法判斷這個班級的課程類別，請改選班級',
      /** A second row's 轉為候補 while one window is opening / open. */
      CONVERT_BUSY: '轉為候補 視窗開啟中，請稍候',
      /** Named with the child only (formatStudentOnlyName), as CONFIRMED_IN_TOAST. */
      CONVERTED_TOAST: (student: string) => `已轉為候補：${student}`,
      /**
       * PO 2026-10-02 (Part 3, Q1 = A): 轉為候補 on 安排中 / 待約課設定 while the old class is
       * still live asks 目前時段 — keep it (the old class back exactly as 取消轉班 puts it) or
       * 不續報 (its seat released). The window's sentence names the choice
       * (CONVERT_EXPLAIN_ARRANGING_SEAT with one of the two lines) and the toast what the
       * backend did (`sourceOutcome` 'restored' / 'notRenewing'; 'unchanged' = no suffix).
       * Fix round 1 (UX review M1): keeping it is not 「恢復原狀」 — 安排轉班 cleared the old
       * class's 續報 notice and the restore does not bring it back. A 轉出中 old class comes
       * back (KEEP_LINE — 2026-10-15, D43 B7: `seatLine` = the shared seat line, as 取消轉班's
       * CANCEL_FROM_AWAITING_RESTORED; was 「恢復在籍」); any other live one is left as it is
       * (UNCHANGED_LINE, CANCEL_SOURCE_UNCHANGED's word). The window's row is 「原班」. The
       * 'restored' toast carries the same line without the class (`seatLine`).
       */
      CONVERT_SEAT_ROW_LABEL: '原班',
      CONVERT_SEAT_KEEP_LINE: (seatLine: string) =>
        `${seatLine} (和 取消轉班 相同；續報 要重新通知)`,
      CONVERT_SEAT_UNCHANGED_LINE: (slot: string) => `原班 ${slot} 維持現狀`,
      CONVERT_SEAT_NOT_RENEWING_LINE: (slot: string) => `原班 ${slot} 記為 不續報 (釋出名額)`,
      CONVERT_EXPLAIN_ARRANGING_SEAT: (seatLine: string, dest: string) =>
        `儲存後：這筆轉班會關閉 (學生紀錄 › 學期紀錄 保留整段歷程)；${seatLine}；候補列出現在 ${dest}；不會通知家長。`,
      CONVERTED_TOAST_SOURCE: {
        restored: (student: string, seatLine: string) => `已轉為候補：${student} (${seatLine})`,
        notRenewing: (student: string) => `已轉為候補：${student} (原班已記為不續報)`,
      },
      /**
       * PO 2026-10-03 (Q2 = B): 重設最後約課日 on an 已轉出 row — the window says the mark goes,
       * as it does for 不續報 (SetReservationModal reclaimBanner).
       */
      SET_LSED_TRANSFERRED_OUT: '目前標記為「已轉出」。確認後將取消此標記，名額重新保留在這一班。',
      /**
       * M7 9B-1 (PO 2026-09-30): 轉為候補's optional reason — the radios are
       * FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.CONVERT_REASONS (stored as labelled); a hint
       * follows two of them on screen only; 其他 needs its 說明
       * (components/waitlist/ConvertReasonField).
       */
      CONVERT_REASON_LABEL: '原因 (選填)',
      CONVERT_REASON_HINT: { 家長暫時無法開始: '(付款、時間等)', 其他: '(請填說明)' } as Record<
        string,
        string
      >,
      CONVERT_REASON_NOTE_PLACEHOLDER: '請填寫說明',
      /**
       * The backend's refusals of the reason — MIRRORED from UI_STRINGS.TRANSFER_REDESIGN
       * (check-config-mirror): a reason outside CONVERT_REASONS; 其他 without its 說明 (the
       * window keeps 儲存 disabled meanwhile; the same words as 重排's).
       */
      ERROR_CONVERT_REASON_UNKNOWN: '轉為候補的原因不在支援的清單中',
      ERROR_CONVERT_REASON_NOTE_REQUIRED: '其他原因需填寫說明',
      /**
       * T2 (PO 2026-09-29 「always show 轉為候補, 取消轉班 nor 修改新班時段」): a 待約課 / 未完成
       * button the backend would refuse stays on the row, muted, with the backend's own
       * refusal as its hover and as the warning on click (utils/transferRowView `blocked`).
       * MIRRORS of functions/src/config/appConfig.ts UI_STRINGS.TRANSFER_REDESIGN
       * ERROR_CONVERT_TARGET_HAS_BOOKINGS / ERROR_CANCEL_TARGET_HAS_BOOKINGS /
       * ERROR_CHANGE_TARGET_HAS_BOOKINGS / ERROR_CONVERT_TARGET_ALIVE — pinned by
       * functions/scripts/check-config-mirror.cjs. PO 2026-10-02 (D-1): 1–7 bookings in the
       * new class (期間已約 N 堂 — maybe 補課) no longer trigger the first three; they name
       * a new class that is not this transfer's hold any more — a term set (最後約課日),
       * paused, or ended (TARGET_PARTIAL_RELEASE_WARNING warns about the bookings instead).
       */
      BLOCKED_CONVERT_TARGET: '新班的報名已設定學期、已暫停或已結束，無法轉為候補',
      BLOCKED_CANCEL_TARGET: '新班的報名已設定學期、已暫停或已結束，無法取消轉班',
      BLOCKED_CHANGE_TARGET: '新班的報名已設定學期、已暫停或已結束，無法修改新班時段',
      // 2026-10-04 (PO term rule 「保留名額」, never 已報名): the four ALIVE sentences below.
      BLOCKED_CONVERT_ALIVE: '新班已保留名額，無法轉為候補；請按列上的 已處理',
      /**
       * PO 2026-10-02 (D-1): the 待約課 new class already has 1–7 學期-ticket bookings (the
       * row's 期間已約 N 堂 — they may be 補課, which the data cannot tell apart) and the
       * action releases this transfer's hold there — one warning line, never a block, in the
       * 取消轉班 confirm (同時一併不續報 included), the 修改新班時段 window and the 轉為候補
       * window (utils/transferRowActions partialReleaseWarningOf). Fix round 1 (UX review
       * M2): no window has a 送出 button, so the line names the outcome only. W10.6 Y7 (review
       * § E): N ≥ 8 says what the system does — it re-checks the class right after the release
       * (D26 (1)) and 8 bookings keep the seat — under the tooltip's threshold name 「已達 8 堂」;
       * no 「立即自動續報」 (the re-check may wait for a run in progress).
       */
      TARGET_PARTIAL_RELEASE_WARNING: (n: number) =>
        n >= SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD
          ? `新班期間已約 ${n} 堂，已達 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂：保留名額釋出後系統會立刻重新檢查，達 ${SESSION_ENROLLMENT_CONFIG.AUTO_TAG_THRESHOLD} 堂就自動保留名額。`
          : `目前的新班期間已約 ${n} 堂 (可能是補課)：BF 的約課不會取消，新班的保留名額仍會釋出`,
      /**
       * PO 2026-10-02 (D-4 + Part 3): 轉為候補 on 安排中 / 待約課設定 is offered even when a
       * proposed new class already has bookings (新時段已約 N 堂) — one line per such class
       * in the window: `slot` its label, `dates` its booked days as M/D joined by 「、」.
       */
      ARRANGING_TARGET_BOOKED_WARNING: (slot: string, n: number, dates: string) =>
        `新班 ${slot} 已約 ${n} 堂 (${dates})：BF 的約課不會取消`,
      /**
       * Fix round 1 (UX M3 / review F-6): 安排中 / 待約課設定 whose old class is paused — the
       * backend refuses 轉為候補 there (and 取消轉班 on 安排中) with the generic
       * ERROR_INVALID_STATE_TRANSITION, which a refresh does not cure: the row's button is
       * blocked with these instead (utils/transferRowActions). Frontend only.
       */
      BLOCKED_CONVERT_SOURCE_PAUSED: '原班報名已暫停，無法轉為候補；請先處理原班',
      BLOCKED_CANCEL_SOURCE_PAUSED: '原班報名已暫停，無法取消轉班；請先處理原班',
      /**
       * Deep review 2026-09-30 (M1 / L5): 未完成 whose new-class enrollment is live again
       * (Excel revived it) — 延長保留 would re-hold a paid term and 轉為不續報 is refused;
       * 已處理 is the exit. MIRRORS of the backend ERROR_EXTEND_TARGET_ALIVE /
       * ERROR_PIVOT_TARGET_ALIVE_EXPIRED (check-config-mirror).
       */
      BLOCKED_EXTEND_ALIVE: '新班已保留名額，無法延長保留；請按列上的 已處理',
      BLOCKED_NOT_RENEWING_ALIVE: '新班已保留名額，無法轉為不續報；請按列上的 已處理',
      /** PO 2026-10-05 (Q8): 修改新班時段 on such a 未完成. MIRROR of ERROR_CHANGE_TARGET_ALIVE. */
      BLOCKED_CHANGE_TARGET_ALIVE: '新班已保留名額，無法修改新班時段；請按列上的 已處理',
      /**
       * PO 2026-10-05 (as-built Q7) — 撤銷確定轉入's refusals, shown on the ⋮ choice when the
       * live feeds show them (utils/transferUndoConfirm) and as the backend's toast otherwise.
       * MIRRORS of UI_STRINGS.TRANSFER_REDESIGN.ERROR_UNDO_CONFIRM_* (check-config-mirror).
       */
      ERROR_UNDO_CONFIRM_OTHER_OPEN: '這位學生還有另一筆轉班進行中，請先處理該筆，再撤銷確定轉入',
      ERROR_UNDO_CONFIRM_SOURCE_CHANGED:
        '原班已有變動，請到 排課時段表 處理原班 (不續報 / 修改最後約課日 / 恢復保留名額)',
      ERROR_UNDO_CONFIRM_TARGET_HAD_RECORD:
        '確定轉入 之前新班就已有這位學生的紀錄，請改用 不續報 處理',
      ERROR_UNDO_CONFIRM_TARGET_CHANGED:
        '新班在確定轉入後已有變動 (新的約課、續報或其他設定)，請改用 不續報 處理',
      /**
       * Deep review round 2 (L-2): 未完成 the backend re-routed because a 課表 upload removed
       * its NEW class (pendingTransferNote = TRANSFER_REDESIGN.TARGET_TIMESLOT_REMOVED_NOTE —
       * utils/transferRowActions isTargetSlotRemoved): a hold there would be ended again by
       * the next detection run, so 延長保留 is refused; 轉為候補 / 轉為不續報 / 已處理 stay.
       * MIRROR of the backend ERROR_EXTEND_TARGET_REMOVED (check-config-mirror).
       */
      BLOCKED_EXTEND_REMOVED:
        '新班時段已下架，無法延長保留；請改用 其他處理：修改新班時段、轉為候補、轉為不續報 或 已處理',
      /**
       * Deep review L1: 待約課 whose new-class enrollment is no longer in the live feed (its
       * timeslot was dropped, or it ended) — the backend refuses 轉為候補 / 取消轉班 /
       * 修改新班時段 (its hold rule needs a live hold) until the record turns 未完成.
       * Frontend only.
       */
      BLOCKED_TARGET_ENDED: '新班的報名已結束 (時段下架或已結束)，系統會在保留到期時轉為 未完成',
      /**
       * W10.4 F17 (D27, PO 2026-10-14 「why still show the 結算轉班 button, only to tell admin
       * that they cannot 結算？」): 待約課's 結算轉班 grey while the new class's hold counts no
       * 學期 booking (partialBookingsPastLsed 0 / absent) — 結算 picks one of them (the server
       * refuses a date that is not one); the ⋮ stays.
       */
      BLOCKED_SETTLE_NO_BOOKINGS: '新班尚無學期約課；上傳課表後才能結算',
      /**
       * Deep review 2026-09-30 — backend refusals, MIRRORED from UI_STRINGS.TRANSFER_REDESIGN
       * (check-config-mirror): another open 轉班 owns the new class's hold (Rule 11 — the
       * row's buttons are blocked with it, utils/transferRowActions); 登記轉班 / 加入安排名單
       * for a class the student already holds through a 待約課 / 未完成 轉班 (`stage` = 待約課
       * | 未完成 — PO 2026-10-02 D-5: the sentence sends the admin to that stage's tab, where
       * the row is); 已處理 on 待約課 before the new class is confirmed; 轉為候補 on a record
       * already closed. All but ERROR_TARGET_HELD_BY_OTHER reach the screen as the backend's
       * own error message (no reader here).
       */
      ERROR_TARGET_HELD_BY_OTHER: '新班的保留屬於另一筆轉班，請先處理該筆',
      ERROR_DUPLICATE_TRANSFER_HELD: (slot: string, stage: string) =>
        `此學生已有一筆轉入 ${slot} 的轉班 (${stage})，請先到 轉班 › ${stage} 處理該筆`,
      /**
       * D2 (PO 2026-10-09, tasks/todo-transfer-ended-source.md) — MIRRORED from
       * UI_STRINGS.TRANSFER_REDESIGN (check-config-mirror): 課務's refusal of 加入安排名單 /
       * 登記轉班 on an old class with an ending mark (its day only from the mark's own
       * 系統自動歸檔 entry), and the 排課時段表 button that lifts the mark (as
       * StudentLifecycleReservationActions names it). The 新建議 row shows the same sentence
       * before any click (utils/transferInboxSource, D3).
       */
      ERROR_SOURCE_ENDED: (reason: string, md: string | null, button: string) =>
        `${md ? `原班名額已於 ${md} 自動歸檔` : '原班名額已標記結束'} (${reason})。要轉班請先 ${button}，或改用 新增候補。`,
      /**
       * 2026-10-14 (續報・轉班 完成規則 W8.3, D-01b): every ending mark's button is 恢復保留名額
       * (RESERVATION_ACTION_LABEL_BY_KIND.restore — was 重設最後約課日 on 不續報 / 已轉出, else
       * 設定最後約課日 — a function of the reason; one answer now, so a plain string: W9.1 eslint
       * no-unused-vars). MIRROR of the backend UI_STRINGS.TRANSFER_REDESIGN.ENDED_SOURCE_BUTTON
       * (check-config-mirror).
       */
      ENDED_SOURCE_BUTTON: '恢復保留名額',
      /**
       * 2026-10-14 (續報・轉班 完成規則 W7.4, J-09) — 處理 › 登記轉班 on a 老師建議 whose old class
       * has ended: the reason the choice is grey with in the 處理 window, and the server's own
       * refusal of a stale click (programWaitlist/transferActions.ts 'acknowledge'). MIRROR of
       * the backend UI_STRINGS.TRANSFER_REDESIGN.ERROR_SOURCE_ENDED_HANDLE (check-config-mirror).
       */
      ERROR_SOURCE_ENDED_HANDLE: '原班已結束，請用 處理 › 已另行安排',
      /**
       * 2026-10-14 (W7.3, J-02 / D19 ③) — the 老師建議 row's 原班狀態 chip (utils/transferInboxSource
       * inboxSourceStateOf; one word per state, the plan's six), its hover (DERIVED ⚑ — the fact,
       * no button) and the reasons 處理 › 登記轉班 is grey with for 請長假 / 轉班中 (PAUSED /
       * TRANSFERRING — the D3 sentences, unchanged). Was the 10/9 D3 注意 flag + D5 取消建議
       * template (its 「此轉班建議取消。」 is dropped — the prefill lives in TRANSFER_HANDLE_PREFILL).
       */
      INBOX_SOURCE: {
        CHIP: {
          held: (md: string) => `原班保留至 ${md}`,
          ended: '原班已結束',
          notRenewing: '已不續報',
          transferredOut: '已轉出',
          transferring: '轉班中',
          paused: '請長假',
        },
        /** 原班保留至 from the 保留至 told at 通知續報 (the old class's reservationLockedUntil). */
        HELD_TOLD_TITLE:
          '原班名額保留到 續報 告知家長的日期；要留久一點，請在原班 ⋮ 其他處理 選 設為暫時保留名額',
        /** `words` = the ending's words, `md` = its day (M/D; null = unknown). */
        ENDED_TITLE: (words: string, md: string | null) =>
          `原班${md ? `已於 ${md} ` : '已'}結束${words ? ` (${words})` : ''}`,
        NOT_RENEWING_TITLE: '原班已不續報',
        TRANSFERRED_OUT_TITLE: '學生已轉出原班',
        /**
         * The stored transferContext.sourceEndedReason words that pick the 已轉出 / 已不續報
         * prefill when the old class has left the feed. MIRROR of the backend
         * UI_STRINGS.TRANSFER_REDESIGN.SUGGESTION_SOURCE_ENDED_REASON (programWaitlist/
         * suggestionSourceEnded.ts writes them).
         */
        STAMP_WORDS: { TRANSFERRED_OUT: '已轉出', NOT_RENEWING: '已不續報' },
        PAUSED: '原班請長假中。',
        TRANSFERRING: '原班已有一筆轉班在處理中。',
        /**
         * 2026-10-14 (W9.5 UX-04): why 處理 › 登記轉班 is grey for an old class that has ended,
         * by state (website only; ERROR_SOURCE_ENDED_HANDLE stays the server's refusal).
         */
        REGISTER_BLOCKED: {
          transferredOut: '學生已轉出原班，請選 已另行安排',
          notRenewing: '原班已不續報，請選 已另行安排',
          ended: '原班已結束，請選 已另行安排',
        },
      },
      /**
       * 2026-10-14 (W7.3, J-02) — the 老師建議 list's column heads (TransferInboxTab; 學生 / 操作
       * are HUB.COLUMNS'). 續報 = the old class's 續報 countdown (TransferRenewalCountdownCell —
       * 距續報開始 N 天 / 續報窗口已開 / 已過續報期限, so the head names the subject, DERIVED ⚑).
       */
      INBOX_COLUMNS: {
        SOURCE: '原班',
        SOURCE_STATE: '原班狀態',
        SUGGESTION: '建議內容',
        RENEWAL: '續報',
      },
      /**
       * 2026-10-14 (W7.4, J-03 / J-05) — the 處理 window (TransferHandleModal): its title, the
       * teacher's 建議內容 block, the choice group, the reply box (required for 已另行安排 / 不採用,
       * 「(選填)」 for 登記轉班 / 轉為候補 — it rides on their own window's write), the footer, the
       * required-reply hint and the toasts of the two choices it writes itself (DERIVED ⚑).
       */
      HANDLE: {
        TITLE: (student: string) => (student ? `處理老師建議 · ${student}` : '處理老師建議'),
        // 2026-10-14 (W9.5 UX-06): the column head's word (INBOX_COLUMNS.SUGGESTION).
        SUGGESTION_LABEL: '建議內容',
        SOURCE_LABEL: '原班',
        /**
         * W10.4 F13 (c) (D26 (13), PO 2026-10-14): 登記轉班／編輯提案's header row for who made
         * the suggestion — 課務 can register one themselves, so not 「老師」.
         */
        PROPOSER_LABEL: '提案人',
        CHOICE_LABEL: '處理方式',
        /** 2026-10-14 (W9.5 UX-11): the greyed 確認's hover while no choice is picked. */
        CHOICE_REQUIRED: '請先選擇 處理方式',
        REPLY_LABEL: '回覆老師',
        REPLY_LABEL_OPTIONAL: '回覆老師 (選填)',
        // 2026-10-14 (W9.5 UX-13): the page's own name 學生紀錄 (was 學生頁).
        REPLY_PLACEHOLDER: '老師會在 學生紀錄 看到這段回覆',
        REPLY_REQUIRED: (choice: string) => `${choice} 時必須填寫回覆老師`,
        // 2026-10-14 (W9.5 UX-02): the footer's cancel says 取消 (was BACK 「返回」).
        CANCEL: '取消',
        CONFIRM: '確認',
        TOAST: (choice: string, student: string) => `已回覆老師 (${choice})：${student}`,
        /** W7.6: 學生紀錄's line under the card the reply's own write drew (登記轉班 / 不採用 / …). */
        TIMELINE_REPLY: (text: string) => `回覆老師：${text}`,
      },
      /**
       * D5 (10/9): 學生紀錄's grey card for a 取消建議 record (admin-cancelled-suggestion — its
       * 取消原因 = 「原因：…」, SYSTEM_CLOSED_REASON); no new ones since W7 (D-07).
       */
      SUGGESTION_CANCELLED_TIMELINE_TITLE: '取消建議',
      /**
       * 2026-10-14 (W7.6, J-03): 處理 › 已另行安排's grey card (admin-handled-elsewhere —
       * utils/transferCancellation kind handledElsewhere) — the choice's own word.
       */
      HANDLED_ELSEWHERE_TIMELINE_TITLE: HUB_TRANSFER_HANDLE_LABEL.handledElsewhere,
      /**
       * Deep review round 2 (L-4): ERROR_DUPLICATE_TRANSFER_HELD worded for a teacher's 建議轉班
       * (a teacher has neither 延長保留 nor 已處理). No reader here — the teacher's window shows
       * the backend's sentence as it is. MIRROR of the backend (check-config-mirror).
       */
      ERROR_DUPLICATE_TRANSFER_HELD_TEACHER: (slot: string) =>
        `此學生已有一筆轉入 ${slot} 的轉班正在處理，請聯絡課務`,
      ERROR_DISMISS_TARGET_UNCONFIRMED: '新班尚未確認約課，請等待保留到期或改用 結算轉班',
      ERROR_CONVERT_ALREADY_CLOSED: '這筆轉班已關閉，請重新整理',
      /**
       * PO 2026-10-03 (Q3 = B, docs/tools/reset-lsed-undo-decisions.html) / 2026-10-04 (Q6 = B):
       * the OLD class's sentence while its 轉班 is 待約課 (the new class still its hold) or
       * 未完成 — since the 轉班鎖定 (PO 2026-10-04) the ONE lock rule's verdict there
       * (utils/transferLock oldClassSentence): every 保留名額 / 最後約課日 / 續報 action on that
       * class is grey with it (暫時保留名額 never — the PO's fail-safe reversed Q7). 取消轉班 puts an
       * 已轉出 row back by itself, never an ended one — so for those the 待約課 sentence ends
       * 「，再回來設定」 like the 未完成 one (_AWAITING_ENDED). MIRROR of the backend refusal for
       * the same rows (check-config-mirror).
       */
      BLOCKED_RECLAIM_SOURCE_AWAITING:
        '轉班還在進行 (待約課)：要留在原班，請到 轉班 › 待約課 按 ⋮ 其他處理 › 取消轉班',
      BLOCKED_RECLAIM_SOURCE_UNFINISHED:
        '轉班未完成：要留在原班，請先到 轉班 › 未完成 按 已處理 (關閉這筆轉班)，再回來設定',
      BLOCKED_RECLAIM_SOURCE_AWAITING_ENDED:
        '轉班還在進行 (待約課)：要留在原班，請先到 轉班 › 待約課 按 ⋮ 其他處理 › 取消轉班，再回來設定',
      /**
       * 轉班鎖定 (PO 2026-10-04 「disable some features while parents are in transfer state」;
       * tasks/todo-transfer-lock.md A1, L6): the grey-button sentence of a locked class
       * (utils/transferLock) — the NEW class at every stage, the OLD class while 安排中, and the
       * student-level lock (候補, 登記轉班). The OLD class in 待約課 / 未完成 keeps the three
       * BLOCKED_RECLAIM_SOURCE_* sentences above. MIRROR of the backend's
       * UI_STRINGS.TRANSFER_REDESIGN.LOCK_STAGE_* (check-config-mirror).
       */
      LOCK_STAGE_ARRANGING: '轉班安排中，請到 轉班 處理 (確定轉入 或 取消轉班)。',
      LOCK_STAGE_AWAITING: '轉班待約課，請到 轉班 處理 (結算轉班 或 取消轉班)。',
      LOCK_STAGE_EXPIRED: '轉班未完成，請到 轉班 處理 (延長保留、轉為不續報 或 已處理)。',
      // ERROR_SPECIAL_HOLD_ON_TRANSFER removed (review 2026-10-04 W-14): option S lets 暫時保留名額
      // ride on a 轉班 hold, so nothing refuses it any more (the backend twin went too).
      /**
       * Deep review M1 (B1's exit): a 未完成 row whose new-class enrollment is a real live one
       * (no 轉班 marker) — the green flag 「新班已保留名額 (M/D 起)」 (M/D = its first class;
       * L5, PO 2026-10-04 term rule — was 新班已報名) and its hover, which names the way out.
       */
      TARGET_ALIVE_FLAG: (md: string) => `新班已保留名額 (${md} 起)`,
      TARGET_ALIVE_TITLE:
        '新班的報名已恢復 (Excel 或 恢復保留名額)，這筆轉班已完成；請按 已處理 關閉 (未按的話，系統隔天凌晨自動歸檔)',
      /**
       * L5 (PO 2026-10-04 decision 5; docs/tools/transfer-lock-proposal.html § 6): the same
       * flag on 安排中 — the student already holds a real seat in a proposed class (Excel
       * booked a full term while the transfer is open). Its hover; `n` = Excel's bookings
       * there (the row's 新時段已約 N 堂), null = not known (that clause left out).
       */
      TARGET_ALIVE_ARRANGING_TITLE: (n: number | null) =>
        `${n === null ? '' : `Excel 已約 ${n} 堂，`}系統已在新班保留名額。按 確定轉入：原班釋出保留名額。`,
      /**
       * 取消轉班 on 安排中 whose old class was already released (deep review M3): the backend
       * does not hold it again — the confirm says so and names 轉為候補, the way to keep
       * waiting for the new class (utils/transferCancel; 待約課's sentence is
       * CONFIRM_DIALOGS.CANCEL_FROM_AWAITING).
       */
      CANCEL_SOURCE_RELEASED_ALT: (slot: string) =>
        `取消轉班：原班 ${slot} 的名額已釋出，不會重新保留；若要繼續等新班名額，請改用 其他處理 › 轉為候補。`,
      /**
       * 轉為候補's window (deep review L18): an open 候補 already carries the same family name
       * (`md` = its 登記日) — a warning line only, it never blocks 儲存.
       */
      DUPLICATE_WAITLIST_WARNING: (md: string) =>
        `候補已有同名家庭的登記 (${md} 登記)，請確認是否重複`,
      /**
       * Deep review A6: 轉為候補 never ends the old class — the window says so when the old
       * class is still a live enrollment (not 已轉出), and a converted 候補's 轉班轉入 hover
       * names the old class as it was at 轉為候補 (the twin's convertedFromSource* fields).
       */
      CONVERT_SOURCE_STILL_ACTIVE_LINE: (slot: string) =>
        `原班 ${slot} 仍有報名 (不會因轉為候補而結束)`,
      WAITLIST_ORIGIN_SOURCE_LSED: (md: string) => `原班學期到 ${md}`,
      WAITLIST_ORIGIN_SOURCE_ACTIVE: '原班仍有報名',
      /** Fix round 1 (F-4): the window's 原班 choice was 不續報 (the twin's convertCurrentSeat). */
      WAITLIST_ORIGIN_SOURCE_NOT_RENEWING: '原班：已記為不續報',
      /**
       * 學生紀錄 › 學期紀錄 (utils/transferCancellation): the closed record's card
       * 「轉為候補 (原 {原時段})」 in the in-process blue, and 取消轉班並不續報's verb.
       */
      CONVERTED_TIMELINE_TITLE: '轉為候補',
      CONVERTED_TIMELINE_ORIGIN: (slot: string) => `(原 ${slot})`,
      /**
       * 9B-5 option A: the 轉為候補 card's outcome once its 候補 completed — 「→ 10/15 已安排」 /
       * 「→ 10/15 放棄」 (`md` = the 候補's completedAt, Taipei; `verb` = the button that
       * completed it). Open, gone or neither flag set → no line (utils/transferCancellation).
       */
      CONVERTED_OUTCOME: (md: string, verb: string) => `→ ${md} ${verb}`,
      /**
       * The 候補's 已安排 that kept it waiting for other slots (繼續候補 — the row stayed open):
       * 「→ 10/12 已安排 (繼續候補)」, before any later completion (UI/UX review 2026-09-30 —
       * a later 放棄 alone would say the family was never placed).
       */
      CONVERTED_OUTCOME_KEEP: (md: string) => `→ ${md} 已安排 (繼續候補)`,
      PIVOT_TIMELINE_TITLE: '取消轉班並不續報',
      /**
       * W10.4 F10 (D26 (10), PO 2026-10-14; H-03): 家長留原班's red card — the record line the
       * spec names (was the bare 取消轉班). The reason's words: TRANSFER_CANCELLATION_REASON_LABELS.
       */
      PARENT_RENEWED_TIMELINE_TITLE: `取消轉班 · ${
        TRANSFER_CANCELLATION_REASON_LABELS[TRANSFER_CANCELLATION_REASONS.PARENT_RENEWED_SOURCE]
      }`,
      /**
       * Deep review M4: the grey cards of a record closed by 已處理 — after the new class was
       * confirmed / after the hold ran out — and of a withdrawn suggestion (撤回).
       */
      HANDLED_TIMELINE_TITLE: '已處理 (轉班完成)',
      HANDLED_EXPIRED_TIMELINE_TITLE: '轉班未完成 · 已處理',
      /**
       * PO 2026-10-03 (未完成 自動歸檔): a record the 03:30 run closed by itself — the card's
       * verb; its 「原因：…」 is the stored message of its 系統自動歸檔 entry
       * (utils/transferCancellation transferCancelledMeta).
       */
      SYSTEM_CLOSED_TIMELINE_TITLE: '系統自動歸檔',
      SYSTEM_CLOSED_REASON: (message: string) => `原因：${message}`,
      RETRACTED_TIMELINE_TITLE: '撤回建議',
      // ── Round 2 (tasks/todo-renewal-pending-chart.md § Build round 2, D31 cards 2 A / 8 A;
      //    mockup v4 docs/tools/hub-chart-windows-mockup.html) ──
      /**
       * R-P15: the 轉班 windows' titles (inline in the components until a later task re-points
       * them) — each the word of the button that opens it (hubUi, the ONE home of the words).
       */
      TITLES: {
        COMPLETE: HUB_BUTTON_KIND.confirmTransferIn.label,
        CANCEL: HUB_BUTTON_KIND.cancelTransfer.label,
        MARK_COMPLETE: HUB_BUTTON_KIND.settleTransfer.label,
        CHANGE_TARGET: HUB_CHANGE_TARGET_LABEL,
        EDIT_PROPOSAL: HUB_BUTTON_KIND.editProposal.ariaLabel,
      },
      /**
       * R-P8 / D31 card 8 A: 確定轉入's checklist box becomes the title's ⓘ — the box's three lines
       * (TransferCompleteForm) as one hover.
       */
      COMPLETE_TIP: `確認前：家長已確認新班時段；BF 轉班票券已處理完畢 (或將於 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天內上傳新預約 Excel)。確認後原班取消名額、新班保留名額。`,
      /** F5-1 (utils/transferCompleteForm confirmInClassText): no class picked yet. */
      CLASS_UNPICKED: '(未選)',
      /**
       * 確定轉入 第 2 步's title, second line (PO 2026-11-03, plan D67 (4)): 「{原班} → {新班}」, each
       * 「週X HH:mm 班名 (老師)」 — the first line is the student's name cell. The 最後約課日 is said once,
       * in 點擊確認後 (was the summary 「{學生} → {新班} · 最後約課日 M/D (已約 N 堂)」).
       */
      COMPLETE_MOVE: (source: string, target: string): string => `${source} → ${target}`,
      /**
       * R-P8: 第 2 步, when the message draft was edited before the 新班 or (R5.2 C5) the 最後約課日
       * changed in 第 1 步.
       */
      DRAFT_STALE_HINT: '訊息草稿是在改新班或最後約課日之前編輯的，請再看一次。',
      /** R5.2 (C1): under 確定轉入 第 1 步's footer while 下一步 is grey for a missing date. */
      NEXT_NEEDS_LSED: '還缺：最後約課日',
      /**
       * R5.2 (C4; mockup confirmInAfter): 第 1 步's class field — the label is the mockup's word; the
       * placeholder keeps the old words (the rule's 「請選擇已確認的新班時段」 stays too).
       */
      TARGET_FIELD_LABEL: '轉入哪一班',
      TARGET_FIELD_PLACEHOLDER: '選擇已確認的新班時段 (必填)',
      /** R5.2 (C6): 還原預設文案 before a class is picked (useTransferConfirmInMessage). */
      TARGET_REQUIRED: '請先選擇新班時段',
      /** R5.2 (C6): the 最後約課日 chips' group name — 確定轉入 and 結算轉班 (one key). */
      LSED_FIELD: '最後約課日',
      /** R5.2 (C6): 結算轉班's 確認 with no date picked. */
      LSED_REQUIRED: '請先選擇 最後約課日',
      /**
       * R5.2 (C6; mockup confirmInAfter): 確定轉入's 「都不是本次轉班的」 chip — its word, its meaning
       * (the hover, and in brackets in the line under the row while picked) and that line's
       * sentence (unless the class holds the student's real seat — C2's note says it then).
       */
      LSED_NONE_LABEL: TRANSFER_LSED_NONE_LABEL,
      LSED_NONE_MEANING: '屬於先前補課或其他學期',
      LSED_NONE_DETAIL: `選「${TRANSFER_LSED_NONE_LABEL}」：先用 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天「待 BF」保留名額，不完成轉班；BF 設定好、重新上傳 Excel 後再結算。`,
      /**
       * R5.2 (C2; mockup confirmInAfter): 確定轉入 第 2 步's 點擊確認後, after the date lines — the 原班
       * released ({cls} = 「週X HH:mm 班名 (老師)」, the block's 原班 line's class), then where the
       * 轉班 moves: 已完成 when 確定轉入 finishes it, else the {days}-day hold in the new class (the
       * PO's sentence, 2026-11-03, plan D67 (4) — was 「這筆轉班移到 待約課 (14 天內等 BF 的 Excel)」).
       */
      PREVIEW_SOURCE_RELEASED: (cls: string): string => `原班 ${cls}：名額釋出`,
      PREVIEW_MOVES_DONE: '這筆轉班移到 已完成 (課務手動確認)',
      PREVIEW_MOVES_PENDING: (days: number): string =>
        `確定轉入後將在新班保留名額 ${days} 天，直到新約課 Excel 上傳`,
      /**
       * Plan D67 (4): 「維持」 (or C2's real seat with no date) keeps the class's 最後約課日 — said here,
       * once, now that 第 2 步's title names the classes only.
       */
      PREVIEW_LSED_KEPT: (md: string): string => `最後約課日：維持 ${md}`,
      /**
       * R4.5 (D52 l; mockup confirmInAfter()): 確定轉入's small grey step caption — under the block
       * on 第 1 步, under the summary on 第 2 步: 「第 1 步 (共 2 步)：選新班、挑最後約課日」.
       * {topic} = what the step does (COMPLETE_STEP_TOPICS); '' = the count alone.
       */
      STEP_CAPTION: (n: number, total: number, topic = ''): string =>
        `第 ${n} 步 (共 ${total} 步)${topic ? `：${topic}` : ''}`,
      /** 確定轉入's two steps as the mockup names them (STEP_CAPTION's {topic}). */
      COMPLETE_STEP_TOPICS: ['選新班、挑最後約課日', '確認、通知家長'] as const,
      /**
       * R-P14 / D33 (ii): a 轉班 window's class-line second rows (utils/transferWindowLines) — the
       * row's own words. 原班 with a live hold reads SEAT_USAGE.LINES.HOLD; {count} = 「N/M」.
       */
      LINES: {
        /**
         * 「確定轉入 M/D」 — R-P14 wrote 「已轉出 (確定轉入 M/D)」; the 原班 seat's row-1 tag already says
         * 已轉出 (SEAT_USAGE.TAG_BY_ROW_STATE['transferred-out']), so the line drops it (said once,
         * 2026-11-02).
         */
        SOURCE_LEFT: (md: string): string => `${HUB_BUTTON_KIND.confirmTransferIn.label} ${md}`,
        PROPOSAL: (count: string): string => `提議 · 保留名額 ${count}`,
        PROPOSAL_FULL: (count: string): string => `提議 · 保留名額 ${count} · 額滿`,
        /**
         * The held 新班's hover. D69 (1) (hover de-duplicated): 轉入待 Excel is the stage pill's word
         * (轉班 › 待約課) — the line says the day only.
         */
        HELD: (md: string, daysLeft: number): string => `保留至 ${md} (剩 ${daysLeft} 天)`,
        DONE: (md: string): string => `已完成 ${md}`,
        FAILED: (md: string): string => `未完成 · 保留至 ${md} 已過`,
        /**
         * R3.3 (2026-11-02): a 待約課 the 03:30 run kept past its 保留至 because the 新班 still has a
         * 學期 lesson ahead (targetLastTermBookingAt — the row's 「等新班約課 (最後一堂 M/D)」,
         * utils/transferHoldCount); the row's hover WAITING_TARGET_TITLE rides as the line's title.
         */
        HELD_KEPT: (md: string, lastMd: string): string => `保留至 ${md} 已過 · 最後一堂 ${lastMd}`,
        /**
         * A 暫時保留名額 原班 or 新班 (F5-2, D60 b, H-13): the line's 🔒 暫時保留名額 label says the
         * state (D69 (1)), so the hover says since when and that it does not run out, once —
         * 「自 M/D 保留，不會到期」 (it was 「暫時保留名額 · 自 M/D 保留」 ＋ 「暫時保留名額，不會到期」);
         * no known start → 「不會到期」.
         */
        SPECIAL_HOLD_TIP: (sinceMd: string | null): string =>
          sinceMd ? `${SEAT_USAGE_HELD_SINCE(sinceMd)}，不會到期` : '不會到期',
      },
      /**
       * R-P14 / D40: the quote block's meta line (components/hub/HubQuote) — 「Wendy · 老師建議 ·
       * 10/20」; {kind} = QUOTE_KIND (the suggestion, or 編輯提案's 回覆老師).
       */
      QUOTE_META: (by: string, kind: string, md: string): string => `${by} · ${kind} · ${md}`,
      QUOTE_KIND: {
        /** R5.2 (F3): the row's first-line word (FROM_TEACHER) — one home. */
        get SUGGESTION(): string {
          return UI_STRINGS.HUB.TRANSFER.FROM_TEACHER;
        },
        REPLY: '回覆老師',
      },
      /**
       * R-P15: 結算轉班's 適用情境 box as the title's ⓘ — its four bullets, one per line (the same
       * words TRANSFER_REDESIGN.SUB_THRESHOLD.MARK_COMPLETE_* read).
       */
      MARK_COMPLETE_TIP: [
        TRANSFER_MARK_COMPLETE_CASES.THRESHOLD,
        TRANSFER_MARK_COMPLETE_CASES.TERM,
        TRANSFER_MARK_COMPLETE_CASES.LSED,
        TRANSFER_MARK_COMPLETE_CASES.EFFECT,
      ].join('\n'),
      /**
       * R-P15: 修改新班時段's 適用情境 box as the title's ⓘ, one line per bullet, as the box draws
       * them today (TransferChangeTargetForm): `expired` = the record is 未完成;
       * `keepsSpecialHold` = the current new class stays on 暫時保留名額 (the form's
       * `specialHoldLine && !releasing`). The box's lines differ by case, so the tip is built.
       */
      CHANGE_TARGET_TIP: (expired: boolean, keepsSpecialHold: boolean): string => {
        const C = TRANSFER_CHANGE_TARGET_CASES;
        const rule = expired ? C.RULE_EXPIRED : keepsSpecialHold ? C.RULE_SPECIAL_HOLD : C.RULE;
        const lines = expired
          ? [C.EXPIRED_CASE, C.WRONG_SLOT_CASE, rule]
          : [C.WRONG_SLOT_CASE, C.NO_TERM_CASE, rule];
        return lines.join('\n');
      },
      /**
       * R3.4 / D40 / D42 (b): the quote block's meta after its bold lead (HubQuote `metaLead` = the
       * person) — 「老師建議 · 10/20」; QUOTE_META above carries the person itself.
       */
      QUOTE_META_TAIL: (kind: string, md: string): string => `${kind} · ${md}`,
      /**
       * R3.4 (RW-19): a 轉班 window's title with the student, for a viewer the 名額使用情況 block is
       * hidden from (seatUsageWindowTitleOf's `named`); '' = the title alone (HANDLE.TITLE's form).
       */
      WINDOW_TITLE_NAMED: (title: string, student: string): string =>
        student ? `${title} · ${student}` : title,
    },
    /**
     * 候補 tab (W5; v9 § 9; text-fix A1.18 / A2.7 / A4.14 / B11 / B12 / C2). The
     * 有名額可上 panel's title and caption are HUB_PANEL_VARIANT.waitlistPlaceable.
     */
    WAITLIST: {
      /** The category tabs (Q8: underline tabs, theme blue). */
      TABLIST_LABEL: '課程類別',
      /** The static green dot on a category with a family that can be offered a date. */
      PLACEABLE_DOT: '有名額可上',
      /**
       * The static red dot on a category with an EXPIRED 候補 hold (deep review Q4) — it wins
       * over the green one: 名額已釋出, staff have to press 已安排 or 取消保留.
       */
      EXPIRED_HOLD_DOT: '有保留已到期',
      /**
       * Violet dot (候補保留 § K, PO 2026-10-01 「yes build it」): a linked 保留中 row the
       * Excel fully has — press 已安排. Below red, above green (view.ts categoryTabDot).
       */
      EXCEL_READY_DOT: '有保留已在 Excel，待按「已安排」',
      /**
       * 使用說明 › 怎麼聯絡 (WaitlistExplainer) — the two-stage flow in the buttons' own words
       * (deep review Q3, PO 2026-09-30; mockup docs/tools/waitlist-placement-hold.html § G).
       * One step = its segments, `strong` = bold: the button names (HUB_BUTTON_KIND) and the
       * payment deadline — OFFER_PAYMENT_DEADLINE, the one the parent messages use.
       */
      CONTACT_STEPS: [
        [
          { text: '按該時段的 ', strong: false },
          { text: HUB_BUTTON_KIND.recommendMessage.label, strong: true },
          { text: '，把訊息貼給家長。', strong: false },
        ],
        [
          { text: '家長答應 → 按 ', strong: false },
          { text: HUB_BUTTON_KIND.holdSeat.label, strong: true },
          { text: ' (先佔位)，請他', strong: false },
          {
            text: `當天 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.OFFER_PAYMENT_DEADLINE} 前`,
            strong: true,
          },
          { text: '完成付款。', strong: false },
        ],
        [
          { text: '期限內沒付款 → 按 ', strong: false },
          { text: HUB_BUTTON_KIND.cancelHold.label, strong: true },
          { text: '。', strong: false },
        ],
        [
          { text: '付款後在 BF 約好；Excel 上傳、名單中有他 → 按 ', strong: false },
          { text: HUB_BUTTON_KIND.scheduled.label, strong: true },
          { text: '。', strong: false },
        ],
      ],
      /**
       * Decision 7 (PO 2026-09-29): a row is either a student picked from the 學生名冊 or a
       * typed LINE 姓名 — one column, one field (UI_STRINGS.STUDENT_SEARCH.FIELD_LABEL).
       */
      COL_NAME: '學生 / LINE 姓名',
      /**
       * Decision 5: the picked student already waits in this 班級 — a yellow warning under
       * the field, never a block. `md` = that row's 登記 date.
       */
      DUPLICATE: (name: string, group: string, md: string) =>
        `${name} 已在 ${group} 候補中 (${md} 登記)，仍可新增`,
      /** The same warning in 編輯 (linking an older row to a student who already waits). */
      DUPLICATE_EDIT: (name: string, group: string, md: string) =>
        `${name} 已在 ${group} 候補中 (${md} 登記)，仍可儲存`,
      /** 類型 — it alone decides the payment ticket (the 已體驗 toggle is gone, PO 2026-09-29). */
      COL_TYPE: '類型',
      COL_SLOTS: '可上時段',
      /**
       * A 體驗三堂 slot that cannot reach its count (WaitlistTimeslotLines) — the 使用說明
       * quotes the same words (WaitlistExplainer; review P9-L).
       */
      SHORT_OF: (n: number) => `不足 ${n} 堂`,
      /**
       * A 可上時段 with no live class (WaitlistTimeslotLines); the 保留名額 window's keep-only
       * lines say the same (deep review W4).
       */
      NO_CLASS: '已無開課',
      /** 登記 — the name's second line (A2.7): 「9/20 · 6 天前 · Sherry」. */
      REGISTERED: (md: string, ago: string, by: string) =>
        by ? `${md} · ${ago} · ${by}` : `${md} · ${ago}`,
      /** Legacy teacher-created rows only (column intents Q11 — dormant). */
      TEACHER_TAG: '老師',
      REST_TITLE: '其餘候補',
      REST_CAPTION: '依登記先後',
      /** B12. */
      UNDO: (seconds: number) => `復原 (${seconds} 秒)`,
      /**
       * The 已安排 / 放棄 / 刪除 confirms (review E1). All three complete the entry, which
       * leaves the list with the same undo window; nothing is purged later
       * (RESOLVED_RETENTION_DAYS only bounds the resolved-entries query).
       */
      COMPLETE_TITLE: (label: string) => `標記為${label}`,
      COMPLETE_CONFIRM: (label: string) =>
        `標記為${label}？此筆候補將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
      DELETE_TITLE: '刪除候補紀錄',
      DELETE_CONFIRM: `刪除此候補紀錄？此筆將移出清單 (可在 ${FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.COMPLETION_COUNTDOWN_SECONDS} 秒內復原)。`,
      /**
       * A row's 已安排 / 放棄 / 刪除 confirm was open when the row left the list under it
       * (someone else resolved it, or it moved between 有名額可上 and 其餘候補) — the
       * dialog closes and nothing is written (review IB1 / FC14).
       */
      ROW_CHANGED: '此筆候補已被處理或有變動，請確認最新狀態後再操作',
      /** 新增候補 while 班級 is 「全部」 — the select shakes; this says why (Chrome test 2026-09-29). */
      PICK_CLASS_FIRST: '請先選擇班級，再新增候補',
      EMPTY_GROUPS: '目前沒有符合條件的課程群組',
      EMPTY: '目前沒有候補紀錄',
      /**
       * 轉為候補 (PO 2026-09-29): the note a converted 候補 starts with (then the 轉班's
       * note — utils/transferToWaitlist), the grey chip at the end of its 登記 line and that
       * chip's hover (utils/waitlistRowView.registeredChips).
       */
      FROM_TRANSFER_NOTE: (slot: string, note: string) =>
        note ? `由轉班轉入 (原 ${slot})：${note}` : `由轉班轉入 (原 ${slot})`,
      FROM_TRANSFER_CHIP: '轉班轉入',
      /** `reason` = the 轉為候補 reason (M7 9B-1; 其他 → its 說明), '' = none. */
      FROM_TRANSFER_TITLE: (
        slot: string,
        regMd: string,
        regBy: string,
        convMd: string,
        convBy: string,
        reason = '',
      ) =>
        `原為 ${slot} 的轉班 (${regBy ? `${regMd} ${regBy}` : regMd} 登記)；${convBy ? `${convMd} ${convBy}` : convMd} 轉為候補${reason ? ` (原因：${reason})` : ''}，登記日沿用 ${regMd}。`,
      /**
       * 重排 (PO 2026-09-29, plan M3; design § 3): the ⋮ item, its confirm (the reasons
       * are FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.REQUEUE_REASONS; 其他 needs a 說明 —
       * the backend's own sentence), the toast, and the grey 「重排 ×N」 chip on the 登記
       * line with its hover (utils/waitlistRowView.registeredChips).
       */
      REQUEUE_MENU: '重排 (排到今天)',
      REQUEUE_TITLE: (name: string, todayMd: string) =>
        name ? `將 ${name} 重排到今天 (${todayMd})？` : `將此筆候補重排到今天 (${todayMd})？`,
      REQUEUE_BODY: (fromMd: string, toMd: string) =>
        `登記日會從 ${fromMd} 改成 ${toMd}，排到隊伍最後；原登記日與原因會記在這一列的紀錄與備註裡。`,
      REQUEUE_REASON_LABEL: '原因 (選填)',
      REQUEUE_NOTE_PLACEHOLDER: '請填寫說明',
      REQUEUE_NOTE_REQUIRED: '其他原因需填寫說明',
      REQUEUED_TOAST: (name: string) => (name ? `已重排：${name}` : '已重排'),
      REQUEUE_CHIP: (count: number) => `重排 ×${count}`,
      /** 「原登記 9/12 (Sherry)；9/29 Sunny 重排：未回覆」 — ends at 「重排」 without a reason. */
      REQUEUE_TITLE_HOVER: (
        origMd: string,
        origBy: string,
        lastMd: string,
        lastBy: string,
        reasonText: string,
      ) =>
        `原登記 ${origBy ? `${origMd} (${origBy})` : origMd}；${lastBy ? `${lastMd} ${lastBy}` : lastMd} 重排${reasonText ? `：${reasonText}` : ''}`,
      /**
       * The 候補 window (WaitlistEditModal): its title and 儲存 in edit mode (the defaults
       * of its `title` / `saveLabel`), create mode's read-only 登記日 row, and the 姓名
       * input's accessible name (its visible label is a plain span).
       */
      EDIT_TITLE: '編輯候補項目',
      EDIT_SAVE: '儲存',
      QUEUE_DATE: '登記日',
      FORM_NAME: '姓名',
      /**
       * 轉為候補 while the family still holds a seat (PO 2026-10-02, Part 3 Q1 = A): the
       * window's 「目前時段」 row — the slot, then keep (default) or 不續報
       * (FRONTEND_BUSINESS_RULES.PROGRAM_WAITLIST.CURRENT_SEAT). 續報 rows and 轉班 › 安排中
       * with a live old class.
       */
      CURRENT_SEAT_LABEL: '目前時段',
      CURRENT_SEAT_KEEP: '繼續保留 (照常續報)',
      CURRENT_SEAT_NOT_RENEWING: '不續報 (釋出名額)',
      /**
       * Fix round 1 (UX review H1): the host says the seat is no longer held (`released` —
       * e.g. 續報 › 已過期未通知): the slot reads 「{slot} (名額已釋出)」 and the radios
       * 維持現狀 (CONFIRM_DIALOGS.CANCEL_SOURCE_UNCHANGED's word) / 不續報 — nothing is kept.
       */
      CURRENT_SEAT_RELEASED_SLOT: (slot: string) => `${slot} (名額已釋出)`,
      CURRENT_SEAT_KEEP_RELEASED: '維持現狀',
      CURRENT_SEAT_NOT_RENEWING_RELEASED: '不續報',
      /**
       * 續報 › 其他處理 › 轉為候補 (PO 2026-10-02, Part 3; utils/renewalToWaitlist,
       * useRenewalToWaitlist): the 候補 window opened from a 續報 row — the note it starts with,
       * its read-only 登記日 (a new registration: the family queues from today, last in its
       * block), the hint when the row's slot is not on the live schedule, the sentence above
       * the footer (the 目前時段 first — `seatLine` = one of the FROM_RENEWAL_SEAT_*, the
       * *_RELEASED pair when the seat is no longer held; `dest` =
       * HUB.TRANSFER.CONVERT_DESTINATION), the duplicate warning when an open 候補 is linked to
       * the same student (`md` = its 登記 date; a name match uses
       * HUB.TRANSFER.DUPLICATE_WAITLIST_WARNING), the warning when the row's slot has an open
       * 轉班 (`stage` = 新建議 / 安排中 — fix round 1 F-5), the toast per backend outcome (`slot`
       * = the row's slot), and a lost reply the 候補 list could not confirm (fix round 1 L2).
       */
      FROM_RENEWAL_NOTE: (slot: string) => `由續報轉入 (原 ${slot})`,
      FROM_RENEWAL_QUEUE_DATE: '今天 (新登記 · 排到最後)',
      FROM_RENEWAL_NO_CLASS_HINT: '目前時段不在課表上 (可能已下架)，請選擇班級與可上時段',
      FROM_RENEWAL_EXPLAIN: (slot: string, seatLine: string, dest: string) =>
        `儲存後：目前時段 ${slot} ${seatLine}；候補列出現在 ${dest}；不會通知家長。`,
      FROM_RENEWAL_SEAT_KEEP: '繼續保留 (照常續報)',
      FROM_RENEWAL_SEAT_NOT_RENEWING: '記為 不續報 (釋出名額)',
      FROM_RENEWAL_SEAT_KEEP_RELEASED: '維持現狀 (名額已釋出)',
      FROM_RENEWAL_SEAT_NOT_RENEWING_RELEASED: '記為 不續報',
      FROM_RENEWAL_DUPLICATE: (group: string, md: string) =>
        `此學生已在 ${group} 候補中 (${md} 登記)，請確認是否重複`,
      FROM_RENEWAL_OPEN_TRANSFER: (stage: string) =>
        `這位學生有一筆進行中的轉班 (${stage})：轉為候補 不會關閉它，請到 轉班 一併處理`,
      FROM_RENEWAL_TOAST: {
        unchanged: (student: string) => `已轉為候補：${student}`,
        notRenewing: (student: string, slot: string) =>
          `已轉為候補：${student} (${slot} 已記為不續報)`,
      },
      FROM_RENEWAL_LOST_REPLY: '請先到 候補 查看是否已有這位學生的新登記，沒有再重試',
    },
    /**
     * 課務總覽 window frame (W6.1; text-fix B4 / B9). The unsaved-候補 questions keep
     * their wording; they now ask in a dialog (確認 / 取消) instead of window.confirm.
     */
    WINDOW: {
      /** The window's title — also the top-tab strip's screen-reader name (HubMainTabs listLabel). */
      TITLE: '課務總覽',
      DISCARD_FORM_TITLE: '關閉新增候補',
      DISCARD_FORM: '未儲存的資料將會遺失，確認關閉？',
      DISCARD_WINDOW_TITLE: '關閉課務總覽',
      DISCARD_WINDOW: '候補名單有未儲存的資料，確認關閉？',
      /** Switching the main tab / opening a tool view with a half-filled 新增候補 (FC2). */
      DISCARD_FORM_LEAVE_TITLE: '離開候補',
      DISCARD_FORM_LEAVE: '候補名單有未儲存的資料，確認離開？',
      /** 重新整理 on a tab whose every source is a live subscription (B9: full-width comma). */
      LIVE_NO_REFRESH: '即時更新中，無需手動重整',
    },
    /**
     * 轉班鎖定 on 排課時段表 (and its peek; PO 2026-10-04, docs/tools/transfer-lock-proposal.html
     * § 5 — tasks/todo-transfer-lock.md A3 / B5). GO_TO_TRANSFER: the button beside the one
     * reason line under a locked menu (and the ❓ 驗證 window) — opens 課務總覽 › 轉班 on the
     * record that locks the class. CLEAR_SPECIAL_HOLD_ON_TRANSFER_HOLD: the extra line of the
     * 移除暫時保留名額 confirmation on the 轉班 hold of an open 待約課 that is on 暫時保留名額
     * (option S) — removing it puts the 轉班 hold back with a fresh expiry (functions
     * sessionEnrollments/core.ts clearSpecialHold). The grey-button sentences themselves are
     * HUB.TRANSFER's LOCK_STAGE_* / BLOCKED_RECLAIM_SOURCE_* (utils/transferLock).
     */
    /**
     * PO 2026-10-06 (item 8 = A — docs/tools/transfer-ui-polish.html § 8): the message after a
     * click on an action that did not run — a grey button that says why (HubActionButton
     * `blocked`) or a refusal right after a click (the 轉班 tab's live-record checks):
     * composables/useBlockedNotice. Red ✕, this bold first line, the reason under it — so a
     * refusal never reads like a success (PO 「seems like a successful alert, not error
     * alert」). {button} = the button's own words.
     */
    BLOCKED_NOTICE: {
      TITLE: (button: string) => `${button} 沒有執行`,
      /** Longer than the 3 s default: two lines to read; it stays while hovered. */
      DURATION_MS: 6000,
    },
    TRANSFER_LOCK: {
      GO_TO_TRANSFER: '前往 轉班',
      /** The stage chip on a locked 待確認 / 續報 / 候補 row (decision 2; transfer page § 5). */
      STAGE_LABEL: {
        arranging: '轉班安排中',
        awaiting: '轉班待約課',
        unfinished: '轉班未完成',
      } as const,
      /**
       * 排課時段表's status legend row for 轉出中 — the seat's one name (TRANSFER_OUT_SEAT_LABEL,
       * PO 2026-10-06 T-J1; was the stage's 「轉班安排中」 from Q11, and 「轉班處理中」 before).
       */
      LEGEND_TRANSFERRING: `${TRANSFER_OUT_SEAT_LABEL} (來源時段暫留，已含在保留名額)`,
      CLEAR_SPECIAL_HOLD_ON_TRANSFER_HOLD: `移除後，新班的轉班保留從今天起再算 ${SESSION_ENROLLMENT_CONFIG.TRANSFER_BF_GRACE_DAYS} 天；期間沒有約課，轉班會變 未完成。`,
      /**
       * 移除暫時保留名額's toasts (utils/specialHoldToast; review 2026-10-04 W-6). BACK_TOAST:
       * the backend put the 轉班 hold back (clearSpecialHold `transferHoldBack`; `days` =
       * TRANSFER_BF_GRACE_DAYS). Otherwise the usual toast — 排課時段表 adds DATES_KEPT, except
       * on an 已轉出 / 轉出中 row (only the hold goes there).
       */
      CLEAR_SPECIAL_HOLD_BACK_TOAST: (student: string, days: number) =>
        `已移除 ${student} 的暫時保留名額；新班回到轉班保留，從今天起再算 ${days} 天`,
      CLEAR_SPECIAL_HOLD_TOAST: (student: string) => `已移除 ${student} 的暫時保留`,
      CLEAR_SPECIAL_HOLD_DATES_KEPT: ' (實際課堂日期保留)',
      /**
       * 設定暫時保留名額 on the NEW class of a 未完成 轉班 (review 2026-10-04 W-2): the backend
       * puts the 轉班 back to 待約課 (setSpecialHold `transferRestarted`). The window's info line
       * (SpecialHoldModal) and its toast (RESTARTED_TOAST; otherwise SET_TOAST —
       * utils/specialHoldToast).
       */
      SPECIAL_HOLD_RESTARTS_UNFINISHED:
        '設定後，這筆轉班回到 待約課 (新班是暫時保留名額，不會到期)。',
      /**
       * W10.4 F13 (g) (D26 (13), PO 2026-10-14): SpecialHoldModal's explainer, one short sentence
       * (was 「暫時保留名額是管理員指定的暫時保留名額 — 不受 Excel 資料與續報保留期限影響。
       * 建立後需手動清除才會釋出。請定期檢視確認原因仍有效。」).
       */
      SPECIAL_HOLD_EXPLAINER:
        '暫時保留名額由課務指定，不受 Excel 與續報期限影響；要手動清除才會釋出。',
      SPECIAL_HOLD_SET_TOAST: (student: string) => `已將 ${student} 設定為暫時保留名額`,
      SPECIAL_HOLD_RESTARTED_TOAST: (student: string) =>
        `已將 ${student} 設定為暫時保留名額；轉班已回到 待約課`,
      /**
       * 排課時段表: the grey 已轉出 chip beside 暫時保留名額 on a locked old class that keeps
       * its 已轉出 mark (review 2026-10-04 W-12 — renewalChips; fix a877f4ce).
       */
      TRANSFERRED_OUT_SPECIAL_HOLD_TITLE: '轉班後的原班，暫時保留名額 在保底；移除後釋出保留名額',
      /**
       * 通知 › 約課通知待發 › 批次無需通知 (Phase 2b): the result toast when the backend left rows
       * of a locked class as they are (batchConfirmRenewalBooking `skippedTransferLocked`).
       * `done` = the toast's own sentence, `n` = how many were left.
       */
      BATCH_SKIPPED: (done: string, n: number) => `${done}；另有 ${n} 筆轉班中，未處理`,
    },
  },
  HOME_DASHBOARD: {
    /** Section titles (top-left of each section box). */
    SECTION_TITLES: {
      SUPERVISOR: 'Supervisor',
      PAYMENT_OPS: '款項作業',
      SIGNIN_OPS: '簽到作業',
      TRACKING: '課務追蹤',
    },
    /** Captions shown directly below each metric's big number.
     *  Captions are role-action descriptors so admin instantly understands
     *  what work the number represents. */
    METRIC_CAPTIONS: {
      // Supervisor section
      FEEDBACK_PENDING: 'Feedback Pending Approval',
      PAYMENT_PENDING: 'Requests Pending Approval',
      // 款項作業 section (role-conditional metrics)
      MY_RECURRING_DUE_SOON: '筆定期付款快到期',
      PAYMENT_TREASURY_PENDING: '筆待付款',
      /** 總務：一般包「待處理」分頁的筆數（待點收／催交中／後開已補待點收 —— 後端
       *  dashboard 與分頁讀同一份 PAPER_RECEIPT.PENDING_TAB_STATES）。v3.3 三個動詞：
       *  影像是出納「驗證」，紙本是總務「點收」（規格 §2）。 */
      INVOICE_VERIFICATION: '筆紙本憑證待點收',
      /** 主管：催交中且已過期限的筆數 —— 只有主管能延長或結案。 */
      PAPER_OVERDUE: '筆紙本憑證逾期',
      /** 主管：機密包「待處理」分頁的筆數 —— 機密請款的紙本沒有別人會驗收。 */
      PAPER_PENDING_CONFIDENTIAL: '筆機密紙本憑證待點收',
      // 簽到作業 section
      PENDING_ADMIN: '筆課務待簽核',
      PENDING_TEACHER: '筆老師待簽核',
      DISCREPANCIES: '筆差異',
      // 課務追蹤 section
      RENEWAL: '筆續報待通知',
      RENEWAL_BF_PENDING: '筆已通知·待確認',
      SPECIAL_HOLD: '筆暫時保留',
      TRANSFER: '筆轉班待處理',
      TRANSFER_BF_PENDING: '筆已確認轉班未約課',
      WAITLIST: '筆候補待處理',
      WAITLIST_WITH_SLOT: '筆候補有名額可上',
      /** 課務總覽 › 通知 (約課切斷確認 W3N.1, Spec 13: one card per top tab) — the list keeps its
       *  name 約課通知待發 (D49). */
      BOOKING_NOTICE: '筆約課通知待發',
    },
    /** Shown at top of dashboard when CF call fails. */
    REFRESH_FAILED: '部分資料載入失敗',
    REFRESH_RETRY: '重試',
  },

  // 常態班更名 (班別識別統一 — class-identity canonicalization, Option B; teacher-change
  // mapping 2026-10, tasks/todo-teacher-change-mapping.md) — supervisor page copy + the
  // 學生紀錄 actor label. All of the page's words live here (A19). No typed confirm since
  // v2: the server-required token (CONFIRM_TOKEN_KEYWORD) is sent automatically on apply;
  // the button text is CONFIRM_BUTTON. Placeholders {X} are replaced at the call site.
  CLASS_IDENTITY: {
    PAGE_TITLE: '常態班更名',
    /** One action line under the title (the scenario guide, SCENARIO_GUIDE, carries the how-to). */
    PAGE_SUBTITLE:
      '班改名或固定老師換人時，先在這裡把舊班接到新班；學生的保留名額、最後約課日、續報、轉班 都跟著走。',
    /** Form-modal explainer — one form for 改班名 / 換老師 / both (mockup §2). */
    FORM_BEHAVIOR_NOTE:
      '可以只換老師、只改短名、或兩個都換。套用後學生紀錄全部接到新時段，續報／保留名額／轉班 狀態不變；單堂課紀錄照 Excel 原樣。',
    STATUS_LABELS: {
      /** W8 (Pass 6 C3) — the same word as the history row's TAG_PENDING. */
      pending: '待套用',
      applying: '套用中',
      applied: '已套用',
    } as Record<string, string>,
    /** blast-radius line — {N}=課, {M}=學生紀錄 (保留名額中), {K}=轉班/候補關聯 */
    /** 2026-07-13 (PO): the warning line lists ONLY operator-verifiable counts
     *  ({K} = 轉班/候補 refs checkable on the 轉班/候補 tabs). BF 郵件紀錄 are
     *  internal reconciliation records with no admin surface — they get their
     *  own automatic-sync line (BF_SYNC_NOTE), never the warning. */
    BLAST_RADIUS: '將更新 {N} 堂課、{M} 位學生的紀錄 (保留名額中)、{K} 筆轉班／候補關聯。',
    BF_SYNC_NOTE: '另將自動同步 {B} 筆 BF 約課郵件紀錄 (系統內部核對用，無需人工檢查)。',
    /** DT2 — {C} = detection-born successor records merged with the student's own record as the base. */
    COLLISION_HINT: '新班已有 {C} 筆系統自動建立的紀錄，將以學生原有紀錄為準合併。',
    /** W1.9 cohort widening — {S} = 不續報／轉班-marked records that now move WITH their mark. */
    SOFTENDED_HINT: '另有 {S} 筆已標記 不續報／轉班 的紀錄一併搬移，標記保留。',
    STRAGGLER_HINT: '偵測到 {R} 筆未標記識別碼的舊名課程 (殘留)，套用後請確認。',
    /** Irreversible-migration notice shown above the red 確認 button (CONFIRM_BUTTON; no typed confirm — the preview + explicit button IS the confirmation). */
    /** W8 I3 — 「改回」, never 「復原」 (GT13: 復原 is reserved for restored records). */
    APPLY_IRREVERSIBLE_NOTICE:
      '此為正式資料搬移，做完無法自動改回。請確認上方影響範圍後再按 確認。',
    /** Q11 (PO 2026-10-05 「give clear independent warning before 套用」) — its own amber alert
     *  above 確認 in the 套用 window (套用 and 重新套用), shown while 確認 can be pressed and
     *  while the apply runs. The merge writes carry no precondition, so an edit to one of the
     *  moving students' records landing during the apply can be overwritten (accepted risk
     *  「常態班更名 R3」, docs/AUDIT_ACCEPTED_RISKS.md). */
    APPLY_CONCURRENT_EDIT_WARNING:
      '套用會重寫這些學生的紀錄：請確認此刻沒有人正在 排課時段表 或 學生紀錄 修改這些學生的資料，套用期間的修改可能被覆蓋。',
    /** 顯示方式 (PO 2026-10-05 第 11 段) — the 套用 window's "what the screen shows" sentence per
     *  changed part, by the entry's displayMode (utils/classIdentityApplyView immediateNotices):
     *  {NAME} = 新班名, {TEACHERS} = 新老師 (joined by 「+」). The chip words are the chips'. The
     *  two 立即 sentences are siblings (review UX-L2, 2026-10-09): both name the BF 未更新 tag. */
    APPLY_NAME_DISPLAY_NOTICES: {
      immediate: `套用後，畫面上的班名立即改為 {NAME}；每一堂仍照 Excel 顯示，BF 更新前會標「${SLOT_CHIP_EXCEL_BEHIND_TEXT}」。`,
      'follow-excel': `套用後，班名要等 BF 課表出現新名稱才改顯示 ({NAME})；之前畫面會標「${SLOT_CHIP_RENAMING_TEXT}」。`,
    } as Record<ClassIdentityDisplayModeChoiceKey, string>,
    APPLY_TEACHERS_DISPLAY_NOTICES: {
      immediate: `套用後，畫面上的老師立即改為 {TEACHERS}；每一堂仍照 Excel 顯示，BF 更新前會標「${SLOT_CHIP_EXCEL_BEHIND_TEXT}」。`,
      'follow-excel': `套用後，老師要等 BF 課表排到新老師才改顯示 ({TEACHERS})；之前畫面會標「${SLOT_CHIP_TEACHER_CHANGING_TEXT}」。`,
    } as Record<ClassIdentityDisplayModeChoiceKey, string>,
    /** 顯示方式 — the principle, one sentence (the form's info alert; the guide says the same). */
    PRINCIPLE_LINE:
      '系統只認「同一個班、同一群學生」；改名是不是同一個班、換老師是不是同一個人、BF 是不是開了「新班」都不重要；唯一要你決定的是畫面何時開始用新的叫法。',
    /** 顯示方式 — the form block / the card window (ClassIdentityDisplayModeFields): one radio row
     *  per changed part, the default marked with DISPLAY_MODE_DEFAULT_SUFFIX. */
    DISPLAY_MODE_TITLE: '顯示方式',
    DISPLAY_MODE_PART_NAME: '班名',
    DISPLAY_MODE_PART_TEACHERS: '老師',
    DISPLAY_MODE_IMMEDIATE_NAME: '立即顯示新班名',
    DISPLAY_MODE_IMMEDIATE_TEACHERS: '立即顯示新老師',
    DISPLAY_MODE_FOLLOW_EXCEL: '等 BF 課表出現後再顯示',
    DISPLAY_MODE_DEFAULT_SUFFIX: ' (預設)',
    /** Review UX-L1 (2026-10-09) — the per-lesson screens by their menu names (D7a scope). */
    DISPLAY_MODE_HINT: '只改畫面上的叫法；每一堂 (簽到作業、iPad 簽到、老師介面) 一律照 Excel。',
    /** The entry card: the button, one line per changed part ({PART} = DISPLAY_MODE_PART_*,
     *  {CHOICE} = the choice's words, no 預設 suffix), the window (title {SLOT} = 週X HH:mm 短名;
     *  buttons CANCEL_BUTTON / CONFIRM_BUTTON) and its toast. */
    DISPLAY_MODE_CARD_BUTTON: '顯示方式',
    DISPLAY_MODE_CARD_LINE: '{PART}：{CHOICE}',
    DISPLAY_MODE_WINDOW_TITLE: '顯示方式：{SLOT}',
    DISPLAY_MODE_WINDOW_HINT: '只改顯示，不改配對與資料。',
    DISPLAY_MODE_SAVE_SUCCESS: '顯示方式已更新。',
    /** 顯示方式 — the slot-label chips (utils/slotDisplay.ts words the backend's chip kinds):
     *  將改老師 / 將改班名 the amber outline (D2 — a reminder: white fill, amber border and text,
     *  readable on every 變更時段 chip colour), BF 未更新 the white fact chip (D2a — the approved
     *  mockup's 「(不變)」); always a tooltip:
     *  TOOLTIP_DATED / TOOLTIP_BF (等 BF), TOOLTIP_IMMEDIATE_DATED (立即, BF switched, ahead),
     *  TOOLTIP_EXCEL_BEHIND (立即, BF not switched). {TO} = the new teachers (「+」), or for a
     *  name 「{FROM} → {TO}」 (SLOT_CHIP_NAME_CHANGE — the drawing's 「小熊班*協同 → 小海豚班*協同」);
     *  {CURRENT} = the next lesson's; {DATE} = M/D (the year outside this Taipei year). */
    SLOT_CHIP_TEACHER_CHANGING: SLOT_CHIP_TEACHER_CHANGING_TEXT,
    SLOT_CHIP_RENAMING: SLOT_CHIP_RENAMING_TEXT,
    SLOT_CHIP_EXCEL_BEHIND: SLOT_CHIP_EXCEL_BEHIND_TEXT,
    SLOT_CHIP_TOOLTIP_DATED: '{TO}，{DATE} 起',
    SLOT_CHIP_TOOLTIP_BF: '{TO}，日期依 BF',
    SLOT_CHIP_TOOLTIP_IMMEDIATE_DATED: '目前 {CURRENT}，{DATE} 起 {TO}',
    SLOT_CHIP_TOOLTIP_EXCEL_BEHIND: '每一堂仍照 Excel 顯示',
    SLOT_CHIP_NAME_CHANGE: '{FROM} → {TO}',
    /** Inside a dropdown option (no tooltip fits): the chip's words after the label; {CHIP}. */
    SLOT_CHIP_OPTION_SUFFIX: ' ({CHIP})',
    /** Where the chip sits inside a hover card already (選擇時段 chips): its sentence as a line;
     *  {LABEL} = the chip, {TOOLTIP} = its tooltip. */
    SLOT_CHIP_TOOLTIP_LINE: '{LABEL}：{TOOLTIP}',
    /** A 候補 班級 heading shown with another class name that another option of the same select
     *  also reads (a split / merge on 等 BF): {SHOWN} = the shown group, {DECLARED} = the group's
     *  own name (utils/programWaitlistUtils waitlistGroupHeading; lead ruling 2026-10-05). */
    WAITLIST_GROUP_HEADING_DISAMBIGUATED: '{SHOWN} ({DECLARED})',
    /** AR2-12 — pending-entry nudge when the successor TIMESLOT (name + teachers) already appears in uploads (two-slot window). */
    SUCCESSOR_SEEN_NUDGE: '新時段已出現在課表，兩個時段目前並存中，請儘速套用以合併。',
    /** AR2-18 — in-product trace of the 修錯=接著改／反悔=改回原本 rule on applied cards
     *  (GT13: 復原 is reserved for restored records). Lineage redesign 2026-07-12: the
     *  mechanism (新增對照／重新套用) is chosen automatically by the slot card's 變更
     *  action — the operator no longer picks it. */
    APPLIED_RECOVERY_HINT:
      '已套用項目如需改回原本或再變更，請於該時段卡片使用「變更」，系統會自動以新增對照或重新套用處理。',
    /** W7.2/AR2-19 — era subcaption on the renamed slot's detail page; {NAMES}=前身班名. */
    PREDECESSOR_SUBCAPTION: '前身：{NAMES} (更名前之課堂紀錄照原名顯示)',
    /** Server still requires this entry-derived token; the FE sends it automatically on apply.
     *  Not the button text (that is CONFIRM_BUTTON). */
    CONFIRM_TOKEN_KEYWORD: CLASS_IDENTITY_CONFIRM_KEYWORD,
    /** The red apply button (GT2/GT6 — standing rule 2026-09-25: the button says only 確認;
     *  the text above it carries the specifics). */
    CONFIRM_BUTTON: '確認',
    RESIDUAL_CLEAN: '殘留：0 (搬移完成)',
    /** {R}=殘留筆數 */
    RESIDUAL_DIRTY:
      '殘留：{R} 筆仍在舊時段，請點「重新套用」修復。 (轉班中衝突需先人工結案或取消轉班，重新套用無法自動修復)',
    APPLY_SUCCESS: '常態班更名已套用完成。',
    /** W8 item 8 — the 套用 call lost its reply: the run may still be going or already done on
     *  the server; the list was reloaded and 確認 stays disabled in this window. */
    APPLY_LOST_REPLY:
      '回應遺失，套用可能仍在執行或已完成。請關閉此視窗，稍後按 重新整理 看卡片狀態。',
    /** W8 M5 — under the preview while the apply runs (the window cannot be closed meanwhile). */
    MODAL_APPLYING_KEEP_OPEN_HINT: '套用中，請勿關閉此視窗。',
    SAVE_SUCCESS: '對照設定已新增。',
    /** Plain 新增對照 toast (the route that does not chain into the preview) — names the next step (Pass 9). */
    SAVE_SUCCESS_PLAIN_ROUTE: '對照已新增為待套用，請於卡片按 套用。',
    UPDATE_SUCCESS: '對照設定已更新。',
    DELETE_SUCCESS: '對照設定已刪除。',
    /** Pending-card delete popconfirm; {OLD} → {NEW} = the pair it deletes. */
    DELETE_CONFIRM: '刪除「{OLD} → {NEW}」的對照？',
    /** Name kinds: the new short name is not on any uploaded class yet. */
    EXACT_MATCH_WARNING:
      '此新班名稱尚未出現在已上傳課表，請確認與 BF 課程名稱完全一致，否則家長續報將無法對應。',
    /** Teacher kinds: a typed 新老師 is not among the PERMANENT teachers of uploaded future
     *  classes (knownTeacherNames excludes 代課 rows by design — the common case is that the new
     *  teacher 代課'd first, so never claim she "never appeared"; W8 Pass 5); {TEACHER}=that name. */
    UNSEEN_TEACHER_WARNING:
      '「{TEACHER}」尚未以常態老師身分出現在課表 (代課不算)，請確認與 BF 老師名稱完全一致。',
    /** Neither the short name nor the teachers changed (no-op entry — save disabled). */
    NO_CHANGE_HINT: CLASS_IDENTITY_NO_CHANGE_HINT,
    /** W8.3 — prefix-lock rule under the composed successor field. */
    PREFIX_LOCK_HINT:
      '課程系列與年齡段 (【】前綴) 沿用原班、不可修改；僅修改班別短名。不同系列＝不同課程，不可透過本工具合併。',
    /** Stuck-套用中 card hint, STALE marker (applyStartedAt older than
     *  FRONTEND_BUSINESS_RULES.CLASS_IDENTITY_LIMITS.APPLY_MARKER_FRESH_MINUTES) — the
     *  crashed-apply runbook rule, in-product. */
    APPLYING_STUCK_HINT:
      '套用已中斷，請於下次課表上傳前按 重新套用，否則尚未搬移的學生可能被結束「時段下架」。',
    /** 套用中 card hint, FRESH marker (the server refuses 重新套用 meanwhile);
     *  {TIME}=applyStartedAt (Taipei HH:mm). */
    APPLYING_FRESH_HINT: `套用仍在進行 (開始於 {TIME})，請勿重複執行；${FRONTEND_BUSINESS_RULES.CLASS_IDENTITY_LIMITS.APPLY_MARKER_FRESH_MINUTES} 分鐘後仍是此狀態再按 重新套用。`,
    /** W8 item 22 — 套用中 with a FRESH marker whose newest audit entry is apply-failed: the run
     *  is over, but the server still refuses 重新套用 until the marker turns stale;
     *  {TIME} = applyStartedAt + APPLY_MARKER_FRESH_MINUTES (Taipei HH:mm). */
    /** PO 2026-10-05 Q12: an interrupted run may have moved SOME records already (the records
     *  are written before the applied flip) — say so, and that 重新套用 finishes and totals. */
    APPLYING_FAILED_HINT:
      '套用已中斷：部分紀錄可能已搬移。{TIME} 後按 重新套用，系統會補完並列出合計。',
    /** PO 2026-10-05 Q12: the plan the interrupted / running apply was executing, stored on the
     *  entry at the flip to 套用中 (applyingPlan) so the card can show it even when the run's
     *  own result was lost. {N} 堂課, {M} 位學生 (保留名額中), {K} 筆轉班／候補關聯. */
    APPLYING_PLAN_LINE:
      '這次套用預計搬移：{N} 堂課、{M} 位學生的紀錄 (保留名額中)、{K} 筆轉班／候補關聯。',
    /** Persisted last-apply warnings section title on the card. */
    LAST_APPLY_WARNINGS_TITLE: '上次套用提示 (請人工複查)',
    /** Campaign progress chip — lineage semantics; {N}=已完成, {M}=全部時段. */
    CAMPAIGN_PROGRESS: '已完成 {N}／全部 {M}',
    // ── Lineage-card redesign 2026-07-12 (one card per physical slot) ──────────
    /** Slot header — prominent CURRENT resting name label. */
    CURRENT_NAME_LABEL: '目前名稱',
    /** History-row status tags. */
    /** R8 (2026-07-12 PO feedback): two roles for a LIVE edge — the change that
     *  produced the CURRENT slot (only row offering 重新套用) vs. an older slot
     *  still auto-routing into the current class. One vocabulary per role, kind-neutral
     *  (a row may change the name, the teachers or both — Pass 9). */
    TAG_CURRENT: '目前生效',
    TAG_BRIDGING: '舊時段銜接中',
    TAG_SUPERSEDED: '已被後續變更取代',
    TAG_PENDING: '待套用',
    TAG_APPLYING: '套用中',
    /** Single user-facing change verb on the slot header (GT13) + its tooltip. */
    RENAME: '變更',
    RENAME_TOOLTIP: '改班名或換固定老師',
    /** W8 F5 — a lineage with an entry 套用中 (or an interrupted run): 變更 is disabled (the form
     *  would open on the old id and the server refuses both a new entry and an edit). Also the
     *  unseen tooltip of that 套用中 entry (instead of 「打錯請用 變更 改正」). */
    RENAME_BLOCKED_APPLYING_TOOLTIP: '套用中／已中斷，請先 重新套用 完成後再 變更。',
    /** W8 (Pass 6 D4) — the slot has a 待套用 entry: 變更 opens THAT entry's edit. */
    RENAME_PENDING_TOOLTIP: '編輯待套用的對照',
    /** W8 (Pass 6 D5) — the card buttons' tooltips. */
    REAPPLY_TOOLTIP: '再執行一次搬移，修復殘留',
    RERUN_TOOLTIP: '讓系統重新檢查全部班級的約課，把新班的約課接上',
    /** W8 (Pass 6 D6) — the apply window of a 重新套用: a label beside the kind chip (tooltip
     *  REAPPLY_TOOLTIP) and this line in place of APPLY_IRREVERSIBLE_NOTICE. */
    REAPPLY_LABEL: '重新套用',
    REAPPLY_NOTICE: '這是 重新套用：只搬移仍留在舊時段的紀錄。',
    /** W8 (Pass 6 D1) — a PENDING entry whose old slot has neither a future class nor a record
     *  holding a seat (absent from the list's slot options / recentlyEnded). Those records were
     *  ended by the upload that dropped the old slot, so the line says "may already be ended;
     *  套用 restores" — not "will be ended" (lead decision 2026-10-05, logged for the PO). */
    PENDING_OLD_RETIRED_LINE:
      '舊時段已沒有未來課程、學生紀錄可能已被結束：請盡快 套用，套用會復原並接到新時段。',
    /** Completed-lineages collapsed section title; {N}=count. */
    COMPLETED_COLLAPSE_TITLE: '已完成 ({N})',
    /** Slot-header residual badge; {N}=殘留筆數. */
    RESIDUAL_BADGE: '殘留 {N} 筆',
    /** The residual badge's tooltip — the case 重新套用 cannot fix (transferring collisions). */
    RESIDUAL_BADGE_TOOLTIP: '轉班中衝突需先人工結案或取消轉班，重新套用無法自動修復。',
    /** 新增對照 empty-state when every live slot already has a lineage (GAP-6 filter) —
     *  point to the card's 變更 (GT13) instead of a bare No Data. */
    ALL_SLOTS_HAVE_LINEAGE_HINT:
      '目前所有有保留名額的時段皆已有變更紀錄。請於下方時段卡片使用「變更」；此處僅供尚未建立過對照的時段。',
    /** save→preview chain fallback: the mapping saved but the fresh entry was
     *  not found after reload (unexpected) — tell the operator where to go on. */
    SAVED_PREVIEW_FALLBACK: '對照已儲存為待套用，請於該時段卡片點「套用」繼續。',

    // ── 常態班更名 2026-10 — teacher change (tasks/todo-teacher-change-mapping.md) ──────
    /** 學生紀錄 actor line of a 常態班更名 apply (`class-identity:{套用者}`,
     *  CLASS_IDENTITY_ACTOR_PREFIX); {NAME}=套用者. Display-only: the July 班別更名 rows read
     *  the same (GT2/GT6). */
    ACTOR_LABEL: '常態班更名套用 ({NAME})',
    /** The same actor line when the token carries no name (`class-identity:` alone). */
    ACTOR_LABEL_NO_NAME: '常態班更名套用',
    /** Entry kind chip per changeKind (GT13) + its ⓘ tooltip (mockup §3). */
    KIND_LABELS: { rename: '改班名', teacher: '換老師', both: '改班名＋換老師' },
    KIND_TOOLTIPS: {
      rename: '班名改了，老師不變。',
      teacher: '固定老師改了，班名不變。學生紀錄接到新老師的時段。',
      both: '班名與固定老師同時改。',
    },
    /** The form's live kind line; {A} → {B} = old → new teachers / short name. */
    KIND_LINE_TEACHER: '這是 換老師：{A} → {B}。班名不變。',
    KIND_LINE_RENAME: '這是 改班名：{A} → {B}。老師不變。',
    /** W8 (Pass 6 S1) — {A1} → {B1} = short names, {A2} → {B2} = teachers. */
    KIND_LINE_BOTH: '這是 改班名＋換老師：{A1} → {B1}；老師：{A2} → {B2}。',
    // Form fields — short label + ⓘ tooltip (Pass 6 Phase B).
    OLD_SLOT_FIELD_LABEL: '舊班時段 (來源)',
    OLD_SLOT_PLACEHOLDER: '選擇要變更的舊班時段',
    /** Old-slot option suffixes: {N}=students holding a seat; ENDED = a recently ended slot (A21). */
    OLD_SLOT_OPTION_HELD: '({N} 位保留名額)',
    OLD_SLOT_OPTION_ENDED: '(已結束)',
    SHORT_NAME_FIELD_LABEL: '新班短名',
    /** W8 (Pass 6 D8) — a slot is picked and the composed name is empty (新增 is disabled). */
    SHORT_NAME_EMPTY_HINT: '請填寫新班短名',
    TEACHERS_FIELD_LABEL: '新老師',
    TEACHERS_FIELD_TOOLTIP: '名字要和 BF 老師名稱一模一樣，可從清單選。',
    TEACHERS_PLACEHOLDER: '輸入或從清單選…',
    /** Every teacher removed — save disabled (A16: no silent fallback to the old teachers). */
    TEACHER_EMPTY_HINT: '請至少填一位老師。',
    /** More teachers than the old class had (GT14 — a warning, never a cap); {A}=old count, {B}=new count. */
    TEACHER_COUNT_INCREASED_WARNING: '老師從 {A} 位變 {B} 位，是否忘了移除離開的老師？',
    /** D4 (PO 2026-10-09: the save warnings show in the form, no pop-up after 新增) — the new
     *  name adds or drops *協同 (utils/classIdentityTeacherTags coopMismatch); the same words as
     *  the save CF's BUSINESS_RULES.CLASS_IDENTITY.VALIDATION_WARNINGS.COOP_MISMATCH. */
    COOP_MISMATCH_WARNING: '新班與舊班的協同課狀態不一致 (一方為 *協同)，請確認。',
    /** Invalid-character hint on the 新老師 input (A16 teacher validator). */
    TEACHER_TAG_INVALID_HINT: '老師名稱不可含有 + _ / 等符號。',
    /** A16 — warn-only: parentheses or 「代」 usually mean a 代課 display string was pasted. */
    TEACHER_TAG_SUSPICIOUS_HINT: '老師名稱含括號或「代」，可能貼到了代課標示，請確認。',
    /** The successor timeslot never appeared on a future 正常 class (A9 / W8 items 5 + A1 + B2):
     *  three states, each with its OWN label (never colour alone) and a sentence per change
     *  kind (a 改班名 never says 新老師):
     *  - white (UNSEEN_ID_LABEL + UNSEEN_ID_TOOLTIP_BY_KIND) — pending, or applied with no
     *    課表上傳 since 套用 (BF simply has not changed yet: the expected S1 state);
     *  - amber (UNSEEN_AFTER_UPLOAD_* — the label's 'warning' tone is the amber chip token; the
     *    card body line under it is the orange indicator) — a 課表上傳 newer than 套用 still
     *    lacks the new slot;
     *  - red (UNSEEN_AFTER_LAST_CLASS_*) — the old slot's last class is over too, or (D6) the
     *    class has no future class and no seat left while the new identity was never seen. */
    UNSEEN_ID_LABEL: '新時段尚未出現',
    UNSEEN_ID_TOOLTIP_BY_KIND: {
      rename: '可能是 BF 還沒改，或班名打錯。BF 上傳後會自動對上；打錯請用 變更 改正。',
      teacher: '可能是 BF 還沒改，或老師名字打錯。BF 上傳後會自動對上；打錯請用 變更 改正。',
      both: '可能是 BF 還沒改，或班名、老師名字打錯。BF 上傳後會自動對上；打錯請用 變更 改正。',
    },
    UNSEEN_AFTER_UPLOAD_LABEL: '新時段仍未出現',
    UNSEEN_AFTER_UPLOAD_BY_KIND: {
      rename: '套用後的課表上傳仍沒有新班名的時段，請逐字核對班名；舊班的課結束後學生會被結束。',
      teacher:
        '新老師的時段在最近一次上傳仍未出現，請逐字核對老師名稱；舊老師的課結束後學生會被結束。',
      both: '套用後的課表上傳仍沒有新時段，請逐字核對班名與老師名稱；舊班的課結束後學生會被結束。',
    },
    UNSEEN_AFTER_LAST_CLASS_LABEL: '新時段未出現・舊課已結束',
    UNSEEN_AFTER_LAST_CLASS_BY_KIND: {
      rename: '舊班的課已結束、新時段仍未出現，學生可能已被結束，請用 變更 改成 BF 的班名。',
      teacher: '舊老師的課已結束、新時段仍未出現，學生可能已被結束，請用 變更 改成 BF 的老師名稱。',
      both: '舊班的課已結束、新時段仍未出現，學生可能已被結束，請用 變更 改成 BF 的班名與老師名稱。',
    },
    /** W8 item 5 — an applied slot with no future class AND no record holding a seat (the
     *  class simply ended): the unseen escalation is moot — a white fact instead. */
    SLOT_RETIRED_LABEL: '已無未來課程',
    SLOT_RETIRED_TOOLTIP: '此班已無未來課程，也沒有保留名額中的學生，無須處理。',
    // Apply modal — preview and result.
    APPLY_MODAL_TITLE: '套用常態班更名',
    /** Label above the new identity (name · teachers). */
    APPLY_TARGET_LABEL: '將接到',
    /** {A} → {B} = old → new teachers (W8 I4: the verb of the kind chip, 換老師; Pass 6: the
     *  PO-approved second sentence restored). */
    PREVIEW_TEACHER_CHANGE_LINE:
      '換老師：{A} → {B}。學生紀錄全部接到新老師的時段，續報／保留名額／轉班 狀態不變。',
    /** A9 — a teacher / both entry whose new slot never appeared in uploads and the backend sent
     *  no UNSEEN_TEACHER line (every new teacher HAS appeared, just not as this slot). When the
     *  backend's line exists it is shown instead (W8 B7: no FE copy of a backend sentence). */
    PREVIEW_UNSEEN_ID_NOTICE_BY_KIND: {
      rename: '新時段尚未出現在已上傳課表：可能是 BF 還沒改；套用前請與 BF 逐字核對班名。',
      teacher: '新時段尚未出現在已上傳課表：可能是 BF 還沒改；套用前請與 BF 逐字核對老師名稱。',
      both: '新時段尚未出現在已上傳課表：可能是 BF 還沒改；套用前請與 BF 逐字核對班名與老師名稱。',
    },
    /** W8 item 19 — the preview's successorHasFutureClass is false: red line before 確認;
     *  {HINT} = PREVIEW_NO_FUTURE_HINT_BY_KIND. */
    PREVIEW_NO_FUTURE_CLASS_LINE:
      '合併後的時段目前沒有未來課程：復原的學生在新班課表上傳前會再被結束；{HINT}',
    PREVIEW_NO_FUTURE_HINT_BY_KIND: {
      rename: '可能是新班名和 BF 不一致。',
      teacher: '可能是新老師名字和 BF 不一致。',
      both: '可能是新班名或新老師名字和 BF 不一致。',
    },
    /** W8 item 18 — the pre-確認 note on what happens after 套用: the automatic 重新偵測
     *  (advisable + needed), not needed (no rerun), or the withheld cause. */
    RERUN_AUTO_PRE_NOTE: `套用後會自動 重新偵測 (檢查全部班級，通常 1–2 分鐘，最多約 ${FRONTEND_BUSINESS_RULES.CLASS_IDENTITY_LIMITS.RERUN_TIMEOUT_SECONDS / 60} 分鐘)，期間視窗不能關閉。`,
    RERUN_PRE_NOTE_GATING:
      '有人工複查項目，套用後不會自動 重新偵測；處理完後請按 重新套用 (完成後會自動 重新偵測)。',
    RERUN_PRE_NOTE_RESIDUAL: '套用後仍有紀錄留在舊時段時，不會自動 重新偵測；請先按 重新套用。',
    /** List titles above the 會員姓名 lists (W8 item 7: never capped — they scroll);
     *  {Z}=restored records, {R}=returning students. */
    PREVIEW_RESTORE_TITLE: '將復原 {Z} 筆被誤判結束的紀錄，接到新時段：',
    PREVIEW_RESURRECT_TITLE: '以下 {R} 位為回流學生，新班紀錄將改為 重新報名：',
    PREVIEW_MERGE_TITLE: '將合併 (以學生原有紀錄為準)：',
    /** W8 (Pass 6 C2) — the next step after handling is 重新套用 (it runs 重新偵測 itself). */
    PREVIEW_SKIP_TITLE: '轉班中、不自動合併：套用後請到 轉班 分頁處理，再按 重新套用：',
    /** A2/GT7 — the successor is another real class (future-class date overlap), shown in its
     *  own error alert; {D}=overlapping future class dates, {NAMES}=that cohort's 會員姓名. */
    PREVIEW_OWN_COHORT_ERROR:
      '新時段在同一時間已有另一個班的課 (未來 {D} 個上課日重疊)，套用會把兩個班併成一個時段：{NAMES}',
    /** W8 item 38 — the overlapping class days under PREVIEW_OWN_COHORT_ERROR: at most
     *  CLASS_IDENTITY_LIMITS.OWN_COHORT_SAMPLE_DATES_MAX Taipei M/D, then OWN_COHORT_DATES_MORE
     *  for the other overlapping days the backend counted. */
    OWN_COHORT_DATES_LINE: '重疊的上課日：{DATES}',
    OWN_COHORT_DATES_MORE: '…另 {N} 天',
    /** Required tick before 確認 enables while PREVIEW_OWN_COHORT_ERROR shows (stored as an audit entry). */
    OWN_COHORT_ACK_CHECKBOX: '我已向 BF 確認這兩班併為同一班',
    // 重新偵測 after apply (GT4 / A13): automatic when the result has no 人工複查 item, else manual.
    /** W8 (Pass 6 D7) — the upper bound = the server's own timeout (RERUN_TIMEOUT_SECONDS). */
    RERUN_AUTO_RUNNING: `重新偵測進行中，通常 1–2 分鐘，最多約 ${FRONTEND_BUSINESS_RULES.CLASS_IDENTITY_LIMITS.RERUN_TIMEOUT_SECONDS / 60} 分鐘…`,
    /** W8 (Pass 6 D7) — leaving the page while 套用 / 重新偵測 runs (buttons CONFIRM_BUTTON /
     *  CANCEL_BUTTON). */
    LEAVE_WHILE_RUNNING_CONFIRM:
      '套用／重新偵測仍在進行，離開後看不到結果 (結果會記在卡片的 套用紀錄)。確定離開？',
    /** {E}=records the run extended — a system-wide count (W8 S5). */
    RERUN_RESULT: '重新偵測完成：延長 {E} 筆紀錄的最後約課日 (全部班級)。',
    /** {C}=records the run CREATED (system-wide) — after a 常態班更名 apply, > 0 may mean a
     *  student was not merged. */
    RERUN_CREATED_WARNING:
      '偵測另外新增了 {C} 筆紀錄 (全部班級)，可能有學生未被合併，請到 排課時段表 檢查此班。',
    /** W8 M6 — {X}=records the run ENDED (orphanedEnded, system-wide). */
    RERUN_ENDED_WARNING: '另有 {X} 筆紀錄被結束 (全部班級)。',
    /** W8 item 18 — the merged slot has no booking to attach yet: no automatic 重新偵測. */
    RERUN_NOT_NEEDED: '目前還沒有新時段的約課，不需 重新偵測；之後課表上傳時會自動接上。',
    /** W8 C1 — after 人工複查 skips the next step is 重新套用 (it moves the rest, then runs
     *  重新偵測 by itself); the window shows no 重新偵測 button for this cause. */
    RERUN_MANUAL_INSTRUCTION:
      '有人工複查項目，未自動重新偵測。處理完後請按 重新套用 (完成後會自動 重新偵測)。',
    RERUN_BUTTON: '重新偵測',
    /** Lost reply — the run may still be going on the server (W8 C7: the card's pending line
     *  is the only place the outcome shows). */
    RERUN_LOST_REPLY:
      '回應遺失，偵測可能仍在執行；稍後按 重新整理，卡片的「自 … 起尚未 重新偵測」消失即完成。',
    RERUN_SYSTEM_WIDE_NOTE: '重新偵測會檢查全部班級的約課。',
    /** W8 item 4 — the card's 重新偵測 while its newest 套用紀錄 still lists 人工複查 lines: a
     *  held-open confirm (title RERUN_BUTTON, button CONFIRM_BUTTON) with RERUN_SYSTEM_WIDE_NOTE,
     *  this lead line and the pending lines. */
    RERUN_CONFIRM_PENDING_LEAD: '以下人工複查項目尚未處理；現在偵測，轉班紀錄可能被誤判結束：',
    // ── W4 (2026-10-05) — form strings (ClassIdentitySuccessorFields / TeacherField / OldSlotField) ──
    /** The new-class NAME field in free-text mode (label short, explanation in the tooltip). */
    NAME_FIELD_LABEL: '新班名稱',
    NAME_FIELD_TOOLTIP: '只填課程名稱，須與 BF 課程名稱完全一致。',
    SHORT_NAME_PLACEHOLDER: '班別短名 (例：小海象一班)',
    NAME_PLACEHOLDER: '輸入或貼上新班課程名稱 (可從建議清單挑選)',
    /** Per-rejection feedback under the 新老師 field (mirrors BE VALIDATION_ERRORS.TEACHER_*;
     *  {N} = CLASS_IDENTITY_LIMITS.MAX_TEACHER_NAME_LENGTH / MAX_TEACHERS). */
    TEACHER_TAG_DUPLICATE_HINT: '老師名稱重複。',
    TEACHER_TAG_TOO_LONG_HINT: '老師名稱過長 (最多 {N} 字)。',
    TEACHER_TAG_TOO_MANY_HINT: '老師人數超過上限 (最多 {N} 位)。',
    TEACHER_TAG_SUBSTITUTE_HINT: '老師名稱不可為 代課 / 臨時老師。',
    /** Old-slot picker: the lineage's resting slot has no live record (「保留名額」 wording, never 在籍). */
    OLD_SLOT_OPTION_NO_LIVE: '(已無保留名額)',
    // ── W4 (2026-10-05) — card / apply-modal / upload-preview strings ──────────────────
    /** Cause-framed audit action labels for the entry card's history
     *  (feedback_system_actor_labels); W2 added own-cohort-acknowledged (A2) and
     *  apply-refused (A14 — the pre-write check failed, nothing was changed). */
    AUDIT_ACTION_LABELS: {
      create: '建立',
      edit: '編輯',
      'own-cohort-acknowledged': '確認併為同一班',
      apply: '套用',
      reapply: '重新套用',
      'apply-completed': '套用完成',
      'apply-failed': '套用失敗',
      'apply-refused': '套用前檢查未通過，未變更資料',
      /** Pass 11 H-2 — a 預覽 of a pending entry moved the restore bound (gather.ts); the row
       *  shows AUDIT_RESTORE_BOUND_LINE instead when the entry carries from / to. */
      'restore-bound-refreshed': '復原期限更新',
      /** 顯示方式 — the card's 顯示方式 window changed the choice (classIdentity/displayMode.ts). */
      'display-mode-changed': '顯示方式變更',
    } as Record<string, string>,
    /** Pass 11 H-2 — the 'restore-bound-refreshed' audit row: {FROM} / {TO} = the bound before /
     *  after as Taipei dates (M/D, YYYY/M/D outside this year); AUDIT_RESTORE_BOUND_NONE when
     *  there was none (the first 預覽). */
    AUDIT_RESTORE_BOUND_LINE: '復原期限更新：{FROM} → {TO}',
    AUDIT_RESTORE_BOUND_NONE: '—',
    /** A5 — the durable per-run reports (ClassIdentityMapping.applyReports[]). */
    APPLY_REPORTS_TITLE: '套用紀錄',
    /** {TIME}=Taipei datetime, {WHO}=applier, {REAPPLY}=APPLY_REPORT_REAPPLY_TAG or ''. */
    APPLY_REPORT_HEADER: '{TIME} {WHO}{REAPPLY}',
    APPLY_REPORT_REAPPLY_TAG: ' (重新套用)',
    /** One run's runCounts. */
    APPLY_REPORT_COUNTS: '搬移 {M}・合併 {G}・復原 {R}・重新報名 {Z}・略過 {S}',
    /** W8 S6 — the lines that withheld the automatic 重新偵測, in 套用紀錄. */
    REPORT_GATING_PREFIX: '人工複查：',
    /** appliedCounts restored / resurrected are CUMULATIVE across runs (W8 item 40: labelled
     *  累計 — the per-run numbers are in 套用紀錄); appended when either is > 0. */
    APPLIED_RESTORED_LINE: '累計 已復原 {R} 筆、重新報名 {Z} 筆',
    /** The applied card's summary — appliedCounts classes / enrollments / refs are the LAST
     *  run's (W8 item 40 / I2: 保留名額, never 在籍). D9 (PO 2026-10-09): {K} = the 轉班／候補
     *  links re-pointed together with a moved record plus the Step-4 sweep, BF 郵件紀錄
     *  excluded — always equal to the preview's {K}. */
    APPLIED_SUMMARY_LINE:
      '最近一次套用：{N} 堂課、{M} 位學生的紀錄 (保留名額中)、{K} 筆轉班／候補關聯',
    /** After the summary: when + who of the 套用. */
    APPLIED_AT_SUFFIX: ' ({TIME}，{WHO})',
    APPLIED_AT_SUFFIX_NO_WHO: ' ({TIME})',
    /** A9 / A24 — the future-class scan hit its limit: "seen" cannot be decided and the
     *  suggestion lists may be short. W8 (Pass 6 I3): ONE sentence for the page alert, the
     *  form, the 套用 window and the 無法確認 label. */
    SCAN_TRUNCATED_HINT:
      '未來課程數量過多：無法判斷新時段是否已出現在課表，新班名稱與老師的建議清單也可能不完整；找不到時請直接輸入，名字要與 BF 完全一致。',
    UNSEEN_ID_UNKNOWN_LABEL: '無法確認是否已出現',
    /** A2 — card line while a pending entry's new slot overlaps another class;
     *  {N}=students in that cohort, {D}=overlapping future class days. */
    OWN_COHORT_CARD_LINE: '新時段同一時間另有 {N} 位學生的課 ({D} 個上課日重疊)',
    OWN_COHORT_ACK_DONE: '已確認併為同一班 ({WHO}，{DATE})',
    /** Preview lists — restoreCandidates[].plan and mergeCollisions[].kind. */
    PREVIEW_PLAN_LABELS: {
      restore: '復原',
      'restore+merge': '復原並合併',
      resurrect: '重新報名',
    } as Record<string, string>,
    PREVIEW_COLLISION_KIND_LABELS: {
      merge: '合併',
      'merge-touched': '合併 (新班紀錄有人工動作)',
      'ended-archived': '新班舊紀錄歸入歷史後合併',
      'transferring-old': '原班轉班中，不合併',
      'transferring-successor': '新班轉班中，不合併',
      'self-transfer-hold': '同一班的轉班，不合併',
    } as Record<string, string>,
    /** The own-cohort names (CLASS_IDENTITY_LIMITS.OWN_COHORT_NAMES_MAX), then this line for
     *  the other students the backend counted (W8 C3: 另 = the ones not named). */
    PREVIEW_MORE_ROWS: '…另 {N} 位',
    PREVIEW_WARNINGS_GATING_TITLE: '人工複查 (套用後不會自動 重新偵測)：',
    PREVIEW_WARNINGS_INFO_TITLE: '提醒：',
    PREVIEW_INVARIANT_BLOCKED:
      '有紀錄套用後狀態會不合法，套用會被擋下 (不會變更任何資料)，請聯絡系統管理員。',
    /** W3 decision — the modal stays open during the automatic 重新偵測. */
    MODAL_RERUN_KEEP_OPEN_HINT: '重新偵測進行中，請勿關閉此視窗。',
    /** A13 — applied card: since when the post-apply 重新偵測 is still owed. */
    DETECTION_PENDING_SINCE_LINE: '自 {TIME} 起尚未 重新偵測',
    /** W4.8 — section title for the upload-preview rows of type APPLYING_MARKER_WARNING_TYPE
     *  and (W8) CLASS_IDENTITY_NOTICE_WARNING_TYPE — not only 套用中 any more. */
    UPLOAD_PREVIEW_APPLYING_TITLE: '常態班更名 提醒',
    /** Card / lineage row — the teacher change line (mockup §3); {A}/{B} = teachers joined by '+'
     *  (the same joiner as the BF id, the form line and the BE 老師更換 log — W4 decision). */
    HISTORY_TEACHER_LINE: '老師：{A} → {B}',
    /** Upload preview — the 改名班別 hint next to the removed/updated classes (A19; was an inline literal). */
    UPLOAD_PREVIEW_RENAME_HINT: '若為改名班別或換固定老師，請告知主管於「常態班更名」頁面處理。',
    /** A2 — the apply preview shows this when the entry's own-cohort overlap was never
     *  acknowledged (the tick lives in the entry's 變更 window, not in the apply modal). */
    PREVIEW_OWN_COHORT_BLOCKED_HINT:
      '請先在此對照的 編輯 視窗勾選「我已向 BF 確認這兩班併為同一班」，再套用。',
    /** Result panel — this run's class / enrollment / ref counts (W8 I2: 保留名額, never 在籍). */
    APPLY_RESULT_COUNTS_LINE:
      '已更新 {N} 堂課、{M} 位學生的紀錄 (保留名額中)、{K} 筆轉班／候補關聯。',
    /** A13 — `needsManualRerun` has three causes; the instruction names the real one:
     *  gating skips → RERUN_MANUAL_INSTRUCTION; residual > 0 → RERUN_MANUAL_RESIDUAL;
     *  the merged slot has no future class → RERUN_MANUAL_NO_FUTURE. W8 C1/C2: the window
     *  shows no 重新偵測 button for any of the three (gating / residual → 重新套用 is the next
     *  step; no future class → a run would end the records as 時段下架). */
    RERUN_MANUAL_RESIDUAL: '仍有 {N} 筆未搬移，請先按 重新套用。',
    RERUN_MANUAL_NO_FUTURE:
      '合併後的時段目前沒有未來課程，先不要 重新偵測；等課表上傳出現這個時段，上傳後的偵測會自動接上。',
    // ── W8 (2026-10-05) — the page / form / card / window words that were inline (L6) ──
    /** The wide gap between 「週X HH:mm」 and the class name in a slot label (U+3000, from its
     *  code point — see IDEOGRAPHIC_SPACE). */
    SLOT_GAP: IDEOGRAPHIC_SPACE,
    REFRESH_BUTTON: '重新整理',
    CREATE_BUTTON: '新增對照',
    EMPTY_STATE: '尚無常態班更名對照，點右上角「新增對照」建立第一筆。',
    FORM_TITLE_CREATE: '新增對照設定',
    FORM_TITLE_EDIT: '編輯對照設定',
    /** C6 — the form opened from a slot card's 變更; {SLOT} = 週X HH:mm 短名. */
    FORM_TITLE_LINEAGE: '變更：{SLOT}',
    NOTE_FIELD_LABEL: '備註 (選填)',
    NOTE_PLACEHOLDER: '選填說明',
    CLOSE_BUTTON: '關閉',
    SAVE_EDIT_BUTTON: '儲存變更',
    SAVE_CREATE_BUTTON: '新增',
    /** The edit was refused because someone changed the entry after this page loaded it
     *  (the server's own sentence is shown as the error); under the form until a reload. */
    ENTRY_CHANGED_RELOAD_HINT: '此對照已被修改，請關閉視窗後按 重新整理，再編輯。',
    /** W8 (Pass 6 D3) — the save was refused for an unacknowledged overlap: the alert's first
     *  line; {REASON} = the server's sentence, {BUTTON} = SAVE_CREATE_BUTTON / SAVE_EDIT_BUTTON. */
    OWN_COHORT_SAVE_REFUSED_LINE: '尚未儲存：{REASON}再按一次 {BUTTON}。',
    CANCEL_BUTTON: '取消',
    DONE_BUTTON: '完成',
    REAPPLY_BUTTON: '重新套用',
    EDIT_BUTTON: '編輯',
    DELETE_BUTTON: '刪除',
    APPLY_BUTTON: '套用',
    /** Entry card head — the two sides. */
    OLD_SIDE_LABEL: '舊班',
    NEW_SIDE_LABEL: '新班',
    /** Entry card — 套用中 next to 重新套用. */
    APPLYING_INLINE: '套用中…',
    /** {NOTE} = the entry's own note (display-only). */
    NOTE_LINE: '備註：{NOTE}',
    /** {N} = audit entries. */
    AUDIT_TITLE: '操作紀錄 ({N})',
    /** Lineage history row / collapsed row — when + who; M9: a pending row says when it was
     *  created ({DATE} = Taipei M/D). */
    HISTORY_APPLIED_WHEN: '套用 {TIME}',
    HISTORY_APPLIED_WHEN_BY: '套用 {TIME}，{WHO}',
    HISTORY_CREATED_WHEN: '{DATE} 建立',
    /** The collapsed completed row: 週X HH:mm 名稱 + this ({WHEN} = HISTORY_APPLIED_WHEN*). */
    ROW_TITLE_WHEN: ' ({WHEN})',
    /**
     * R1 / DT8 — the scenario guide under the page header. ITEMS = the five scenarios of
     * mockup §5: `label` (≤ 7 chars) + ⓘ `tooltip` + `lines` = the expanded text, one sentence
     * per entry (lines.join('') = the mockup paragraph, except the card button 更名 → 變更
     * per GT13). TIMING_LINE's {DAYS} = SESSION_ENROLLMENT_CONFIG.RENEWAL_REMINDER_DAYS,
     * filled at the call site — never a literal. TIMING_LINE = the W0-corrected DT3 rule;
     * BF_RULE_LINE = the PO's banner line ② (the same const as BF_CHANGE_REMINDER.LINES[1]);
     * WARN_LINE = A25.
     */
    SCENARIO_GUIDE: {
      TITLE: '怎麼做：依你的情況選一個',
      TIMING_LINE:
        '最晚：學生最後約課日之前；換老師一決定就建立。最好：續報提醒開始前 (最後約課日前 {DAYS} 天)。',
      BF_RULE_LINE: BF_CHANGE_REMINDER_BF_RULE_LINE,
      WARN_LINE: '看到同班新老師時段：先不要在新老師時段 確認保留名額／轉班，先告訴主管。',
      ITEMS: [
        {
          // S1 — declared before BF changes (best).
          key: 'bfNotChanged',
          label: 'BF 還沒改',
          tooltip: '決定要換老師或改名，BF 還沒動。',
          lines: [
            '現在就 新增對照：填新老師 (或新短名) → 新增 → 套用。',
            '之後 BF 不管先標 代課 還是直接開新班，學生紀錄都會自動接到新老師的時段，沒有任何學生會被結束。',
          ],
        },
        {
          // S2 — BF changed and uploaded; the old teacher's classes still exist (代課 period).
          key: 'uploadedOldAlive',
          label: '已上傳、舊課還在',
          tooltip: 'BF 已改、課表已上傳，舊老師還有課 (代課期間)。',
          lines: [
            '新增對照 → 套用。',
            '新班若已被系統自動建了紀錄，會以學生原本的紀錄為準合併，續報／保留名額／轉班 狀態不變。',
            '套用後會自動 重新偵測。',
          ],
        },
        {
          // S3 — too late: the old records already ended (W8 B5: 時段下架 OR the normal
          // auto-end — the census shows 時段下架 never happened in prod; F1 restores both).
          key: 'timeslotRemoved',
          label: '已被結束',
          tooltip: '學生紀錄顯示 時段下架 或 已到期結束。',
          lines: [
            '新增對照 → 套用。',
            '預覽會列出要復原的學生；套用後會自動 重新偵測，有 人工複查 項目時，處理完後按 重新套用 (完成後會自動 重新偵測)。',
            '已通知續報 的學生，續報期限要到 續報 分頁重設。',
          ],
        },
        {
          // S4 — a wrong name (teacher or class), in BF or here.
          key: 'nameTypo',
          label: '名字打錯',
          tooltip: '老師或班名打錯，在 BF 或在這裡。',
          lines: [
            '還沒套用 → 編輯 或 刪除。',
            '已套用 → 該時段卡片 變更，填正確的名字再套用一次。',
            'BF 打錯 → 先改 BF 再上傳，卡片的提醒會自己消失。',
            // W8 (Pass 6 D10) — the guide's H2 sentence, word for word
            // (public/guides/hub-class-identity-guide.html 第 5 節「名字打錯」).
            'BF 打錯、而且打錯的班已經有學生的紀錄 → 照這個順序：先 變更 成 BF 現在的寫法並套用 → 再改 BF → 再 變更 成正確的名字並套用。直接改 BF，打錯那個班上的學生紀錄會被當成另一個班而結束。',
          ],
        },
        {
          // S5 — the original teacher comes back as the permanent teacher.
          key: 'teacherReturns',
          label: '老師回來',
          tooltip: '原老師之後又回來當固定老師。',
          lines: ['該時段卡片 變更 → 老師填回原老師 → 套用。', '只是代課回來 → 不用做任何事。'],
        },
      ],
    },
  },

  /**
   * 常態班更名 DT9 / GT3 — the monthly banner for 主管 + 課務: one version for both roles
   * (PO 2026-10-04); words = the PO's final text (mockup §7; the title is shown without the
   * 【】 the PO typed around it). Shown to `seesBfChangeReminder` holders unless
   * FRONTEND_BUSINESS_RULES.USER_BANNERS.BF_CHANGE_REMINDER was dismissed in the current
   * Taipei calendar month. GO_GUIDE opens GUIDE_PATH and never dismisses.
   */
  BF_CHANGE_REMINDER: {
    TITLE: 'BF 常態班：要改班名、換老師？先看這裡',
    LINES: [
      '改之前先告訴主管，先在 Hub 把新舊班接起來，學生的紀錄才接得上。',
      BF_CHANGE_REMINDER_BF_RULE_LINE,
      '已經改了、也上傳了？馬上告訴主管，還來得及補救。',
    ],
    DISMISS: '已了解，關閉',
    GO_GUIDE: '看說明',
    GUIDE_PATH: '/guides/hub-class-identity-guide.html',
    /** 轉班 畫面整理 item 5 (PO 2026-10-06): the drawer under the top bar — closed by default. */
    EXPAND: '展開',
    COLLAPSE: '收起',
  },
} as const;

/**
 * 課務總覽 › 體驗回饋 tab numbers (tasks/todo-trial-feedback-hub.md P6, F-A16; spec § 5.5).
 * Frontend only — no backend reader, so no mirror. The four stage sub-tabs list every row (no
 * paging, spec § 5.2); only 已完成 and 停用 page.
 */
export const HUB_FEEDBACK_RULES = {
  /** 已完成: rows per page (getDocs finalStatus=="completed" … limit + startAfter, P6). */
  COMPLETED_PAGE_SIZE: 20,
  /** 停用: rows per client-side 載入更多 page over the open-forms listener's disabled docs (P6). */
  DISABLED_PAGE_SIZE: 20,
  /** 待建立 (feedback-getPendingFirstTrials) is fetched again every N minutes while the tab is visible (F-A16). */
  PENDING_REFRESH_MIN: 15,
  /**
   * 刷新's feedback-checkJotformApprovalStatus call only (fix-choices B38): above the 70 s
   * default, so a slow Jotform shows a late result instead of 操作逾時 while the server works on.
   */
  REFRESH_CALL_TIMEOUT_MS: 120_000,
} as const;

/**
 * 約課切斷確認 D80 (PO 2026-10-25 「附錄 照畫的」, annex § 一) — the state's on-screen name in the
 * 「點擊確認後：」 list's first line (UI_STRINGS.HUB.STATE_KEPT / STATE_LEFT), read from the words
 * the screens already show: the grey state tag (SEAT_USAGE.TAG_BY_ROW_STATE — the 約課切斷確認
 * row's tag), the 續報 page 近期未處理 for 已通知·已逾期 and 名額已釋出, and the 約課切斷確認 row's
 * stage words 「轉班 安排中」 for 轉出中 (a 轉班保留 has its own line, STATE_LEFT_TRANSFER_HOLD).
 * null = no line: 一般 and 沒有紀錄 have no state; 約課通知待發's line is the list's last one
 * (「約課通知待發：仍列入 (…)」, said once). 不續報 / 已轉出 / 請長假 are not drawn on the page —
 * no words decided, so no line (the window's banner above the list names 不續報 / 已轉出).
 * Read by utils/listStateLine.
 */
export const LIST_STATE_NAME_BY_STATE = {
  general: null,
  'pending-notify': UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE['pending-notify'],
  'expired-unnotified': UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE['expired-unnotified'],
  'notified-awaiting': UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE['notified-awaiting'],
  'notified-overdue': HUB_PAGE_IDENTITY.renewalMissed.title,
  'booking-notice-pending': null,
  'not-renewing': null,
  'transferred-out': null,
  'released-admin-unnotified': HUB_PAGE_IDENTITY.renewalMissed.title,
  'released-expired': HUB_PAGE_IDENTITY.renewalMissed.title,
  'released-other': HUB_PAGE_IDENTITY.renewalMissed.title,
  'special-hold': UI_STRINGS.SEAT_USAGE.TAG_BY_ROW_STATE['special-hold'],
  'long-leave': null,
  transferring: UI_STRINGS.HUB.REVIEW.CUTOFF.STAGE_TRANSFERRING,
  none: null,
} as const satisfies Record<RenewalRowState, string | null>;

/**
 * 約課切斷確認 D80 (annex § 二.3: 「提示開頭回到 按的鈕的名字」) — the 修改最後約課日 / 結算續報
 * window's success toast opens with what the SERVER wrote (its outcome kind, P4), done:
 * 「已修改最後約課日」, 「已恢復保留名額」, 「已結算續報」. 確認保留名額 is not one of D80's windows
 * and is not drawn — it keeps its words (the label alone). Read by utils/reanchorPreviewLines.
 */
export const RESERVATION_TOAST_LEAD_BY_KIND: Readonly<Record<RenewalReanchorKind, string>> = {
  reanchor: doneToastLead(RESERVATION_ACTION_LABEL_BY_KIND.reanchor.label),
  restore: doneToastLead(RESERVATION_ACTION_LABEL_BY_KIND.restore.label),
  settle: doneToastLead(RESERVATION_ACTION_LABEL_BY_KIND.settle.label),
  create: RESERVATION_ACTION_LABEL_BY_KIND.create.label,
};

// 2026-05-21 cleanup: LINE_NOTIFICATION_TEMPLATES removed. All TRANSFER_*
// keys migrated to NOTIFICATION_TEMPLATES (TRANSFER_PROPOSAL with the new
// bracketed multi-slot list); RENEWAL_CONFIRMATION / TRANSFER_COMPLETION /
// NOT_RENEWING_FAREWELL / PAUSE_* keys had no remaining callsites. Restore
// from git history when the 停課 / farewell flows resurface (and migrate
// them to the typed NOTIFICATION_TEMPLATES shape at that time).

// ==================================================================================
// CATEGORY: PAYMENT AMENDMENT CONFIGURATION
// Description: Constants and helpers for the payment amendment + prevention bundle.
// ==================================================================================

export const PAYMENT_ADJUSTMENT_METHODS = ['銀行另行匯款', '零用金', '現金退還', '其他'] as const;
export type PaymentAdjustmentMethod = (typeof PAYMENT_ADJUSTMENT_METHODS)[number];

// ── Editable-field whitelist per payment request status (Section 4b business logic) ──

/**
 * Whitelist of fields that may be edited by `editPaymentRecord` per state.
 *
 * Mirrored byte-for-byte in functions/src/config/appConfig.ts — keep in sync.
 *
 * The top-level keys are LOGICAL states (not raw Firestore `status` values).
 * The CF must map `(status, releaseConfirmed)` → key:
 *   - status === 'approved'                                   → 'approved'
 *   - status === 'paid' && releaseConfirmed !== true           → 'paid_preReleased'
 *   - status === 'paid' && releaseConfirmed === true           → 'paid_released'
 * All other statuses (draft, pending_approval*, returned, declined, voided) are
 * rejected outright by `editPaymentRecord` before this whitelist is consulted.
 *
 * Post-released state is deliberately narrowest:
 *   - Line amount / taxType / documentType are LOCKED (amount → use 金額調整;
 *     taxType mistakes → dispute + void).
 *   - `allCompanyTaxIdsConfirmed` is omitted because the 統編 relevance does
 *     not change after release — the original submit-time confirmation stands.
 *
 * `invoiceDeferred` (per-line) is NEVER in this whitelist — it transitions
 * only via `markReceiptReceived`, which auto-clears it when a deferred line
 * is marked received.
 *
 * `invoiceNumber` is NEVER in this whitelist — routed to `updateInvoiceDetails`.
 *
 * `expenseDate` is ALSO editable outside this CF since W7 (PO 2026-11-03, D22):
 * the 核准 and 付款 windows, 發票更正 and 出納's 填寫發票並驗證 on a 後開 line
 * (`UI_STRINGS.EXPENSE_DATE_BOX`; server rule in functions
 * `payment/expenseDateEdits.ts`). The batch windows and the requester／總務
 * 補發票 cannot change it.
 *
 * Line add/delete/split are never permitted by this CF — a forgotten line must
 * be submitted as a new payment request.
 */
export const PAYMENT_EDITABLE_FIELDS = {
  approved: {
    request: ['billingPeriod', 'allCompanyTaxIdsConfirmed'] as const,
    item: [
      'description',
      'category',
      'categoryOther',
      'expenseDate',
      'invoiceDate',
      'amountWithTax',
      'taxType',
      'documentType',
    ] as const,
  },
  paid_preReleased: {
    request: ['billingPeriod', 'allCompanyTaxIdsConfirmed'] as const,
    item: [
      'description',
      'category',
      'categoryOther',
      'expenseDate',
      'invoiceDate',
      'amountWithTax',
      'taxType',
      'documentType',
    ] as const,
  },
  paid_released: {
    request: ['billingPeriod'] as const,
    item: ['description', 'category', 'categoryOther', 'expenseDate', 'invoiceDate'] as const,
  },
} as const;

// ── Shared request validation (mirrored byte-for-byte in functions/src/config/appConfig.ts) ──

export interface ValidateRequestItemLike {
  /** 品項編號 —— 「同 #N」用它找到目標行。 */
  itemId?: string;
  documentType?: string;
  invoiceDeferred?: boolean;
  receiptDocument?: { storageRef: string } | undefined;
  /** 同 #N — itemId of the line whose 統一發票／收據 covers this one (that line's image counts here). */
  sameDocumentAs?: string;
}

export interface ValidateRequestLike {
  items?: ValidateRequestItemLike[];
  allCompanyTaxIdsConfirmed?: boolean;
}

export interface ValidateRequestError {
  line: number; // 1-based item index; 0 = request-level (header) error
  reason: string;
}

/**
 * Validates a payment request against the receipt-presence and 統編 confirmation rules.
 *
 * Scope: covers ONLY (a) per-line receipt/deferred rule and (b) request-level
 * 統編 confirmation rule. Empty `items` passes here BY DESIGN — this validator
 * does not enforce min-one-line. Callers are responsible for additional
 * structural checks:
 *   - Client: `RequestFormPage.vue` submit handler (pre-submit gate).
 *   - Server: `submitPaymentRequest` CF must call this at submit time AND
 *     separately enforce `items.length >= 1` for non-refund types.
 *     `createPaymentRequest` deliberately does NOT call this (drafts may be
 *     saved incrementally with empty items).
 */
export function validateRequestBeforeSubmit(request: ValidateRequestLike): {
  ok: boolean;
  errors: ValidateRequestError[];
} {
  const errors: ValidateRequestError[] = [];
  const items = request.items ?? [];

  // Per-line rule: receipt-bearing documentType must have a receiptDocument.
  // CR-2 fix: `invoiceDeferred=true` is ONLY a valid bypass for `uniform_invoice`
  // (Taiwan business practice: 統一發票 may be issued post-payment for monthly
  // services / contractor billing). 收據 is point-of-sale — receipts cannot be
  // legitimately "deferred" — so a 收據 line MUST carry a receiptDocument
  // regardless of the invoiceDeferred flag.
  items.forEach((item, idx) => {
    const isReceiptBearing = isReceiptBearingDocType(item.documentType);
    if (!isReceiptBearing) return;
    // 同 #N（計畫 D12、D36）：有效連結的行看目標行的影像（或目標行是後開的統一發票）；
    // 它自己留著的影像不算。無效的連結（不存在、指向自己、串鏈、類型不同）當成沒有連結。
    // 與伺服器 documentGroups.linkTargetIndex 同一條：只看**第一個**同 itemId 的行。
    const targetId = item.sameDocumentAs;
    const candidate =
      typeof targetId === 'string' && targetId && targetId !== item.itemId
        ? items.find((other) => other.itemId === targetId)
        : undefined;
    const target =
      candidate && !candidate.sameDocumentAs && candidate.documentType === item.documentType
        ? candidate
        : undefined;
    const evidence = target ?? item;
    const hasReceipt = !!evidence.receiptDocument?.storageRef;
    const isDeferred =
      evidence.invoiceDeferred === true && evidence.documentType === 'uniform_invoice';
    if (!hasReceipt && !isDeferred) {
      errors.push({
        line: idx + 1,
        reason: '請上傳發票／收據，或勾選「後開發票」。',
      });
    }
  });

  // Request-level rule: if any uniform_invoice line is non-deferred, allCompanyTaxIdsConfirmed must be true
  const anyNonDeferredUniformInvoice = items.some(
    (i) => i.documentType === 'uniform_invoice' && i.invoiceDeferred !== true,
  );
  if (anyNonDeferredUniformInvoice && request.allCompanyTaxIdsConfirmed !== true) {
    errors.push({
      line: 0, // request-level (0 = header)
      reason: '請確認所有統一發票之買受人統編，並勾選確認方塊。',
    });
  }

  return { ok: errors.length === 0, errors };
}

// ==================================================================================
// CATEGORY: INSTANT CACHE — COLD-START MITIGATION
// Description: Feature flag and prefetch registry for the localStorage SWR cache.
// ==================================================================================

/** Master on/off switch for the instant-cache system. Set to false to revert all
 *  cached surfaces to plain async-fetch behavior without touching component code. */
export const INSTANT_CACHE_ENABLED = true;

/** Single source of truth for the admin-help gallery list cache lifetime.
 *  Imported by useInstructionGallery so the composable and the registry
 *  cannot drift apart. */
export const ADMIN_HELP_GALLERIES_TTL_MS = 5 * 60_000;

/**
 * Route gate for a prefetch entry (data-only — the runner owns the matching).
 * When the path the user is on at fire time starts with any of these prefixes,
 * the entry is skipped: the surface already fetches the same data itself, so the
 * idle warm-up would only add a duplicate call (N+1 review §7 hunt c, 2026-09-20).
 */
type InstantPrefetchRouteGate = {
  skipRoutePrefixes?: readonly string[];
};

export type InstantPrefetchEntry =
  | ({
      type: 'l1';
      key: string;
      /** Key of a `ComputedRef<boolean>` returned by usePermissions(), e.g.
       *  'canViewAdminSection'. Must NOT be 'hasRole' (function) or any
       *  non-ComputedRef value — those silently evaluate to false at fire time. */
      permission: string;
      /** Cloud Function name as registered in firebaseFunctions.ts. */
      fetcher: string;
      payload?: unknown;
      /** Extracts the cacheable value from the CF response data object. */
      select: (data: unknown) => unknown;
      ttl: number;
      version: string;
      /** CF names whose success should trigger invalidate() on this key. Documentation only. */
      mutators: string[];
    } & InstantPrefetchRouteGate)
  | ({
      type: 'l2-only';
      key: string;
      permission: string;
      /** Identifier of the composable whose prefetch() hook Wave 4 will wire up. */
      composablePrefetch: string;
    } & InstantPrefetchRouteGate);

export const INSTANT_PREFETCH_REGISTRY: ReadonlyArray<InstantPrefetchEntry> = [
  // 體驗回饋佇列 hidden 2026-11 — docs/TRIAL_FEEDBACK_HUB_SPEC.md § 8 (D8); restore = uncomment.
  // {
  //   type: 'l1',
  //   key: 'feedback-prep-list',
  //   permission: 'canViewFeedbackQueue',
  //   fetcher: 'getFeedbackPreparationList',
  //   payload: undefined,
  //   select: (d) => d,
  //   ttl: 60_000,
  //   version: '1',
  //   mutators: [
  //     'addToFeedbackPreparationList',
  //     'removeFromFeedbackPreparationList',
  //     'updateFeedbackPreparationItem',
  //     'addManualFeedbackPreparationItem',
  //     'processFeedbackPreparationList',
  //     // Removes the row's queue item when it creates the form (首堂課與體驗回饋 A4).
  //     'createFeedbackForAttendance',
  //   ],
  // },
  // 'admin-help-galleries' removed 2026-05-17 — rarely-used surface, cold-start
  // tolerable (~1-2s on first open). Components still get on-demand L1 caching
  // via useInstantData when they mount; only the idle-prefetch is dropped.
  {
    type: 'l2-only',
    key: 'timeslot-discovery',
    permission: 'canViewAdminSection',
    composablePrefetch: 'useTimeSlotData',
  },
  {
    type: 'l2-only',
    key: 'teacher-mobile-classes',
    permission: 'canViewTeacherSection',
    composablePrefetch: 'useTeacherClassCache',
    // /teacher/signin + /teacher/completed both render TeacherPage.vue, which
    // already calls getClassesForTeacherView TWICE at mount (today + the 2-week
    // 簽到 range). The warm-up's skip-if-fresh gate reads the in-memory cache
    // only TeacherMobilePage writes, so on those pages it always fired a THIRD
    // call for a container the page itself is already warming (N+1 review §7
    // hunt c, 2026-09-20). Every other /teacher/* path is a mobile surface that
    // benefits from the warm — do not widen this to the whole prefix.
    skipRoutePrefixes: ['/teacher/signin', '/teacher/completed'],
  },
  {
    // 學生名冊 (decision 10, PO 2026-09-29): 課務 / 主管 get the directory at idle after
    // login so every student search is instant. ONE getDoc (+ parts), not a listener and
    // not a CF call (composables/useStudentDirectory.ts). Teachers are not warmed — their
    // phone loads it when 學生查詢 opens. canViewCourseOpsPanel = admin + supervisor only.
    type: 'l2-only',
    key: 'student-directory',
    permission: 'canViewCourseOpsPanel',
    composablePrefetch: 'useStudentDirectory',
  },
  // 'kiosk-screensaver-config' removed 2026-05-17 — admin-only setup page used
  // rarely (~quarterly). Same reasoning as admin-help-galleries above.
] as const;

// ==================================================================================
// CATEGORY: TYPE EXPORTS
// Description: TypeScript type definitions derived from configuration constants.
// ==================================================================================

export type ValidationRuleKey = keyof typeof VALIDATION_CONFIG.RULES;
export type NotificationTypeKey = keyof typeof NOTIFICATION_CONFIG.TYPES;

// ==================================================================================
// CATEGORY: 出勤管理 (Staff Attendance) — Phase 1
// Description: Frontend mirror of backend attendance config. Lean copy — only what
// the frontend directly needs for display, form logic, and status gating.
// Numeric constants with // CPA-TUNABLE are defaults pending CPA feedback (Wave 8).
// ==================================================================================

export const ATTENDANCE_CONFIG = {
  /** Standard working hours per day. // CPA-TUNABLE */
  STANDARD_WORK_HOURS: 8,
  /** Minimum leave booking unit in hours (0.5 = half-hour). // CPA-TUNABLE */
  LEAVE_MIN_UNIT_HOURS: 0.5,
  /**
   * Half-day leave preset windows (HH:MM, Taipei) for the 上午 / 下午 modes of the
   * leave-request period picker. CPA/PO-TUNABLE — approximations until Phase-2 排班
   * provides real scheduled times. Each window spans 4h (= 0.5 day at 8h/day).
   */
  HALF_DAY_PRESETS: {
    morning: { start: '09:00', end: '13:00' },
    afternoon: { start: '13:00', end: '17:00' },
  } as Record<'morning' | 'afternoon', { start: string; end: string }>,
  /** Remaining-leave threshold (hours) at or below which the balance shows a warning color. // CPA-TUNABLE */
  LOW_BALANCE_WARNING_HOURS: 16,
  /**
   * 週年制 (Milestone L): days before the 屆滿日 (到職週年 / 年度終結) at which the staff
   * 特休 card escalates to a §38-IV 折算 warning when remaining > 0. Lean mirror of the
   * backend BUSINESS_RULES.ANNUAL_LEAVE.ANNIVERSARY_REMINDER_LEAD_DAYS[0] (largest lead).
   * // CPA-TUNABLE — keep in sync with the backend reminder cron's largest lead.
   */
  ANNUAL_LEAVE_EXPIRY_WARNING_LEAD_DAYS: 60,
  /**
   * Max age (ms) for a cached geolocation fix to still count as fresh at punch time.
   * Beyond this, the clock page re-fetches before punching (falling back to the
   * stale fix only if the refetch fails). 120000 = 2 minutes.
   */
  GEO_FRESHNESS_MS: 120000,
  /**
   * attendance-batchAdjustRecords: most record ids the CF accepts in ONE 全部核可
   * call — mirrors functions/src/config/appConfig.ts
   * ATTENDANCE_PUNCH.MAX_BATCH_ACKNOWLEDGE (guarded by check-config-mirror.cjs).
   * useAttendanceReview splits a larger queue into chunks of this size
   * (N+1 review §4.10, 2026-09-20). A drift means the CF rejects a chunk the
   * page just sent → nothing in that chunk is acknowledged.
   */
  MAX_BATCH_ACKNOWLEDGE: 200,

  // ─── Trust / honesty copy (staff-facing — dignified, never threatening) ──
  // Shown on the no-GPS attestation tap and the 補登申請 flow. PO-careable
  // wording lives here per CLAUDE.md §4b.

  /** Statement of record integrity — styled .pay-hint below the body text. */
  HONESTY_STATEMENT: '出勤紀錄為薪資計算之依據；不實申報屬重大違規，公司必要時得調閱監視紀錄核對。',
  /** Primary button on the no-GPS attestation modal. */
  ATTEST_BUTTON_LABEL: '我確認目前在公司',
  /** Title of the no-GPS attestation modal. */
  ATTEST_TITLE: '未能取得定位',
  /** Body of the no-GPS attestation modal (HONESTY_STATEMENT shown below it). */
  ATTEST_BODY: '請確認您目前人在公司，打卡將照常記錄並由主管核對。',

  // ─── Leave attachment upload limits ──────────────────────────────────────
  // Mirrors ATTACH_* constants in LeaveRequestPage — single source of truth.

  ATTACHMENT: {
    MAX_SIZE_MB: 3,
    MAX_WIDTH_PX: 1600,
    JPEG_QUALITY: 0.85,
    MAX_PDF_BYTES: 5 * 1024 * 1024,
  },

  // ─── Location confidence display labels ──────────────────────────────────

  /**
   * Staff-facing labels — shown as pills on the staff's own punch history.
   * Keep ALL ≤5 chars so the pill never wraps / pushes the time column out of
   * alignment (PO 2026-06-14). The "always records / supervisor reviews"
   * reassurance lives in the footer hint, so the pill only needs the short word.
   */
  LOCATION_CONFIDENCE_LABELS_STAFF: {
    verified: '位置已核對',
    location_unverified: '位置待核對',
    gps_unavailable: '位置待核對',
    manual: '主管補登',
  } as Record<string, string>,

  /** Supervisor-facing labels — shown in the anomaly review queue. */
  LOCATION_CONFIDENCE_LABELS_SUPERVISOR: {
    location_unverified: '位置異常',
    gps_unavailable: '未取得定位',
  } as Record<string, string>,

  /**
   * Neutral label for a location-review-EXEMPT (WFH/外勤) punch (PO 2026-06-11).
   * Shown to BOTH audiences in place of an unverified/unavailable warning — the
   * punch was intentionally never flagged, so it reads as a normal remote punch
   * rather than an anomaly.
   */
  LOCATION_EXEMPT_LABEL: '遠端打卡',

  // ─── Leave status display labels ─────────────────────────────────────────

  LEAVE_STATUS_LABELS: {
    draft: '草稿',
    pending: '待審核',
    approved: '已核准',
    declined: '已拒絕',
    cancelled: '已取消',
  } as Record<string, string>,

  // ─── Leave status transitions — mirrors backend BUSINESS_RULES.LEAVE ─────
  // Used by UI to determine which action buttons (cancel, etc.) are visible.

  LEAVE_STATUS_TRANSITIONS: {
    draft: ['pending', 'cancelled'],
    pending: ['approved', 'declined', 'cancelled'],
    approved: ['cancelled'],
    declined: [] as string[],
    cancelled: [] as string[],
  } as Record<string, string[]>,
} as const;

// ─── Leave type registry — frontend display + form logic ─────────────────────
// Lean mirror of backend LEAVE_TYPES. Fields needed by the form:
//   code, label, unit, requiresAttachment, countsTowardAnnual, implemented.
// The leave form shows only implemented:true types in Phase 1.

export const LEAVE_TYPES = {
  annual: {
    code: 'annual',
    label: '特休',
    unit: 'hour' as const,
    requiresAttachment: false,
    /** Must match backend leave-type registry requiresReason field. */
    requiresReason: false,
    countsTowardAnnual: true,
    implemented: true,
  },
  personal: {
    code: 'personal',
    label: '事假',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: true,
    countsTowardAnnual: false,
    implemented: true,
  },
  sick: {
    code: 'sick',
    label: '病假',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: true,
    countsTowardAnnual: false,
    implemented: true,
  },
  marriage: {
    code: 'marriage',
    label: '婚假',
    unit: 'day' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: true,
  },
  bereavement: {
    code: 'bereavement',
    label: '喪假',
    unit: 'day' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: true,
  },
  menstrual: {
    code: 'menstrual',
    label: '生理假',
    unit: 'day' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: true,
  },
  sick_hospital: {
    code: 'sick_hospital',
    label: '住院傷病假',
    unit: 'hour' as const,
    requiresAttachment: true,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
  injury_official: {
    code: 'injury_official',
    label: '公傷病假',
    unit: 'hour' as const,
    requiresAttachment: true,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
  family_care: {
    code: 'family_care',
    label: '家庭照顧假',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
  official: {
    code: 'official',
    label: '公假',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
  overtime_comp: {
    code: 'overtime_comp',
    label: '補休',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
  unpaid: {
    code: 'unpaid',
    label: '無薪假',
    unit: 'hour' as const,
    requiresAttachment: false,
    requiresReason: false,
    countsTowardAnnual: false,
    implemented: false,
  },
} as const;

/** Type alias for the leave type code keys. */
export type LeaveTypeCode = keyof typeof LEAVE_TYPES;

// ── 交付包 Wave 2 compile-time guards ──────────────────────────────────────────
// The two label maps must name every state key the CF can emit (the key sets
// themselves are mirrored with the backend by scripts/check-config-mirror.cjs).
// A missing key here is a 費用明細 cell that prints the raw key to the accountant.
void (FRONTEND_BUSINESS_RULES.PAYMENT.CPA_EXPORT.PERIOD_END_STATE_LABELS satisfies Record<
  PeriodEndState,
  string
>);
void (FRONTEND_BUSINESS_RULES.PAYMENT.CPA_EXPORT.PAPER_STATE_LABELS satisfies Record<
  PaperState,
  string
>);
